import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  memo,
  Fragment,
} from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import { geoMercator, geoBounds } from "d3-geo";
import US_COUNTIES from "./usCounties";
import US_ZIPS from "./usZips";
import { useAuth } from "./AuthContext";
import LoginModal from "./LoginModal";
import ProvisionCampaignModal from "./ProvisionCampaignModal";
import ConnectedAccountsPanel from "./ConnectedAccountsPanel";
import PubNub from "pubnub";
import ReactMarkdown from "react-markdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faInstagram,
  faXTwitter,
  faLinkedin,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import {
  faThumbsUp,
  faHeart,
  faFire,
  faComment,
  faShare,
  faBookmark,
  faEllipsis,
  faGlobe,
  faRetweet,
  faArrowUpFromBracket,
  faEnvelope,
  faMessage,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faThumbsUp as faThumbsUpSolid,
  faHeart as faHeartSolid,
  faFire as faFireSolid,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Drawer,
  Divider,
  Skeleton,
  Button,
  Select,
  MenuItem,
  Avatar,
  Menu,
  ListItemText,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";
import { helix } from "ldrs";
import kalshiLogo from "./kalshi.svg";
import pollenLogo from "./pollis_brand_whitered.svg";
import socialSearchIcon from "./socialsearch.svg";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import {
  C,
  PARTY_COLORS,
  SIGNAL_COLORS,
  baseTooltip,
  baseGridScale,
  baseLegend,
  KALSHI_MACRO_SERIES,
  KALSHI_HISTORY_SERIES,
  WINRED_MONTHLY,
  FEC_QUARTERS,
  CANDIDATE_FUNDRAISING,
  OPPONENT_FUNDRAISING,
  WILSON_FUNDRAISING,
  SMALL_DONOR_COUNT,
  POLL_DATES,
  POLL_AVERAGES,
  FAV_DATES,
  FAV_NET,
  STRUCTURAL_DATES,
  PRESIDENTIAL_APPROVAL,
  RIGHT_TRACK,
  GENERIC_BALLOT,
  UNEMPLOYMENT,
  VOTER_REG_DATES,
  VOTER_REG_NET,
  ISSUE_SALIENCE,
  FUNDRAISING_DRIVERS,
  ELECTION_HISTORY,
  DONOR_COHORTS,
} from "./data";

helix.register();

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  Filler,
);

const KALSHI_URL = process.env.REACT_APP_KALSHI_API_URL || "";
const PHP_API_URL = process.env.REACT_APP_PHP_API_URL || "";

// ─── Helpers ───────────────────────────────────────────────────────────────

// Derive Kalshi series keys from campaign metadata when not explicitly configured in DB.
// Pattern: KX{STATE}{DISTRICT}R/D for House, KX{STATE}SENR/D for Senate, KX{STATE}GOVR/D for Governor.
const deriveRaceSeries = (state, office, district) => {
  const st = (state || "").toUpperCase().trim();
  if (!st) return [];
  const off = (office || "").toLowerCase();
  const dist = district ? String(district).padStart(2, "0") : null;
  if ((off.includes("house") || off.includes("representative")) && dist)
    return [`KX${st}${dist}R`, `KX${st}${dist}D`];
  if (off.includes("senate")) return [`KXSENATE${st}R`, `KXSENATE${st}D`];
  if (off.includes("governor") || off.includes("gov"))
    return [`KXGOV${st}NOMR`, `KXGOV${st}NOMD`];
  return [];
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.max(0, Math.ceil(diff / 86400000));
};

// Data is stale if it was generated before the most recent 6 AM local time
const isStale6am = (generatedAt) => {
  if (!generatedAt) return true;
  const now = new Date();
  const cutoff = new Date(now);
  cutoff.setHours(6, 0, 0, 0);
  if (now < cutoff) cutoff.setDate(cutoff.getDate() - 1);
  return new Date(generatedAt) < cutoff;
};

const fmtUpdatedAt = (ts) => {
  if (!ts) return "";
  return (
    new Date(ts).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }) +
    " · " +
    new Date(ts).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
  );
};

const fmtMoney = (n) => {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${abs.toLocaleString()}`;
};

const detectStage = (primaryDt, electionDt) => {
  const daysToPrimary = daysUntil(primaryDt);
  const daysToElection = daysUntil(electionDt);
  if (!electionDt) return { label: "—", color: C.muted };
  if (daysToElection <= 0) return { label: "Post-Election", color: C.muted };
  if (daysToElection <= 90) return { label: "Final Sprint", color: C.red };
  if (primaryDt && daysToPrimary <= 90)
    return { label: "Primary", color: C.orange };
  if (daysToElection <= 365) return { label: "General", color: C.blue };
  return { label: "Early Cycle", color: C.green };
};

const smooth3 = (arr) =>
  arr.map((_, i) => {
    const slice = arr.slice(Math.max(0, i - 2), i + 1);
    return slice.reduce((s, v) => s + v, 0) / slice.length;
  });

const totalRaised = WINRED_MONTHLY.raised.reduce((s, v) => s + v, 0);
const lastMonthRaised = WINRED_MONTHLY.raised[WINRED_MONTHLY.raised.length - 1];
const prevMonthRaised = WINRED_MONTHLY.raised[WINRED_MONTHLY.raised.length - 2];
const totalDonors = WINRED_MONTHLY.donorCount.reduce((s, v) => s + v, 0);

// ─── Sub-components ────────────────────────────────────────────────────────
const _fmtFreshness = (ts) => {
  if (!ts) return null;
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const SectionHeader = ({ title, sub, freshness, onSync, syncing }) => (
  <Box
    sx={{
      mb: 2,
      pb: 1,
      borderBottom: `2px solid ${C.navy}`,
      width: "fit-content",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          color: C.navy,
          textTransform: "uppercase",
          letterSpacing: "0.09em",
        }}
      >
        {title}
      </Typography>
      {freshness && _fmtFreshness(freshness) && (
        <Typography sx={{ fontSize: 9, color: C.muted, fontWeight: 500 }}>
          · {_fmtFreshness(freshness)}
        </Typography>
      )}
      {onSync && (
        <Box
          component="button"
          onClick={onSync}
          disabled={syncing}
          title="Sync data"
          sx={{
            ml: 0.5,
            fontSize: 10,
            fontWeight: 700,
            px: 0.75,
            py: 0.2,
            border: `1px solid ${C.border}`,
            borderRadius: "4px",
            bgcolor: C.white,
            color: syncing ? C.muted : C.navy,
            cursor: syncing ? "not-allowed" : "pointer",
            lineHeight: 1,
            "&:hover": { borderColor: C.navy },
            animation: syncing ? "spin 1s linear infinite" : "none",
            "@keyframes spin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        >
          ↻
        </Box>
      )}
    </Box>
    {sub && (
      <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const StatCard = ({ label, value, sub, accent, info, setup }) => {
  const [open, setOpen] = useState(false);
  const isEmpty =
    value === null ||
    value === undefined ||
    value === "" ||
    value === 0 ||
    (typeof value === "number" && isNaN(value)) ||
    value === "$0" ||
    value === "0";
  // When the card has no data and a setup CTA is provided, render an actionable prompt
  // ("Connect WinRed →", "Set dates →") instead of a dead "—" — per the Overview redesign.
  const showSetup = isEmpty && !!setup;
  const clickable = showSetup || !!info;
  return (
    <Box
      onClick={
        showSetup ? setup.onClick : info ? () => setOpen(true) : undefined
      }
      sx={{
        bgcolor: C.white,
        border: `1px solid ${C.border}`,
        borderTop: `3px solid ${showSetup ? C.border : accent || C.navy}`,
        p: 2,
        borderRadius: 1,
        flex: 1,
        minWidth: 130,
        position: "relative",
        cursor: clickable ? "pointer" : "default",
        transition: "box-shadow 0.15s",
        ...(clickable
          ? {
              "&:hover": {
                boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
                borderColor: showSetup ? C.blue : accent || C.navy,
              },
            }
          : {}),
      }}
    >
      {info && !showSetup && (
        <Typography
          sx={{
            position: "absolute",
            top: 7,
            right: 8,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1,
            userSelect: "none",
          }}
        >
          ⓘ
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: C.muted,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          pr: info ? 2 : 0,
        }}
      >
        {label}
      </Typography>
      {showSetup ? (
        <>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: C.blue,
              mt: 0.6,
              lineHeight: 1.3,
            }}
          >
            {setup.label} →
          </Typography>
          {setup.sub && (
            <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.4 }}>
              {setup.sub}
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color: accent || C.charcoal,
              mt: 0.5,
              lineHeight: 1,
            }}
          >
            {isEmpty ? "—" : value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.4 }}>
              {sub}
            </Typography>
          )}
        </>
      )}
      {info && (
        <InfoModal info={open ? info : null} onClose={() => setOpen(false)} />
      )}
    </Box>
  );
};

const SignalBadge = ({ signal }) => {
  const color = SIGNAL_COLORS[signal] || C.muted;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        px: 1,
        py: 0.3,
        borderRadius: "3px",
        bgcolor: color + "18",
        border: `1px solid ${color}44`,
        color,
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {signal}
    </Box>
  );
};

const PriorityBadge = ({ priority }) => {
  const color =
    priority === "High" ? C.red : priority === "Medium" ? C.orange : C.muted;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        px: 1,
        py: 0.3,
        borderRadius: "3px",
        bgcolor: color + "18",
        border: `1px solid ${color}44`,
        color,
      }}
    >
      {priority}
    </Box>
  );
};

// ── InfoModal ─────────────────────────────────────────────────────────────────
// Reusable dark-navy detail modal. Pass `info` object with:
//   eyebrow, title, sections: [{ heading, accentColor, body, tiers: [{dot, label, desc}] }], footnote
const InfoModal = ({ info, onClose, intro }) => {
  if (!info) return null;
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        bgcolor: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: "#0f1f36",
          color: "#e8edf4",
          borderRadius: "12px",
          p: "28px 32px 24px",
          maxWidth: 620,
          width: "100%",
          position: "relative",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Close */}
        <Box
          component="button"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 20,
            right: 22,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.4)",
            fontSize: 18,
            lineHeight: 1,
            p: 0,
            "&:hover": { color: "#fff" },
          }}
        >
          ✕
        </Box>

        {/* Header */}
        <Box
          sx={{
            mb: 2,
            pb: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            pr: 4,
          }}
        >
          {info.eyebrow && (
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: "rgba(255,255,255,0.35)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                mb: 0.4,
              }}
            >
              {info.eyebrow}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 800,
              color: "#fff",
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            {info.title}
          </Typography>
          {info.subtitle && (
            <Typography
              sx={{
                fontSize: 12,
                color: "rgba(255,255,255,0.45)",
                mt: 0.3,
                fontStyle: "italic",
              }}
            >
              {info.subtitle}
            </Typography>
          )}
        </Box>

        {/* DISC intro */}
        {intro && (
          <Typography
            sx={{
              fontSize: 12,
              color: "rgba(255,255,255,0.45)",
              lineHeight: 1.6,
              mb: 2,
              fontStyle: "italic",
            }}
          >
            {intro}
          </Typography>
        )}

        {/* Sections */}
        {(info.sections || []).map((sec, i) => (
          <Box key={i} sx={{ mb: i < info.sections.length - 1 ? 1.75 : 0 }}>
            {sec.heading && (
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: 0.6,
                }}
              >
                {sec.heading}
              </Typography>
            )}
            {sec.body && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.8)",
                  lineHeight: 1.6,
                  mb: sec.tiers ? 0.75 : 0,
                }}
              >
                {sec.body}
              </Typography>
            )}
            {sec.tiers && (
              <Box>
                {sec.tiers.map((tier, j) => (
                  <Box
                    key={j}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 0,
                      mb: 0.35,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        flexShrink: 0,
                        width: 58,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: tier.dot || "rgba(255,255,255,0.3)",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: tier.dot || "#fff",
                        }}
                      >
                        {tier.label}
                      </Typography>
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 12,
                        color: "rgba(255,255,255,0.55)",
                        lineHeight: 1.5,
                      }}
                    >
                      {tier.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}

        {/* Footnote */}
        {info.footnote && (
          <>
            <Box
              sx={{
                borderTop: "1px solid rgba(255,255,255,0.08)",
                mt: 1.75,
                mb: 1.25,
              }}
            />
            <Typography
              sx={{
                fontSize: 11,
                color: "rgba(255,255,255,0.3)",
                fontStyle: "italic",
              }}
            >
              {info.footnote}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

// ── DISC info definitions ─────────────────────────────────────────────────────
const DISC_INTRO =
  "DISC is a behavioral model that classifies how people communicate, decide, and respond to influence. The four types — Dominant, Influential, Steady, Conscientious — reveal how to structure asks, set amounts, and frame outreach for maximum conversion.";

const DISC_INFO = {
  D: {
    eyebrow: "DISC Personality Profile",
    title: "Dominant (D)",
    subtitle: "Results-driven · Direct · Decisive",
    sections: [
      {
        heading: "Donor Profile",
        accentColor: "#dc2626",
        body: "D-type donors are fast decision-makers motivated by control, results, and winning. High-capacity givers who act quickly when asked clearly — but disengage when over-explained or managed. They want to know what their money does, not how it makes them feel. A direct call from the candidate with one clear number outperforms any email sequence.",
      },
      {
        heading: "Donation Page Strategy",
        accentColor: "#dc2626",
        body: "3–4 clean round numbers. Pre-select the ask. One bold impact statement, nothing else.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Be direct — lead with impact, give one number, keep everything short.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Peer-level outreach: candidate call or campaign manager, not a form letter.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Frame around winning and being part of the decisive coalition.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Over-explain, apologize, or use emotional appeals.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Send mass emails or automated follow-up sequences.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Hedge the ask — 'whatever you can give' is invisible to a D.",
          },
        ],
      },
    ],
    footnote:
      "DISC type inferred from donation history, LinkedIn career history, and professional signals.",
  },
  I: {
    eyebrow: "DISC Personality Profile",
    title: "Influential (I)",
    subtitle: "Enthusiastic · Social · Optimistic",
    sections: [
      {
        heading: "Donor Profile",
        accentColor: "#f59e0b",
        body: "I-type donors are relationship-driven and energized by being part of something exciting. They give because they want to belong to the winning team. Highly receptive to milestone framing and social proof — 'Join 47 donors at this level.' A well-cultivated I-type often brings other donors along.",
      },
      {
        heading: "Donation Page Strategy",
        accentColor: "#f59e0b",
        body: "Milestone-anchored amounts. Social proof on the ask button. Name the giving tier. Aspirational and exclusive tone.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Use milestone and social proof framing — name the tier, cite the count.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Invite them to events, donor calls, and exclusive briefings.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Keep tone energetic and aspirational — movement, not transaction.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Lead with data, policy, or dry impact metrics.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Make them feel like one of many — personalize everything.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Ghost them after the gift — recognition and follow-up are critical.",
          },
        ],
      },
    ],
    footnote:
      "DISC type inferred from donation history, LinkedIn career history, and professional signals.",
  },
  S: {
    eyebrow: "DISC Personality Profile",
    title: "Steady (S)",
    subtitle: "Loyal · Patient · Team-Oriented",
    sections: [
      {
        heading: "Donor Profile",
        accentColor: "#16a34a",
        body: "S-type donors are loyal and deeply uncomfortable with pressure or confrontation. Your most reliable recurring donors — they give because they believe in the cause and feel a personal connection to the team. Heavy-handed tactics cause S-types to disengage silently: they won't push back, they'll just stop giving.",
      },
      {
        heading: "Donation Page Strategy",
        accentColor: "#16a34a",
        body: "Step-up ladder anchored at prior gift. Warm continuity copy. Never show a jump that feels aggressive.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Reference their history — 'Your continued support has been foundational.'",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Warm, personal, relationship-first language. Make them feel part of the team.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Propose modest step-ups that feel achievable, not aggressive.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Pressure, deadline-threaten, or use urgency tactics.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Skip the thank-you — S-types notice when they feel taken for granted.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Lead with a big jump in ask — escalate gradually.",
          },
        ],
      },
    ],
    footnote:
      "DISC type inferred from donation history, LinkedIn career history, and professional signals.",
  },
  C: {
    eyebrow: "DISC Personality Profile",
    title: "Conscientious (C)",
    subtitle: "Analytical · Precise · Quality-Driven",
    sections: [
      {
        heading: "Donor Profile",
        accentColor: "#2563eb",
        body: "C-type donors are motivated by accuracy, ROI, and specificity. Skeptical of vague claims — they need to feel their gift is deployed intelligently. '$5,000 funds 30 days of field outreach in Charleston County' converts far better than '$5,000 makes a difference.' They'll research the campaign and notice inconsistencies.",
      },
      {
        heading: "Donation Page Strategy",
        accentColor: "#2563eb",
        body: "Specific non-round amounts tied to named impact metrics. One precise deliverable per button. No emotional language.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Tie every ask to a specific, measurable impact metric.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Share field reports, polling data, or spending breakdowns — they'll read them.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Use precise amounts ($4,750) over round numbers — specificity signals rigor.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Use emotional appeals or vague language ('fight for our future').",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Rush them — C-types need time to process before committing.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Make claims you can't back up — they will check.",
          },
        ],
      },
    ],
    footnote:
      "DISC type inferred from donation history, LinkedIn career history, and professional signals.",
  },
};

// ── Cohort info definitions ───────────────────────────────────────────────────
const COHORT_INTRO =
  "Donor cohorts classify where each supporter is in their giving lifecycle — how recently they've given, at what level, and how likely they are to give again. Each cohort calls for a distinct outreach approach.";

const COHORT_INFO = {
  "Major Donor": {
    eyebrow: "Donor Cohort",
    title: "Major Donor",
    subtitle: "High-capacity · Mission-aligned · Principal-level ask",
    sections: [
      {
        heading: "Cohort Profile",
        accentColor: "#16a34a",
        body: "Donors who have given at a level that puts them in the top tier of the campaign's file. They've demonstrated both capacity and commitment — they're not just giving money, they're investing in an outcome. Treat them as principals, not list members.",
      },
      {
        heading: "Engagement Strategy",
        accentColor: "#16a34a",
        body: "Personalized, candidate-direct communication only. Never batch-email a major donor.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Candidate call or handwritten note before any major ask.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Provide exclusive access — briefings, events, early intel.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Acknowledge their history specifically ('Your $5,000 last quarter...').",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Send them generic blast emails or mass texts.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Let the relationship go cold between asks.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Ask for less than their established giving level.",
          },
        ],
      },
    ],
    footnote:
      "Cohort assigned based on cumulative giving history and single-gift thresholds in the WinRed file.",
  },
  "High Propensity": {
    eyebrow: "Donor Cohort",
    title: "High Propensity",
    subtitle: "Likely to give · Warm · Ready to activate",
    sections: [
      {
        heading: "Cohort Profile",
        accentColor: "#1e40af",
        body: "Donors identified as likely to give based on FEC cross-campaign history, wealth signals, or engagement patterns — but haven't yet given to this campaign or are in an early stage. The highest-ROI segment for prospecting.",
      },
      {
        heading: "Engagement Strategy",
        accentColor: "#1e40af",
        body: "Warm, personalized first touch. Make it easy to say yes.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Lead with the candidate's mission and a specific ask amount.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Reference any shared connection or prior political activity.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Make the first ask small and frictionless — get them in the file.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Treat them like cold prospects — they're already warm.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Over-ask on first contact — get the relationship started first.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Rely on digital-only outreach — phone or direct mail converts better.",
          },
        ],
      },
    ],
    footnote:
      "Cohort assigned based on FEC giving history, wealth signals, and engagement scoring.",
  },
  "New Donor": {
    eyebrow: "Donor Cohort",
    title: "New Donor",
    subtitle: "First gift · Conversion moment · Cultivation window",
    sections: [
      {
        heading: "Cohort Profile",
        accentColor: "#2563eb",
        body: "Donors who made their first contribution to this campaign. The first 30 days after acquisition are the highest-churn window — if they don't hear from the campaign again, they won't give again. This is the most critical cultivation moment in the donor lifecycle.",
      },
      {
        heading: "Engagement Strategy",
        accentColor: "#2563eb",
        body: "Immediate acknowledgment, personal welcome, low-pressure second touch.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Thank them within 48 hours — personally if possible.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Make them feel they made the right decision — reinforce the mission.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Introduce the campaign's next milestone — give them something to look forward to.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Let weeks pass before the next contact.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Hit them with a major second ask too quickly.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Treat them like a recurring donor before they've proven it.",
          },
        ],
      },
    ],
    footnote:
      "Cohort assigned to donors with a single recorded gift and no prior giving history on file.",
  },
  "Lapsed 90-180d": {
    eyebrow: "Donor Cohort",
    title: "Lapsed 90–180 Days",
    subtitle: "Recently lapsed · Reactivation window · Warm",
    sections: [
      {
        heading: "Cohort Profile",
        accentColor: "#ea580c",
        body: "Donors who gave within the last 6 months but not the last 90 days. Still within the warm reactivation window — they haven't forgotten the campaign and likely still support the candidate. A targeted personal touch can bring them back.",
      },
      {
        heading: "Engagement Strategy",
        accentColor: "#ea580c",
        body: "Warm reactivation. Reference their prior gift. Give them a specific reason to return now.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Reference their last gift specifically — amount and timing.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Connect the ask to something new — a milestone, opponent development, or deadline.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Keep it short — they don't need to be re-convinced, just re-engaged.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Treat them as cold prospects — they're still warm.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Send generic fundraising blasts — they'll tune out.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Wait — the longer you wait, the colder this segment gets.",
          },
        ],
      },
    ],
    footnote:
      "Cohort assigned to donors whose last recorded gift was 90–180 days ago.",
  },
  "Lapsed 180-365d": {
    eyebrow: "Donor Cohort",
    title: "Lapsed 180–365 Days",
    subtitle: "Extended lapse · Cooling · Re-engagement required",
    sections: [
      {
        heading: "Cohort Profile",
        accentColor: "#92400e",
        body: "Donors who have not given in 6–12 months. Still recoverable but requires deliberate re-engagement. They may have forgotten, become distracted, or had concerns go unaddressed. A strong ask with a compelling reason to return is needed.",
      },
      {
        heading: "Engagement Strategy",
        accentColor: "#92400e",
        body: "Re-engagement with context. Remind them of the mission and what's changed, not just the need.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Lead with what's at stake — the race has evolved since they last gave.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Consider a softer entry point — petition, event, or smaller ask before the big one.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Acknowledge the gap without making them feel guilty.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Send the same fundraising email they've already tuned out.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Lead with urgency before re-establishing the relationship.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Give up after one touch — this segment often requires 3+ contacts.",
          },
        ],
      },
    ],
    footnote:
      "Cohort assigned to donors whose last recorded gift was 180–365 days ago.",
  },
  "One-Time Donor": {
    eyebrow: "Donor Cohort",
    title: "One-Time Donor",
    subtitle: "Single gift · Unretained · High-churn risk",
    sections: [
      {
        heading: "Cohort Profile",
        accentColor: "#64748b",
        body: "Donors who gave exactly once and have not given again. The most common profile and the hardest to convert to recurring. Their single gift may have been event-driven or impulsive — the mission hasn't yet been internalized. Every subsequent gift requires intentional cultivation.",
      },
      {
        heading: "Engagement Strategy",
        accentColor: "#64748b",
        body: "Make them feel like giving again was their own idea.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Speak to what they responded to the first time — replicate that moment.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Show progress — what happened because of their gift.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Lower friction for the second gift — match or beat their first amount.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Assume loyalty — they haven't demonstrated it yet.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Overwhelm them with high-frequency asks.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Ignore the context of their first gift when crafting the next ask.",
          },
        ],
      },
    ],
    footnote:
      "Cohort assigned to donors with exactly one recorded gift in the WinRed and FEC history.",
  },
  "Lapsed 365d+": {
    eyebrow: "Donor Cohort",
    title: "Lapsed 365+ Days",
    subtitle: "Cold · Win-back required · Low-cost reactivation",
    sections: [
      {
        heading: "Cohort Profile",
        accentColor: "#dc2626",
        body: "Donors who have not given in over a year. The coldest recoverable segment — many have moved on, changed circumstances, or shifted priorities. Win-back campaigns for this group have low response rates but can surface high-value reactivations worth the effort.",
      },
      {
        heading: "Engagement Strategy",
        accentColor: "#dc2626",
        body: "Treat them like a warm prospect, not a lapsed donor. Lead with what's new.",
        tiers: [
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Lead with what's new and why it matters now — don't dwell on old history.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Make it easy to re-enter with a modest ask — no big commitment feel.",
          },
          {
            dot: "#22c55e",
            label: "Do",
            desc: "Use a different channel than the one that failed to retain them.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Reference their prior gift as if it were recent — acknowledge the gap.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Invest heavy personal attention until they show a signal.",
          },
          {
            dot: "#ef4444",
            label: "Don't",
            desc: "Over-suppress — periodic low-cost touches still generate reactivations.",
          },
        ],
      },
    ],
    footnote:
      "Cohort assigned to donors whose last recorded gift was more than 365 days ago.",
  },
};

const COHORT_COLORS = {
  "Major Donor": C.green,
  "High Propensity": C.navy,
  "New Donor": C.blue,
  "Lapsed 90-180d": C.orange,
  "Lapsed 180-365d": "#b07030",
  "One-Time Donor": C.muted,
  "Lapsed 365d+": C.red,
};

const CohortBadge = ({ cohort, onClick }) => {
  const color = COHORT_COLORS[cohort] || C.muted;
  return (
    <Box
      component="span"
      onClick={onClick}
      sx={{
        fontSize: 10,
        fontWeight: 700,
        px: 1,
        py: 0.3,
        borderRadius: "3px",
        bgcolor: color + "18",
        border: `1px solid ${color}44`,
        color,
        whiteSpace: "nowrap",
        display: "inline-block",
        ...(onClick && {
          cursor: "pointer",
          "&:hover": { bgcolor: color + "28" },
        }),
      }}
    >
      {cohort || "—"}
    </Box>
  );
};

const Loader = ({ text = "Loading…" }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      py: 6,
      gap: 2,
    }}
  >
    <l-helix size="45" speed="2.5" color="#0a2240"></l-helix>
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        color: C.muted,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
      }}
    >
      {text}
    </Typography>
  </Box>
);

// ─── Kalshi Signal Card ────────────────────────────────────────────────────
const KalshiSignalCard = ({ kalshi, kalshiLoading }) => {
  // Extract the most impactful live signal, fall back to static defaults
  const houseMarkets = kalshi?.series?.CONTROLH?.markets || [];
  const dWinsHouse = houseMarkets.find(
    (m) =>
      m.ticker?.endsWith("-D") || m.title?.toLowerCase().includes("democrat"),
  );
  const houseDProb =
    dWinsHouse?.last_price != null
      ? Math.round(dWinsHouse.last_price * 100)
      : 86;

  const recessionMkts = kalshi?.series?.KXRECSSNBER?.markets || [];
  const recessionProb =
    recessionMkts[0]?.last_price != null
      ? Math.round(recessionMkts[0].last_price * 100)
      : 23;

  const approvalMkts = kalshi?.series?.KXAPRPOTUS?.markets || [];
  // Approximate approval range from market titles
  const approvalNote = approvalMkts.length
    ? `${approvalMkts.length} active approval markets`
    : "~44% RCP avg";

  const waveSignal =
    houseDProb >= 75
      ? "Strong Headwind"
      : houseDProb >= 55
        ? "Headwind"
        : "Neutral";
  const waveColor = SIGNAL_COLORS[waveSignal];

  return (
    <Box sx={{ mb: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: "0.09em",
          }}
        >
          ⚡ Election Environment · Kalshi
        </Typography>
        {kalshiLoading && (
          <Typography sx={{ fontSize: 9, color: C.muted }}>Loading…</Typography>
        )}
      </Box>
      <Box
        sx={{
          bgcolor: C.navyDark,
          borderLeft: `4px solid ${waveColor}`,
          p: 2,
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 0.75,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: C.white,
              lineHeight: 1.3,
              maxWidth: "75%",
            }}
          >
            Democrats {houseDProb}% to win House in 2026 — wave environment
            active
          </Typography>
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                px: 1,
                py: 0.3,
                borderRadius: "3px",
                bgcolor: waveColor + "25",
                border: `1px solid ${waveColor}55`,
                color: waveColor,
              }}
            >
              {waveSignal}
            </Box>
          </Box>
        </Box>
        <Typography
          sx={{ fontSize: 11, color: "#a0b4c8", lineHeight: 1.65, mb: 1.25 }}
        >
          Markets price Democrats at {houseDProb}% to win the House — the
          strongest anti-establishment signal since 2018. This is a strategic
          asset for challenger candidates: the outsider frame resonates with
          primary voters angry at institutional insiders. Recession risk at{" "}
          {recessionProb}% elevates fiscal urgency messaging before the primary
          sprint.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {[
            {
              label: "D wins House",
              value: `${houseDProb}%`,
              color: waveColor,
            },
            {
              label: "Recession risk",
              value: `${recessionProb}%`,
              color: C.orange,
            },
            { label: "Trump approval", value: approvalNote, color: C.muted },
          ].map((s) => (
            <Box key={s.label}>
              <Typography
                sx={{
                  fontSize: 9,
                  color: "#5a7898",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                {s.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: s.color,
                  lineHeight: 1.2,
                }}
              >
                {s.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Profile Drawer ────────────────────────────────────────────────────────
const DISC_COLORS = {
  D: "#dc2626",
  I: "#f59e0b",
  S: "#16a34a",
  C: "#2563eb",
};
const DISC_LABELS = {
  D: "Dominant",
  I: "Influential",
  S: "Steady",
  C: "Conscientious",
};

// Per-network display metadata for the profile header + Social Presence cards. `mark` is the small glyph
// shown in the header pill; `icon` renders the branded chip in the presence grid. `host` builds a profile
// link from a vanity handle; `idUrl` builds one from a numeric id where the network supports it.
const PLATFORM_META = {
  x:         { label: "X",         color: "#0f1419", mark: "𝕏",  host: "x.com",         idUrl: null },
  facebook:  { label: "Facebook",  color: "#1877f2", mark: "f",  host: "facebook.com",  idUrl: (id) => `https://facebook.com/profile.php?id=${id}` },
  instagram: { label: "Instagram", color: "#c13584", mark: "IG", host: "instagram.com", idUrl: null },
  nextdoor:  { label: "Nextdoor",  color: "#00b246", mark: "N",  host: "nextdoor.com",  idUrl: null },
};
const PLATFORM_ORDER = ["x", "facebook", "instagram", "nextdoor"];
// Compact follower/engagement formatting: 1200 → "1.2k", 3500 → "3.5k", 10000 → "10k".
const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return (v / 1e6).toFixed(v % 1e6 ? 1 : 0).replace(/\.0$/, "") + "m";
  if (v >= 1e3) return (v / 1e3).toFixed(v % 1e3 && v < 1e4 ? 1 : 0).replace(/\.0$/, "") + "k";
  return String(v);
};
// Build a profile URL for a presence entry from its clean handle or numeric id (null when unlinkable).
const platformUrl = (platform, handle, id) => {
  const m = PLATFORM_META[platform];
  if (!m) return null;
  if (handle) return `https://${m.host}/${handle.replace(/^@/, "")}`;
  if (id && m.idUrl) return m.idUrl(id);
  return null;
};

// Real brand glyphs (24×24 paths) — rendered white inside a platform-colored rounded square. Never text.
const PLATFORM_SVG = {
  x: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  facebook:
    "M15.12 5.32H17V2.14A26.11 26.11 0 0 0 14.26 2c-2.72 0-4.58 1.66-4.58 4.7v2.6H6.61v3.56h3.07V22h3.68v-9.14h3.06l.46-3.56h-3.52V7.05c0-1.03.28-1.73 1.76-1.73z",
  instagram:
    "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z",
  nextdoor:
    "M12 2.75 3.5 9.2V21H9.6v-6.15a2.4 2.4 0 0 1 4.8 0V21h6.1V9.2z",
};
const PlatformIcon = ({ platform, size = 22, radius = 6 }) => {
  const m = PLATFORM_META[platform];
  if (!m || !PLATFORM_SVG[platform]) return null;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: `${radius}px`,
        flexShrink: 0,
        bgcolor: m.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg
        width={size * 0.6}
        height={size * 0.6}
        viewBox="0 0 24 24"
        fill="#fff"
        aria-hidden="true"
      >
        <path d={PLATFORM_SVG[platform]} />
      </svg>
    </Box>
  );
};

const StatusBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  const color =
    s === "paid" || s === "approved" || s === "succeeded"
      ? "#16a34a"
      : s === "pending"
        ? "#d97706"
        : C.muted;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        px: 1,
        py: 0.15,
        borderRadius: "99px",
        border: `1px solid ${color}`,
        color,
        display: "inline-block",
      }}
    >
      {status || "—"}
    </Box>
  );
};

const WR_PAGE_SIZE = 10;

const WinRedDonations = ({ loading, winred, fmtDate, fmtAmt, donorRow }) => {
  const [page, setPage] = useState(1);
  const [activeYear, setActiveYear] = useState(null);

  const transactions = useMemo(
    () => winred.transactions || [],
    [winred.transactions],
  );

  const yearTotals = useMemo(() => {
    const totals = {};
    transactions.forEach((t) => {
      const y = (t["Created At"] || "").slice(0, 4);
      if (!y) return;
      const amt =
        parseFloat(String(t["Amount"] || "0").replace(/[$,]/g, "")) || 0;
      totals[y] = (totals[y] || 0) + amt;
    });
    return Object.entries(totals).sort((a, b) => b[0].localeCompare(a[0]));
  }, [transactions]);

  const filtered = useMemo(() => {
    if (!activeYear) return transactions;
    return transactions.filter((t) =>
      (t["Created At"] || "").startsWith(activeYear),
    );
  }, [transactions, activeYear]);

  const totalPages = Math.ceil(filtered.length / WR_PAGE_SIZE);
  const pg = Math.min(page, totalPages || 1);
  const visible = filtered.slice((pg - 1) * WR_PAGE_SIZE, pg * WR_PAGE_SIZE);
  const globalOffset = (pg - 1) * WR_PAGE_SIZE;

  const handleYear = (y) => {
    setActiveYear((prev) => (prev === y ? null : y));
    setPage(1);
  };

  return (
    <>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          color: C.charcoal,
          mb: 1,
          mt: 2.5,
        }}
      >
        {loading
          ? "Donation History"
          : winred.count
            ? `Donation History (${winred.count} · ${fmtAmt(winred.total)})`
            : "Donation History"}
      </Typography>

      {!loading &&
        yearTotals.length > 0 &&
        (() => {
          const maxAmt = Math.max(...yearTotals.map(([, v]) => v));
          const fmtShort = (n) =>
            n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;
          return (
            <Box
              sx={{
                bgcolor: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: "6px",
                p: 1.5,
                mb: 1.5,
              }}
            >
              {yearTotals.map(([year, total]) => {
                const active = activeYear === year;
                const pct = maxAmt > 0 ? (total / maxAmt) * 100 : 0;
                return (
                  <Box
                    key={year}
                    onClick={() => handleYear(year)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 0.75,
                      cursor: "pointer",
                      "&:last-child": { mb: 0 },
                      "&:hover .bar": { opacity: 0.85 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: active ? C.navy : C.muted,
                        width: 36,
                        flexShrink: 0,
                        textAlign: "right",
                      }}
                    >
                      {year}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: 14,
                        borderRadius: "3px",
                        bgcolor: C.bg,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        className="bar"
                        sx={{
                          width: `${pct}%`,
                          height: "100%",
                          borderRadius: "3px",
                          bgcolor: active ? C.navy : C.blue,
                          transition: "width 0.3s ease, background-color 0.15s",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: active ? C.navy : C.charcoal,
                        width: 42,
                        flexShrink: 0,
                      }}
                    >
                      {fmtShort(total)}
                    </Typography>
                  </Box>
                );
              })}
              {activeYear && (
                <Typography
                  onClick={() => handleYear(activeYear)}
                  sx={{
                    fontSize: 9,
                    color: C.navy,
                    mt: 1,
                    cursor: "pointer",
                    textAlign: "right",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Clear filter ✕
                </Typography>
              )}
            </Box>
          );
        })()}

      <Box
        sx={{
          bgcolor: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 1.5 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: 2, mb: 0.75 }}>
                <Skeleton variant="text" width="5%" height={16} />
                <Skeleton variant="text" width="30%" height={16} />
                <Skeleton variant="text" width="25%" height={16} />
                <Skeleton variant="text" width="15%" height={16} />
                <Skeleton variant="text" width="18%" height={16} />
              </Box>
            ))}
          </Box>
        ) : visible.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                {["No", "Source", "Date", "Amount", "Status"].map((h) => (
                  <th
                    key={h}
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      padding: "8px 12px",
                      borderBottom: `1px solid ${C.border}`,
                      textAlign: "left",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((t, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 11,
                      color: C.muted,
                      width: 28,
                    }}
                  >
                    {globalOffset + i + 1}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 11,
                      color: C.charcoal,
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t["Source Code"] || "—"}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 11,
                      color: C.charcoal,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtDate(t["Created At"])}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: C.charcoal,
                    }}
                  >
                    {fmtAmt(t["Amount"])}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <StatusBadge status={t["Status"]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : donorRow ? (
          <Box sx={{ p: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            {[
              {
                label: "Most Recent Gift",
                value: donorRow["Most Recent Contribution"]
                  ? fmtAmt(
                      parseFloat(
                        String(donorRow["Most Recent Contribution"]).replace(
                          /[$,]/g,
                          "",
                        ),
                      ),
                    )
                  : "—",
              },
              {
                label: "Average Gift",
                value: donorRow["Average Contribution"]
                  ? fmtAmt(
                      parseFloat(
                        String(donorRow["Average Contribution"]).replace(
                          /[$,]/g,
                          "",
                        ),
                      ),
                    )
                  : "—",
              },
              {
                label: "Last Gift Date",
                value: donorRow["Created At"]
                  ? fmtDate(donorRow["Created At"])
                  : "—",
              },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ flex: 1, minWidth: 120 }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: C.muted,
                    mb: 0.4,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
            <Typography
              sx={{
                width: "100%",
                fontSize: 10,
                color: C.muted,
                fontStyle: "italic",
                mt: 0.5,
              }}
            >
              Full transaction history available after enrichment.
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 11, color: C.muted, p: 2 }}>
            No WinRed transactions found.
          </Typography>
        )}
      </Box>

      {!loading && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1,
          }}
        >
          <Typography sx={{ fontSize: 10, color: C.muted }}>
            {(pg - 1) * WR_PAGE_SIZE + 1}–
            {Math.min(pg * WR_PAGE_SIZE, filtered.length)} of {filtered.length}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Box
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: 11,
                cursor: pg === 1 ? "default" : "pointer",
                color: pg === 1 ? C.muted : C.navy,
                border: `1px solid ${C.border}`,
                borderRadius: "4px",
                "&:hover": pg > 1 ? { borderColor: C.navy } : {},
              }}
            >
              ‹ Prev
            </Box>
            <Box
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: 11,
                cursor: pg === totalPages ? "default" : "pointer",
                color: pg === totalPages ? C.muted : C.navy,
                border: `1px solid ${C.border}`,
                borderRadius: "4px",
                "&:hover": pg < totalPages ? { borderColor: C.navy } : {},
              }}
            >
              Next ›
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

const FEC_PAGE_SIZE = 10;

const FecContributions = ({ loading, fec, fmtDate, fmtAmt }) => {
  const [fecPage, setFecPage] = useState(1);
  const [activeCommittee, setActiveCommittee] = useState(null);
  const [showAllFec, setShowAllFec] = useState(false);

  // FEC matches by name, so a common name pulls in strangers. When the backend resolved the collision
  // (fec_verified), default to showing only THIS person's gifts; let the user reveal the raw set.
  const hasVerified = Array.isArray(fec.fec_verified);
  const contributions = useMemo(
    () => (hasVerified && !showAllFec ? fec.fec_verified : fec.contributions || []),
    [fec.contributions, fec.fec_verified, hasVerified, showAllFec],
  );

  const committeeCounts = useMemo(() => {
    const counts = {};
    contributions.forEach((c) => {
      const name = c.committee || "Unknown";
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [contributions]);

  const filtered = useMemo(() => {
    if (!activeCommittee) return contributions;
    return contributions.filter(
      (c) => (c.committee || "Unknown") === activeCommittee,
    );
  }, [contributions, activeCommittee]);

  const totalPages = Math.ceil(filtered.length / FEC_PAGE_SIZE);
  const page = Math.min(fecPage, totalPages || 1);
  const visible = filtered.slice(
    (page - 1) * FEC_PAGE_SIZE,
    page * FEC_PAGE_SIZE,
  );

  const handleChip = (name) => {
    setActiveCommittee((prev) => (prev === name ? null : name));
    setFecPage(1);
  };

  return (
    <>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          color: C.charcoal,
          mb: hasVerified ? 0.25 : 1,
          mt: 2.5,
        }}
      >
        {loading
          ? "Federal Contributions"
          : hasVerified && !showAllFec
            ? `Federal Contributions — verified ${fmtAmt(fec.fec_verified_total)} · ${fec.fec_verified_count} gift${fec.fec_verified_count === 1 ? "" : "s"}`
            : `Federal Contributions (${fec.unique_committees} committees · ${fmtAmt(fec.total_federal)})`}
      </Typography>
      {!loading && hasVerified && (
        <Typography sx={{ fontSize: 9.5, color: C.muted, mb: 1, lineHeight: 1.4 }}>
          ⚠️ Name-collision filtered —{" "}
          {(fec.contributions?.length || 0) - (fec.fec_verified_count || 0)} record(s) belonging to other
          people with this name excluded.{" "}
          <Box
            component="span"
            onClick={() => {
              setShowAllFec((v) => !v);
              setActiveCommittee(null);
              setFecPage(1);
            }}
            sx={{ color: C.navy, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
          >
            {showAllFec ? "show verified only" : "show all raw"}
          </Box>
          {fec.fec_note ? ` · ${fec.fec_note}` : ""}
        </Typography>
      )}

      {/* Committee filter chips */}
      {!loading && committeeCounts.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
          {committeeCounts.map(([name, count]) => {
            const active = activeCommittee === name;
            return (
              <Box
                key={name}
                onClick={() => handleChip(name)}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: 10,
                  fontWeight: 600,
                  px: 1,
                  py: 0.3,
                  borderRadius: "4px",
                  cursor: "pointer",
                  userSelect: "none",
                  bgcolor: active ? C.navy : "#f4f6f9",
                  color: active ? C.white : C.charcoal,
                  border: `1px solid ${active ? C.navy : C.border}`,
                  "&:hover": {
                    borderColor: C.navy,
                    color: active ? C.white : C.navy,
                  },
                }}
              >
                {name}
                <Box
                  component="span"
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    px: 0.6,
                    py: 0.1,
                    borderRadius: "4px",
                    bgcolor: active ? "rgba(255,255,255,0.25)" : C.bg,
                  }}
                >
                  {count}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          bgcolor: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 1.5 }}>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: 2, mb: 0.75 }}>
                <Skeleton variant="text" width="20%" height={16} />
                <Skeleton variant="text" width="15%" height={16} />
                <Skeleton variant="text" width="55%" height={16} />
              </Box>
            ))}
          </Box>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                {["Date", "Amount", "Committee"].map((h) => (
                  <th
                    key={h}
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      padding: "8px 12px",
                      borderBottom: `1px solid ${C.border}`,
                      textAlign: "left",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((c, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 11,
                      color: C.charcoal,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtDate(c.date)}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: C.charcoal,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtAmt(c.amount)}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 11,
                      color: C.charcoal,
                    }}
                  >
                    {c.committee}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1,
          }}
        >
          <Typography sx={{ fontSize: 10, color: C.muted }}>
            {(page - 1) * FEC_PAGE_SIZE + 1}–
            {Math.min(page * FEC_PAGE_SIZE, filtered.length)} of{" "}
            {filtered.length}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Box
              onClick={() => setFecPage((p) => Math.max(1, p - 1))}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: 11,
                cursor: page === 1 ? "default" : "pointer",
                color: page === 1 ? C.muted : C.navy,
                border: `1px solid ${C.border}`,
                borderRadius: "4px",
                "&:hover": page > 1 ? { borderColor: C.navy } : {},
              }}
            >
              ‹ Prev
            </Box>
            <Box
              onClick={() => setFecPage((p) => Math.min(totalPages, p + 1))}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: 11,
                cursor: page === totalPages ? "default" : "pointer",
                color: page === totalPages ? C.muted : C.navy,
                border: `1px solid ${C.border}`,
                borderRadius: "4px",
                "&:hover": page < totalPages ? { borderColor: C.navy } : {},
              }}
            >
              Next ›
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

const ProfileDrawer = ({ personId, email, donorRow, onClose, campaignId, onDeepDive }) => {
  const { authFetch } = useAuth();
  const profileUrl = process.env.REACT_APP_PROFILE_API_URL;
  const pubnubSubKey = process.env.REACT_APP_PUBNUB_SUB_KEY;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusLabel, setStatusLabel] = useState("");
  const [infoModal, setInfoModal] = useState(null);
  const [enrichConfirmOpen, setEnrichConfirmOpen] = useState(false);
  const [winredHistory, setWinredHistory] = useState(null);
  const [cacheChecked, setCacheChecked] = useState(false);
  const pnRef = useRef(null);
  const [personRecord, setPersonRecord] = useState(null);
  const [personReloadKey, setPersonReloadKey] = useState(0);
  const [deepDiveRunning, setDeepDiveRunning] = useState(false);
  const [deepDivePlatform, setDeepDivePlatform] = useState(""); // which platform's read is spinning (x|facebook|instagram)
  const [unlinkingPlatform, setUnlinkingPlatform] = useState(""); // platform being purged as "not this person"
  // Consolidated "Read Social Conversations" run: discover missing networks → deep-dive every present one.
  const [socialRunAll, setSocialRunAll] = useState(false);
  const [socialStep, setSocialStep] = useState({}); // {discover|x|facebook|instagram: 'running'|'done'}
  const [socialStepLabel, setSocialStepLabel] = useState("");
  const [socialRunResult, setSocialRunResult] = useState(null);
  const [deepDiveResult, setDeepDiveResult] = useState(null);
  const [deepDiveStatus, setDeepDiveStatus] = useState("");
  const [deepDiveVoterRead, setDeepDiveVoterRead] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const deepDivePnRef = useRef(null);
  const [findSocialRunning, setFindSocialRunning] = useState(false);
  const [findSocialStatus, setFindSocialStatus] = useState("");
  const [findSocialResult, setFindSocialResult] = useState(null);
  const findSocialPnRef = useRef(null);
  const [findEmailRunning, setFindEmailRunning] = useState(false);
  const [findEmailStatus, setFindEmailStatus] = useState("");
  const [findEmailResult, setFindEmailResult] = useState(null);
  const findEmailPnRef = useRef(null);

  // This drawer is the DETAIL VIEW of a person record — keyed on personId, period. Email is just a column
  // on the person we load here; it's used ONLY as the match string the external enrichment provider (PDL/FEC)
  // requires — never as an identity or lookup key from the caller.
  const person = personRecord?.person || null;
  const effEmail = email || person?.email || "";
  // ALL contacts we hold (child tables, with provenance) — a person can have several from different sources.
  // Fall back to the single effEmail/phone when the detail response predates the emails/phones arrays.
  const personEmails =
    personRecord?.emails?.length
      ? personRecord.emails
      : effEmail
        ? [{ email: effEmail, source: null }]
        : profile?.email
          ? [{ email: profile.email, source: "enrich" }]
          : [];
  const personPhones =
    personRecord?.phones?.length
      ? personRecord.phones
      : person?.phone
        ? [{ phone: person.phone, source: null }]
        : [];
  const personIssues = personRecord?.issues || [];
  const personSignals = personRecord?.signals || [];
  const personFollowers = personRecord?.followers || 0;
  const personSocialStats = personRecord?.social_stats || null;
  // Linked handles across platforms (X + FB/IG from find-social) → per-platform deep-dive buttons.
  const personHandles = personRecord?.handles || [];
  // Per-platform presence for the header chip row + Social Presence grid, built RESILIENTLY so it renders
  // even if a piece of the API response is older: start from the rich `social_presence` array (handle + bio
  // + location + stats) when present, fall back to `handles` (always returned) for any platform it omits,
  // and always fold in the primary X profile from the person record. Ordered X → FB → IG → Nextdoor.
  const socialPresence = (() => {
    const byPlatform = {};
    const isCleanHandle = (u) =>
      u && !/^https?:/i.test(u) && !u.includes("/") && !/\.com/i.test(u);
    (personRecord?.social_presence || []).forEach((sp) => {
      if (!sp?.platform) return;
      const prev = byPlatform[sp.platform];
      if (!prev) {
        byPlatform[sp.platform] = sp;
        return;
      }
      // Duplicate platform (e.g. two Facebook rows): keep the one with more posts, but adopt a clean
      // vanity handle from the sibling if the richer one only has a URL/synthetic identity.
      const rich = (sp.stats?.posts || 0) >= (prev.stats?.posts || 0) ? sp : prev;
      const other = rich === sp ? prev : sp;
      byPlatform[sp.platform] = {
        ...rich,
        username: isCleanHandle(rich.username)
          ? rich.username
          : isCleanHandle(other.username)
            ? other.username
            : rich.username,
      };
    });
    personHandles.forEach((h) => {
      if (h?.platform && !byPlatform[h.platform]) {
        byPlatform[h.platform] = {
          platform: h.platform,
          username: h.username || "",
          platform_user_id: h.platform_user_id || "",
          bio: "",
          location: "",
          stats: {},
        };
      }
    });
    // The primary X profile lives on the person record (username/bio/followers), not always in handles.
    if (person?.username && !byPlatform.x) {
      byPlatform.x = {
        platform: "x",
        username: person.username,
        platform_user_id: person.platform_user_id || "",
        bio: person.bio || "",
        location: person.location_raw || "",
        stats: personSocialStats || { followers: personFollowers },
      };
    }
    return PLATFORM_ORDER.map((p) => byPlatform[p]).filter(Boolean);
  })();
  // "Broken" evidence — signals with NO post text AND no link (a source-less classify leftover). They carry
  // no real content, so they're hidden from Evidence and offered up for a one-click purge.
  const orphanedSignals = personSignals.filter((s) => !s.text && !s.source_url);
  // Only signals with something to actually show — real post text or a working permalink.
  const visibleSignals = personSignals.filter((s) => s.text || s.source_url);
  // Campaign Fit — the stored scoring for this person × campaign (present once score-campaign has run).
  const personFit = personRecord?.fit || null;
  // Prefer a just-generated Voter Read from the deep-dive's `done` message (instant, no DB round-trip),
  // falling back to the persisted one on the record.
  const voterRead = deepDiveVoterRead || personRecord?.person?.voter_read || null;
  // Fallback stance per topic from the latest signal, for when the person_topics rollup has no stance.
  const stanceBySlug = {};
  personSignals.forEach((s) => {
    if (s.topic_slug && s.stance && !stanceBySlug[s.topic_slug])
      stanceBySlug[s.topic_slug] = s.stance;
  });
  const titleCase = (s) =>
    (s || "").replace(/(^|[\s-])\w/g, (c) => c.toUpperCase());

  const channel = effEmail
    ? `profile-${effEmail.replace(/[^a-zA-Z0-9]/g, "-")}`
    : (personId || personRecord?.person?.id)
      ? `profile-pid-${personId || personRecord?.person?.id}`
      : null;

  // Load the canonical person record — by id, else resolve by email (so the Donor list, which opens by
  // email, gets the SAME full drawer). identity + voter registration + issue profile + donations.
  useEffect(() => {
    const key = personId
      ? `id=${personId}`
      : email
        ? `email=${encodeURIComponent(email)}`
        : null;
    if (!key || !PHP_API_URL) {
      setPersonRecord(null);
      return;
    }
    setPersonRecord(null);
    authFetch(
      `${PHP_API_URL}/identity/persons?${key}${campaignId ? `&campaign_id=${campaignId}` : ""}`,
    )
      .then((r) => r.json())
      .then((d) => setPersonRecord(d && !d.error ? d : null))
      .catch(() => setPersonRecord(null));
  }, [personId, email, campaignId, personReloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the previous person's deep-dive report when the drawer switches to a DIFFERENT person. Keyed on
  // identity ONLY (not personReloadKey) so the "✓ N signals" success message survives its own post-dive reload.
  useEffect(() => {
    setDeepDiveResult(null);
    setDeepDiveStatus("");
  }, [personId, email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effective person id — the prop, or the id resolved from the loaded record (email-opened case). Used by
  // the deep-dive / find-social actions, which key on person_id.
  const effPersonId = personId || personRecord?.person?.id || null;

  // Drop any in-memory Voter Read when switching to a different person.
  useEffect(() => {
    setDeepDiveVoterRead(null);
  }, [personId]);

  // PubNub subscription — set up before fetch so no messages are missed
  useEffect(() => {
    if (!channel || !pubnubSubKey) return;
    const pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
    pnRef.current = pn;
    const listener = {
      message: ({ message: msg }) => {
        const { type, data } = msg;
        if (type === "pdl") setProfile((p) => ({ ...(p || {}), pdl: data }));
        if (type === "fec") setProfile((p) => ({ ...(p || {}), fec: data }));
        if (type === "winred")
          setProfile((p) => ({ ...(p || {}), winred: data }));
        if (type === "disc") setProfile((p) => ({ ...(p || {}), disc: data }));
        if (type === "status") setStatusLabel(data.label);
        if (type === "done") {
          setProfile(data);
          setLoading(false);
          setStatusLabel("");
        }
        if (type === "error") {
          setError(data.message);
          setLoading(false);
          setStatusLabel("");
        }
      },
    };
    pn.addListener(listener);
    pn.subscribe({ channels: [channel] });
    return () => {
      pn.removeListener(listener);
      pn.unsubscribeAll();
      pn.destroy();
      pnRef.current = null;
    };
  }, [channel, pubnubSubKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = (force = false) => {
    if (!profileUrl) {
      setError("REACT_APP_PROFILE_API_URL not configured");
      return;
    }
    const enrichPid = personId || personRecord?.person?.id || null;
    if (!effEmail && !enrichPid) return; // nothing to enrich against
    setLoading(true);
    setProfile(null);
    setError(null);
    setStatusLabel("Connecting…");
    // No email on file → enrich BY person_id (finding the email/phone is the whole point). enrichPerson runs
    // the full canonical waterfall server-side and streams 'done' over `channel` (pid-based here).
    if (!effEmail && enrichPid) {
      authFetch(`${profileUrl}/enrich-person`, {
        method: "POST",
        body: JSON.stringify({ person_id: enrichPid, campaign_id: campaignId || 0, channel: channel || "" }),
      })
        .then((r) => {
          if (r.status === 202 || r.status === 200) { setStatusLabel("Enriching… this takes ~60s"); return null; }
          return r.json();
        })
        .then((d) => { if (d && !d.error) { setProfile(d); setLoading(false); setStatusLabel(""); } })
        .catch((e) => { setError(e.message); setLoading(false); setStatusLabel(""); });
      return;
    }
    const firstName = encodeURIComponent(donorRow?.["First Name"] || "");
    const lastName = encodeURIComponent(donorRow?.["Last Name"] || "");
    const cohort = encodeURIComponent(donorRow?.["Cohort"] || "");
    const zip = encodeURIComponent(
      (donorRow?.["Zip"] || donorRow?.["zip_code"] || "")
        .toString()
        .slice(0, 5),
    );
    const state = encodeURIComponent(
      donorRow?.["State"] || donorRow?.["state"] || "",
    );
    const ch = encodeURIComponent(channel || "");
    const handle = encodeURIComponent(person?.username || "");
    const url = `${profileUrl}?email=${encodeURIComponent(effEmail)}&first_name=${firstName}&last_name=${lastName}${cohort ? `&cohort=${cohort}` : ""}${zip ? `&zip=${zip}` : ""}${state ? `&state=${state}` : ""}${handle ? `&handle=${handle}` : ""}${ch ? `&channel=${ch}` : ""}${force ? "&force=true" : ""}`;
    authFetch(url)
      .then((r) => {
        // PHP returns 202 (enriching in background); the old Lambda returned 503. Either way the work
        // continues server-side and completes via PubNub — keep loading=true and wait for the "done" msg.
        if (r.status === 503 || r.status === 202) {
          setStatusLabel("Enriching… this takes ~60s");
          return null;
        }
        return r.json();
      })
      .then((d) => {
        if (!d) return;
        setProfile(d);
        setLoading(false);
        setStatusLabel("");
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
        setStatusLabel("");
      });
  };

  // X deep-dive — the endpoint returns 202 immediately and streams progress + result over PubNub
  // (X full-archive + Claude far exceeds the proxy timeout, so we can't await it). We subscribe to
  // `deepdive-{personId}`, show progress, and on "done" reload the record so the Issue Profile + Activity
  // feed reflect the new signals in place.
  const cleanupDeepDivePn = () => {
    try {
      deepDivePnRef.current?.unsubscribeAll();
      deepDivePnRef.current?.destroy();
    } catch (e) {
      /* noop */
    }
    deepDivePnRef.current = null;
  };
  // Purge "broken" evidence — signals with no backing post (source-less classify leftovers) that inflate an
  // issue and render as blank, link-less rows. Deletes them + recomputes issues, then reloads.
  const runPurgeOrphaned = async () => {
    if (!PHP_API_URL || !effPersonId || unlinkingPlatform) return;
    setUnlinkingPlatform("__orphaned__");
    try {
      await authFetch(`${PHP_API_URL}/identity/purge-orphaned`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ person_id: effPersonId }),
      });
    } catch (e) {
      /* reload anyway */
    } finally {
      setPersonReloadKey((k) => k + 1);
      setUnlinkingPlatform("");
    }
  };

  // Promise wrappers around the async endpoints (which stream progress + a terminal message over PubNub),
  // so the consolidated run can await each step in sequence and drive per-platform status.
  const awaitPubnubJob = (channel, url, body, timeoutMs, onProgress) =>
    new Promise((resolve) => {
      let pn = null;
      const done = (r) => {
        clearTimeout(guard);
        try {
          pn?.unsubscribeAll?.();
          pn?.stop?.();
        } catch (e) {
          /* noop */
        }
        resolve(r || {});
      };
      const guard = setTimeout(() => done({ timeout: true }), timeoutMs);
      try {
        if (pubnubSubKey) {
          pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
          pn.addListener({
            message: ({ channel: ch, message: m }) => {
              if (ch !== channel) return;
              if (m.type === "progress") onProgress?.(m.label || "Working…");
              else if (m.type === "done") done(m.data || {});
              else if (m.type === "error")
                done({ error: m.data?.message || "failed" });
            },
          });
          pn.subscribe({ channels: [channel] });
        }
        authFetch(`${PHP_API_URL}${url}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }).catch(() => {});
      } catch (e) {
        done({ error: "start failed" });
      }
    });

  // One button: (a) find networks we don't have yet (find-social), then (b) deep-dive every present account,
  // reporting each step's status. This is what most users want — read ALL their social, not one channel.
  const runAllSocial = async () => {
    if (!PHP_API_URL || !effPersonId || socialRunAll || deepDiveRunning) return;
    setSocialRunAll(true);
    setSocialRunResult(null);
    setSocialStep({});
    const mark = (k, v) => setSocialStep((s) => ({ ...s, [k]: v }));
    const targets = []; // {platform, handle}
    const addTarget = (platform, handle) => {
      if (handle && !targets.find((t) => t.platform === platform))
        targets.push({ platform, handle });
    };
    // Seed from what's already linked.
    if (person?.username) addTarget("x", person.username);
    socialPresence.forEach((sp) => {
      if (sp.platform === "facebook" || sp.platform === "instagram") {
        const loc =
          sp.username ||
          (sp.platform === "facebook" ? sp.platform_user_id : "");
        addTarget(sp.platform, loc);
      }
    });

    // (a) Discover — only worth it when we have an email to anchor the web search.
    if (effEmail) {
      mark("discover", "running");
      setSocialStepLabel("Finding social accounts…");
      const fs = await awaitPubnubJob(
        `findsocial-${effPersonId}`,
        "/social/find-social",
        { person_id: effPersonId },
        120000,
        setSocialStepLabel,
      );
      (fs.linked || []).forEach((l) => {
        if (["x", "facebook", "instagram"].includes(l.platform))
          addTarget(l.platform, l.username);
      });
      mark("discover", "done");
    }

    // (b) Fire ALL deep-dives AT ONCE. They each 202 and stream over the shared `deepdive-{personId}`
    //     channel; every terminal message carries its `platform`, so one subscription can route completions
    //     and count down — the run is done when the last account's account reports in (or the guard trips).
    const totalSignals = await new Promise((resolve) => {
      if (!targets.length) return resolve(0);
      const chan = `deepdive-${effPersonId}`;
      const pending = new Set(targets.map((t) => t.platform));
      let signals = 0;
      let pn = null;
      const finish = () => {
        clearTimeout(guard);
        try {
          pn?.unsubscribeAll?.();
          pn?.stop?.();
        } catch (e) {
          /* noop */
        }
        resolve(signals);
      };
      const complete = (platform, n) => {
        if (!platform || !pending.has(platform)) return;
        pending.delete(platform);
        signals += Number(n || 0);
        mark(platform, "done"); // per-platform spinners already show which are still running
        if (pending.size === 0) finish();
      };
      // Hard stop so a dropped PubNub message can't hang the button forever.
      const guard = setTimeout(finish, 210000);
      try {
        if (pubnubSubKey) {
          pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
          pn.addListener({
            message: ({ channel: ch, message: m }) => {
              if (ch !== chan) return;
              if (m.type === "done") complete(m.data?.platform, m.data?.signals);
              else if (m.type === "error") complete(m.data?.platform, 0);
            },
          });
          pn.subscribe({ channels: [chan] });
        }
        // Kick them all off concurrently.
        targets.forEach((t) => {
          mark(t.platform, "running");
          authFetch(`${PHP_API_URL}/social/deep-dive`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              person_id: effPersonId,
              platform: t.platform,
              handle: t.handle,
            }),
          }).catch(() => {});
        });
        setSocialStepLabel("Reading social accounts…");
      } catch (e) {
        finish();
      }
    });

    setSocialStepLabel("");
    setSocialRunAll(false);
    setSocialRunResult({
      platforms: targets.length,
      signals: totalSignals,
      discovered: effEmail,
    });
    setPersonReloadKey((k) => k + 1);
  };

  const runDeepDive = async (platform = "x", handle = "") => {
    if (!PHP_API_URL || !effPersonId || deepDiveRunning) return;
    setDeepDiveRunning(true);
    setDeepDivePlatform(platform);
    setDeepDiveResult(null);
    setDeepDiveStatus("Starting…");
    const chan = `deepdive-${effPersonId}`;
    cleanupDeepDivePn();

    // Safety net: if PubNub never delivers a terminal message, stop spinning after 3 min and reload.
    const guard = setTimeout(() => {
      if (deepDivePnRef.current) {
        cleanupDeepDivePn();
        setDeepDiveRunning(false);
        setDeepDivePlatform("");
        setPersonReloadKey((k) => k + 1);
        setDeepDiveStatus("");
      }
    }, 180000);

    const finish = (updater) => {
      clearTimeout(guard);
      cleanupDeepDivePn();
      setDeepDiveRunning(false);
      setDeepDivePlatform("");
      updater();
    };

    try {
      if (pubnubSubKey) {
        const pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
        deepDivePnRef.current = pn;
        pn.addListener({
          message: ({ channel: ch, message: m }) => {
            if (ch !== chan) return;
            if (m.type === "progress") setDeepDiveStatus(m.label || "Working…");
            else if (m.type === "done") {
              finish(() => {
                setDeepDiveResult(m.data || {});
                if (m.data?.voter_read) setDeepDiveVoterRead(m.data.voter_read);
                setPersonReloadKey((k) => k + 1);
                setDeepDiveStatus("");
              });
            } else if (m.type === "error") {
              finish(() => {
                setDeepDiveResult({ error: m.data?.message || "deep-dive failed" });
                setDeepDiveStatus("");
              });
            }
          },
        });
        pn.subscribe({ channels: [chan] });
      }

      const r = await authFetch(`${PHP_API_URL}/social/deep-dive`, {
        method: "POST",
        body: JSON.stringify({
          person_id: effPersonId,
          campaign_id: campaignId,
          platform,
          handle: handle || person?.username || "",
        }),
      });
      // 202 = accepted, running in background; anything else (400/404/500) is a real error.
      if (r.status !== 202) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
      if (!pubnubSubKey) {
        // No realtime channel — just reload after a delay so signals show up.
        setTimeout(() => finish(() => setPersonReloadKey((k) => k + 1)), 45000);
      }
    } catch (e) {
      finish(() => setDeepDiveResult({ error: e.message }));
    }
  };
  // Tear down the deep-dive PubNub instance if the drawer closes mid-run.
  useEffect(
    () => () => {
      try {
        deepDivePnRef.current?.unsubscribeAll();
        deepDivePnRef.current?.destroy();
      } catch (e) {
        /* noop */
      }
      deepDivePnRef.current = null;
    },
    [],
  );

  // Find Social Handles — from name + email (requires email). 202 + PubNub, like the deep-dive; on done we
  // reload the record so the newly-linked handle (and the now-available deep-dive) appear in place.
  const cleanupFindSocialPn = () => {
    try {
      findSocialPnRef.current?.unsubscribeAll();
      findSocialPnRef.current?.destroy();
    } catch (e) {
      /* noop */
    }
    findSocialPnRef.current = null;
  };
  const runFindSocial = async () => {
    if (!PHP_API_URL || !effPersonId || findSocialRunning || !effEmail) return;
    setFindSocialRunning(true);
    setFindSocialResult(null);
    setFindSocialStatus("Starting…");
    const chan = `findsocial-${effPersonId}`;
    cleanupFindSocialPn();
    const guard = setTimeout(() => {
      if (findSocialPnRef.current) {
        cleanupFindSocialPn();
        setFindSocialRunning(false);
        setPersonReloadKey((k) => k + 1);
        setFindSocialStatus("");
      }
    }, 120000);
    const finish = (updater) => {
      clearTimeout(guard);
      cleanupFindSocialPn();
      setFindSocialRunning(false);
      updater();
    };
    try {
      if (pubnubSubKey) {
        const pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
        findSocialPnRef.current = pn;
        pn.addListener({
          message: ({ channel: ch, message: m }) => {
            if (ch !== chan) return;
            if (m.type === "progress") setFindSocialStatus(m.label || "Working…");
            else if (m.type === "done")
              finish(() => {
                setFindSocialResult(m.data || {});
                setPersonReloadKey((k) => k + 1);
                setFindSocialStatus("");
              });
            else if (m.type === "error")
              finish(() => {
                setFindSocialResult({ error: m.data?.message || "search failed" });
                setFindSocialStatus("");
              });
          },
        });
        pn.subscribe({ channels: [chan] });
      }
      const r = await authFetch(`${PHP_API_URL}/social/find-social`, {
        method: "POST",
        body: JSON.stringify({ person_id: effPersonId, campaign_id: campaignId }),
      });
      if (r.status !== 202) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
      if (!pubnubSubKey)
        setTimeout(() => finish(() => setPersonReloadKey((k) => k + 1)), 30000);
    } catch (e) {
      finish(() => setFindSocialResult({ error: e.message }));
    }
  };
  useEffect(() => () => cleanupFindSocialPn(), []);

  // Find Email — from name + handle + location (Flow B). Runs when there's NO email. 202 + PubNub.
  const cleanupFindEmailPn = () => {
    try {
      findEmailPnRef.current?.unsubscribeAll();
      findEmailPnRef.current?.destroy();
    } catch (e) {
      /* noop */
    }
    findEmailPnRef.current = null;
  };
  const runFindEmail = async () => {
    if (!PHP_API_URL || !effPersonId || findEmailRunning) return;
    setFindEmailRunning(true);
    setFindEmailResult(null);
    setFindEmailStatus("Starting…");
    const chan = `findemail-${effPersonId}`;
    cleanupFindEmailPn();
    const guard = setTimeout(() => {
      if (findEmailPnRef.current) {
        cleanupFindEmailPn();
        setFindEmailRunning(false);
        setPersonReloadKey((k) => k + 1);
        setFindEmailStatus("");
      }
    }, 120000);
    const finish = (updater) => {
      clearTimeout(guard);
      cleanupFindEmailPn();
      setFindEmailRunning(false);
      updater();
    };
    try {
      if (pubnubSubKey) {
        const pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
        findEmailPnRef.current = pn;
        pn.addListener({
          message: ({ channel: ch, message: m }) => {
            if (ch !== chan) return;
            if (m.type === "progress") setFindEmailStatus(m.label || "Working…");
            else if (m.type === "done")
              finish(() => {
                setFindEmailResult(m.data || {});
                setPersonReloadKey((k) => k + 1);
                setFindEmailStatus("");
              });
            else if (m.type === "error")
              finish(() => {
                setFindEmailResult({ error: m.data?.message || "search failed" });
                setFindEmailStatus("");
              });
          },
        });
        pn.subscribe({ channels: [chan] });
      }
      const r = await authFetch(`${PHP_API_URL}/social/find-email`, {
        method: "POST",
        body: JSON.stringify({ person_id: effPersonId, campaign_id: campaignId }),
      });
      if (r.status !== 202) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
      if (!pubnubSubKey)
        setTimeout(() => finish(() => setPersonReloadKey((k) => k + 1)), 30000);
    } catch (e) {
      finish(() => setFindEmailResult({ error: e.message }));
    }
  };
  useEffect(() => () => cleanupFindEmailPn(), []);

  // Check S3 cache for a prior enrichment — the external provider caches by email (its match key).
  useEffect(() => {
    // Clear the previous person's enrichment FIRST, always — otherwise opening a no-email record (a social
    // lead) hits the early-return below without resetting `profile`, leaving the prior card in state so the
    // identity guard flags it against the new record (the transient "identity mismatch" on the next person).
    setProfile(null);
    if (!effEmail || !profileUrl) {
      setCacheChecked(true); // nothing to enrich against — don't leave the Summary stuck loading
      return;
    }
    setCacheChecked(false);
    authFetch(`${profileUrl}?email=${encodeURIComponent(effEmail)}&cache_only=true`)
      .then((r) => (r.status === 200 ? r.json() : null))
      .then((d) => {
        if (d && !d.error) setProfile(d);
      })
      .catch(() => {})
      .finally(() => setCacheChecked(true));
  }, [effEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Donation history is loaded BY person_id from the person record (donations.voter_id = persons.id) — no
  // by-email lookup. Shaped into the WinRed CSV keys the WinRedDonations table renders.
  const winredUrl = PHP_API_URL ? `${PHP_API_URL}/winred` : null;
  useEffect(() => {
    if (personId) {
      // person-id path: donations already came back on the person record
      if (!personRecord) return; // still loading
      const tx = (personRecord.donations || []).map((d) => ({
        "Created At": d.donated_at,
        Amount: d.amount,
        Source: d.source || "",
      }));
      setWinredHistory({
        transactions: tx,
        count: personRecord.donation_count ?? tx.length,
        total: personRecord.donation_total ?? 0,
      });
      return;
    }
    // Legacy fallback (donor tab opens with email only, no person id yet)
    if (!effEmail || !winredUrl || !campaignId) return;
    setWinredHistory(null);
    authFetch(
      `${winredUrl}?type=donations&campaign_id=${campaignId}&email=${encodeURIComponent(effEmail)}`,
    )
      .then((r) => r.json())
      .then((d) => setWinredHistory(d))
      .catch(() => setWinredHistory({ transactions: [], count: 0, total: 0 }));
  }, [personId, personRecord, effEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtAmt = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return s || "—";
    }
  };
  const fmtTime = (s) => {
    try {
      const d = new Date(s);
      return `${fmtDate(s)} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
    } catch {
      return s || "—";
    }
  };
  const fmtPhone = (v) => {
    if (!v) return "—";
    const d = String(v).replace(/\D/g, "");
    const n = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
    return n.length === 10
      ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`
      : v;
  };

  // ── Enrichment identity guard ──────────────────────────────────────────────
  // External enrichment (PDL identity + FEC name-search) matches by email/name and can attach a DIFFERENT
  // person's record (name collisions, bad PDL email data). If we KNOW who this person is (from their record)
  // and the enrichment's returned name doesn't match, the enrichment is untrustworthy — suppress ALL of it
  // rather than show a stranger's giving history / DISC / ask.
  const rawPdl = profile?.pdl || {};
  const knownLast = (
    person?.last_name ||
    (person?.social_name || person?.full_name || "").trim().split(/\s+/).pop() ||
    ""
  ).toLowerCase();
  const enrichedName = String(rawPdl.full_name || "").toLowerCase();
  const enrichMismatch = !!(
    person &&
    knownLast &&
    knownLast.length > 2 &&
    enrichedName &&
    !enrichedName.includes(knownLast)
  );
  const enrichOk = !enrichMismatch;

  const pdl = enrichOk ? rawPdl : {};
  const disc = enrichOk ? profile?.disc || {} : {};
  const fec = enrichOk ? profile?.fec || {} : {};
  const winred = profile?.winred || {};
  const summary = enrichOk ? profile?.summary || "" : "";
  const summaryHeadline = enrichOk ? profile?.summary_headline || "" : "";
  const nextAsk = enrichOk ? profile?.next_ask || "" : "";
  const nextAskRationale = enrichOk ? profile?.next_ask_rationale || "" : "";
  const donateButtons = enrichOk ? profile?.donate_buttons || [] : [];
  const donateDefault = enrichOk ? profile?.donate_default || 0 : 0;
  const donateButtonLabels = enrichOk ? profile?.donate_button_labels || [] : [];
  const donateButtonRationale = enrichOk
    ? profile?.donate_button_rationale || ""
    : "";
  const donateDiscFrame = enrichOk ? profile?.donate_disc_frame || "" : "";
  // Charity is FIRST-PARTY person data (person_charity) — comes with the person record, so it shows
  // even if donor-enrichment never ran. Fall back to the enrichment blob only if the record lacks it.
  const charity =
    personRecord?.charity || (enrichOk ? profile?.charity || null : null) || null;
  const compliance = enrichOk ? profile?.compliance || null : null;
  const complianceRedirects = enrichOk ? profile?.compliance_redirects || null : null;
  const fmtUsd0 = (n) =>
    "$" + Math.round(Number(n) || 0).toLocaleString("en-US");
  // Per-person spend: identity enrichment (Grok + brokers + AI) + "social read" = deep-diving their posts on
  // any network (X native, or Facebook/Instagram/NextDoor via Apify) — fetch cost + AI classification.
  const fmtCost = (n) => "$" + Number(n || 0).toFixed(2);
  const encRuns = person?.enrichment_runs || 0;
  const encCost = Number(person?.enrichment_cost || 0);
  const srRuns = person?.socialread_runs || 0;
  const srXapi = Number(person?.socialread_xapi_cost || 0);
  const srAi = Number(person?.socialread_ai_cost || 0);
  const srCost = srXapi + srAi;
  const totalSpend = encCost + srCost;

  // WinRed self-reported name has proper casing; PDL often returns lowercase
  const toTitle = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());
  // PDL can return boolean true/false for location fields — guard both string and boolean
  const safeVal = (s) =>
    typeof s === "string" && s && s !== "true" && s !== "false" ? s : "";
  const rowName =
    `${donorRow?.["First Name"] || person?.first_name || ""} ${donorRow?.["Last Name"] || person?.last_name || ""}`.trim() ||
    (person?.full_name || "");
  const displayName = rowName || toTitle(pdl.full_name || "") || effEmail;
  const titleLine = [pdl.job_title, pdl.company].filter(Boolean).join(" at ");
  const locationLine = [
    safeVal(pdl.city) || safeVal(donorRow?.City) || safeVal(person?.city),
    safeVal(pdl.state) || safeVal(donorRow?.State) || safeVal(person?.state),
  ]
    .filter(Boolean)
    .join(", ");
  const photoUrl = pdl.photo_url || "";
  const initials = (displayName || "")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const discType = disc.disc_type || "";
  const discColor = DISC_COLORS[discType] || C.muted;
  const discArchetype = disc.archetype || "";
  const discConfidence = disc.confidence || "";
  const discSignals = disc.signals || [];
  const discSecondary = disc.disc_secondary || "";
  const discSource = disc._source || "";
  const cohort = donorRow?.Cohort || profile?.cohort || "";

  const SectionLabel = ({ children }) => (
    <Typography
      sx={{ fontSize: 10, fontWeight: 800, color: C.charcoal, mb: 1, mt: 2.5 }}
    >
      {children}
    </Typography>
  );

  const contactCellStyle = {
    flex: 1,
    border: `1px solid ${C.border}`,
    borderRadius: "6px",
    p: 1.25,
    display: "flex",
    flexDirection: "column",
    gap: 0.25,
    bgcolor: C.white,
  };

  // First-party brief, computed from the record we loaded by id — so the Summary is never blank even before
  // (or without) external AI enrichment. This is what we KNOW, said plainly.
  const notVoted = (v) =>
    !v || /^(n|no|0|false|did not vote)$/i.test(String(v).trim());
  // Classify what this person IS — voter-match beats donor beats social-lead. "Social lead" only applies
  // to a person_type='lead' with no contact, NOT to a donor who simply isn't voter-matched.
  const isDonorRec = (personRecord?.donation_count || 0) > 0;
  const srcLabel = /winred/i.test(person?.origin_source || "")
    ? "WinRed"
    : /social/i.test(person?.origin_source || "")
      ? "Social"
      : "";
  const personDescriptor = (() => {
    if (!person) return "";
    if (person.party || person.county || person.general_last || person.cd)
      return "Voter-file matched";
    if (isDonorRec) return `${srcLabel || "WinRed"} donor`;
    if (person.person_type === "lead" || (!effEmail && !person.phone))
      return "Social lead (not yet voter-matched)";
    return `${srcLabel ? srcLabel + " " : ""}contact on file`;
  })();
  const personBrief = (() => {
    if (!person) return "";
    const parts = [];
    const loc = locationLine || person.city || "";
    parts.push(
      `${personDescriptor}` +
        `${loc ? ` — ${loc}` : ""}` +
        `${person.county ? ` (${person.county} County)` : ""}` +
        `${person.party ? `, registered ${person.party}` : ""}.`,
    );
    if (person.general_last || person.general_previous) {
      const vl = !notVoted(person.general_last);
      const vp = !notVoted(person.general_previous);
      parts.push(
        vl && vp
          ? "Consistent voter — turned out in both recent generals."
          : vl && !vp
            ? "Intermittent voter — voted in the last general, missed the prior."
            : !vl && vp
              ? "Lapsing voter — missed the last general after voting before."
              : "Low-propensity — no recent general turnout on file.",
      );
    }
    const dc = personRecord?.donation_count || 0;
    parts.push(
      dc > 0
        ? `Donor — ${dc} gift${dc > 1 ? "s" : ""} totaling ${fmtAmt(personRecord.donation_total)}.`
        : "No donation history on file.",
    );
    if (personIssues.length) {
      const top = personIssues
        .slice(0, 3)
        .map((i) => (i.stance_label ? `${i.label} (${i.stance_label})` : i.label))
        .join(", ");
      parts.push(
        `Issue focus: ${top}${personIssues.length > 3 ? `, +${personIssues.length - 3} more` : ""}.`,
      );
    }
    if (person.username) {
      parts.push(
        `Active on X as @${person.username}` +
          `${personFollowers ? ` (${personFollowers.toLocaleString()} followers)` : ""}` +
          `${personSignals.length ? `, ${personSignals.length} tracked post${personSignals.length > 1 ? "s" : ""}` : ""}.`,
      );
    }
    return parts.join(" ");
  })();

  // TLDR one-liner shown at the very top of every profile. Voter Read headline (issue identity) leads when
  // present; else the donor AI headline; else a computed one so no profile is ever headline-less.
  const tldr =
    voterRead?.headline ||
    summaryHeadline ||
    (() => {
      if (!person) return "";
      const bits = [];
      const hasReg = person.county || person.party || person.general_last;
      const voterLine = person.general_last || person.general_previous
        ? (!notVoted(person.general_last) ? "Active voter" : "Low-propensity voter")
        : hasReg
          ? "Registered voter"
          : isDonorRec
            ? srcLabel || "Donor"
            : person.person_type === "lead" || (!effEmail && !person.phone)
              ? "Social lead"
              : "Contact";
      const loc =
        (person.county && `${person.county} County`) ||
        locationLine ||
        person.state ||
        "";
      bits.push([voterLine, person.party, loc].filter(Boolean).join(" · "));
      const dc = personRecord?.donation_count || 0;
      bits.push(
        dc > 0 ? `${fmtAmt(personRecord.donation_total)} donor` : "Non-donor",
      );
      if (personIssues.length) {
        bits.push(
          personIssues
            .slice(0, 2)
            .map((i) => (i.stance_label ? `${i.label} (${i.stance_label})` : i.label))
            .join(", "),
        );
      }
      return bits.filter(Boolean).join("  ·  ");
    })();

  return (
    <>
      <Drawer
        anchor="right"
        open={!!(personId || email)}
        onClose={onClose}
        transitionDuration={300}
        PaperProps={{
          sx: {
            width: { xs: "100vw", md: "50vw" },
            minWidth: { xs: 0, md: 420 },
            display: "flex",
            flexDirection: "column",
            bgcolor: "#f9fafb",
          },
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            bgcolor: C.white,
            px: 3,
            py: 2.5,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "flex-start" },
              gap: 2,
            }}
          >
            {/* Avatar + Name wrapper */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                flex: 1,
              }}
            >
              {/* Avatar */}
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  overflow: "hidden",
                  flexShrink: 0,
                  bgcolor: C.navy,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={displayName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Typography
                    sx={{ fontSize: 18, fontWeight: 800, color: C.white }}
                  >
                    {initials}
                  </Typography>
                )}
              </Box>

              {/* Name / title */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {loading ? (
                  <>
                    <Skeleton variant="text" width="55%" height={22} />
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={16}
                      sx={{ mt: 0.5 }}
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: 17,
                        fontWeight: 800,
                        color: C.charcoal,
                        lineHeight: 1.25,
                      }}
                    >
                      {displayName}
                    </Typography>
                    {titleLine && (
                      <Typography
                        sx={{ fontSize: 12, color: C.muted, mt: 0.3 }}
                      >
                        {titleLine}
                      </Typography>
                    )}
                    {person && locationLine && (
                      <Typography
                        sx={{ fontSize: 12, color: C.muted, mt: 0.3 }}
                      >
                        {locationLine}
                      </Typography>
                    )}
                    {/* Multi-platform social row — one branded chip per linked network + its follower count
                        (compact). Each chip links to that profile; follower count shows once a dive has run. */}
                    {socialPresence.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: 0.9,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        {socialPresence.map((sp) => {
                          const meta = PLATFORM_META[sp.platform];
                          if (!meta) return null;
                          const url = platformUrl(
                            sp.platform,
                            sp.username,
                            sp.platform_user_id,
                          );
                          const followers = sp.stats?.followers || sp.stats?.friends || 0;
                          return (
                            <Box
                              key={sp.platform}
                              component={url ? "a" : "div"}
                              href={url || undefined}
                              target={url ? "_blank" : undefined}
                              rel={url ? "noreferrer" : undefined}
                              title={`${meta.label}${sp.username ? " @" + sp.username : ""}`}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                textDecoration: "none",
                              }}
                            >
                              <PlatformIcon platform={sp.platform} size={24} radius={6} />
                              {followers > 0 && (
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: C.charcoal,
                                  }}
                                >
                                  {fmtCompact(followers)}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mt: 1,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {cohort && (
                    <CohortBadge
                      cohort={cohort}
                      onClick={
                        COHORT_INFO[cohort]
                          ? () =>
                              setInfoModal({
                                ...COHORT_INFO[cohort],
                                _isCohort: true,
                              })
                          : undefined
                      }
                    />
                  )}
                  {!loading && discType && (
                    <Box
                      component="span"
                      onClick={() => setInfoModal(DISC_INFO[discType])}
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        px: 1,
                        py: 0.25,
                        borderRadius: "4px",
                        bgcolor: discColor + "18",
                        border: `1px solid ${discColor}50`,
                        color: discColor,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": { bgcolor: discColor + "28" },
                      }}
                    >
                      DISC {discType} · {DISC_LABELS[discType]}
                    </Box>
                  )}
                  {loading && (
                    <Skeleton variant="rounded" width={80} height={18} />
                  )}
                </Box>
              </Box>
            </Box>
            {/* end Avatar+Name wrapper */}

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                order: { xs: -1, md: 3 },
                justifyContent: "flex-end",
              }}
            >
              {!loading && pdl.linkedin_url && (
                <Typography
                  component="a"
                  href={pdl.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    fontSize: 10,
                    color: C.navy,
                    textDecoration: "none",
                    border: `1px solid ${C.border}`,
                    borderRadius: "4px",
                    px: 1,
                    py: 0.4,
                    "&:hover": { bgcolor: C.bg },
                  }}
                >
                  LinkedIn ↗
                </Typography>
              )}
              {loading && statusLabel && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: C.navy,
                      animation: "pulse 1.2s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1, transform: "scale(1)" },
                        "50%": { opacity: 0.4, transform: "scale(0.75)" },
                      },
                    }}
                  />
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 600, color: C.navy }}
                  >
                    {statusLabel}
                  </Typography>
                </Box>
              )}
              {!loading && profile && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEnrichConfirmOpen(true)}
                  sx={{
                    fontSize: 10,
                    py: 0.4,
                    px: 1.5,
                    borderColor: C.border,
                    color: C.muted,
                    textTransform: "none",
                    minWidth: 0,
                    borderRadius: "4px",
                    fontWeight: 600,
                    "&:hover": { borderColor: C.navy, color: C.navy },
                  }}
                >
                  Refresh Data
                </Button>
              )}
              <Box
                onClick={onClose}
                sx={{
                  cursor: "pointer",
                  color: C.muted,
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1,
                  ml: 0.5,
                  "&:hover": { color: C.charcoal },
                }}
              >
                ✕
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, pb: 5 }}>
          {error && (
            <Typography sx={{ fontSize: 11, color: C.red, mt: 2 }}>
              {error}
            </Typography>
          )}

          {profile?.identity_conflict ? (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "6px",
                bgcolor: "#eff6ff",
                border: "1px solid #bfdbfe",
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: "#1d4ed8", mb: 0.25 }}
              >
                ✓ Identity resolved by consensus
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#1e3a5f", lineHeight: 1.5 }}>
                PDL returned{" "}
                <b>{toTitle(profile.conflicting_name || "a different person")}</b>{" "}
                for this email — a wrong match.{" "}
                {profile.resolved_via === "web" ? (
                  <>
                    The web arbiter corrected it to <b>{displayName}</b>
                    {profile.web_verify?.sources
                      ? ` (${profile.web_verify.sources} corroborating sources)`
                      : ""}
                    .{" "}
                  </>
                ) : (
                  <>
                    Corrected to <b>{displayName}</b> using the verified name on
                    file.{" "}
                  </>
                )}
                Giving history and summary below reflect the verified person.
              </Typography>
            </Box>
          ) : enrichMismatch ? (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "6px",
                bgcolor: "#fef2f2",
                border: "1px solid #fecaca",
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: "#b91c1c", mb: 0.25 }}
              >
                ⚠ Enrichment identity mismatch — suppressed
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#7f1d1d", lineHeight: 1.5 }}>
                External enrichment returned{" "}
                <b>{toTitle(rawPdl.full_name || "a different person")}</b>, which
                doesn't match this record. Their giving history, DISC, and ask have
                been hidden to avoid attributing a stranger's data. Showing only
                verified first-party data below.
              </Typography>
            </Box>
          ) : null}

          {/* ── Headline (TLDR) — Voter Read headline leads when present ── */}
          {(loading || tldr) && (
            <Box
              sx={{
                mt: 2,
                mb: 0.5,
                px: 2,
                py: 1.25,
                bgcolor: C.bg,
                borderLeft: `3px solid ${C.navy}`,
                borderRadius: "0 6px 6px 0",
              }}
            >
              {loading ? (
                <Skeleton variant="text" width="85%" height={16} />
              ) : (
                <Typography
                  sx={{
                    fontSize: voterRead?.headline || summaryHeadline ? 16 : 14,
                    fontWeight: voterRead?.headline || summaryHeadline ? 600 : 700,
                    color: C.charcoal,
                    fontStyle:
                      voterRead?.headline || summaryHeadline ? "italic" : "normal",
                    lineHeight: 1.5,
                  }}
                >
                  {tldr}
                </Typography>
              )}
            </Box>
          )}

          {/* ── Voter Read — the synthesis: what his issue signals MEAN ── */}
          {voterRead && (voterRead.read || voterRead.archetype) && (
            <Box
              sx={{
                mt: 1.5,
                mb: 0.5,
                p: 1.75,
                borderRadius: "8px",
                bgcolor: C.navy,
                color: "#fff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#93c5fd",
                  }}
                >
                  Voter Read
                </Typography>
                {voterRead.archetype && (
                  <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                    {voterRead.archetype}
                  </Typography>
                )}
              </Box>
              {voterRead.read && (
                <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, mb: 1.25 }}>
                  {voterRead.read}
                </Typography>
              )}
              {Array.isArray(voterRead.top_issues) &&
                voterRead.top_issues.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.25 }}>
                    {voterRead.top_issues.map((iss, i) => (
                      <Box
                        key={i}
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: "rgba(255,255,255,0.15)",
                          borderRadius: "999px",
                          px: 1,
                          py: 0.25,
                        }}
                      >
                        {iss}
                      </Box>
                    ))}
                  </Box>
                )}
              {voterRead.persuasion && (
                <Box sx={{ display: "flex", gap: 0.75, mb: voterRead.tension ? 0.75 : 0 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#86efac", flexShrink: 0 }}>
                    MOVE →
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, lineHeight: 1.5 }}>
                    {voterRead.persuasion}
                  </Typography>
                </Box>
              )}
              {voterRead.tension && (
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#fcd34d", flexShrink: 0 }}>
                    WATCH →
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, lineHeight: 1.5 }}>
                    {voterRead.tension}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ── Campaign Fit — the scorecard: roles, model percentiles, Next Best Action ── */}
          {personFit && personFit.scores_json && (
            <CampaignFitBox fit={personFit} />
          )}

          {/* ── Next Ask + Donate Buttons ── */}
          {(loading || nextAsk || compliance) && (
            <Box
              sx={{
                mt: 1.5,
                mb: 0.5,
                border: "1px solid #bbf7d0",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              {/* Ask amount + rationale row */}
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  bgcolor: "#f0fdf4",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Box sx={{ flexShrink: 0, mt: 0.2 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#16a34a",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Next Ask
                  </Typography>
                  {loading ? (
                    <Skeleton variant="text" width={64} height={28} />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 800,
                        color: "#15803d",
                        lineHeight: 1.1,
                      }}
                    >
                      {nextAsk}
                    </Typography>
                  )}
                </Box>
                {!loading && nextAskRationale && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#166534",
                      lineHeight: 1.5,
                      mt: 0.25,
                    }}
                  >
                    {nextAskRationale}
                  </Typography>
                )}
                {loading && (
                  <Skeleton
                    variant="text"
                    width="65%"
                    height={14}
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>

              {/* Contribution-limit headroom strip (hard-capped server-side) */}
              {!loading && compliance && compliance.limit != null && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: compliance.at_limit ? "#fef2f2" : "#f8fafc",
                    borderTop: "1px solid",
                    borderColor: compliance.at_limit ? "#fecaca" : "#e2e8f0",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: compliance.at_limit ? "#b91c1c" : "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      mb: 0.25,
                    }}
                  >
                    {(compliance.jurisdiction || "").toUpperCase()} limit ·{" "}
                    {compliance.election_type} election
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: "#334155", lineHeight: 1.5 }}>
                    {fmtUsd0(compliance.limit)}/election cap · given{" "}
                    {fmtUsd0(compliance.given)} ·{" "}
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: compliance.at_limit ? "#b91c1c" : "#15803d" }}
                    >
                      {fmtUsd0(compliance.remaining)} remaining
                    </Box>
                  </Typography>
                  {compliance.at_limit && complianceRedirects && complianceRedirects.length > 0 && (
                    <Box sx={{ mt: 0.75 }}>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#b91c1c", mb: 0.25 }}>
                        Maxed to the campaign — redirect the ask:
                      </Typography>
                      {complianceRedirects.map((rp, i) => (
                        <Typography
                          key={i}
                          sx={{ fontSize: 11, color: "#475569", lineHeight: 1.5 }}
                        >
                          • <b>{rp.label}</b>
                          {rp.limit != null ? ` — ${fmtUsd0(rp.limit)}` : " — unlimited"}
                          {rp.note ? ` · ${rp.note}` : ""}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Donate button set */}
              {(loading || donateButtons.length > 0) && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "#fff",
                    borderTop: "1px solid #bbf7d0",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#16a34a",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      mb: 1,
                    }}
                  >
                    Suggested Button Set
                  </Typography>

                  {/* Button row */}
                  {loading ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton
                          key={i}
                          variant="rounded"
                          width={72}
                          height={32}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        alignItems: "flex-end",
                      }}
                    >
                      {donateButtons.map((amt, idx) => {
                        const isDefault = amt === donateDefault;
                        const label = donateButtonLabels[idx] || "";
                        const fmtAmt =
                          amt >= 1000
                            ? `$${(amt / 1000).toLocaleString("en-US", { maximumFractionDigits: 1 })}K`
                            : `$${amt.toLocaleString()}`;
                        return (
                          <Box
                            key={amt}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.4,
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.75,
                                py: 0.7,
                                borderRadius: "4px",
                                border: isDefault
                                  ? "2px solid #15803d"
                                  : "1px solid #d1d5db",
                                bgcolor: isDefault ? "#15803d" : "#fff",
                                color: isDefault ? "#fff" : C.charcoal,
                                fontSize: 13,
                                fontWeight: isDefault ? 700 : 500,
                                fontFamily: "monospace",
                                position: "relative",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fmtAmt}
                              {isDefault && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -8,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    fontSize: 8,
                                    fontWeight: 700,
                                    color: "#15803d",
                                    bgcolor: "#f0fdf4",
                                    px: 0.5,
                                    borderRadius: "2px",
                                    letterSpacing: "0.04em",
                                    fontFamily: "sans-serif",
                                    whiteSpace: "nowrap",
                                    border: "1px solid #bbf7d0",
                                  }}
                                >
                                  DEFAULT
                                </Box>
                              )}
                            </Box>
                            {label && (
                              <Typography
                                sx={{
                                  fontSize: 9,
                                  color: isDefault ? "#15803d" : C.muted,
                                  fontWeight: isDefault ? 600 : 400,
                                  textAlign: "center",
                                  maxWidth: 80,
                                  lineHeight: 1.3,
                                }}
                              >
                                {label}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.6,
                          borderRadius: "4px",
                          border: "1px dashed #d1d5db",
                          bgcolor: "#f9fafb",
                          color: C.muted,
                          fontSize: 12,
                          fontWeight: 400,
                        }}
                      >
                        Other $___
                      </Box>
                    </Box>
                  )}

                  {/* Anchoring rationale */}
                  {!loading && donateButtonRationale && (
                    <Box
                      sx={{
                        fontSize: 10,
                        color: C.muted,
                        mt: 1,
                        lineHeight: 1.5,
                        "& p": { m: 0 },
                      }}
                    >
                      <ReactMarkdown>{donateButtonRationale}</ReactMarkdown>
                    </Box>
                  )}

                  {/* DISC framing guidance */}
                  {!loading && donateDiscFrame && (
                    <Box
                      sx={{
                        mt: 1.25,
                        px: 1.5,
                        py: 1,
                        bgcolor: "#f8fafc",
                        borderRadius: "4px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.navy,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          mb: 0.5,
                        }}
                      >
                        {disc?.disc_type
                          ? `${disc.disc_type}-Type Ask Framing`
                          : "Ask Framing"}
                      </Typography>
                      <Box
                        sx={{
                          fontSize: 11,
                          color: C.charcoal,
                          lineHeight: 1.6,
                          "& p": { m: 0 },
                          "& p + p": { mt: 0.75 },
                        }}
                      >
                        <ReactMarkdown>{donateDiscFrame}</ReactMarkdown>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* ── Charitable Giving (Event.Gives — non-political capacity proxy) ── */}
          {charity && Number(charity.ask_anchor) > 0 && (
            <Box
              sx={{
                mt: 1.5,
                border: `1px solid ${C.border}`,
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: C.bg,
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Charitable Giving
                </Typography>
                <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                  non-political · capacity proxy
                </Typography>
                {!!Number(charity.is_event_table_prospect) && (
                  <Box
                    sx={{
                      ml: "auto",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#9a3412",
                      bgcolor: "#ffedd5",
                      borderRadius: "999px",
                      px: 0.75,
                      py: 0.2,
                    }}
                  >
                    🍽️ Table prospect
                  </Box>
                )}
              </Box>
              <Box sx={{ px: 2, py: 1.25 }}>
                <Box
                  sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mb: 1 }}
                >
                  {[
                    ["Donation", charity.highest_donation, charity.avg_donation],
                    ["Ticket / table", charity.highest_ticket, charity.avg_ticket],
                    ["Auction", charity.highest_bid, charity.avg_bid],
                  ].map(([label, hi, avg]) => (
                    <Box key={label}>
                      <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.charcoal }}>
                        {fmtUsd0(hi)}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                        avg {fmtUsd0(avg)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: 11, color: "#475569", lineHeight: 1.5 }}>
                  Ask anchor (giving-weighted ceiling):{" "}
                  <b style={{ color: "#15803d" }}>{fmtUsd0(charity.ask_anchor)}</b>
                  {" — informs the Next Ask above."}
                </Typography>
              </Box>
            </Box>
          )}

          {/* ── What we already know (first-party record, loaded by person_id) ── */}
          {person && (
            <>
              {!!(person.party || person.county || person.cd) && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                  {[
                    person.party && `${person.party}`,
                    person.county && `${person.county} County`,
                    person.cd && `CD-${person.cd}`,
                  ]
                    .filter(Boolean)
                    .map((t) => (
                      <Box
                        key={t}
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.charcoal,
                          bgcolor: C.bg,
                          border: `1px solid ${C.border}`,
                          borderRadius: "999px",
                          px: 1,
                          py: 0.25,
                        }}
                      >
                        {t}
                      </Box>
                    ))}
                </Box>
              )}

              {!!(person.general_last || person.general_previous) && (
                <>
                  <SectionLabel>Voting</SectionLabel>
                  <Box
                    sx={{
                      bgcolor: C.white,
                      border: `1px solid ${C.border}`,
                      borderRadius: "6px",
                      p: 1.5,
                      display: "flex",
                      gap: 3,
                    }}
                  >
                    {[
                      ["Last general", person.general_last],
                      ["Prior general", person.general_previous],
                    ].map(([label, v]) => {
                      const voted =
                        v && !/^(n|no|0|false|did not vote)$/i.test(String(v).trim());
                      return (
                        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 9,
                              height: 9,
                              borderRadius: "50%",
                              bgcolor: voted ? "#16a34a" : C.border,
                            }}
                          />
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.charcoal }}>
                              {voted ? "Voted" : "Did not vote"}
                            </Typography>
                            <Typography sx={{ fontSize: 9, color: C.muted, textTransform: "uppercase" }}>
                              {label}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}

              <SectionLabel>
                Issue Profile{personIssues.length ? ` (${personIssues.length})` : ""}
              </SectionLabel>
              <Typography
                sx={{ fontSize: 10.5, color: C.muted, mt: -0.5, mb: 1, lineHeight: 1.5 }}
              >
                Where they stand on each issue and how strongly they engage it. The
                bar is <b>intensity</b> (0–1) — recency-weighted volume of their
                signals.
              </Typography>
              <Box
                sx={{
                  bgcolor: C.white,
                  border: `1px solid ${C.border}`,
                  borderRadius: "6px",
                  p: 1.5,
                }}
              >
                {personIssues.length === 0 ? (
                  <Typography sx={{ fontSize: 11, color: C.muted }}>
                    No issue signals captured yet.
                  </Typography>
                ) : (
                  personIssues.map((it, i) => {
                    const intensity = Math.max(
                      Math.min(Number(it.intensity) || 0, 1),
                      0,
                    );
                    const level =
                      intensity >= 0.66
                        ? "High"
                        : intensity >= 0.33
                          ? "Medium"
                          : "Low";
                    const stanceRaw =
                      it.stance_label || stanceBySlug[it.slug] || it.stance || "";
                    const stanceText = it.stance_label
                      ? it.stance_label
                      : titleCase(stanceRaw);
                    const neg = /concern|affect|anti|negativ|oppos|against/i.test(
                      stanceRaw,
                    );
                    const pos = /pro|support|positiv|favor|identif/i.test(stanceRaw);
                    const stanceColor = neg
                      ? "#b45309"
                      : pos
                        ? "#15803d"
                        : C.navy;
                    const stanceBg = neg
                      ? "#fef3c7"
                      : pos
                        ? "#dcfce7"
                        : C.navy + "12";
                    return (
                      <Box
                        key={it.slug || i}
                        sx={{
                          py: 1,
                          borderBottom:
                            i < personIssues.length - 1
                              ? `1px solid ${C.border}`
                              : "none",
                        }}
                      >
                        {/* line 1: issue + stance + date */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.6,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: 12.5, fontWeight: 700, color: C.charcoal }}
                          >
                            {it.label || it.slug}
                          </Typography>
                          <Box
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: stanceColor,
                              bgcolor: stanceBg,
                              borderRadius: "4px",
                              px: 0.75,
                              py: 0.2,
                            }}
                          >
                            {stanceText || "no clear stance"}
                          </Box>
                          {it.last_observed_at && (
                            <Typography
                              sx={{ fontSize: 10, color: C.muted, ml: "auto" }}
                            >
                              {fmtDate(it.last_observed_at)}
                            </Typography>
                          )}
                        </Box>
                        {/* line 2: intensity bar + level + signal count */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 90,
                              height: 7,
                              borderRadius: 4,
                              bgcolor: C.navy + "18",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              sx={{
                                width: `${Math.max(intensity * 100, 4)}%`,
                                height: "100%",
                                bgcolor: C.navy,
                              }}
                            />
                          </Box>
                          <Typography sx={{ fontSize: 10.5, color: C.muted }}>
                            {level} intensity ({intensity.toFixed(2)})
                            {it.signal_count
                              ? ` · ${it.signal_count} signal${it.signal_count > 1 ? "s" : ""}`
                              : ""}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* Evidence — collapsed by default; the raw posts behind the read, on demand only */}
              {visibleSignals.length > 0 && (
                <>
                  <Box
                    onClick={() => setActivityOpen((o) => !o)}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "baseline",
                      gap: 0.75,
                      mt: 2.5,
                      mb: activityOpen ? 1 : 0,
                      userSelect: "none",
                    }}
                  >
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: C.charcoal }}>
                      {activityOpen ? "▾" : "▸"} Evidence — {visibleSignals.length}{" "}
                      post{visibleSignals.length === 1 ? "" : "s"}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: C.muted }}>
                      the raw posts behind the read
                    </Typography>
                  </Box>
                  {activityOpen && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {visibleSignals.map((s, i) => (
                      <Box
                        key={s.source_post_id || i}
                        sx={{
                          bgcolor: C.white,
                          border: `1px solid ${C.border}`,
                          borderRadius: "6px",
                          p: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            mb: s.text ? 0.75 : 0,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box
                            sx={{
                              fontSize: 9,
                              fontWeight: 800,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              color: C.navy,
                              bgcolor: C.navy + "12",
                              borderRadius: "4px",
                              px: 0.75,
                              py: 0.25,
                            }}
                          >
                            {s.topic_label}
                            {s.stance ? ` · ${s.stance}` : ""}
                          </Box>
                          {s.signal_type && s.signal_type !== "authored" && (
                            <Typography sx={{ fontSize: 10, color: C.muted }}>
                              {s.signal_type}
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: C.muted,
                              ml: "auto",
                            }}
                          >
                            {fmtDate(s.observed_at || s.tweet_created_at)}
                          </Typography>
                        </Box>
                        {s.text && (
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: C.charcoal,
                              lineHeight: 1.55,
                            }}
                          >
                            {s.text}
                          </Typography>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            mt: 0.75,
                            alignItems: "center",
                          }}
                        >
                          {[
                            ["♥", s.likes],
                            ["↺", s.retweets],
                            ["↩", s.replies],
                          ]
                            .filter(([, n]) => n)
                            .map(([g, n]) => (
                              <Typography
                                key={g}
                                sx={{ fontSize: 10, color: C.muted }}
                              >
                                {g} {Number(n).toLocaleString()}
                              </Typography>
                            ))}
                          {(() => {
                            // Platform-aware evidence link. FB/IG use the stored permalink (source_url); X
                            // builds the status URL from the id. NEVER assume X for an unknown platform —
                            // a source-less signal simply gets no link (no bogus "View on X").
                            const plat = s.platform;
                            const meta =
                              plat === "facebook"
                                ? { label: "Facebook", color: "#1877f2" }
                                : plat === "instagram"
                                  ? { label: "Instagram", color: "#c13584" }
                                  : plat === "x"
                                    ? { label: "X", color: "#1da1f2" }
                                    : null;
                            const href =
                              s.source_url ||
                              (plat === "x" && s.source_post_id
                                ? `https://x.com/${s.author_username || "i"}/status/${s.source_post_id}`
                                : null);
                            if (!href || !meta) return null;
                            return (
                              <Box
                                component="a"
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: meta.color,
                                  textDecoration: "none",
                                  ml: "auto",
                                }}
                              >
                                View on {meta.label} →
                              </Box>
                            );
                          })()}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Summary ── */}
          <SectionLabel>Summary</SectionLabel>
          <Box
            sx={{
              bgcolor: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              p: 2,
            }}
          >
            {loading ? (
              <>
                <Skeleton variant="text" width="100%" height={14} />
                <Skeleton variant="text" width="96%" height={14} />
                <Skeleton
                  variant="text"
                  width="80%"
                  height={14}
                  sx={{ mb: 1.5 }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Skeleton variant="rounded" width={110} height={22} />
                  <Skeleton variant="rounded" width={90} height={22} />
                  <Skeleton variant="rounded" width={120} height={22} />
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    fontSize: 12,
                    color: C.charcoal,
                    lineHeight: 1.7,
                    mb: 1.5,
                    "& p": { mt: 0, mb: 1 },
                    "& strong": { fontWeight: 700 },
                    "& ul,& ol": { pl: 2.5, mt: 0, mb: 1 },
                    "& li": { mb: 0.25 },
                    "& h1,& h2,& h3": {
                      fontSize: 13,
                      fontWeight: 700,
                      mt: 0,
                      mb: 0.75,
                    },
                  }}
                >
                  {summary ? (
                    <ReactMarkdown>{summary}</ReactMarkdown>
                  ) : !cacheChecked ? (
                    <Box sx={{ pt: 1 }}>
                      <Skeleton
                        variant="text"
                        width="90%"
                        height={14}
                        sx={{ mb: 0.75 }}
                      />
                      <Skeleton
                        variant="text"
                        width="75%"
                        height={14}
                        sx={{ mb: 0.75 }}
                      />
                      <Skeleton variant="text" width="82%" height={14} />
                    </Box>
                  ) : personBrief ? (
                    <>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: C.charcoal,
                          lineHeight: 1.7,
                          mb: 1.5,
                        }}
                      >
                        {personBrief}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: C.muted }}>
                          {effEmail
                            ? "Run enrichment for DISC, FEC history & a tailored ask."
                            : "No email on file — enrich to add contact + strategy."}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          disableElevation
                          disabled={!effEmail && !effPersonId}
                          onClick={() => setEnrichConfirmOpen(true)}
                          sx={{
                            textTransform: "none",
                            fontSize: 10,
                            fontWeight: 700,
                            px: 1.5,
                            py: 0.4,
                            borderRadius: "6px",
                            bgcolor: "#22c55e",
                            color: "#fff",
                            "&:hover": { bgcolor: "#16a34a" },
                            "&.Mui-disabled": { bgcolor: "#e2e8f0", color: C.muted },
                          }}
                        >
                          Enrich
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        border: `1px dashed ${C.border}`,
                        borderRadius: "8px",
                        p: 2.5,
                        textAlign: "center",
                        bgcolor: C.bg,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: C.navy,
                          mb: 0.75,
                        }}
                      >
                        Record not yet enriched
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: C.muted,
                          lineHeight: 1.65,
                          mb: 2,
                        }}
                      >
                        Enrichment unlocks the intelligence card, DISC
                        personality profile, FEC cross-campaign giving history,
                        and a personalized ask strategy with DISC-optimized
                        donate buttons.
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setEnrichConfirmOpen(true)}
                        sx={{
                          bgcolor: C.navy,
                          color: "#fff",
                          textTransform: "none",
                          fontSize: 11,
                          fontWeight: 700,
                          px: 2.5,
                          py: 0.75,
                          borderRadius: "6px",
                          "&:hover": { bgcolor: "#0a2240" },
                        }}
                      >
                        Enrich Record
                      </Button>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {pdl.industry && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        px: 1.25,
                        py: 0.35,
                        borderRadius: "4px",
                        bgcolor: C.bg,
                        border: `1px solid ${C.border}`,
                        color: C.charcoal,
                      }}
                    >
                      Industry: <strong>{pdl.industry}</strong>
                    </Box>
                  )}
                  {pdl.company && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        px: 1.25,
                        py: 0.35,
                        borderRadius: "4px",
                        bgcolor: C.bg,
                        border: `1px solid ${C.border}`,
                        color: C.charcoal,
                      }}
                    >
                      Company: <strong>{pdl.company}</strong>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>

          {/* ── Background — deep web research (current role, full career, bio). The PRIMARY identity
                 resolver: reads the live web + LinkedIn, not just stale data brokers. ── */}
          {person && (
            <>
              <SectionLabel>Background</SectionLabel>
              <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: "6px", p: 1.5, mb: 1 }}>
                {person.web_profile ? (
                  <>
                    {(person.web_profile.current_title || person.web_profile.current_employer) && (
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.navy, mb: 0.5 }}>
                        {[person.web_profile.current_title, person.web_profile.current_employer].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                    {person.web_profile.bio && (
                      <Typography sx={{ fontSize: 12, color: C.charcoal, lineHeight: 1.6, mb: person.web_profile.career?.length ? 1 : 0 }}>
                        {person.web_profile.bio}
                      </Typography>
                    )}
                    {Array.isArray(person.web_profile.career) && person.web_profile.career.length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, mb: person.web_profile.education?.length ? 1 : 0 }}>
                        {person.web_profile.career.slice(0, 8).map((c, i) => (
                          <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                            <Typography sx={{ fontSize: 11.5, color: C.charcoal }}>
                              <strong>{c.title}</strong>
                              {c.company ? ` — ${c.company}` : ""}
                            </Typography>
                            {(c.start || c.end) && (
                              <Typography sx={{ fontSize: 10, color: C.muted, whiteSpace: "nowrap" }}>
                                {[c.start, c.end].filter(Boolean).join("–")}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    {Array.isArray(person.web_profile.education) && person.web_profile.education.length > 0 && (
                      <Typography sx={{ fontSize: 10.5, color: C.muted }}>
                        {person.web_profile.education
                          .map((e) => [e.degree, e.school, e.years].filter(Boolean).join(", "))
                          .join(" · ")}
                      </Typography>
                    )}
                    {person.web_profile.generated_at && (
                      <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.75 }}>
                        Web research · {person.web_profile.confidence || "?"} confidence ·{" "}
                        {person.web_profile.sources || 0} sources
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: 11, color: C.muted }}>
                    No background yet — run <b>Enrich</b> to pull their current role, career, address, and
                    emails from the public web.
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* ── Social profile + X deep-dive CTA (or Find-social CTA when no handle yet) ── */}
          {person &&
            (person.username ||
              person.bio ||
              personSignals.length > 0 ||
              effEmail) && (
            <>
              <SectionLabel>Social Presence</SectionLabel>
              <Box
                sx={{
                  bgcolor: C.white,
                  border: `1px solid ${C.border}`,
                  borderRadius: "6px",
                  p: 1.5,
                }}
              >
                {/* ── Social Presence — one card per linked network (X + FB/IG/Nextdoor), each with that
                       platform's own handle, location, bio, and engagement stats. Falls back to a single X
                       card synthesized from the person record when the presence array hasn't populated. ── */}
                {(() => {
                  const cards = socialPresence;
                  if (!cards.length) return null;
                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.5,
                        mb: 1.5,
                        pb: 1.5,
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {cards.map((sp) => {
                        const meta = PLATFORM_META[sp.platform];
                        if (!meta) return null;
                        const url = platformUrl(
                          sp.platform,
                          sp.username,
                          sp.platform_user_id,
                        );
                        const st = sp.stats || {};
                        // A FB Page (and X/IG) reports followers; a FB personal profile reports friends.
                        // Show whichever we have, labeled correctly.
                        const audienceVal = st.followers || st.friends || 0;
                        const audienceLbl = st.followers
                          ? "Followers"
                          : st.friends
                            ? "Friends"
                            : "Followers";
                        const big = [
                          [audienceLbl, fmtCompact(audienceVal)],
                          [
                            "Engagement",
                            st.engagement_rate != null
                              ? `${st.engagement_rate}%`
                              : "—",
                          ],
                        ];
                        const small = [
                          ["Avg likes", st.avg_likes ?? "—"],
                          ["Posts", st.posts ?? 0],
                          ["Avg replies", st.avg_replies ?? "—"],
                        ];
                        return (
                          <Box
                            key={sp.platform}
                            sx={{
                              border: `1.5px solid ${meta.color}`,
                              bgcolor: `${meta.color}0a`,
                              borderRadius: "12px",
                              p: 2.25,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.75,
                            }}
                          >
                            {/* Avatar + RECENT ACTIVITY stats */}
                            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                              <Box
                                sx={{
                                  position: "relative",
                                  width: 68,
                                  height: 68,
                                  borderRadius: "50%",
                                  flexShrink: 0,
                                  overflow: "hidden",
                                  bgcolor: C.navy,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {/* Initials are the base layer; the photo overlays it and, if it fails to
                                    load (IG CDN URLs are hotlink-protected), onError hides it → initials show. */}
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>
                                  {initials}
                                </Typography>
                                {(sp.profile_pic || photoUrl) && (
                                  <Box
                                    component="img"
                                    src={sp.profile_pic || photoUrl}
                                    alt=""
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: C.muted,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.6,
                                    mb: 0.9,
                                  }}
                                >
                                  Recent activity
                                </Typography>
                                <Box sx={{ display: "flex", gap: 3.5, mb: 1.5 }}>
                                  {big.map(([l, v]) => (
                                    <Box key={l}>
                                      <Typography
                                        sx={{
                                          fontSize: 22,
                                          fontWeight: 800,
                                          color: C.charcoal,
                                          lineHeight: 1.05,
                                        }}
                                      >
                                        {v}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: 9,
                                          color: C.muted,
                                          textTransform: "uppercase",
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        {l}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                                <Box sx={{ display: "flex", gap: 2.5 }}>
                                  {small.map(([l, v]) => (
                                    <Box key={l}>
                                      <Typography
                                        sx={{
                                          fontSize: 15,
                                          fontWeight: 700,
                                          color: C.charcoal,
                                          lineHeight: 1.05,
                                        }}
                                      >
                                        {v}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: 9,
                                          color: C.muted,
                                          textTransform: "uppercase",
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        {l}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                            {/* Identity — branded icon + handle + location */}
                            <Box
                              component={url ? "a" : "div"}
                              href={url || undefined}
                              target={url ? "_blank" : undefined}
                              rel={url ? "noreferrer" : undefined}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                textDecoration: "none",
                              }}
                            >
                              <PlatformIcon platform={sp.platform} size={28} radius={7} />
                              <Typography
                                sx={{ fontSize: 15, fontWeight: 800, color: C.charcoal }}
                              >
                                {sp.username || `${meta.label} profile ↗`}
                              </Typography>
                              {sp.location && (
                                <Typography sx={{ fontSize: 13, color: C.muted, ml: 1.5 }}>
                                  {sp.location}
                                </Typography>
                              )}
                            </Box>
                            {/* Bio */}
                            {sp.bio && (
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  color: C.muted,
                                  fontStyle: "italic",
                                  lineHeight: 1.5,
                                }}
                              >
                                “{sp.bio}”
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
                {/* Broken-evidence cleanup — signals with no backing post (source-less classify leftovers)
                    silently inflate an issue and render as blank rows. Offer a one-click purge + recompute. */}
                {orphanedSignals.length > 0 && (
                  <Box
                    sx={{
                      mb: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: "6px",
                      bgcolor: "#fef2f2",
                      border: "1px solid #fecaca",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography sx={{ fontSize: 10.5, color: "#991b1b", flex: 1, minWidth: 140 }}>
                      {orphanedSignals.length} broken signal
                      {orphanedSignals.length === 1 ? "" : "s"} with no post behind{" "}
                      {orphanedSignals.length === 1 ? "it" : "them"} are inflating this person's issues.
                    </Typography>
                    <Box
                      component="button"
                      disabled={!!unlinkingPlatform}
                      onClick={runPurgeOrphaned}
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: "#fff",
                        bgcolor: "#dc2626",
                        border: "none",
                        borderRadius: "5px",
                        px: 1,
                        py: 0.5,
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#b91c1c" },
                        "&:disabled": { opacity: 0.6, cursor: "default" },
                      }}
                    >
                      {unlinkingPlatform === "__orphaned__"
                        ? "Cleaning…"
                        : "Clean up"}
                    </Box>
                  </Box>
                )}
                {/* ── ONE action: discover any missing networks, then deep-dive every present account.
                       Per-platform status shows as the row of brand icons (spinner while that one runs). ── */}
                {(() => {
                  const statusPlats = PLATFORM_ORDER.filter(
                    (p) =>
                      (p === "x" && person?.username) ||
                      socialPresence.some((sp) => sp.platform === p),
                  );
                  const lastRead = person?.socialread_at;
                  const canRun = statusPlats.length > 0 || effEmail;
                  return (
                    <>
                      <Box
                        component="button"
                        disabled={socialRunAll || deepDiveRunning || findSocialRunning || !canRun}
                        onClick={runAllSocial}
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          bgcolor: "#eff6ff",
                          border: "1.5px solid #bfdbfe",
                          borderRadius: "10px",
                          px: 1.75,
                          py: 1.25,
                          cursor: "pointer",
                          "&:hover": { bgcolor: canRun ? "#dbeafe" : "#eff6ff" },
                          "&:disabled": { cursor: "default", opacity: canRun ? 0.9 : 0.5 },
                        }}
                      >
                        <Box
                          component="img"
                          src={socialSearchIcon}
                          alt=""
                          sx={{ width: 22, height: 16, flexShrink: 0 }}
                        />
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#1da1f2" }}>
                          {socialRunAll
                            ? socialStepLabel || "Reading social…"
                            : "Read Social Conversations"}
                        </Typography>
                        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                          {statusPlats.map((p) =>
                            socialStep[p] === "running" ? (
                              <Box
                                key={p}
                                sx={{
                                  width: 26,
                                  height: 26,
                                  borderRadius: "50%",
                                  border: "2.5px solid #bfdbfe",
                                  borderTopColor: "#2563eb",
                                  animation: "ppspin 0.8s linear infinite",
                                  "@keyframes ppspin": { "100%": { transform: "rotate(360deg)" } },
                                }}
                              />
                            ) : (
                              <Box
                                key={p}
                                sx={{
                                  opacity:
                                    socialRunAll && socialStep[p] !== "done" ? 0.35 : 1,
                                }}
                              >
                                <PlatformIcon platform={p} size={26} radius={7} />
                              </Box>
                            ),
                          )}
                        </Box>
                      </Box>
                      {/* Status is relayed in the button text; success is a toast. Below the button we only
                          show the persistent "LAST READ" stamp (or the one-line what-it-does when idle). */}
                      {!socialRunAll &&
                        (lastRead ? (
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#60a5fa", mt: 0.6, textAlign: "center", letterSpacing: "0.06em" }}>
                            LAST READ {fmtDate(lastRead).toUpperCase()}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.6, textAlign: "center" }}>
                            {statusPlats.length
                              ? "Finds any missing networks, then reads every linked account for new issue signals."
                              : "Add an email to discover and read their social accounts."}
                          </Typography>
                        ))}
                    </>
                  );
                })()}
                {/* ── LinkedIn — a profile LINK only (from enrichment); we don't pull LinkedIn metrics, so
                       it lives apart from the X block above to avoid implying the stats are LinkedIn's. ── */}
                {pdl.linkedin_url && (
                  <Box
                    sx={{
                      mt: 1.25,
                      pt: 1,
                      borderTop: `1px solid ${C.border}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      component="a"
                      href={pdl.linkedin_url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.4,
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#0a66c2",
                        textDecoration: "none",
                        px: 0.9,
                        py: 0.3,
                        borderRadius: "5px",
                        border: `1px solid ${C.border}`,
                        bgcolor: C.bg,
                      }}
                    >
                      in View LinkedIn ↗
                    </Box>
                    <Typography sx={{ fontSize: 10, color: C.muted }}>
                      profile link only — no LinkedIn metrics pulled
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* ── Contact grid ── */}
          <SectionLabel>Contact</SectionLabel>
          <Box sx={{ display: "flex", gap: 1 }}>
            {loading ? (
              [1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    border: `1px solid ${C.border}`,
                    borderRadius: "6px",
                    p: 1.25,
                    bgcolor: C.white,
                  }}
                >
                  <Skeleton variant="text" width="50%" height={12} />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={16}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ))
            ) : (
              <>
                <Box sx={contactCellStyle}>
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Email
                  </Typography>
                  {personEmails.length ? (
                    personEmails.map((em, i) => (
                      <Box key={i} sx={{ mt: i ? 0.4 : 0 }}>
                        <Typography
                          onClick={(e) => {
                            const r = document.createRange();
                            r.selectNodeContents(e.currentTarget);
                            const sel = window.getSelection();
                            sel.removeAllRanges();
                            sel.addRange(r);
                          }}
                          title="Click to select · Ctrl+C to copy"
                          sx={{
                            fontSize: 11,
                            color: C.charcoal,
                            wordBreak: "break-all",
                            cursor: "text",
                            userSelect: "all",
                          }}
                        >
                          {em.email}
                        </Typography>
                        {em.source && (
                          <Typography
                            sx={{
                              fontSize: 8.5,
                              color: C.muted,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            {em.source}
                            {em.is_primary ? " · primary" : ""}
                          </Typography>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ fontSize: 11, color: C.charcoal }}>—</Typography>
                  )}
                  {!personEmails.length &&
                    person &&
                    (person.username || person.full_name || person.first_name) && (
                      <Box sx={{ mt: 0.5 }}>
                        <Box
                          component="button"
                          disabled={findEmailRunning}
                          onClick={runFindEmail}
                          sx={{
                            fontSize: 9.5,
                            fontWeight: 700,
                            py: 0.3,
                            px: 0.75,
                            border: `1px solid ${C.border}`,
                            borderRadius: "4px",
                            bgcolor: C.white,
                            color: C.navy,
                            cursor: findEmailRunning ? "default" : "pointer",
                          }}
                        >
                          {findEmailRunning
                            ? `⏳ ${findEmailStatus || "Searching…"}`
                            : "🔍 Find email"}
                        </Box>
                        {findEmailResult &&
                          (findEmailResult.error ? (
                            <Typography sx={{ fontSize: 9, color: C.red, mt: 0.3 }}>
                              {findEmailResult.error}
                            </Typography>
                          ) : findEmailResult.filled ? (
                            <Typography sx={{ fontSize: 9, color: "#15803d", mt: 0.3, fontWeight: 700 }}>
                              ✓ Found &amp; saved
                            </Typography>
                          ) : findEmailResult.email ? (
                            <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.3 }}>
                              Candidate: {findEmailResult.email} (
                              {findEmailResult.confidence}) — not confident enough to
                              auto-fill
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.3 }}>
                              No email found
                            </Typography>
                          ))}
                      </Box>
                    )}
                </Box>
                <Box sx={contactCellStyle}>
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Phone
                  </Typography>
                  {donorRow?.Phone ? (
                    <Typography sx={{ fontSize: 11, color: C.charcoal }}>
                      {fmtPhone(donorRow.Phone)}
                    </Typography>
                  ) : personPhones.length ? (
                    personPhones.map((ph, i) => (
                      <Box key={i} sx={{ mt: i ? 0.4 : 0 }}>
                        <Typography sx={{ fontSize: 11, color: C.charcoal }}>
                          {fmtPhone(ph.phone)}
                        </Typography>
                        {ph.source && (
                          <Typography
                            sx={{
                              fontSize: 8.5,
                              color: C.muted,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            {ph.source}
                            {ph.is_primary ? " · primary" : ""}
                          </Typography>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ fontSize: 11, color: C.charcoal }}>—</Typography>
                  )}
                </Box>
                <Box sx={contactCellStyle}>
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Location
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.charcoal }}>
                    {locationLine || donorRow?.["Zip"] || person?.zip_code || "—"}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* ── DISC ── */}
          {(loading || discType) && (
            <SectionLabel>DISC Personality</SectionLabel>
          )}
          {(loading || discType) && (
            <Box
              sx={{
                bgcolor: C.white,
                border: `1px solid ${C.border}`,
                borderLeft: `4px solid ${loading ? C.border : discColor || C.border}`,
                borderRadius: "6px",
                p: 2,
              }}
            >
              {loading ? (
                <>
                  <Skeleton
                    variant="text"
                    width="35%"
                    height={18}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="text" width="100%" height={13} />
                  <Skeleton variant="text" width="90%" height={13} />
                  <Skeleton
                    variant="text"
                    width="75%"
                    height={13}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 0.75, mb: 1 }}>
                    {[55, 65, 50, 70, 45].map((w, i) => (
                      <Skeleton
                        key={i}
                        variant="rounded"
                        width={w}
                        height={20}
                      />
                    ))}
                  </Box>
                  <Skeleton variant="text" width="30%" height={13} />
                  <Skeleton variant="text" width="85%" height={13} />
                  <Skeleton variant="text" width="80%" height={13} />
                </>
              ) : !discType ? (
                <Typography
                  sx={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}
                >
                  LinkedIn profile not found — DISC analysis requires a LinkedIn
                  match via NinjaPear, which was unable to locate a profile for
                  this donor.
                </Typography>
              ) : (
                <>
                  {/* Header row: type + archetype + confidence + source */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.5,
                      mb: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 900,
                        color: discColor,
                        lineHeight: 1,
                      }}
                    >
                      {discType}
                      {discSecondary ? `/${discSecondary}` : ""}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 700, color: discColor }}
                    >
                      {DISC_LABELS[discType]}
                    </Typography>
                    {discArchetype && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: C.muted,
                          fontStyle: "italic",
                        }}
                      >
                        · {discArchetype}
                      </Typography>
                    )}
                    {discConfidence && (
                      <Box
                        sx={{
                          ml: "auto",
                          fontSize: 9,
                          fontWeight: 700,
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          bgcolor:
                            discConfidence === "high"
                              ? "#dcfce7"
                              : discConfidence === "medium"
                                ? "#fef9c3"
                                : "#fee2e2",
                          color:
                            discConfidence === "high"
                              ? "#15803d"
                              : discConfidence === "medium"
                                ? "#92400e"
                                : "#991b1b",
                          border: `1px solid ${discConfidence === "high" ? "#bbf7d0" : discConfidence === "medium" ? "#fde68a" : "#fecaca"}`,
                        }}
                      >
                        {discConfidence} confidence
                      </Box>
                    )}
                  </Box>

                  {/* Source badge */}
                  {discSource && (
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: C.muted,
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {discSource === "claude-pdl-only"
                        ? "Inferred from professional signals · low confidence"
                        : "Inferred from professional signals"}
                    </Typography>
                  )}

                  {/* Overview */}
                  {disc.overview && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: C.charcoal,
                        lineHeight: 1.7,
                        mb: 1.25,
                      }}
                    >
                      {disc.overview}
                    </Typography>
                  )}

                  {/* Traits */}
                  {disc.traits?.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.75,
                        flexWrap: "wrap",
                        mb: 1.25,
                      }}
                    >
                      {disc.traits.map((t) => (
                        <Box
                          key={t}
                          component="span"
                          sx={{
                            fontSize: 10,
                            fontWeight: 600,
                            px: 1,
                            py: 0.25,
                            borderRadius: "4px",
                            bgcolor: discColor + "14",
                            color: discColor,
                            border: `1px solid ${discColor}30`,
                          }}
                        >
                          {t}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Signals */}
                  {discSignals.length > 0 && (
                    <Box sx={{ mb: 1.25 }}>
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          mb: 0.5,
                        }}
                      >
                        Key Evidence
                      </Typography>
                      {discSignals.map((s, i) => (
                        <Typography
                          key={i}
                          sx={{ fontSize: 10, color: C.charcoal, mb: 0.2 }}
                        >
                          · {s}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {/* Do / Don't */}
                  <Box sx={{ display: "flex", gap: 3 }}>
                    {disc.dos?.length > 0 && (
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: C.green,
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            mb: 0.5,
                          }}
                        >
                          Do
                        </Typography>
                        {disc.dos.map((d, i) => (
                          <Typography
                            key={i}
                            sx={{
                              fontSize: 11,
                              color: C.charcoal,
                              mb: 0.3,
                              lineHeight: 1.5,
                            }}
                          >
                            · {d}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {disc.donts?.length > 0 && (
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: C.red,
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            mb: 0.5,
                          }}
                        >
                          Don't
                        </Typography>
                        {disc.donts.map((d, i) => (
                          <Typography
                            key={i}
                            sx={{
                              fontSize: 11,
                              color: C.charcoal,
                              mb: 0.3,
                              lineHeight: 1.5,
                            }}
                          >
                            · {d}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* ── Donation History ── */}
          <WinRedDonations
            loading={loading && !winredHistory}
            winred={winred.transactions ? winred : winredHistory || {}}
            fmtDate={fmtDate}
            fmtAmt={fmtAmt}
            donorRow={donorRow}
          />

          {/* ── Federal Contributions ── */}
          {(loading || fec.contributions?.length > 0) && (
            <FecContributions
              loading={loading}
              fec={fec}
              fmtDate={fmtDate}
              fmtAmt={fmtAmt}
            />
          )}

          {/* ── Footer ── */}
          {!loading && (profile || encRuns > 0 || srRuns > 0) && (
            <>
              <Divider sx={{ mt: 3, mb: 1.5 }} />
              {profile && (
                <Typography sx={{ fontSize: 9, color: C.muted }}>
                  Enriched {fmtDate(profile.enriched_at)} ·{" "}
                  {profile._source === "cache" ? "cached" : "live"} · PDL +
                  NinjaPear + FEC
                </Typography>
              )}
              {(encRuns > 0 || srRuns > 0) && (
                <Typography
                  sx={{ fontSize: 9, color: C.muted, mt: 0.4, lineHeight: 1.5 }}
                >
                  {encRuns > 0 && (
                    <>
                      Enrichment {encRuns}× · {fmtCost(encCost)}
                    </>
                  )}
                  {encRuns > 0 && srRuns > 0 && " · "}
                  {srRuns > 0 && (
                    <>
                      Social read {srRuns}× · {fmtCost(srCost)}{" "}
                      <Box component="span" sx={{ opacity: 0.65 }}>
                        (fetch {fmtCost(srXapi)} · AI {fmtCost(srAi)})
                      </Box>
                    </>
                  )}
                  {" · "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: C.charcoal }}
                  >
                    Total {fmtCost(totalSpend)}
                  </Box>
                </Typography>
              )}
            </>
          )}
        </Box>
      </Drawer>
      {/* Social-read success toast — replaces the old inline "✓ read N accounts" text. */}
      <Snackbar
        open={!!socialRunResult}
        autoHideDuration={5000}
        onClose={() => setSocialRunResult(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#0f2942",
            color: "#fff",
            px: 2,
            py: 1.25,
            borderRadius: "8px",
            boxShadow: "0 6px 24px rgba(0,0,0,0.28)",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Box sx={{ color: "#4ade80", fontWeight: 800 }}>✓</Box>
          Read {socialRunResult?.platforms || 0} account
          {socialRunResult?.platforms === 1 ? "" : "s"}
          {socialRunResult?.signals
            ? ` · ${socialRunResult.signals} new signal${socialRunResult.signals === 1 ? "" : "s"}`
            : ""}{" "}
          — profile updated.
        </Box>
      </Snackbar>
      <InfoModal
        info={infoModal}
        onClose={() => setInfoModal(null)}
        intro={infoModal?._isCohort ? COHORT_INTRO : DISC_INTRO}
      />

      {/* ── Enrichment Confirmation Modal ── */}
      {enrichConfirmOpen && (
        <Box
          onClick={() => setEnrichConfirmOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2100,
            bgcolor: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: "#0f1f36",
              color: "#e8edf4",
              borderRadius: "12px",
              p: "28px 32px 24px",
              maxWidth: 520,
              width: "100%",
              position: "relative",
              boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Box
              component="button"
              onClick={() => setEnrichConfirmOpen(false)}
              sx={{
                position: "absolute",
                top: 18,
                right: 20,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "rgba(255,255,255,0.4)",
                fontSize: 18,
                lineHeight: 1,
                p: 0,
                "&:hover": { color: "#fff" },
              }}
            >
              ✕
            </Box>

            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: "rgba(255,255,255,0.35)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                mb: 0.4,
              }}
            >
              Donor Intelligence
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "-0.01em",
                mb: 0.3,
              }}
            >
              Enrich Donor Record
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: "rgba(255,255,255,0.45)",
                fontStyle: "italic",
                mb: 2,
              }}
            >
              ~30–60 seconds · results cached for 30 days
            </Typography>

            <Box
              sx={{
                borderTop: "1px solid rgba(255,255,255,0.08)",
                pt: 1.75,
                mb: 2,
              }}
            >
              {[
                {
                  label: "Identity & Professional Profile",
                  desc: "Employer, title, LinkedIn headline, and photo via PeopleDataLabs",
                },
                {
                  label: "DISC Personality Assessment",
                  desc: "Behavioral profile inferred from LinkedIn career history via NinjaPear + Claude",
                },
                {
                  label: "Federal Giving History",
                  desc: "Cross-campaign contribution data pulled from FEC Schedule A",
                },
                {
                  label: "Donor Intelligence Card",
                  desc: "Headline, giving profile, engagement strategy, and next ask generated by Claude",
                },
                {
                  label: "Personalized Ask Strategy",
                  desc: "DISC-optimized donate button set with specific amounts and framing copy",
                },
              ].map(({ label, desc }, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1.25, mb: 1.25 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#22c55e",
                      flexShrink: 0,
                      mt: "5px",
                    }}
                  />
                  <Box>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#fff",
                        display: "block",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "rgba(255,255,255,0.5)",
                        lineHeight: 1.5,
                      }}
                    >
                      {desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
              <Box
                component="button"
                onClick={() => setEnrichConfirmOpen(false)}
                sx={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.5)",
                  borderRadius: "6px",
                  px: "18px",
                  py: "8px",
                  fontSize: 12,
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.4)",
                    color: "#fff",
                  },
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                onClick={() => {
                  setEnrichConfirmOpen(false);
                  fetchProfile(!!profile);
                }}
                sx={{
                  background: "#22c55e",
                  border: "none",
                  color: "#fff",
                  borderRadius: "6px",
                  px: "20px",
                  py: "8px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  "&:hover": { background: "#16a34a" },
                }}
              >
                Enrich Donor Record
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

// ─── Voter Drawer ────────────────────────────────────────────────────────────
const VoterDrawer = ({ voterRow, onClose, campaignId }) => {
  const { authFetch } = useAuth();
  const winredUrl = PHP_API_URL ? `${PHP_API_URL}/winred` : null;
  const [donationHistory, setDonationHistory] = useState(null);

  const email = voterRow?.email;
  const firstName = voterRow?.first_name || "";
  const lastName = voterRow?.last_name || "";
  const name =
    [firstName, lastName].filter(Boolean).join(" ") ||
    voterRow?.full_name ||
    voterRow?.social_username ||
    email ||
    "Unknown";
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  useEffect(() => {
    if (!email || !winredUrl || !campaignId) return;
    setDonationHistory(null);
    authFetch(
      `${winredUrl}?type=donations&campaign_id=${campaignId}&email=${encodeURIComponent(email)}`,
    )
      .then((r) => r.json())
      .then((d) => setDonationHistory(d))
      .catch(() =>
        setDonationHistory({ transactions: [], count: 0, total: 0 }),
      );
  }, [email, winredUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtAmt = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtD = (s) => {
    if (!s) return "—";
    try {
      return new Date(s).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return s;
    }
  };

  const Field = ({ label, value }) => {
    if (!value) return null;
    return (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "baseline",
          borderBottom: `1px solid ${C.border}`,
          pb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: C.muted,
            minWidth: 80,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: C.charcoal, wordBreak: "break-word" }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const SecLabel = ({ children }) => (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 800,
        color: C.charcoal,
        mb: 1,
        mt: 2.5,
        letterSpacing: "0.06em",
      }}
    >
      {children}
    </Typography>
  );

  const isSocial = voterRow?.origin_source === "social";

  return (
    <Drawer
      anchor="right"
      open={!!voterRow}
      onClose={onClose}
      transitionDuration={300}
      PaperProps={{
        sx: {
          width: { xs: "100vw", md: "50vw" },
          minWidth: { xs: 0, md: 420 },
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f9fafb",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: C.white,
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: C.navy,
              fontSize: 16,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initials || "?"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: C.charcoal,
                lineHeight: 1.2,
              }}
            >
              {name}
            </Typography>
            {email && (
              <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.25 }}>
                {email}
              </Typography>
            )}
            {voterRow?.social_username && (
              <Typography sx={{ fontSize: 12, color: "#1da1f2", mt: 0.25 }}>
                @{voterRow.social_username}
              </Typography>
            )}
            <Box sx={{ mt: 0.75 }}>
              {isSocial ? (
                <Box
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    px: 0.75,
                    py: 0.3,
                    borderRadius: "4px",
                    bgcolor: "rgba(29,161,242,0.1)",
                    color: "#1da1f2",
                  }}
                >
                  Social
                </Box>
              ) : (
                <Box
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    px: 0.75,
                    py: 0.3,
                    borderRadius: "4px",
                    bgcolor: "rgba(0,0,0,0.05)",
                    color: C.muted,
                  }}
                >
                  WinRed
                </Box>
              )}
              {voterRow?.research_status === "researched" && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    px: 0.75,
                    py: 0.3,
                    ml: 0.5,
                    borderRadius: "4px",
                    bgcolor: "rgba(22,163,74,0.12)",
                    color: "#16a34a",
                  }}
                >
                  Enriched
                </Box>
              )}
            </Box>
          </Box>
          <Box
            onClick={onClose}
            sx={{
              cursor: "pointer",
              color: C.muted,
              fontSize: 20,
              lineHeight: 1,
              p: 0.5,
              flexShrink: 0,
            }}
          >
            ×
          </Box>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
        {/* Contact */}
        <SecLabel>CONTACT INFO</SecLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
          <Field label="Email" value={voterRow?.email} />
          <Field label="Phone" value={voterRow?.phone} />
          <Field label="Address" value={voterRow?.street_address} />
          <Field label="City" value={voterRow?.city} />
          <Field label="State" value={voterRow?.state} />
          <Field label="ZIP" value={voterRow?.zip_code} />
        </Box>

        {/* Social */}
        {voterRow?.social_username && (
          <>
            <SecLabel>SOCIAL</SecLabel>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}
            >
              <Field label="X Handle" value={`@${voterRow.social_username}`} />
              {voterRow?.disc_label && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    borderBottom: `1px solid ${C.border}`,
                    pb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.muted,
                      minWidth: 80,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    DISC
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: "4px",
                      bgcolor: voterRow.disc_color
                        ? `${voterRow.disc_color}22`
                        : "rgba(0,0,0,0.05)",
                      color: voterRow.disc_color || C.charcoal,
                    }}
                  >
                    {voterRow.disc_label}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Donation history */}
        {email && (
          <>
            <SecLabel>DONATIONS</SecLabel>
            {!donationHistory && (
              <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                <Skeleton
                  variant="rectangular"
                  height={52}
                  sx={{ flex: 1, borderRadius: "6px" }}
                />
                <Skeleton
                  variant="rectangular"
                  height={52}
                  sx={{ flex: 1, borderRadius: "6px" }}
                />
              </Box>
            )}
            {donationHistory && (
              <>
                <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                  <Box
                    sx={{
                      flex: 1,
                      border: `1px solid ${C.border}`,
                      borderRadius: "6px",
                      p: 1.25,
                      bgcolor: C.white,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Total Donated
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: C.charcoal,
                        mt: 0.25,
                      }}
                    >
                      {fmtAmt(donationHistory.total)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      border: `1px solid ${C.border}`,
                      borderRadius: "6px",
                      p: 1.25,
                      bgcolor: C.white,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Gifts
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: C.charcoal,
                        mt: 0.25,
                      }}
                    >
                      {donationHistory.count ||
                        (donationHistory.transactions || []).length}
                    </Typography>
                  </Box>
                </Box>
                {(donationHistory.transactions || [])
                  .slice(0, 15)
                  .map((tx, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 0.75,
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: C.muted }}>
                        {fmtD(tx.donated_at || tx.date)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.charcoal,
                        }}
                      >
                        {fmtAmt(tx.amount)}
                      </Typography>
                    </Box>
                  ))}
                {(donationHistory.transactions || []).length === 0 && (
                  <Typography sx={{ fontSize: 12, color: C.muted }}>
                    No donations on record.
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

// ─── Voters Tab ─────────────────────────────────────────────────────────────
const DonorsTab = ({
  onOpenProfile,
  subView = "donors",
  onSubViewChange,
  donorCohorts,
  cd = {},
  campaignId,
  onEnrichLocations,
  onEnrichIdentity,
  onImportHandles,
  locating = false,
  enriching = false,
  leadsEnrichProgress = null,
  leadsRefreshTick = 0,
  leadsMsg = null,
}) => {
  const { authFetch } = useAuth(); // needed for the PHP /social/leads call (bearer-protected)
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const winredUrl = PHP_API_URL ? `${PHP_API_URL}/winred` : null;
  const donationsUrl = PHP_API_URL ? `${PHP_API_URL}/donations` : null;

  const runSync = () => {
    if (!donationsUrl || !campaignId) return;
    setSyncing(true);
    setSyncMsg(null);
    authFetch(
      `${donationsUrl}?action=sync&source=winred&campaign_id=${campaignId}`,
      { method: "POST" },
    )
      .then((r) => r.json())
      .then((d) => {
        setSyncMsg(
          d.error
            ? `Error: ${d.error}`
            : `Synced — ${d.donors_processed ?? 0} donors, ${d.donations_inserted ?? 0} new donations`,
        );
        setSyncing(false);
      })
      .catch((e) => {
        setSyncMsg(`Error: ${e}`);
        setSyncing(false);
      });
  };
  const setSubView = (v) => onSubViewChange?.(v);
  const [votersData, setVotersData] = useState(null);
  const [donorsData, setDonorsData] = useState(null);
  const [leadsData, setLeadsData] = useState(null);
  const [votersPage, setVotersPage] = useState(1);
  const [donorsPage, setDonorsPage] = useState(1);
  const [leadsPage, setLeadsPage] = useState(1);
  const [votersLoading, setVotersLoading] = useState(false);
  const [donorsLoading, setDonorsLoading] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [voterSort, setVoterSort] = useState({ col: "last_name", dir: "asc" });
  const [donorSort, setDonorSort] = useState({ col: "Last Name", dir: "asc" });
  const [leadSort, setLeadSort] = useState({ col: "Last Name", dir: "asc" });
  const [voterFilterSource, setVoterFilterSource] = useState("");
  const [voterFilterCity, setVoterFilterCity] = useState("");
  const [voterFilterState, setVoterFilterState] = useState("");
  const [voterFilterZip, setVoterFilterZip] = useState("");
  const [voterDrawerRow, setVoterDrawerRow] = useState(null);
  const [leadsFilterState, setLeadsFilterState] = useState("");
  const [leadsFilterCity, setLeadsFilterCity] = useState("");
  const [leadsFilterZip, setLeadsFilterZip] = useState("");
  const [leadsFilterCd, setLeadsFilterCd] = useState("");
  const [leadsFilterParty, setLeadsFilterParty] = useState(new Set());
  const [leadsFilterTurnout, setLeadsFilterTurnout] = useState(new Set());
  const [enrichingLeadId, setEnrichingLeadId] = useState(null);
  const [enrichModal, setEnrichModal] = useState({
    open: false,
    handle: "",
    result: null,
    error: null,
  });
  const [cohortFilter, setCohortFilter] = useState("");
  const [enrichAllOpen, setEnrichAllOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importDraft, setImportDraft] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [enrichProgress, setEnrichProgress] = useState(null); // null | { queued, skipped, total } | "error"
  const [enrichLoading, setEnrichLoading] = useState(false);

  const fmtPhone = (v) => {
    if (!v) return "—";
    const d = String(v).replace(/\D/g, "");
    const n = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
    if (n.length !== 10) return v;
    return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
  };

  const fmtMoneyCel = (v) => {
    const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
    if (isNaN(n)) return v || "—";
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fmtDate = (str) => {
    if (!str) return "—";
    try {
      const d = new Date(str);
      if (isNaN(d)) return str;
      const date = d
        .toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
        .replace(",", "");
      const time = d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${date} ${time}`;
    } catch {
      return str;
    }
  };

  const VOTER_COLS = [
    {
      key: "origin_source",
      label: "Source",
      render: (v) => {
        if (!v)
          return (
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                px: 0.75,
                py: 0.3,
                borderRadius: "4px",
                bgcolor: "rgba(0,0,0,0.05)",
                color: C.muted,
              }}
            >
              WinRed
            </Box>
          );
        if (v === "social")
          return (
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                px: 0.75,
                py: 0.3,
                borderRadius: "4px",
                bgcolor: "rgba(29,161,242,0.1)",
                color: "#1da1f2",
              }}
            >
              Social
            </Box>
          );
        return (
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              px: 0.75,
              py: 0.3,
              borderRadius: "4px",
              bgcolor: "rgba(0,0,0,0.05)",
              color: C.muted,
            }}
          >
            {v}
          </Box>
        );
      },
    },
    { key: "first_name", label: "First Name" },
    { key: "last_name", label: "Last Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone", render: fmtPhone },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "total_donated", label: "Total Donated", render: fmtMoneyCel },
    { key: "donation_count", label: "Gifts" },
    {
      key: "social_username",
      label: "X Handle",
      render: (v) => (v ? `@${v}` : "—"),
    },
    {
      key: "research_status",
      label: "Enriched",
      render: (v) => (
        <Box
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            px: 0.75,
            py: 0.3,
            borderRadius: "4px",
            bgcolor:
              v === "researched" ? "rgba(22,163,74,0.12)" : "rgba(0,0,0,0.05)",
            color: v === "researched" ? "#16a34a" : C.muted,
          }}
        >
          {v === "researched" ? "Yes" : "No"}
        </Box>
      ),
    },
  ];

  const DONOR_COLS = [
    { key: "First Name", label: "First Name", clickable: true },
    { key: "Last Name", label: "Last Name", clickable: true },
    { key: "Email", label: "Email" },
    { key: "Phone", label: "Phone", render: fmtPhone },
    { key: "City", label: "City" },
    { key: "State", label: "State" },
    {
      key: "Most Recent Contribution",
      label: "Most Recent",
      render: fmtMoneyCel,
    },
    { key: "Average Contribution", label: "Avg Gift", render: fmtMoneyCel },
    { key: "Created At", label: "Last Gift", render: fmtDate },
    {
      key: "Cohort",
      label: "Cohort",
      render: (v) => <CohortBadge cohort={v} />,
    },
  ];

  const LEAD_COLS = [
    {
      key: "source_label",
      label: "Source",
      render: (v) => {
        const isFB = v === "Facebook";
        return (
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              px: 0.75,
              py: 0.3,
              borderRadius: "4px",
              bgcolor: isFB ? "rgba(24,119,242,0.1)" : "rgba(29,161,242,0.1)",
              color: isFB ? "#1877f2" : "#1da1f2",
            }}
          >
            {v || "—"}
          </Box>
        );
      },
    },
    { key: "first_name", label: "First Name" },
    { key: "last_name", label: "Last Name" },
    {
      key: "x_username",
      label: "Handle",
      render: (v) =>
        v ? (
          <Box component="span" sx={{ fontSize: 11, color: "#1da1f2" }}>
            @{v}
          </Box>
        ) : (
          "—"
        ),
    },
    {
      key: "author_followers",
      label: "Followers",
      render: (v) => {
        if (v == null || v === "") return "—";
        return v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v;
      },
    },
    {
      key: "author_following",
      label: "Following",
      render: (v) => {
        if (v == null || v === "") return "—";
        return v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v;
      },
    },
    { key: "location_raw", label: "Raw Location" },
    { key: "inferred_city", label: "City" },
    { key: "inferred_state", label: "State" },
    { key: "inferred_zip", label: "ZIP" },
    {
      key: "voter_party",
      label: "Party",
      render: (v) =>
        v ? (
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              px: 0.75,
              py: 0.3,
              borderRadius: "4px",
              bgcolor:
                v === "R" ? "rgba(220,38,38,0.1)" : "rgba(37,99,235,0.1)",
              color: v === "R" ? "#dc2626" : "#2563eb",
            }}
          >
            {v === "R" ? "Republican" : "Democrat"}
          </Box>
        ) : (
          "—"
        ),
    },
    {
      key: "voter_turnout",
      label: "Turnout",
      render: (_, row) => {
        const last = row.voter_general_last,
          prev = row.voter_general_previous;
        if (last == null && prev == null) return "—";
        if (last && prev)
          return (
            <Box
              component="span"
              sx={{ fontSize: 10, fontWeight: 600, color: "#16a34a" }}
            >
              Active
            </Box>
          );
        if (last || prev)
          return (
            <Box
              component="span"
              sx={{ fontSize: 10, fontWeight: 600, color: "#d97706" }}
            >
              Irregular
            </Box>
          );
        return (
          <Box component="span" sx={{ fontSize: 10, color: "#9ca3af" }}>
            Low
          </Box>
        );
      },
    },
    {
      key: "voter_district_cd",
      label: "CD",
      render: (v) => (v ? `CD-${v}` : "—"),
    },
    {
      key: "research_status",
      label: "Status",
      render: (v) => (
        <Box
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            px: 0.75,
            py: 0.3,
            borderRadius: "4px",
            bgcolor:
              v === "researched"
                ? "rgba(34,197,94,0.1)"
                : "rgba(156,163,175,0.15)",
            color: v === "researched" ? "#16a34a" : "#6b7280",
          }}
        >
          {v === "researched"
            ? "Matched"
            : v === "enrich_attempted"
              ? "No Match"
              : "Pending"}
        </Box>
      ),
    },
    { key: "created_at", label: "Date", render: fmtDate },
    {
      key: "_enrich",
      label: "",
      render: (_, row) => {
        const id = row.platform_user_id || row.x_username;
        const busy = enrichingLeadId === id;
        if (!row.x_username) return null;
        return (
          <Box
            component="span"
            onClick={() => !busy && enrichSingleLead(row)}
            sx={{
              fontSize: 10,
              fontWeight: 700,
              px: 0.75,
              py: 0.3,
              borderRadius: "4px",
              bgcolor: busy
                ? "rgba(156,163,175,0.15)"
                : "rgba(29,161,242,0.08)",
              color: busy ? C.muted : "#1da1f2",
              border: `1px solid ${busy ? C.border : "rgba(29,161,242,0.3)"}`,
              cursor: busy ? "default" : "pointer",
              display: "inline-block",
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: busy ? undefined : "rgba(29,161,242,0.15)",
              },
            }}
          >
            {busy ? "Running…" : "Enrich"}
          </Box>
        );
      },
    },
  ];

  const activeSort =
    subView === "leads"
      ? leadSort
      : subView === "voters"
        ? voterSort
        : donorSort;
  const setActiveSort =
    subView === "leads"
      ? setLeadSort
      : subView === "voters"
        ? setVoterSort
        : setDonorSort;
  const activeData =
    subView === "leads"
      ? leadsData
      : subView === "voters"
        ? votersData
        : donorsData;
  const activeLoading =
    subView === "leads"
      ? leadsLoading
      : subView === "voters"
        ? votersLoading
        : donorsLoading;
  const activeCols =
    subView === "leads"
      ? LEAD_COLS
      : subView === "voters"
        ? VOTER_COLS
        : DONOR_COLS;
  const activePage =
    subView === "leads"
      ? leadsPage
      : subView === "voters"
        ? votersPage
        : donorsPage;
  const setActivePage =
    subView === "leads"
      ? setLeadsPage
      : subView === "voters"
        ? setVotersPage
        : setDonorsPage;

  const handleSort = (colKey) => {
    const next = {
      col: colKey,
      dir:
        activeSort.col === colKey && activeSort.dir === "asc" ? "desc" : "asc",
    };
    setActiveSort(next);
    setActivePage(1);
  };

  const buildUrl = (type, page, sort, cohort = "", baseOverride = null) => {
    const base = baseOverride || winredUrl;
    let url = `${base}?type=${type}&page=${page}&sort=${encodeURIComponent(sort.col)}&dir=${sort.dir}`;
    if (campaignId) url += `&campaign_id=${campaignId}`;
    if (cohort) url += `&cohort=${encodeURIComponent(cohort)}`;
    return url;
  };

  useEffect(() => {
    if (!winredUrl || subView !== "voters") return;
    setVotersLoading(true);
    let url = `${winredUrl}?type=voters&page=${votersPage}&sort=${encodeURIComponent(voterSort.col)}&dir=${voterSort.dir}`;
    if (campaignId) url += `&campaign_id=${campaignId}`;
    if (voterFilterSource)
      url += `&source=${encodeURIComponent(voterFilterSource)}`;
    if (voterFilterCity) url += `&city=${encodeURIComponent(voterFilterCity)}`;
    if (voterFilterState)
      url += `&state=${encodeURIComponent(voterFilterState)}`;
    if (voterFilterZip) url += `&zip=${encodeURIComponent(voterFilterZip)}`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        setVotersData(d);
        setVotersLoading(false);
      })
      .catch(() => setVotersLoading(false));
  }, [
    winredUrl,
    subView,
    votersPage,
    voterSort,
    campaignId,
    voterFilterSource,
    voterFilterCity,
    voterFilterState,
    voterFilterZip,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!winredUrl || subView !== "donors") return;
    setDonorsLoading(true);
    fetch(buildUrl("donors", donorsPage, donorSort, cohortFilter))
      .then((r) => r.json())
      .then((d) => {
        setDonorsData(d);
        setDonorsLoading(false);
      })
      .catch(() => setDonorsLoading(false));
  }, [winredUrl, subView, donorsPage, donorSort, cohortFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!SOCIAL_API_URL || !campaignId || subView !== "leads") return;
    setLeadsLoading(true);
    let url = `${SOCIAL_API_URL}/leads?campaign_id=${campaignId}&page=${leadsPage}&sort=${encodeURIComponent(leadSort.col)}&dir=${leadSort.dir}`;
    if (leadsFilterState)
      url += `&state=${encodeURIComponent(leadsFilterState)}`;
    if (leadsFilterCity) url += `&city=${encodeURIComponent(leadsFilterCity)}`;
    if (leadsFilterZip) url += `&zip=${encodeURIComponent(leadsFilterZip)}`;
    if (leadsFilterCd) url += `&cd=${encodeURIComponent(leadsFilterCd)}`;
    if (leadsFilterParty.size === 1)
      url += `&party=${encodeURIComponent([...leadsFilterParty][0])}`;
    if (leadsFilterTurnout.size === 1)
      url += `&turnout=${encodeURIComponent([...leadsFilterTurnout][0])}`;
    authFetch(url) // PHP /social requires the bearer token — plain fetch returns empty_token
      .then((r) => r.json())
      .then((d) => {
        setLeadsData(d);
        setLeadsLoading(false);
      })
      .catch(() => setLeadsLoading(false));
  }, [
    subView,
    leadsPage,
    leadSort,
    campaignId,
    leadsRefreshTick,
    leadsFilterState,
    leadsFilterCity,
    leadsFilterZip,
    leadsFilterCd,
    leadsFilterParty,
    leadsFilterTurnout,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const enrichSingleLead = async (row) => {
    const handle = row.x_username || row.author_username;
    const profileUrl = process.env.REACT_APP_PROFILE_API_URL;
    if (!handle || !profileUrl) return;
    const id = row.platform_user_id || handle;
    setEnrichingLeadId(id);
    try {
      const authorName =
        [row.first_name, row.last_name].filter(Boolean).join(" ") ||
        row.author_name ||
        "";
      const resp = await authFetch(`${profileUrl}/enrich-social`, {
        method: "POST",
        body: JSON.stringify({
          twitter_handle: handle,
          author_name: authorName,
          author_location: row.location_raw || "",
          author_zip: row.inferred_zip || "",
          author_bio: row.author_bio || "",
          author_followers: row.author_followers || 0,
          author_following: row.author_following || 0,
          force: true,
        }),
      });
      const data = await resp.json();
      setEnrichModal({ open: true, handle, result: data, error: null });
    } catch (e) {
      setEnrichModal({ open: true, handle, result: null, error: e.message });
    } finally {
      setEnrichingLeadId(null);
    }
  };

  const thBase = {
    fontSize: 10,
    fontWeight: 700,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    padding: "6px 8px",
    borderBottom: `1px solid ${C.border}`,
    textAlign: "left",
    verticalAlign: "bottom",
    lineHeight: 1.3,
    cursor: "pointer",
    userSelect: "none",
  };
  const tdStyle = {
    padding: "6px 8px",
    fontSize: 11,
    color: C.charcoal,
    wordBreak: "break-word",
  };

  const NavBtn = ({ disabled, onClick, children }) => (
    <Box
      onClick={() => !disabled && onClick()}
      sx={{
        px: 1.5,
        py: 0.5,
        fontSize: 11,
        fontWeight: 600,
        border: `1px solid ${C.border}`,
        borderRadius: 1,
        cursor: disabled ? "default" : "pointer",
        color: disabled ? C.border : C.navy,
        bgcolor: C.white,
        "&:hover": { bgcolor: disabled ? C.white : C.bg },
      }}
    >
      {children}
    </Box>
  );

  const Paginator = ({ data, page, setPage, loading }) => {
    if (!data) return null;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Typography sx={{ fontSize: 11, color: C.muted }}>
          Page {data.page} of {(data.pages ?? 0).toLocaleString()} ·{" "}
          {(data.total ?? 0).toLocaleString()} records
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <NavBtn disabled={loading || page <= 1} onClick={() => setPage(1)}>
            «
          </NavBtn>
          <NavBtn
            disabled={loading || page <= 1}
            onClick={() => setPage(page - 1)}
          >
            ‹ Prev
          </NavBtn>
          <NavBtn
            disabled={loading || page >= data.pages}
            onClick={() => setPage(page + 1)}
          >
            Next ›
          </NavBtn>
          <NavBtn
            disabled={loading || page >= data.pages}
            onClick={() => setPage(data.pages)}
          >
            »
          </NavBtn>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {/* Donor Cohort Intelligence */}
      <Box sx={{ mb: 3 }}>
        <SectionHeader
          title="Donor Cohort Intelligence"
          sub={
            donorCohorts
              ? "Live WinRed cohort counts · Strategy metadata by segment"
              : "Loading live cohort data…"
          }
        />
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[
                  "Cohort",
                  "Count",
                  "Last Gift",
                  "Avg Gift",
                  "Priority",
                  "Channel",
                  "Frame",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "6px 10px",
                      borderBottom: `1px solid ${C.border}`,
                      textAlign: "left",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(donorCohorts
                ? COHORT_ORDER.filter((name) => donorCohorts[name] != null).map(
                    (name) => ({
                      name,
                      count: donorCohorts[name],
                      days: COHORT_DAYS[name],
                      ...COHORT_META[name],
                    }),
                  )
                : cd.donorCohorts.map((r) => ({
                    ...r,
                    days: `${r.lastGiftDays}d ago`,
                  }))
              ).map((row, i) => (
                <tr
                  key={i}
                  style={{
                    backgroundColor: i % 2 === 1 ? C.rowAlt : "transparent",
                  }}
                >
                  <td
                    style={{
                      padding: "7px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.charcoal,
                    }}
                  >
                    {row.name}
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      fontSize: 11,
                      color: C.charcoal,
                      fontWeight: donorCohorts ? 700 : 400,
                    }}
                  >
                    {row.count?.toLocaleString?.() ?? row.count}
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      fontSize: 11,
                      color: C.charcoal,
                    }}
                  >
                    {row.days ?? `${row.lastGiftDays}d ago`}
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      fontSize: 11,
                      color: C.charcoal,
                    }}
                  >
                    {fmtMoney(row.avgGift)}
                  </td>
                  <td style={{ padding: "7px 10px" }}>
                    <PriorityBadge priority={row.priority} />
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      fontSize: 11,
                      color: C.charcoal,
                    }}
                  >
                    {row.channel}
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      fontSize: 11,
                      color: C.charcoal,
                    }}
                  >
                    {row.frame}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            border: `1px solid ${C.border}`,
            borderRadius: 1,
            overflow: "hidden",
            width: "fit-content",
          }}
        >
          {[
            { key: "donors", label: "Donors" },
            { key: "donations", label: "Donations" },
          ].map(({ key, label }, i) => (
            <Box
              key={key}
              onClick={() => setSubView(key)}
              sx={{
                px: 2,
                py: 0.75,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                bgcolor: subView === key ? C.navy : C.white,
                color: subView === key ? C.white : C.muted,
                borderLeft: i > 0 ? `1px solid ${C.border}` : "none",
                transition: "all 0.15s",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={runSync}
          disabled={syncing || !campaignId}
          sx={{
            fontSize: 11,
            textTransform: "none",
            borderColor: C.border,
            color: C.muted,
            py: 0.5,
            px: 1.5,
            whiteSpace: "nowrap",
          }}
        >
          {syncing ? "Syncing…" : "Sync Data"}
        </Button>
        {syncMsg && (
          <Typography
            sx={{ fontSize: 11, color: C.muted, alignSelf: "center" }}
          >
            {syncMsg}
          </Typography>
        )}
      </Box>

      {subView === "donations" && (
        <DonationsTab onOpenProfile={onOpenProfile} campaignId={campaignId} />
      )}

      {subView === "donors" && (
        <Box
          sx={{
            mb: 1.5,
            px: "10px 0",
            fontSize: 11,
            color: C.muted,
            fontStyle: "italic",
          }}
        >
          WinRed export data · use <strong>Voters</strong> tab for unified voter
          records from all sources
        </Box>
      )}

      {subView !== "donations" && (
        <>
          {activeLoading && !activeData && <Loader />}

          {/* Enrich Leads CTA — social leads research workflow */}
          {subView === "leads" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                p: "10px 14px",
                border: `1px solid ${C.border}`,
                borderRadius: "8px",
                bgcolor: C.bg,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.navy,
                    mb: 0.25,
                  }}
                >
                  Research Social Leads
                </Typography>
                <Typography sx={{ fontSize: 11, color: C.muted }}>
                  Resolve location and identity for{" "}
                  {leadsData?.total?.toLocaleString() || "…"} social engagers —
                  location first, then identity + contact info.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  ml: 2,
                  flexShrink: 0,
                  alignItems: "flex-end",
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={importing || !campaignId}
                    onClick={() => {
                      setImportOpen(true);
                      setImportResult(null);
                    }}
                    sx={{
                      fontSize: 11,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Import Handles
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={locating || enriching || !campaignId}
                    onClick={() => onEnrichLocations?.()}
                    sx={{
                      fontSize: 11,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {locating ? "Running…" : "Enrich Locations"}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={enriching || locating || !campaignId}
                    onClick={() => onEnrichIdentity?.()}
                    sx={{
                      fontSize: 11,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      bgcolor: C.navy,
                      "&:hover": { bgcolor: C.charcoal },
                    }}
                  >
                    {enriching ? "Running…" : "Enrich Identity"}
                  </Button>
                </Box>
                {leadsEnrichProgress && (
                  <Typography
                    sx={{ fontSize: 11, color: C.muted, textAlign: "right" }}
                  >
                    {leadsEnrichProgress.processed} /{" "}
                    {leadsEnrichProgress.total} processed ·{" "}
                    {leadsEnrichProgress.matched ?? 0} identified ·{" "}
                    {leadsEnrichProgress.enriched ?? 0} total enriched
                  </Typography>
                )}
                {locating && !leadsEnrichProgress && (
                  <Typography
                    sx={{ fontSize: 11, color: C.muted, textAlign: "right" }}
                  >
                    Locating…
                  </Typography>
                )}
                {!locating && !leadsEnrichProgress && leadsMsg && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: C.muted,
                      textAlign: "right",
                      maxWidth: 360,
                    }}
                  >
                    {leadsMsg}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Leads filter bar */}
          {subView === "leads" &&
            (() => {
              const toggleParty = (v) => {
                const s = new Set(leadsFilterParty);
                s.has(v) ? s.delete(v) : s.add(v);
                setLeadsFilterParty(s);
                setLeadsPage(1);
              };
              const toggleTurnout = (v) => {
                const s = new Set(leadsFilterTurnout);
                s.has(v) ? s.delete(v) : s.add(v);
                setLeadsFilterTurnout(s);
                setLeadsPage(1);
              };
              const hasFilters =
                leadsFilterState ||
                leadsFilterCity ||
                leadsFilterZip ||
                leadsFilterCd ||
                leadsFilterParty.size ||
                leadsFilterTurnout.size;
              const chipSx = (active) => ({
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.4,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "6px",
                cursor: "pointer",
                border: "1px solid",
                borderColor: active ? C.navy : C.border,
                bgcolor: active ? C.navy : C.white,
                color: active ? C.white : C.muted,
                userSelect: "none",
              });
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  {/* State select */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: `1px solid ${C.border}`,
                      borderRadius: "6px",
                      overflow: "hidden",
                      bgcolor: C.white,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        px: 0.75,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      State
                    </Typography>
                    <select
                      value={leadsFilterState}
                      onChange={(e) => {
                        setLeadsFilterState(e.target.value);
                        setLeadsPage(1);
                      }}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: 11,
                        padding: "4px 6px",
                        fontFamily: "inherit",
                        background: "transparent",
                        cursor: "pointer",
                        minWidth: 52,
                      }}
                    >
                      <option value="">All</option>
                      {[
                        "AL",
                        "AK",
                        "AZ",
                        "AR",
                        "CA",
                        "CO",
                        "CT",
                        "DE",
                        "FL",
                        "GA",
                        "HI",
                        "ID",
                        "IL",
                        "IN",
                        "IA",
                        "KS",
                        "KY",
                        "LA",
                        "ME",
                        "MD",
                        "MA",
                        "MI",
                        "MN",
                        "MS",
                        "MO",
                        "MT",
                        "NE",
                        "NV",
                        "NH",
                        "NJ",
                        "NM",
                        "NY",
                        "NC",
                        "ND",
                        "OH",
                        "OK",
                        "OR",
                        "PA",
                        "RI",
                        "SC",
                        "SD",
                        "TN",
                        "TX",
                        "UT",
                        "VT",
                        "VA",
                        "WA",
                        "WV",
                        "WI",
                        "WY",
                        "DC",
                      ].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Box>
                  {/* City, ZIP, CD text inputs */}
                  {[
                    {
                      label: "City",
                      val: leadsFilterCity,
                      set: setLeadsFilterCity,
                      w: 110,
                    },
                    {
                      label: "ZIP",
                      val: leadsFilterZip,
                      set: setLeadsFilterZip,
                      w: 72,
                    },
                    {
                      label: "CD",
                      val: leadsFilterCd,
                      set: setLeadsFilterCd,
                      w: 60,
                    },
                  ].map(({ label, val, set, w }) => (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        border: `1px solid ${C.border}`,
                        borderRadius: "6px",
                        overflow: "hidden",
                        bgcolor: C.white,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.muted,
                          px: 0.75,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Typography>
                      <input
                        value={val}
                        onChange={(e) => {
                          set(e.target.value);
                          setLeadsPage(1);
                        }}
                        style={{
                          width: w,
                          border: "none",
                          outline: "none",
                          fontSize: 11,
                          padding: "4px 6px",
                          fontFamily: "inherit",
                        }}
                      />
                    </Box>
                  ))}
                  {/* Party chips */}
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {[
                      ["R", "Rep"],
                      ["D", "Dem"],
                    ].map(([v, lbl]) => (
                      <Box
                        key={v}
                        onClick={() => toggleParty(v)}
                        sx={chipSx(leadsFilterParty.has(v))}
                      >
                        {lbl}
                      </Box>
                    ))}
                  </Box>
                  {/* Turnout chips */}
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {["Active", "Irregular", "Low"].map((v) => (
                      <Box
                        key={v}
                        onClick={() => toggleTurnout(v.toLowerCase())}
                        sx={chipSx(leadsFilterTurnout.has(v.toLowerCase()))}
                      >
                        {v}
                      </Box>
                    ))}
                  </Box>
                  {hasFilters && (
                    <Box
                      onClick={() => {
                        setLeadsFilterState("");
                        setLeadsFilterCity("");
                        setLeadsFilterZip("");
                        setLeadsFilterCd("");
                        setLeadsFilterParty(new Set());
                        setLeadsFilterTurnout(new Set());
                        setLeadsPage(1);
                      }}
                      sx={{
                        fontSize: 11,
                        color: C.muted,
                        cursor: "pointer",
                        textDecoration: "underline",
                        ml: 0.5,
                      }}
                    >
                      Clear
                    </Box>
                  )}
                  {leadsData && hasFilters && (
                    <Typography
                      sx={{ fontSize: 11, color: C.muted, ml: "auto" }}
                    >
                      {(leadsData.total ?? 0).toLocaleString()} match
                    </Typography>
                  )}
                </Box>
              );
            })()}

          {/* Voters filter bar */}
          {subView === "voters" &&
            (() => {
              const hasFilters =
                voterFilterSource ||
                voterFilterCity ||
                voterFilterState ||
                voterFilterZip;
              const chipSx = (active) => ({
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.4,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "6px",
                cursor: "pointer",
                border: "1px solid",
                borderColor: active ? C.navy : C.border,
                bgcolor: active ? C.navy : C.white,
                color: active ? C.white : C.muted,
                userSelect: "none",
              });
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {[
                      ["social", "Social"],
                      ["winred", "WinRed"],
                    ].map(([v, lbl]) => (
                      <Box
                        key={v}
                        onClick={() => {
                          setVoterFilterSource(
                            voterFilterSource === v ? "" : v,
                          );
                          setVotersPage(1);
                        }}
                        sx={chipSx(voterFilterSource === v)}
                      >
                        {lbl}
                      </Box>
                    ))}
                  </Box>
                  {/* State select */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: `1px solid ${C.border}`,
                      borderRadius: "6px",
                      overflow: "hidden",
                      bgcolor: C.white,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        px: 0.75,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      State
                    </Typography>
                    <select
                      value={voterFilterState}
                      onChange={(e) => {
                        setVoterFilterState(e.target.value);
                        setVotersPage(1);
                      }}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: 11,
                        padding: "4px 6px",
                        fontFamily: "inherit",
                        background: "transparent",
                        cursor: "pointer",
                        minWidth: 52,
                      }}
                    >
                      <option value="">All</option>
                      {[
                        "AL",
                        "AK",
                        "AZ",
                        "AR",
                        "CA",
                        "CO",
                        "CT",
                        "DE",
                        "FL",
                        "GA",
                        "HI",
                        "ID",
                        "IL",
                        "IN",
                        "IA",
                        "KS",
                        "KY",
                        "LA",
                        "ME",
                        "MD",
                        "MA",
                        "MI",
                        "MN",
                        "MS",
                        "MO",
                        "MT",
                        "NE",
                        "NV",
                        "NH",
                        "NJ",
                        "NM",
                        "NY",
                        "NC",
                        "ND",
                        "OH",
                        "OK",
                        "OR",
                        "PA",
                        "RI",
                        "SC",
                        "SD",
                        "TN",
                        "TX",
                        "UT",
                        "VT",
                        "VA",
                        "WA",
                        "WV",
                        "WI",
                        "WY",
                        "DC",
                      ].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Box>
                  {/* City + ZIP text inputs */}
                  {[
                    {
                      label: "City",
                      val: voterFilterCity,
                      set: setVoterFilterCity,
                      w: 110,
                    },
                    {
                      label: "ZIP",
                      val: voterFilterZip,
                      set: setVoterFilterZip,
                      w: 72,
                    },
                  ].map(({ label, val, set, w }) => (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        border: `1px solid ${C.border}`,
                        borderRadius: "6px",
                        overflow: "hidden",
                        bgcolor: C.white,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.muted,
                          px: 0.75,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Typography>
                      <input
                        value={val}
                        onChange={(e) => {
                          set(e.target.value);
                          setVotersPage(1);
                        }}
                        style={{
                          width: w,
                          border: "none",
                          outline: "none",
                          fontSize: 11,
                          padding: "4px 6px",
                          fontFamily: "inherit",
                        }}
                      />
                    </Box>
                  ))}
                  {hasFilters && (
                    <Box
                      onClick={() => {
                        setVoterFilterSource("");
                        setVoterFilterCity("");
                        setVoterFilterState("");
                        setVoterFilterZip("");
                        setVotersPage(1);
                      }}
                      sx={{
                        fontSize: 11,
                        color: C.muted,
                        cursor: "pointer",
                        textDecoration: "underline",
                        ml: 0.5,
                      }}
                    >
                      Clear
                    </Box>
                  )}
                  {votersData && hasFilters && (
                    <Typography
                      sx={{ fontSize: 11, color: C.muted, ml: "auto" }}
                    >
                      {(votersData.total ?? 0).toLocaleString()} match
                    </Typography>
                  )}
                </Box>
              );
            })()}

          {/* Import Handles modal */}
          <Dialog
            open={importOpen}
            onClose={() => !importing && setImportOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: 14, fontWeight: 700, pb: 1 }}>
              Import X Handles
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: 12, color: C.muted, mb: 1.5 }}>
                Paste Twitter/X handles, one per line or comma-separated. The @
                sign is optional.
              </Typography>
              <textarea
                value={importDraft}
                onChange={(e) => setImportDraft(e.target.value)}
                placeholder={"@handle1\n@handle2\nhandle3"}
                rows={8}
                style={{
                  width: "100%",
                  fontFamily: "monospace",
                  fontSize: 12,
                  padding: "8px",
                  border: `1px solid #ccc`,
                  borderRadius: 4,
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
              {importResult && (
                <Typography
                  sx={{
                    fontSize: 12,
                    mt: 1,
                    color: importResult.error ? "error.main" : C.green,
                  }}
                >
                  {importResult.error
                    ? `Error: ${importResult.error}`
                    : `Imported ${importResult.imported} of ${importResult.total} handles${importResult.not_found > 0 ? ` (${importResult.not_found} not found on X)` : ""}.`}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                size="small"
                onClick={() => setImportOpen(false)}
                disabled={importing}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={importing || !importDraft.trim()}
                onClick={async () => {
                  const handles = importDraft
                    .split(/[\n,]+/)
                    .map((h) => h.trim())
                    .filter(Boolean);
                  if (!handles.length) return;
                  setImporting(true);
                  setImportResult(null);
                  try {
                    const result = await onImportHandles?.(handles);
                    setImportResult(result || { error: "No response" });
                    if (!result?.error) {
                      setImportDraft("");
                    }
                  } catch (e) {
                    setImportResult({ error: e.message });
                  } finally {
                    setImporting(false);
                  }
                }}
                sx={{
                  textTransform: "none",
                  fontSize: 12,
                  bgcolor: C.navy,
                  "&:hover": { bgcolor: C.charcoal },
                }}
              >
                {importing ? "Importing…" : "Import"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Single-lead enrich result dialog */}
          <Dialog
            open={enrichModal.open}
            onClose={() => setEnrichModal((m) => ({ ...m, open: false }))}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: 14, fontWeight: 700, pb: 0.5 }}>
              Enrich Result ·{" "}
              <Box component="span" sx={{ color: "#1da1f2" }}>
                @{enrichModal.handle}
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              {enrichModal.error && (
                <Typography sx={{ fontSize: 12, color: "error.main", mb: 1 }}>
                  Error: {enrichModal.error}
                </Typography>
              )}
              {enrichModal.result &&
                (() => {
                  const r = enrichModal.result;
                  const layers = r.enrichment_layers || [];
                  const layerColor = {
                    datagma: "#16a34a",
                    pdl: "#7c3aed",
                    apollo: "#f59e0b",
                    fec: "#16a34a",
                    proxycurl: "#2563eb",
                    "claude-pdl-only": "#6b7280",
                    "claude-proxycurl": "#2563eb",
                  };
                  const fields = [
                    { label: "Full Name", val: r.full_name },
                    { label: "Email", val: r.email },
                    { label: "Phone", val: r.phone },
                    { label: "Job Title", val: r.job_title },
                    { label: "Company", val: r.company },
                    { label: "City", val: r.city },
                    { label: "State", val: r.state },
                    { label: "Location", val: r.location_name },
                    { label: "ZIP", val: r.location_postal_code },
                    { label: "LinkedIn", val: r.linkedin_url },
                    { label: "Bio", val: r.bio, full: true },
                  ];
                  return (
                    <>
                      {/* Layers */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.75,
                          flexWrap: "wrap",
                          mb: 2,
                        }}
                      >
                        {layers.length === 0 ? (
                          <Typography sx={{ fontSize: 11, color: C.muted }}>
                            No enrichment layers fired
                          </Typography>
                        ) : (
                          layers.map((l) => (
                            <Box
                              key={l}
                              component="span"
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                px: 0.75,
                                py: 0.3,
                                borderRadius: "4px",
                                bgcolor: `${layerColor[l] || C.navy}18`,
                                color: layerColor[l] || C.navy,
                                border: `1px solid ${layerColor[l] || C.navy}40`,
                              }}
                            >
                              {l}
                            </Box>
                          ))
                        )}
                      </Box>

                      {/* Key fields grid */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "4px 16px",
                          mb: 2,
                        }}
                      >
                        {fields.map(({ label, val, full }) => (
                          <Box
                            key={label}
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "baseline",
                              borderBottom: `1px solid ${C.border}`,
                              pb: 0.5,
                              ...(full ? { gridColumn: "1 / -1" } : {}),
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: C.muted,
                                minWidth: 72,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: val ? C.charcoal : C.muted,
                                wordBreak: "break-word",
                              }}
                            >
                              {val || "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Per-source raw blocks */}
                      {[
                        {
                          key: "datagma",
                          label: "Datagma raw",
                          color: "#16a34a",
                        },
                        { key: "pdl", label: "PDL raw", color: "#7c3aed" },
                        {
                          key: "apollo",
                          label: "Apollo raw",
                          color: "#f59e0b",
                        },
                        { key: "disc", label: "DISC raw", color: "#2563eb" },
                      ].map(({ key, label, color }) => {
                        const val = r[key];
                        if (!val || typeof val !== "object") return null;
                        const isEmpty = Object.keys(val).length === 0;
                        return (
                          <Box key={key} sx={{ mb: 1.5 }}>
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                mb: 0.5,
                              }}
                            >
                              {label}{" "}
                              {isEmpty ? (
                                <Box
                                  component="span"
                                  sx={{ color: C.muted, fontWeight: 400 }}
                                >
                                  (no data)
                                </Box>
                              ) : (
                                ""
                              )}
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                fontSize: 10,
                                bgcolor: C.bg,
                                border: `1px solid ${isEmpty ? C.border : color + "40"}`,
                                borderRadius: 1,
                                p: 1,
                                overflowX: "auto",
                                maxHeight: 160,
                                m: 0,
                                color: isEmpty ? C.muted : "inherit",
                              }}
                            >
                              {isEmpty ? "{}" : JSON.stringify(val, null, 2)}
                            </Box>
                          </Box>
                        );
                      })}
                    </>
                  );
                })()}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                size="small"
                onClick={() => setEnrichModal((m) => ({ ...m, open: false }))}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Enrich All Records CTA — only for WinRed donors subview */}
          {subView === "donors" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                p: "10px 14px",
                border: `1px solid ${C.border}`,
                borderRadius: "8px",
                bgcolor: C.bg,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.navy,
                    mb: 0.25,
                  }}
                >
                  Unlock Intelligence for Your Entire Donor List
                </Typography>
                <Typography sx={{ fontSize: 11, color: C.muted }}>
                  DISC profiles, FEC giving history, and personalized ask
                  strategies for every record.
                  {donorsData
                    ? donorsData.total != null
                      ? ` ${donorsData.total.toLocaleString()} donors · est. $${(donorsData.total * 0.8).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total`
                      : ""
                    : ""}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => setEnrichAllOpen(true)}
                sx={{
                  ml: 2,
                  flexShrink: 0,
                  bgcolor: C.green,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "6px",
                  px: 2,
                  py: 0.75,
                  "&:hover": { bgcolor: "#3aaf6a" },
                }}
              >
                Enrich All Records
              </Button>
            </Box>
          )}

          {/* Cohort filter chips — only for donors subview */}
          {subView === "donors" && donorsData?.cohort_counts && (
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
              {Object.entries(donorsData.cohort_counts)
                .sort((a, b) => b[1] - a[1])
                .map(([cohort, count]) => {
                  const active = cohortFilter === cohort;
                  return (
                    <Box
                      key={cohort}
                      onClick={() => {
                        setCohortFilter(active ? "" : cohort);
                        setDonorsPage(1);
                      }}
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: 12,
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        borderRadius: "4px",
                        cursor: "pointer",
                        userSelect: "none",
                        bgcolor: C.bg,
                        color: active
                          ? COHORT_COLORS[cohort] || C.navy
                          : C.charcoal,
                        border: `1px solid ${active ? COHORT_COLORS[cohort] || C.navy : C.border}`,
                        "&:hover": {
                          borderColor: COHORT_COLORS[cohort] || C.navy,
                          color: COHORT_COLORS[cohort] || C.navy,
                        },
                      }}
                    >
                      {cohort}
                      <Box
                        component="span"
                        sx={{
                          fontSize: 9,
                          fontWeight: 800,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: "4px",
                          bgcolor: C.white,
                        }}
                      >
                        {count.toLocaleString()}
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          )}

          {activeData && activeData.rows && (
            <>
              <Box sx={{ overflowX: "auto", mx: { xs: -0.5, md: 0 } }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                    minWidth: 600,
                  }}
                >
                  <thead>
                    <tr>
                      {activeCols.map((col) => {
                        const isActive = activeSort.col === col.key;
                        return (
                          <th
                            key={col.key}
                            style={{
                              ...thBase,
                              color: isActive ? C.navy : C.muted,
                            }}
                            onClick={() => handleSort(col.key)}
                          >
                            {col.label}
                            {isActive
                              ? activeSort.dir === "asc"
                                ? " ↑"
                                : " ↓"
                              : ""}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {activeData.rows.map((row, i) => (
                      <tr
                        key={i}
                        style={{
                          backgroundColor:
                            i % 2 === 1 ? C.rowAlt : "transparent",
                          cursor: subView === "voters" ? "pointer" : "default",
                        }}
                        onClick={
                          subView === "voters"
                            ? () =>
                                onOpenProfile &&
                                onOpenProfile(
                                  row["Email"] || row.email || "",
                                  row,
                                )
                            : undefined
                        }
                      >
                        {activeCols.map((col) => (
                          <td
                            key={col.key}
                            style={{
                              ...tdStyle,
                              ...(col.clickable
                                ? {
                                    cursor: "pointer",
                                    color: C.navy,
                                    fontWeight: 600,
                                    textDecoration: "underline",
                                  }
                                : {}),
                            }}
                            onClick={
                              col.clickable
                                ? (e) => {
                                    e.stopPropagation();
                                    onOpenProfile &&
                                      onOpenProfile(row["Email"], row);
                                  }
                                : undefined
                            }
                          >
                            {col.render
                              ? col.render(row[col.key], row)
                              : row[col.key] || "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              <Paginator
                data={activeData}
                page={activePage}
                setPage={setActivePage}
                loading={activeLoading}
              />
            </>
          )}

          {(!activeData || !activeData.rows) && !activeLoading && (
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              {winredUrl
                ? "Failed to load data."
                : "REACT_APP_WINRED_API_URL not configured."}
            </Typography>
          )}

          {/* Enrich All Records modal */}
          {enrichAllOpen &&
            (() => {
              const total = donorsData?.total || 0;
              const costPer = 0.8;
              const totalCost = total * costPer;
              return (
                <Box
                  onClick={() => setEnrichAllOpen(false)}
                  sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 2100,
                    bgcolor: "rgba(0,0,0,0.55)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                  }}
                >
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      bgcolor: "#0f1f36",
                      color: "#e8edf4",
                      borderRadius: "12px",
                      p: "28px 32px 24px",
                      maxWidth: 540,
                      width: "100%",
                      position: "relative",
                      boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Box
                      onClick={() => setEnrichAllOpen(false)}
                      sx={{
                        position: "absolute",
                        top: 14,
                        right: 16,
                        cursor: "pointer",
                        fontSize: 18,
                        color: "rgba(255,255,255,0.4)",
                        lineHeight: 1,
                        "&:hover": { color: "#fff" },
                      }}
                    >
                      ✕
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 800,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.45)",
                        mb: 0.5,
                      }}
                    >
                      Bulk Enrichment
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#fff",
                        mb: 0.4,
                      }}
                    >
                      Enrich All Donor Records
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontStyle: "italic",
                        color: "rgba(255,255,255,0.5)",
                        mb: 2.5,
                      }}
                    >
                      One-time batch · results cached 30 days per record
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "rgba(255,255,255,0.75)",
                        mb: 1.5,
                        lineHeight: 1.6,
                      }}
                    >
                      Each enriched record unlocks a full intelligence package:
                    </Typography>
                    {[
                      [
                        "Identity & Professional Profile",
                        "Employer, title, LinkedIn, location — sourced from PDL and NinjaPear.",
                      ],
                      [
                        "DISC Personality Assessment",
                        "Behavioral archetype that drives donation page layout, headline, and CTA copy.",
                      ],
                      [
                        "Federal Giving History",
                        "Cross-campaign FEC contributions showing total giving, cycles, and party loyalty.",
                      ],
                      [
                        "Donor Intelligence Card",
                        "Summary synthesizing all data sources into a campaign briefing.",
                      ],
                      [
                        "Personalized Ask Strategy",
                        "Tier-by-tier recommended amounts and framing tuned to each donor's profile.",
                      ],
                    ].map(([label, desc]) => (
                      <Box
                        key={label}
                        sx={{ display: "flex", gap: 1.5, mb: 1.25 }}
                      >
                        <Box
                          sx={{
                            mt: 0.45,
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: C.green,
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#fff",
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "rgba(255,255,255,0.55)",
                              mt: 0.2,
                            }}
                          >
                            {desc}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        mt: 2.5,
                        mb: 2.5,
                        p: "14px 18px",
                        borderRadius: "8px",
                        bgcolor: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.75,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}
                        >
                          Total donor records
                        </Typography>
                        <Typography
                          sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}
                        >
                          {total.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.75,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}
                        >
                          Cost per enrichment
                        </Typography>
                        <Typography
                          sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}
                        >
                          ${costPer.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          borderTop: "1px solid rgba(255,255,255,0.1)",
                          pt: 0.75,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}
                        >
                          Estimated total
                        </Typography>
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 800, color: C.green }}
                        >
                          $
                          {totalCost.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.35)",
                        fontStyle: "italic",
                        mb: 2.5,
                      }}
                    >
                      Existing enriched records are skipped — you're only
                      charged for unenriched donors.
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => setEnrichAllOpen(false)}
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "none",
                          color: "rgba(255,255,255,0.55)",
                          px: 2,
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "none",
                          bgcolor: C.green,
                          color: "#fff",
                          px: 2.5,
                          borderRadius: "6px",
                          "&:hover": { bgcolor: "#3aaf6a" },
                        }}
                        disabled={enrichLoading}
                        onClick={async () => {
                          if (!profileUrl) return;
                          setEnrichLoading(true);
                          setEnrichProgress(null);
                          try {
                            const res = await fetch(`${profileUrl}`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ limit: 20 }),
                            });
                            const data = await res.json();
                            setEnrichProgress(res.ok ? data : "error");
                          } catch {
                            setEnrichProgress("error");
                          } finally {
                            setEnrichLoading(false);
                          }
                        }}
                      >
                        {enrichLoading
                          ? "Queuing…"
                          : enrichProgress && enrichProgress !== "error"
                            ? "Queue Another Batch"
                            : "Start Bulk Enrichment"}
                      </Button>
                    </Box>
                    {enrichProgress && enrichProgress !== "error" && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: "rgba(22,163,74,0.12)",
                          borderRadius: "8px",
                          border: "1px solid rgba(22,163,74,0.3)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#16a34a",
                          }}
                        >
                          ✓ {enrichProgress.queued} enrichment
                          {enrichProgress.queued !== 1 ? "s" : ""} queued
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "rgba(255,255,255,0.6)",
                            mt: 0.5,
                          }}
                        >
                          {enrichProgress.skipped} already enriched ·{" "}
                          {enrichProgress.total} total donors
                        </Typography>
                      </Box>
                    )}
                    {enrichProgress === "error" && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: "rgba(220,38,38,0.12)",
                          borderRadius: "8px",
                          border: "1px solid rgba(220,38,38,0.3)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#dc2626",
                          }}
                        >
                          Enrichment failed — check console
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })()}
        </>
      )}

      {/* Voter/Donor/Lead rows all open the unified ProfileDrawer via onOpenProfile (app level). */}
    </Box>
  );
};

// ─── Donations Tab ─────────────────────────────────────────────────────────
const DonationsTab = ({ onOpenProfile, campaignId }) => {
  const { authFetch } = useAuth();
  const winredUrl = PHP_API_URL ? `${PHP_API_URL}/winred` : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ col: "Created At", dir: "desc" });

  const fmtAmount = (v) => {
    const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
    if (isNaN(n)) return v || "—";
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fmtDate = (str) => {
    if (!str) return "—";
    try {
      const d = new Date(str);
      if (isNaN(d)) return str;
      const date = d
        .toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
        .replace(",", "");
      const time = d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${date} ${time}`;
    } catch {
      return str;
    }
  };

  const STATUS_COLOR = {
    completed: C.green,
    succeeded: C.green,
    failed: C.red,
    refunded: C.orange,
    refund: C.orange,
    chargeback: C.red,
    cancelled: C.muted,
    canceled: C.muted,
  };

  const COLS = [
    { key: "Created At", label: "Date", render: fmtDate },
    { key: "Amount", label: "Amount", render: fmtAmount },
    {
      key: "Status",
      label: "Status",
      render: (v) => {
        const color = STATUS_COLOR[(v || "").toLowerCase()] || C.charcoal;
        return (
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              px: 1,
              py: 0.3,
              borderRadius: "3px",
              bgcolor: color + "18",
              border: `1px solid ${color}44`,
              color,
              display: "inline-block",
            }}
          >
            {v || "—"}
          </Box>
        );
      },
    },
    { key: "First Name", label: "First Name", clickable: true },
    { key: "Last Name", label: "Last Name", clickable: true },
    { key: "Email", label: "Email" },
    { key: "State", label: "State" },
    { key: "Source Code", label: "Source" },
  ];

  const handleSort = (colKey) => {
    const next = {
      col: colKey,
      dir: sort.col === colKey && sort.dir === "asc" ? "desc" : "asc",
    };
    setSort(next);
    setPage(1);
  };

  const buildUrl = (p, s) =>
    `${winredUrl}?type=donations&page=${p}&sort=${encodeURIComponent(s.col)}&dir=${s.dir}${campaignId ? `&campaign_id=${campaignId}` : ""}`;

  useEffect(() => {
    if (!winredUrl) return;
    setLoading(true);
    authFetch(buildUrl(page, sort))
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [winredUrl, page, sort, campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const thBase = {
    fontSize: 10,
    fontWeight: 700,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    padding: "6px 8px",
    borderBottom: `1px solid ${C.border}`,
    textAlign: "left",
    verticalAlign: "bottom",
    lineHeight: 1.3,
    cursor: "pointer",
    userSelect: "none",
  };
  const tdStyle = {
    padding: "6px 8px",
    fontSize: 11,
    color: C.charcoal,
    wordBreak: "break-word",
  };

  const NavBtn = ({ disabled, onClick, children }) => (
    <Box
      onClick={() => !disabled && onClick()}
      sx={{
        px: 1.5,
        py: 0.5,
        fontSize: 11,
        fontWeight: 600,
        border: `1px solid ${C.border}`,
        borderRadius: 1,
        cursor: disabled ? "default" : "pointer",
        color: disabled ? C.border : C.navy,
        bgcolor: C.white,
        "&:hover": { bgcolor: disabled ? C.white : C.bg },
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box>
      {loading && <Loader text="Loading donations…" />}

      {data?.rows?.length > 0 && !loading && (
        <>
          <Box sx={{ overflowX: "auto", mx: { xs: -0.5, md: 0 } }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
                minWidth: 560,
              }}
            >
              <thead>
                <tr>
                  {COLS.map((col) => {
                    const isActive = sort.col === col.key;
                    return (
                      <th
                        key={col.key}
                        style={{
                          ...thBase,
                          color: isActive ? C.navy : C.muted,
                        }}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                        {isActive ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr
                    key={i}
                    style={{
                      backgroundColor: i % 2 === 1 ? C.rowAlt : "transparent",
                    }}
                  >
                    {COLS.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          ...tdStyle,
                          ...(col.clickable
                            ? {
                                cursor: "pointer",
                                color: C.navy,
                                fontWeight: 600,
                                textDecoration: "underline",
                              }
                            : {}),
                        }}
                        onClick={
                          col.clickable
                            ? () =>
                                onOpenProfile &&
                                onOpenProfile(row["Email"], row)
                            : undefined
                        }
                      >
                        {col.render
                          ? col.render(row[col.key], row)
                          : row[col.key] || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Typography sx={{ fontSize: 11, color: C.muted }}>
              Page {data.page} of {(data.pages ?? 0).toLocaleString()} ·{" "}
              {(data.total ?? 0).toLocaleString()} transactions
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <NavBtn
                disabled={loading || page <= 1}
                onClick={() => setPage(1)}
              >
                «
              </NavBtn>
              <NavBtn
                disabled={loading || page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ‹ Prev
              </NavBtn>
              <NavBtn
                disabled={loading || page >= data.pages}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </NavBtn>
              <NavBtn
                disabled={loading || page >= data.pages}
                onClick={() => setPage(data.pages)}
              >
                »
              </NavBtn>
            </Box>
          </Box>
        </>
      )}

      {!loading && (!data || !data.rows) && (
        <Typography sx={{ fontSize: 12, color: C.muted }}>
          {!winredUrl
            ? "REACT_APP_WINRED_API_URL not configured."
            : data?.error
              ? `Error: ${data.error}`
              : "Failed to load data."}
        </Typography>
      )}
    </Box>
  );
};

// ─── Cohort metadata: strategy fields are campaign decisions, counts come from live API ─
const COHORT_META = {
  "High Propensity": {
    priority: "High",
    channel: "Email",
    frame: "Legislative win",
    avgGift: 127,
  },
  "Lapsed 90-180d": {
    priority: "High",
    channel: "Email",
    frame: "Opponent threat",
    avgGift: 52,
  },
  "Lapsed 180-365d": {
    priority: "Medium",
    channel: "Email + SMS",
    frame: "Primary urgency",
    avgGift: 38,
  },
  "New Donor": {
    priority: "Medium",
    channel: "Email",
    frame: "Welcome + engage",
    avgGift: 35,
  },
  "Major Donor": {
    priority: "High",
    channel: "Phone + Email",
    frame: "Personal ask",
    avgGift: 843,
  },
  "One-Time Donor": {
    priority: "Low",
    channel: "Email",
    frame: "Re-engagement",
    avgGift: 29,
  },
};
const COHORT_DAYS = {
  "High Propensity": "< 30d",
  "New Donor": "< 90d",
  "Lapsed 90-180d": "90–180d",
  "Lapsed 180-365d": "180–365d",
  "Major Donor": "varies",
  "One-Time Donor": "365d+",
};
const COHORT_ORDER = [
  "High Propensity",
  "Major Donor",
  "Lapsed 90-180d",
  "New Donor",
  "Lapsed 180-365d",
  "One-Time Donor",
];

// ─── Compute Fundraising Drivers from live data ────────────────────────────
const computeDrivers = (
  kalshi,
  winredData,
  candidateCoh,
  opponentCoh,
  latestPoll = {},
) => {
  // Kalshi signals
  const hMkts = kalshi?.series?.CONTROLH?.markets || [];
  const dHouseP =
    hMkts.find(
      (m) =>
        m.ticker?.endsWith("-D") || m.title?.toLowerCase().includes("democrat"),
    )?.last_price ?? 0.81;
  const recP = kalshi?.series?.KXRECSSNBER?.markets?.[0]?.last_price ?? 0.23;
  const appMkts = kalshi?.series?.KXAPRPOTUS?.markets || [];
  const appP =
    appMkts.reduce(
      (best, m) =>
        (m.open_interest ?? 0) > (best?.open_interest ?? 0) ? m : best,
      appMkts[0],
    )?.last_price ?? 0.44;
  const gbMkts = kalshi?.series?.KXGENERICBALLOTVOTEHUB?.markets || [];
  const gbDP =
    gbMkts.find(
      (m) =>
        m.title?.toLowerCase().includes("democrat") || m.ticker?.endsWith("-D"),
    )?.last_price ?? 0.5;

  // WinRed velocity MoM
  const monthly = winredData?.monthly || [];
  let velR = 0.5;
  if (monthly.length >= 2) {
    const _mnC = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const _nowC = new Date();
    const _curLabelC = `${_mnC[_nowC.getMonth()]} '${String(_nowC.getFullYear()).slice(2)}`;
    const _completed = monthly.filter((m) => m.month !== _curLabelC);
    const last = _completed[_completed.length - 1]?.raised || 0;
    const prev = _completed[_completed.length - 2]?.raised || 1;
    velR = Math.min(0.99, Math.max(-0.99, (last - prev) / prev));
  }

  // Poll position — candidate key is always "candidate"; opponents are all other numeric keys
  const candidatePct = latestPoll.candidate ?? 0;
  const oppValues = Object.entries(latestPoll)
    .filter(
      ([k]) =>
        k !== "candidate" &&
        k !== "Undecided" &&
        typeof latestPoll[k] === "number",
    )
    .map(([, v]) => v);
  const fieldMax = oppValues.length ? Math.max(...oppValues) : 0;
  const pollGap = +(candidatePct - fieldMax).toFixed(1);
  const cohDiff = candidateCoh - opponentCoh;

  const sig = (v, hi, lo) =>
    v >= hi
      ? "Strong Tailwind"
      : v >= (hi + lo) / 2
        ? "Tailwind"
        : v >= lo
          ? "Neutral"
          : v < 0
            ? "Headwind"
            : "Headwind";

  return [
    {
      label: "Primary Competitiveness",
      signal:
        pollGap > 2
          ? "Strong Tailwind"
          : pollGap > 0
            ? "Tailwind"
            : pollGap > -3
              ? "Neutral"
              : "Headwind",
      r: +Math.min(0.99, Math.max(-0.99, 0.5 + pollGap * 0.04)).toFixed(2),
      interpretation: `Candidate at ${candidatePct}% — ${pollGap >= 0 ? `+${pollGap}pts` : `${pollGap}pts`} vs field leader. ${pollGap > 0 ? "Live primary with path to victory is the strongest driver of small-dollar urgency." : "Trailing position — underdog urgency frame drives higher response rates."}`,
    },
    {
      label: cohDiff >= 0 ? "CoH Advantage" : "CoH Deficit vs Opponent",
      signal:
        cohDiff >= 500000
          ? "Strong Tailwind"
          : cohDiff >= 0
            ? "Tailwind"
            : cohDiff >= -500000
              ? "Headwind"
              : "Headwind",
      r: +Math.min(0.99, Math.max(-0.99, cohDiff / 2000000)).toFixed(2),
      interpretation: `${cohDiff >= 0 ? `+${fmtMoney(cohDiff)} CoH lead` : `${fmtMoney(Math.abs(cohDiff))} behind opponent`}. ${cohDiff < 0 ? "War-chest gap is the single most effective donor urgency frame — cite the disparity directly." : "CoH lead signals operational strength; accelerate to extend the advantage into primary sprint."}`,
    },
    {
      label: "Kalshi Wave Signal",
      signal:
        dHouseP >= 0.7
          ? "Strong Tailwind"
          : dHouseP >= 0.55
            ? "Tailwind"
            : dHouseP >= 0.45
              ? "Neutral"
              : "Headwind",
      r: +Math.min(0.99, Math.max(-0.99, (dHouseP - 0.5) * 1.6 + 0.3)).toFixed(
        2,
      ),
      interpretation: `D+${Math.round(dHouseP * 100)}¢ to flip House (Kalshi live). ${dHouseP >= 0.6 ? "Anti-establishment wave creates outsider narrative that resonates with the challenger brand against establishment figures." : "Wave signal softening — lean on local contrast with primary opponents rather than national environment."}`,
    },
    {
      label: "Presidential Approval",
      signal: appP >= 0.52 ? "Tailwind" : appP >= 0.42 ? "Neutral" : "Headwind",
      r: +Math.min(0.99, Math.max(-0.99, (appP - 0.5) * 1.5)).toFixed(2),
      interpretation: `Trump approval market ${Math.round(appP * 100)}¢ (most-liquid Kalshi market). ${appP >= 0.5 ? "Approval above 50¢ energizes GOP base — MAGA-aligned messaging maximizes turnout response." : "Sub-50¢ approval suppresses base energy; tie the candidate to Trump's core agenda on border and spending to offset drag."}`,
    },
    {
      label: "Generic Ballot (D–R)",
      signal: gbDP >= 0.58 ? "Headwind" : gbDP <= 0.45 ? "Tailwind" : "Neutral",
      r: +Math.min(0.99, Math.max(-0.99, 0.5 - gbDP)).toFixed(2),
      interpretation: `Generic ballot D-lead market at ${Math.round(gbDP * 100)}¢. ${gbDP >= 0.55 ? "Challenging national environment for Rs — frame June 9 primary as the decisive election, not November." : "R-favorable generic ballot boosts base enthusiasm heading into primary sprint."}`,
    },
    {
      label: "Donation Velocity Momentum",
      signal:
        velR > 0.15
          ? "Strong Tailwind"
          : velR > 0
            ? "Tailwind"
            : velR > -0.15
              ? "Neutral"
              : "Headwind",
      r: +velR.toFixed(2),
      interpretation:
        monthly.length >= 2
          ? `WinRed ${velR >= 0 ? "+" : ""}${(velR * 100).toFixed(0)}% MoM. ${velR > 0.15 ? "Accelerating — sustain with high-frequency communication through primary." : velR > 0 ? "Positive but slowing — reactivate lapsed cohorts to re-accelerate." : "Declining velocity — launch emergency re-engagement sequences immediately."}`
          : "WinRed velocity loading...",
    },
    {
      label: "Recession Risk",
      signal: recP >= 0.32 ? "Headwind" : recP >= 0.2 ? "Neutral" : "Tailwind",
      r: +Math.min(0.99, Math.max(-0.99, -(recP - 0.15) * 2.5)).toFixed(2),
      interpretation: `${Math.round(recP * 100)}% NBER recession probability (Kalshi live). ${recP >= 0.28 ? "Economic anxiety reduces discretionary giving — reframe donation as political urgency, not charity." : "Stable outlook; donors less price-sensitive. Opportunity to push higher ask amounts."}`,
    },
  ];
};

// ─── Chart skeleton loader ─────────────────────────────────────────────────
const ChartSkeleton = ({ height = 160 }) => (
  <Box
    sx={{
      height,
      borderRadius: 1,
      bgcolor: "#f3f4f6",
      position: "relative",
      overflow: "hidden",
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%)",
        backgroundSize: "200% 100%",
        animation: "skeletonShimmer 1.4s infinite",
      },
      "@keyframes skeletonShimmer": {
        "0%": { backgroundPosition: "-200% 0" },
        "100%": { backgroundPosition: "200% 0" },
      },
    }}
  />
);

// ─── Card skeleton loader ───────────────────────────────────────────────────
const CardSkeleton = ({ count = 4, height = 80 }) => (
  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={i}
        sx={{
          flex: 1,
          minWidth: 130,
          height,
          bgcolor: "#f0f4f8",
          borderRadius: 1,
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%)",
            backgroundSize: "200% 100%",
            animation: "cardShimmer 1.5s infinite",
            "@keyframes cardShimmer": {
              "0%": { backgroundPosition: "200% 0" },
              "100%": { backgroundPosition: "-200% 0" },
            },
          },
        }}
      />
    ))}
  </Box>
);

// ─── Intelligence loader ─────────────────────────────────────────────────────
// THE canonical loading state for every AI intelligence section (brief, tab summaries,
// polling/messaging/policy intel). helix spinner + skeleton bars — shown until content is
// ready, never an interim/alternate loader. Optional `steps` renders cascade progress
// (e.g. the Daily Brief synthesizing from each intelligence tab).
const IntelLoader = ({
  size = 32,
  bars = [80, 100, 60, 90, 70],
  barHeight = 10,
  steps = null,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
    <l-helix size={String(size)} speed="2.5" color="#0a2240"></l-helix>
    <Box sx={{ flex: 1 }}>
      {Array.isArray(steps) && steps.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 0.75,
            }}
          >
            Synthesizing from intelligence tabs
          </Typography>
          {steps.map((s, i) => (
            <Box
              key={i}
              sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.4 }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  width: 14,
                  textAlign: "center",
                  flexShrink: 0,
                  color: s.done ? "#22c55e" : C.blue,
                  animation: s.done ? "none" : "spin 1s linear infinite",
                  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                }}
              >
                {s.done ? "✓" : "↻"}
              </Box>
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: s.done ? C.charcoal : C.muted,
                  fontWeight: s.done ? 600 : 400,
                }}
              >
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {bars.map((w, i) => (
        <Box
          key={i}
          sx={{
            height: barHeight,
            bgcolor: C.border,
            borderRadius: 1,
            mb: 1,
            width: `${w}%`,
            opacity: 0.6,
          }}
        />
      ))}
    </Box>
  </Box>
);

// ─── Button with inline spinner ─────────────────────────────────────────────
const SpinnerBtn = ({
  loading,
  children,
  onClick,
  disabled,
  sx = {},
  variant = "contained",
  ...props
}) => (
  <Button
    variant={variant}
    onClick={onClick}
    disabled={loading || disabled}
    sx={{ display: "inline-flex", alignItems: "center", gap: 1, ...sx }}
    {...props}
  >
    {loading && (
      <Box
        sx={{
          width: 13,
          height: 13,
          border: "2px solid currentColor",
          borderTopColor: "transparent",
          borderRadius: "50%",
          animation: "btnSpin 0.65s linear infinite",
          "@keyframes btnSpin": { to: { transform: "rotate(360deg)" } },
          flexShrink: 0,
        }}
      />
    )}
    {children}
  </Button>
);

// ── OpponentLens ──────────────────────────────────────────────────────────────
// Per-tab section showing quantitative comparisons + intelligence analysis.
// Drop into any tab that has opponentFR data.
const OpponentLens = ({
  cd,
  cfg,
  liveFEC,
  opponentIntel,
  onGenerate,
  generating,
}) => {
  // Prefer FEC opponent data; fall back to race_finance (SC Ethics / TransparencyUSA state races
  // have no opponentFR, so without this the whole lens — including the Generate Analysis button —
  // would never render).
  const _lnk = (n) => (n || "").toLowerCase().trim().split(/\s+/).slice(-1)[0];
  const rfCands = cd.race_finance?.candidates || [];
  const rfOur = rfCands.find((c) => c.role === "our_candidate");
  const cohOf = (name) =>
    rfCands.find((c) => _lnk(c.name) === _lnk(name))?.coh_estimated ?? null;
  const fecOppKey = Object.keys(cd.opponentFR || {})[0];

  // Who the live opponent is comes from Ballotpedia (race_status) — the source of truth for race
  // state. race_finance only supplies the dollar figures. Among active opponents, CoH is just a
  // tiebreaker for which single rival to headline.
  const ourLast = _lnk(cfg?.candidateName);
  const activeOpps = (cd.raceStatus?.candidates || [])
    .filter(
      (c) =>
        ["running", "won"].includes((c.status || "").toLowerCase()) &&
        _lnk(c.name) !== ourLast,
    )
    .map((c) => c.name);

  let opp1Key;
  if (activeOpps.length) {
    opp1Key = activeOpps
      .slice()
      .sort((a, b) => (cohOf(b) ?? 0) - (cohOf(a) ?? 0))[0];
  } else if (fecOppKey) {
    opp1Key = fecOppKey;
  } else {
    opp1Key =
      rfCands
        .filter((c) => c.role !== "our_candidate")
        .slice()
        .sort((a, b) => (b.coh_estimated ?? 0) - (a.coh_estimated ?? 0))[0]
        ?.name ?? null;
  }
  if (!opp1Key) return null;

  const lastQ = Math.max(0, (cd.fecQuarters || []).length - 1);
  const oppCohArr = cd.opponentFR?.[opp1Key]?.cashOnHand || [];
  const candCoh =
    liveFEC?.totals?.[cfg?.candidateName]?.cash_on_hand ??
    (fecOppKey
      ? (cd.candidateFR?.cashOnHand?.[lastQ] ?? 0)
      : (rfOur?.coh_estimated ?? 0));
  const oppCoh = fecOppKey ? (oppCohArr[lastQ] ?? 0) : (cohOf(opp1Key) ?? 0);
  const cohDiff = candCoh - oppCoh;

  const latestPoll = (cd.pollAverages || []).at(-1) || {};
  const candPoll = latestPoll.candidate ?? null;
  const oppPoll = latestPoll[opp1Key] ?? null;
  const pollMargin =
    candPoll != null && oppPoll != null
      ? parseFloat((candPoll - oppPoll).toFixed(1))
      : null;

  const latestFav = (cd.favNet || []).at(-1) || {};
  const candFav = latestFav.candidate ?? null;
  const oppFavKey = Object.keys(latestFav).find((k) => k !== "candidate");
  const oppFav = oppFavKey ? (latestFav[oppFavKey] ?? null) : null;
  const favDiff =
    candFav != null && oppFav != null
      ? parseFloat((candFav - oppFav).toFixed(1))
      : null;

  const candShort = cfg.candidateName?.split(" ").pop() || "Candidate";

  const metrics = [
    ...(candCoh > 0 || oppCoh > 0
      ? [
          {
            label: "Cash on Hand",
            cand: fmtMoney(candCoh),
            opp: fmtMoney(oppCoh),
            diff: cohDiff,
            fmtDiff: fmtMoney(Math.abs(cohDiff)),
          },
        ]
      : []),
    ...(candPoll != null && oppPoll != null
      ? [
          {
            label: "Poll Average",
            cand: candPoll + "%",
            opp: oppPoll + "%",
            diff: pollMargin,
            fmtDiff: Math.abs(pollMargin) + "pt",
          },
        ]
      : []),
    ...(candFav != null && oppFav != null
      ? [
          {
            label: "Net Favorability",
            cand: (candFav >= 0 ? "+" : "") + candFav + "%",
            opp: (oppFav >= 0 ? "+" : "") + oppFav + "%",
            diff: favDiff,
            fmtDiff: Math.abs(favDiff) + "pt",
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        mt: 3,
        mb: 1,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: C.navy,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: "#fff",
              textTransform: "uppercase",
              letterSpacing: "0.09em",
            }}
          >
            Opponent Lens
          </Typography>
          <Typography
            sx={{ fontSize: 10, color: "rgba(255,255,255,0.5)", mt: 0.25 }}
          >
            {candShort} vs. {opp1Key}
          </Typography>
        </Box>
        <SpinnerBtn
          loading={generating}
          onClick={onGenerate}
          variant="outlined"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            px: 1.5,
            py: 0.4,
            borderColor: "rgba(255,255,255,0.3)",
            color: "#fff",
            textTransform: "none",
            borderRadius: 1,
            minWidth: 0,
            "&:hover": {
              borderColor: "#fff",
              bgcolor: "rgba(255,255,255,0.12)",
            },
            "&.Mui-disabled": {
              borderColor: "rgba(255,255,255,0.15)",
              color: "rgba(255,255,255,0.35)",
            },
          }}
        >
          {opponentIntel ? "↻ Refresh" : "Generate Analysis"}
        </SpinnerBtn>
      </Box>

      {/* Metrics comparison grid */}
      {metrics.length > 0 && (
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${C.border}`,
            display: "grid",
            gridTemplateColumns: `repeat(${metrics.length}, 1fr)`,
            gap: 0,
          }}
        >
          {metrics.map((m, i) => (
            <Box
              key={i}
              sx={{
                px: i > 0 ? 2 : 0,
                borderLeft: i > 0 ? `1px solid ${C.border}` : "none",
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: C.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  mb: 1,
                }}
              >
                {m.label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
                <Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}
                  >
                    {m.cand}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: C.muted }}>
                    {candShort}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 9, color: C.muted, mb: 0.5 }}>
                  vs
                </Typography>
                <Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 800, color: C.navyMid }}
                  >
                    {m.opp}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: C.muted }}>
                    {opp1Key}
                  </Typography>
                </Box>
                {m.diff != null && (
                  <Box
                    sx={{
                      ml: "auto",
                      px: 1,
                      py: 0.3,
                      borderRadius: "3px",
                      bgcolor: m.diff >= 0 ? "#dcfce7" : "#fef2f2",
                      border: `1px solid ${m.diff >= 0 ? "#86efac" : "#fca5a5"}`,
                      alignSelf: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: m.diff >= 0 ? "#15803d" : C.red,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.diff >= 0 ? "+" : "−"}
                      {m.fmtDiff}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Intelligence analysis */}
      {generating ? (
        <Box
          sx={{
            px: 2.5,
            py: 2.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <l-helix size="28" speed="2.5" color={C.navy}></l-helix>
          <Box sx={{ flex: 1 }}>
            {[80, 90, 65, 75].map((w, i) => (
              <Box
                key={i}
                sx={{
                  height: 9,
                  bgcolor: C.border,
                  borderRadius: 1,
                  mb: 1,
                  width: `${w}%`,
                }}
              />
            ))}
          </Box>
        </Box>
      ) : opponentIntel ? (
        <Box>
          {opponentIntel.summary && (
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: "#f8fafc",
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <Typography
                sx={{ fontSize: 11, color: C.charcoal, lineHeight: 1.6 }}
              >
                {opponentIntel.summary}
              </Typography>
            </Box>
          )}

          {/* Backend schema: one card per opponent (handles a full multi-candidate field). */}
          {Array.isArray(opponentIntel.opponents) &&
          opponentIntel.opponents.length > 0 ? (
            <>
              {opponentIntel.opponents.map((o, oi) => {
                const threat = (o.threat_level || "").toLowerCase();
                const threatColor =
                  threat === "high"
                    ? C.red
                    : threat === "medium"
                      ? C.orange
                      : C.muted;
                const lines = [
                  ["Fundraising", o.fundraising],
                  ["Polling", o.polling],
                  ["Markets", o.kalshi],
                  ["Policy contrast", o.policy_contrast],
                ].filter(([, v]) => v);
                return (
                  <Box
                    key={oi}
                    sx={{ borderTop: `1px solid ${C.border}`, p: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: lines.length ? 1 : 0.5,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 800, color: C.navy }}
                      >
                        {o.name}
                      </Typography>
                      {threat && (
                        <Box
                          sx={{
                            fontSize: 8.5,
                            fontWeight: 800,
                            px: 0.75,
                            py: "2px",
                            borderRadius: "3px",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: threatColor,
                            bgcolor: `${threatColor}14`,
                            border: `1px solid ${threatColor}40`,
                          }}
                        >
                          {threat} threat
                        </Box>
                      )}
                    </Box>
                    {lines.map(([label, val], li) => (
                      <Typography
                        key={li}
                        sx={{
                          fontSize: 10,
                          color: C.charcoal,
                          lineHeight: 1.5,
                          mb: 0.4,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: C.muted }}
                        >
                          {label}:{" "}
                        </Box>
                        {val}
                      </Typography>
                    ))}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1.5,
                        mt: 1,
                      }}
                    >
                      {[
                        {
                          label: "Vulnerabilities",
                          items: o.vulnerabilities || [],
                          accent: C.red,
                          bullet: "−",
                        },
                        {
                          label: "Their Strengths",
                          items: o.strengths || [],
                          accent: "#15803d",
                          bullet: "+",
                        },
                      ].map(
                        (col, ci) =>
                          col.items.length > 0 && (
                            <Box key={ci}>
                              <Typography
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 800,
                                  color: col.accent,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.07em",
                                  mb: 0.75,
                                }}
                              >
                                {col.label}
                              </Typography>
                              {col.items.map((item, ii) => (
                                <Box
                                  key={ii}
                                  sx={{
                                    display: "flex",
                                    gap: 0.6,
                                    mb: 0.6,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: col.accent,
                                      flexShrink: 0,
                                      mt: 0.1,
                                    }}
                                  >
                                    {col.bullet}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      color: C.charcoal,
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {item}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ),
                      )}
                    </Box>
                    {o.recommended_response && (
                      <Box
                        sx={{
                          mt: 1,
                          px: 1.25,
                          py: 0.85,
                          bgcolor: C.bg,
                          borderRadius: 1,
                          borderLeft: `2px solid ${C.navy}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: C.navy,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            mb: 0.25,
                          }}
                        >
                          Recommended Response
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: C.charcoal,
                            lineHeight: 1.5,
                          }}
                        >
                          {o.recommended_response}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
              {(opponentIntel.strategic_priority ||
                opponentIntel.cross_cutting_themes) && (
                <Box
                  sx={{
                    borderTop: `1px solid ${C.border}`,
                    px: 2.5,
                    py: 1.5,
                    bgcolor: "#f8fafc",
                  }}
                >
                  {opponentIntel.strategic_priority && (
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: C.charcoal,
                        lineHeight: 1.6,
                        mb: opponentIntel.cross_cutting_themes ? 0.6 : 0,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontWeight: 800, color: C.navy }}
                      >
                        Strategic priority:{" "}
                      </Box>
                      {opponentIntel.strategic_priority}
                    </Typography>
                  )}
                  {opponentIntel.cross_cutting_themes && (
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: C.charcoal,
                        lineHeight: 1.6,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontWeight: 800, color: C.navy }}
                      >
                        Across the field:{" "}
                      </Box>
                      {opponentIntel.cross_cutting_themes}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            /* Legacy flat shape (summary + top-level strengths/weaknesses/contrasts) */
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
              {[
                {
                  label: "Strengths vs Opponent",
                  items: opponentIntel.strengths || [],
                  accent: "#15803d",
                  bullet: "+",
                },
                {
                  label: "Vulnerabilities",
                  items: opponentIntel.weaknesses || [],
                  accent: C.red,
                  bullet: "−",
                },
                {
                  label: "Key Contrasts",
                  items: opponentIntel.contrasts || [],
                  accent: C.navy,
                  bullet: "↔",
                },
              ].map((col, ci) => (
                <Box
                  key={ci}
                  sx={{
                    p: 2,
                    borderLeft: ci > 0 ? `1px solid ${C.border}` : "none",
                    borderTop: `1px solid ${C.border}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: col.accent,
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      mb: 1.25,
                    }}
                  >
                    {col.label}
                  </Typography>
                  {col.items.map((item, ii) => (
                    <Box
                      key={ii}
                      sx={{
                        display: "flex",
                        gap: 0.75,
                        mb: 0.85,
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: col.accent,
                          flexShrink: 0,
                          mt: 0.1,
                        }}
                      >
                        {col.bullet}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: C.charcoal,
                          lineHeight: 1.55,
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ px: 2.5, py: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 11, color: C.muted }}>
            Generate an analysis to see strategic strengths, vulnerabilities,
            and key contrasts vs. {opp1Key}.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Crosshair plugin — module-level, works for both line and bar charts
const _crosshairPlugin = {
  id: "velCrosshair",
  afterDraw(chart) {
    const idx = chart._velHoverIdx;
    if (idx == null) return;
    // For bar charts, get x from bar element; for line charts, from dataset point
    let x = null;
    for (let i = 0; i < chart.data.datasets.length; i++) {
      const meta = chart.getDatasetMeta(i);
      const el = meta?.data?.[idx];
      if (el) {
        x = el.x;
        break;
      }
    }
    if (x == null || !chart.chartArea) return;
    const {
      ctx,
      chartArea: { top, bottom },
    } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = "rgba(0,0,0,0.22)";
    ctx.lineWidth = 1;
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.restore();
  },
};

// ─── Generic Stripe Line Chart ─────────────────────────────────────────────
// datasets: [{ label, data, color, dashed?, fill? }]
// yFmt: tick formatter fn; valFmt: tooltip value formatter fn
const StripeLineChart = ({
  labels,
  datasets,
  height = 200,
  yMin,
  yMax,
  yFmt,
  valFmt,
}) => {
  const [hovered, setHovered] = useState(null);

  const chartData = {
    labels,
    datasets: datasets.map(({ label, data, color, dashed, fill }) => ({
      label,
      data,
      borderColor: color,
      backgroundColor: fill ? color + "18" : "transparent",
      borderWidth: 2,
      borderDash: dashed ? [4, 3] : undefined,
      pointRadius: 0,
      pointHoverRadius: 4,
      tension: 0.3,
      fill: !!fill,
    })),
  };

  const opts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { tooltip: { enabled: false }, legend: baseLegend },
      onHover: (event, elements, chart) => {
        if (!elements.length) {
          chart._velHoverIdx = null;
          setHovered(null);
          return;
        }
        const idx = elements[0].index;
        chart._velHoverIdx = idx;
        const pt = chart.getDatasetMeta(0)?.data?.[idx];
        if (pt)
          setHovered({
            idx,
            x: pt.x,
            areaRight: chart.chartArea?.right ?? chart.width,
          });
      },
      scales: {
        x: { ...baseGridScale, grid: { display: false } },
        y: {
          ...baseGridScale,
          ...(yMin != null ? { min: yMin } : {}),
          ...(yMax != null ? { max: yMax } : {}),
          ticks: { ...baseGridScale.ticks, callback: yFmt || ((v) => v) },
        },
      },
    }),
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const tipLeft = hovered
    ? hovered.x > hovered.areaRight * 0.6
      ? hovered.x - 200
      : hovered.x + 12
    : 0;

  return (
    <Box sx={{ position: "relative", height, mb: 3 }}>
      <Line data={chartData} options={opts} plugins={[_crosshairPlugin]} />
      {hovered && (
        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: tipLeft,
            bgcolor: "#fff",
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: "6px",
            p: "10px 14px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
            minWidth: 190,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Typography
            sx={{ fontSize: 11, fontWeight: 600, color: C.muted, mb: 0.75 }}
          >
            {labels[hovered.idx]}
          </Typography>
          {datasets.map(({ label, data, color, dashed }) => (
            <Box
              key={label}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}
            >
              {dashed ? (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    border: `2px dashed ${color}`,
                    borderRadius: "2px",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    bgcolor: color,
                    borderRadius: "2px",
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography sx={{ fontSize: 11, color: C.muted, flexGrow: 1 }}>
                {label}
              </Typography>
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
              >
                {valFmt ? valFmt(data[hovered.idx]) : data[hovered.idx]}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Generic Stripe Stacked Bar Chart ──────────────────────────────────────
// datasets: [{ label, data, color }]
const StripeStackedBarChart = ({ labels, datasets, height = 180, valFmt }) => {
  const [hovered, setHovered] = useState(null);

  const chartData = {
    labels,
    datasets: datasets.map(({ label, data, color }) => ({
      label,
      data,
      backgroundColor: color,
      borderRadius: 2,
      stack: "net",
    })),
  };

  const opts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { tooltip: { enabled: false }, legend: baseLegend },
      onHover: (event, elements, chart) => {
        if (!elements.length) {
          chart._velHoverIdx = null;
          setHovered(null);
          return;
        }
        const idx = elements[0].index;
        chart._velHoverIdx = idx;
        let x = null;
        for (let i = 0; i < chart.data.datasets.length; i++) {
          const el = chart.getDatasetMeta(i)?.data?.[idx];
          if (el) {
            x = el.x;
            break;
          }
        }
        if (x != null)
          setHovered({
            idx,
            x,
            areaRight: chart.chartArea?.right ?? chart.width,
          });
      },
      scales: {
        x: { ...baseGridScale, grid: { display: false }, stacked: true },
        y: {
          ...baseGridScale,
          stacked: true,
          ticks: {
            ...baseGridScale.ticks,
            callback: (v) => v.toLocaleString(),
          },
        },
      },
    }),
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const tipLeft = hovered
    ? hovered.x > hovered.areaRight * 0.6
      ? hovered.x - 200
      : hovered.x + 12
    : 0;

  return (
    <Box sx={{ position: "relative", height, mb: 3 }}>
      <Bar data={chartData} options={opts} plugins={[_crosshairPlugin]} />
      {hovered && (
        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: tipLeft,
            bgcolor: "#fff",
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: "6px",
            p: "10px 14px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
            minWidth: 190,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Typography
            sx={{ fontSize: 11, fontWeight: 600, color: C.muted, mb: 0.75 }}
          >
            {labels[hovered.idx]}
          </Typography>
          {datasets.map(({ label, data, color }) => (
            <Box
              key={label}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  bgcolor: color,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 11, color: C.muted, flexGrow: 1 }}>
                {label}
              </Typography>
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
              >
                {valFmt
                  ? valFmt(data[hovered.idx])
                  : `+${(data[hovered.idx] || 0).toLocaleString()}`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── CoH Quarterly Line Chart (Stripe-style) ───────────────────────────────
const CoHLineChart = ({ quarters, datasets }) => {
  const [hovered, setHovered] = useState(null);

  const chartData = {
    labels: quarters,
    datasets: datasets.map(({ label, data, color, dashed }) => ({
      label,
      data,
      borderColor: color,
      backgroundColor: dashed ? "transparent" : color + "18",
      borderWidth: 2,
      borderDash: dashed ? [4, 3] : undefined,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.3,
      fill: !dashed,
    })),
  };

  const opts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { tooltip: { enabled: false }, legend: baseLegend },
      onHover: (event, elements, chart) => {
        if (!elements.length) {
          chart._velHoverIdx = null;
          setHovered(null);
          return;
        }
        const idx = elements[0].index;
        chart._velHoverIdx = idx;
        const pt = chart.getDatasetMeta(0)?.data?.[idx];
        if (pt)
          setHovered({
            idx,
            x: pt.x,
            areaRight: chart.chartArea?.right ?? chart.width,
          });
      },
      scales: {
        x: { ...baseGridScale, grid: { display: false } },
        y: {
          ...baseGridScale,
          ticks: { ...baseGridScale.ticks, callback: (v) => fmtMoney(v) },
        },
      },
    }),
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const tipLeft = hovered
    ? hovered.x > hovered.areaRight * 0.6
      ? hovered.x - 200
      : hovered.x + 12
    : 0;

  return (
    <Box
      sx={{ position: "relative", height: 180, mb: 3 }}
      onMouseLeave={() => setHovered(null)}
    >
      <Line data={chartData} options={opts} plugins={[_crosshairPlugin]} />
      {hovered && (
        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: tipLeft,
            bgcolor: "#fff",
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: "6px",
            p: "10px 14px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
            minWidth: 190,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Typography
            sx={{ fontSize: 11, fontWeight: 600, color: C.muted, mb: 0.75 }}
          >
            {quarters[hovered.idx]}
          </Typography>
          {datasets.map(({ label, data, color, dashed }) => (
            <Box
              key={label}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}
            >
              {dashed ? (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    border: `2px dashed ${color}`,
                    borderRadius: "2px",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    bgcolor: color,
                    borderRadius: "2px",
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography sx={{ fontSize: 11, color: C.muted, flexGrow: 1 }}>
                {label}
              </Typography>
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
              >
                {fmtMoney(data[hovered.idx] ?? 0)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Monthly Velocity Chart (Stripe-style bar + smoothed line) ─────────────
const MonthlyVelocityChart = ({ labels, raised, smoothed }) => {
  const [hovered, setHovered] = useState(null); // { idx, x, areaRight }

  const chartData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Monthly Raised",
        data: raised,
        backgroundColor: C.navy + "cc",
        borderRadius: 2,
        order: 2,
      },
      {
        type: "line",
        label: "3-Month Avg",
        data: smoothed,
        borderColor: C.red,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
        order: 1,
        fill: false,
      },
    ],
  };

  const opts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { tooltip: { enabled: false }, legend: baseLegend },
      onHover: (event, elements, chart) => {
        if (!elements.length) {
          chart._velHoverIdx = null;
          setHovered(null);
          return;
        }
        const idx = elements[0].index;
        chart._velHoverIdx = idx;
        // Get x from the bar element (dataset index 0)
        let x = null;
        for (let i = 0; i < chart.data.datasets.length; i++) {
          const el = chart.getDatasetMeta(i)?.data?.[idx];
          if (el) {
            x = el.x;
            break;
          }
        }
        if (x != null)
          setHovered({
            idx,
            x,
            areaRight: chart.chartArea?.right ?? chart.width,
          });
      },
      scales: {
        x: { ...baseGridScale, grid: { display: false } },
        y: {
          ...baseGridScale,
          ticks: { ...baseGridScale.ticks, callback: (v) => fmtMoney(v) },
        },
      },
    }),
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const hovLabel = hovered != null ? labels[hovered.idx] : null;
  const hovRaised = hovered != null ? raised[hovered.idx] : null;
  const hovAvg = hovered != null ? smoothed[hovered.idx] : null;
  const tipLeft = hovered
    ? hovered.x > hovered.areaRight * 0.6
      ? hovered.x - 196
      : hovered.x + 12
    : 0;

  return (
    <Box sx={{ position: "relative", height: 200 }}>
      <Bar data={chartData} options={opts} plugins={[_crosshairPlugin]} />
      {hovered && hovLabel != null && (
        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: tipLeft,
            bgcolor: "#fff",
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: "6px",
            p: "10px 14px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
            minWidth: 180,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Typography
            sx={{ fontSize: 11, fontWeight: 600, color: C.muted, mb: 0.75 }}
          >
            {hovLabel}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: C.navy,
                borderRadius: "2px",
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 11, color: C.muted, flexGrow: 1 }}>
              Raised
            </Typography>
            <Typography
              sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
            >
              {fmtMoney(hovRaised)}
            </Typography>
          </Box>
          {hovAvg != null && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  border: `2px solid ${C.red}`,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 11, color: C.muted, flexGrow: 1 }}>
                3-mo avg
              </Typography>
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
              >
                {fmtMoney(hovAvg)}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Daily Velocity Chart (Stripe-style) ───────────────────────────────────
const DailyVelocityChart = ({ dailyData }) => {
  if (!dailyData?.current?.length) return null;

  const [hovered, setHovered] = useState(null); // { idx, x (canvas px), areaRight }

  const {
    current,
    prior,
    current_total,
    prior_total,
    pct_change,
    window_days = 30,
  } = dailyData;
  const pctUp = pct_change >= 0;
  const fmtD = (s) =>
    new Date(s + "T12:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  const labels = current.map((d) => fmtD(d.date));

  const chartData = {
    labels,
    datasets: [
      {
        label: `Last ${window_days} days`,
        data: current.map((d) => d.amount),
        borderColor: C.red,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: C.red,
        tension: 0.3,
        fill: false,
      },
      {
        label: `Prior ${window_days} days`,
        data: prior.map((d) => d.amount),
        borderColor: C.navyMid,
        borderWidth: 1.5,
        borderDash: [4, 3],
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: C.navyMid,
        tension: 0.3,
        fill: false,
      },
    ],
  };

  const opts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { tooltip: { enabled: false }, legend: baseLegend },
      onHover: (event, elements, chart) => {
        if (!elements.length) {
          chart._velHoverIdx = null;
          setHovered(null);
          return;
        }
        const idx = elements[0].index;
        chart._velHoverIdx = idx;
        const pt = chart.getDatasetMeta(0)?.data?.[idx];
        if (pt)
          setHovered({
            idx,
            x: pt.x,
            areaRight: chart.chartArea?.right ?? chart.width,
          });
      },
      scales: {
        x: {
          ...baseGridScale,
          grid: { display: false },
          ticks: { ...baseGridScale.ticks, maxTicksLimit: 8 },
        },
        y: {
          ...baseGridScale,
          ticks: { ...baseGridScale.ticks, callback: (v) => fmtMoney(v) },
        },
      },
    }),
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Tooltip positioning — flip left when near right edge
  const curEntry = hovered != null ? current[hovered.idx] : null;
  const priEntry = hovered != null ? prior[hovered.idx] : null;
  const dayPct =
    curEntry && priEntry && priEntry.amount > 0
      ? (((curEntry.amount - priEntry.amount) / priEntry.amount) * 100).toFixed(
          1,
        )
      : null;
  const dayPctUp = dayPct != null && Number(dayPct) >= 0;
  const tipLeft = hovered
    ? hovered.x > hovered.areaRight * 0.55
      ? hovered.x - 204
      : hovered.x + 14
    : 0;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 0.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.text }}>
          {fmtMoney(current_total)}
        </Typography>
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, color: pctUp ? C.green : C.red }}
        >
          {pctUp ? "+" : ""}
          {pct_change}%
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 11, color: C.muted, mb: 1.5 }}>
        {fmtMoney(prior_total)} prior {window_days} days
      </Typography>
      <Box sx={{ position: "relative", height: 160 }}>
        <Line data={chartData} options={opts} plugins={[_crosshairPlugin]} />

        {hovered && curEntry && priEntry && (
          <Box
            sx={{
              position: "absolute",
              top: 6,
              left: tipLeft,
              bgcolor: "#fff",
              border: "1px solid rgba(0,0,0,0.10)",
              borderRadius: "6px",
              p: "10px 14px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
              minWidth: 196,
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.muted,
                  letterSpacing: "0.02em",
                }}
              >
                Daily donations
              </Typography>
              {dayPct != null && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: dayPctUp ? C.green : C.red,
                  }}
                >
                  {dayPctUp ? "+" : ""}
                  {dayPct}%
                </Typography>
              )}
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  bgcolor: C.red,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 11, color: C.muted, flexGrow: 1 }}>
                {fmtD(curEntry.date)}
              </Typography>
              <Typography
                sx={{ fontSize: 11, fontWeight: 600, color: C.text, ml: 2 }}
              >
                {fmtMoney(curEntry.amount)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  border: `2px dashed ${C.navyMid}`,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 11, color: C.muted, flexGrow: 1 }}>
                {fmtD(priEntry.date)}
              </Typography>
              <Typography
                sx={{ fontSize: 11, fontWeight: 600, color: C.text, ml: 2 }}
              >
                {fmtMoney(priEntry.amount)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Race Finance — Full Field CoH Ranking ─────────────────────────────────
const PARTY_DOT = {
  Republican: C.red,
  Democratic: "#2563eb",
  Democrat: "#2563eb",
  Green: "#16a34a",
  Independent: C.muted,
};

const RaceFinanceCard = ({ raceFinance, candidateName }) => {
  const [view, setView] = useState("coh"); // "coh" | "momentum"
  if (!raceFinance?.field_analytics) return null;

  const fa = raceFinance.field_analytics;
  const cands = raceFinance.candidates || [];
  const ourSlug = cands.find((c) => c.role === "our_candidate")?.slug;
  const cohList = fa.coh_ranking || [];
  const momList = fa.momentum_ranking || [];
  const maxCoh = Math.max(...cohList.map((c) => Math.max(c.coh ?? 0, 0)), 1);

  const statusColor = (s = "") => {
    if (/withdrew|withdraw/i.test(s)) return C.muted;
    if (/disqualified/i.test(s)) return "#c0392b";
    return "#16a34a";
  };

  return (
    <Box
      sx={{
        mb: 3,
        border: `1px solid ${C.border}`,
        borderRadius: 1.5,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: C.bg,
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Full Race ·{" "}
            {raceFinance.cycle?.replace("-election-cycle", "") || ""}
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
            {fmtMoney(fa.total_raised_in_race)} raised in race ·{" "}
            {fa.our_market_share_pct ?? 0}% our share ·{" "}
            <Box component="span" sx={{ color: C.charcoal, fontWeight: 700 }}>
              {cands.length} candidates
            </Box>
            {raceFinance.generated_at && (
              <Box component="span" sx={{ ml: 1, color: C.muted }}>
                · as of{" "}
                {new Date(raceFinance.generated_at).toLocaleDateString(
                  "en-US",
                  { month: "short", day: "numeric" },
                )}
              </Box>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {[
            ["coh", "CoH Ranking"],
            ["momentum", "Momentum"],
          ].map(([v, label]) => (
            <Box
              key={v}
              component="button"
              onClick={() => setView(v)}
              sx={{
                fontSize: 10,
                fontWeight: 700,
                px: 1.25,
                py: 0.4,
                borderRadius: 1,
                border: `1px solid ${view === v ? C.navy : C.border}`,
                bgcolor: view === v ? C.navy : C.white,
                color: view === v ? C.white : C.muted,
                cursor: "pointer",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* CoH ranking */}
      {view === "coh" && (
        <Box sx={{ px: 2, py: 1.5 }}>
          {cohList.map((c, i) => {
            const isOurs =
              cands.find((x) => x.name === c.name)?.role === "our_candidate";
            const pct = Math.max(
              0,
              Math.round((Math.max(c.coh ?? 0, 0) / maxCoh) * 100),
            );
            const dotColor = PARTY_DOT[c.party] || C.muted;
            const status = cands.find((x) => x.name === c.name)?.status || "";
            const withdrew = /withdrew|withdraw|disqualified/i.test(status);
            return (
              <Box
                key={i}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "16px 1fr 90px 70px",
                  alignItems: "center",
                  gap: 1,
                  py: 0.6,
                  borderBottom:
                    i < cohList.length - 1 ? `1px solid ${C.border}` : "none",
                  opacity: withdrew ? 0.45 : 1,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: isOurs ? C.red : dotColor,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: isOurs ? 800 : 600,
                      color: isOurs ? C.navy : C.charcoal,
                    }}
                  >
                    {c.name}
                    {isOurs ? " ★" : ""}
                    {withdrew ? (
                      <Box
                        component="span"
                        sx={{ ml: 0.5, fontSize: 9, color: C.muted }}
                      >
                        ({status})
                      </Box>
                    ) : null}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.4,
                      height: 4,
                      bgcolor: C.border,
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${pct}%`,
                        bgcolor: isOurs ? C.red : dotColor,
                        borderRadius: 2,
                        opacity: withdrew ? 0.4 : 1,
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: (c.coh ?? 0) >= 0 ? C.charcoal : "#c0392b",
                    }}
                  >
                    {fmtMoney(c.coh ?? 0)}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: C.muted }}>
                    est. CoH
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  {c.months_runway != null ? (
                    <>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color:
                            c.months_runway < 2
                              ? "#c0392b"
                              : c.months_runway < 4
                                ? "#d97706"
                                : "#16a34a",
                        }}
                      >
                        {c.months_runway}mo
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: C.muted }}>
                        runway
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 10, color: C.muted }}>
                      —
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Momentum ranking */}
      {view === "momentum" && (
        <Box sx={{ px: 2, py: 1.5 }}>
          {momList.length === 0 && (
            <Typography sx={{ fontSize: 11, color: C.muted, py: 1 }}>
              Momentum data not yet available — requires 2+ quarters of
              contribution history.
            </Typography>
          )}
          {momList.map((c, i) => {
            const isOurs =
              cands.find((x) => x.name === c.name)?.role === "our_candidate";
            const positive = (c.momentum_pct ?? 0) >= 0;
            const barWidth = Math.min(100, Math.abs(c.momentum_pct ?? 0));
            const dotColor =
              PARTY_DOT[cands.find((x) => x.name === c.name)?.party] || C.muted;
            return (
              <Box
                key={i}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "16px 1fr 80px",
                  alignItems: "center",
                  gap: 1,
                  py: 0.75,
                  borderBottom:
                    i < momList.length - 1 ? `1px solid ${C.border}` : "none",
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: isOurs ? C.red : dotColor,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: isOurs ? 800 : 600,
                      color: isOurs ? C.navy : C.charcoal,
                    }}
                  >
                    {c.name}
                    {isOurs ? " ★" : ""}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.4,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {positive ? (
                      <Box
                        sx={{
                          height: 4,
                          width: `${barWidth}%`,
                          maxWidth: 160,
                          bgcolor: "#16a34a",
                          borderRadius: 2,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 4,
                          width: `${barWidth}%`,
                          maxWidth: 160,
                          bgcolor: "#c0392b",
                          borderRadius: 2,
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    textAlign: "right",
                    color: positive ? "#16a34a" : "#c0392b",
                  }}
                >
                  {positive ? "+" : ""}
                  {c.momentum_pct}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Donor Intelligence — shared donors, entity breakdown, shared payees ────
const DonorIntelligenceCard = ({ raceFinance, cfg }) => {
  const [view, setView] = useState("overlaps"); // "overlaps" | "breakdown" | "payees"
  if (!raceFinance) return null;

  const fa = raceFinance.field_analytics || {};
  const cands = raceFinance.candidates || [];
  const overlaps = fa.donor_overlaps || [];
  const sharedPay = fa.shared_payees || [];
  const candidateName = cfg?.candidateName || "";

  return (
    <Box
      sx={{
        mb: 3,
        border: `1px solid ${C.border}`,
        borderRadius: 1.5,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: C.bg,
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Donor Intelligence
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
            Cross-candidate contributor analysis
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {[
            ["overlaps", `Shared Donors (${overlaps.length})`],
            ["breakdown", "Entity/Individual"],
            ["payees", `Shared Vendors (${sharedPay.length})`],
          ].map(([v, label]) => (
            <Box
              key={v}
              component="button"
              onClick={() => setView(v)}
              sx={{
                fontSize: 10,
                fontWeight: 700,
                px: 1.25,
                py: 0.4,
                borderRadius: 1,
                border: `1px solid ${view === v ? C.navy : C.border}`,
                bgcolor: view === v ? C.navy : C.white,
                color: view === v ? C.white : C.muted,
                cursor: "pointer",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Shared donors */}
      {view === "overlaps" && (
        <Box>
          {overlaps.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: C.muted, px: 2, py: 1.5 }}>
              No shared donors found in the top-25 contributor lists.
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  px: 2,
                  py: 0.75,
                  bgcolor: C.bg,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                {["Contributor", "Also gave to"].map((h) => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>
              {overlaps.map((o, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    px: 2,
                    py: 0.9,
                    borderBottom:
                      i < overlaps.length - 1
                        ? `1px solid ${C.border}`
                        : "none",
                    alignItems: "start",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 600, color: C.charcoal }}
                  >
                    {o.contributor}
                  </Typography>
                  <Box>
                    {(o.candidates || [])
                      .filter((n) => n !== candidateName)
                      .map((n, j) => (
                        <Box
                          key={j}
                          sx={{
                            display: "inline-block",
                            mr: 0.5,
                            mb: 0.5,
                            px: 0.75,
                            py: 0.2,
                            bgcolor: "#fef3c7",
                            borderRadius: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: "#92400e",
                            }}
                          >
                            {n}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
      )}

      {/* Entity vs individual breakdown */}
      {view === "breakdown" && (
        <Box sx={{ px: 2, py: 1.5 }}>
          {cands
            .filter((c) => c.contributor_breakdown)
            .map((c, i) => {
              const isOurs = c.role === "our_candidate";
              const eb = c.contributor_breakdown;
              const withdrew = /withdrew|withdraw|disqualified/i.test(
                c.status || "",
              );
              return (
                <Box key={i} sx={{ mb: 1.5, opacity: withdrew ? 0.45 : 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.4,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: isOurs ? 800 : 600,
                        color: isOurs ? C.navy : C.charcoal,
                      }}
                    >
                      {c.name}
                      {isOurs ? " ★" : ""}
                      <Box
                        component="span"
                        sx={{
                          ml: 0.75,
                          fontSize: 9,
                          color: PARTY_DOT[c.party] || C.muted,
                          fontWeight: 700,
                        }}
                      >
                        {c.party}
                      </Box>
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: C.muted }}>
                      {eb.entity_pct}% entity / {eb.individual_pct}% individual
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      height: 8,
                      borderRadius: 1,
                      overflow: "hidden",
                      bgcolor: C.border,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${eb.entity_pct}%`,
                        bgcolor: C.navy,
                        opacity: 0.8,
                      }}
                    />
                    <Box
                      sx={{
                        width: `${eb.individual_pct}%`,
                        bgcolor: C.red,
                        opacity: 0.7,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, mt: 0.4 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: 0.5,
                          bgcolor: C.navy,
                          opacity: 0.8,
                        }}
                      />
                      <Typography sx={{ fontSize: 9, color: C.muted }}>
                        Entity / PAC
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: 0.5,
                          bgcolor: C.red,
                          opacity: 0.7,
                        }}
                      />
                      <Typography sx={{ fontSize: 9, color: C.muted }}>
                        Individual
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
        </Box>
      )}

      {/* Shared payees */}
      {view === "payees" && (
        <Box>
          {sharedPay.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: C.muted, px: 2, py: 1.5 }}>
              No shared vendors found in the top-25 payee lists.
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  px: 2,
                  py: 0.75,
                  bgcolor: C.bg,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                {["Vendor / Payee", "Candidates using"].map((h) => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>
              {sharedPay.map((p, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    px: 2,
                    py: 0.9,
                    borderBottom:
                      i < sharedPay.length - 1
                        ? `1px solid ${C.border}`
                        : "none",
                    alignItems: "start",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 600, color: C.charcoal }}
                  >
                    {p.payee}
                  </Typography>
                  <Box>
                    {(p.candidates || []).map((n, j) => {
                      const isOurs = n === candidateName;
                      return (
                        <Box
                          key={j}
                          sx={{
                            display: "inline-block",
                            mr: 0.5,
                            mb: 0.5,
                            px: 0.75,
                            py: 0.2,
                            bgcolor: isOurs ? "#fee2e2" : "#eff6ff",
                            borderRadius: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: isOurs ? "#991b1b" : "#1e40af",
                            }}
                          >
                            {n}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Finance Tab ───────────────────────────────────────────────────────────
const FinanceTab = ({
  winredData,
  winredDaily,
  winredLoading,
  winredDailyLoading,
  liveFEC,
  kalshi,
  donorCohorts,
  cfg = {},
  cd = {},
  raceFinance,
  opponentIntel,
  onGenerateOpponentIntel,
  opponentIntelLoading,
  activeCampaignId,
}) => {
  const { authFetch } = useAuth();
  const [fecSyncing, setFecSyncing] = useState(false);
  const [fecSyncMsg, setFecSyncMsg] = useState(null);
  const dataSyncUrl = PHP_API_URL ? `${PHP_API_URL}/data-sync` : "";
  const handleFecSync = async () => {
    if (!dataSyncUrl || !activeCampaignId || fecSyncing) return;
    setFecSyncing(true);
    setFecSyncMsg(null);
    try {
      const res = await authFetch(
        `${dataSyncUrl}/trigger?campaign_id=${activeCampaignId}`,
        { method: "POST" },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || res.status);
      }
      setFecSyncMsg("Sync started — data updates in ~2 min");
    } catch (e) {
      setFecSyncMsg(`Failed: ${e.message}`);
    }
    setFecSyncing(false);
  };
  // Use live WinRed data if available, fall back to static monthly data
  const monthly =
    winredData?.monthly?.length > 0
      ? {
          labels: winredData.monthly.map((m) => m.month),
          raised: winredData.monthly.map((m) => m.raised),
        }
      : {
          labels: cd.winredMonthly?.labels || [],
          raised: cd.winredMonthly?.raised || [],
        };

  const smoothed = smooth3(monthly.raised);

  // FEC cash-on-hand comparison chart
  const _candShortLabel = cfg.candidateName?.split(" ").pop() || "Candidate";
  // All opponents that have CoH data
  const _oppFREntries = Object.entries(cd.opponentFR || {}).filter(([, fr]) =>
    (fr?.cashOnHand || []).some((v) => v > 0),
  );
  const OPP_COLORS = [C.navyMid, "#7c3aed", "#d97706", "#059669"];
  const _targetLen = cd.candidateFR.cashOnHand.length;
  const _cohDatasets = [
    {
      label: `${_candShortLabel} CoH`,
      data: cd.candidateFR.cashOnHand,
      color: C.red,
      dashed: false,
    },
    ..._oppFREntries.map(([name, fr], i) => {
      const raw = fr.cashOnHand || [];
      // Right-align: pad with nulls on the left so single data points plot at the most recent quarter
      const data =
        raw.length < _targetLen
          ? [...Array(_targetLen - raw.length).fill(null), ...raw]
          : raw;
      return {
        label: `${name.split(" ").pop()} CoH`,
        data,
        color: OPP_COLORS[i % OPP_COLORS.length],
        dashed: true,
      };
    }),
  ];

  const lastQ = cd.fecQuarters.length - 1;
  const candidateName = cd.candidateName || "Candidate";
  // Live candidate CoH from FEC API if available, else S3 data fallback
  const candidateCoh =
    liveFEC?.totals?.[cfg?.candidateName]?.cash_on_hand ??
    cd.candidateFR.cashOnHand[lastQ] ??
    0;
  const fecPeriod = liveFEC?.totals?.[cfg?.candidateName]?.coverage_end_date
    ? new Date(
        liveFEC.totals[cfg?.candidateName].coverage_end_date,
      ).toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : cd.fecQuarters[lastQ] || "—";
  const opp1FRKey = _oppFREntries[0]?.[0] ?? null;
  const opponentCohArr = opp1FRKey
    ? cd.opponentFR[opp1FRKey]?.cashOnHand || []
    : [];
  const oppCoh = opponentCohArr[lastQ] ?? 0;
  const smallDonors = cd.smallDonors[lastQ] ?? 0;
  const cohDiff = candidateCoh - oppCoh;

  // Donor cohort rows from live API counts, merged with static strategy metadata
  const cohortRows = donorCohorts
    ? COHORT_ORDER.filter((name) => donorCohorts[name] != null).map((name) => ({
        name,
        count: donorCohorts[name],
        days: COHORT_DAYS[name],
        ...COHORT_META[name],
      }))
    : cd.donorCohorts.map((r) => ({ ...r, days: `${r.lastGiftDays}d ago` }));

  // Fundraising drivers computed from live signals
  const latestPollForDrivers =
    cd.pollAverages[cd.pollAverages.length - 1] || {};
  const drivers = computeDrivers(
    kalshi,
    winredData,
    candidateCoh,
    oppCoh,
    latestPollForDrivers,
  );

  const hasWinredData = monthly.raised.some((v) => v > 0);

  return (
    <Box>
      {/* Full-race field overview (TransparencyUSA campaigns only) */}
      {cfg.financeSource === "transparencyusa" && raceFinance && (
        <>
          <SectionHeader
            title="Race Finance Overview"
            sub="Full candidate field · TransparencyUSA state filings"
            freshness={cd.freshness?.race_finance}
          />
          <RaceFinanceCard
            raceFinance={raceFinance}
            candidateName={cfg.candidateName}
          />
        </>
      )}

      {/* WinRed monthly velocity — only show when there is donation data */}
      {(winredLoading || hasWinredData) && (
        <>
          <SectionHeader
            title="Fundraising Velocity"
            sub={
              winredData?.monthly?.length > 0
                ? "WinRed live · 3-month smoothing average"
                : "WinRed static · 3-month smoothing average"
            }
          />
          <Box sx={{ mb: 3 }}>
            {winredLoading ? (
              <ChartSkeleton height={200} />
            ) : (
              <MonthlyVelocityChart
                labels={monthly.labels}
                raised={monthly.raised}
                smoothed={smoothed}
              />
            )}
          </Box>
        </>
      )}

      {/* Daily donation velocity — only show when there is WinRed data */}
      {(winredLoading || (hasWinredData && winredDaily)) && (
        <>
          <SectionHeader
            title="Daily Donation Velocity"
            sub="Last 30 days vs prior period"
          />
          {winredLoading || winredDailyLoading || !winredDaily ? (
            <ChartSkeleton height={200} />
          ) : (
            <DailyVelocityChart dailyData={winredDaily} />
          )}
        </>
      )}

      {/* FEC cash-on-hand sections — only for campaigns using FEC as finance source */}
      {cfg.financeSource === "fec" && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              mb: 0.5,
            }}
          >
            <SectionHeader
              title="Cash on Hand — FEC Quarterly"
              sub={`${cfg.candidateName || "Candidate"} vs. opponents · Candidate: OpenFEC live${liveFEC?.totals?.[cfg.candidateName] ? " · " + fecPeriod : ""}`}
              freshness={cd.freshness?.fec}
            />
            {dataSyncUrl && activeCampaignId && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  flexShrink: 0,
                }}
              >
                {fecSyncMsg && (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: fecSyncMsg.includes("fail")
                        ? "#c0392b"
                        : "#16a34a",
                    }}
                  >
                    {fecSyncMsg}
                  </Typography>
                )}
                <Box
                  component="button"
                  onClick={handleFecSync}
                  disabled={fecSyncing}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    px: 1.25,
                    py: 0.4,
                    border: `1px solid ${C.border}`,
                    borderRadius: 1,
                    bgcolor: C.white,
                    color: fecSyncing ? C.muted : C.navy,
                    cursor: fecSyncing ? "not-allowed" : "pointer",
                    "&:hover": { borderColor: C.navy },
                  }}
                >
                  {fecSyncing ? "Syncing…" : "↻ Sync FEC"}
                </Box>
              </Box>
            )}
          </Box>
          {/* Candidate headline stats */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <StatCard
              label={`${cfg.candidateName?.split(" ").pop() || "Candidate"} CoH`}
              value={fmtMoney(candidateCoh)}
              sub={`${fecPeriod} · ${liveFEC?.totals?.[cfg.candidateName] ? "FEC live" : "FEC"}`}
              accent={C.red}
              info={{
                eyebrow: "FEC · CASH ON HAND",
                title: `${cfg.candidateName || "Candidate"} Cash on Hand`,
                subtitle: `OpenFEC filing · ${fecPeriod}`,
                sections: [
                  {
                    heading: "WHAT IT MEASURES",
                    body: "Total cash on hand as reported in the candidate's most recent FEC filing.",
                  },
                  {
                    heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                    body: "CoH determines how many weeks of operations the campaign can sustain without new fundraising.",
                  },
                ],
              }}
            />
            <StatCard
              label="Small Donors"
              value={smallDonors.toLocaleString()}
              sub={`${cd.fecQuarters?.[lastQ] || "Latest"} · <$200`}
              accent={C.navy}
              info={{
                eyebrow: "FEC · SMALL-DOLLAR DONORS",
                title: "Small-Dollar Donor Count",
                subtitle: "FEC itemized contributions · under $200",
                sections: [
                  {
                    heading: "WHAT IT MEASURES",
                    body: "Number of individual contributors giving under $200 in the most recent reporting quarter.",
                  },
                  {
                    heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                    body: "Small-dollar donor count is the broadest measure of grassroots support and provides a renewable re-solicitation pool.",
                  },
                ],
              }}
            />
          </Box>

          {/* Opponent comparison table */}
          {(() => {
            const allOpps =
              Array.isArray(cfg.opponents) && cfg.opponents.length
                ? cfg.opponents.map((o) => {
                    const name = o.name || String(o);
                    const fr = cd.opponentFR?.[name] || null;
                    const coh = fr
                      ? ((fr.cashOnHand || [])[lastQ] ??
                        (fr.cashOnHand || []).at(-1) ??
                        0)
                      : null;
                    return { name, fec_id: o.fec_id || "", coh, fr };
                  })
                : _oppFREntries.map(([name, fr]) => {
                    const coh =
                      (fr.cashOnHand || [])[lastQ] ??
                      (fr.cashOnHand || []).at(-1) ??
                      0;
                    return { name, fec_id: "", coh, fr };
                  });

            const sorted = [...allOpps].sort((a, b) => {
              if (a.coh === null && b.coh === null) return 0;
              if (a.coh === null) return 1;
              if (b.coh === null) return -1;
              return b.coh - a.coh;
            });

            if (!sorted.length) return null;
            return (
              <Box
                sx={{
                  mb: 2,
                  border: `1px solid ${C.border}`,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 110px 110px 90px",
                    px: 1.5,
                    py: 0.75,
                    bgcolor: C.bg,
                    borderBottom: `2px solid ${C.border}`,
                  }}
                >
                  {["Opponent", "Cash on Hand", "Lead / Deficit", "FEC ID"].map(
                    (h) => (
                      <Typography
                        key={h}
                        sx={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {h}
                      </Typography>
                    ),
                  )}
                </Box>
                {sorted.map(({ name, fec_id, coh }, i) => {
                  const diff = coh !== null ? candidateCoh - coh : null;
                  const hasData = coh !== null;
                  const barPct =
                    hasData && candidateCoh > 0
                      ? Math.min(100, Math.round((coh / candidateCoh) * 100))
                      : 0;
                  const color = OPP_COLORS[i % OPP_COLORS.length];
                  return (
                    <Box
                      key={name}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 110px 110px 90px",
                        px: 1.5,
                        py: 1,
                        borderBottom:
                          i < sorted.length - 1
                            ? `1px solid ${C.border}`
                            : "none",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: C.charcoal,
                          }}
                        >
                          {name}
                        </Typography>
                        {hasData && (
                          <Box
                            sx={{
                              mt: 0.5,
                              height: 4,
                              bgcolor: C.border,
                              borderRadius: 2,
                              width: "80%",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                height: "100%",
                                width: `${barPct}%`,
                                bgcolor: color,
                                borderRadius: 2,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: hasData ? C.charcoal : C.muted,
                        }}
                      >
                        {hasData ? fmtMoney(coh) : "No filing"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color:
                            diff === null
                              ? C.muted
                              : diff >= 0
                                ? "#16a34a"
                                : "#c0392b",
                        }}
                      >
                        {diff === null
                          ? "—"
                          : (diff >= 0 ? "+" : "") + fmtMoney(Math.abs(diff))}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: fec_id ? C.muted : "#e5e7eb",
                          fontFamily: "monospace",
                        }}
                      >
                        {fec_id || "not set"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}

          <CoHLineChart quarters={cd.fecQuarters} datasets={_cohDatasets} />
        </>
      )}

      {/* Cross-candidate donor intelligence (TransparencyUSA campaigns only) */}
      {cfg.financeSource === "transparencyusa" && raceFinance && (
        <>
          <SectionHeader
            title="Donor Intelligence"
            sub="Shared contributors, entity/individual mix, shared vendors across the field"
          />
          <DonorIntelligenceCard raceFinance={raceFinance} cfg={cfg} />
        </>
      )}

      <SectionHeader
        title="Donor Cohort Intelligence"
        sub={
          donorCohorts
            ? "Live WinRed cohort counts · Strategy metadata by segment"
            : "Loading live cohort data…"
        }
      />
      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {[
                "Cohort",
                "Count",
                "Last Gift",
                "Avg Gift",
                "Priority",
                "Channel",
                "Frame",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    padding: "6px 10px",
                    borderBottom: `1px solid ${C.border}`,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohortRows.map((row, i) => (
              <tr
                key={i}
                style={{
                  backgroundColor: i % 2 === 1 ? C.rowAlt : "transparent",
                }}
              >
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.charcoal,
                  }}
                >
                  {row.name}
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                    fontWeight: donorCohorts ? 700 : 400,
                  }}
                >
                  {row.count?.toLocaleString?.() ?? row.count}
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                  }}
                >
                  {row.days ?? `${row.lastGiftDays}d ago`}
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                  }}
                >
                  {fmtMoney(row.avgGift)}
                </td>
                <td style={{ padding: "7px 10px" }}>
                  <PriorityBadge priority={row.priority} />
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                  }}
                >
                  {row.channel}
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                  }}
                >
                  {row.frame}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      <Box sx={{ mt: 3 }}>
        <SectionHeader
          title="Fundraising Driver Correlation"
          sub="Computed from live Kalshi markets, WinRed velocity & FEC data"
        />
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {[
                { label: "Variable", width: "22%" },
                { label: "Signal", width: "1%" },
                { label: "Correlation", width: "1%" },
                { label: "Interpretation" },
              ].map(({ label, width }) => (
                <th
                  key={label}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    padding: "6px 10px",
                    borderBottom: `1px solid ${C.border}`,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    width,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {drivers.map((d, i) => (
              <tr
                key={i}
                style={{
                  backgroundColor: i % 2 === 1 ? C.rowAlt : "transparent",
                }}
              >
                <td
                  style={{
                    padding: "8px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.charcoal,
                  }}
                >
                  {d.label}
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    whiteSpace: "nowrap",
                    width: "1%",
                  }}
                >
                  <SignalBadge signal={d.signal} />
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    fontSize: 12,
                    fontWeight: 800,
                    color: d.r >= 0 ? C.green : C.red,
                    whiteSpace: "nowrap",
                    width: "1%",
                  }}
                >
                  {d.r >= 0 ? "+" : ""}
                  {d.r.toFixed(2)}
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    fontSize: 10,
                    color: C.charcoal,
                    lineHeight: 1.4,
                    maxWidth: 180,
                  }}
                >
                  {d.interpretation}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      <OpponentLens
        cd={cd}
        cfg={cfg}
        liveFEC={liveFEC}
        opponentIntel={opponentIntel}
        onGenerate={onGenerateOpponentIntel}
        generating={opponentIntelLoading}
      />
    </Box>
  );
};

// ─── Campaign Tab ──────────────────────────────────────────────────────────
const CampaignTab = ({
  kalshi,
  kalshiLoading,
  cd = {},
  cfg = {},
  opponentIntel,
  onGenerateOpponentIntel,
  opponentIntelLoading,
  liveFEC,
  onSync,
  syncing,
}) => {
  // Polling detail chart — primary head-to-head
  // poll_averages rows are keyed by "candidate" plus opponent names dynamically
  const pollKeys =
    cd.pollAverages.length > 0
      ? Object.keys(cd.pollAverages[0]).filter(
          (k) => k !== "candidate" && k !== "Undecided",
        )
      : [];
  const POLL_COLORS = [
    C.navyMid,
    C.blue,
    C.orange,
    C.muted,
    "#6d28d9",
    "#059669",
  ];
  // Net favorability opponent key (still needed for StripeLineChart datasets)
  const favOpponentKey =
    cd.favNet.length > 0
      ? Object.keys(cd.favNet[0]).find((k) => k !== "candidate")
      : null;

  // Summary stats from last poll
  const latestPoll = cd.pollAverages[cd.pollAverages.length - 1] || {};
  const latestFav = cd.favNet[cd.favNet.length - 1] || {};
  const latestPollKeys = Object.keys(latestPoll).filter(
    (k) => k !== "candidate" && k !== "Undecided",
  );
  const primaryOpponentKey = latestPollKeys[0] || null;
  const latestMargin = primaryOpponentKey
    ? (
        (latestPoll.candidate || 0) - (latestPoll[primaryOpponentKey] || 0)
      ).toFixed(1)
    : null;
  const latestApproval =
    PRESIDENTIAL_APPROVAL[PRESIDENTIAL_APPROVAL.length - 1];
  const latestRightTrack = RIGHT_TRACK[RIGHT_TRACK.length - 1];
  const isLeading = primaryOpponentKey
    ? (latestPoll.candidate || 0) > (latestPoll[primaryOpponentKey] || 0)
    : true;
  const candName = cfg.candidateName || "Candidate";
  const OPP_COLORS = [C.navyMid, C.blue, C.orange, C.muted, "#6d28d9"];
  const hasPollData = Object.keys(latestPoll).length > 0;

  return (
    <Box>
      {/* Poll summary stat boxes */}
      {hasPollData && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <StatCard
            label={candName}
            value={`${latestPoll.candidate ?? "—"}%`}
            sub={cd.pollDates[cd.pollDates.length - 1] || ""}
            accent={C.red}
            info={{
              eyebrow: "POLLING · CANDIDATE SHARE",
              title: `${candName} — Vote Share`,
              subtitle: `Latest polling average · ${cd.pollDates[cd.pollDates.length - 1] || ""}`,
              sections: [
                {
                  heading: "WHAT IT MEASURES",
                  body: "Average vote share across recent public polls for this race. Where multiple polls exist, figures represent a simple average of likely voter samples.",
                },
                {
                  heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                  body: "This is the headline number — but snapshot position matters less than direction. A candidate at 32% trending up has more momentum value than one at 38% trending down. Watch the trend across snapshots, not just the current figure.",
                },
              ],
            }}
          />
          {latestPollKeys.slice(0, 3).map((k, i) => (
            <StatCard
              key={k}
              label={k}
              value={`${latestPoll[k] ?? "—"}%`}
              sub=""
              accent={OPP_COLORS[i]}
              info={{
                eyebrow: "POLLING · OPPONENT SHARE",
                title: `${k} — Vote Share`,
                subtitle: "Latest polling average",
                sections: [
                  {
                    heading: "WHAT IT MEASURES",
                    body: `Average vote share for ${k} across recent public polls. Figures represent likely voter samples.`,
                  },
                  {
                    heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                    body: `Track ${k}'s number relative to yours — the head-to-head gap is what matters most. Watch for movement in their number when major news breaks; a sudden shift often signals a campaign event worth analyzing.`,
                  },
                ],
              }}
            />
          ))}
          {primaryOpponentKey && latestMargin !== null && (
            <StatCard
              label={
                isLeading
                  ? `Lead vs ${primaryOpponentKey}`
                  : `Deficit vs ${primaryOpponentKey}`
              }
              value={`${isLeading ? "+" : ""}${latestMargin}pts`}
              sub="key head-to-head"
              accent={isLeading ? C.green : C.red}
              info={{
                eyebrow: "POLLING · HEAD-TO-HEAD MARGIN",
                title: isLeading ? "Current Lead" : "Current Deficit",
                subtitle: `${candName} vs. ${primaryOpponentKey}`,
                sections: [
                  {
                    heading: "WHAT IT MEASURES",
                    body: `The point difference between ${candName} and ${primaryOpponentKey} in the latest polling average. A positive number means leading; negative means trailing.`,
                  },
                  {
                    heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                    body: isLeading
                      ? "A lead at this stage is an asset to protect and extend. Leads evaporate fastest in the final 3 weeks — that is when paid media, GOTV, and earned media matter most. Do not assume it holds without continued investment."
                      : "A deficit at this stage is not fatal — races flip regularly — but it requires a clear theory of where the votes come from. Identify which voter segments are movable and focus messaging there rather than reinforcing existing support.",
                  },
                ],
              }}
            />
          )}
          {latestFav.candidate != null && (
            <StatCard
              label={`${candName.split(" ").pop()} Net Fav`}
              value={`${latestFav.candidate > 0 ? "+" : ""}${latestFav.candidate}`}
              sub="Favorable − Unfavorable"
              accent={latestFav.candidate > 0 ? C.green : C.orange}
              info={{
                eyebrow: "POLLING · NET FAVORABILITY",
                title: `${candName} Net Favorability`,
                subtitle: "Favorable % minus Unfavorable %",
                sections: [
                  {
                    heading: "WHAT IT MEASURES",
                    body: "Net favorability is the percentage of voters who view the candidate favorably minus those who view them unfavorably. Unlike vote share, it captures sentiment among voters who may not yet have committed.",
                  },
                  {
                    heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                    body: "Favorability is a leading indicator — it often moves before vote share does. A candidate with rising favorability and flat vote share is building a coalition that hasn't yet fully converted. A candidate with declining favorability and stable vote share is living on borrowed time.",
                  },
                ],
              }}
            />
          )}
        </Box>
      )}

      {/* Field — segmented by race state (Ballotpedia = source of truth for who's still in) */}
      {(() => {
        const field = cd.fieldCandidates || [];
        if (field.length === 0) {
          return (
            <>
              <SectionHeader
                title={`${cfg.party || "Republican"} Field${cfg.office ? ` — ${cfg.office}` : ""}${cfg.state ? ` · ${cfg.state}` : ""}`}
                freshness={cd.freshness?.race_status || cd.freshness?.field}
              />
              <Box
                sx={{
                  height: 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  bgcolor: C.bg,
                  borderRadius: 1.5,
                  border: `1px dashed ${C.border}`,
                }}
              >
                <Typography sx={{ fontSize: 12, color: C.muted }}>
                  No field data available
                </Typography>
              </Box>
            </>
          );
        }

        const normParty = (p) => {
          const s = (p || "").toLowerCase();
          if (s.includes("republican") || s === "rep" || s === "r")
            return "REP";
          if (s.includes("democrat") || s === "dem" || s === "d") return "DEM";
          if (s.includes("libertarian")) return "LIB";
          if (s.includes("green")) return "GRE";
          if (s.includes("independ")) return "IND";
          return (p || "").toUpperCase().slice(0, 3);
        };
        const ourParty = normParty(cfg.party || "");
        const isOurs = (c) => c.name === cfg.candidateName;
        const ELIM = [
          "lost_primary",
          "lost_runoff",
          "withdrew",
          "disqualified",
          "endorsed_out",
        ];
        const samePty = field.filter(
          (c) => isOurs(c) || !ourParty || c.party === ourParty,
        );
        const groups = [
          {
            key: "active",
            title: `${cfg.party || "Primary"} Field — Still In`,
            sub: `Active in the current contest${cfg.office ? ` · ${cfg.office}` : ""}`,
            rows: samePty
              .filter((c) => !ELIM.includes(c.status))
              .sort((a, b) => (isOurs(b) ? 1 : 0) - (isOurs(a) ? 1 : 0)),
          },
          {
            key: "elim",
            title: "Eliminated",
            sub: "Lost their primary or withdrew",
            rows: samePty.filter((c) => ELIM.includes(c.status) && !isOurs(c)),
          },
          {
            key: "gen",
            title: "General Election Field",
            sub: "Opposing-party & third-party candidates — your opponents only if you advance",
            rows: field.filter(
              (c) => ourParty && c.party !== ourParty && !isOurs(c),
            ),
          },
        ].filter((g) => g.rows.length);

        const statusStyle = (status) => {
          if (["running", "won"].includes(status))
            return { color: "#15803d", bg: "#f0fdf4" };
          if (["withdrew", "endorsed_out"].includes(status))
            return { color: C.orange, bg: "#fff7ed" };
          if (["lost_primary", "lost_runoff", "disqualified"].includes(status))
            return { color: C.red, bg: "#fef2f2" };
          return { color: C.muted, bg: C.bg };
        };

        return groups.map((g) => (
          <Box key={g.key} sx={{ mb: 3 }}>
            <SectionHeader
              title={g.title}
              sub={g.sub}
              freshness={cd.freshness?.race_status || cd.freshness?.field}
            />
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Candidate", "Status", "Notes"].map((h) => (
                      <th
                        key={h}
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          padding: "6px 10px",
                          borderBottom: `1px solid ${C.border}`,
                          textAlign: "left",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((c, i) => {
                    const ss = statusStyle(c.status);
                    const isOurs = c.name === cfg.candidateName;
                    return (
                      <tr
                        key={i}
                        style={{
                          backgroundColor:
                            i % 2 === 1 ? C.rowAlt : "transparent",
                        }}
                      >
                        <td
                          style={{
                            padding: "7px 10px",
                            fontSize: 11,
                            fontWeight: isOurs ? 800 : 600,
                            color: isOurs ? C.red : C.charcoal,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isOurs ? `★ ${c.name}` : c.name}
                          {c.party && !isOurs && (
                            <Box
                              component="span"
                              sx={{ ml: 0.75, fontSize: 9, color: C.muted }}
                            >
                              {c.party}
                            </Box>
                          )}
                        </td>
                        <td
                          style={{ padding: "7px 10px", whiteSpace: "nowrap" }}
                        >
                          {c.statusLabel ? (
                            <Box
                              component="span"
                              sx={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                px: 0.75,
                                py: "2px",
                                borderRadius: "3px",
                                color: ss.color,
                                bgcolor: ss.bg,
                                border: `1px solid ${ss.color}33`,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                              }}
                            >
                              {c.statusLabel}
                            </Box>
                          ) : (
                            <Box
                              component="span"
                              sx={{ fontSize: 11, color: C.muted }}
                            >
                              —
                            </Box>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "7px 10px",
                            fontSize: 11,
                            color: C.charcoal,
                            lineHeight: 1.5,
                          }}
                        >
                          {c.note}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </Box>
        ));
      })()}

      {/* Polling detail chart */}
      <SectionHeader
        title={`Primary Polling Trend${cfg.office ? ` — ${cfg.office}` : ""}`}
        sub="Named candidates · polling averages"
        freshness={cd.freshness?.polling}
        onSync={onSync}
        syncing={syncing}
      />
      {cd.pollDates.length === 0 ? (
        <Box
          sx={{
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            bgcolor: C.bg,
            borderRadius: 1.5,
            border: `1px dashed ${C.border}`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            No polling data — add polls using the Polling tab
          </Typography>
        </Box>
      ) : (
        <StripeLineChart
          labels={cd.pollDates}
          datasets={[
            {
              label: candName,
              data: cd.pollAverages.map((r) => r.candidate),
              color: PARTY_COLORS.Republican,
              fill: true,
            },
            ...pollKeys.map((key, i) => ({
              label: key,
              data: cd.pollAverages.map((r) => r[key]),
              color: POLL_COLORS[i % POLL_COLORS.length],
              dashed: true,
            })),
          ]}
          height={200}
          yMin={0}
          yMax={
            Math.max(40, ...cd.pollAverages.map((r) => r.candidate || 0)) + 5
          }
          yFmt={(v) => v + "%"}
          valFmt={(v) => (v != null ? v.toFixed(1) + "%" : "—")}
        />
      )}

      {/* Net favorability */}
      <SectionHeader
        title="Net Favorability"
        sub="Favorable% − Unfavorable% · quarterly"
        freshness={cd.freshness?.polling}
      />
      {cd.favDates.length === 0 ? (
        <Box
          sx={{
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            bgcolor: C.bg,
            borderRadius: 1.5,
            border: `1px dashed ${C.border}`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            No favorability data available
          </Typography>
        </Box>
      ) : (
        <StripeLineChart
          labels={cd.favDates}
          datasets={[
            {
              label: `${candName} Net Fav`,
              data: cd.favNet.map((r) => r.candidate),
              color: PARTY_COLORS.Republican,
            },
            ...(favOpponentKey
              ? [
                  {
                    label: `${favOpponentKey} Net Fav`,
                    data: cd.favNet.map((r) => r[favOpponentKey]),
                    color: C.navyMid,
                    dashed: true,
                  },
                ]
              : []),
          ]}
          height={160}
          yFmt={(v) => (v > 0 ? "+" : "") + v}
          valFmt={(v) => (v != null ? (v > 0 ? "+" : "") + v : "—")}
        />
      )}

      {/* Structural indicators */}
      <SectionHeader
        title="Structural Indicators"
        sub="Presidential approval · right track · generic ballot · Source: Gallup / RCP"
      />
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <StatCard
          label="Trump Approval"
          value={`${latestApproval}%`}
          sub="Gallup/RCP avg · Mar '26"
          accent={C.red}
          info={{
            eyebrow: "MACRO · PRESIDENTIAL APPROVAL",
            title: "Trump Approval Rating",
            subtitle: "Gallup / RCP composite average",
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "The percentage of Americans who approve of President Trump's job performance, averaged across major polling firms including Gallup and RealClearPolitics.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: "Presidential approval is the single most powerful coattail signal for down-ballot Republicans. Above 50%, base turnout is energized and MAGA-aligned messaging performs well. Below 50%, lean into local contrast and distance from Washington — the anti-establishment frame becomes a structural asset.",
              },
            ],
          }}
        />
        <StatCard
          label="Right Track"
          value={`${latestRightTrack}%`}
          sub="RCP composite · Mar '26"
          accent={C.orange}
          info={{
            eyebrow: "MACRO · NATIONAL DIRECTION",
            title: "Right Track / Wrong Track",
            subtitle: "RCP composite · national direction sentiment",
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "The percentage of voters who believe the country is heading in the right direction, as measured by the RealClearPolitics composite average of major polls.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: "Right Track below 40% signals an anti-incumbent environment. Challenger candidates benefit from low Right Track — it validates the 'change' narrative. Incumbents should pivot to local performance rather than national conditions when this figure is depressed.",
              },
            ],
          }}
        />
        <StatCard
          label="Generic Ballot"
          value={`D+${GENERIC_BALLOT[GENERIC_BALLOT.length - 1]}`}
          sub="National · Mar '26"
          accent={C.blue}
          info={{
            eyebrow: "MACRO · GENERIC BALLOT",
            title: "Congressional Generic Ballot",
            subtitle: "National party preference · likely voters",
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "The generic congressional ballot asks voters whether they would vote for the Republican or Democratic candidate in their House district, without naming specific candidates. Measured as the D advantage or disadvantage vs. R.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: "A D+3 or higher generic ballot historically produces House seat flips. For Republican candidates, a negative generic environment means base mobilization matters more than persuasion — the wave works against you and you must over-perform your party baseline.",
              },
            ],
          }}
        />
        <StatCard
          label="Unemployment"
          value={`${UNEMPLOYMENT[UNEMPLOYMENT.length - 1]}%`}
          sub="BLS national · Mar '26"
          accent={C.navy}
          info={{
            eyebrow: "MACRO · ECONOMIC CONDITIONS",
            title: "National Unemployment Rate",
            subtitle: "Bureau of Labor Statistics · seasonally adjusted",
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "The U.S. national unemployment rate as reported monthly by the Bureau of Labor Statistics, seasonally adjusted.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: "Rising unemployment increases economic anxiety and incumbent vulnerability. For challengers, a rising rate supports the 'change' narrative. For incumbents, it creates pressure to defend economic stewardship. Unemployment above 5% historically correlates with meaningful down-ballot swings.",
              },
            ],
          }}
        />
      </Box>
      <StripeLineChart
        labels={STRUCTURAL_DATES}
        datasets={[
          {
            label: "Trump Approval %",
            data: PRESIDENTIAL_APPROVAL,
            color: C.red,
          },
          {
            label: "Right Track %",
            data: RIGHT_TRACK,
            color: C.orange,
            dashed: true,
          },
          {
            label: "Generic Ballot (D-R)",
            data: GENERIC_BALLOT,
            color: C.blue,
            dashed: true,
          },
        ]}
        height={180}
        yFmt={(v) => v + "%"}
        valFmt={(v) => (v != null ? (v > 0 ? "+" : "") + v + "%" : "—")}
      />

      {/* Voter registration */}
      <SectionHeader
        title={`Voter Registration${cfg.state ? ` — ${cfg.state}` : ""} Net New`}
        sub="Quarterly net new registrations by party · State Election Commission"
        freshness={cd.freshness?.voter_reg}
      />
      {cd.voterRegDates.length === 0 ? (
        <Box
          sx={{
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            bgcolor: C.bg,
            borderRadius: 1.5,
            border: `1px dashed ${C.border}`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            No voter registration data available
          </Typography>
        </Box>
      ) : (
        <StripeStackedBarChart
          labels={cd.voterRegDates}
          datasets={[
            {
              label: "Democrat",
              data: cd.voterRegNet.map((q) => q[0]),
              color: PARTY_COLORS.Democrat + "cc",
            },
            {
              label: "Republican",
              data: cd.voterRegNet.map((q) => q[1]),
              color: PARTY_COLORS.Republican + "cc",
            },
            {
              label: "Other",
              data: cd.voterRegNet.map((q) => q[2]),
              color: C.muted + "99",
            },
          ]}
          height={180}
        />
      )}

      {/* Issue salience */}
      <SectionHeader
        title={`Issue Salience${cfg.state ? ` — ${cfg.state} Statewide` : ""}`}
        sub={`Voter priority ranking · ${cfg.office || "primary"} electorate`}
        freshness={cd.freshness?.strategy}
      />
      <Box sx={{ mb: 3 }}>
        {cd.issueSalience.length === 0 && (
          <Box
            sx={{
              height: 60,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: C.bg,
              borderRadius: 1.5,
              border: `1px dashed ${C.border}`,
            }}
          >
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              No issue salience data available
            </Typography>
          </Box>
        )}
        {cd.issueSalience.map((issue, i) => {
          const trend =
            issue.trend === "up" ? "▲" : issue.trend === "down" ? "▼" : "—";
          const trendColor =
            issue.trend === "up"
              ? C.red
              : issue.trend === "down"
                ? C.green
                : C.muted;
          return (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.4,
                }}
              >
                <Typography
                  sx={{ fontSize: 11, fontWeight: 600, color: C.charcoal }}
                >
                  {issue.label}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography
                    sx={{ fontSize: 11, color: trendColor, fontWeight: 700 }}
                  >
                    {trend}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 800, color: C.navyDark }}
                  >
                    {issue.pct}%
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  height: 10,
                  bgcolor: "#e2e8f0",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${issue.pct * 4}%`,
                    bgcolor: C.navy,
                    borderRadius: "0 3px 3px 0",
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Historical results */}
      {cd.electionHistory?.length > 0 && (
        <>
          <SectionHeader
            title={`${candName} Electoral History`}
            sub="Prior race results · context for current race"
            freshness={cd.freshness?.election_history}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[
                  "Year",
                  `${candName.split(" ").pop()} %`,
                  "Opponent %",
                  "Margin",
                  "Opponent",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "6px 10px",
                      borderBottom: `1px solid ${C.border}`,
                      textAlign: "left",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cd.electionHistory.map((row, i) => {
                const margin = (row.candidate - row.opponent).toFixed(1);
                return (
                  <tr
                    key={i}
                    style={{
                      backgroundColor: i % 2 === 1 ? C.rowAlt : "transparent",
                    }}
                  >
                    <td
                      style={{
                        padding: "8px 10px",
                        fontSize: 12,
                        fontWeight: 800,
                        color: C.navy,
                      }}
                    >
                      {row.year}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.red,
                      }}
                    >
                      {row.candidate}%
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        fontSize: 11,
                        color: C.muted,
                      }}
                    >
                      {row.opponent}%
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        fontSize: 12,
                        fontWeight: 800,
                        color: C.green,
                      }}
                    >
                      +{margin}%
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        fontSize: 11,
                        color: C.charcoal,
                      }}
                    >
                      {row.note}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <OpponentLens
        cd={cd}
        cfg={cfg}
        liveFEC={liveFEC}
        opponentIntel={opponentIntel}
        onGenerate={onGenerateOpponentIntel}
        generating={opponentIntelLoading}
      />
    </Box>
  );
};

// ─── Kalshi Historical Trend Charts ────────────────────────────────────────
const SPARK_SERIES = [
  {
    key: "KXRECSSNBER",
    label: "Recession Risk",
    color: "#f87171",
    note: "NBER 2026",
    info: {
      eyebrow: "PREDICTION MARKET · KXRECSSNBER",
      title: "NBER Recession Probability 2026",
      subtitle:
        "Kalshi · National Bureau of Economic Research official declaration",
      sections: [
        {
          heading: "WHAT IT MEASURES",
          body: "Probability that NBER officially declares a U.S. recession in 2026. NBER uses a broad set of indicators — GDP, employment, income, and consumer spending — and typically declares recessions months after they begin.",
        },
        {
          heading: "WHY IT MATTERS",
          body: "Economic anxiety is a double-edged signal. Higher recession probability suppresses discretionary giving but amplifies anti-incumbent sentiment — a structural tailwind for challenger candidates vs. establishment figures.",
        },
      ],
    },
  },
  {
    key: "CONTROLH",
    label: "D Wins House 2026",
    color: "#60a5fa",
    note: "National wave signal",
    info: {
      eyebrow: "PREDICTION MARKET · CONTROLH",
      title: "Democrats Win House Majority 2026",
      subtitle: "Kalshi · congressional control · midterm cycle",
      sections: [
        {
          heading: "WHAT IT MEASURES",
          body: "Market-implied probability that Democrats flip the House in the 2026 midterms. Kalshi traders put real money at risk — this price reflects collective wisdom across thousands of informed market participants.",
        },
        {
          heading: "WHY IT MATTERS",
          body: "A high D-wins-House probability signals an anti-R national environment. For a primary, this can paradoxically help challenger candidates — the outsider frame resonates when voters are rejecting the establishment.",
        },
      ],
    },
  },
  {
    key: "KXRETIREMACE",
    label: "Candidate Exits Early",
    color: "#fb923c",
    note: "Before midterms · Kalshi",
    info: {
      eyebrow: "PREDICTION MARKET · CANDIDATE EXITS SEAT",
      title: "Candidate Leaves Current Seat Before Midterms",
      subtitle: "Kalshi · retirement or resignation from current seat",
      sections: [
        {
          heading: "WHAT IT MEASURES",
          body: "Probability that the candidate vacates their current seat before the 2026 midterms — which would occur upon winning the target race and being inaugurated. A declining price reflects reduced market confidence in a win.",
        },
        {
          heading: "CAMPAIGN SIGNAL",
          body: "This market effectively prices the candidate's probability of winning the target race. Watch for convergence with the direct race market — wide divergence can indicate market inefficiency or breaking news.",
        },
      ],
    },
  },
  {
    key: "KXAPRPOTUS",
    label: "Trump Approval Proxy",
    color: "#a78bfa",
    note: "Most-liquid approval market",
    info: {
      eyebrow: "PREDICTION MARKET · KXAPRPOTUS",
      title: "Trump Presidential Approval Market",
      subtitle: "Kalshi · most-liquid presidential approval proxy",
      sections: [
        {
          heading: "WHAT IT MEASURES",
          body: "The most actively traded Kalshi market tracking Trump's approval probability. Different contract buckets track specific approval thresholds — this displays the most-liquid open contract, which approximates current approval momentum.",
        },
        {
          heading: "WHY IT MATTERS",
          body: "Trump approval drives the coattails environment for GOP candidates. High approval energizes the base and amplifies urgency messaging tied to Trump's agenda. Declining approval suppresses base turnout and can create space for the outsider narrative.",
        },
      ],
    },
  },
];

const BackfillButton = ({ onDone, historyEmpty }) => {
  const [state, setState] = useState("idle"); // idle | running | done | error
  const [report, setReport] = useState(null);
  const base = KALSHI_URL.replace("/markets", "");
  const run = async () => {
    setState("running");
    try {
      const res = await fetch(`${base}/snapshot/backfill`, { method: "POST" });
      const data = await res.json();
      if (res.ok && data.ok) {
        setReport(data.report);
        setState("done");
        setTimeout(() => {
          onDone?.();
          setState("idle");
        }, 2000);
      } else {
        setReport(data);
        setState("error");
      }
    } catch (e) {
      setReport({ error: e.message });
      setState("error");
    }
  };
  if (!historyEmpty && state === "idle") return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {report && (
        <Typography
          sx={{
            fontSize: 9,
            color: state === "error" ? C.red : "#22c55e",
            maxWidth: 300,
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {state === "done"
            ? "Backfill complete — refreshing…"
            : JSON.stringify(report).slice(0, 120)}
        </Typography>
      )}
      <Box
        component="button"
        onClick={run}
        disabled={state === "running"}
        sx={{
          fontSize: 10,
          fontWeight: 700,
          px: 1.5,
          py: 0.5,
          bgcolor:
            state === "done" ? "#22c55e" : state === "error" ? C.red : C.navy,
          color: "#fff",
          border: "none",
          borderRadius: 1,
          cursor: state === "running" ? "default" : "pointer",
          opacity: state === "running" ? 0.7 : 1,
          whiteSpace: "nowrap",
        }}
      >
        {state === "running"
          ? "Backfilling…"
          : state === "done"
            ? "✓ Done"
            : "Backfill History"}
      </Box>
    </Box>
  );
};

const KalshiTrendCharts = ({ history, loading, onChartClick }) => {
  if (loading && !history)
    return (
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            sx={{ flex: "1 1 220px", height: 160, borderRadius: 1.5 }}
          />
        ))}
      </Box>
    );
  if (!history?.history) return null;

  const fmtDate = (ts) =>
    new Date(ts * 1000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

  const charts = SPARK_SERIES.map(({ key, label, color, note, info }) => {
    const s = history.history[key];
    if (!s?.candlesticks?.length) return null;
    const candles = s.candlesticks;
    const current = candles[candles.length - 1]?.close;
    const first = candles[0]?.close;
    const change = current != null && first != null ? current - first : null;
    const vals = candles.map((c) => c.close);
    const yMin = Math.max(0, Math.min(...vals) - 4);
    const yMax = Math.min(100, Math.max(...vals) + 4);
    const clickable = !!info && !!onChartClick;

    return (
      <Box
        key={key}
        onClick={() => clickable && onChartClick(info)}
        sx={{
          bgcolor: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 1.5,
          p: 2,
          flex: "1 1 220px",
          minWidth: 200,
          cursor: clickable ? "pointer" : "default",
          transition: "box-shadow 0.15s",
          "&:hover": clickable ? { boxShadow: `0 2px 10px ${color}33` } : {},
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                lineHeight: 1.3,
              }}
            >
              {label}
            </Typography>
            <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.25 }}>
              {s.ticker} · {note}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0, ml: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                justifyContent: "flex-end",
              }}
            >
              {clickable && (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: C.muted,
                    lineHeight: 1,
                    userSelect: "none",
                  }}
                >
                  ⓘ
                </Typography>
              )}
              <Typography
                sx={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}
              >
                {current != null ? `${current}%` : "—"}
              </Typography>
            </Box>
            {change != null && (
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: change > 0 ? C.red : C.green,
                }}
              >
                {change > 0 ? "+" : ""}
                {change.toFixed(0)}pp
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ height: 90 }}>
          <Line
            data={{
              labels: candles.map((c) => fmtDate(c.ts)),
              datasets: [
                {
                  data: vals,
                  borderColor: color,
                  backgroundColor: `${color}18`,
                  fill: true,
                  tension: 0.4,
                  pointRadius: 0,
                  borderWidth: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => `${ctx.parsed.y}%`,
                    title: (ctx) => ctx[0].label,
                  },
                  backgroundColor: "#ffffff",
                  borderColor: "rgba(0,0,0,0.10)",
                  borderWidth: 1,
                  titleColor: "#1f2937",
                  bodyColor: "#1f2937",
                  padding: 8,
                  cornerRadius: 6,
                  titleFont: { size: 10 },
                  bodyFont: { size: 11, weight: "bold" },
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: {
                    maxTicksLimit: 4,
                    font: { size: 8 },
                    color: C.muted,
                  },
                },
                y: {
                  min: yMin,
                  max: yMax,
                  grid: { color: "#f0f0f0" },
                  ticks: {
                    callback: (v) => `${v}%`,
                    maxTicksLimit: 4,
                    font: { size: 8 },
                    color: C.muted,
                  },
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  }).filter(Boolean);

  if (!charts.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>{charts}</Box>
  );
};

// ─── Policy Tab ───────────────────────────────────────────────────────────────
const _partyColor = (party) =>
  party === "Republican"
    ? PARTY_COLORS.Republican
    : party === "Democrat"
      ? PARTY_COLORS.Democrat
      : PARTY_COLORS.Independent;

const PolicyTab = ({
  cd,
  cfg,
  opponentIntel,
  onGenerateOpponentIntel,
  opponentIntelLoading,
  policyPositionsLoading,
  onRegeneratePositions,
}) => {
  const candName = cfg.candidateName || "Candidate";
  const candShort = candName.split(" ").pop();
  // Prefer opponent derived from live race data (Kalshi markets / field.json) stored in the positions result.
  // Fall back to configured opponents list only if positions haven't been generated yet.
  const oppName =
    cd.policyOpponent ||
    (cfg.opponentsLabel ? cfg.opponentsLabel.split(",")[0].trim() : null);
  const oppParty =
    cd.policyOpponentParty ||
    (oppName ? (cfg.party === "Republican" ? "Democrat" : "Republican") : null);

  const hasIssues = cd.issueSalience?.length > 0;
  const hasPositions = cd.policyPositions?.length > 0;

  return (
    <Box>
      {/* Issue Landscape */}
      <SectionHeader
        title={`Issue Landscape${cfg.state ? ` — ${cfg.state}` : ""}`}
        sub="Voter-ranked issue priorities · electorate"
        freshness={cd.freshness?.strategy}
      />
      {!hasIssues ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 11, color: C.muted }}>
            No issue data loaded
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          {cd.issueSalience.map((issue, i) => {
            const trend =
              issue.trend === "up" ? "▲" : issue.trend === "down" ? "▼" : "—";
            const trendColor =
              issue.trend === "up"
                ? C.red
                : issue.trend === "down"
                  ? C.green
                  : C.muted;
            return (
              <Box key={i} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.4,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 600, color: C.charcoal }}
                  >
                    {issue.label}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography
                      sx={{ fontSize: 11, color: trendColor, fontWeight: 700 }}
                    >
                      {trend}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, fontWeight: 800, color: C.navyDark }}
                    >
                      {issue.pct}%
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    height: 10,
                    bgcolor: "#e2e8f0",
                    borderRadius: "3px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${issue.pct * 4}%`,
                      bgcolor: C.navy,
                      borderRadius: "0 3px 3px 0",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Candidate Positions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <SectionHeader
          title="Candidate Positions"
          sub={`${candShort} stance by issue${oppName ? ` vs. ${oppName.split(" ").pop()}` : ""}`}
        />
        {onRegeneratePositions && (
          <Box
            component="button"
            onClick={policyPositionsLoading ? undefined : onRegeneratePositions}
            disabled={policyPositionsLoading}
            title={
              policyPositionsLoading ? "Regenerating…" : "Regenerate positions"
            }
            sx={{
              background: "none",
              border: `1px solid ${policyPositionsLoading ? C.border : C.navy}`,
              borderRadius: "4px",
              px: 1,
              py: 0.3,
              cursor: policyPositionsLoading ? "default" : "pointer",
              color: policyPositionsLoading ? C.muted : C.navy,
              fontSize: 10,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 1,
              opacity: policyPositionsLoading ? 0.5 : 1,
              "&:hover": {
                bgcolor: policyPositionsLoading ? "transparent" : C.navy,
                color: policyPositionsLoading ? C.muted : "#fff",
              },
            }}
          >
            {policyPositionsLoading ? "Regenerating…" : "↻ Regenerate"}
          </Box>
        )}
      </Box>
      {!hasPositions ? (
        <Box
          sx={{
            border: `1px dashed ${C.border}`,
            borderRadius: 1,
            py: 3,
            px: 3,
            mb: 3,
          }}
        >
          {policyPositionsLoading ? (
            <IntelLoader bars={[70, 90, 55, 80]} barHeight={8} />
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: 11, fontWeight: 600, color: C.muted, mb: 1 }}
              >
                No positions loaded
              </Typography>
              <SpinnerBtn
                loading={false}
                onClick={onRegeneratePositions}
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.5,
                  bgcolor: C.navy,
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: 1,
                  minWidth: 0,
                }}
              >
                ⚡ Generate Positions
              </SpinnerBtn>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          {cd.policyPositions.map((pos, i) => {
            const candColor = _partyColor(cfg.party || "Republican");
            const oppColor = oppParty ? _partyColor(oppParty) : C.muted;
            const candInitial = (cfg.party || "R").charAt(0);
            const oppInitial = oppParty ? oppParty.charAt(0) : "D";
            return (
              <Box
                key={i}
                sx={{
                  mb: 2,
                  border: `1px solid ${C.border}`,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                {/* Issue header */}
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: C.navy,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#fff",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {pos.issue}
                  </Typography>
                  {pos.salience != null && (
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "rgba(255,255,255,0.55)",
                        fontWeight: 600,
                      }}
                    >
                      {pos.salience}% salience
                    </Typography>
                  )}
                </Box>
                {/* Position rows */}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      mb: pos.opponentStance ? 1 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: candColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.1,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 8, fontWeight: 900, color: "#fff" }}
                      >
                        {candInitial}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.charcoal,
                          mb: 0.25,
                        }}
                      >
                        {candShort}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          color: C.charcoal,
                          lineHeight: 1.55,
                        }}
                      >
                        {pos.candidateStance}
                      </Typography>
                    </Box>
                  </Box>
                  {pos.opponentStance && oppName && (
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          bgcolor: oppColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.1,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 8, fontWeight: 900, color: "#fff" }}
                        >
                          {oppInitial}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.charcoal,
                            mb: 0.25,
                          }}
                        >
                          {oppName.split(" ").pop()}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: C.muted,
                            lineHeight: 1.55,
                          }}
                        >
                          {pos.opponentStance}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                {pos.contrast && (
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      bgcolor: "#fff8f0",
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 9.5, color: "#92400e", fontWeight: 600 }}
                    >
                      ⚡ {pos.contrast}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <OpponentLens
        cd={cd}
        cfg={cfg}
        liveFEC={null}
        opponentIntel={opponentIntel}
        onGenerate={onGenerateOpponentIntel}
        generating={opponentIntelLoading}
      />
    </Box>
  );
};

// ─── News Tab ─────────────────────────────────────────────────────────────────
const IMPACT_COLOR = {
  positive: "#16a34a",
  negative: "#dc2626",
  neutral: "#6b7280",
  mixed: "#d97706",
};
const IMPACT_BG = {
  positive: "#f0fdf4",
  negative: "#fef2f2",
  neutral: "#f9fafb",
  mixed: "#fffbeb",
};
const IMPACT_LABEL = {
  positive: "Positive",
  negative: "Negative",
  neutral: "Neutral",
  mixed: "Mixed",
};

const UrgencyDots = ({ level = 1 }) => (
  <Box sx={{ display: "flex", gap: 0.4, alignItems: "center" }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Box
        key={i}
        sx={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          bgcolor:
            i <= level
              ? level >= 4
                ? "#dc2626"
                : level >= 3
                  ? "#d97706"
                  : C.navy
              : C.border,
        }}
      />
    ))}
  </Box>
);

const NEWS_NOISE_TITLE_RE =
  /\b(obituary|obituaries|funeral home|funeral parlor|memorial funeral|in memoriam|passed away|rest in peace|remembering|celebrating the life|death notice|death of|died at|died on|beloved|condolences)\b/i;
const NEWS_NOISE_SOURCE_RE =
  /legacy\.com|tribute archive|dignity memorial|funeral|obits\b|obituari/i;
const isNoiseArticle = (a) =>
  a.relevant === false ||
  NEWS_NOISE_TITLE_RE.test(a.title || "") ||
  NEWS_NOISE_SOURCE_RE.test(a.source || "");

const NewsTab = ({
  newsData,
  newsLoading,
  onRefresh,
  cfg,
  cd,
  opponentIntel,
  onGenerateOpponentIntel,
  opponentIntelLoading,
  section,
}) => {
  const [filter, setFilter] = useState("all");
  const [hideNoise, setHideNoise] = useState(true);
  const articles = newsData?.articles || [];
  const candidateArticles = articles.filter((a) => a.subject === "candidate");
  const opponentArticles = articles.filter((a) => a.subject === "opponent");
  const baseShown =
    filter === "candidate"
      ? candidateArticles
      : filter === "opponent"
        ? opponentArticles
        : articles;
  const shown = hideNoise
    ? baseShown.filter((a) => !isNoiseArticle(a))
    : baseShown;
  const noiseCount = baseShown.filter((a) => isNoiseArticle(a)).length;

  const fmtRelative = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return "< 1h ago";
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // ── Left column: insight panel ──────────────────────────────────────────
  if (section === "insight") {
    return (
      <Box>
        {/* Refresh button for article data */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Box
            component="button"
            onClick={onRefresh}
            disabled={newsLoading}
            title="Refresh news articles"
            sx={{
              background: "none",
              border: "none",
              p: 0,
              lineHeight: 1,
              cursor: newsLoading ? "not-allowed" : "pointer",
              color: C.muted,
              fontSize: 15,
              display: "flex",
              alignItems: "center",
              opacity: newsLoading ? 0.4 : 0.7,
              "&:hover": { opacity: 1, color: C.navy },
              animation: newsLoading ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          >
            ↻
          </Box>
        </Box>

        {/* Loading skeleton */}
        {newsLoading && !newsData && <IntelLoader />}

        {/* Stats row */}
        {newsData && articles.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {[
              { label: "Total", value: articles.length, color: C.navy },
              {
                label: cfg.candidateName?.split(" ").pop() || "Candidate",
                value: candidateArticles.length,
                color: C.blue,
              },
              {
                label: "Opponents",
                value: opponentArticles.length,
                color: C.red,
              },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  flex: 1,
                  minWidth: 70,
                  bgcolor: C.bg,
                  border: `1px solid ${C.border}`,
                  borderTop: `3px solid ${s.color}`,
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: s.color,
                    lineHeight: 1,
                  }}
                >
                  {s.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: C.muted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mt: 0.25,
                  }}
                >
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Urgency breakdown */}
        {newsData &&
          articles.length > 0 &&
          (() => {
            const urgent = articles.filter((a) => (a.urgency || 0) >= 4).length;
            const negative = articles.filter(
              (a) => a.campaign_impact === "negative",
            ).length;
            const positive = articles.filter(
              (a) => a.campaign_impact === "positive",
            ).length;
            if (!urgent && !negative && !positive) return null;
            return (
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    mb: 1,
                  }}
                >
                  Signal breakdown
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {urgent > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: C.red,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: 11, color: C.charcoal }}>
                        <strong>{urgent}</strong> high-urgency{" "}
                        {urgent === 1 ? "story" : "stories"} requiring attention
                      </Typography>
                    </Box>
                  )}
                  {negative > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: IMPACT_COLOR.negative,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: 11, color: C.charcoal }}>
                        <strong>{negative}</strong> negative coverage{" "}
                        {negative === 1 ? "item" : "items"}
                      </Typography>
                    </Box>
                  )}
                  {positive > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: IMPACT_COLOR.positive,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: 11, color: C.charcoal }}>
                        <strong>{positive}</strong> positive coverage{" "}
                        {positive === 1 ? "item" : "items"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })()}

        {/* Opponent coverage mini-list */}
        {opponentArticles.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 800,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                mb: 1,
              }}
            >
              Opponent Coverage · {opponentArticles.length}{" "}
              {opponentArticles.length === 1 ? "article" : "articles"}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {opponentArticles.slice(0, 4).map((a, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                >
                  <Box
                    sx={{
                      mt: 0.5,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: IMPACT_COLOR[a.campaign_impact] || C.muted,
                    }}
                  />
                  <Typography
                    component={a.url ? "a" : "span"}
                    href={a.url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.navy,
                      lineHeight: 1.4,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: a.url ? "underline" : "none",
                      },
                    }}
                  >
                    {a.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <OpponentLens
          cd={cd}
          cfg={cfg}
          liveFEC={null}
          opponentIntel={opponentIntel}
          onGenerate={onGenerateOpponentIntel}
          generating={opponentIntelLoading}
        />
      </Box>
    );
  }

  // ── Right column: article list ───────────────────────────────────────────
  return (
    <Box>
      {/* Filter bar */}
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {[
          ["all", "All"],
          ["candidate", cfg.candidateName?.split(" ").pop() || "Candidate"],
          ["opponent", "Opponents"],
        ].map(([key, label]) => (
          <Box
            key={key}
            component="button"
            onClick={() => setFilter(key)}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              px: 1.5,
              py: 0.5,
              border: `1px solid ${filter === key ? C.navy : C.border}`,
              bgcolor: filter === key ? C.navy : C.white,
              color: filter === key ? "#fff" : C.muted,
              borderRadius: 1,
              cursor: "pointer",
            }}
          >
            {label}{" "}
            {key === "all"
              ? `(${articles.length})`
              : key === "candidate"
                ? `(${candidateArticles.length})`
                : `(${opponentArticles.length})`}
          </Box>
        ))}
        {noiseCount > 0 && (
          <Box
            component="button"
            onClick={() => setHideNoise((p) => !p)}
            sx={{
              ml: "auto",
              fontSize: 10,
              fontWeight: 700,
              px: 1.25,
              py: 0.5,
              border: `1px solid ${hideNoise ? "#f59e0b" : C.border}`,
              bgcolor: hideNoise ? "#fffbeb" : C.white,
              color: hideNoise ? "#b45309" : C.muted,
              borderRadius: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {hideNoise
              ? `⚠ ${noiseCount} hidden`
              : `Show all (${noiseCount} noise)`}
          </Box>
        )}
      </Box>

      {/* Loading skeleton */}
      {newsLoading && !newsData && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Box
              key={i}
              sx={{
                p: 2,
                bgcolor: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: 1.5,
              }}
            >
              <Box
                sx={{
                  bgcolor: C.border,
                  height: 14,
                  width: "70%",
                  borderRadius: 1,
                  mb: 1,
                }}
              />
              <Box
                sx={{
                  bgcolor: C.border,
                  height: 10,
                  width: "40%",
                  borderRadius: 1,
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      {shown.length === 0 && !newsLoading && (
        <Typography
          sx={{ fontSize: 13, color: C.muted, textAlign: "center", py: 4 }}
        >
          No articles found — try refreshing.
        </Typography>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {shown.map((article, i) => {
          const impact = article.campaign_impact;
          const iColor = IMPACT_COLOR[impact] || C.muted;
          const iBg = IMPACT_BG[impact] || C.bg;
          return (
            <Box
              key={i}
              sx={{
                bgcolor: C.white,
                border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${iColor}`,
                borderRadius: 1.5,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography
                  component={article.url ? "a" : "span"}
                  href={article.url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.navy,
                    lineHeight: 1.4,
                    flex: 1,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: article.url ? "underline" : "none",
                    },
                  }}
                >
                  {article.title}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: iBg,
                      color: iColor,
                      fontSize: 10,
                      fontWeight: 700,
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.75,
                      border: `1px solid ${iColor}30`,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {IMPACT_LABEL[impact] || impact || "—"}
                  </Box>
                  {article.urgency > 0 && (
                    <UrgencyDots level={article.urgency} />
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.75,
                  mb: article.impact_summary ? 0.75 : 0,
                }}
              >
                {article.source && (
                  <Typography
                    sx={{ fontSize: 10, color: C.muted, fontWeight: 600 }}
                  >
                    {article.source}
                  </Typography>
                )}
                {article.source && article.published_at && (
                  <Typography sx={{ fontSize: 10, color: C.border }}>
                    ·
                  </Typography>
                )}
                <Typography sx={{ fontSize: 10, color: C.muted }}>
                  {fmtRelative(article.published_at)}
                </Typography>
                {article.subject === "opponent" && (
                  <Box
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      px: 0.75,
                      py: 0.15,
                      bgcolor: "#f1f5f9",
                      color: C.muted,
                      borderRadius: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    opponent
                  </Box>
                )}
                {(article.tags || []).map((tag) => (
                  <Box
                    key={tag}
                    sx={{
                      fontSize: 9,
                      px: 0.75,
                      py: 0.15,
                      bgcolor: C.bg,
                      color: C.muted,
                      borderRadius: 0.5,
                    }}
                  >
                    {tag}
                  </Box>
                ))}
              </Box>

              {article.impact_summary && (
                <Typography
                  sx={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}
                >
                  {article.impact_summary}
                </Typography>
              )}
              {!article.impact_summary && article.snippet && (
                <Typography
                  sx={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}
                >
                  {article.snippet}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─── Social Media Tab ─────────────────────────────────────────────────────────
const SOCIAL_API_URL = PHP_API_URL ? `${PHP_API_URL}/social` : "";
const TOPICS_API_URL = PHP_API_URL ? `${PHP_API_URL}/topics` : "";
const IDENTITY_API_URL = PHP_API_URL ? `${PHP_API_URL}/identity` : "";

// ─── Audience modals (isolated local state — no panel re-renders on keystroke) ─
const CreateAudienceModal = ({
  open,
  onClose,
  onCreate,
  onCollectAll,
  terms,
  C,
}) => {
  const [name, setName] = useState("");
  const [termId, setTermId] = useState("");
  const [creating, setCreating] = useState(false);
  const sourceTerm = termId
    ? terms.find((t) => t.id === parseInt(termId))
    : null;
  const isTimeline = sourceTerm?.monitor_type === "timeline";

  const submit = async () => {
    if (!name.trim() || creating) return;
    setCreating(true);
    if (isTimeline && onCollectAll)
      onCollectAll(sourceTerm.id, sourceTerm.platform || "x");
    await onCreate(name.trim(), sourceTerm ? sourceTerm.query : "");
    setCreating(false);
    setName("");
    setTermId("");
    onClose();
  };

  if (!open) return null;
  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(10,22,40,0.6)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          p: 3,
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
        }}
      >
        <Typography
          sx={{ fontSize: 15, fontWeight: 800, color: C.navy, mb: 0.5 }}
        >
          Create Audience
        </Typography>
        <Typography
          sx={{ fontSize: 11, color: C.muted, lineHeight: 1.7, mb: 1.5 }}
        >
          Build a reusable group of people to poll repeatedly. Members are
          collected from your monitored terms.
        </Typography>
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, color: C.charcoal, mb: 0.4 }}
        >
          Audience Name
        </Typography>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onClose();
          }}
          placeholder="e.g. Post & Courier Readers"
          style={{
            width: "100%",
            fontSize: 12,
            padding: "9px 12px",
            border: `1px solid ${C.border}`,
            borderRadius: "6px",
            color: C.charcoal,
            outline: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
            marginBottom: 12,
          }}
        />
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, color: C.charcoal, mb: 0.4 }}
        >
          Source{" "}
          <Box component="span" sx={{ fontWeight: 400, color: C.muted }}>
            (optional)
          </Box>
        </Typography>
        <select
          value={termId}
          onChange={(e) => setTermId(e.target.value)}
          style={{
            width: "100%",
            fontSize: 12,
            padding: "9px 12px",
            border: `1px solid ${C.border}`,
            borderRadius: "6px",
            color: C.charcoal,
            outline: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
            marginBottom: 12,
            background: "#fff",
          }}
        >
          <option value="">All monitored terms</option>
          {terms.map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.monitor_type === "timeline" ? "📡 " : "🔍 "}
              {t.query} ({t.post_count || 0}{" "}
              {t.monitor_type === "timeline" ? "posts" : "mentions"})
            </option>
          ))}
        </select>
        {isTimeline && (
          <Box
            sx={{
              mb: 1.5,
              px: 1,
              py: 0.75,
              bgcolor: C.green + "10",
              border: `1px solid ${C.green}33`,
              borderRadius: "5px",
            }}
          >
            <Typography
              sx={{
                fontSize: 9.5,
                color: C.green,
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              Reply collection runs automatically. Members are people who
              replied to {sourceTerm.query}'s posts.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 0.8,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              bgcolor: "#fff",
              color: C.muted,
              cursor: "pointer",
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            disabled={creating || !name.trim()}
            onClick={submit}
            sx={{
              flex: 2,
              py: 0.8,
              fontSize: 11,
              fontWeight: 700,
              border: "none",
              borderRadius: "6px",
              bgcolor:
                creating || !name.trim()
                  ? C.border
                  : isTimeline
                    ? C.green
                    : C.navy,
              color: creating || !name.trim() ? C.muted : "#fff",
              cursor: creating || !name.trim() ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
            }}
          >
            {creating && (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  animation: "spin 0.7s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              >
                ↻
              </Box>
            )}
            {creating
              ? "Creating…"
              : isTimeline
                ? "Collect Replies & Create"
                : "Create Audience"}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const PollAudienceModal = ({
  open,
  audience,
  onClose,
  onPoll,
  polling,
  pollProgress,
  C,
  defaultQuestion = "",
}) => {
  const [question, setQuestion] = useState(defaultQuestion);
  const [options, setOptions] = useState("");

  useEffect(() => {
    if (open) {
      setQuestion(defaultQuestion);
      setOptions("");
    }
  }, [open, defaultQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (polling) return;
    await onPoll(audience?.id, question, options);
    onClose();
  };

  if (!open || !audience) return null;
  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(10,22,40,0.6)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          p: 3,
          width: "100%",
          maxWidth: 460,
          boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
        }}
      >
        <Typography
          sx={{ fontSize: 15, fontWeight: 800, color: C.navy, mb: 0.25 }}
        >
          Poll Audience
        </Typography>
        <Typography sx={{ fontSize: 11, color: C.muted, mb: 1.5 }}>
          {audience.name} ·{" "}
          {Number(audience.member_count || 0).toLocaleString()} members
        </Typography>
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, color: C.charcoal, mb: 0.4 }}
        >
          Question
        </Typography>
        <textarea
          autoFocus
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
          }}
          placeholder="e.g. Who do you support for House Speaker?"
          style={{
            width: "100%",
            fontSize: 12,
            padding: "9px 12px",
            border: `1px solid ${C.border}`,
            borderRadius: "6px",
            color: C.charcoal,
            outline: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
            marginBottom: 4,
            resize: "vertical",
            height: 100,
          }}
        />
        <Typography sx={{ fontSize: 9.5, color: C.muted, mb: 1.5 }}>
          Claude uses this to interpret each reply.
        </Typography>
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, color: C.charcoal, mb: 0.4 }}
        >
          Answer options{" "}
          <Box component="span" sx={{ fontWeight: 400, color: C.muted }}>
            (optional)
          </Box>
        </Typography>
        <input
          value={options}
          onChange={(e) => setOptions(e.target.value)}
          placeholder="e.g. Nancy Mace, Kevin McCarthy, Neither"
          style={{
            width: "100%",
            fontSize: 12,
            padding: "9px 12px",
            border: `1px solid ${C.border}`,
            borderRadius: "6px",
            color: C.charcoal,
            outline: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
            marginBottom: 4,
          }}
        />
        <Typography sx={{ fontSize: 9.5, color: C.muted, mb: 1.5 }}>
          Answers are normalized to these exact values.
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 0.8,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              bgcolor: "#fff",
              color: C.muted,
              cursor: "pointer",
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            onClick={submit}
            disabled={polling}
            sx={{
              flex: 2,
              py: 0.8,
              fontSize: 11,
              fontWeight: 700,
              border: "none",
              borderRadius: "6px",
              bgcolor: polling ? C.border : C.green,
              color: polling ? C.muted : "#fff",
              cursor: polling ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
            }}
          >
            {polling && (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  animation: "spin 0.7s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              >
                ↻
              </Box>
            )}
            {polling ? "Polling…" : "Poll Audience"}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Bulk Reply Compose Modal ─────────────────────────────────────────────────
const BulkReplyModal = ({
  open,
  answerFilter,
  targetCount,
  collectionId,
  sampleUsername,
  onClose,
  onSubmit,
  C,
  activeJob,
}) => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const MAX = 280;
  const charsLeft = MAX - message.length;
  const preview = message
    .replace(/\{\{handle\}\}/gi, `@${sampleUsername || "handle"}`)
    .replace(/\{\{username\}\}/gi, sampleUsername || "username");
  const estMinutes = Math.ceil((targetCount * 3.5) / 60);

  useEffect(() => {
    if (open && !activeJob) {
      setMessage("");
      setSending(false);
    }
  }, [open, activeJob]);

  const submit = async () => {
    if (!message.trim() || sending || charsLeft < 0) return;
    setSending(true);
    await onSubmit(collectionId, answerFilter, message.trim());
    setSending(false);
    onClose();
  };

  if (!open) return null;

  const jobPct = activeJob
    ? Math.round(
        ((activeJob.sent + activeJob.failed) / Math.max(activeJob.total, 1)) *
          100,
      )
    : 0;
  const isComplete = activeJob?.status === "complete";

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(10,22,40,0.6)",
        zIndex: 1500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          p: 3,
          width: "100%",
          maxWidth: 480,
          boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
        }}
      >
        <Typography
          sx={{ fontSize: 15, fontWeight: 800, color: C.navy, mb: 0.25 }}
        >
          Reply to Respondents
        </Typography>
        <Typography sx={{ fontSize: 11, color: C.muted, mb: 2 }}>
          Answered{" "}
          <Box component="span" sx={{ fontWeight: 700, color: C.charcoal }}>
            "{answerFilter}"
          </Box>{" "}
          · {Number(targetCount).toLocaleString()} replies · ~{estMinutes} min
          to send
        </Typography>

        {activeJob ? (
          // ── Progress view ──
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: isComplete ? "#16a34a" : C.navy,
                }}
              >
                {isComplete ? "Complete" : "Sending…"}
              </Typography>
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
              >
                {activeJob.sent} / {activeJob.total} sent
              </Typography>
            </Box>
            <Box
              sx={{
                height: 8,
                bgcolor: C.border,
                borderRadius: 4,
                overflow: "hidden",
                mb: 0.75,
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${jobPct}%`,
                  bgcolor: isComplete ? "#16a34a" : "#22c55e",
                  borderRadius: 4,
                  transition: "width 0.4s ease",
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
              <Typography sx={{ fontSize: 10, color: C.muted }}>
                {jobPct}% complete
              </Typography>
              {activeJob.failed > 0 && (
                <Typography sx={{ fontSize: 10, color: "#dc2626" }}>
                  {activeJob.failed} failed
                </Typography>
              )}
              {!isComplete && (
                <Typography sx={{ fontSize: 10, color: C.muted }}>
                  ~
                  {Math.ceil(
                    ((activeJob.total - activeJob.sent - activeJob.failed) *
                      3.5) /
                      60,
                  )}{" "}
                  min remaining
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          // ── Compose view ──
          <>
            <Typography
              sx={{ fontSize: 10, fontWeight: 700, color: C.charcoal, mb: 0.4 }}
            >
              Message
            </Typography>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              autoFocus
              placeholder={`e.g. Thanks for sharing! We're fighting to eliminate property taxes in SC. Follow us for updates.`}
              style={{
                width: "100%",
                height: 100,
                fontSize: 12,
                padding: "9px 12px",
                border: `1px solid ${C.border}`,
                borderRadius: "6px",
                color: C.charcoal,
                outline: "none",
                fontFamily: "inherit",
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
                mt: 0.5,
              }}
            >
              <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                Use{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontFamily: "monospace",
                    bgcolor: "#f1f5f9",
                    px: 0.5,
                    borderRadius: "3px",
                  }}
                >
                  {"{{handle}}"}
                </Box>{" "}
                to insert @username
              </Typography>
              <Typography
                sx={{
                  fontSize: 9.5,
                  color:
                    charsLeft < 0
                      ? "#dc2626"
                      : charsLeft < 30
                        ? "#f59e0b"
                        : C.muted,
                  fontWeight: charsLeft < 30 ? 700 : 400,
                }}
              >
                {charsLeft} left
              </Typography>
            </Box>
            {message.trim() && (
              <Box
                sx={{
                  bgcolor: "#f8fafc",
                  border: `1px solid ${C.border}`,
                  borderRadius: "6px",
                  p: 1.25,
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: C.muted,
                    mb: 0.4,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Preview
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: C.charcoal,
                    lineHeight: 1.5,
                    wordBreak: "break-word",
                  }}
                >
                  {preview}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                bgcolor: "#fffbeb",
                border: "1px solid #fbbf24",
                borderRadius: "6px",
                p: 1,
                mb: 1.75,
              }}
            >
              <Typography
                sx={{ fontSize: 9.5, color: "#92400e", lineHeight: 1.5 }}
              >
                Replies post publicly to each person's tweet. Sent at ~1
                reply/3.5s to stay within rate limits.
                {targetCount > 300 &&
                  ` Large batch — will run across multiple Lambda invocations (~${Math.ceil(targetCount / 50)} rounds).`}
              </Typography>
            </Box>
          </>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 0.8,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              bgcolor: "#fff",
              color: C.muted,
              cursor: "pointer",
            }}
          >
            {activeJob ? "Close" : "Cancel"}
          </Box>
          {!activeJob && (
            <Box
              component="button"
              onClick={submit}
              disabled={sending || !message.trim() || charsLeft < 0}
              sx={{
                flex: 2,
                py: 0.8,
                fontSize: 11,
                fontWeight: 700,
                border: "none",
                borderRadius: "6px",
                bgcolor:
                  sending || !message.trim() || charsLeft < 0
                    ? C.border
                    : "#0a2240",
                color:
                  sending || !message.trim() || charsLeft < 0
                    ? C.muted
                    : "#fff",
                cursor:
                  sending || !message.trim() || charsLeft < 0
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
              }}
            >
              {sending && (
                <Box
                  component="span"
                  sx={{
                    animation: "spin 0.7s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                    display: "inline-block",
                  }}
                >
                  ↻
                </Box>
              )}
              {sending
                ? "Queuing…"
                : `Send ${Number(targetCount).toLocaleString()} Replies`}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Audience Intelligence Panel (left column, audiences mode) ────────────────
const AudienceIntelPanel = ({
  audiences,
  audiencesLoading,
  selectedAudienceId,
  onSelectAudience,
  collections,
  selectedCollectionId,
  onSelectCollection,
  polling,
  pollProgress,
  onCreateAudience,
  onRefreshAudience,
  onPollAudience,
  error,
  audiencePreFill,
  onClearAudiencePreFill,
  terms = [],
  onCollectAll,
  collectingAllTermId,
  collectAllProgress,
  onPollRequest,
  onDeleteCollection,
  onDeleteAudience,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (!audiencePreFill) return;
    setShowCreateModal(true);
    onClearAudiencePreFill?.();
  }, [audiencePreFill]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      {error && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            bgcolor: "#fef2f2",
            border: `1px solid #fca5a5`,
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: 10, color: "#b91c1c" }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: C.charcoal,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Audiences
        </Typography>
      </Box>

      {/* CTA card — matches Monitor style */}
      <Box sx={{ mb: 1.5 }}>
        <Box
          component="button"
          onClick={() => setShowCreateModal(true)}
          sx={{
            display: "block",
            width: "100%",
            py: 1.25,
            px: 1.25,
            border: `1px dashed ${C.navy + "66"}`,
            borderRadius: "8px",
            bgcolor: C.white,
            cursor: "pointer",
            textAlign: "left",
            "&:hover": { borderColor: C.navy, bgcolor: C.navy + "05" },
          }}
        >
          <Typography
            sx={{ fontSize: 10.5, fontWeight: 700, color: C.navy, mb: 0.2 }}
          >
            + New Audience
          </Typography>
          <Typography sx={{ fontSize: 9, color: C.muted, lineHeight: 1.4 }}>
            Build a group to poll from your monitored posts
          </Typography>
        </Box>
      </Box>

      {audiencesLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                height: 40,
                bgcolor: C.border,
                borderRadius: 1,
                opacity: 0.3 + i * 0.15,
              }}
            />
          ))}
        </Box>
      ) : audiences.length === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${C.border}`,
            borderRadius: "8px",
            p: 2.5,
            textAlign: "center",
            bgcolor: C.bg,
          }}
        >
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, color: C.navy, mb: 0.75 }}
          >
            No audiences yet
          </Typography>
          <Typography
            sx={{ fontSize: 11, color: C.muted, lineHeight: 1.65, mb: 2 }}
          >
            Create an audience from your collected replies. Audiences let you
            run multiple questions against the same group of real people.
          </Typography>
          <Box
            component="button"
            onClick={() => setShowCreateModal(true)}
            sx={{
              bgcolor: C.navy,
              color: "#fff",
              textTransform: "none",
              fontSize: 11,
              fontWeight: 700,
              px: 2.5,
              py: 0.75,
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
            }}
          >
            Create First Audience
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
          {audiences.map((a) => {
            const isSelected = a.id === selectedAudienceId;
            const isRefreshing = refreshingId === a.id;
            return (
              <Box
                key={a.id}
                sx={{
                  border: `1px solid ${isSelected ? C.navy + "44" : C.border}`,
                  bgcolor: isSelected ? C.navy + "10" : C.white,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: isSelected ? C.navy + "44" : C.muted,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    p: "10px",
                  }}
                >
                  {/* Initial avatar */}
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: isSelected ? C.navy : "#f0f4f8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 800,
                        color: isSelected ? "#fff" : C.charcoal,
                        lineHeight: 1,
                      }}
                    >
                      {a.name.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                  {/* Name — clickable */}
                  <Box
                    component="button"
                    onClick={() => onSelectAudience(isSelected ? null : a.id)}
                    sx={{
                      flex: 1,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      minWidth: 0,
                      p: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: isSelected ? 700 : 600,
                        color: isSelected ? C.navy : C.charcoal,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {a.name}
                    </Typography>
                  </Box>
                  {/* Badges right-aligned */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        px: 1,
                        py: 0.3,
                        borderRadius: "3px",
                        bgcolor: C.navy + "18",
                        border: `1px solid ${C.navy}44`,
                        color: C.navy,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {Number(a.member_count || 0).toLocaleString()} members
                    </Box>
                    {a.collection_count > 0 && (
                      <Box
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          px: 1,
                          py: 0.3,
                          borderRadius: "3px",
                          bgcolor: "#64748b18",
                          border: "1px solid #64748b44",
                          color: "#64748b",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {a.collection_count} polls
                      </Box>
                    )}
                  </Box>
                  {/* Actions */}
                  <Box sx={{ display: "flex", gap: 0.35, flexShrink: 0 }}>
                    {confirmDeleteId === a.id ? (
                      <>
                        <Box
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteAudience?.(a.id);
                            setConfirmDeleteId(null);
                          }}
                          sx={{
                            px: 0.75,
                            height: 26,
                            fontSize: 9,
                            fontWeight: 700,
                            bgcolor: "#dc2626",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Delete
                        </Box>
                        <Box
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(null);
                          }}
                          sx={{
                            px: 0.75,
                            height: 26,
                            fontSize: 9,
                            fontWeight: 600,
                            bgcolor: "transparent",
                            color: C.muted,
                            border: `1px solid ${C.border}`,
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectAudience(a.id);
                            onPollRequest();
                          }}
                          title="Poll this audience"
                          sx={{
                            width: 26,
                            height: 26,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "transparent",
                            border: `1px solid ${C.border}`,
                            borderRadius: "5px",
                            cursor: "pointer",
                            color: C.muted,
                            fontSize: 9,
                            fontWeight: 700,
                            "&:hover": {
                              bgcolor: C.navy + "08",
                              borderColor: C.navy,
                              color: C.navy,
                            },
                          }}
                        >
                          Poll
                        </Box>
                        <Box
                          component="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            setRefreshingId(a.id);
                            await onRefreshAudience(a.id);
                            setRefreshingId(null);
                          }}
                          disabled={isRefreshing}
                          title="Refresh member list"
                          sx={{
                            width: 26,
                            height: 26,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "transparent",
                            border: `1px solid ${C.border}`,
                            borderRadius: "5px",
                            cursor: isRefreshing ? "not-allowed" : "pointer",
                            color: isRefreshing ? C.muted : C.navy,
                            fontSize: 13,
                            "&:hover": {
                              bgcolor: C.navy + "10",
                              borderColor: C.navy,
                            },
                          }}
                        >
                          {isRefreshing ? (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                animation: "spin 0.7s linear infinite",
                                "@keyframes spin": {
                                  "0%": { transform: "rotate(0deg)" },
                                  "100%": { transform: "rotate(360deg)" },
                                },
                              }}
                            >
                              ↻
                            </Box>
                          ) : (
                            "↻"
                          )}
                        </Box>
                        <Box
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(a.id);
                          }}
                          title="Delete audience"
                          sx={{
                            width: 26,
                            height: 26,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "transparent",
                            border: `1px solid ${C.border}`,
                            borderRadius: "5px",
                            cursor: "pointer",
                            color: C.muted,
                            fontSize: 14,
                            "&:hover": {
                              borderColor: "#dc2626",
                              color: "#dc2626",
                            },
                          }}
                        >
                          ×
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <CreateAudienceModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={onCreateAudience}
        onCollectAll={onCollectAll}
        terms={terms}
        C={C}
      />
    </Box>
  );
};

const fmtRelTime = (iso) => {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
};

const MENTION_TYPE_LABEL = { replied_to: "Reply", quoted: "Quote" };
const MENTION_TYPE_COLOR = { replied_to: C.blue, quoted: C.orange };

const parseSearchTerms = (query) => {
  if (!query) return [];
  const terms = [];
  const quoted = query.match(/"([^"]+)"/g) || [];
  quoted.forEach((q) => terms.push(q.replace(/"/g, "")));
  const rest = query
    .replace(/"[^"]+"/g, "")
    .replace(/\bOR\b/g, "")
    .trim();
  rest.split(/\s+/).forEach((w) => {
    const t = w.replace(/^@/, "").trim();
    if (t.length > 1) terms.push(t);
  });
  return [...new Set(terms.filter(Boolean))];
};

const HighlightSearchTerms = ({ text, searchQuery }) => {
  if (!text) return null;
  const terms = parseSearchTerms(searchQuery);
  if (!terms.length) return <>{text}</>;
  const pattern = new RegExp(
    `(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "gi",
  );
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, i) =>
        pattern.test(part) ? (
          <Box
            key={i}
            component="span"
            sx={{
              color: "#ff0a4b",
              fontWeight: 900,
              textDecoration: "underline",
            }}
          >
            {part}
          </Box>
        ) : (
          part
        ),
      )}
    </>
  );
};

const PAGE_SIZE = 5;

// Isolated modal components — local state so only the modal re-renders per keystroke
const AddTermModal = ({ open, type, onClose, onAdd, C }) => {
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const isMention = type === "mention";
  const accent = isMention ? C.navy : C.green;
  const examples = isMention
    ? ["@NancyMace", '"SC governor"', "DOGE SC"]
    : ["@postandcourier", "@fitsnews", "@SCGOP"];

  const submit = async () => {
    if (!draft.trim() || adding) return;
    setAdding(true);
    await onAdd(draft.trim(), type);
    setDraft("");
    setAdding(false);
    onClose();
  };

  if (!open) return null;
  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(10,22,40,0.6)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          p: 3,
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
        }}
      >
        <Typography
          sx={{ fontSize: 15, fontWeight: 800, color: C.navy, mb: 0.5 }}
        >
          {isMention
            ? "Monitor a Mention or Term"
            : "Monitor an Account Timeline"}
        </Typography>
        <Typography
          sx={{ fontSize: 11, color: C.muted, lineHeight: 1.7, mb: 1.5 }}
        >
          {isMention
            ? "Searches X in real-time for posts that include a keyword, phrase, or @handle. Use this to find everyone publicly talking about a candidate, issue, or news event."
            : "Pulls an account's own posts directly — ideal for media outlets, opponents, or influencers. Find high-engagement threads, then build an audience from the people who replied to them."}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75, mb: 1.5, flexWrap: "wrap" }}>
          {examples.map((ex) => (
            <Box
              key={ex}
              component="button"
              onClick={() => setDraft(ex)}
              sx={{
                fontSize: 10,
                fontFamily: "monospace",
                px: 0.75,
                py: 0.35,
                bgcolor: accent + "12",
                border: `1px solid ${accent}44`,
                borderRadius: "4px",
                cursor: "pointer",
                color: accent,
                "&:hover": { bgcolor: accent + "20" },
              }}
            >
              {ex}
            </Box>
          ))}
        </Box>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onClose();
          }}
          placeholder={isMention ? `@handle or "exact phrase"` : "@account"}
          style={{
            width: "100%",
            fontSize: 12,
            padding: "9px 12px",
            border: `1px solid ${accent}66`,
            borderRadius: "6px",
            color: C.charcoal,
            outline: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
            marginBottom: 12,
          }}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 0.8,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              bgcolor: "#fff",
              color: C.muted,
              cursor: "pointer",
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            disabled={adding || !draft.trim()}
            onClick={submit}
            sx={{
              flex: 2,
              py: 0.8,
              fontSize: 11,
              fontWeight: 700,
              border: "none",
              borderRadius: "6px",
              bgcolor: adding || !draft.trim() ? C.border : accent,
              color: adding || !draft.trim() ? C.muted : "#fff",
              cursor: adding || !draft.trim() ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
            }}
          >
            {adding && (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  animation: "spin 0.7s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              >
                ↻
              </Box>
            )}
            {adding ? "Adding…" : "Start Monitoring"}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ReplyModal = ({ open, target, onClose, onPost, C }) => {
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [err, setErr] = useState(null);
  const isFB = target?.platform === "facebook";
  const charLimit = isFB ? 8000 : 280;
  const submit = async () => {
    if (!draft.trim() || posting) return;
    setPosting(true);
    setErr(null);
    try {
      await onPost(
        target.reply_tweet_id,
        draft.trim(),
        target.author_id,
        target.author_username,
        target.platform || "x",
      );
      setPosted(true);
      setTimeout(() => {
        setPosted(false);
        setDraft("");
        onClose();
      }, 1200);
    } catch (e) {
      setErr(e.message);
    }
    setPosting(false);
  };
  if (!open) return null;
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(10,34,64,0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "10px",
          p: 3,
          width: 420,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <Typography
          sx={{ fontSize: 13, fontWeight: 800, color: C.navy, mb: 0.5 }}
        >
          {isFB ? "Reply on Facebook" : "Reply on X"}
        </Typography>
        {target?.author_name && (
          <Typography sx={{ fontSize: 10.5, color: C.muted, mb: 1.5 }}>
            Replying to{" "}
            {isFB
              ? target.author_name
              : `@${target.author_username || target.author_name}`}
          </Typography>
        )}
        {target?.reply_text && (
          <Box
            sx={{
              bgcolor: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              p: 1.25,
              mb: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: 10.5, color: C.charcoal, lineHeight: 1.5 }}
            >
              "{(target.reply_text || "").slice(0, 200)}
              {(target.reply_text || "").length > 200 ? "…" : ""}"
            </Typography>
          </Box>
        )}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          placeholder="Write your reply…"
          style={{
            width: "100%",
            fontSize: 12,
            padding: "9px 12px",
            border: `1px solid ${C.border}`,
            borderRadius: "6px",
            color: C.charcoal,
            outline: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
            resize: "vertical",
            marginBottom: 4,
          }}
        />
        <Typography
          sx={{ fontSize: 9.5, color: C.muted, mb: 1.5, textAlign: "right" }}
        >
          {draft.length}
          {!isFB && "/280"}
        </Typography>
        {err && (
          <Typography sx={{ fontSize: 10.5, color: "#c0392b", mb: 1 }}>
            {err}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 0.8,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              bgcolor: "#fff",
              color: C.muted,
              cursor: "pointer",
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            disabled={
              posting || !draft.trim() || draft.length > charLimit || posted
            }
            onClick={submit}
            sx={{
              flex: 2,
              py: 0.8,
              fontSize: 11,
              fontWeight: 700,
              border: "none",
              borderRadius: "6px",
              bgcolor: posted
                ? "#16a34a"
                : posting || !draft.trim()
                  ? C.border
                  : C.navy,
              color: posted || (!posting && draft.trim()) ? "#fff" : C.muted,
              cursor:
                posting || !draft.trim() || posted ? "not-allowed" : "pointer",
              transition: "background 0.2s",
            }}
          >
            {posted ? "Sent!" : posting ? "Posting…" : "Post Reply"}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const CollectReplyAllModal = ({
  open,
  totalReplies,
  sourceTweetId,
  platform = "x",
  onClose,
  onSubmit,
  onGenerateVariations,
  activeJob,
  C,
}) => {
  const [template, setTemplate] = useState("");
  const [variations, setVariations] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const MAX = 240;
  const estMinutes = Math.ceil((totalReplies * 3.5) / 60);
  const jobPct = activeJob
    ? Math.round(
        ((activeJob.sent + activeJob.failed) / Math.max(activeJob.total, 1)) *
          100,
      )
    : 0;
  const isComplete = activeJob?.status === "complete";

  useEffect(() => {
    if (open && !activeJob) {
      setTemplate("");
      setVariations([]);
      setSending(false);
    }
  }, [open, activeJob]);

  const handleGenerate = async () => {
    if (!template.trim() || generating) return;
    setGenerating(true);
    const vars = await onGenerateVariations?.(template.trim(), 10);
    setVariations(vars || [template]);
    setGenerating(false);
  };

  const handleSend = async () => {
    if (!template.trim() || sending) return;
    setSending(true);
    await onSubmit(null, null, template.trim(), {
      sourceTweetId,
      variations: variations.length > 1 ? variations : null,
    });
    setSending(false);
  };

  if (!open) return null;
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(10,34,64,0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "10px",
          p: 3,
          width: 480,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <Typography
          sx={{ fontSize: 13, fontWeight: 800, color: C.navy, mb: 0.25 }}
        >
          Reply to all {totalReplies?.toLocaleString()} repliers
        </Typography>
        <Typography sx={{ fontSize: 10, color: C.muted, mb: 2 }}>
          Estimated time: ~{estMinutes} min · Replies are varied automatically
          so they feel natural
        </Typography>

        {activeJob ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  height: 8,
                  bgcolor: C.border,
                  borderRadius: 4,
                  overflow: "hidden",
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${jobPct}%`,
                    bgcolor: isComplete
                      ? "#16a34a"
                      : platform === "facebook"
                        ? "#1877f2"
                        : C.navy,
                    borderRadius: 4,
                    transition: "width 0.4s",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 10.5, color: C.charcoal }}>
                  {isComplete
                    ? "Complete!"
                    : `${activeJob.sent} of ${activeJob.total} sent`}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: C.muted }}>
                  {jobPct}%
                </Typography>
              </Box>
              {activeJob.failed > 0 && (
                <Typography sx={{ fontSize: 10, color: "#c0392b", mt: 0.5 }}>
                  {activeJob.failed} failed
                </Typography>
              )}
            </Box>
            <Box
              component="button"
              onClick={onClose}
              sx={{
                width: "100%",
                py: 0.9,
                fontSize: 11,
                fontWeight: 700,
                border: `1px solid ${C.border}`,
                borderRadius: "6px",
                bgcolor: "#fff",
                color: C.navy,
                cursor: "pointer",
              }}
            >
              Close
            </Box>
          </>
        ) : (
          <>
            <textarea
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={4}
              placeholder="Write your reply template… use {{handle}} to personalize"
              style={{
                width: "100%",
                fontSize: 12,
                padding: "9px 12px",
                border: `1px solid ${C.border}`,
                borderRadius: "6px",
                color: C.charcoal,
                outline: "none",
                fontFamily: "inherit",
                boxSizing: "border-box",
                resize: "vertical",
                marginBottom: 4,
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 9.5,
                  color: template.length > MAX ? "#c0392b" : C.muted,
                }}
              >
                {template.length}/{MAX}
              </Typography>
              <Box
                component="button"
                onClick={handleGenerate}
                disabled={!template.trim() || generating}
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.5,
                  border: `1px solid ${C.navy}`,
                  borderRadius: "6px",
                  bgcolor: "#fff",
                  color: C.navy,
                  cursor:
                    !template.trim() || generating ? "not-allowed" : "pointer",
                  opacity: !template.trim() || generating ? 0.5 : 1,
                }}
              >
                {generating ? "Generating…" : "Generate Variations"}
              </Box>
            </Box>

            {variations.length > 1 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: C.bg,
                  borderRadius: "6px",
                  border: `1px solid ${C.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: C.muted,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {variations.length} variations generated — preview
                </Typography>
                {variations.slice(0, 3).map((v, i) => (
                  <Box
                    key={i}
                    sx={{
                      mb: 0.75,
                      p: 1,
                      bgcolor: "#fff",
                      borderRadius: "4px",
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: C.charcoal,
                        lineHeight: 1.5,
                      }}
                    >
                      {v}
                    </Typography>
                  </Box>
                ))}
                {variations.length > 3 && (
                  <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                    …and {variations.length - 3} more
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                component="button"
                onClick={onClose}
                sx={{
                  flex: 1,
                  py: 0.9,
                  fontSize: 11,
                  fontWeight: 600,
                  border: `1px solid ${C.border}`,
                  borderRadius: "6px",
                  bgcolor: "#fff",
                  color: C.muted,
                  cursor: "pointer",
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                disabled={sending || !template.trim() || template.length > MAX}
                onClick={handleSend}
                sx={{
                  flex: 2,
                  py: 0.9,
                  fontSize: 11,
                  fontWeight: 700,
                  border: "none",
                  borderRadius: "6px",
                  bgcolor:
                    sending || !template.trim()
                      ? C.border
                      : platform === "facebook"
                        ? "#1877f2"
                        : "#0a2240",
                  color: "#fff",
                  cursor:
                    sending || !template.trim() ? "not-allowed" : "pointer",
                }}
              >
                {sending
                  ? "Starting…"
                  : `Send to ${totalReplies?.toLocaleString()} ${platform === "facebook" ? "commenters" : "repliers"}`}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

const DonorLinkModal = ({
  open,
  target,
  existingLink,
  campaignId,
  onClose,
  onLink,
  C,
}) => {
  const { authFetch } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const WINRED_API_URL = PHP_API_URL ? `${PHP_API_URL}/winred` : "";
  const search = async () => {
    if (!query.trim() || !WINRED_API_URL || !campaignId) return;
    setSearching(true);
    setResults([]);
    try {
      const res = await authFetch(
        `${WINRED_API_URL}?type=donors&campaign_id=${campaignId}&q=${encodeURIComponent(query.trim())}&limit=20`,
      );
      const data = await res.json();
      setResults(data.donors || []);
    } catch {
      setResults([]);
    }
    setSearching(false);
  };
  const link = async (donor) => {
    if (saving) return;
    setSaving(true);
    try {
      await onLink(
        target.author_id,
        target.author_username,
        target.author_name,
        donor.email,
        donor.name || donor.full_name,
        target.collection_id,
      );
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 1000);
    } catch {
      /* ignore */
    }
    setSaving(false);
  };
  if (!open) return null;
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(10,34,64,0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "10px",
          p: 3,
          width: 440,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{ fontSize: 13, fontWeight: 800, color: C.navy, mb: 0.5 }}
        >
          Link to Donor Record
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: C.muted, mb: 1.5 }}>
          @{target?.author_username || target?.author_name} → match to a WinRed
          donor by name or email
        </Typography>
        {existingLink && (
          <Box
            sx={{
              bgcolor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "6px",
              px: 1.5,
              py: 1,
              mb: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: 10.5, fontWeight: 700, color: "#16a34a" }}
            >
              Currently linked:{" "}
              {existingLink.donor_name || existingLink.donor_email}
            </Typography>
            <Typography sx={{ fontSize: 10, color: C.muted }}>
              {existingLink.donor_email}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") search();
            }}
            placeholder="Search by name or email…"
            style={{
              flex: 1,
              fontSize: 12,
              padding: "8px 12px",
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              color: C.charcoal,
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
          <Box
            component="button"
            onClick={search}
            disabled={searching || !query.trim()}
            sx={{
              px: 1.5,
              py: 0.75,
              fontSize: 11,
              fontWeight: 700,
              border: "none",
              borderRadius: "6px",
              bgcolor: searching || !query.trim() ? C.border : C.navy,
              color: searching || !query.trim() ? C.muted : "#fff",
              cursor: searching || !query.trim() ? "not-allowed" : "pointer",
            }}
          >
            {searching ? "…" : "Search"}
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {results.length === 0 && !searching && query && (
            <Typography
              sx={{
                fontSize: 10.5,
                color: C.muted,
                textAlign: "center",
                py: 2,
              }}
            >
              No donors found
            </Typography>
          )}
          {results.map((d, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 0.75,
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
                >
                  {d.name || d.full_name || "Unknown"}
                </Typography>
                <Typography sx={{ fontSize: 10, color: C.muted }}>
                  {d.email}
                </Typography>
              </Box>
              <Box
                component="button"
                onClick={() => link(d)}
                disabled={saving || saved}
                sx={{
                  px: 1.25,
                  py: 0.4,
                  fontSize: 10.5,
                  fontWeight: 700,
                  border: "none",
                  borderRadius: "5px",
                  bgcolor: saved ? "#16a34a" : C.navy,
                  color: "#fff",
                  cursor: saving || saved ? "not-allowed" : "pointer",
                }}
              >
                {saved ? "Linked!" : "Link"}
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ mt: 1.5, textAlign: "right" }}>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              px: 2,
              py: 0.75,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${C.border}`,
              borderRadius: "6px",
              bgcolor: "#fff",
              color: C.muted,
              cursor: "pointer",
            }}
          >
            Close
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ExpandableReply = ({ text, max = 120 }) => {
  const [expanded, setExpanded] = useState(false);
  const truncated = (text || "").length > max;
  return (
    <Typography
      onClick={() => truncated && setExpanded((e) => !e)}
      sx={{
        fontSize: 10,
        color: C.muted,
        lineHeight: 1.4,
        wordBreak: "break-word",
        cursor: truncated ? "pointer" : "default",
      }}
    >
      {expanded || !truncated ? text : (text || "").slice(0, max) + "…"}
    </Typography>
  );
};

const InlineTermEdit = ({ term, accent, onSave, onCancel }) => {
  const [draft, setDraft] = useState(term.query);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!draft.trim() || saving) return;
    setSaving(true);
    await onSave(draft.trim());
    setSaving(false);
  };
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
      }}
    >
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
          if (e.key === "Enter") save();
        }}
        style={{
          flex: 1,
          height: 26,
          fontSize: 10.5,
          fontWeight: 600,
          border: `1px solid ${accent}66`,
          borderRadius: 4,
          padding: "0 8px",
          outline: "none",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      />
      <Box
        component="button"
        onClick={save}
        disabled={saving || !draft.trim()}
        sx={{
          height: 26,
          px: 1.25,
          fontSize: 11,
          fontWeight: 700,
          bgcolor: saving || !draft.trim() ? "#e2e8f0" : accent,
          color: saving || !draft.trim() ? "#94a3b8" : "#fff",
          border: "none",
          borderRadius: "5px",
          cursor: saving || !draft.trim() ? "not-allowed" : "pointer",
          flexShrink: 0,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {saving ? (
          <>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                animation: "spin 0.7s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            >
              ↻
            </Box>{" "}
            Saving…
          </>
        ) : (
          "Save"
        )}
      </Box>
      <Box
        component="button"
        onClick={onCancel}
        disabled={saving}
        sx={{
          height: 26,
          px: 1.25,
          fontSize: 11,
          fontWeight: 600,
          bgcolor: "transparent",
          color: "#64748b",
          border: "1px solid #e2e8f0",
          borderRadius: "5px",
          cursor: saving ? "not-allowed" : "pointer",
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        Cancel
      </Box>
    </Box>
  );
};

// ── Audience Discovery — behavioral lead-segment builder (self-contained) ──────────────
// NL query → X full-archive search (posts + thread comments) → enrich + voter-match → tiered
// segment. Manages its own state + PubNub progress. Backend: /social/segments/* (social_segments.php).
const SEG_TIER_META = {
  voter_matched: { label: "Voter", color: "#16a34a" },
  consented: { label: "Consented", color: "#2563eb" },
  local: { label: "Local", color: "#64748b" },
};
// prettier-ignore
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];
const SEG_SENT_COLOR = {
  positive: "#16a34a",
  negative: "#dc2626",
  neutral: "#64748b",
  mixed: "#d97706",
};
const segStageLabel = (s) =>
  ({
    starting: "Starting…",
    crawling: "Crawling posts…",
    "collecting-comments": "Collecting thread comments…",
    "saving-contacts": "Saving contacts…",
    scoring: "Scoring relevance…",
    enriching: "Enriching + voter-matching…",
    "collecting-likers": "Collecting comment likers…",
    "enriching-more": "Enriching more…",
    retiering: "Re-tiering…",
  })[s] || "Working…";

// Isolated composer so keystrokes only re-render this tiny input — NOT the heavy member table.
// X is query-seeded (NL → full-archive search); Facebook/Instagram are URL-seeded — the campaign pastes
// seed post URLs and we comment-mine them (no open topic-search exists). See SOCIAL_SOURCES_SPEC.md.
function SegmentComposer({ onSubmit, creating }) {
  const [nl, setNl] = useState("");
  const [platform, setPlatform] = useState("x");
  const [urls, setUrls] = useState("");
  const isUrlSeeded = platform === "facebook" || platform === "instagram";
  const urlList = urls
    .split("\n")
    .map((u) => u.trim())
    .filter(Boolean);
  const submit = async () => {
    const t = nl.trim();
    if (!t || (isUrlSeeded && !urlList.length)) return;
    const ok = await onSubmit(t, platform, urlList);
    if (ok) {
      setNl("");
      setUrls("");
    }
  };
  const disabled = creating || !nl.trim() || (isUrlSeeded && !urlList.length);
  const labelSx = {
    fontSize: 10,
    fontWeight: 700,
    color: C.charcoal,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    mb: 0.75,
  };
  const taSx = {
    width: "100%",
    fontSize: 12,
    fontFamily: "inherit",
    p: 1,
    border: `1px solid ${C.border}`,
    borderRadius: 1,
    resize: "vertical",
    boxSizing: "border-box",
  };
  const PLATS = [
    { k: "x", label: "𝕏 X" },
    { k: "facebook", label: "📘 Facebook" },
    { k: "instagram", label: "📸 Instagram" },
  ];
  return (
    <>
      {/* Source platform — X searches the whole platform; FB/IG mine the comments on posts you paste. */}
      <Box sx={{ display: "flex", gap: 0.75, mb: 1 }}>
        {PLATS.map((p) => (
          <Box
            key={p.k}
            component="button"
            onClick={() => setPlatform(p.k)}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              px: 1,
              py: 0.5,
              borderRadius: "6px",
              cursor: "pointer",
              border: `1px solid ${platform === p.k ? C.navy : C.border}`,
              bgcolor: platform === p.k ? C.navy : C.white,
              color: platform === p.k ? "#fff" : C.charcoal,
            }}
          >
            {p.label}
          </Box>
        ))}
      </Box>
      <Typography sx={labelSx}>
        {isUrlSeeded ? "Describe the topic to score for" : "Describe your audience"}
      </Typography>
      <Box
        component="textarea"
        value={nl}
        onChange={(e) => setNl(e.target.value)}
        rows={isUrlSeeded ? 2 : 3}
        placeholder={
          isUrlSeeded
            ? "e.g. housing affordability — used to keep only the on-topic commenters"
            : "e.g. Every Charleston voter discussing housing affordability in the last 90 days"
        }
        sx={taSx}
      />
      {isUrlSeeded && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={labelSx}>Seed post URLs — one per line</Typography>
          <Box
            component="textarea"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            rows={3}
            placeholder={
              platform === "facebook"
                ? "https://www.facebook.com/PageName/posts/123…"
                : "https://www.instagram.com/p/AbC123…/"
            }
            sx={taSx}
          />
          <Typography sx={{ fontSize: 9.5, color: C.muted, mt: 0.4 }}>
            Every commenter on these public posts is mined, scored for topic relevance, and added as a lead.
          </Typography>
        </Box>
      )}
      <Box sx={{ mt: 0.75 }}>
        <Box
          component="button"
          onClick={submit}
          disabled={disabled}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            px: 1.5,
            py: 0.6,
            borderRadius: "6px",
            cursor: disabled ? "not-allowed" : "pointer",
            bgcolor: disabled ? C.border : C.navy,
            color: disabled ? C.muted : "#fff",
            border: "none",
          }}
        >
          {creating ? "Building…" : "＋ Build segment"}
        </Box>
      </Box>
    </>
  );
}

// Issue Landscape — the proprietary topic-intelligence view: which issues are loudest (engagement-
// weighted), where they concentrate (zip heatmap), across the campaign. Reads /topics/*.
function TopicLandscape({ authFetch }) {
  const [open, setOpen] = useState(false);
  const [stateFilter, setStateFilter] = useState("SC");
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState(null);
  const [heatmap, setHeatmap] = useState(null);

  const load = useCallback(async () => {
    if (!TOPICS_API_URL) return;
    setLoading(true);
    try {
      const qs = stateFilter ? `?state=${stateFilter}&days=90` : "?days=90";
      const r = await authFetch(`${TOPICS_API_URL}/ranking${qs}`);
      const d = await r.json();
      setRanking(d.ranking || []);
    } catch (e) {
      setRanking([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch, stateFilter]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const openHeatmap = async (slug) => {
    if (topic === slug) {
      setTopic(null);
      setHeatmap(null);
      return;
    }
    setTopic(slug);
    setHeatmap(null);
    try {
      const qs = `?topic=${slug}&by=zip${stateFilter ? `&state=${stateFilter}` : ""}`;
      const r = await authFetch(`${TOPICS_API_URL}/heatmap${qs}`);
      const d = await r.json();
      setHeatmap(d.heatmap || []);
    } catch (e) {
      setHeatmap([]);
    }
  };

  if (!TOPICS_API_URL) return null;
  const maxW =
    ranking && ranking.length
      ? Math.max(...ranking.map((x) => +x.weight || 0), 1)
      : 1;

  return (
    <Box
      sx={{
        mb: 2,
        border: `1px solid ${C.border}`,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        component="button"
        onClick={() => setOpen((o) => !o)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.25,
          py: 0.9,
          bgcolor: C.navy + "06",
          border: "none",
          cursor: "pointer",
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            color: C.charcoal,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Issue Landscape {stateFilter ? `· ${stateFilter}` : ""} · last 90 days
        </Typography>
        <Typography sx={{ fontSize: 12, color: C.muted }}>
          {open ? "▲" : "▼"}
        </Typography>
      </Box>
      {open && (
        <Box sx={{ p: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <Typography sx={{ fontSize: 9, color: C.muted }}>State</Typography>
            <Box
              component="input"
              value={stateFilter}
              onChange={(e) =>
                setStateFilter(e.target.value.toUpperCase().slice(0, 2))
              }
              sx={{
                fontSize: 10,
                width: 44,
                px: 0.5,
                py: 0.2,
                border: `1px solid ${C.border}`,
                borderRadius: "4px",
                textAlign: "center",
              }}
            />
            <Typography sx={{ fontSize: 9, color: C.muted }}>
              engagement-weighted mentions of each issue
            </Typography>
          </Box>
          {loading && <IntelLoader size={30} bars={[70, 90, 55]} barHeight={6} />}
          {!loading && ranking && ranking.length === 0 && (
            <Typography sx={{ fontSize: 10, color: C.muted }}>
              No topic signals yet — run a segment.
            </Typography>
          )}
          {!loading &&
            ranking &&
            ranking.map((t) => (
              <Box key={t.slug}>
                <Box
                  component="button"
                  onClick={() => openHeatmap(t.slug)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.4,
                    border: "none",
                    bgcolor: "transparent",
                    cursor: "pointer",
                    p: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: C.charcoal,
                      width: 150,
                      flexShrink: 0,
                      textAlign: "right",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.label}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: 14,
                      bgcolor: C.navy + "0a",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(((+t.weight || 0) / maxW) * 100, 1.5)}%`,
                        height: "100%",
                        bgcolor: topic === t.slug ? "#0369a1" : C.navy,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: C.charcoal,
                      width: 60,
                      flexShrink: 0,
                      textAlign: "right",
                    }}
                  >
                    {(+t.weight || 0).toLocaleString()}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: C.muted,
                      width: 48,
                      flexShrink: 0,
                      textAlign: "right",
                    }}
                  >
                    {(+t.people || 0).toLocaleString()} ppl
                  </Typography>
                </Box>
                {topic === t.slug && (
                  <Box sx={{ ml: "158px", mb: 0.75 }}>
                    {!heatmap && (
                      <Typography sx={{ fontSize: 9, color: C.muted }}>
                        Loading map…
                      </Typography>
                    )}
                    {heatmap && heatmap.length === 0 && (
                      <Typography sx={{ fontSize: 9, color: C.muted }}>
                        No geo data.
                      </Typography>
                    )}
                    {heatmap &&
                      heatmap.slice(0, 12).map((h) => (
                        <Box
                          key={h.area}
                          component="span"
                          sx={{
                            fontSize: 9,
                            color: "#0369a1",
                            bgcolor: "#0369a10d",
                            border: "1px solid #0369a122",
                            px: 0.5,
                            py: 0.15,
                            borderRadius: "3px",
                            mr: 0.4,
                            display: "inline-block",
                            mb: 0.3,
                          }}
                        >
                          {h.area}: {(+h.weight || 0).toLocaleString()}
                        </Box>
                      ))}
                  </Box>
                )}
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
}

// State view-filter select. Holds its displayed value in LOCAL state (updates instantly on click) and
// defers the parent update — which triggers a re-fetch + heavy re-render of the member table — via
// startTransition, so changing the dropdown never hangs the UI waiting on that work.
function StateFilterSelect({ value, onPick, sx }) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <Box
      component="select"
      value={local}
      onChange={(e) => {
        const v = e.target.value;
        setLocal(v); // instant: the dropdown reflects the pick immediately
        onPick(v);
      }}
      sx={sx}
    >
      <option value="all">All / National</option>
      {US_STATES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </Box>
  );
}

// Isolated add-chip input: keeps the value in LOCAL state so keystrokes re-render only this tiny
// input, not the whole AudienceDiscovery tree (member table, topic list, drawer). Commits on Enter.
function AddChipInput({ placeholder, borderColor, onAdd }) {
  const [v, setV] = useState("");
  return (
    <Box
      component="input"
      placeholder={placeholder}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && v.trim()) {
          onAdd(v.trim());
          setV("");
        }
      }}
      sx={{
        fontSize: 9,
        width: 84,
        px: 0.5,
        py: 0.2,
        border: `1px solid ${borderColor}`,
        borderRadius: "4px",
        outline: "none",
      }}
    />
  );
}

// Segment results panel: inline (Discover) or a right-anchored Drawer (Topic Research). Temporary
// Drawer unmounts its children when closed, so the detail never renders against a null segment.
function DetailShell({ topicMode, open, onClose, children }) {
  if (!topicMode) {
    return <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>{children}</Box>;
  }
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={300}
      PaperProps={{
        sx: { width: { xs: "100%", md: 780 }, p: 2, boxSizing: "border-box" },
      }}
    >
      {children}
    </Drawer>
  );
}

function AudienceDiscovery({ campaignId, topicMode = false }) {
  const { authFetch } = useAuth();
  const api = SOCIAL_API_URL;
  const pubKey = process.env.REACT_APP_PUBNUB_SUB_KEY;

  const [stateSel, setStateSel] = useState("all"); // Topic Research: VIEW filter by state ("all" = national, default)
  const [topicStats, setTopicStats] = useState({}); // topic label → landscape stat {weight, people}
  const termsFetchedRef = useRef(new Set()); // topic segment ids we've already generated terms for
  const [segments, setSegments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [sel, setSel] = useState(null); // { segment, members }
  const [selLoading, setSelLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estById, setEstById] = useState({}); // Topic Research: per-topic inline estimate
  const [busy, setBusy] = useState(false); // a synchronous action is in flight
  const [progress, setProgress] = useState(null); // { stage } during an async run
  const [err, setErr] = useState(null);
  const selIdRef = useRef(null);
  const [enrichScope, setEnrichScope] = useState(null); // open enrich-more panel + selected scope
  const [enrichEst, setEnrichEst] = useState(null); // { scope, unenriched, est_cost_cents }
  // Create-Topic (promote a Discover run to a first-class, filterable topic). Holds the open panel's
  // state: the 3-tier suggestion, the picked mode, and an editable name for the "new topic" path.
  const [topicPromo, setTopicPromo] = useState(null); // { segId, loading, suggestion, mode, name, submitting }
  const [enrichRunning, setEnrichRunning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null); // { keywords, cities, date_from, date_to }
  const [savingDraft, setSavingDraft] = useState(false);

  const fetchList = useCallback(async () => {
    if (!api || !campaignId) return;
    try {
      const url = topicMode
        ? `${api}/segments/topics?campaign_id=${campaignId}`
        : `${api}/segments?campaign_id=${campaignId}`;
      const r = await authFetch(url);
      const d = await r.json();
      setSegments(d.segments || []);
    } catch (e) {
      /* ignore */
    }
  }, [api, campaignId, authFetch, topicMode]);

  const openSegment = useCallback(
    async (id) => {
      selIdRef.current = id;
      setSelLoading(true);
      setEstimate(null);
      setErr(null);
      try {
        // State is a VIEW filter for Topic Research — narrows the members server-side; "all" = full set.
        const st =
          topicMode && stateSel && stateSel !== "all"
            ? `?state=${encodeURIComponent(stateSel)}`
            : "";
        const r = await authFetch(`${api}/segments/${id}${st}`);
        const d = await r.json();
        setSel(d);
      } catch (e) {
        setErr("Failed to load segment");
      } finally {
        setSelLoading(false);
      }
    },
    [api, authFetch, topicMode, stateSel],
  );

  // Change the state view-filter. Flip to the loader SYNCHRONOUSLY so the heavy member table unmounts
  // immediately (no re-render against stale rows), then set the filter — the effect below re-fetches.
  const pickState = useCallback((v) => {
    if (selIdRef.current) setSelLoading(true);
    setStateSel(v);
  }, []);

  // Re-fetch the open segment's members when the state view-filter changes.
  useEffect(() => {
    if (topicMode && selIdRef.current) openSegment(selIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateSel]);

  useEffect(() => {
    setSel(null);
    selIdRef.current = null;
    fetchList();
  }, [fetchList]);

  // Topic Research: pull the issue-landscape stats (folded into each topic row), keyed by label.
  useEffect(() => {
    if (!topicMode || !TOPICS_API_URL) return;
    (async () => {
      try {
        const st = stateSel && stateSel !== "all" ? `&state=${stateSel}` : "";
        const r = await authFetch(`${TOPICS_API_URL}/ranking?days=90${st}`);
        const d = await r.json();
        const m = {};
        (d.ranking || []).forEach((t) => {
          if (t && t.label) m[t.label] = t;
        });
        setTopicStats(m);
      } catch (e) {
        /* ignore */
      }
    })();
  }, [topicMode, stateSel, authFetch]);

  // Topic Research: generate each topic's canonical terms once (so chips always show), progressively.
  useEffect(() => {
    if (!topicMode || !api) return;
    segments.forEach((s) => {
      const hasTerms = (s.keywords || []).length > 0;
      if (hasTerms || termsFetchedRef.current.has(s.id)) return;
      termsFetchedRef.current.add(s.id);
      authFetch(`${api}/segments/${s.id}/terms`, { method: "POST" })
        .then((r) => r.json())
        .then((d) => {
          const kw = d?.segment?.query_json?.keywords || [];
          if (kw.length)
            setSegments((prev) =>
              prev.map((x) => (x.id === s.id ? { ...x, keywords: kw } : x)),
            );
        })
        .catch(() => {});
    });
  }, [topicMode, api, segments, authFetch]);

  // Live progress: own PubNub sub on the campaign channel, filtered to segment-* events.
  useEffect(() => {
    if (!pubKey || !campaignId) return;
    const pn = new PubNub({ subscribeKey: pubKey, userId: "pollen-app" });
    const listener = {
      message: ({ message: msg }) => {
        if (
          !msg ||
          typeof msg.action !== "string" ||
          !(
            msg.action.startsWith("segment-") ||
            msg.action.startsWith("enrich-more-")
          )
        )
          return;
        if (msg.action.startsWith("enrich-more-")) {
          if (msg.action === "enrich-more-progress")
            setProgress({
              stage: "enriching-more",
              done: msg.done,
              total: msg.total,
            });
          else if (msg.action === "enrich-more-done") {
            setProgress(null);
            setEnrichEst(null);
            setEnrichScope(null);
            fetchList();
            if (selIdRef.current === msg.segment_id)
              openSegment(msg.segment_id);
          }
          return;
        }
        if (msg.action === "segment-progress")
          setProgress({
            stage: msg.stage,
            kept: msg.kept,
            authors: msg.authors,
            threads: msg.threads,
            candidates: msg.candidates,
            done: msg.done,
            total: msg.total,
          });
        else if (msg.action === "segment-done") {
          setProgress(null);
          fetchList();
          if (selIdRef.current === msg.segment_id) openSegment(msg.segment_id);
        } else if (msg.action === "segment-error") {
          setProgress(null);
          setErr(msg.error || "Run failed");
          fetchList();
        }
      },
    };
    pn.addListener(listener);
    pn.subscribe({ channels: [`campaign-${campaignId}`] });
    return () => {
      try {
        pn.removeListener(listener);
        pn.unsubscribe({ channels: [`campaign-${campaignId}`] });
      } catch (e) {}
    };
  }, [pubKey, campaignId, fetchList, openSegment]);

  const createSegment = async (text, platform = "x", sourceUrls = []) => {
    if (!text || !campaignId) return false;
    setCreating(true);
    setErr(null);
    try {
      const r = await authFetch(`${api}/segments`, {
        method: "POST",
        body: JSON.stringify({
          campaign_id: campaignId,
          nl_query: text,
          platform,
          source_urls: sourceUrls,
        }),
      });
      const d = await r.json();
      if (d.segment) {
        await fetchList();
        openSegment(d.segment.id);
        return true;
      }
      setErr(d.error || "Could not build the segment — try rephrasing.");
      return false;
    } catch (e) {
      setErr("Create failed");
      return false;
    } finally {
      setCreating(false);
    }
  };

  const runAction = async (id, action, startStage) => {
    setBusy(true);
    setErr(null);
    try {
      // Topic Research runs are NATIONAL — no state on the crawl. State is a view filter (below).
      const r = await authFetch(`${api}/segments/${id}/${action}`, {
        method: "POST",
      });
      const d = await r.json().catch(() => ({}));
      if (action === "estimate") {
        if (d.error) setErr(d.error);
        else if (topicMode) {
          // Topic Research: show the estimate inline on the row; refresh the row's chips from the
          // persisted terms. Do NOT touch `sel` — that would pop the results drawer open.
          setEstById((prev) => ({ ...prev, [id]: d }));
          try {
            const rs = await authFetch(`${api}/segments/${id}`);
            const ds = await rs.json();
            const kw = ds?.segment?.query_json?.keywords || [];
            if (kw.length)
              setSegments((prev) =>
                prev.map((x) => (x.id === id ? { ...x, keywords: kw } : x)),
              );
          } catch (e) {
            /* ignore */
          }
        } else {
          setEstimate(d);
          // Discover: refresh the detail so keyword chips render.
          try {
            const rs = await authFetch(`${api}/segments/${id}`);
            const ds = await rs.json();
            if (ds.segment)
              setSel((prev) => ({ ...(prev || {}), segment: ds.segment }));
          } catch (e) {
            /* ignore */
          }
        }
      } else setProgress({ stage: startStage }); // run/retier: async; progress arrives via PubNub
    } catch (e) {
      setErr(`${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  const enrichEstimate = async (id, scope) => {
    setEnrichScope(scope);
    setEnrichEst(null);
    setBusy(true);
    setErr(null);
    try {
      const r = await authFetch(
        `${api}/segments/${id}/enrich-estimate?scope=${scope}`,
        { method: "POST" },
      );
      const d = await r.json();
      if (d.error) setErr(d.error);
      else setEnrichEst(d);
    } catch (e) {
      setErr("Estimate failed");
    } finally {
      setBusy(false);
    }
  };

  const enrichMore = async (id, scope) => {
    setEnrichRunning(true);
    setErr(null);
    try {
      await authFetch(`${api}/segments/${id}/enrich-more?scope=${scope}`, {
        method: "POST",
      });
      setProgress({ stage: "enriching-more" }); // completes via PubNub enrich-more-done
    } catch (e) {
      setErr("Enrich failed");
    } finally {
      setEnrichRunning(false);
    }
  };

  const startEdit = (seg) => {
    setDraft({
      keywords: [...(seg.query_json?.keywords || [])],
      cities: [...(seg.geo_json?.cities || [])],
      date_from: (seg.date_from || "").slice(0, 10),
      date_to: (seg.date_to || "").slice(0, 10),
    });
    setEditing(true);
  };

  const saveDraft = async (id) => {
    if (!draft) return;
    setSavingDraft(true);
    setErr(null);
    try {
      const r = await authFetch(`${api}/segments/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          query_json: { keywords: draft.keywords },
          geo_json: { cities: draft.cities },
          date_from: draft.date_from,
          date_to: draft.date_to,
        }),
      });
      const d = await r.json();
      if (d.segment) {
        setEditing(false);
        setEstimate(null);
        await fetchList();
        openSegment(id);
      } else setErr(d.error || "Save failed");
    } catch (e) {
      setErr("Save failed");
    } finally {
      setSavingDraft(false);
    }
  };

  const exportCsv = async (id) => {
    try {
      const r = await authFetch(`${api}/segments/${id}/export`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `segment-${id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr("Export failed");
    }
  };

  // Promote a completed Discover run into a first-class topic. Fetches the 3-tier suggestion (reuse a
  // core topic / adopt another campaign's discovered one / mint new), defaulting to the strongest match
  // so we don't create near-duplicates.
  const openCreateTopic = async (seg) => {
    setTopicPromo({ segId: seg.id, loading: true, suggestion: null, mode: null, name: "", submitting: false });
    setErr(null);
    try {
      const r = await authFetch(`${api}/segments/${seg.id}/suggest-topic`);
      const d = await r.json();
      const s = d.suggestion || {};
      const mode = s.adopt ? "adopt" : s.map ? "map" : "new"; // prefer reuse over minting
      setTopicPromo({
        segId: seg.id,
        loading: false,
        suggestion: s,
        mode,
        name: s.new?.label || d.query_label || seg.name || "",
        submitting: false,
      });
    } catch (e) {
      setErr("Could not load topic suggestions");
      setTopicPromo(null);
    }
  };

  const submitCreateTopic = async () => {
    if (!topicPromo) return;
    const { segId, suggestion, mode, name } = topicPromo;
    const body = { mode };
    if (mode === "new") body.name = (name || "").trim();
    if (mode === "adopt") body.topic_id = suggestion?.adopt?.id;
    if (mode === "map") body.topic_id = suggestion?.map?.id;
    if (mode === "new" && !body.name) { setErr("Enter a topic name"); return; }
    setTopicPromo((p) => ({ ...p, submitting: true }));
    setErr(null);
    try {
      const r = await authFetch(`${api}/segments/${segId}/promote-topic`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.error) {
        setErr(d.error);
        setTopicPromo((p) => (p ? { ...p, submitting: false } : p));
        return;
      }
      setTopicPromo(null);
      // Refresh: the segment now carries its bound topic; the People issue filter + Topic Research card
      // pick it up on their next load.
      await fetchList();
      openSegment(segId);
    } catch (e) {
      setErr("Create topic failed");
      setTopicPromo((p) => (p ? { ...p, submitting: false } : p));
    }
  };

  const TierChip = ({ t }) => {
    const m = SEG_TIER_META[t] || { label: t, color: "#999" };
    return (
      <Box
        component="span"
        sx={{
          fontSize: 9,
          fontWeight: 800,
          color: "#fff",
          bgcolor: m.color,
          px: 0.6,
          py: 0.15,
          borderRadius: "4px",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {m.label}
      </Box>
    );
  };
  const Btn = ({ onClick, disabled, primary, children }) => (
    <Box
      component="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        fontSize: 11,
        fontWeight: 700,
        px: 1.5,
        py: 0.6,
        borderRadius: "6px",
        cursor: disabled ? "not-allowed" : "pointer",
        bgcolor: disabled ? C.border : primary ? C.navy : C.white,
        color: disabled ? C.muted : primary ? "#fff" : C.navy,
        border: `1px solid ${disabled ? C.border : C.navy + (primary ? "" : "66")}`,
        "&:hover": {
          bgcolor: disabled ? C.border : primary ? "#0b2a4a" : C.navy + "08",
        },
      }}
    >
      {children}
    </Box>
  );

  const seg = sel?.segment;
  const members = sel?.members || [];
  const tc = seg?.tier_counts_json || {};
  const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  // Source platform: X is query-seeded (keyword/geo search); FB/IG are URL-seeded (comment-mine the seed
  // posts), so the keyword/geo chips + X cost labels don't apply — show the seed URLs instead.
  const segPlatform = seg?.query_json?.platform || "x";
  const isFbIgSeg = segPlatform === "facebook" || segPlatform === "instagram";
  const segUrls = Array.isArray(seg?.query_json?.source_urls) ? seg.query_json.source_urls : [];
  const platBadge = { x: "𝕏 X", facebook: "📘 Facebook", instagram: "📸 Instagram" }[segPlatform] || "𝕏 X";
  // Topic Research: derive the count/tiers/sentiment from the LOADED (state-filtered) members, so the
  // header reflects exactly what's on screen — not the stale national run summary.
  const liveStats = useMemo(() => {
    const sent = { positive: 0, neutral: 0, negative: 0, mixed: 0 };
    const tiers = { voter_matched: 0, local: 0, consented: 0 };
    members.forEach((m) => {
      if (sent[m.sentiment] != null) sent[m.sentiment] += 1;
      if (tiers[m.tier] != null) tiers[m.tier] += 1;
    });
    return { count: members.length, sentiment: sent, tiers };
  }, [members]);
  const eTc = topicMode ? liveStats.tiers : tc;
  const eSent = topicMode ? liveStats.sentiment : seg?.summary_json?.sentiment;

  return (
    <Box>
      {api && !topicMode && <TopicLandscape authFetch={authFetch} />}
      {!api && (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            border: `1px dashed ${C.border}`,
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted }}>
            Social API not configured
          </Typography>
        </Box>
      )}

      {api && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: {
              xs: "column",
              md: topicMode ? "column" : "row",
            },
            alignItems: "flex-start",
          }}
        >
          {/* Left: composer (Discover) OR state picker (Topic Research) + segment list */}
          <Box
            sx={{
              width: { xs: "100%", md: topicMode ? "100%" : 300 },
              flexShrink: 0,
            }}
          >
            {topicMode ? (
              <Box>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.charcoal,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.75,
                  }}
                >
                  Filter by state (view)
                </Typography>
                <StateFilterSelect
                  value={stateSel}
                  onPick={pickState}
                  sx={{
                    width: "100%",
                    fontSize: 12,
                    fontFamily: "inherit",
                    p: 0.75,
                    border: `1px solid ${C.border}`,
                    borderRadius: 1,
                    boxSizing: "border-box",
                    bgcolor: C.white,
                  }}
                />
                <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.5 }}>
                  Pick an issue below, then Run / Re-tier. Each run adds to the
                  list — results accumulate over time.
                </Typography>
              </Box>
            ) : (
              <SegmentComposer onSubmit={createSegment} creating={creating} />
            )}

            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.5 }}
            >
              {segments.length === 0 && (
                <Typography sx={{ fontSize: 11, color: C.muted }}>
                  No segments yet.
                </Typography>
              )}
              {segments.map((s) => {
                const active = seg?.id === s.id;
                const stc = s.tier_counts_json || {};
                const statLine =
                  `${s.status}` +
                  (s.member_count != null ? ` · ${s.member_count} members` : "") +
                  (stc.voter_matched ? ` · ${stc.voter_matched} voters` : "");
                const tstat = topicStats[s.name];

                if (topicMode) {
                  const est = estById[s.id];
                  const btn = (label, onClick, primary) => (
                    <Box
                      component="button"
                      onClick={onClick}
                      disabled={busy}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "6px",
                        cursor: busy ? "not-allowed" : "pointer",
                        bgcolor: primary ? C.navy : C.white,
                        color: primary ? "#fff" : C.charcoal,
                        border: `1px solid ${primary ? C.navy : C.border}`,
                      }}
                    >
                      {label}
                    </Box>
                  );
                  return (
                    <Box
                      key={s.id}
                      sx={{
                        border: `1px solid ${active ? C.navy : C.border}`,
                        borderRadius: 1,
                        p: 1.5,
                        mb: 1,
                        bgcolor: C.white,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 800,
                            color: C.charcoal,
                          }}
                        >
                          {s.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            color: tstat ? C.navy : C.muted,
                          }}
                        >
                          {tstat
                            ? `${(tstat.weight || 0).toLocaleString()} mentions · ${(
                                tstat.people || 0
                              ).toLocaleString()} people`
                            : "no signals yet"}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
                        {statLine}
                      </Typography>
                      {(s.keywords || []).length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.4,
                            mt: 0.75,
                          }}
                        >
                          {s.keywords.slice(0, 18).map((k, i) => (
                            <Box
                              key={i}
                              sx={{
                                fontSize: 9,
                                px: 0.6,
                                py: 0.15,
                                border: `1px solid ${C.border}`,
                                borderRadius: "4px",
                                color: C.charcoal,
                              }}
                            >
                              {k}
                            </Box>
                          ))}
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.75,
                          mt: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {btn("Estimate", () => runAction(s.id, "estimate"))}
                        {btn(
                          "▶ Run",
                          () => {
                            runAction(s.id, "run", "starting");
                            openSegment(s.id);
                          },
                          true,
                        )}
                        {btn("↻ Re-tier", () =>
                          runAction(s.id, "retier", "retiering"),
                        )}
                        {btn("View results →", () => openSegment(s.id))}
                      </Box>
                      {est && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            border: `1px solid ${C.border}`,
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: C.charcoal,
                            }}
                          >
                            ~{(est.est_post_count || 0).toLocaleString()} matching
                            posts · ~{(est.will_crawl || 0).toLocaleString()}{" "}
                            crawled (incl. comments) · est. ${est.est_cost_usd}
                          </Typography>
                          {est.cost_breakdown && (
                            <Typography
                              sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}
                            >
                              X ${est.cost_breakdown.x_crawl} · scoring $
                              {est.cost_breakdown.scoring} · enrichment $
                              {est.cost_breakdown.enrichment}
                              {est.projection?.est_enriched != null
                                ? ` · ~${est.projection.est_enriched} enriched`
                                : ""}{" "}
                              · likers ${est.cost_breakdown.likers}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                }

                return (
                  <Box
                    key={s.id}
                    component="button"
                    onClick={() => openSegment(s.id)}
                    sx={{
                      textAlign: "left",
                      border: `1px solid ${active ? C.navy : C.border}`,
                      bgcolor: active ? C.navy + "08" : C.white,
                      borderRadius: 1,
                      p: 1,
                      cursor: "pointer",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.charcoal,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {s.name}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.25 }}>
                      {statLine}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Right: detail — inline (Discover) or right Drawer (Topic Research) */}
          <DetailShell
            topicMode={topicMode}
            open={!!seg || selLoading}
            onClose={() => {
              setSel(null);
              setSelLoading(false);
              selIdRef.current = null;
            }}
          >
            {err && (
              <Box
                sx={{
                  mb: 1.5,
                  p: 1,
                  fontSize: 11,
                  color: "#dc2626",
                  bgcolor: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 1,
                }}
              >
                {err}
              </Box>
            )}

            {progress && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  border: `1px solid ${C.border}`,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <IntelLoader size={40} bars={[70, 90, 55, 80]} barHeight={7} />
                <Box>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: C.charcoal }}
                  >
                    {segStageLabel(progress.stage)}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: C.muted }}>
                    {progress.done != null && progress.total
                      ? `${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}`
                      : progress.authors != null
                        ? `${progress.authors} authors`
                        : progress.threads != null
                          ? `${progress.threads} threads`
                          : progress.kept != null
                            ? `${progress.kept} kept`
                            : progress.candidates != null
                              ? `${progress.candidates} candidates`
                              : "this can take a few minutes"}
                  </Typography>
                </Box>
              </Box>
            )}

            {selLoading && <IntelLoader bars={[80, 100, 60, 90]} />}

            {!seg && !selLoading && (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                  border: `1px dashed ${C.border}`,
                  borderRadius: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, color: C.muted }}
                >
                  Build a segment or pick one on the left.
                </Typography>
              </Box>
            )}

            {seg && !selLoading && (
              <Box>
                <Typography
                  sx={{ fontSize: 15, fontWeight: 800, color: C.charcoal }}
                >
                  {seg.name}
                </Typography>
                <Typography sx={{ fontSize: 10, color: C.muted, mb: 1 }}>
                  {platBadge} · {seg.query_json?.topic}
                  {isFbIgSeg ? "" : ` · ${seg.date_from} → ${seg.date_to}`} ·{" "}
                  {seg.status}
                </Typography>

                {/* FB/IG seed post URLs — the posts whose commenters we mine (URL-seeded, no keyword search) */}
                {isFbIgSeg && segUrls.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.4 }}>
                      Mining comments on {segUrls.length} post{segUrls.length === 1 ? "" : "s"}
                    </Typography>
                    {segUrls.map((u, i) => (
                      <Box
                        key={i}
                        component="a"
                        href={u}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ display: "block", fontSize: 10, color: "#0369a1", textDecoration: "none", wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}
                      >
                        {u}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* keyword / geo chips (X only — FB/IG don't keyword-search) */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    mb: 1.5,
                    alignItems: "center",
                  }}
                >
                  {(editing && draft
                    ? draft.keywords
                    : isFbIgSeg
                      ? []
                      : seg.query_json?.keywords || []
                  )
                    .slice(0, editing ? 100 : 12)
                    .map((k, i) => (
                      <Box
                        key={i}
                        component="span"
                        sx={{
                          fontSize: 9,
                          color: C.navy,
                          bgcolor: C.navy + "0d",
                          border: `1px solid ${C.navy}22`,
                          px: 0.6,
                          py: 0.15,
                          borderRadius: "4px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.3,
                        }}
                      >
                        {k}
                        {editing && (
                          <Box
                            component="span"
                            onClick={() =>
                              setDraft((d) => ({
                                ...d,
                                keywords: d.keywords.filter((_, j) => j !== i),
                              }))
                            }
                            sx={{
                              cursor: "pointer",
                              fontWeight: 800,
                              color: C.muted,
                              "&:hover": { color: "#dc2626" },
                            }}
                          >
                            ×
                          </Box>
                        )}
                      </Box>
                    ))}
                  {(editing && draft
                    ? draft.cities
                    : isFbIgSeg
                      ? []
                      : seg.geo_json?.cities || []
                  )
                    .slice(0, editing ? 100 : 8)
                    .map((k, i) => (
                      <Box
                        key={"c" + i}
                        component="span"
                        sx={{
                          fontSize: 9,
                          color: "#0369a1",
                          bgcolor: "#0369a10d",
                          border: "1px solid #0369a122",
                          px: 0.6,
                          py: 0.15,
                          borderRadius: "4px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.3,
                        }}
                      >
                        📍 {k}
                        {editing && (
                          <Box
                            component="span"
                            onClick={() =>
                              setDraft((d) => ({
                                ...d,
                                cities: d.cities.filter((_, j) => j !== i),
                              }))
                            }
                            sx={{
                              cursor: "pointer",
                              fontWeight: 800,
                              color: C.muted,
                              "&:hover": { color: "#dc2626" },
                            }}
                          >
                            ×
                          </Box>
                        )}
                      </Box>
                    ))}
                  {editing && draft && (
                    <>
                      <AddChipInput
                        placeholder="+ keyword"
                        borderColor={`${C.navy}44`}
                        onAdd={(val) =>
                          setDraft((d) => ({
                            ...d,
                            keywords: [...d.keywords, val],
                          }))
                        }
                      />
                      <AddChipInput
                        placeholder="+ place"
                        borderColor="#0369a144"
                        onAdd={(val) =>
                          setDraft((d) => ({ ...d, cities: [...d.cities, val] }))
                        }
                      />
                      <Box
                        component="input"
                        type="date"
                        value={draft.date_from}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, date_from: e.target.value }))
                        }
                        sx={{
                          fontSize: 9,
                          px: 0.5,
                          py: 0.2,
                          border: `1px solid ${C.border}`,
                          borderRadius: "4px",
                        }}
                      />
                      <Box
                        component="input"
                        type="date"
                        value={draft.date_to}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, date_to: e.target.value }))
                        }
                        sx={{
                          fontSize: 9,
                          px: 0.5,
                          py: 0.2,
                          border: `1px solid ${C.border}`,
                          borderRadius: "4px",
                        }}
                      />
                    </>
                  )}
                </Box>

                {/* actions */}
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}
                >
                  {["draft", "ready_to_run"].includes(seg.status) && (
                    <Btn
                      onClick={() =>
                        editing ? saveDraft(seg.id) : startEdit(seg)
                      }
                      disabled={savingDraft}
                      primary={editing}
                    >
                      {editing ? (savingDraft ? "Saving…" : "✓ Save") : "✎ Edit"}
                    </Btn>
                  )}
                  {editing && (
                    <Btn onClick={() => setEditing(false)} disabled={savingDraft}>
                      Cancel
                    </Btn>
                  )}
                  {topicMode && (
                    <StateFilterSelect
                      value={stateSel}
                      onPick={pickState}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: "inherit",
                        px: 0.75,
                        py: 0.5,
                        border: `1px solid ${C.border}`,
                        borderRadius: "6px",
                        bgcolor: C.white,
                        cursor: "pointer",
                      }}
                    />
                  )}
                  <Btn
                    onClick={() => runAction(seg.id, "estimate")}
                    disabled={busy || !!progress}
                  >
                    Estimate
                  </Btn>
                  <Btn
                    onClick={() => runAction(seg.id, "run", "starting")}
                    disabled={busy || !!progress}
                    primary
                  >
                    ▶ Run
                  </Btn>
                  {seg.member_count != null && (
                    <Btn
                      onClick={() => runAction(seg.id, "retier", "retiering")}
                      disabled={busy || !!progress}
                    >
                      ↻ Re-tier (free)
                    </Btn>
                  )}
                  {members.length > 0 && (
                    <Btn onClick={() => exportCsv(seg.id)}>⬇ Export CSV</Btn>
                  )}
                  {/* Enrich-more = paid PDL/Datagma/Apollo, keyed on the X handle → X-only. Hidden for FB/IG
                      (its "Whole pool" count is the campaign-wide X pool, unrelated to this run). */}
                  {seg.status === "complete" && !isFbIgSeg && (
                    <Btn
                      onClick={() =>
                        enrichScope
                          ? setEnrichScope(null)
                          : enrichEstimate(seg.id, "in_area")
                      }
                      disabled={busy || !!progress}
                    >
                      ✨ Enrich more
                    </Btn>
                  )}
                  {/* Discover only: promote this run to a filterable, re-runnable topic. */}
                  {!topicMode && seg.status === "complete" && (
                    <Btn
                      onClick={() =>
                        topicPromo?.segId === seg.id
                          ? setTopicPromo(null)
                          : openCreateTopic(seg)
                      }
                      disabled={busy || !!progress}
                      primary={!seg.topic_id && topicPromo?.segId !== seg.id}
                    >
                      🏷 {seg.topic_id ? "Topic ✓ — edit" : "Create Topic"}
                    </Btn>
                  )}
                </Box>

                {/* create-topic panel — promote this Discover run to a first-class topic */}
                {topicPromo?.segId === seg.id && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.25,
                      border: `1px solid ${C.border}`,
                      borderRadius: 1,
                      bgcolor: C.navy + "04",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        mb: 0.75,
                      }}
                    >
                      Create a topic from this audience — makes these people
                      filterable everywhere + re-runnable
                    </Typography>
                    {topicPromo.loading ? (
                      <Typography sx={{ fontSize: 11, color: C.muted }}>
                        Finding the best match…
                      </Typography>
                    ) : (
                      <>
                        {[
                          topicPromo.suggestion?.map && {
                            mode: "map",
                            label: `Add to standard issue: ${topicPromo.suggestion.map.label}`,
                            hint: "already a core topic shown to every campaign",
                          },
                          topicPromo.suggestion?.adopt && {
                            mode: "adopt",
                            label: `Reuse existing topic: ${topicPromo.suggestion.adopt.label}`,
                            hint: "another campaign already created this — reuses it so the master list stays clean (no duplicates)",
                          },
                          {
                            mode: "new",
                            label: "Create a new topic",
                            hint: "only if it isn't one of the above",
                          },
                        ]
                          .filter(Boolean)
                          .map((o) => (
                            <Box
                              key={o.mode}
                              onClick={() =>
                                setTopicPromo((p) => ({ ...p, mode: o.mode }))
                              }
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                                p: 0.9,
                                mb: 0.5,
                                borderRadius: "6px",
                                cursor: "pointer",
                                border: `1px solid ${
                                  topicPromo.mode === o.mode ? C.navy : C.border
                                }`,
                                bgcolor:
                                  topicPromo.mode === o.mode
                                    ? C.navy + "0A"
                                    : C.white,
                              }}
                            >
                              <Box
                                sx={{
                                  mt: "3px",
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  flexShrink: 0,
                                  border: `2px solid ${
                                    topicPromo.mode === o.mode
                                      ? C.navy
                                      : C.border
                                  }`,
                                  bgcolor:
                                    topicPromo.mode === o.mode
                                      ? C.navy
                                      : "transparent",
                                }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  sx={{ fontSize: 11.5, fontWeight: 700, color: C.navy }}
                                >
                                  {o.label}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: C.muted }}>
                                  {o.hint}
                                </Typography>
                                {o.mode === "new" &&
                                  topicPromo.mode === "new" && (
                                    <Box
                                      component="input"
                                      value={topicPromo.name}
                                      onChange={(e) =>
                                        setTopicPromo((p) => ({
                                          ...p,
                                          name: e.target.value,
                                        }))
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="Topic name"
                                      sx={{
                                        mt: 0.5,
                                        fontSize: 12,
                                        fontFamily: "inherit",
                                        px: 0.75,
                                        py: 0.4,
                                        width: "100%",
                                        maxWidth: 280,
                                        border: `1px solid ${C.border}`,
                                        borderRadius: "6px",
                                        boxSizing: "border-box",
                                      }}
                                    />
                                  )}
                              </Box>
                            </Box>
                          ))}
                        <Box sx={{ display: "flex", gap: 0.75, mt: 0.75 }}>
                          <Btn
                            primary
                            onClick={submitCreateTopic}
                            disabled={topicPromo.submitting}
                          >
                            {topicPromo.submitting ? "Creating…" : "🏷 Create Topic"}
                          </Btn>
                          <Btn
                            onClick={() => setTopicPromo(null)}
                            disabled={topicPromo.submitting}
                          >
                            Cancel
                          </Btn>
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                {/* enrich-more panel (pay-as-you-go expansion) — X-only */}
                {enrichScope && !isFbIgSeg && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.25,
                      border: `1px solid ${C.border}`,
                      borderRadius: 1,
                      bgcolor: C.navy + "04",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        mb: 0.75,
                      }}
                    >
                      Enrich a wider slice — pay only for un-enriched contacts
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
                      {[
                        { s: "in_area", l: "In-area" },
                        { s: "relevant", l: "All relevant" },
                        { s: "pool", l: "Whole pool" },
                      ].map(({ s, l }) => (
                        <Box
                          key={s}
                          component="button"
                          onClick={() => enrichEstimate(seg.id, s)}
                          disabled={busy}
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            px: 1,
                            py: 0.4,
                            borderRadius: "6px",
                            cursor: busy ? "not-allowed" : "pointer",
                            bgcolor: enrichScope === s ? C.navy : C.white,
                            color: enrichScope === s ? "#fff" : C.navy,
                            border: `1px solid ${C.navy}66`,
                          }}
                        >
                          {l}
                        </Box>
                      ))}
                    </Box>
                    {busy && !enrichEst && (
                      <Typography sx={{ fontSize: 10, color: C.muted }}>
                        Counting…
                      </Typography>
                    )}
                    {enrichEst && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: C.charcoal }}>
                          {enrichEst.unenriched?.toLocaleString()} un-enriched ·
                          est. {usd(enrichEst.est_cost_cents)}
                        </Typography>
                        <Btn
                          primary
                          disabled={
                            enrichRunning || !enrichEst.unenriched || !!progress
                          }
                          onClick={() => enrichMore(seg.id, enrichEst.scope)}
                        >
                          {enrichRunning
                            ? "Starting…"
                            : `Enrich ${enrichEst.unenriched?.toLocaleString()}`}
                        </Btn>
                      </Box>
                    )}
                  </Box>
                )}

                {/* estimate result */}
                {estimate && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.25,
                      border: `1px solid ${C.border}`,
                      borderRadius: 1,
                      bgcolor: C.white,
                    }}
                  >
                    {isFbIgSeg ? (
                      <>
                        <Typography
                          sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
                        >
                          ~{estimate.est_post_count?.toLocaleString()} comments to
                          mine · est. {usd(estimate.est_cost_cents)}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
                          comment scrape{" "}
                          {usd((estimate.cost_breakdown?.comment_scrape || 0) * 100)} ·
                          scoring {usd((estimate.cost_breakdown?.scoring || 0) * 100)}
                          {estimate.projection?.est_authors != null
                            ? ` · ~${estimate.projection.est_authors.toLocaleString()} commenters`
                            : ""}
                        </Typography>
                        <Typography sx={{ fontSize: 9.5, color: C.muted, mt: 0.4 }}>
                          Comment counts aren’t known before the run — projected from
                          the seed-URL count, capped.
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography
                          sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
                        >
                          ~{estimate.est_post_count?.toLocaleString()} matching
                          posts · ~
                          {estimate.projection?.total_posts?.toLocaleString()}{" "}
                          crawled (incl. comments) · est.{" "}
                          {usd(estimate.est_cost_cents)}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
                          X {usd(estimate.cost_breakdown?.x_crawl * 100)} · scoring{" "}
                          {usd(estimate.cost_breakdown?.scoring * 100)} · enrichment{" "}
                          {usd(estimate.cost_breakdown?.enrichment * 100)} · ~
                          {estimate.projection?.est_enriched} enriched
                          {estimate.cost_breakdown?.likers ? (
                            <>
                              {" "}
                              · likers {usd(estimate.cost_breakdown.likers * 100)}
                            </>
                          ) : null}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}

                {/* Plain-English "what you got + what's next" — leads with human terms so a campaign
                    manager sees the value + the action, not just funnel numbers. Shows on EVERY discover
                    run that produced data — gated on run output (funnel), not status (a re-estimate flips
                    status back to ready_to_run but the prior run's results still stand). */}
                {!topicMode &&
                  seg.summary_json?.funnel?.length > 0 &&
                  (() => {
                    const fCount = (k) =>
                      (seg.summary_json?.funnel || []).find((x) => x.key === k)
                        ?.count ?? null;
                    const commenters =
                      fCount("participants") ??
                      seg.summary_json?.contacts ??
                      null;
                    const onTopic = fCount("relevant") ?? seg.member_count;
                    const matched = fCount("voter_matched") ?? 0;
                    const topic = seg.query_json?.topic || seg.name;
                    return (
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.25,
                          border: `1px solid ${C.navy}22`,
                          bgcolor: C.navy + "06",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: C.charcoal,
                            lineHeight: 1.5,
                          }}
                        >
                          {isFbIgSeg ? (
                            <>
                              {commenters != null
                                ? `${commenters.toLocaleString()} people commented on this post`
                                : `${(onTopic || 0).toLocaleString()} commenters found`}
                              {onTopic != null
                                ? ` — ${onTopic.toLocaleString()} genuinely engaged “${topic}.”`
                                : "."}
                            </>
                          ) : (
                            <>
                              {(onTopic || 0).toLocaleString()} people genuinely
                              discussing “{topic}”
                              {matched
                                ? ` · ${matched.toLocaleString()} matched to the voter file`
                                : ""}
                              .
                            </>
                          )}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: C.charcoal,
                            mt: 0.6,
                            lineHeight: 1.5,
                          }}
                        >
                          {isFbIgSeg
                            ? "This is message-response intelligence: who commented, how they felt, and which issue they raised. Facebook commenters are identified by name only — most can't be matched to contacts or the voter file off-platform."
                            : "All are saved to your People list as campaign leads, tagged with this issue — most X handles resolve to a contact or voter match."}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 10.5, color: C.muted, mt: 0.5 }}
                        >
                          {isFbIgSeg ? (
                            <>
                              <b>Do next:</b> Read the sentiment + issue split
                              above to see how the post landed. Engage individuals
                              by replying from your Facebook Page; Export CSV for
                              your records. Don’t run contact-enrichment on
                              name-only commenters — there’s nothing to resolve.
                            </>
                          ) : (
                            <>
                              <b>Do next:</b> Export CSV, or open them in{" "}
                              <b>People</b> to find contacts (email / phone),
                              score them, and add to outreach.
                            </>
                          )}
                        </Typography>
                      </Box>
                    );
                  })()}

                {/* summary when complete */}
                {seg.status === "complete" &&
                  (topicMode || seg.member_count != null) && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: C.charcoal,
                          lineHeight: 1,
                        }}
                      >
                        {topicMode ? liveStats.count : seg.member_count}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 9,
                          color: C.muted,
                          textTransform: "uppercase",
                        }}
                      >
                        {topicMode
                          ? "members shown"
                          : isFbIgSeg
                            ? "on-topic commenters"
                            : "in-area members"}
                      </Typography>
                    </Box>
                    {["voter_matched", "local", "consented"].map((t) =>
                      eTc[t] ? (
                        <Box key={t}>
                          <Typography
                            sx={{
                              fontSize: 22,
                              fontWeight: 800,
                              color: SEG_TIER_META[t].color,
                              lineHeight: 1,
                            }}
                          >
                            {eTc[t]}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 9,
                              color: C.muted,
                              textTransform: "uppercase",
                            }}
                          >
                            {SEG_TIER_META[t].label}
                          </Typography>
                        </Box>
                      ) : null,
                    )}
                    {eSent && (
                      <Box sx={{ flex: 1, minWidth: 160 }}>
                        <Typography
                          sx={{
                            fontSize: 9,
                            color: C.muted,
                            textTransform: "uppercase",
                            mb: 0.25,
                          }}
                        >
                          Sentiment
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            height: 10,
                            borderRadius: 5,
                            overflow: "hidden",
                            border: `1px solid ${C.border}`,
                          }}
                        >
                          {["positive", "neutral", "negative", "mixed"].map(
                            (s) => {
                              const v = eSent[s] || 0;
                              return v ? (
                                <Box
                                  key={s}
                                  sx={{ flex: v, bgcolor: SEG_SENT_COLOR[s] }}
                                  title={`${s}: ${v}`}
                                />
                              ) : null;
                            },
                          )}
                        </Box>
                        {!topicMode && seg.summary_json.sentiment_weighted && (
                          <>
                            <Typography
                              sx={{ fontSize: 8, color: C.muted, mt: 0.6 }}
                            >
                              Weighted by engagement (who agreed)
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                height: 10,
                                borderRadius: 5,
                                overflow: "hidden",
                                border: `1px solid ${C.border}`,
                                mt: 0.25,
                              }}
                            >
                              {["positive", "neutral", "negative", "mixed"].map(
                                (s) => {
                                  const v =
                                    seg.summary_json.sentiment_weighted[s] || 0;
                                  return v ? (
                                    <Box
                                      key={s}
                                      sx={{ flex: v, bgcolor: SEG_SENT_COLOR[s] }}
                                      title={`${s}: ${v} (weighted)`}
                                    />
                                  ) : null;
                                },
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                )}

                {/* run funnel — how the crawl narrowed to confirmed voters (% = retained from prior stage) */}
                {seg.summary_json?.funnel?.length > 0 &&
                  (() => {
                    const f = seg.summary_json.funnel;
                    const max = Math.max(...f.map((s) => s.count || 0), 1);
                    const colorOf = (k) =>
                      k === "voter_matched"
                        ? SEG_TIER_META.voter_matched.color
                        : k === "in_area"
                          ? C.navy
                          : k === "relevant"
                            ? "#0369a1"
                            : k === "enriched"
                              ? "#7c3aed"
                              : C.muted;
                    return (
                      <Box
                        sx={{
                          mb: 2,
                          border: `1px solid ${C.border}`,
                          borderRadius: 1,
                          p: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            mb: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: C.muted,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                            }}
                          >
                            Run funnel
                          </Typography>
                          {seg.summary_json.contacts != null && (
                            <Typography sx={{ fontSize: 9, color: C.muted }}>
                              {seg.summary_json.contacts.toLocaleString()}{" "}
                              contacts saved to pool
                            </Typography>
                          )}
                        </Box>
                        {f.map((s, i) => {
                          const prev = i > 0 ? f[i - 1].count || 0 : null;
                          const pct =
                            prev && prev > 0
                              ? Math.round(((s.count || 0) / prev) * 100)
                              : null;
                          return (
                            <Box
                              key={s.key}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.4,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: C.charcoal,
                                  width: 112,
                                  flexShrink: 0,
                                  textAlign: "right",
                                }}
                              >
                                {s.label}
                              </Typography>
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 15,
                                  bgcolor: C.navy + "0a",
                                  borderRadius: "3px",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${Math.max(((s.count || 0) / max) * 100, 1.5)}%`,
                                    height: "100%",
                                    bgcolor: colorOf(s.key),
                                  }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color: C.charcoal,
                                  width: 52,
                                  flexShrink: 0,
                                  textAlign: "right",
                                }}
                              >
                                {(s.count || 0).toLocaleString()}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 9,
                                  color: C.muted,
                                  width: 32,
                                  flexShrink: 0,
                                  textAlign: "right",
                                }}
                              >
                                {pct != null ? `${pct}%` : ""}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}

                {/* members table */}
                {members.length > 0 && (
                  <Box
                    sx={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.4fr 1fr 0.8fr 2fr",
                        gap: 1,
                        px: 1,
                        py: 0.6,
                        bgcolor: C.navy + "06",
                        fontSize: 9,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: "uppercase",
                      }}
                    >
                      <Box>Person</Box>
                      <Box>Location</Box>
                      <Box>Signal</Box>
                      <Box>Sample post</Box>
                    </Box>
                    {members.map((m, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1.4fr 1fr 0.8fr 2fr",
                          gap: 1,
                          px: 1,
                          py: 0.75,
                          borderTop: `1px solid ${C.border}`,
                          alignItems: "start",
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: C.charcoal,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {m.display_name || "@" + m.username}
                            </Typography>
                            <TierChip t={m.tier} />
                            {m.signal_source === "like" && (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 8,
                                  fontWeight: 800,
                                  color: "#b45309",
                                  bgcolor: "#fef3c7",
                                  px: 0.5,
                                  py: 0.1,
                                  borderRadius: "3px",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                                title="Agreed by liking a top comment"
                              >
                                liked
                              </Box>
                            )}
                          </Box>
                          {/* Handle link — platform-correct host. FB commenters usually have NO @handle
                              (identified by name + profile id), so show "View profile" instead of a bare "@". */}
                          {m.username ? (
                            <Box
                              component="a"
                              href={
                                segPlatform === "facebook"
                                  ? `https://facebook.com/${m.username.replace(/^@/, "")}`
                                  : segPlatform === "instagram"
                                    ? `https://instagram.com/${m.username.replace(/^@/, "")}`
                                    : `https://x.com/${m.username.replace(/^@/, "")}`
                              }
                              target="_blank"
                              rel="noreferrer"
                              sx={{
                                fontSize: 10,
                                color: C.navy,
                                textDecoration: "none",
                              }}
                            >
                              @{m.username.replace(/^@/, "")}
                            </Box>
                          ) : isFbIgSeg &&
                            m.platform_user_id &&
                            !/^(facebook|instagram):/i.test(
                              String(m.platform_user_id),
                            ) ? (
                            // No vanity handle (profile.php?id=… commenter) — link by the FB/IG id
                            // (facebook.com/{id} resolves for numeric AND pfbid ids).
                            <Box
                              component="a"
                              href={`https://${segPlatform === "instagram" ? "instagram" : "facebook"}.com/${m.platform_user_id}`}
                              target="_blank"
                              rel="noreferrer"
                              sx={{
                                fontSize: 10,
                                color: C.navy,
                                textDecoration: "none",
                              }}
                            >
                              View{" "}
                              {segPlatform === "instagram"
                                ? "Instagram"
                                : "Facebook"}{" "}
                              profile ↗
                            </Box>
                          ) : null}
                          {(m.pdl_email || m.pdl_phone) && (
                            <Typography
                              sx={{
                                fontSize: 9,
                                color: C.muted,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {m.pdl_email || m.pdl_phone}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{ fontSize: 10, color: C.charcoal, minWidth: 0 }}
                        >
                          {[m.inferred_city, m.inferred_zip]
                            .filter(Boolean)
                            .join(" ") ||
                            m.inferred_state ||
                            "—"}
                          {m.voter_party && (
                            <Box
                              component="span"
                              sx={{
                                ml: 0.5,
                                fontWeight: 800,
                                color:
                                  m.voter_party === "R"
                                    ? "#dc2626"
                                    : m.voter_party === "D"
                                      ? "#2563eb"
                                      : C.muted,
                              }}
                            >
                              {m.voter_party}
                            </Box>
                          )}
                          {m.voter_district_cd && (
                            <Typography sx={{ fontSize: 9, color: C.muted }}>
                              CD-{m.voter_district_cd}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          {m.sentiment && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: SEG_SENT_COLOR[m.sentiment] || C.muted,
                              }}
                            >
                              {m.sentiment}
                            </Box>
                          )}
                          {m.relevance_score != null && (
                            <Typography sx={{ fontSize: 9, color: C.muted }}>
                              {Math.round(parseFloat(m.relevance_score) * 100)}%
                              rel
                            </Typography>
                          )}
                          {m.stance && (
                            <Typography
                              sx={{ fontSize: 9, color: C.charcoal, mt: 0.25 }}
                            >
                              {m.stance}
                            </Typography>
                          )}
                          {m.topic_tags && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.3,
                                mt: 0.3,
                              }}
                            >
                              {m.topic_tags
                                .split("|")
                                .slice(0, 4)
                                .map((tag, ti) => (
                                  <Box
                                    key={ti}
                                    component="span"
                                    sx={{
                                      fontSize: 8,
                                      fontWeight: 700,
                                      color: C.navy,
                                      bgcolor: C.navy + "12",
                                      px: 0.5,
                                      py: 0.15,
                                      borderRadius: "3px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {tag}
                                  </Box>
                                ))}
                            </Box>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: C.muted,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {m.sample_text || ""}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {seg.summary_json?.hotspots?.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Hotspots (zip)
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {seg.summary_json.hotspots.map((h, i) => (
                        <Box
                          key={i}
                          component="span"
                          sx={{
                            fontSize: 9,
                            color: C.charcoal,
                            bgcolor: C.navy + "0d",
                            px: 0.6,
                            py: 0.2,
                            borderRadius: "4px",
                          }}
                        >
                          {h.zip} · {h.count}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DetailShell>
        </Box>
      )}
    </Box>
  );
}

const SocialTab = ({
  section,
  cfg,
  allMentions,
  loading,
  syncing,
  syncResult,
  lastSynced,
  error,
  searchQuery,
  filter,
  authorFilter,
  page,
  onFilterChange,
  onAuthorChange,
  onPageChange,
  onSync,
  terms = [],
  termsLoading,
  selectedTerm,
  onSelectTerm,
  onAddTerm,
  onDeleteTerm,
  onRefreshTerm,
  onUpdateTerm,
  mode = "monitor",
  onModeChange,
  leads = [],
  leadsLoading,
  collecting,
  collectResult,
  collectProgress = null,
  collectTweetUrl = "",
  onCollectTweetUrlChange,
  onCollect,
  onCollectCard,
  audiencePreFill,
  onCreateAudienceFromTweet,
  onClearAudiencePreFill,
  audiences = [],
  audiencesLoading,
  selectedAudienceId,
  onSelectAudience,
  collections = [],
  selectedCollectionId,
  onSelectCollection,
  collectionAnswers = [],
  collectionInsights = null,
  donorLinks = [],
  polling,
  pollProgress,
  onCreateAudience,
  onRefreshAudience,
  onPollAudience,
  onPollFromPost,
  onBulkReply,
  bulkReplyJob,
  onTagCohort,
  onPostReply,
  onGenerateVariations,
  onSaveDonorLink,
  activeCampaignId,
  onCollectAll,
  collectingAllTermId,
  collectAllProgress,
  onDeleteCollection,
  onNormalizeCollection,
  onDeleteAudience,
  enriching = false,
  enrichProgress = null,
  locating = false,
  onEnrichLocations,
  onEnrichIdentity,
  onOpenProfile,
}) => {
  const { authFetch } = useAuth();
  const [addMentionOpen, setAddMentionOpen] = useState(false);
  const [addTimelineOpen, setAddTimelineOpen] = useState(false);
  const [addingFBPage, setAddingFBPage] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [bulkReplyTarget, setBulkReplyTarget] = useState(null); // { answerFilter, targetCount, sampleUsername }
  const [refreshingTermId, setRefreshingTermId] = useState(null);
  const [normalizing, setNormalizing] = useState(false);
  const [normalizeMode, setNormalizeMode] = useState(false);
  const [confirmDeletePollId, setConfirmDeletePollId] = useState(null);
  const [deletingTermId, setDeletingTermId] = useState(null);
  const [editingTermId, setEditingTermId] = useState(null);
  const [collectUrlDraft, setCollectUrlDraft] = useState(collectTweetUrl);
  const [insightsGenerating, setInsightsGenerating] = useState(false);
  useEffect(() => {
    if (collectionInsights) setInsightsGenerating(false);
  }, [collectionInsights]);
  useEffect(() => {
    setInsightsGenerating(false);
  }, [selectedCollectionId]);
  const [leadsPage, setLeadsPage] = useState(1);
  const [answersPage, setAnswersPage] = useState(1);
  const [sortBy, setSortBy] = useState("recent");
  const [showAllDistribution, setShowAllDistribution] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [donorLinkTarget, setDonorLinkTarget] = useState(null);
  const [taggedAnswers, setTaggedAnswers] = useState({}); // answer → audienceId
  const [showLeadsList, setShowLeadsList] = useState(false);
  const [pollFilterAnswers, setPollFilterAnswers] = useState(new Set());
  const [pollFilterConf, setPollFilterConf] = useState(new Set());
  const [pollFilterLocation, setPollFilterLocation] = useState("");
  const [collectSearch, setCollectSearch] = useState("");
  const [showPollFromPostModal, setShowPollFromPostModal] = useState(false);
  const [collectReplyAllOpen, setCollectReplyAllOpen] = useState(false);
  const [collectReplyAllJob, setCollectReplyAllJob] = useState(null); // { jobId, total, sent, failed, status }
  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const POST_TRUNCATE = 280;
  const togglePostExpand = (id) =>
    setExpandedPosts((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const LEADS_PER_PAGE = 20;
  const isFBCollect =
    collectResult?.platform === "facebook" ||
    /facebook\.com/i.test(collectUrlDraft);

  // Sync URL draft when parent sets it (e.g. from "Collect" card button)
  useEffect(() => {
    setCollectUrlDraft(collectTweetUrl);
  }, [collectTweetUrl]);

  // Effective search terms for highlighting — use selected term's query, or all terms combined, or candidate name
  const selectedTermObj = selectedTerm
    ? terms.find((t) => t.id === selectedTerm)
    : null;
  const effectiveSearchQuery =
    selectedTermObj?.query ||
    (terms.length > 0 ? terms.map((t) => t.query).join(" OR ") : null) ||
    searchQuery ||
    (cfg.candidateName ? `"${cfg.candidateName}"` : "");
  const FILTERS = [
    { key: "all", label: "All" },
    { key: "replies", label: "Replies" },
    { key: "quotes", label: "Quotes" },
    { key: "mentions", label: "Mentions" },
  ];

  // ── Client-side filtering + sorting + aggregates ────────────────────────────
  const filteredMentions = useMemo(() => {
    let rows = allMentions || [];
    if (selectedTerm) rows = rows.filter((m) => m.term_id === selectedTerm);
    if (filter === "replies")
      rows = rows.filter((m) => m.referenced_tweet_type === "replied_to");
    else if (filter === "quotes")
      rows = rows.filter((m) => m.referenced_tweet_type === "quoted");
    else if (filter === "mentions")
      rows = rows.filter((m) => !m.referenced_tweet_type);
    if (authorFilter) rows = rows.filter((m) => m.author_id === authorFilter);
    return rows;
  }, [allMentions, filter, authorFilter, selectedTerm]);

  const sortedMentions = useMemo(() => {
    const rows = [...filteredMentions];
    if (sortBy === "likes")
      rows.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    else if (sortBy === "replies")
      rows.sort((a, b) => (b.replies || 0) - (a.replies || 0));
    return rows;
  }, [filteredMentions, sortBy]);

  const totalPages = Math.ceil(sortedMentions.length / PAGE_SIZE);
  const pageMentions = sortedMentions.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const breakdown = useMemo(() => {
    const base = selectedTerm
      ? (allMentions || []).filter((m) => m.term_id === selectedTerm)
      : allMentions || [];
    return {
      replies: base.filter((m) => m.referenced_tweet_type === "replied_to")
        .length,
      quotes: base.filter((m) => m.referenced_tweet_type === "quoted").length,
      mentions: base.filter((m) => !m.referenced_tweet_type).length,
    };
  }, [allMentions, selectedTerm]);

  const topAccounts = useMemo(() => {
    const map = {};
    (allMentions || []).forEach((m) => {
      if (!m.author_id) return;
      if (
        !map[m.author_id] ||
        m.author_followers > map[m.author_id].author_followers
      ) {
        map[m.author_id] = {
          author_id: m.author_id,
          author_username: m.author_username,
          author_name: m.author_name,
          author_followers: m.author_followers || 0,
        };
      }
    });
    return Object.values(map)
      .sort((a, b) => b.author_followers - a.author_followers)
      .slice(0, 10);
  }, [allMentions]);

  const totalAll = (allMentions || []).length;
  const total = selectedTerm
    ? (allMentions || []).filter((m) => m.term_id === selectedTerm).length
    : totalAll;

  const handleFilterChange = (f) => onFilterChange(f);
  const handleAuthorChange = (id) => onAuthorChange(id);

  // ── Intelligence panel (left column) ────────────────────────────────────────
  if (section === "intelligence") {
    const repliesCount = breakdown.replies;
    const quotesCount = breakdown.quotes;
    const mentionsCount = breakdown.mentions;
    const accounts = topAccounts;

    return (
      <Box>
        {/* Header */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.09em",
            }}
          >
            Social Media
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
            {cfg.candidateName || "Campaign"}
            {mode === "monitor" && lastSynced && (
              <>
                {" "}
                ·{" "}
                <Box
                  component="span"
                  sx={{ color: "#22c55e", fontWeight: 600 }}
                >
                  Synced {fmtRelTime(lastSynced)} ago
                </Box>
              </>
            )}
          </Typography>
        </Box>

        {/* Mode toggle */}
        <Box
          sx={{
            display: "inline-flex",
            border: `1px solid ${C.border}`,
            borderRadius: 1,
            mb: 2,
            overflow: "hidden",
          }}
        >
          {[
            { key: "monitor", label: "Monitor" },
            { key: "collect", label: "Collect" },
            { key: "audiences", label: "Audiences" },
            { key: "discovery", label: "Discover" },
            { key: "topics", label: "Topic Research" },
          ].map(({ key, label }) => (
            <Box
              key={key}
              component="button"
              onClick={() => onModeChange(key)}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                px: 2,
                py: 0.75,
                background: mode === key ? C.navy : C.white,
                color: mode === key ? "#fff" : C.muted,
                border: "none",
                cursor: "pointer",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        {!SOCIAL_API_URL && (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              border: `1px dashed ${C.border}`,
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.5 }}
            >
              Social API not configured
            </Typography>
            <Typography sx={{ fontSize: 10, color: C.muted }}>
              Set REACT_APP_SOCIAL_API_URL in .env and redeploy.
            </Typography>
          </Box>
        )}

        {SOCIAL_API_URL && mode === "discovery" && (
          <AudienceDiscovery campaignId={activeCampaignId} />
        )}

        {SOCIAL_API_URL && mode === "topics" && (
          <AudienceDiscovery campaignId={activeCampaignId} topicMode />
        )}

        {SOCIAL_API_URL && mode === "monitor" && (
          <>
            {/* Monitor terms manager */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.charcoal,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Monitored Terms
                </Typography>
                <Box
                  component="button"
                  onClick={() => onSync()}
                  disabled={syncing || terms.length === 0}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    px: 1.25,
                    py: 0.35,
                    bgcolor: C.white,
                    color: syncing || terms.length === 0 ? C.muted : C.navy,
                    border: `1px solid ${syncing || terms.length === 0 ? C.border : C.navy + "66"}`,
                    borderRadius: "5px",
                    cursor:
                      syncing || terms.length === 0 ? "not-allowed" : "pointer",
                    "&:hover": { bgcolor: C.navy + "08" },
                  }}
                >
                  {syncing ? (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          animation: "spin 0.7s linear infinite",
                          "@keyframes spin": {
                            "0%": { transform: "rotate(0deg)" },
                            "100%": { transform: "rotate(360deg)" },
                          },
                        }}
                      >
                        ↻
                      </Box>{" "}
                      Syncing…
                    </Box>
                  ) : (
                    "↻ Sync Now"
                  )}
                </Box>
              </Box>

              {syncResult && (
                <Typography
                  sx={{
                    fontSize: 10,
                    color: "#22c55e",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  +{syncResult.inserted} new · {syncResult.total_fetched}{" "}
                  fetched
                  {syncResult.terms?.length > 1 &&
                    ` across ${syncResult.terms.length} terms`}
                </Typography>
              )}

              {/* Add monitor CTAs */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 0.75,
                  mb: 0.75,
                }}
              >
                <Box
                  component="button"
                  onClick={() => setAddMentionOpen(true)}
                  sx={{
                    py: 1.25,
                    px: 1.25,
                    border: `1px dashed ${C.border}`,
                    borderRadius: "8px",
                    bgcolor: C.white,
                    cursor: "pointer",
                    textAlign: "left",
                    "&:hover": {
                      borderColor: C.navy + "88",
                      bgcolor: C.navy + "04",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: C.navy,
                      mb: 0.2,
                    }}
                  >
                    + Monitor Term
                  </Typography>
                  <Typography
                    sx={{ fontSize: 9, color: C.muted, lineHeight: 1.4 }}
                  >
                    Track a keyword, phrase or handle
                  </Typography>
                </Box>
                <Box
                  component="button"
                  onClick={() => setAddTimelineOpen(true)}
                  sx={{
                    py: 1.25,
                    px: 1.25,
                    border: `1px dashed ${C.green + "66"}`,
                    borderRadius: "8px",
                    bgcolor: C.white,
                    cursor: "pointer",
                    textAlign: "left",
                    "&:hover": {
                      borderColor: C.green,
                      bgcolor: C.green + "05",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: C.green,
                      mb: 0.2,
                    }}
                  >
                    + Monitor Account
                  </Typography>
                  <Typography
                    sx={{ fontSize: 9, color: C.muted, lineHeight: 1.4 }}
                  >
                    Track an account's own posts
                  </Typography>
                </Box>
              </Box>
              <Box
                component="button"
                disabled={addingFBPage || syncing}
                onClick={async () => {
                  setAddingFBPage(true);
                  await onSync("facebook");
                  setAddingFBPage(false);
                }}
                sx={{
                  width: "100%",
                  mb: 1.5,
                  py: 1,
                  px: 1.25,
                  border: "1px dashed #1877f244",
                  borderRadius: "8px",
                  bgcolor: C.white,
                  cursor: addingFBPage || syncing ? "not-allowed" : "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": { borderColor: "#1877f2aa", bgcolor: "#1877f205" },
                }}
              >
                {addingFBPage ? (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 14,
                      color: "#1877f2",
                      flexShrink: 0,
                      display: "inline-block",
                      animation: "spin 0.7s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  >
                    ↻
                  </Box>
                ) : (
                  <FontAwesomeIcon
                    icon={faFacebook}
                    style={{ fontSize: 14, color: "#1877f2", flexShrink: 0 }}
                  />
                )}
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: "#1877f2",
                      mb: 0.1,
                    }}
                  >
                    {addingFBPage
                      ? "Syncing Facebook Page..."
                      : "+ Monitor Facebook Page"}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 9, color: C.muted, lineHeight: 1.4 }}
                  >
                    Adds your connected Facebook Page — posts sync in the
                    background
                  </Typography>
                </Box>
              </Box>

              {termsLoading ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  {[1, 2, 3].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        height: 44,
                        bgcolor: C.border,
                        borderRadius: "8px",
                        opacity: 0.25 + i * 0.1,
                      }}
                    />
                  ))}
                </Box>
              ) : terms.length === 0 ? (
                <Box
                  sx={{
                    py: 3,
                    textAlign: "center",
                    border: `1px dashed ${C.border}`,
                    borderRadius: "8px",
                  }}
                >
                  <Typography sx={{ fontSize: 11, color: C.muted }}>
                    No monitored terms yet.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  {terms.map((t) => {
                    const isActive = selectedTerm === t.id;
                    const isTimeline = t.monitor_type === "timeline";
                    const isPage = t.monitor_type === "page";
                    const isFacebook = t.platform === "facebook";
                    const accent = isFacebook
                      ? "#1877f2"
                      : isTimeline
                        ? C.green
                        : C.navy;
                    const termIcon = isFacebook ? faFacebook : faXTwitter;
                    return (
                      <Box
                        key={t.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          p: "10px",
                          border: `1px solid ${isActive ? accent + "44" : C.border}`,
                          bgcolor: isActive ? accent + "10" : C.white,
                          borderRadius: "4px",
                          "&:hover": {
                            borderColor: isActive ? accent + "44" : C.muted,
                          },
                        }}
                      >
                        {/* Icon */}
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            bgcolor: isActive ? accent : "#f0f4f8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={termIcon}
                            style={{
                              fontSize: 10,
                              color: isActive ? "#fff" : C.charcoal,
                            }}
                          />
                        </Box>
                        {/* Query — inline edit or clickable label */}
                        {editingTermId === t.id ? (
                          <InlineTermEdit
                            term={t}
                            accent={accent}
                            onSave={async (q) => {
                              await onUpdateTerm(t.id, q);
                              setEditingTermId(null);
                            }}
                            onCancel={() => setEditingTermId(null)}
                          />
                        ) : (
                          <>
                            <Box
                              component="button"
                              onClick={() => {
                                onSelectTerm(isActive ? null : t.id);
                                onPageChange(1);
                              }}
                              sx={{
                                flex: 1,
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                textAlign: "left",
                                minWidth: 0,
                                p: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 10.5,
                                  fontWeight: isActive ? 700 : 600,
                                  color: isActive ? accent : C.charcoal,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t.query}
                              </Typography>
                            </Box>
                            {/* Type badge + count — right-aligned, cohort-badge style */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  px: 1,
                                  py: 0.3,
                                  borderRadius: "3px",
                                  bgcolor: accent + "18",
                                  border: `1px solid ${accent}44`,
                                  color: accent,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {isPage
                                  ? "Page"
                                  : isTimeline
                                    ? "Timeline"
                                    : "Mention"}
                              </Box>
                              {t.post_count != null && (
                                <Typography
                                  sx={{
                                    fontSize: 9.5,
                                    color: C.muted,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {Number(t.post_count).toLocaleString()}
                                </Typography>
                              )}
                              {t.last_synced_at && (
                                <Typography
                                  sx={{
                                    fontSize: 9,
                                    color: C.muted,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  · {fmtRelTime(t.last_synced_at)}
                                </Typography>
                              )}
                            </Box>
                          </>
                        )}
                        {/* Actions */}
                        {editingTermId !== t.id && (
                          <Box
                            sx={{ display: "flex", gap: 0.35, flexShrink: 0 }}
                          >
                            <Box
                              component="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTermId(t.id);
                              }}
                              title="Edit query"
                              sx={{
                                width: 26,
                                height: 26,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "transparent",
                                border: `1px solid ${C.border}`,
                                borderRadius: "5px",
                                cursor: "pointer",
                                color: C.muted,
                                fontSize: 11,
                                "&:hover": {
                                  bgcolor: C.navy + "08",
                                  borderColor: C.navy,
                                  color: C.navy,
                                },
                              }}
                            >
                              ✎
                            </Box>
                            <Box
                              component="button"
                              disabled={refreshingTermId === t.id}
                              onClick={async (e) => {
                                e.stopPropagation();
                                setRefreshingTermId(t.id);
                                await onRefreshTerm(t.id);
                                setRefreshingTermId(null);
                              }}
                              title="Re-sync now"
                              sx={{
                                width: 26,
                                height: 26,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "transparent",
                                border: `1px solid ${C.border}`,
                                borderRadius: "5px",
                                cursor:
                                  refreshingTermId === t.id
                                    ? "not-allowed"
                                    : "pointer",
                                color:
                                  refreshingTermId === t.id ? C.muted : C.navy,
                                fontSize: 13,
                                "&:hover": {
                                  bgcolor: C.navy + "10",
                                  borderColor: C.navy,
                                },
                              }}
                            >
                              {refreshingTermId === t.id ? (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-block",
                                    animation: "spin 0.7s linear infinite",
                                    "@keyframes spin": {
                                      "0%": { transform: "rotate(0deg)" },
                                      "100%": { transform: "rotate(360deg)" },
                                    },
                                  }}
                                >
                                  ↻
                                </Box>
                              ) : (
                                "↻"
                              )}
                            </Box>
                            <Box
                              component="button"
                              disabled={deletingTermId === t.id}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (
                                  !window.confirm(
                                    `Remove "${t.query}" and all its stored posts?`,
                                  )
                                )
                                  return;
                                setDeletingTermId(t.id);
                                await onDeleteTerm(t.id);
                                setDeletingTermId(null);
                              }}
                              title="Remove"
                              sx={{
                                width: 26,
                                height: 26,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "transparent",
                                border: `1px solid ${C.border}`,
                                borderRadius: "5px",
                                cursor:
                                  deletingTermId === t.id
                                    ? "not-allowed"
                                    : "pointer",
                                color:
                                  deletingTermId === t.id ? C.muted : C.muted,
                                fontSize: 15,
                                lineHeight: 1,
                                "&:hover": {
                                  bgcolor:
                                    deletingTermId === t.id
                                      ? "transparent"
                                      : "#fef2f2",
                                  borderColor:
                                    deletingTermId === t.id
                                      ? C.border
                                      : "#fca5a5",
                                  color:
                                    deletingTermId === t.id
                                      ? C.muted
                                      : "#b91c1c",
                                },
                              }}
                            >
                              {deletingTermId === t.id ? (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 11,
                                    display: "inline-block",
                                    animation: "spin 0.7s linear infinite",
                                    "@keyframes spin": {
                                      "0%": { transform: "rotate(0deg)" },
                                      "100%": { transform: "rotate(360deg)" },
                                    },
                                  }}
                                >
                                  ↻
                                </Box>
                              ) : (
                                "×"
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {error && (
              <Box
                sx={{
                  mb: 2,
                  px: 2,
                  py: 1.5,
                  bgcolor: "#fef2f2",
                  border: `1px solid #fca5a5`,
                  borderRadius: 1,
                }}
              >
                <Typography sx={{ fontSize: 10, color: "#b91c1c" }}>
                  {error}
                </Typography>
              </Box>
            )}

            {/* Volume stat */}
            <Box
              sx={{
                mb: 2,
                px: 2,
                py: 1.5,
                bgcolor: "#f0f4f8",
                border: `1px solid ${C.border}`,
                borderRadius: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: C.navy,
                  lineHeight: 1,
                }}
              >
                {total.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
                {selectedTerm
                  ? `posts for "${terms.find((t) => t.id === selectedTerm)?.query || ""}"`
                  : "total posts stored"}
                {selectedTerm && (
                  <Box
                    component="span"
                    sx={{ ml: 0.75, color: C.muted, fontWeight: 400 }}
                  >
                    / {totalAll.toLocaleString()} total
                  </Box>
                )}
              </Typography>
            </Box>

            {/* Type breakdown */}
            {total > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.charcoal,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Breakdown · all posts
                </Typography>
                {[
                  {
                    key: "replies",
                    label: "Replies",
                    count: repliesCount,
                    color: C.blue,
                  },
                  {
                    key: "quotes",
                    label: "Quotes",
                    count: quotesCount,
                    color: C.orange,
                  },
                  {
                    key: "mentions",
                    label: "Mentions",
                    count: mentionsCount,
                    color: C.navy,
                  },
                ].map(({ key, label, count, color }) => (
                  <Box
                    key={key}
                    component="button"
                    onClick={() =>
                      handleFilterChange(filter === key ? "all" : key)
                    }
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.5,
                      px: 0.75,
                      width: "100%",
                      background: filter === key ? color + "12" : "none",
                      border: `1px solid ${filter === key ? color + "44" : "transparent"}`,
                      borderRadius: 1,
                      cursor: "pointer",
                      mb: 0.25,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: color,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          color: filter === key ? color : C.charcoal,
                          fontWeight: filter === key ? 700 : 400,
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: filter === key ? color : C.charcoal,
                      }}
                    >
                      {count.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Top accounts by reach — clickable to filter */}
            {accounts.length > 0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.charcoal,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Top Accounts by Reach
                  </Typography>
                  {authorFilter && (
                    <Box
                      component="button"
                      onClick={() => handleAuthorChange(null)}
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        px: 0.75,
                        py: 0.2,
                        bgcolor: C.navy + "12",
                        border: `1px solid ${C.navy}44`,
                        borderRadius: "3px",
                        color: C.navy,
                        cursor: "pointer",
                      }}
                    >
                      Clear ×
                    </Box>
                  )}
                </Box>
                {accounts.map((m, i) => {
                  const isActive = authorFilter === m.author_id;
                  const followers =
                    m.author_followers >= 1000
                      ? `${(m.author_followers / 1000).toFixed(1)}K`
                      : m.author_followers || 0;
                  return (
                    <Box
                      key={m.author_id || i}
                      component="button"
                      onClick={() =>
                        handleAuthorChange(isActive ? null : m.author_id)
                      }
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 0.625,
                        px: 0.75,
                        width: "100%",
                        background: isActive ? C.navy + "10" : "none",
                        border: `1px solid ${isActive ? C.navy + "44" : "transparent"}`,
                        borderRadius: 1,
                        cursor: "pointer",
                        mb: 0.25,
                        textAlign: "left",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            fontWeight: isActive ? 700 : 600,
                            color: isActive ? C.navy : C.charcoal,
                            lineHeight: 1.2,
                          }}
                        >
                          {m.author_name || m.author_username}
                        </Typography>
                        {m.author_username && (
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              color: isActive ? C.navy : C.muted,
                            }}
                          >
                            @{m.author_username}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: isActive ? C.navy : C.muted,
                          flexShrink: 0,
                          ml: 1,
                        }}
                      >
                        {followers}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {SOCIAL_API_URL && mode === "collect" && (
          <Box>
            <Typography sx={{ fontSize: 10, color: C.muted, mb: 2 }}>
              Paste a tweet URL to browse its replies. To run a question, create
              an audience from the replies.
            </Typography>

            {error && (
              <Box
                sx={{
                  mb: 2,
                  px: 2,
                  py: 1.5,
                  bgcolor: "#fef2f2",
                  border: `1px solid #fca5a5`,
                  borderRadius: 1,
                }}
              >
                <Typography sx={{ fontSize: 10, color: "#b91c1c" }}>
                  {error}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.charcoal,
                  mb: 0.5,
                }}
              >
                Tweet URL
              </Typography>
              <input
                value={collectUrlDraft}
                onChange={(e) => setCollectUrlDraft(e.target.value)}
                onBlur={(e) => onCollectTweetUrlChange(e.target.value)}
                placeholder="https://x.com/user/status/..."
                style={{
                  width: "100%",
                  fontSize: 11,
                  padding: "6px 12px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  color: C.charcoal,
                  outline: "none",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              disabled={collecting}
              onClick={() => onCollect(collectUrlDraft)}
              sx={{
                bgcolor: isFBCollect ? "#1877f2" : "#0a2240",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "6px",
                py: 0.75,
                "&:hover": { bgcolor: isFBCollect ? "#1464cc" : "#061828" },
                "&.Mui-disabled": { bgcolor: C.border, color: C.muted },
              }}
            >
              {collecting
                ? isFBCollect
                  ? "Loading comments…"
                  : "Loading replies…"
                : isFBCollect
                  ? "Browse Comments"
                  : "Browse Replies"}
            </Button>

            {leads.length > 0 && (
              <>
                <Box
                  sx={{
                    mt: 2,
                    px: 2,
                    py: 1.5,
                    bgcolor: "#f0f4f8",
                    border: `1px solid ${C.border}`,
                    borderRadius: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {(() => {
                    const matchTw =
                      collectProgress &&
                      collectProgress.tweetId ===
                        collectResult?.source_tweet_id;
                    const liveCount = matchTw
                      ? Math.max(leads.length, collectProgress.collected || 0)
                      : leads.length;
                    const busy = matchTw && !collectProgress.done;
                    return (
                      <>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 22,
                              fontWeight: 800,
                              color: C.navy,
                              lineHeight: 1,
                            }}
                          >
                            {liveCount.toLocaleString()}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: busy ? C.navy : C.muted,
                              mt: 0.25,
                            }}
                          >
                            {busy
                              ? "replies collected · still loading…"
                              : "replies collected"}
                          </Typography>
                        </Box>
                        {collectResult?.source_tweet?.public_metrics
                          ?.reply_count > liveCount && (
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              color: C.muted,
                              textAlign: "right",
                              maxWidth: 130,
                            }}
                          >
                            {collectResult.source_tweet.public_metrics.reply_count.toLocaleString()}{" "}
                            reported by X<br />
                            <Box component="span" sx={{ color: C.charcoal }}>
                              {busy ? "collecting more…" : "recent-window set"}
                            </Box>
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Box>
                <input
                  value={collectSearch}
                  onChange={(e) => {
                    setCollectSearch(e.target.value);
                    setLeadsPage(1);
                  }}
                  placeholder="Search replies, @handles…"
                  style={{
                    width: "100%",
                    fontSize: 11,
                    padding: "6px 12px",
                    border: `1px solid ${C.border}`,
                    borderRadius: 4,
                    color: C.charcoal,
                    outline: "none",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    marginTop: 10,
                  }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setShowPollFromPostModal(true)}
                  sx={{
                    mt: 1.25,
                    bgcolor: C.navy,
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "6px",
                    py: 0.75,
                    "&:hover": { bgcolor: "#0a2240" },
                  }}
                >
                  {isFBCollect ? "Poll These Comments" : "Poll These Replies"}
                </Button>
                <Box sx={{ textAlign: "center", mt: 1 }}>
                  <Box
                    component="button"
                    onClick={() => {
                      const srcAuthor =
                        collectResult?.source_tweet?.author?.username;
                      onCreateAudienceFromTweet(
                        collectUrlDraft || collectTweetUrl,
                        srcAuthor,
                      );
                    }}
                    sx={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 10.5,
                      color: C.muted,
                      textDecoration: "underline",
                      "&:hover": { color: C.navy },
                    }}
                  >
                    Save as audience instead
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}

        {SOCIAL_API_URL && mode === "audiences" && (
          <AudienceIntelPanel
            audiences={audiences}
            audiencesLoading={audiencesLoading}
            selectedAudienceId={selectedAudienceId}
            onSelectAudience={onSelectAudience}
            collections={collections}
            selectedCollectionId={selectedCollectionId}
            onSelectCollection={onSelectCollection}
            polling={polling}
            pollProgress={pollProgress}
            onCreateAudience={onCreateAudience}
            onRefreshAudience={onRefreshAudience}
            onPollAudience={onPollAudience}
            error={error}
            audiencePreFill={audiencePreFill}
            onClearAudiencePreFill={onClearAudiencePreFill}
            terms={terms}
            onCollectAll={onCollectAll}
            collectingAllTermId={collectingAllTermId}
            collectAllProgress={collectAllProgress}
            onPollRequest={() => setShowPollModal(true)}
            onDeleteCollection={onDeleteCollection}
            onDeleteAudience={onDeleteAudience}
          />
        )}

        <AddTermModal
          open={addMentionOpen}
          type="mention"
          onClose={() => setAddMentionOpen(false)}
          onAdd={onAddTerm}
          C={C}
        />
        <AddTermModal
          open={addTimelineOpen}
          type="timeline"
          onClose={() => setAddTimelineOpen(false)}
          onAdd={onAddTerm}
          C={C}
        />
        <PollAudienceModal
          open={showPollModal}
          audience={audiences.find((a) => a.id === selectedAudienceId) || null}
          onClose={() => setShowPollModal(false)}
          onPoll={onPollAudience}
          polling={polling}
          pollProgress={pollProgress}
          C={C}
        />
        {/* Poll from post — pre-populates question with tweet body, no audience selection required */}
        <PollAudienceModal
          open={showPollFromPostModal}
          audience={{
            id: null,
            name: isFBCollect
              ? `Comments on this post`
              : `Replies to this post`,
            member_count: leads.length,
          }}
          defaultQuestion={
            collectResult?.source_tweet?.tweet?.text ||
            collectResult?.source_tweet?.text ||
            collectResult?.tweet_text ||
            ""
          }
          onClose={() => setShowPollFromPostModal(false)}
          onPoll={(_, question, options) =>
            onPollFromPost(
              collectUrlDraft || collectTweetUrl,
              question,
              options,
            )
          }
          polling={polling}
          pollProgress={pollProgress}
          C={C}
        />
        <BulkReplyModal
          open={!!bulkReplyTarget}
          answerFilter={bulkReplyTarget?.answerFilter || ""}
          targetCount={bulkReplyTarget?.targetCount || 0}
          collectionId={selectedCollectionId}
          sampleUsername={bulkReplyTarget?.sampleUsername}
          onClose={() => setBulkReplyTarget(null)}
          onSubmit={onBulkReply}
          activeJob={
            bulkReplyJob &&
            bulkReplyTarget &&
            bulkReplyJob.answerFilter === bulkReplyTarget.answerFilter
              ? bulkReplyJob
              : null
          }
          C={C}
        />
      </Box>
    );
  }

  // ── Feed panel (right column) ────────────────────────────────────────────────
  const activeAccount = authorFilter
    ? topAccounts.find((a) => a.author_id === authorFilter)
    : null;

  // CSV export for leads
  const exportLeadsCsv = () => {
    const headers = [
      "Name",
      "Handle",
      "Extracted Answer",
      "Reply Text",
      "Confidence",
    ];
    const rows = leads.map((l) => [
      l.author_name || "",
      l.author_username ? `@${l.author_username}` : "",
      l.extracted_answer || "",
      (l.reply_text || "").replace(/"/g, '""'),
      l.confidence || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${c}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Audiences mode — collection answers table ───────────────────────────────
  if (mode === "audiences") {
    const audience = audiences.find((a) => a.id === selectedAudienceId) || null;
    const collection =
      collections.find((c) => c.id === selectedCollectionId) || null;

    if (!selectedAudienceId || !audience) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            border: `1px dashed ${C.border}`,
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.5 }}
          >
            Select an audience
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.muted }}>
            Choose or create an audience in the panel on the left.
          </Typography>
        </Box>
      );
    }

    if (!selectedCollectionId || !collection) {
      const donorMatches = donorLinks.filter(
        (l) => l.match_type === "donor_match",
      ).length;
      const newLeads = donorLinks.filter(
        (l) => l.match_type === "new_lead",
      ).length;
      const zipsFound = donorLinks.filter((l) => l.zip_code).length;
      return (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: C.navy,
                  textTransform: "uppercase",
                  letterSpacing: "0.09em",
                }}
              >
                {audience.name}
              </Typography>
              <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
                {Number(audience.member_count || 0).toLocaleString()}{" "}
                {audience.member_count === 1 ? "member" : "members"}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => setShowPollModal(true)}
              sx={{
                bgcolor: "#22c55e",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                py: 0.75,
                px: 2.5,
                textTransform: "none",
                borderRadius: "6px",
                flexShrink: 0,
                "&:hover": { bgcolor: "#16a34a" },
              }}
            >
              + Poll Audience
            </Button>
          </Box>

          {/* Identity Enrichment */}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: `1px solid ${C.border}`,
              borderRadius: "8px",
              bgcolor: "#f8fafc",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: enriching || donorLinks.length > 0 ? 1 : 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.charcoal,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Identity Enrichment
              </Typography>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Box
                  component="button"
                  disabled={locating}
                  onClick={() => onEnrichLocations?.(audience.id)}
                  title="Geocode X profile locations to zip codes"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.25,
                    py: 0.5,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: "transparent",
                    color: locating ? C.muted : C.charcoal,
                    border: `1px solid ${C.border}`,
                    borderRadius: "5px",
                    cursor: locating ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                    "&:hover:not(:disabled)": {
                      borderColor: C.navy,
                      color: C.navy,
                    },
                  }}
                >
                  {locating && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        border: "1.5px solid currentColor",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "spin 0.7s linear infinite",
                        "@keyframes spin": {
                          to: { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  )}
                  {locating ? "Locating…" : "Locate Members"}
                </Box>
                <Box
                  component="button"
                  disabled={enriching}
                  onClick={() => onEnrichIdentity?.(audience.id)}
                  title="Match X users to existing donors via PDL enrichment"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.25,
                    py: 0.5,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: enriching ? C.border : C.navy,
                    color: enriching ? C.muted : "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: enriching ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {enriching && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        border: "1.5px solid currentColor",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "spin 0.7s linear infinite",
                      }}
                    />
                  )}
                  {enriching ? "Matching…" : "Match Donors"}
                </Box>
              </Box>
            </Box>
            {enriching && (
              <Box>
                <Box
                  sx={{
                    height: 3,
                    borderRadius: 2,
                    bgcolor: C.border,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      bgcolor: C.navy,
                      width: enrichProgress
                        ? `${Math.round((enrichProgress.processed / enrichProgress.total) * 100)}%`
                        : "5%",
                      transition: "width 0.4s ease",
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.4 }}>
                  {enrichProgress
                    ? `${enrichProgress.processed} / ${enrichProgress.total} checked · ${enrichProgress.matched} identified · ${enrichProgress.enriched} total enriched`
                    : "Starting…"}
                </Typography>
              </Box>
            )}
            {!enriching && donorLinks.length > 0 && (
              <Box>
                <Box
                  onClick={() => setShowLeadsList((v) => !v)}
                  sx={{
                    display: "flex",
                    gap: 2.5,
                    cursor: "pointer",
                    alignItems: "flex-end",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 800,
                        color: C.navy,
                        lineHeight: 1,
                      }}
                    >
                      {donorMatches}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.2 }}>
                      donor matches
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 800,
                        color: "#22c55e",
                        lineHeight: 1,
                      }}
                    >
                      {newLeads}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.2 }}>
                      new leads
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 800,
                        color: C.charcoal,
                        lineHeight: 1,
                      }}
                    >
                      {zipsFound}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.2 }}>
                      zips identified
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontSize: 9, color: C.muted, ml: "auto", mb: 0.25 }}
                  >
                    {showLeadsList ? "▲ hide" : "▼ view all"}
                  </Typography>
                </Box>
                {showLeadsList && (
                  <Box
                    sx={{
                      mt: 1.5,
                      border: `1px solid ${C.border}`,
                      borderRadius: "6px",
                      overflow: "hidden",
                    }}
                  >
                    {/* header */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 90px 110px 80px 70px",
                        gap: 0,
                        px: 1.5,
                        py: 0.75,
                        bgcolor: "#f1f5f9",
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {["Handle", "Match", "Email", "Company", "DISC"].map(
                        (h) => (
                          <Typography
                            key={h}
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: C.muted,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                            }}
                          >
                            {h}
                          </Typography>
                        ),
                      )}
                    </Box>
                    {donorLinks.map((link) => {
                      const enc = (() => {
                        try {
                          return typeof link.enrichment_data === "string"
                            ? JSON.parse(link.enrichment_data)
                            : link.enrichment_data || {};
                        } catch {
                          return {};
                        }
                      })();
                      const disc = enc.disc || {};
                      const isMatch = link.match_type === "donor_match";
                      return (
                        <Box
                          key={link.id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 90px 110px 80px 70px",
                            gap: 0,
                            px: 1.5,
                            py: 1,
                            borderBottom: `1px solid ${C.border}`,
                            "&:last-child": { borderBottom: "none" },
                            alignItems: "center",
                            "&:hover": { bgcolor: "#f8fafc" },
                          }}
                        >
                          {/* Handle + name */}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: C.navy,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {link.author_name || link.author_username || "—"}
                            </Typography>
                            {link.author_username && (
                              <Typography sx={{ fontSize: 9, color: C.muted }}>
                                @{link.author_username}
                              </Typography>
                            )}
                            {link.zip_code && (
                              <Typography sx={{ fontSize: 9, color: C.muted }}>
                                {link.zip_code}
                              </Typography>
                            )}
                          </Box>
                          {/* Match type badge */}
                          <Box>
                            <Box
                              sx={{
                                display: "inline-block",
                                px: 0.75,
                                py: 0.25,
                                borderRadius: "4px",
                                bgcolor: isMatch ? "#dbeafe" : "#dcfce7",
                                fontSize: 8.5,
                                fontWeight: 700,
                                color: isMatch ? "#1d4ed8" : "#15803d",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {isMatch ? "donor match" : "new lead"}
                            </Box>
                          </Box>
                          {/* Email */}
                          <Box sx={{ minWidth: 0 }}>
                            {link.donor_email ? (
                              <Typography
                                sx={{
                                  fontSize: 9,
                                  color: C.charcoal,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={link.donor_email}
                              >
                                {link.donor_email}
                              </Typography>
                            ) : (
                              <Typography sx={{ fontSize: 9, color: C.muted }}>
                                —
                              </Typography>
                            )}
                          </Box>
                          {/* Company */}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 9,
                                color: C.charcoal,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={enc.pdl_company || ""}
                            >
                              {enc.pdl_company || "—"}
                            </Typography>
                          </Box>
                          {/* DISC */}
                          <Box>
                            {disc.disc_type ? (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.4,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: disc.disc_color || "#94a3b8",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    color: C.charcoal,
                                  }}
                                >
                                  {disc.disc_type}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: 9, color: C.muted }}>
                                —
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
            {!enriching && donorLinks.length === 0 && (
              <Typography
                sx={{ fontSize: 9.5, color: C.muted, lineHeight: 1.6 }}
              >
                Locate zip codes from X profiles, then match audience members to
                existing donors or create new leads.
              </Typography>
            )}
          </Box>
          {collections.length > 0 || polling ? (
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.charcoal,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  mb: 1,
                }}
              >
                Past Polls
              </Typography>
              {polling && (
                <Box
                  sx={{
                    border: `1px solid ${C.green}`,
                    borderRadius: "8px",
                    p: 1.5,
                    mb: 1.25,
                    bgcolor: C.green + "0a",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.charcoal,
                      mb: 0.75,
                    }}
                  >
                    {!pollProgress || pollProgress.phase === "building"
                      ? "Building audience…"
                      : pollProgress.processed === 0
                        ? `Polling ${pollProgress.total ? pollProgress.total.toLocaleString() + " members" : "members"}…`
                        : `${pollProgress.processed.toLocaleString()} / ${pollProgress.total.toLocaleString()} members analyzed`}
                  </Typography>
                  <Box
                    sx={{
                      height: 5,
                      borderRadius: 3,
                      bgcolor: C.border,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        borderRadius: 3,
                        bgcolor: C.green,
                        width:
                          !pollProgress || pollProgress.phase === "building"
                            ? "5%"
                            : pollProgress.processed === 0
                              ? "10%"
                              : `${Math.max(10, Math.round((pollProgress.processed / pollProgress.total) * 100))}%`,
                        transition: "width 0.4s ease",
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.5 }}>
                    {!pollProgress || pollProgress.phase === "building"
                      ? "Creating audience from collected replies…"
                      : pollProgress.processed === 0
                        ? "Audience ready — classifying replies…"
                        : `${Math.round((pollProgress.processed / pollProgress.total) * 100)}% complete · replies being classified`}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {collections
                  .filter((col) => !(polling && col.answer_count === 0))
                  .map((col) => {
                    const confirming = confirmDeletePollId === col.id;
                    return (
                      <Box
                        key={col.id}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          component="button"
                          onClick={() => onSelectCollection(col.id)}
                          sx={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 1.5,
                            py: 1.25,
                            border: `1px solid ${C.border}`,
                            borderRadius: "6px",
                            bgcolor: C.white,
                            cursor: "pointer",
                            textAlign: "left",
                            minWidth: 0,
                            "&:hover": {
                              borderColor: C.navy,
                              bgcolor: C.navy + "06",
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: C.charcoal,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {col.question || `Poll #${col.id}`}
                            </Typography>
                            {col.collected_at && (
                              <Typography
                                sx={{ fontSize: 9.5, color: C.muted, mt: 0.25 }}
                              >
                                {fmtRelTime(col.collected_at)}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexShrink: 0,
                              ml: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: C.muted,
                              }}
                            >
                              {col.answer_count} responses
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: C.muted }}>
                              ›
                            </Typography>
                          </Box>
                        </Box>
                        {confirming ? (
                          <>
                            <Box
                              component="button"
                              onClick={() => {
                                onDeleteCollection(col.id, selectedAudienceId);
                                setConfirmDeletePollId(null);
                              }}
                              sx={{
                                px: 0.75,
                                py: 0.5,
                                fontSize: 9,
                                fontWeight: 700,
                                bgcolor: "#dc2626",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                            >
                              Delete
                            </Box>
                            <Box
                              component="button"
                              onClick={() => setConfirmDeletePollId(null)}
                              sx={{
                                px: 0.75,
                                py: 0.5,
                                fontSize: 9,
                                fontWeight: 600,
                                bgcolor: "transparent",
                                color: C.muted,
                                border: `1px solid ${C.border}`,
                                borderRadius: "4px",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                            >
                              Cancel
                            </Box>
                          </>
                        ) : (
                          <Box
                            component="button"
                            onClick={() => setConfirmDeletePollId(col.id)}
                            sx={{
                              width: 26,
                              height: 26,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "transparent",
                              border: `1px solid ${C.border}`,
                              borderRadius: "4px",
                              cursor: "pointer",
                              color: C.muted,
                              fontSize: 13,
                              flexShrink: 0,
                              "&:hover": {
                                borderColor: "#dc2626",
                                color: "#dc2626",
                              },
                            }}
                          >
                            ×
                          </Box>
                        )}
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                border: `1px dashed ${C.border}`,
                borderRadius: "8px",
                p: 2.5,
                textAlign: "center",
                bgcolor: C.bg,
              }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: C.navy, mb: 0.75 }}
              >
                No polls yet
              </Typography>
              <Typography
                sx={{ fontSize: 11, color: C.muted, lineHeight: 1.65, mb: 2 }}
              >
                Ask a question to find out what this audience thinks — replies
                are analyzed and grouped automatically.
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => setShowPollModal(true)}
                sx={{
                  bgcolor: C.navy,
                  color: "#fff",
                  textTransform: "none",
                  fontSize: 11,
                  fontWeight: 700,
                  px: 2.5,
                  py: 0.75,
                  borderRadius: "6px",
                  "&:hover": { bgcolor: "#0a2240" },
                }}
              >
                Run First Poll
              </Button>
            </Box>
          )}
          <PollAudienceModal
            open={showPollModal}
            audience={audience}
            onClose={() => setShowPollModal(false)}
            onPoll={onPollAudience}
            polling={polling}
            pollProgress={pollProgress}
            C={C}
          />
        </Box>
      );
    }

    // Answer distribution
    const parsedNormMap = (() => {
      try {
        return collection.normalization_map
          ? JSON.parse(collection.normalization_map)
          : null;
      } catch (_) {
        return null;
      }
    })();
    const hasNormMap = parsedNormMap && Object.keys(parsedNormMap).length > 0;
    const normMap = normalizeMode && hasNormMap ? parsedNormMap : null;
    const answerCounts = {};
    const totalAnswered = collectionAnswers.filter(
      (a) => a.extracted_answer,
    ).length;
    collectionAnswers.forEach((a) => {
      if (!a.extracted_answer) return;
      const key = normMap
        ? (normMap[a.extracted_answer] ?? a.extracted_answer)
        : a.extracted_answer;
      answerCounts[key] = (answerCounts[key] || 0) + 1;
    });
    const sortedAnswers = Object.entries(answerCounts).sort(
      (a, b) => b[1] - a[1],
    );

    const exportAnswersCsv = () => {
      const headers = [
        "Name",
        "Handle",
        "Extracted Answer",
        "Confidence",
        "Reply Text",
      ];
      const rows = collectionAnswers.map((a) => [
        a.author_name || "",
        a.author_username ? `@${a.author_username}` : "",
        a.extracted_answer || "",
        a.confidence || "",
        (a.reply_text || "").replace(/"/g, '""'),
      ]);
      const csv = [headers, ...rows]
        .map((r) => r.map((c) => `"${c}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audience-poll-${collection.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              component="button"
              onClick={() => onSelectCollection(null)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                px: 1,
                py: 0.4,
                bgcolor: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: "5px",
                cursor: "pointer",
                color: C.charcoal,
                fontSize: 10,
                fontWeight: 600,
                "&:hover": {
                  borderColor: C.navy,
                  color: C.navy,
                  bgcolor: C.navy + "06",
                },
              }}
            >
              ← Back to {audience.name}
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                color: C.navy,
                textTransform: "uppercase",
                letterSpacing: "0.09em",
              }}
            >
              {collection.question || "No question"}
            </Typography>
            <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
              {collectionAnswers.length} responses
              {collection.collected_at
                ? ` · ${fmtRelTime(collection.collected_at)}`
                : ""}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
            {collectionAnswers.length > 0 && (
              <Box
                component="button"
                disabled={insightsGenerating}
                onClick={async () => {
                  if (!SOCIAL_API_URL || !collection?.id) return;
                  setInsightsGenerating(true);
                  try {
                    await authFetch(`${SOCIAL_API_URL}/generate-insights`, {
                      method: "POST",
                      body: JSON.stringify({
                        collection_id: collection.id,
                        campaign_id: activeCampaignId,
                      }),
                    });
                  } catch {
                    /* handled by insights-done / error */
                  }
                }}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  px: 1.25,
                  py: 0.5,
                  border: `1px solid ${C.border}`,
                  bgcolor: C.white,
                  color: insightsGenerating ? C.muted : C.charcoal,
                  borderRadius: 1,
                  cursor: insightsGenerating ? "default" : "pointer",
                  opacity: insightsGenerating ? 0.7 : 1,
                }}
              >
                {insightsGenerating
                  ? "Analyzing…"
                  : collectionInsights
                    ? "Re-analyze"
                    : "Analyze"}
              </Box>
            )}
            <Box
              component="button"
              onClick={() => setShowPollModal(true)}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                px: 1.25,
                py: 0.5,
                border: `1px solid ${C.navy}`,
                bgcolor: C.white,
                color: C.navy,
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: C.navy + "08" },
              }}
            >
              New Poll
            </Box>
            {collectionAnswers.length > 0 && (
              <Box
                component="button"
                onClick={exportAnswersCsv}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.5,
                  border: `1px solid ${C.navy}`,
                  bgcolor: C.white,
                  color: C.navy,
                  borderRadius: 1,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Export CSV
              </Box>
            )}
          </Box>
        </Box>

        {/* Distribution bar chart */}
        {sortedAnswers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.charcoal,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Distribution
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {hasNormMap && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <Box
                      component="button"
                      onClick={() => setNormalizeMode(false)}
                      sx={{
                        px: 1,
                        py: 0.3,
                        fontSize: 9,
                        fontWeight: 600,
                        border: `1px solid ${C.border}`,
                        borderRight: "none",
                        borderRadius: "4px 0 0 4px",
                        cursor: "pointer",
                        bgcolor: !normalizeMode ? C.navy : C.white,
                        color: !normalizeMode ? "#fff" : C.muted,
                      }}
                    >
                      Raw
                    </Box>
                    <Box
                      component="button"
                      onClick={() => setNormalizeMode(true)}
                      sx={{
                        px: 1,
                        py: 0.3,
                        fontSize: 9,
                        fontWeight: 600,
                        border: `1px solid ${C.border}`,
                        borderRadius: "0 4px 4px 0",
                        cursor: "pointer",
                        bgcolor: normalizeMode ? C.navy : C.white,
                        color: normalizeMode ? "#fff" : C.muted,
                      }}
                    >
                      Normalized
                    </Box>
                  </Box>
                )}
                <Box
                  component="button"
                  onClick={async () => {
                    setNormalizing(true);
                    const result = await onNormalizeCollection(collection.id);
                    setNormalizing(false);
                    if (
                      result &&
                      typeof result === "object" &&
                      Object.keys(result).length > 0
                    )
                      setNormalizeMode(true);
                  }}
                  disabled={normalizing}
                  sx={{
                    px: 1,
                    py: 0.3,
                    fontSize: 9,
                    fontWeight: 600,
                    border: `1px solid ${C.border}`,
                    borderRadius: "4px",
                    cursor: normalizing ? "not-allowed" : "pointer",
                    bgcolor: C.white,
                    color: normalizing ? C.muted : C.charcoal,
                    "&:hover": { borderColor: C.navy, color: C.navy },
                  }}
                >
                  {normalizing
                    ? "Normalizing…"
                    : hasNormMap
                      ? "Re-normalize"
                      : "Normalize"}
                </Box>
              </Box>
            </Box>
            {(() => {
              const indexed = sortedAnswers.map(([ans, count], i) => ({
                ans,
                count,
                i,
              }));
              const collapsible = indexed.filter(
                ({ count }) =>
                  totalAnswered > 0 &&
                  Math.round((count / totalAnswered) * 100) <= 1,
              );
              const visible = showAllDistribution
                ? indexed
                : indexed.filter(
                    ({ count }) =>
                      totalAnswered > 0 &&
                      Math.round((count / totalAnswered) * 100) > 1,
                  );
              return (
                <>
                  {visible.map(({ ans, count, i }) => {
                    const pct =
                      totalAnswered > 0
                        ? Math.round((count / totalAnswered) * 100)
                        : 0;
                    const barColor = [
                      "#0a2240",
                      "#0d2f60",
                      "#123d80",
                      "#1a52a6",
                      "#2368c8",
                      "#3a7ed8",
                      "#5494e4",
                      "#6daaf0",
                    ][i % 8];
                    const alreadyTagged = !!taggedAnswers[ans];
                    return (
                      <Box key={ans} sx={{ mb: 0.75 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.25,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: C.charcoal,
                            }}
                          >
                            {ans}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {onBulkReply &&
                              selectedCollectionId &&
                              (() => {
                                const isActive =
                                  bulkReplyJob &&
                                  bulkReplyJob.answerFilter === ans &&
                                  bulkReplyJob.status !== "complete";
                                return (
                                  <Box
                                    component="button"
                                    onClick={() => {
                                      const sample = collectionAnswers.find(
                                        (a) =>
                                          (normMap
                                            ? (normMap[a.extracted_answer] ??
                                              a.extracted_answer)
                                            : a.extracted_answer) === ans,
                                      );
                                      setBulkReplyTarget({
                                        answerFilter: ans,
                                        targetCount: count,
                                        sampleUsername:
                                          sample?.author_username || "handle",
                                      });
                                    }}
                                    sx={{
                                      fontSize: 9,
                                      fontWeight: 800,
                                      px: 1,
                                      py: 0.35,
                                      border: "none",
                                      borderRadius: "5px",
                                      bgcolor: isActive ? "#16a34a" : "#22c55e",
                                      color: "#fff",
                                      cursor: "pointer",
                                      letterSpacing: "0.02em",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                                      "&:hover": { bgcolor: "#16a34a" },
                                    }}
                                  >
                                    {isActive
                                      ? `${bulkReplyJob.sent}/${bulkReplyJob.total} sent`
                                      : "Reply"}
                                  </Box>
                                );
                              })()}
                            {onTagCohort && selectedCollectionId && (
                              <Box
                                component="button"
                                onClick={async () => {
                                  if (alreadyTagged) return;
                                  const rawAnswers =
                                    normalizeMode && parsedNormMap
                                      ? Object.entries(parsedNormMap)
                                          .filter(([, v]) => v === ans)
                                          .map(([k]) => k)
                                      : [ans];
                                  const result = await onTagCohort(
                                    selectedCollectionId,
                                    ans,
                                    ans,
                                    rawAnswers,
                                  );
                                  if (result)
                                    setTaggedAnswers((prev) => ({
                                      ...prev,
                                      [ans]: result.id,
                                    }));
                                }}
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  px: 0.75,
                                  py: 0.2,
                                  border: `1px solid ${alreadyTagged ? "#16a34a" : C.border}`,
                                  borderRadius: "4px",
                                  bgcolor: alreadyTagged ? "#f0fdf4" : C.white,
                                  color: alreadyTagged ? "#16a34a" : C.muted,
                                  cursor: alreadyTagged ? "default" : "pointer",
                                  "&:hover": alreadyTagged
                                    ? {}
                                    : { borderColor: C.navy, color: C.navy },
                                }}
                              >
                                {alreadyTagged ? "Tagged" : "Tag"}
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: C.charcoal,
                                }}
                              >
                                {pct}%
                              </Typography>
                              <Typography
                                sx={{ fontSize: 9.5, color: C.muted }}
                              >
                                {count}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            bgcolor: C.border,
                            borderRadius: 3,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${pct}%`,
                              bgcolor: barColor,
                              borderRadius: 3,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                  {collapsible.length > 0 && (
                    <Box
                      component="button"
                      onClick={() => setShowAllDistribution((s) => !s)}
                      sx={{
                        mt: 0.5,
                        fontSize: 9.5,
                        fontWeight: 600,
                        color: C.navy,
                        bgcolor: "transparent",
                        border: "none",
                        cursor: "pointer",
                        p: 0,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {showAllDistribution
                        ? `Show fewer`
                        : `+ ${collapsible.length} more at 1%`}
                    </Box>
                  )}
                </>
              );
            })()}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 0.75,
              }}
            >
              <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                {totalAnswered} of {collectionAnswers.length} responded ·{" "}
                {collectionAnswers.length - totalAnswered} no answer
              </Typography>
              {totalAnswered > 0 &&
                (() => {
                  const moe = (98 / Math.sqrt(totalAnswered)).toFixed(1);
                  const color =
                    totalAnswered >= 400
                      ? "#16a34a"
                      : totalAnswered >= 100
                        ? "#ca8a04"
                        : "#dc2626";
                  return (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                        ±{moe}% statistical MOE
                      </Typography>
                    </Box>
                  );
                })()}
            </Box>
          </Box>
        )}

        {/* Insights panel — MicroPoster style */}
        {collectionInsights &&
          (() => {
            const findings = Array.isArray(collectionInsights.key_findings)
              ? collectionInsights.key_findings
              : [];
            const themes = Array.isArray(collectionInsights.themes)
              ? collectionInsights.themes
              : [];
            const ideas = Array.isArray(collectionInsights.content_ideas)
              ? collectionInsights.content_ideas
              : [];
            const topicDist = Array.isArray(
              collectionInsights.topic_distribution,
            )
              ? collectionInsights.topic_distribution
              : [];
            const total = collectionAnswers.length;
            const posCnt = collectionAnswers.filter(
              (a) => a.sentiment === "positive",
            ).length;
            const negCnt = collectionAnswers.filter(
              (a) => a.sentiment === "negative",
            ).length;
            const neuCnt = total - posCnt - negCnt;
            const agreesCnt = collectionAnswers.filter(
              (a) => a.agreement === "agrees",
            ).length;
            const disaCnt = collectionAnswers.filter(
              (a) => a.agreement === "disagrees",
            ).length;
            const neuAgCnt = total - agreesCnt - disaCnt;
            const commentCnt = collectionAnswers.filter(
              (a) => a.reply_type === "comment",
            ).length;
            const questionCnt = collectionAnswers.filter(
              (a) => a.reply_type === "question",
            ).length;
            const criticismCnt = collectionAnswers.filter(
              (a) => a.reply_type === "criticism",
            ).length;
            const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);

            // Dominant sentiment for donut center
            const sentMax = Math.max(posCnt, neuCnt, negCnt);
            const domSentLabel =
              sentMax === posCnt
                ? "Positive"
                : sentMax === negCnt
                  ? "Negative"
                  : "Neutral";
            const domSentPct = pct(sentMax);
            // Donut: conic-gradient segments — app palette: blue / slate / orange
            const SENT_POS = "#1f6da8";
            const SENT_NEU = "#9ca3af";
            const SENT_NEG = "#d35400";
            const AGR_AGR = "#1f6da8";
            const AGR_NEU = "#9ca3af";
            const AGR_DIS = "#d35400";
            const posDeg = Math.round((posCnt / Math.max(total, 1)) * 360);
            const negDeg = Math.round((negCnt / Math.max(total, 1)) * 360);
            const neuDeg = 360 - posDeg - negDeg;
            const donutGradient = `conic-gradient(${SENT_POS} 0deg ${posDeg}deg, ${SENT_NEU} ${posDeg}deg ${posDeg + neuDeg}deg, ${SENT_NEG} ${posDeg + neuDeg}deg 360deg)`;

            // Agreement ratio
            const agreeRatio =
              disaCnt > 0
                ? (agreesCnt / disaCnt).toFixed(1)
                : agreesCnt > 0
                  ? "∞"
                  : "—";

            const FINDING_COLORS = ["#7c3aed", "#0891b2", "#d97706"];
            const THEME_COLORS = [
              "#7c3aed",
              "#0891b2",
              "#d97706",
              "#16a34a",
              "#e11d48",
            ];

            return (
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {/* Summary card */}
                <Box
                  sx={{
                    bgcolor: "#f8f9fa",
                    border: `1px solid ${C.border}`,
                    borderRadius: 1.5,
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      mb: 0.75,
                    }}
                  >
                    Summary
                  </Typography>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => (
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            color: C.charcoal,
                            lineHeight: 1.65,
                            fontWeight: 400,
                          }}
                        >
                          {children}
                        </Typography>
                      ),
                    }}
                  >
                    {collectionInsights.summary || ""}
                  </ReactMarkdown>
                </Box>

                {/* Key findings — 3 side-by-side cards */}
                {findings.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mb: 0.75,
                      }}
                    >
                      Key Findings
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {findings.slice(0, 3).map((f, i) => (
                        <Box
                          key={i}
                          sx={{
                            flex: 1,
                            border: `1px solid ${C.border}`,
                            borderTop: `3px solid ${FINDING_COLORS[i] || C.navy}`,
                            borderRadius: 1,
                            p: 1.25,
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 40,
                              fontWeight: 900,
                              color: FINDING_COLORS[i] || C.navy,
                              opacity: 0.08,
                              position: "absolute",
                              top: -6,
                              right: 6,
                              lineHeight: 1,
                              userSelect: "none",
                            }}
                          >
                            {i + 1}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: C.charcoal,
                              lineHeight: 1.55,
                            }}
                          >
                            {f}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Sentiment donut + Agreement bars — 2 columns */}
                {total > 0 && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {/* Sentiment donut */}
                    <Box
                      sx={{
                        flex: 1,
                        border: `1px solid ${C.border}`,
                        borderRadius: 1.5,
                        p: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          mb: 1,
                        }}
                      >
                        Sentiment Analysis
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        {/* Donut */}
                        <Box
                          sx={{
                            flexShrink: 0,
                            position: "relative",
                            width: 64,
                            height: 64,
                          }}
                        >
                          <Box
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              background: donutGradient,
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              bgcolor: "#fff",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 800,
                                color: C.charcoal,
                                lineHeight: 1,
                              }}
                            >
                              {domSentPct}%
                            </Typography>
                          </Box>
                        </Box>
                        {/* Legend */}
                        <Box sx={{ flex: 1 }}>
                          {[
                            {
                              label: "Positive",
                              count: posCnt,
                              color: SENT_POS,
                            },
                            {
                              label: "Neutral",
                              count: neuCnt,
                              color: SENT_NEU,
                            },
                            {
                              label: "Negative",
                              count: negCnt,
                              color: SENT_NEG,
                            },
                          ].map(({ label, count, color }) => (
                            <Box
                              key={label}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                mb: 0.35,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: color,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: C.charcoal,
                                  flex: 1,
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: C.charcoal,
                                }}
                              >
                                {count}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 9.5,
                                  color: C.muted,
                                  width: 30,
                                  textAlign: "right",
                                }}
                              >
                                {pct(count)}%
                              </Typography>
                            </Box>
                          ))}
                          <Typography
                            sx={{ fontSize: 9.5, color: C.muted, mt: 0.5 }}
                          >
                            Dominant:{" "}
                            <strong style={{ color: C.charcoal }}>
                              {domSentLabel}
                            </strong>
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Agreement bars */}
                    <Box
                      sx={{
                        flex: 1,
                        border: `1px solid ${C.border}`,
                        borderRadius: 1.5,
                        p: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          mb: 1,
                        }}
                      >
                        Audience Agreement
                      </Typography>
                      {[
                        { label: "Agrees", count: agreesCnt, color: AGR_AGR },
                        { label: "Neutral", count: neuAgCnt, color: AGR_NEU },
                        { label: "Disagrees", count: disaCnt, color: AGR_DIS },
                      ].map(({ label, count, color }) => (
                        <Box key={label} sx={{ mb: 0.6 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.2,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 10, color: C.charcoal }}
                            >
                              {label}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: C.muted }}>
                              {count} · {pct(count)}%
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              height: 7,
                              bgcolor: "#f0f0f0",
                              borderRadius: 1,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${pct(count)}%`,
                                height: "100%",
                                bgcolor: color,
                                borderRadius: 1,
                                transition: "width 0.4s ease",
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                      <Box
                        sx={{
                          mt: 1,
                          pt: 0.75,
                          borderTop: `1px solid ${C.border}`,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                          Agreement Ratio
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: agreesCnt >= disaCnt ? AGR_AGR : AGR_DIS,
                          }}
                        >
                          {agreeRatio}:1
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Topic distribution */}
                {topicDist.length > 0 && (
                  <Box
                    sx={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mb: 1,
                      }}
                    >
                      Topic Distribution
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.7,
                      }}
                    >
                      {topicDist.map((t, i) => (
                        <Box key={i}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.2,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 10.5, color: C.charcoal }}
                            >
                              {t.topic}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: C.charcoal,
                              }}
                            >
                              {t.pct}%
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              height: 7,
                              bgcolor: "#f0f0f0",
                              borderRadius: 1,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${t.pct}%`,
                                height: "100%",
                                bgcolor: C.navy,
                                borderRadius: 1,
                                transition: "width 0.4s ease",
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Conversation themes */}
                {themes.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mb: 0.75,
                      }}
                    >
                      Conversation Themes
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                      }}
                    >
                      {themes.map((t, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "flex",
                            gap: 1.25,
                            border: `1px solid ${C.border}`,
                            borderRadius: 1.5,
                            p: 1.25,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              flexShrink: 0,
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              bgcolor: THEME_COLORS[i % THEME_COLORS.length],
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mt: "1px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 800,
                                color: "#fff",
                              }}
                            >
                              {i + 1}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 1,
                                mb: 0.2,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  color: C.charcoal,
                                }}
                              >
                                {t.label}
                              </Typography>
                              {t.pct != null && (
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color:
                                      THEME_COLORS[i % THEME_COLORS.length],
                                  }}
                                >
                                  {t.pct}%
                                </Typography>
                              )}
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 10.5,
                                color: C.muted,
                                lineHeight: 1.45,
                                mb: t.example_quote ? 0.5 : 0,
                              }}
                            >
                              {t.description}
                            </Typography>
                            {t.example_quote && (
                              <Box
                                sx={{
                                  borderLeft: `2px solid ${THEME_COLORS[i % THEME_COLORS.length]}`,
                                  pl: 0.75,
                                  mt: 0.4,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    color: C.charcoal,
                                    fontStyle: "italic",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  "{t.example_quote}"
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Reply categories */}
                {total > 0 && (
                  <Box
                    sx={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mb: 1,
                      }}
                    >
                      Reply Categories
                    </Typography>
                    {[
                      { label: "Comments", count: commentCnt, color: C.navy },
                      {
                        label: "Questions",
                        count: questionCnt,
                        color: C.navyMid,
                      },
                      {
                        label: "Criticism",
                        count: criticismCnt,
                        color: C.orange,
                      },
                    ].map(({ label, count, color }) => (
                      <Box key={label} sx={{ mb: 0.7 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.2,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: 10.5, color: C.charcoal }}
                          >
                            {label}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: C.muted }}>
                            {count} · {pct(count)}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 7,
                            bgcolor: "#f0f0f0",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${pct(count)}%`,
                              height: "100%",
                              bgcolor: color,
                              borderRadius: 1,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Content ideas */}
                {ideas.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mb: 0.75,
                      }}
                    >
                      Content Ideas
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.6,
                      }}
                    >
                      {ideas.map((idea, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "flex",
                            gap: 1.25,
                            border: `1px solid ${C.border}`,
                            borderRadius: 1,
                            px: 1.25,
                            py: 0.9,
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 800,
                              color: C.navy,
                              flexShrink: 0,
                              minWidth: 16,
                            }}
                          >
                            {i + 1}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: C.charcoal,
                              lineHeight: 1.5,
                            }}
                          >
                            {idea}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}

        {/* Answers table */}
        {collectionAnswers.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
              border: `1px dashed ${C.border}`,
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted }}>
              No responses yet
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Filter bar */}
            {(() => {
              const uniqueAnswers = Object.entries(answerCounts)
                .filter(([, cnt]) => cnt / totalAnswered > 0.01)
                .sort((a, b) => b[1] - a[1])
                .map(([ans]) => ans);
              const uniqueStates = [
                ...new Set(
                  collectionAnswers.map((a) => a.author_state).filter(Boolean),
                ),
              ].sort();
              const confOptions = [
                { val: "high", label: "High" },
                { val: "medium", label: "Med" },
                { val: "low", label: "Low" },
              ];
              const toggleSet = (setter, val) => {
                setter((prev) => {
                  const s = new Set(prev);
                  s.has(val) ? s.delete(val) : s.add(val);
                  return s;
                });
                setAnswersPage(1);
              };
              const hasFilters =
                pollFilterAnswers.size > 0 ||
                pollFilterConf.size > 0 ||
                pollFilterLocation.trim();
              return (
                <Box
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    alignItems: "center",
                  }}
                >
                  {uniqueAnswers.map((ans) => (
                    <Box
                      key={ans}
                      component="button"
                      onClick={() => toggleSet(setPollFilterAnswers, ans)}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        px: "8px",
                        py: "4px",
                        border: `1px solid ${pollFilterAnswers.has(ans) ? C.navy : C.border}`,
                        borderRadius: "4px",
                        bgcolor: pollFilterAnswers.has(ans) ? C.navy : C.white,
                        color: pollFilterAnswers.has(ans)
                          ? C.white
                          : C.charcoal,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ans}
                    </Box>
                  ))}
                  {confOptions.map(({ val, label }) => (
                    <Box
                      key={val}
                      component="button"
                      onClick={() => toggleSet(setPollFilterConf, val)}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        px: "8px",
                        py: "4px",
                        border: `1px solid ${pollFilterConf.has(val) ? C.orange : C.border}`,
                        borderRadius: "4px",
                        bgcolor: pollFilterConf.has(val)
                          ? C.orange + "20"
                          : C.white,
                        color: pollFilterConf.has(val) ? C.orange : C.muted,
                        cursor: "pointer",
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                  {uniqueStates.length > 0 && (
                    <Box
                      component="select"
                      value={pollFilterLocation}
                      onChange={(e) => {
                        setPollFilterLocation(e.target.value);
                        setAnswersPage(1);
                      }}
                      sx={{
                        fontSize: 11,
                        px: "8px",
                        py: "4px",
                        border: `1px solid ${pollFilterLocation ? C.navy : C.border}`,
                        borderRadius: "4px",
                        outline: "none",
                        color: pollFilterLocation ? C.charcoal : C.muted,
                        bgcolor: C.white,
                        cursor: "pointer",
                      }}
                    >
                      <option value="">State…</option>
                      {uniqueStates.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Box>
                  )}
                  {hasFilters && (
                    <Box
                      component="button"
                      onClick={() => {
                        setPollFilterAnswers(new Set());
                        setPollFilterConf(new Set());
                        setPollFilterLocation("");
                        setAnswersPage(1);
                      }}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        px: "8px",
                        py: "4px",
                        border: `1px solid ${C.border}`,
                        borderRadius: "4px",
                        bgcolor: C.white,
                        color: C.muted,
                        cursor: "pointer",
                        "&:hover": { color: C.navy, borderColor: C.navy },
                      }}
                    >
                      Clear
                    </Box>
                  )}
                </Box>
              );
            })()}
            {(() => {
              const filtered = collectionAnswers.filter((a) => {
                if (
                  pollFilterAnswers.size > 0 &&
                  !pollFilterAnswers.has(a.extracted_answer)
                )
                  return false;
                if (
                  pollFilterConf.size > 0 &&
                  !pollFilterConf.has(a.confidence)
                )
                  return false;
                if (pollFilterLocation && a.author_state !== pollFilterLocation)
                  return false;
                return true;
              });
              const hasFilters =
                pollFilterAnswers.size > 0 ||
                pollFilterConf.size > 0 ||
                pollFilterLocation;
              return hasFilters ? (
                <Typography
                  sx={{
                    fontSize: 10,
                    color: C.navy,
                    fontWeight: 700,
                    mb: 0.75,
                  }}
                >
                  {filtered.length} of {totalAnswered} responded match filters
                </Typography>
              ) : null;
            })()}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                px: 1,
                pb: 0.75,
                mb: 0.5,
                borderBottom: `2px solid ${C.border}`,
              }}
            >
              <Box sx={{ flex: "0 0 110px" }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Account
                </Typography>
              </Box>
              <Box sx={{ flex: 2 }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Answer
                </Typography>
              </Box>
              <Box sx={{ flex: "0 0 90px" }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Location
                </Typography>
              </Box>
              <Box sx={{ flex: 3 }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Reply
                </Typography>
              </Box>
              <Box sx={{ flex: "0 0 54px", textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Conf.
                </Typography>
              </Box>
              <Box sx={{ flex: "0 0 96px" }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Actions
                </Typography>
              </Box>
            </Box>
            {(() => {
              const ANSWERS_PER_PAGE = 10;
              const filtered = collectionAnswers.filter((a) => {
                if (
                  pollFilterAnswers.size > 0 &&
                  !pollFilterAnswers.has(a.extracted_answer)
                )
                  return false;
                if (
                  pollFilterConf.size > 0 &&
                  !pollFilterConf.has(a.confidence)
                )
                  return false;
                if (pollFilterLocation && a.author_state !== pollFilterLocation)
                  return false;
                return true;
              });
              const totalPages = Math.max(
                1,
                Math.ceil(filtered.length / ANSWERS_PER_PAGE),
              );
              const safePage = Math.min(answersPage, totalPages);
              const paged = filtered.slice(
                (safePage - 1) * ANSWERS_PER_PAGE,
                safePage * ANSWERS_PER_PAGE,
              );
              return (
                <>
                  {paged.map((ans, i) => {
                    const confColor =
                      ans.confidence === "high"
                        ? "#16a34a"
                        : ans.confidence === "medium"
                          ? C.orange
                          : C.muted;
                    const confLabel =
                      ans.confidence === "high"
                        ? "High"
                        : ans.confidence === "medium"
                          ? "Med"
                          : "Low";
                    const linkedDonor = donorLinks.find(
                      (l) => l.author_id === ans.author_id,
                    );
                    return (
                      <Box
                        key={ans.id || i}
                        sx={{
                          display: "flex",
                          gap: 1,
                          px: 1,
                          py: 1,
                          borderBottom: `1px solid ${C.border}`,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box sx={{ flex: "0 0 110px" }}>
                          <Typography
                            onClick={() => {
                              if (linkedDonor?.email) {
                                onOpenProfile?.(linkedDonor.email, {
                                  "First Name": (ans.author_name || "").split(
                                    " ",
                                  )[0],
                                  "Last Name": (ans.author_name || "")
                                    .split(" ")
                                    .slice(1)
                                    .join(" "),
                                });
                              } else if (ans.author_username) {
                                window.open(
                                  `https://x.com/${ans.author_username}`,
                                  "_blank",
                                );
                              }
                            }}
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.charcoal,
                              lineHeight: 1.2,
                              cursor: "pointer",
                              "&:hover": { color: C.navy },
                            }}
                          >
                            {ans.author_name ||
                              ans.author_username ||
                              "Unknown"}
                          </Typography>
                          {ans.author_username && (
                            <Box
                              component="a"
                              href={`https://x.com/${ans.author_username}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                fontSize: 9.5,
                                color: C.muted,
                                textDecoration: "none",
                                "&:hover": { color: C.navy },
                              }}
                            >
                              @{ans.author_username}
                            </Box>
                          )}
                          {linkedDonor && (
                            <Box
                              sx={{
                                mt: 0.25,
                                fontSize: 9,
                                fontWeight: 700,
                                color: "#16a34a",
                                bgcolor: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                borderRadius: "3px",
                                px: 0.5,
                                py: 0.1,
                                display: "inline-block",
                              }}
                            >
                              Donor linked
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flex: 2 }}>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              color: ans.extracted_answer
                                ? C.charcoal
                                : C.muted,
                              fontWeight: ans.extracted_answer ? 600 : 400,
                              lineHeight: 1.4,
                            }}
                          >
                            {ans.extracted_answer || "—"}
                          </Typography>
                          {(ans.sentiment ||
                            ans.agreement ||
                            ans.reply_type) && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.4,
                                mt: 0.4,
                              }}
                            >
                              {ans.sentiment && (
                                <Box
                                  sx={{
                                    fontSize: 8.5,
                                    fontWeight: 700,
                                    px: "5px",
                                    py: "2px",
                                    borderRadius: "3px",
                                    display: "inline-block",
                                    bgcolor:
                                      ans.sentiment === "positive"
                                        ? "#eff6ff"
                                        : ans.sentiment === "negative"
                                          ? "#fff4ee"
                                          : "#f4f6f9",
                                    color:
                                      ans.sentiment === "positive"
                                        ? C.blue
                                        : ans.sentiment === "negative"
                                          ? C.orange
                                          : C.muted,
                                    border: `1px solid ${ans.sentiment === "positive" ? "#bfdbfe" : ans.sentiment === "negative" ? "#fdd0b5" : C.border}`,
                                  }}
                                >
                                  {ans.sentiment}
                                </Box>
                              )}
                              {ans.agreement && ans.agreement !== "neutral" && (
                                <Box
                                  sx={{
                                    fontSize: 8.5,
                                    fontWeight: 700,
                                    px: "5px",
                                    py: "2px",
                                    borderRadius: "3px",
                                    display: "inline-block",
                                    bgcolor:
                                      ans.agreement === "agrees"
                                        ? "#eef3fa"
                                        : "#fff4ee",
                                    color:
                                      ans.agreement === "agrees"
                                        ? C.navyMid
                                        : C.orange,
                                    border: `1px solid ${ans.agreement === "agrees" ? "#c5d8ef" : "#fdd0b5"}`,
                                  }}
                                >
                                  {ans.agreement}
                                </Box>
                              )}
                              {ans.reply_type &&
                                ans.reply_type !== "comment" && (
                                  <Box
                                    sx={{
                                      fontSize: 8.5,
                                      fontWeight: 700,
                                      px: "5px",
                                      py: "2px",
                                      borderRadius: "3px",
                                      display: "inline-block",
                                      bgcolor: "#faf5ff",
                                      color: "#7c3aed",
                                      border: "1px solid #e9d5ff",
                                    }}
                                  >
                                    {ans.reply_type}
                                  </Box>
                                )}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flex: "0 0 90px" }}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: C.charcoal,
                              lineHeight: 1.3,
                            }}
                          >
                            {ans.author_city && ans.author_state
                              ? `${ans.author_city}, ${ans.author_state}`
                              : ans.author_state ||
                                ans.author_city ||
                                (ans.author_zip ? ans.author_zip : "—")}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 3 }}>
                          <ExpandableReply text={ans.reply_text || ""} />
                        </Box>
                        <Box
                          sx={{
                            flex: "0 0 54px",
                            textAlign: "center",
                            pt: 0.25,
                          }}
                        >
                          {ans.extracted_answer && (
                            <Box
                              sx={{
                                fontSize: 9,
                                fontWeight: 800,
                                color: confColor,
                                bgcolor: confColor + "18",
                                border: `1px solid ${confColor}44`,
                                borderRadius: "3px",
                                px: 0.5,
                                py: 0.2,
                                display: "inline-block",
                              }}
                            >
                              {confLabel}
                            </Box>
                          )}
                        </Box>
                        <Box
                          sx={{
                            flex: "0 0 96px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {ans.reply_tweet_id && onPostReply && (
                            <Box
                              component="button"
                              onClick={() =>
                                setReplyTarget({
                                  ...ans,
                                  collection_id: collection?.id,
                                })
                              }
                              sx={{
                                fontSize: 9,
                                fontWeight: 700,
                                px: 0.75,
                                py: 0.35,
                                border: `1px solid ${C.border}`,
                                borderRadius: "4px",
                                bgcolor: C.white,
                                color: C.navy,
                                cursor: "pointer",
                                textAlign: "center",
                                "&:hover": { borderColor: C.navy },
                              }}
                            >
                              Reply
                            </Box>
                          )}
                          {onSaveDonorLink && (
                            <Box
                              component="button"
                              onClick={() =>
                                setDonorLinkTarget({
                                  ...ans,
                                  collection_id: collection?.id,
                                })
                              }
                              sx={{
                                fontSize: 9,
                                fontWeight: 700,
                                px: 0.75,
                                py: 0.35,
                                border: `1px solid ${C.border}`,
                                borderRadius: "4px",
                                bgcolor: linkedDonor ? "#f0fdf4" : C.white,
                                color: linkedDonor ? "#16a34a" : C.muted,
                                cursor: "pointer",
                                textAlign: "center",
                                "&:hover": {
                                  borderColor: linkedDonor ? "#16a34a" : C.navy,
                                  color: linkedDonor ? "#16a34a" : C.navy,
                                },
                              }}
                            >
                              {linkedDonor ? "Re-link" : "Link Donor"}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  {totalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: 1.25,
                        mt: 0.5,
                      }}
                    >
                      <Box
                        component="button"
                        onClick={() =>
                          setAnswersPage((p) => Math.max(1, p - 1))
                        }
                        disabled={safePage === 1}
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          px: 1.25,
                          py: 0.4,
                          border: `1px solid ${C.border}`,
                          borderRadius: 1,
                          bgcolor: "transparent",
                          color: safePage === 1 ? C.muted : C.navy,
                          cursor: safePage === 1 ? "not-allowed" : "pointer",
                        }}
                      >
                        ← Prev
                      </Box>
                      <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                        {safePage} / {totalPages} · {filtered.length}
                        {filtered.length !== collectionAnswers.length
                          ? ` of ${collectionAnswers.length}`
                          : ""}{" "}
                        total
                      </Typography>
                      <Box
                        component="button"
                        onClick={() =>
                          setAnswersPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={safePage === totalPages}
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          px: 1.25,
                          py: 0.4,
                          border: `1px solid ${C.border}`,
                          borderRadius: 1,
                          bgcolor: "transparent",
                          color: safePage === totalPages ? C.muted : C.navy,
                          cursor:
                            safePage === totalPages ? "not-allowed" : "pointer",
                        }}
                      >
                        Next →
                      </Box>
                    </Box>
                  )}
                </>
              );
            })()}
          </Box>
        )}
        <PollAudienceModal
          open={showPollModal}
          audience={audience}
          onClose={() => setShowPollModal(false)}
          onPoll={onPollAudience}
          polling={polling}
          pollProgress={pollProgress}
          C={C}
        />
        <ReplyModal
          open={!!replyTarget}
          target={replyTarget}
          onClose={() => setReplyTarget(null)}
          onPost={onPostReply || (() => {})}
          C={C}
        />
        <DonorLinkModal
          open={!!donorLinkTarget}
          target={donorLinkTarget}
          existingLink={
            donorLinks.find(
              (l) => l.author_id === donorLinkTarget?.author_id,
            ) || null
          }
          campaignId={activeCampaignId}
          onClose={() => setDonorLinkTarget(null)}
          onLink={async (...args) => {
            await onSaveDonorLink?.(...args);
            setDonorLinkTarget(null);
          }}
          C={C}
        />
        <BulkReplyModal
          open={!!bulkReplyTarget}
          answerFilter={bulkReplyTarget?.answerFilter || ""}
          targetCount={bulkReplyTarget?.targetCount || 0}
          collectionId={selectedCollectionId}
          sampleUsername={bulkReplyTarget?.sampleUsername}
          onClose={() => setBulkReplyTarget(null)}
          onSubmit={onBulkReply}
          activeJob={
            bulkReplyJob &&
            bulkReplyTarget &&
            bulkReplyJob.answerFilter === bulkReplyTarget.answerFilter
              ? bulkReplyJob
              : null
          }
          C={C}
        />
      </Box>
    );
  }

  // ── Collect mode — leads table ──────────────────────────────────────────────
  if (mode === "collect") {
    const srcTweet = collectResult?.source_tweet?.tweet || null;
    const srcAuthor = collectResult?.source_tweet?.author || null;
    const srcUrl = isFBCollect
      ? `https://www.facebook.com/${collectResult?.source_tweet_id}`
      : srcAuthor?.username
        ? `https://x.com/${srcAuthor.username}/status/${collectResult?.source_tweet_id}`
        : collectTweetUrl;
    const srcMetrics = srcTweet?.public_metrics || {};
    const q = collectSearch.toLowerCase().trim();
    const filteredLeads = q
      ? leads.filter(
          (l) =>
            (l.author_username || "").toLowerCase().includes(q) ||
            (l.author_name || "").toLowerCase().includes(q) ||
            (l.reply_text || "").toLowerCase().includes(q),
        )
      : leads;
    const leadsTotal = filteredLeads.length;
    const leadsTotalPages = Math.max(1, Math.ceil(leadsTotal / LEADS_PER_PAGE));
    const pagedLeads = filteredLeads.slice(
      (leadsPage - 1) * LEADS_PER_PAGE,
      leadsPage * LEADS_PER_PAGE,
    );

    return (
      <Box>
        {/* Source post context card */}
        {(srcTweet || (isFBCollect && collectResult?.tweet_text)) && (
          <Box
            sx={{
              border: `1px solid ${isFBCollect ? "#1877f2" : C.border}`,
              borderRadius: 1,
              p: 1.75,
              bgcolor: C.white,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: isFBCollect ? "#1877f2" : C.navy,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 800, color: "#fff" }}
                  >
                    {isFBCollect
                      ? "F"
                      : (srcAuthor?.name || srcAuthor?.username || "?")
                          .slice(0, 2)
                          .toUpperCase()}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.charcoal,
                      lineHeight: 1.2,
                    }}
                  >
                    {isFBCollect
                      ? srcAuthor?.name || "Facebook Page"
                      : srcAuthor?.name || srcAuthor?.username || "Unknown"}
                  </Typography>
                  {!isFBCollect && srcAuthor?.username && (
                    <Typography sx={{ fontSize: 10, color: C.muted }}>
                      @{srcAuthor.username}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    px: 0.75,
                    py: 0.2,
                    borderRadius: "3px",
                    bgcolor: (isFBCollect ? "#1877f2" : C.navy) + "18",
                    border: `1px solid ${isFBCollect ? "#1877f2" : C.navy}44`,
                    color: isFBCollect ? "#1877f2" : C.navy,
                  }}
                >
                  {isFBCollect ? "Source post" : "Source tweet"}
                </Box>
              </Box>
            </Box>
            {(() => {
              const srcText = isFBCollect
                ? collectResult.tweet_text
                : srcTweet?.text;
              const srcKey = `src-${collectResult?.source_tweet_id}`;
              const isSrcLong =
                isFBCollect && srcText && srcText.length > POST_TRUNCATE;
              const srcExpanded = expandedPosts.has(srcKey);
              return (
                <Box sx={{ mb: 1.25 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: C.charcoal,
                      lineHeight: 1.65,
                      wordBreak: "break-word",
                    }}
                  >
                    {isSrcLong && !srcExpanded
                      ? `${srcText.slice(0, POST_TRUNCATE)}…`
                      : srcText}
                  </Typography>
                  {isSrcLong && (
                    <Box
                      component="button"
                      onClick={() => togglePostExpand(srcKey)}
                      sx={{
                        mt: 0.5,
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#1877f2",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        p: 0,
                      }}
                    >
                      {srcExpanded ? "Show less" : "Show more"}
                    </Box>
                  )}
                </Box>
              );
            })()}
            {!isFBCollect && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5 }}
              >
                {[
                  ["❤", srcMetrics.like_count],
                  ["💬", srcMetrics.reply_count],
                  ["🔁", srcMetrics.retweet_count],
                ].map(([icon, val]) =>
                  val > 0 ? (
                    <Typography
                      key={icon}
                      sx={{ fontSize: 10, color: C.muted }}
                    >
                      {icon} {val >= 1000 ? `${(val / 1000).toFixed(1)}K` : val}
                    </Typography>
                  ) : null,
                )}
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              {srcUrl && (
                <Box
                  component="a"
                  href={srcUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 10,
                    color: isFBCollect ? "#1877f2" : C.navy,
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {isFBCollect ? "View on Facebook →" : "View on X →"}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.09em",
            }}
          >
            {isFBCollect ? "Comments" : "Replies"}{" "}
            {leads.length > 0 && `· ${leads.length}`}
          </Typography>
          {leads.length > 0 && onPostReply && (
            <Box
              component="button"
              onClick={() => setCollectReplyAllOpen(true)}
              sx={{
                fontSize: 10,
                fontWeight: 800,
                px: 1.5,
                py: 0.5,
                border: "none",
                borderRadius: "6px",
                bgcolor: isFBCollect ? "#1877f2" : "#0a2240",
                color: "#fff",
                cursor: "pointer",
                letterSpacing: "0.02em",
                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                "&:hover": { bgcolor: isFBCollect ? "#1464cc" : "#061828" },
              }}
            >
              {collectReplyAllJob?.status === "running"
                ? `${collectReplyAllJob.sent}/${collectReplyAllJob.total} sent`
                : "Reply All"}
            </Box>
          )}
        </Box>

        {leadsLoading ? (
          <Loader text="Loading replies…" />
        ) : leads.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
              border: `1px dashed ${C.border}`,
              borderRadius: 1,
            }}
          >
            {collectResult?.too_old ? (
              <>
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, color: C.red, mb: 0.5 }}
                >
                  Replies unavailable
                </Typography>
                <Typography sx={{ fontSize: 10, color: C.muted }}>
                  This tweet is {collectResult.tweet_age_days}d old. X API reply
                  search is limited to 7 days.
                </Typography>
              </>
            ) : collectResult?.source_tweet_id ? (
              <>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.muted,
                    mb: 0.5,
                  }}
                >
                  No replies found
                </Typography>
                <Typography sx={{ fontSize: 10, color: C.muted }}>
                  {isFBCollect
                    ? "0 comments returned for this post."
                    : collectResult.is_nested_reply
                      ? "This tweet is part of a thread. X API indexed 0 direct replies — try the original thread's root tweet URL."
                      : "0 replies returned from X API for this tweet."}
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.muted,
                    mb: 0.5,
                  }}
                >
                  No replies loaded yet
                </Typography>
                <Typography sx={{ fontSize: 10, color: C.muted }}>
                  {isFBCollect
                    ? "Paste a Facebook post URL and click Browse Comments."
                    : "Paste a tweet URL and click Browse Replies."}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Box>
            {/* Table header */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                px: 1,
                pb: 0.75,
                mb: 0.5,
                borderBottom: `2px solid ${C.border}`,
              }}
            >
              <Box sx={{ flex: "0 0 120px" }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Account
                </Typography>
              </Box>
              <Box sx={{ flex: "0 0 80px" }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Location
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Reply
                </Typography>
              </Box>
              <Box sx={{ flex: "0 0 48px" }} />
            </Box>

            {pagedLeads.map((lead, i) => (
              <Box
                key={lead.reply_tweet_id || i}
                sx={{
                  display: "flex",
                  gap: 1,
                  px: 1,
                  py: 1,
                  borderBottom: `1px solid ${C.border}`,
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: "0 0 120px" }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.charcoal,
                      lineHeight: 1.2,
                    }}
                  >
                    {lead.author_name ||
                      lead.author_username ||
                      (lead.platform === "facebook"
                        ? (() => {
                            const w = (lead.reply_text || "")
                              .trim()
                              .split(/\s+/);
                            return (
                              w.slice(0, 4).join(" ") +
                                (w.length > 4 ? "…" : "") || "Facebook User"
                            );
                          })()
                        : "Unknown")}
                  </Typography>
                  {lead.author_username && lead.platform !== "facebook" && (
                    <Box
                      component="a"
                      href={`https://x.com/${lead.author_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: 9.5,
                        color: C.muted,
                        textDecoration: "none",
                        "&:hover": { color: C.navy },
                      }}
                    >
                      @{lead.author_username}
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: "0 0 80px" }}>
                  <Typography
                    sx={{ fontSize: 9.5, color: C.charcoal, lineHeight: 1.4 }}
                  >
                    {lead.author_location || ""}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: C.charcoal,
                      lineHeight: 1.5,
                      wordBreak: "break-word",
                    }}
                  >
                    {lead.reply_text || ""}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: "0 0 48px",
                    display: "flex",
                    alignItems: "flex-start",
                    pt: 0.25,
                  }}
                >
                  {lead.reply_tweet_id && onPostReply && (
                    <Box
                      component="button"
                      onClick={() =>
                        setReplyTarget({ ...lead, collection_id: null })
                      }
                      sx={{
                        fontSize: 9,
                        fontWeight: 800,
                        px: 1,
                        py: 0.35,
                        border: "none",
                        borderRadius: "5px",
                        bgcolor: isFBCollect ? "#1877f2" : "#0a2240",
                        color: "#fff",
                        cursor: "pointer",
                        letterSpacing: "0.02em",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                        "&:hover": {
                          bgcolor: isFBCollect ? "#1464cc" : "#061828",
                        },
                      }}
                    >
                      Reply
                    </Box>
                  )}
                </Box>
              </Box>
            ))}

            {/* Pagination */}
            {leadsTotalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.5,
                }}
              >
                <Box
                  component="button"
                  onClick={() => setLeadsPage((p) => Math.max(1, p - 1))}
                  disabled={leadsPage <= 1}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    px: 1.5,
                    py: 0.5,
                    border: `1px solid ${C.border}`,
                    bgcolor: C.white,
                    color: C.navy,
                    borderRadius: 1,
                    cursor: leadsPage <= 1 ? "not-allowed" : "pointer",
                    opacity: leadsPage <= 1 ? 0.4 : 1,
                  }}
                >
                  ← Prev
                </Box>
                <Typography sx={{ fontSize: 11, color: C.muted }}>
                  {leadsPage} / {leadsTotalPages}
                </Typography>
                <Box
                  component="button"
                  onClick={() =>
                    setLeadsPage((p) => Math.min(leadsTotalPages, p + 1))
                  }
                  disabled={leadsPage >= leadsTotalPages}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    px: 1.5,
                    py: 0.5,
                    border: `1px solid ${C.border}`,
                    bgcolor: C.white,
                    color: C.navy,
                    borderRadius: 1,
                    cursor:
                      leadsPage >= leadsTotalPages ? "not-allowed" : "pointer",
                    opacity: leadsPage >= leadsTotalPages ? 0.4 : 1,
                  }}
                >
                  Next →
                </Box>
              </Box>
            )}
          </Box>
        )}
        <ReplyModal
          open={!!replyTarget}
          target={replyTarget}
          onClose={() => setReplyTarget(null)}
          onPost={onPostReply || (() => {})}
          C={C}
        />
        <CollectReplyAllModal
          open={collectReplyAllOpen}
          totalReplies={leads.length}
          sourceTweetId={collectResult?.source_tweet_id}
          platform={collectResult?.platform || "x"}
          onClose={() => setCollectReplyAllOpen(false)}
          onSubmit={async (...args) => {
            await onBulkReply?.(...args);
            setCollectReplyAllOpen(false);
          }}
          onGenerateVariations={onGenerateVariations}
          activeJob={
            bulkReplyJob?.answerFilter === "__collect_all__"
              ? bulkReplyJob
              : null
          }
          C={C}
        />
      </Box>
    );
  }

  // ── Monitor mode — mention feed ─────────────────────────────────────────────
  return (
    <Box>
      {selectedTerm &&
        (() => {
          const t = terms.find((x) => x.id === selectedTerm);
          const isTimeline = t?.monitor_type === "timeline";
          const isPage = t?.monitor_type === "page";
          const isFacebook = t?.platform === "facebook";
          const color = isFacebook ? "#1877f2" : isTimeline ? C.green : C.navy;
          const headerIcon = isFacebook ? faFacebook : faXTwitter;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
                px: 1.25,
                py: 0.85,
                bgcolor: color + "10",
                border: `1px solid ${color}33`,
                borderRadius: "6px",
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  bgcolor: color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon
                  icon={headerIcon}
                  style={{ fontSize: 9, color: "#fff" }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t?.query || selectedTerm}
              </Typography>
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: 1,
                  py: 0.3,
                  bgcolor: color + "18",
                  border: `1px solid ${color}44`,
                  color,
                  borderRadius: "3px",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {isPage ? "Page" : isTimeline ? "Timeline" : "Mention"}
              </Box>
              <Box
                component="button"
                onClick={() => {
                  onSelectTerm(null);
                  onPageChange(1);
                }}
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: 0.75,
                  py: 0.25,
                  color,
                  background: color + "15",
                  border: `1px solid ${color}33`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  flexShrink: 0,
                  "&:hover": { bgcolor: color + "25" },
                }}
              >
                Clear
              </Box>
            </Box>
          );
        })()}
      {/* Filter + sort bar */}
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          mb: authorFilter ? 1 : 1.25,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {FILTERS.map(({ key, label }) => {
          const count =
            key === "all"
              ? total
              : key === "replies"
                ? breakdown.replies
                : key === "quotes"
                  ? breakdown.quotes
                  : breakdown.mentions;
          return (
            <Box
              key={key}
              component="button"
              onClick={() => {
                handleFilterChange(key);
                onPageChange(1);
              }}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                px: 1.25,
                py: 0.45,
                border: `1px solid ${filter === key ? C.navy : C.border}`,
                bgcolor: filter === key ? C.navy : C.white,
                color: filter === key ? "#fff" : C.muted,
                borderRadius: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {label}
              <Box
                component="span"
                sx={{ fontSize: 9.5, fontWeight: 400, opacity: 0.8 }}
              >
                ({count})
              </Box>
            </Box>
          );
        })}
        <Box
          sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}
        >
          {[
            { key: "recent", label: "Recent" },
            { key: "likes", label: "Most Liked" },
            { key: "replies", label: "Most Commented" },
          ].map((s) => (
            <Box
              key={s.key}
              component="button"
              onClick={() => {
                setSortBy(s.key);
                onPageChange(1);
              }}
              sx={{
                fontSize: 10,
                fontWeight: 600,
                px: 1,
                py: 0.4,
                border: `1px solid ${sortBy === s.key ? C.navy : C.border}`,
                bgcolor: sortBy === s.key ? C.navy + "12" : "transparent",
                color: sortBy === s.key ? C.navy : C.muted,
                borderRadius: 1,
                cursor: "pointer",
              }}
            >
              {s.label}
            </Box>
          ))}
        </Box>
      </Box>
      {total > 0 && (
        <Typography sx={{ fontSize: 9.5, color: C.muted, mb: 1.5 }}>
          {sortedMentions.length.toLocaleString()} / {total.toLocaleString()}
        </Typography>
      )}

      {/* Active author filter pill */}
      {authorFilter && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 600,
              px: 1,
              py: 0.4,
              bgcolor: C.navy + "10",
              border: `1px solid ${C.navy}33`,
              borderRadius: 1,
              color: C.navy,
            }}
          >
            @{activeAccount?.author_username || authorFilter}
          </Box>
          <Box
            component="button"
            onClick={() => handleAuthorChange(null)}
            sx={{
              fontSize: 10,
              color: C.muted,
              background: "none",
              border: "none",
              cursor: "pointer",
              p: 0,
              lineHeight: 1,
              "&:hover": { color: C.charcoal },
            }}
          >
            × clear
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Box
              key={i}
              sx={{
                height: 90,
                bgcolor: C.border,
                borderRadius: 1,
                opacity: 0.4 + i * 0.1,
              }}
            />
          ))}
        </Box>
      ) : pageMentions.length === 0 ? (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            border: `1px dashed ${C.border}`,
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.5 }}
          >
            No mentions found
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.muted }}>
            {lastSynced
              ? "No matching posts in the current filter."
              : "Enter a search query and click Sync Now."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {pageMentions.map((m) => {
            const initials = (m.author_name || m.author_username || "?")
              .slice(0, 2)
              .toUpperCase();
            const typeLabel = m.referenced_tweet_type
              ? MENTION_TYPE_LABEL[m.referenced_tweet_type] ||
                m.referenced_tweet_type
              : "Mention";
            const typeColor = m.referenced_tweet_type
              ? MENTION_TYPE_COLOR[m.referenced_tweet_type] || C.muted
              : C.navy;
            const isFBMention = m.platform === "facebook";
            const tweetUrl = isFBMention
              ? `https://www.facebook.com/${m.tweet_id}`
              : m.tweet_id
                ? m.author_username
                  ? `https://x.com/${m.author_username}/status/${m.tweet_id}`
                  : `https://x.com/i/web/status/${m.tweet_id}`
                : null;
            const followers =
              m.author_followers >= 1000
                ? `${(m.author_followers / 1000).toFixed(1)}K`
                : m.author_followers;

            return (
              <Box
                key={m.id}
                sx={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 1,
                  p: 1.75,
                  bgcolor: C.white,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{ display: "flex", gap: 1.25, alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        bgcolor: C.navy,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 11, fontWeight: 800, color: "#fff" }}
                      >
                        {initials}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.charcoal,
                          lineHeight: 1.2,
                        }}
                      >
                        {m.author_name || m.author_username || "Unknown"}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: C.muted }}>
                        {m.author_username ? `@${m.author_username}` : ""}
                        {m.author_followers > 0 && (
                          <Box component="span" sx={{ ml: 0.75 }}>
                            {followers} followers
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: "flex", gap: 0.75, alignItems: "center" }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        px: 0.75,
                        py: 0.2,
                        borderRadius: "3px",
                        bgcolor: typeColor + "18",
                        border: `1px solid ${typeColor}44`,
                        color: typeColor,
                      }}
                    >
                      {typeLabel}
                    </Box>
                    <Typography sx={{ fontSize: 10, color: C.muted }}>
                      {fmtRelTime(m.tweet_created_at)}
                    </Typography>
                  </Box>
                </Box>
                {(() => {
                  const isLong = m.text && m.text.length > POST_TRUNCATE;
                  const isExpanded = expandedPosts.has(m.id);
                  const displayText =
                    isLong && !isExpanded
                      ? `${m.text.slice(0, POST_TRUNCATE)}…`
                      : m.text;
                  return (
                    <Box sx={{ mb: 1.25 }}>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: C.charcoal,
                          lineHeight: 1.65,
                          wordBreak: "break-word",
                        }}
                      >
                        <HighlightSearchTerms
                          text={displayText}
                          searchQuery={effectiveSearchQuery}
                        />
                      </Typography>
                      {isLong && (
                        <Box
                          component="button"
                          onClick={() => togglePostExpand(m.id)}
                          sx={{
                            mt: 0.5,
                            fontSize: 10,
                            fontWeight: 700,
                            color: isFBMention ? "#1877f2" : C.navy,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            p: 0,
                          }}
                        >
                          {isExpanded ? "Show less" : "Show more"}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {[
                    ["❤", m.likes],
                    ["💬", m.replies],
                    ["🔁", m.retweets],
                  ].map(([icon, val]) =>
                    val > 0 ? (
                      <Typography
                        key={icon}
                        sx={{ fontSize: 10, color: C.muted }}
                      >
                        {icon}{" "}
                        {val >= 1000 ? `${(val / 1000).toFixed(1)}K` : val}
                      </Typography>
                    ) : null,
                  )}
                  <Box
                    sx={{
                      ml: "auto",
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    {tweetUrl && onCollectCard && m.replies > 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => onCollectCard(tweetUrl)}
                        sx={{
                          bgcolor: isFBMention ? "#1877f2" : "#0a2240",
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "none",
                          borderRadius: "6px",
                          px: 1.5,
                          py: 0.4,
                          minWidth: 0,
                          "&:hover": {
                            bgcolor: isFBMention ? "#1464cc" : "#061828",
                          },
                        }}
                      >
                        {isFBMention ? "Browse Comments" : "Browse Replies"}
                      </Button>
                    )}
                    {tweetUrl && (
                      <Box
                        component="a"
                        href={tweetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: 10,
                          color: isFBMention ? "#1877f2" : C.navy,
                          fontWeight: 600,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {isFBMention ? "View on Facebook →" : "View on X →"}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}

          {totalPages > 1 && (
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}
            >
              <Box
                component="button"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.5,
                  border: `1px solid ${C.border}`,
                  bgcolor: C.white,
                  color: C.navy,
                  borderRadius: 1,
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                  opacity: page <= 1 ? 0.4 : 1,
                }}
              >
                ← Prev
              </Box>
              <Typography
                sx={{ fontSize: 11, color: C.muted, alignSelf: "center" }}
              >
                {page} / {totalPages}
              </Typography>
              <Box
                component="button"
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.5,
                  border: `1px solid ${C.border}`,
                  bgcolor: C.white,
                  color: C.navy,
                  borderRadius: 1,
                  cursor: page >= totalPages ? "not-allowed" : "pointer",
                  opacity: page >= totalPages ? 0.4 : 1,
                }}
              >
                Next →
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Tab Summary Card (Prediction Markets / Finance / Campaign left column) ───
const TAB_SUMMARY_TITLES = {
  "Prediction Markets": {
    title: "Prediction Market Intelligence",
    sub: "Kalshi-implied signals · campaign race",
  },
  Finance: {
    title: "Finance Intelligence",
    sub: "Fundraising position & strategic read",
  },
  Campaign: {
    title: "Campaign Intelligence",
    sub: "Race overview & strategic priorities",
  },
  Policy: {
    title: "Policy Intelligence",
    sub: "Issue landscape & candidate positions",
  },
};

const TabSummaryCard = ({ tabName, summary, loading, onRefresh }) => {
  const { title, sub } = TAB_SUMMARY_TITLES[tabName] || {
    title: tabName,
    sub: "",
  };
  const isReady = summary?.status === "completed" && summary?.headline;
  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.09em",
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
            {sub} ·{" "}
            {loading && !isReady ? (
              <Box component="span" sx={{ color: C.blue, fontWeight: 600 }}>
                Analyzing…
              </Box>
            ) : isReady ? (
              <Box component="span" sx={{ color: "#22c55e", fontWeight: 700 }}>
                Updated {fmtUpdatedAt(summary.generated_at)}
              </Box>
            ) : (
              <Box component="span">Loading</Box>
            )}
          </Typography>
        </Box>
        <Box
          component="button"
          onClick={onRefresh}
          disabled={loading}
          title={`Refresh ${title}`}
          sx={{
            background: "none",
            border: "none",
            p: 0,
            lineHeight: 1,
            cursor: loading ? "not-allowed" : "pointer",
            color: C.muted,
            fontSize: 15,
            display: "flex",
            alignItems: "center",
            opacity: loading ? 0.4 : 0.7,
            "&:hover": { opacity: 1, color: C.navy },
            animation: loading ? "spin 1s linear infinite" : "none",
            "@keyframes spin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        >
          ↻
        </Box>
      </Box>

      {/* Loading skeleton */}
      {loading && !isReady && <IntelLoader />}

      {/* Content */}
      {isReady && (
        <Box>
          {/* Headline */}
          <Box
            sx={{
              bgcolor: "#f0f4f8",
              border: `1px solid ${C.border}`,
              borderLeft: `4px solid ${C.navy}`,
              borderRadius: "0 4px 4px 0",
              px: 2,
              py: 1.25,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 800,
                color: C.charcoal,
                lineHeight: 1.5,
                fontStyle: "italic",
              }}
            >
              {summary.headline}
            </Typography>
          </Box>

          {/* Signals */}
          {Array.isArray(summary.signals) && summary.signals.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
              {summary.signals.map((sig, i) => {
                const sigColors = {
                  existential: C.red,
                  momentum: C.green,
                  opportunity: C.blue,
                  context: C.muted,
                };
                const col = sigColors[sig.type] || C.muted;
                return (
                  <Box
                    key={i}
                    sx={{
                      bgcolor: col + "14",
                      border: `1px solid ${col}44`,
                      borderRadius: 1,
                      px: 1.25,
                      py: 0.5,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.75,
                      maxWidth: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 8,
                        fontWeight: 800,
                        color: col,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        pt: 0.2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sig.type}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 10, color: C.charcoal, lineHeight: 1.5 }}
                    >
                      {sig.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Summary */}
          {summary.summary && (
            <Typography
              sx={{ fontSize: 11, color: C.charcoal, lineHeight: 1.8, mb: 1.5 }}
            >
              {summary.summary}
            </Typography>
          )}

          {/* Summary macro (prediction markets only) */}
          {summary.summary_macro && (
            <Typography
              sx={{ fontSize: 11, color: C.muted, lineHeight: 1.8, mb: 1.5 }}
            >
              {summary.summary_macro}
            </Typography>
          )}

          {/* Key findings */}
          {Array.isArray(summary.key_findings) &&
            summary.key_findings.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.navy,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    mb: 0.75,
                  }}
                >
                  Key Findings
                </Typography>
                {summary.key_findings.map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        bgcolor: C.navy,
                        mt: 0.6,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 11, color: C.charcoal, lineHeight: 1.6 }}
                    >
                      {f}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

          {/* Opponent lens */}
          {summary.opponent_lens && (
            <Box
              sx={{
                bgcolor: "#f8f9fa",
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                px: 2,
                py: 1.5,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: C.red,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 0.5,
                }}
              >
                Opponent Lens
              </Typography>
              <Typography
                sx={{ fontSize: 11, color: C.charcoal, lineHeight: 1.7 }}
              >
                {summary.opponent_lens}
              </Typography>
            </Box>
          )}

          {/* Recommended action */}
          {summary.recommended_action && (
            <Box sx={{ bgcolor: C.navy, borderRadius: 1, px: 2, py: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.5)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: 0.5,
                }}
              >
                Recommended Action
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1.6,
                }}
              >
                {summary.recommended_action}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Polling Tab ──────────────────────────────────────────────────────────
const PollingTab = ({
  kalshi,
  livePolls,
  liveFEC,
  onRefreshPolls,
  pollsRefreshing,
  pollingSummary,
  pollingSummaryLoading,
  cfg = {},
  section = "charts",
  cd = {},
  opponentIntel,
  onGenerateOpponentIntel,
  opponentIntelLoading,
  onSync,
  syncing,
  onSaveManualPolls,
}) => {
  const [slider1, setSlider1] = useState(50);
  const [slider2, setSlider2] = useState(30);
  const [undecidedToCandidate, setUndecidedToCandidate] = useState(50);
  const [showPollForm, setShowPollForm] = useState(false);
  const [formPolls, setFormPolls] = useState([]);
  const [draftPollster, setDraftPollster] = useState("");
  const [draftDate, setDraftDate] = useState("");
  const [draftRows, setDraftRows] = useState([{ name: "", pct: "" }]);
  const [formSaving, setFormSaving] = useState(false);
  const [formMsg, setFormMsg] = useState("");

  const candName = cfg.candidateName || "Candidate";
  const candShort = candName.split(" ").pop();

  // Derive opponent keys from poll data
  const pollKeys =
    cd.pollAverages.length > 0
      ? Object.keys(cd.pollAverages[0]).filter(
          (k) => k !== "candidate" && k !== "Undecided",
        )
      : [];
  const opp1Key = pollKeys[0] || null;
  const opp2Key = pollKeys[1] || null;

  // Map live API candidate names → data keys dynamically
  // Build from both pollKeys and candidatesGop so all candidates resolve correctly
  const CAND_NAME_KEY = {
    [candName]: "candidate",
    [candShort]: "candidate",
    ...Object.fromEntries(
      pollKeys.flatMap((k) => [
        [k, k],
        [k.split(" ").pop(), k],
      ]),
    ),
    ...(cd.candidatesGop || []).reduce((acc, c) => {
      if (!c.name) return acc;
      const lastName = c.name.split(" ").pop();
      const isPrimary = c.name === candName || lastName === candShort;
      const key = isPrimary
        ? "candidate"
        : c.key ||
          pollKeys.find((k) => k.includes(lastName) || lastName?.includes(k)) ||
          lastName;
      acc[c.name] = key;
      acc[lastName] = key;
      return acc;
    }, {}),
  };
  const liveLatest = livePolls?.averages?.length
    ? (() => {
        const obj = livePolls.averages.reduce((acc, { candidate, average }) => {
          const lastName = candidate.split(" ").pop();
          const key =
            CAND_NAME_KEY[candidate] ?? CAND_NAME_KEY[lastName] ?? lastName;
          acc[key] = average;
          return acc;
        }, {});
        const totalKnown = Object.entries(obj)
          .filter(([k]) => k !== "Undecided")
          .reduce((s, [, v]) => s + v, 0);
        if (!("Undecided" in obj))
          obj.Undecided = Math.max(0, Math.round((100 - totalKnown) * 10) / 10);
        return obj;
      })()
    : null;

  const isLive = !!liveLatest;
  const staticLatest = cd.pollAverages[cd.pollAverages.length - 1] || {};
  // Merge static fallback + live: live wins where it has data, static fills gaps
  const latest = liveLatest ? { ...staticLatest, ...liveLatest } : staticLatest;
  const prev = cd.pollAverages[cd.pollAverages.length - 2] || {};
  const hasData = Object.keys(latest).length > 0;

  // CANDIDATES: from field.json candidatesGop (authoritative), then from pollKeys in polling.json.
  // NEVER derived from livePolls — 538 CSV can include non-running candidates (e.g. McMaster).
  const OPP_COLORS = [C.navyMid, C.blue, C.orange, C.muted, "#6d28d9"];
  const CANDIDATES_UNSORTED =
    cd.candidatesGop?.length > 0
      ? cd.candidatesGop.map((c, i) => {
          const lastName = c.name?.split(" ").pop();
          const isPrimary = c.name === candName || lastName === candShort;
          const matchedKey = isPrimary
            ? "candidate"
            : pollKeys.find(
                (k) => k.includes(lastName) || lastName?.includes(k),
              ) || lastName;
          return {
            ...c,
            key: c.key || matchedKey,
            color:
              c.color ||
              (isPrimary
                ? C.red
                : OPP_COLORS[(i > 0 ? i - 1 : 0) % OPP_COLORS.length]),
          };
        })
      : hasData
        ? [
            { name: candName, lane: "", color: C.red, key: "candidate" },
            ...pollKeys.map((key, i) => ({
              name: key,
              lane: "",
              color: OPP_COLORS[i % OPP_COLORS.length],
              key,
            })),
          ]
        : [];
  const CANDIDATES = [...CANDIDATES_UNSORTED].sort((a, b) => {
    const av = latest[a.key];
    const bv = latest[b.key];
    if (av == null || isNaN(Number(av))) return 1;
    if (bv == null || isNaN(Number(bv))) return -1;
    return Number(bv) - Number(av);
  });

  // Generic runoff projection: candidate gains from opp2, opp1, undecideds
  const projCandidate = Math.min(
    99,
    (latest.candidate || 0) +
      ((opp2Key ? latest[opp2Key] || 0 : 0) * slider1) / 100 +
      ((opp1Key ? latest[opp1Key] || 0 : 0) * slider2) / 100 +
      ((latest.Undecided || 0) * undecidedToCandidate) / 100,
  );
  const projOpp1 = opp1Key
    ? Math.max(
        1,
        (latest[opp1Key] || 0) +
          (((opp2Key ? latest[opp2Key] || 0 : 0) * (100 - slider1)) / 100) *
            0.5 +
          (((latest.Undecided || 0) * (100 - undecidedToCandidate)) / 100) *
            0.7,
      )
    : null;
  const runsOff = projCandidate < 50;

  // Trend chart (all candidates over time) — dynamic keys from poll data
  const trendPollKeys =
    cd.pollAverages.length > 0
      ? Object.keys(cd.pollAverages[0]).filter((k) => k !== "candidate")
      : [];
  const TREND_COLORS = [C.navyMid, C.blue, C.orange, C.muted, "#6d28d9"];
  const trendData = {
    labels: cd.pollDates,
    datasets: [
      {
        label: "Candidate",
        data: cd.pollAverages.map((r) => r.candidate),
        borderColor: C.red,
        backgroundColor: C.red + "18",
        borderWidth: 2.5,
        pointRadius: 4,
        tension: 0.3,
        fill: true,
      },
      ...trendPollKeys.map((key, i) => ({
        label: key,
        data: cd.pollAverages.map((r) => r[key]),
        borderColor: TREND_COLORS[i % TREND_COLORS.length],
        backgroundColor: "transparent",
        borderWidth: key === "Undecided" ? 1 : 1.5,
        pointRadius: key === "Undecided" ? 0 : 2,
        tension: 0.3,
        fill: false,
        borderDash:
          key === "Undecided" ? [3, 3] : [i === 0 ? 5 : i === 1 ? 2 : 1, 3],
      })),
    ],
  };
  const trendOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: baseLegend,
      tooltip: {
        ...baseTooltip,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: { ...baseGridScale, grid: { display: false } },
      y: {
        ...baseGridScale,
        min: 0,
        max: 40,
        ticks: { callback: (v) => `${v}%` },
      },
    },
  };

  // Polling/fundraising correlation chart — all opponents with FEC receipts data
  const _corrOppColors = [
    C.navyMid,
    "#7c3aed",
    "#d97706",
    "#059669",
    "#dc2626",
    "#0891b2",
    "#65a30d",
    "#9333ea",
  ];
  const _corrOppEntries = Object.entries(cd.opponentFR || {}).filter(([, fr]) =>
    (fr?.receipts || []).some((v) => v > 0),
  );
  const _targetCorrLen = cd.candidateFR.receipts.length;
  const corrData = {
    labels: cd.fecQuarters,
    datasets: [
      {
        type: "bar",
        label: `${cfg.candidateName?.split(" ").pop() || "Candidate"} Raised`,
        data: cd.candidateFR.receipts,
        backgroundColor: C.red + "99",
        borderColor: C.red,
        borderWidth: 1,
        borderRadius: 2,
        yAxisID: "yRaised",
        order: 4,
      },
      ..._corrOppEntries.map(([name, fr], i) => {
        const raw = fr.receipts || [];
        const data =
          raw.length < _targetCorrLen
            ? [...Array(_targetCorrLen - raw.length).fill(null), ...raw]
            : raw;
        return {
          type: "bar",
          label: `${name.split(" ").pop()} Raised`,
          data,
          backgroundColor: _corrOppColors[i % _corrOppColors.length] + "99",
          borderColor: _corrOppColors[i % _corrOppColors.length],
          borderWidth: 1,
          borderRadius: 2,
          yAxisID: "yRaised",
          order: 5 + i,
        };
      }),
      {
        type: "line",
        label: "Candidate %",
        data: cd.pollAverages.map((r) => r.candidate),
        borderColor: C.red,
        backgroundColor: "transparent",
        borderWidth: 2.5,
        pointRadius: 4,
        tension: 0.3,
        spanGaps: true,
        yAxisID: "yPoll",
        order: 1,
      },
    ],
  };
  const corrOpts = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: baseLegend,
      tooltip: {
        ...baseTooltip,
        callbacks: {
          label: (ctx) =>
            ctx.datasetIndex < 3
              ? ` ${ctx.dataset.label}: ${fmtMoney(ctx.parsed.y)}`
              : ` ${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: { ...baseGridScale, grid: { display: false } },
      yPoll: {
        ...baseGridScale,
        position: "left",
        min: 0,
        max: 35,
        ticks: { callback: (v) => `${v}%` },
        title: { display: true, text: "Poll %", font: { size: 9 } },
      },
      yRaised: {
        ...baseGridScale,
        position: "right",
        grid: { drawOnChartArea: false },
        ticks: { callback: (v) => `$${(v / 1000).toFixed(0)}K` },
        title: { display: true, text: "Quarterly Raised", font: { size: 9 } },
      },
    },
  };

  const SectionLabel = ({ children }) => (
    <Typography
      sx={{
        fontSize: 9,
        fontWeight: 800,
        color: C.muted,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        mb: 1.5,
        mt: 3,
      }}
    >
      {children}
    </Typography>
  );

  // Derived stats for the summary column
  const candPct = latest.candidate ?? 0;
  const opp1Pct = opp1Key ? (latest[opp1Key] ?? 0) : 0;
  const opp2Pct = opp2Key ? (latest[opp2Key] ?? 0) : 0;
  const undecPct = latest.Undecided ?? 0;
  const pollGap = (candPct - opp1Pct).toFixed(1);
  const leader = opp1Pct > candPct ? opp1Key || "opponent" : candShort;
  const undecRunof = Math.round(undecPct * 0.5 + candPct);

  const INSIGHT_BULLETS = hasData
    ? [
        {
          head: opp1Key
            ? candPct >= opp1Pct
              ? `${candShort} leads ${opp1Key} by ${Math.abs(Number(pollGap))}pp`
              : `${opp1Key} leads by ${Math.abs(Number(pollGap))}pp`
            : `${candShort} at ${candPct}%`,
          body: opp1Key
            ? `With ${undecPct}% undecided, the field is fluid. The race turns on late-breaking name-ID and turnout consolidation in the final weeks.`
            : `Current polling shows ${candShort} at ${candPct}%. Upload full poll data to see head-to-head analysis.`,
        },
        {
          head: `${undecPct}% still undecided`,
          body: `An undecided pool this size represents more voters than the current frontrunner's support. Late-deciding voters and debate moments can move double digits.`,
        },
        ...(projCandidate < 50
          ? [
              {
                head: "Runoff is the likeliest path",
                body: `No candidate is near 50%+1. Even if ${candShort} wins half the undecideds they project to ~${undecRunof}% — potentially short. A top-2 finish sets up a favorable 1v1.`,
              },
            ]
          : [
              {
                head: `${candShort} projects above 50%`,
                body: `Under current consolidation assumptions ${candShort} projects to ${projCandidate.toFixed(1)}% — above the 50%+1 threshold for an outright primary win.`,
              },
            ]),
      ]
    : [];

  // ── INTELLIGENCE section (left column) ──────────────────────────────────────
  const aiBullets =
    pollingSummary?.status === "completed" && pollingSummary?.bullets?.length
      ? pollingSummary.bullets
      : null;
  // Don't show stale AI bullets that say "no data" when data now exists
  const aiStale =
    hasData &&
    aiBullets?.some((b) =>
      (b.head || "").toLowerCase().includes("no polling data"),
    );
  const displayBullets = (aiStale ? null : aiBullets) || INSIGHT_BULLETS;

  if (section === "intelligence")
    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                color: C.navy,
                textTransform: "uppercase",
                letterSpacing: "0.09em",
              }}
            >
              Polling Intelligence
            </Typography>
            <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
              {cfg.office || "Primary"}
              {cfg.state ? ` · ${cfg.state}` : ""}
              {cfg.primaryDate
                ? ` · ${new Date(cfg.primaryDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                : ""}{" "}
              ·{" "}
              {pollingSummaryLoading && !aiBullets ? (
                <Box component="span" sx={{ color: C.blue, fontWeight: 600 }}>
                  Analyzing latest data…
                </Box>
              ) : aiBullets ? (
                <Box
                  component="span"
                  sx={{ color: "#22c55e", fontWeight: 700 }}
                >
                  Updated {fmtUpdatedAt(pollingSummary.generated_at)}
                </Box>
              ) : (
                <Box component="span">Based on available public polling</Box>
              )}
            </Typography>
          </Box>
          <Box
            component="button"
            onClick={onRefreshPolls}
            disabled={pollsRefreshing || pollingSummaryLoading}
            title="Refresh polling data & regenerate analysis"
            sx={{
              background: "none",
              border: "none",
              p: 0,
              lineHeight: 1,
              cursor:
                pollsRefreshing || pollingSummaryLoading
                  ? "not-allowed"
                  : "pointer",
              color: C.muted,
              fontSize: 15,
              display: "flex",
              alignItems: "center",
              opacity: pollsRefreshing || pollingSummaryLoading ? 0.4 : 0.7,
              "&:hover": { opacity: 1, color: C.navy },
              animation:
                pollsRefreshing || pollingSummaryLoading
                  ? "spin 1s linear infinite"
                  : "none",
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          >
            ↻
          </Box>
        </Box>
        {aiStale && (
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 1,
              bgcolor: "#fffbeb",
              border: `1px solid #fbbf24`,
              borderRadius: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 10, color: "#92400e" }}>
              Analysis was generated before polling data was available — click ↻
              to update.
            </Typography>
          </Box>
        )}
        {pollingSummaryLoading && !aiBullets ? (
          <IntelLoader bars={[80, 100, 60, 90, 70, 85, 70]} />
        ) : (
          displayBullets.map((b, i) => (
            <Box
              key={i}
              sx={{
                mb: 2.5,
                pb: 2.5,
                borderBottom:
                  i < displayBullets.length - 1
                    ? `1px solid ${C.border}`
                    : "none",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#f0f4f8",
                  border: `1px solid ${C.border}`,
                  borderLeft: `4px solid ${C.navy}`,
                  borderRadius: "0 4px 4px 0",
                  px: 2,
                  py: 1.25,
                  mb: 1.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: C.charcoal,
                    lineHeight: 1.5,
                    fontStyle: "italic",
                  }}
                >
                  {b.head}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 10.5, color: C.muted, lineHeight: 1.75 }}
              >
                {b.body}
              </Typography>
            </Box>
          ))
        )}
        <Box sx={{ mt: 1, pt: 1.5, borderTop: `1px solid ${C.border}` }}>
          <Typography
            sx={{
              fontSize: 9,
              color: C.muted,
              fontStyle: "italic",
              lineHeight: 1.6,
            }}
          >
            {aiBullets
              ? "Analysis based on current polling averages, FEC data, and prediction markets."
              : "Polling data is limited — treat averages as directional."}
          </Typography>
        </Box>
      </Box>
    );

  // ── CHARTS section (right column) ────────────────────────────────────────────
  return (
    <Box>
      {/* ── Section header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 0,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.09em",
            }}
          >
            Polling Summary
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
            Current standings, trends & scenario modeling
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          {onSaveManualPolls && (
            <Box
              component="button"
              onClick={() => {
                setShowPollForm((f) => !f);
                setFormMsg("");
              }}
              title="Enter poll results manually"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                px: 1.25,
                py: 0.4,
                border: `1px solid ${showPollForm ? C.navy : C.border}`,
                borderRadius: 1,
                bgcolor: showPollForm ? C.navy : C.white,
                color: showPollForm ? "#fff" : C.navy,
                cursor: "pointer",
                "&:hover": { borderColor: C.navy },
              }}
            >
              {showPollForm ? "✕ Close" : "+ Add Poll"}
            </Box>
          )}
          {onSync && (
            <Box
              component="button"
              onClick={onSync}
              disabled={syncing}
              title="Sync polling & FEC data"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                px: 1.25,
                py: 0.4,
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                bgcolor: C.white,
                color: syncing ? C.muted : C.navy,
                cursor: syncing ? "not-allowed" : "pointer",
                "&:hover": { borderColor: C.navy },
              }}
            >
              {syncing ? "Syncing…" : "↻ Sync"}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Manual poll entry form ── */}
      {showPollForm &&
        (() => {
          const nameToKey = (name) => {
            const ln = name
              .trim()
              .split(" ")
              .pop()
              .toLowerCase()
              .replace(/[^a-z0-9]/g, "");
            return ln === candShort.toLowerCase() ? "candidate" : ln;
          };

          const addPoll = () => {
            if (!draftPollster.trim() || !draftDate) return;
            const cands = {};
            for (const row of draftRows) {
              if (!row.name.trim()) continue;
              const v = parseFloat(row.pct);
              if (!isNaN(v)) cands[nameToKey(row.name)] = v;
            }
            if (!Object.keys(cands).length) return;
            setFormPolls((prev) => [
              ...prev,
              {
                pollster: draftPollster.trim(),
                end_date: draftDate,
                candidates: cands,
              },
            ]);
            setDraftPollster("");
            setDraftDate("");
            setDraftRows([{ name: "", pct: "" }]);
          };

          const savePollsToS3 = () => {
            if (!formPolls.length || formSaving) return;
            setFormSaving(true);
            setFormMsg("");
            onSaveManualPolls(formPolls)
              .then(() => {
                setFormMsg(
                  `${formPolls.length} poll${formPolls.length > 1 ? "s" : ""} saved`,
                );
                setFormPolls([]);
                setShowPollForm(false);
              })
              .catch((e) =>
                setFormMsg(typeof e === "string" ? e : "Save failed"),
              )
              .finally(() => setFormSaving(false));
          };

          const inputSx = {
            fontSize: 11,
            px: 0.75,
            py: 0.4,
            border: `1px solid ${C.border}`,
            borderRadius: "4px",
            outline: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
          };

          return (
            <Box
              sx={{
                mt: 1.5,
                mb: 1.5,
                p: 1.5,
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.navy,
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}
              >
                New Poll Entry
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 1.25 }}>
                <Box sx={{ flex: 2 }}>
                  <Typography sx={{ fontSize: 9, color: C.muted, mb: 0.25 }}>
                    Pollster
                  </Typography>
                  <Box
                    component="input"
                    placeholder="e.g. Trafalgar Group"
                    value={draftPollster}
                    onChange={(e) => setDraftPollster(e.target.value)}
                    sx={{ ...inputSx, width: "100%" }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 9, color: C.muted, mb: 0.25 }}>
                    End Date
                  </Typography>
                  <Box
                    component="input"
                    type="date"
                    value={draftDate}
                    onChange={(e) => setDraftDate(e.target.value)}
                    sx={{ ...inputSx, width: "100%" }}
                  />
                </Box>
              </Box>

              {/* Candidate rows */}
              <Typography sx={{ fontSize: 9, color: C.muted, mb: 0.5 }}>
                Candidates
              </Typography>
              {draftRows.map((row, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    gap: 0.75,
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Box
                    component="input"
                    placeholder="Candidate name"
                    value={row.name}
                    onChange={(e) =>
                      setDraftRows((prev) =>
                        prev.map((r, j) =>
                          j === i ? { ...r, name: e.target.value } : r,
                        ),
                      )
                    }
                    sx={{ ...inputSx, flex: 3 }}
                  />
                  <Box
                    component="input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="%"
                    value={row.pct}
                    onChange={(e) =>
                      setDraftRows((prev) =>
                        prev.map((r, j) =>
                          j === i ? { ...r, pct: e.target.value } : r,
                        ),
                      )
                    }
                    sx={{ ...inputSx, width: 60 }}
                  />
                  <Typography sx={{ fontSize: 10, color: C.muted, mr: 0.5 }}>
                    %
                  </Typography>
                  {draftRows.length > 1 && (
                    <Box
                      component="button"
                      onClick={() =>
                        setDraftRows((prev) => prev.filter((_, j) => j !== i))
                      }
                      sx={{
                        fontSize: 12,
                        lineHeight: 1,
                        background: "none",
                        border: "none",
                        color: C.muted,
                        cursor: "pointer",
                        px: 0.25,
                        "&:hover": { color: C.red },
                      }}
                    >
                      ×
                    </Box>
                  )}
                </Box>
              ))}
              <Box
                component="button"
                onClick={() =>
                  setDraftRows((prev) => [...prev, { name: "", pct: "" }])
                }
                sx={{
                  fontSize: 10,
                  color: C.navy,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  px: 0,
                  mb: 1,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                + Add candidate
              </Box>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Box
                  component="button"
                  onClick={addPoll}
                  disabled={!draftPollster || !draftDate}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    px: 1.5,
                    py: 0.4,
                    border: `1px solid ${C.navy}`,
                    borderRadius: 1,
                    bgcolor: C.navy,
                    color: "#fff",
                    cursor:
                      !draftPollster || !draftDate ? "not-allowed" : "pointer",
                    opacity: !draftPollster || !draftDate ? 0.5 : 1,
                  }}
                >
                  + Add to List
                </Box>
                <Box
                  component="button"
                  onClick={() => {
                    setDraftPollster("");
                    setDraftDate("");
                    setDraftRows([{ name: "", pct: "" }]);
                  }}
                  sx={{
                    fontSize: 10,
                    px: 1,
                    py: 0.4,
                    border: `1px solid ${C.border}`,
                    borderRadius: 1,
                    bgcolor: C.white,
                    color: C.muted,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </Box>
              </Box>

              {formPolls.length > 0 && (
                <Box
                  sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${C.border}` }}
                >
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      mb: 0.75,
                    }}
                  >
                    Pending — {formPolls.length} poll
                    {formPolls.length > 1 ? "s" : ""}
                  </Typography>
                  {formPolls.map((p, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                        px: 1,
                        py: 0.4,
                        bgcolor: C.white,
                        border: `1px solid ${C.border}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 9, color: C.charcoal }}>
                        {p.pollster} · {p.end_date} ·{" "}
                        {Object.entries(p.candidates)
                          .map(([k, v]) => `${k}: ${v}%`)
                          .join("  ")}
                      </Typography>
                      <Box
                        component="button"
                        onClick={() =>
                          setFormPolls((prev) => prev.filter((_, j) => j !== i))
                        }
                        sx={{
                          ml: 1,
                          fontSize: 11,
                          lineHeight: 1,
                          background: "none",
                          border: "none",
                          color: C.muted,
                          cursor: "pointer",
                          "&:hover": { color: C.red },
                        }}
                      >
                        ×
                      </Box>
                    </Box>
                  ))}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <Box
                      component="button"
                      onClick={savePollsToS3}
                      disabled={formSaving}
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        px: 1.5,
                        py: 0.4,
                        border: `1px solid #16a34a`,
                        borderRadius: 1,
                        bgcolor: "#16a34a",
                        color: "#fff",
                        cursor: formSaving ? "not-allowed" : "pointer",
                        opacity: formSaving ? 0.6 : 1,
                      }}
                    >
                      {formSaving
                        ? "Saving…"
                        : `Save ${formPolls.length} Poll${formPolls.length > 1 ? "s" : ""} to S3`}
                    </Box>
                    <Box
                      component="button"
                      onClick={() => setFormPolls([])}
                      sx={{
                        fontSize: 10,
                        px: 1,
                        py: 0.4,
                        border: `1px solid ${C.border}`,
                        borderRadius: 1,
                        bgcolor: C.white,
                        color: C.muted,
                        cursor: "pointer",
                      }}
                    >
                      Clear All
                    </Box>
                    {formMsg && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: formMsg.includes("saved") ? "#16a34a" : C.red,
                        }}
                      >
                        {formMsg}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })()}

      {/* ── Charts and data ── */}
      <Box>
        {!hasData && !pollingSummaryLoading && !showPollForm && (
          <Box sx={{ py: 3, textAlign: "center", color: C.muted }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>
              No polling data
            </Typography>
            <Typography sx={{ fontSize: 10, mb: 1.5 }}>
              Use "+ Add Poll" above to enter poll results manually, or upload{" "}
              <code>data/polling.json</code> to S3.
            </Typography>
          </Box>
        )}
        {hasData && (
          <>
            {/* ── Current Standing ── */}
            <SectionLabel>
              Current Standing ·{" "}
              {isLive
                ? new Date(livePolls.cached_at).toLocaleDateString("en-US", {
                    month: "short",
                    year: "numeric",
                  })
                : cd.pollDates[cd.pollDates.length - 1] || "Latest"}
            </SectionLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 0.5 }}>
              {CANDIDATES.map((c) => {
                const cur = latest[c.key];
                const rawChg = isLive
                  ? null
                  : parseFloat((latest[c.key] - prev[c.key]).toFixed(1));
                const chg = rawChg !== null && isNaN(rawChg) ? null : rawChg;
                const up = chg !== null && chg > 0;
                const dn = chg !== null && chg < 0;
                return (
                  <Box
                    key={c.key}
                    sx={{
                      bgcolor: C.white,
                      border: `1px solid ${C.border}`,
                      borderTop: `3px solid ${c.color}`,
                      p: 1.5,
                      borderRadius: 1,
                      flex: "1 1 160px",
                      minWidth: 150,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        mb: 0.25,
                      }}
                    >
                      {c.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 28,
                          fontWeight: 800,
                          color: c.color,
                          lineHeight: 1,
                        }}
                      >
                        {cur != null && !isNaN(Number(cur)) ? `${cur}%` : "—"}
                      </Typography>
                      {chg !== null && (up || dn) ? (
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: up ? C.green : C.red,
                          }}
                        >
                          {up ? "↑" : "↓"}
                          {Math.abs(chg)}pp
                        </Typography>
                      ) : chg === 0 ? (
                        <Typography sx={{ fontSize: 10, color: C.muted }}>
                          —
                        </Typography>
                      ) : isLive ? (
                        <Typography
                          sx={{
                            fontSize: 9,
                            color: C.muted,
                            fontStyle: "italic",
                          }}
                        >
                          live avg
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: C.muted,
                        mt: 0.4,
                        fontStyle: "italic",
                      }}
                    >
                      {c.lane}
                    </Typography>
                  </Box>
                );
              })}
              {/* Undecided */}
              <Box
                sx={{
                  bgcolor: C.white,
                  border: `1px solid ${C.border}`,
                  borderTop: `3px solid ${C.muted}`,
                  p: 1.5,
                  borderRadius: 1,
                  flex: "1 1 160px",
                  minWidth: 150,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.25,
                  }}
                >
                  Undecided
                </Typography>
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: C.muted,
                    lineHeight: 1,
                  }}
                >
                  {latest.Undecided != null && !isNaN(Number(latest.Undecided))
                    ? `${latest.Undecided}%`
                    : "—"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: C.muted,
                    mt: 0.4,
                    fontStyle: "italic",
                  }}
                >
                  Up for grabs · runoff key
                </Typography>
              </Box>
            </Box>

            {/* ── Momentum Table ── */}
            <SectionLabel>Momentum · vs Prior Month</SectionLabel>
            <Box
              sx={{
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                overflow: "hidden",
                mb: 0.5,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: C.bg }}>
                    {[
                      "Candidate",
                      "Lane",
                      "Current",
                      "Prior",
                      "Change",
                      "Trend",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          padding: "6px 10px",
                          borderBottom: `1px solid ${C.border}`,
                          textAlign: "left",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CANDIDATES.map((c, i) => {
                    const cur = latest[c.key];
                    const pri = prev[c.key];
                    const rawChg = isLive ? null : cur - pri;
                    const chg =
                      rawChg !== null && isNaN(rawChg) ? null : rawChg;
                    const noData = cur == null || isNaN(Number(cur));
                    const trend =
                      chg === null
                        ? isLive
                          ? "Live"
                          : "—"
                        : chg > 1
                          ? "Ascending"
                          : chg < -1
                            ? "Declining"
                            : "Stagnant";
                    const trendColor =
                      chg === null
                        ? isLive
                          ? C.blue
                          : C.muted
                        : chg > 1
                          ? C.green
                          : chg < -1
                            ? C.red
                            : C.muted;
                    return (
                      <tr
                        key={c.key}
                        style={{
                          backgroundColor:
                            i % 2 === 1 ? C.rowAlt : "transparent",
                        }}
                      >
                        <td
                          style={{
                            padding: "7px 10px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: c.color,
                          }}
                        >
                          {c.name}
                        </td>
                        <td
                          style={{
                            padding: "7px 10px",
                            fontSize: 10,
                            color: C.muted,
                          }}
                        >
                          {c.lane || "—"}
                        </td>
                        <td
                          style={{
                            padding: "7px 10px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: C.charcoal,
                          }}
                        >
                          {noData ? "—" : `${cur}%`}
                        </td>
                        <td
                          style={{
                            padding: "7px 10px",
                            fontSize: 11,
                            color: C.muted,
                          }}
                        >
                          {pri != null && !isNaN(Number(pri)) ? `${pri}%` : "—"}
                        </td>
                        <td
                          style={{
                            padding: "7px 10px",
                            fontSize: 11,
                            fontWeight: 700,
                            color:
                              chg === null
                                ? C.muted
                                : chg > 0
                                  ? C.green
                                  : chg < 0
                                    ? C.red
                                    : C.muted,
                          }}
                        >
                          {chg === null
                            ? "—"
                            : `${chg > 0 ? "+" : ""}${chg.toFixed(1)}pp`}
                        </td>
                        <td style={{ padding: "7px 10px" }}>
                          <Box
                            component="span"
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              px: 0.75,
                              py: 0.2,
                              borderRadius: "3px",
                              bgcolor: trendColor + "18",
                              border: `1px solid ${trendColor}44`,
                              color: trendColor,
                            }}
                          >
                            {trend}
                          </Box>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>

            {/* ── Poll Trend Chart ── */}
            <SectionLabel>Poll Trend · Sep 2025 – Mar 2026</SectionLabel>
            <Box sx={{ height: 260, mb: 0.5 }}>
              <Line data={trendData} options={trendOpts} />
            </Box>

            {/* ── Runoff Scenario Modeler ── */}
            <SectionLabel>Runoff Scenario Modeler</SectionLabel>
            <Box
              sx={{ bgcolor: C.navyDark, borderRadius: 1.5, p: 2.5, mb: 0.5 }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.55)",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                Adjust the sliders to model how consolidating votes from other
                candidates affects {candShort}'s runoff position.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {[
                  ...(opp2Key
                    ? [
                        {
                          label: `${opp2Key} voters (${latest[opp2Key] ?? 0}%) → ${candShort}`,
                          val: slider1,
                          set: setSlider1,
                          note: "Vote consolidation",
                        },
                      ]
                    : []),
                  ...(opp1Key
                    ? [
                        {
                          label: `${opp1Key} voters (${latest[opp1Key] ?? 0}%) → ${candShort}`,
                          val: slider2,
                          set: setSlider2,
                          note: "Vote consolidation",
                        },
                      ]
                    : []),
                  {
                    label: `Undecideds (${latest.Undecided ?? 0}%) → ${candShort}`,
                    val: undecidedToCandidate,
                    set: setUndecidedToCandidate,
                    note: "Late-breaking consolidation",
                  },
                ].map(({ label, val, set, note }) => (
                  <Box key={label} sx={{ flex: "1 1 200px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "rgba(255,255,255,0.7)",
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 10, fontWeight: 800, color: C.red }}
                      >
                        {val}%
                      </Typography>
                    </Box>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={val}
                      onChange={(e) => set(Number(e.target.value))}
                      style={{
                        width: "100%",
                        accentColor: C.red,
                        cursor: "pointer",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "rgba(255,255,255,0.35)",
                        mt: 0.25,
                      }}
                    >
                      {note}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  mt: 2.5,
                  pt: 2,
                  borderTop: "1px solid rgba(255,255,255,0.1)",
                  display: "flex",
                  gap: 3,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: "rgba(255,255,255,0.4)",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    Projected {candShort}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 32,
                      fontWeight: 800,
                      color: projCandidate >= 50 ? "#22c55e" : C.orange,
                      lineHeight: 1,
                    }}
                  >
                    {projCandidate.toFixed(1)}%
                  </Typography>
                </Box>
                {projOpp1 != null && opp1Key && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "rgba(255,255,255,0.4)",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      Projected {opp1Key}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 32,
                        fontWeight: 800,
                        color: "rgba(255,255,255,0.6)",
                        lineHeight: 1,
                      }}
                    >
                      {projOpp1.toFixed(1)}%
                    </Typography>
                  </Box>
                )}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      bgcolor: runsOff ? C.orange + "22" : "#22c55e22",
                      border: `1px solid ${runsOff ? C.orange + "55" : "#22c55e55"}`,
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: runsOff ? C.orange : "#22c55e",
                      }}
                    >
                      {runsOff
                        ? `→ Runoff likely · ${candShort} needs top-2 finish`
                        : "✓ Primary win · Clears 50%+1 threshold"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* ── Polling / Fundraising Correlation ── */}
            <SectionLabel>Polling & Fundraising Correlation</SectionLabel>
            <Box sx={{ height: 300 }}>
              <Bar data={corrData} options={corrOpts} />
            </Box>
            <Typography
              sx={{ fontSize: 9, color: C.muted, fontStyle: "italic", mt: 1.5 }}
            >
              Bars = quarterly fundraising (right axis) · Lines = poll % where
              available (left axis) · FEC quarters Q3 2024 – Q1 2026
            </Typography>

            {/* ── Live FEC Totals ── */}
            {liveFEC?.totals && (
              <>
                <SectionLabel>
                  FEC Fundraising Totals ·{" "}
                  {liveFEC.cached_at
                    ? new Date(liveFEC.cached_at).toLocaleDateString("en-US", {
                        month: "short",
                        year: "numeric",
                      })
                    : "Live"}
                </SectionLabel>
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 0.5 }}
                >
                  {Object.entries(liveFEC.totals)
                    .filter(([, t]) => !t.error)
                    .map(([name, t]) => {
                      const cand = CANDIDATES.find((c) =>
                        name.includes(c.name.split(" ").pop()),
                      );
                      const color = cand?.color || C.muted;
                      return (
                        <Box
                          key={name}
                          sx={{
                            bgcolor: C.white,
                            border: `1px solid ${C.border}`,
                            borderTop: `3px solid ${color}`,
                            p: 1.5,
                            borderRadius: 1,
                            flex: "1 1 180px",
                            minWidth: 170,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: C.muted,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              mb: 0.5,
                            }}
                          >
                            {name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Box>
                              <Typography sx={{ fontSize: 8, color: C.muted }}>
                                Cash on Hand
                              </Typography>
                              <Typography
                                sx={{ fontSize: 14, fontWeight: 800, color }}
                              >
                                {t.cash_on_hand != null
                                  ? `$${(t.cash_on_hand / 1e6).toFixed(2)}M`
                                  : "—"}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 8, color: C.muted }}>
                                Cycle Raised
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: C.charcoal,
                                }}
                              >
                                {t.receipts != null
                                  ? `$${(t.receipts / 1e6).toFixed(2)}M`
                                  : "—"}
                              </Typography>
                            </Box>
                          </Box>
                          {t.coverage_end_date && (
                            <Typography
                              sx={{ fontSize: 8, color: C.muted, mt: 0.5 }}
                            >
                              thru {t.coverage_end_date}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                </Box>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: C.muted,
                    fontStyle: "italic",
                    mt: 0.5,
                  }}
                >
                  Source: OpenFEC · Federal candidates only · State candidates
                  not in FEC
                </Typography>
              </>
            )}
          </>
        )}{" "}
        {/* end hasData guard */}
      </Box>

      <OpponentLens
        cd={cd}
        cfg={cfg}
        liveFEC={liveFEC}
        opponentIntel={opponentIntel}
        onGenerate={onGenerateOpponentIntel}
        generating={opponentIntelLoading}
      />
    </Box>
  );
};

// ─── Prediction Markets Tab ────────────────────────────────────────────────
const MacroTab = ({
  kalshi,
  kalshiLoading,
  fetchedAt,
  onRefresh,
  kalshiHistory,
  historyLoading,
  cfg = {},
}) => {
  const [mktModal, setMktModal] = useState(null);

  const fmtTime = (d) =>
    d
      ? d.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
        })
      : null;

  // Extract a single headline probability from a series
  const getProb = (seriesKey, matchFn) => {
    const mkts = kalshi?.series?.[seriesKey]?.markets || [];
    const m = matchFn ? mkts.find(matchFn) : mkts[0];
    return m?.last_price != null ? Math.round(m.last_price * 100) : null;
  };

  const getMktCount = (seriesKey) =>
    (kalshi?.series?.[seriesKey]?.markets || []).length;

  // Live values (static fallback)
  const houseDProb =
    getProb(
      "CONTROLH",
      (m) =>
        m.ticker?.endsWith("-D") || m.title?.toLowerCase().includes("democrat"),
    ) ?? 86;
  const houseRProb =
    getProb(
      "CONTROLH",
      (m) =>
        m.ticker?.endsWith("-R") ||
        m.title?.toLowerCase().includes("republican"),
    ) ?? 14;
  const senateRProb =
    getProb(
      "CONTROLS",
      (m) =>
        m.ticker?.endsWith("-R") ||
        m.title?.toLowerCase().includes("republican"),
    ) ?? 52;
  const recessionProb = getProb("KXRECSSNBER", null) ?? 23;
  const approvalMkts = getMktCount("KXAPRPOTUS");
  const genericMkts = getMktCount("KXGENERICBALLOTVOTEHUB");
  const campaignSeries = cfg.kalshiSeries || [];
  const raceSeriesR = campaignSeries.find((s) => /R$/.test(s)) || null;
  const raceSeriesD = campaignSeries.find((s) => /D$/.test(s)) || null;
  const retireSeries = campaignSeries.find((s) => /RETIRE/.test(s));
  const senateSeries = campaignSeries.find((s) => /SENATE/.test(s));
  const candidateLastNameKalshi = (cfg.candidateName || "")
    .split(" ")
    .pop()
    .toLowerCase();
  const scGopWinProb = raceSeriesR
    ? (getProb(
        raceSeriesR,
        (m) =>
          candidateLastNameKalshi &&
          m.title?.toLowerCase().includes(candidateLastNameKalshi),
      ) ?? getProb(raceSeriesR, null))
    : null;
  const scGopMkts = raceSeriesR ? getMktCount(raceSeriesR) : null;
  const scDemMkts = raceSeriesD ? getMktCount(raceSeriesD) : null;
  const retireProb = retireSeries ? (getProb(retireSeries, null) ?? 8) : null;
  const scSenateRProb = senateSeries
    ? (getProb(
        senateSeries,
        (m) =>
          m.ticker?.endsWith("-R") ||
          m.title?.toLowerCase().includes("republican"),
      ) ?? 72)
    : null;
  const tariffChina = getProb("KXTARIFFRATEPRC", null);
  const deportations = getProb("KXDEPORTATIONS", null);

  // Signal card component (American Pledge style)
  const MktCard = ({ label, value, sub, accent, note, info }) => {
    const color =
      accent ||
      (typeof value === "number"
        ? value >= 60
          ? C.green
          : value >= 40
            ? C.orange
            : C.red
        : C.navy);
    return (
      <Box
        onClick={() => info && setMktModal(info)}
        sx={{
          bgcolor: C.white,
          border: `1px solid ${C.border}`,
          borderTop: `3px solid ${color}`,
          p: 1.5,
          borderRadius: 1,
          flex: "1 1 150px",
          minWidth: 140,
          cursor: info ? "pointer" : "default",
          transition: "border-color 0.15s",
          "&:hover": info
            ? { borderColor: color, boxShadow: `0 2px 8px ${color}22` }
            : {},
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              color: C.muted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              mb: 0.5,
              lineHeight: 1.3,
            }}
          >
            {label}
          </Typography>
          {info && (
            <Typography
              sx={{
                fontSize: 11,
                color: C.muted,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              ⓘ
            </Typography>
          )}
        </Box>
        <Typography
          sx={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}
        >
          {typeof value === "number" ? `${value}%` : value}
        </Typography>
        <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.3 }}>
          market probability
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.2 }}>
            {sub}
          </Typography>
        )}
        {note && (
          <Typography
            sx={{ fontSize: 9, color: C.muted, mt: 0.2, fontStyle: "italic" }}
          >
            {note}
          </Typography>
        )}
      </Box>
    );
  };

  const GroupLabel = ({ children }) => (
    <Typography
      sx={{
        fontSize: 9,
        fontWeight: 800,
        color: C.muted,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        mb: 1,
        mt: 2.5,
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Box>
      {/* ── Header bar ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <img src={kalshiLogo} alt="Kalshi" style={{ height: 24 }} />
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 800,
                color: C.charcoal,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                lineHeight: 1,
              }}
            >
              Prediction Market Signals
            </Typography>
            <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
              Market-implied consensus · political 2026
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {fetchedAt && (
            <Typography
              sx={{ fontSize: 10, color: C.muted, fontStyle: "italic" }}
            >
              Updated {fmtTime(fetchedAt)}
            </Typography>
          )}
          <SpinnerBtn
            loading={kalshiLoading}
            onClick={onRefresh}
            sx={{
              px: 1.5,
              py: 0.6,
              bgcolor: "#00b374",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 1,
              minWidth: 0,
              "&:hover": { bgcolor: "#00995f" },
              "&.Mui-disabled": { bgcolor: "#00b37466", color: "#fff" },
            }}
          >
            ↻ Refresh
          </SpinnerBtn>
        </Box>
      </Box>

      {/* ── What is Kalshi ── */}
      <Box
        sx={{
          border: `1px solid #00b37433`,
          borderLeft: `4px solid #00b374`,
          bgcolor: "#f0fdf8",
          p: 2,
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 11, color: C.charcoal, lineHeight: 1.75 }}>
          <strong>Kalshi</strong> is a regulated U.S. prediction market exchange
          where participants trade contracts on real-world outcomes — including
          election results, economic indicators, and policy decisions. Unlike
          surveys or analyst forecasts, Kalshi prices reflect{" "}
          <strong>actual money at risk</strong>: traders profit only when
          they're right, creating a powerful incentive for accuracy. Academic
          research consistently shows prediction markets outperform traditional
          polling, especially near events when informed participants
          concentrate. The values below represent the{" "}
          <strong>market-implied probability</strong> for each outcome — a
          real-time, independent read on the political environment surrounding
          this campaign.
        </Typography>
      </Box>

      {/* ── National Environment ── */}
      <GroupLabel>National Political Environment</GroupLabel>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        <MktCard
          label="D Wins House 2026"
          value={houseDProb}
          sub={`R wins: ${houseRProb}%`}
          accent={houseDProb >= 60 ? C.red : C.green}
          info={{
            eyebrow: "PREDICTION MARKET · CONTROLH",
            title: `Democrats ${houseDProb}% to Win House 2026`,
            subtitle: "Kalshi · congressional control · midterm cycle",
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "Probability that Democrats win a majority of U.S. House seats in the November 2026 midterms. Kalshi traders put real money at risk — this price reflects the collective wisdom of the most informed participants in the market.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: `A ${houseDProb}% D-wins price signals a strong anti-establishment wave environment. This is a strategic asset for challenger candidates: primary voters angry at DC insiders respond to the outsider-challenger frame. Position messaging around disrupting the status quo before the primary.`,
              },
            ],
          }}
        />
        <MktCard
          label="R Holds Senate 2026"
          value={senateRProb}
          sub="current majority"
          accent={senateRProb >= 50 ? C.green : C.red}
          info={{
            eyebrow: "PREDICTION MARKET · CONTROLS",
            title: `Republicans ${senateRProb}% to Hold Senate`,
            subtitle: "Kalshi · Senate control · midterm cycle",
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "Probability that Republicans retain their Senate majority after the November 2026 midterms.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: "Senate hold probability tracks national R sentiment. In a split-environment (D House gain, R Senate hold), Republican candidates benefit from split-ticket voter mobilization — a candidate who can run on both R-brand loyalty and anti-DC disruption captures both coalition segments.",
              },
            ],
          }}
        />
        <MktCard
          label="Recession Risk 2026"
          value={recessionProb}
          sub="NBER definition"
          accent={recessionProb >= 40 ? C.red : C.orange}
          info={{
            eyebrow: "PREDICTION MARKET · KXRECSSNBER",
            title: `${recessionProb}% Recession Probability 2026`,
            subtitle: "Kalshi · NBER official recession declaration",
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "Market-implied probability that the National Bureau of Economic Research (NBER) officially declares a U.S. recession in 2026. NBER declarations use a broad set of indicators — GDP, employment, income, and spending.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: "Elevated recession risk elevates fiscal-urgency messaging before the June primary sprint. SC primary voters are acutely sensitive to economic anxiety — tariffs, inflation, and cost of living. A candidate who owns the economic stewardship frame (cutting state waste, opposing DC recklessness) converts recession fear into votes.",
              },
            ],
          }}
        />
        <MktCard
          label="Trump Approval"
          value={`${approvalMkts || 8} mkts`}
          sub="RCP composite · Kalshi"
          accent={C.navy}
          info={{
            eyebrow: "PREDICTION MARKET · KXAPRPOTUS",
            title: "Trump Approval Markets",
            subtitle: `Kalshi · ${approvalMkts || 8} active approval markets`,
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "Number of active Kalshi markets tracking Trump's approval rating at various threshold levels (e.g., above/below 44%). Each market prices the probability of his approval crossing a specific percentage.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: "Trump approval in SC drives the coattails environment. High approval strengthens R-brand candidates statewide; declining approval favors the outsider frame. Monitor trajectory — a candidate who mirrors Trump's economic populism captures his coalition while maintaining independent credibility.",
              },
            ],
          }}
        />
        <MktCard
          label="Generic Ballot"
          value={`${genericMkts || 4} mkts`}
          sub="D–R margin weekly"
          accent={C.navy}
          info={{
            eyebrow: "PREDICTION MARKET · KXGENERICBALLOTVOTEHUB",
            title: "Generic Congressional Ballot",
            subtitle: `Kalshi · ${genericMkts || 4} active weekly markets`,
            sections: [
              {
                heading: "WHAT IT MEASURES",
                body: "Weekly Kalshi markets on the aggregate D–R margin in the congressional generic ballot — a leading indicator of midterm wave direction and intensity.",
              },
              {
                heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                body: "The generic ballot is the earliest signal of national wave direction. When D margin on generic ballot expands, SC primary donors respond to urgency — fear of a wave motivates base turnout and opens major donor wallets. Track week-over-week shifts as a fundraising trigger.",
              },
            ],
          }}
        />
      </Box>

      {/* ── Campaign Race Markets — only show if Kalshi has markets for this race ── */}
      {(scGopMkts > 0 ||
        scDemMkts > 0 ||
        retireProb != null ||
        scSenateRProb != null) && (
        <>
          <GroupLabel>
            {cfg.office
              ? `${cfg.office} Race · Kalshi`
              : "Campaign Race · Kalshi"}
          </GroupLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {/* ── GOP candidate-by-candidate leaderboard ── */}
            {scGopMkts > 0 &&
              (() => {
                const gopMarkets = (
                  kalshi?.series?.[raceSeriesR]?.markets || []
                )
                  .slice()
                  .sort((a, b) => (b.last_price || 0) - (a.last_price || 0));
                return (
                  <Box
                    onClick={() =>
                      setMktModal({
                        eyebrow: `PREDICTION MARKET · ${raceSeriesR}`,
                        title: `${cfg.party || "GOP"} ${cfg.office || "Primary"} — Candidate Win Probabilities`,
                        subtitle: `Kalshi · ${scGopMkts} candidates · real money at risk`,
                        sections: [
                          {
                            heading: "WHAT IT MEASURES",
                            body: `Market-implied win probabilities for every candidate in the ${cfg.party || "Republican"} ${cfg.office || "primary"}. Each market prices the probability that a specific candidate wins the nomination. Prices move in real time on news, polls, and endorsements.`,
                          },
                          {
                            heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                            body: `${cfg.candidateName || "The candidate"} is currently priced at ${scGopWinProb != null ? `${scGopWinProb}%` : "—"} — the most direct money-on-the-line read of primary viability. A sustained decline below 15% is a credibility alarm. A rise above 30% signals momentum that major donors will follow.`,
                          },
                        ],
                      })
                    }
                    sx={{
                      bgcolor: C.white,
                      border: `1px solid ${C.border}`,
                      borderTop: `3px solid ${C.red}`,
                      borderRadius: 1,
                      p: 1.5,
                      flex: "1 1 260px",
                      minWidth: 240,
                      cursor: "pointer",
                      "&:hover": { boxShadow: `0 2px 8px ${C.red}22` },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          lineHeight: 1.3,
                        }}
                      >
                        {cfg.party || "GOP"} Primary · Win Probability
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: C.muted,
                          lineHeight: 1,
                          userSelect: "none",
                        }}
                      >
                        ⓘ
                      </Typography>
                    </Box>
                    {gopMarkets.map((m, i) => {
                      const prob =
                        m.last_price != null
                          ? Math.round(m.last_price * 100)
                          : null;
                      const name = m.title || m.ticker;
                      const isOurs =
                        candidateLastNameKalshi &&
                        name.toLowerCase().includes(candidateLastNameKalshi);
                      return (
                        <Box
                          key={m.ticker}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            py: 0.4,
                            borderTop: i > 0 ? `1px solid ${C.border}` : "none",
                            bgcolor: isOurs ? "#f0fdf4" : "transparent",
                            mx: isOurs ? -0.5 : 0,
                            px: isOurs ? 0.5 : 0,
                            borderRadius: isOurs ? 0.5 : 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: isOurs ? 800 : 400,
                              color: isOurs ? C.green : C.navy,
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              mr: 1,
                            }}
                          >
                            {name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 800,
                              color: isOurs
                                ? C.green
                                : prob != null && prob >= 25
                                  ? C.navy
                                  : C.muted,
                              minWidth: 36,
                              textAlign: "right",
                            }}
                          >
                            {prob != null ? `${prob}%` : "—"}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Typography
                      sx={{
                        fontSize: 8,
                        color: C.muted,
                        mt: 0.75,
                        fontStyle: "italic",
                      }}
                    >
                      {raceSeriesR} · Kalshi · real money at risk
                    </Typography>
                  </Box>
                );
              })()}
            {scDemMkts > 0 &&
              (() => {
                const demMarkets = (
                  kalshi?.series?.[raceSeriesD]?.markets || []
                )
                  .slice()
                  .sort((a, b) => (b.last_price || 0) - (a.last_price || 0));
                return (
                  <Box
                    onClick={() =>
                      setMktModal({
                        eyebrow: `PREDICTION MARKET · ${raceSeriesD}`,
                        title: `Democratic ${cfg.office || "Primary"} — Candidate Win Probabilities`,
                        subtitle: `Kalshi · ${scDemMkts} candidates`,
                        sections: [
                          {
                            heading: "WHAT IT MEASURES",
                            body: `Market-implied win probabilities for Democratic ${cfg.office || "primary"} candidates. Identifies the most likely general election opponent.`,
                          },
                          {
                            heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                            body: "Knowing the likely Democratic nominee early allows message testing and contrast development before the primary sprint ends.",
                          },
                        ],
                      })
                    }
                    sx={{
                      bgcolor: C.white,
                      border: `1px solid ${C.border}`,
                      borderTop: `3px solid ${C.blue}`,
                      borderRadius: 1,
                      p: 1.5,
                      flex: "1 1 220px",
                      minWidth: 200,
                      cursor: "pointer",
                      "&:hover": { boxShadow: `0 2px 8px ${C.blue}22` },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          lineHeight: 1.3,
                        }}
                      >
                        Dem Primary · Win Probability
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: C.muted,
                          lineHeight: 1,
                          userSelect: "none",
                        }}
                      >
                        ⓘ
                      </Typography>
                    </Box>
                    {demMarkets.map((m, i) => {
                      const prob =
                        m.last_price != null
                          ? Math.round(m.last_price * 100)
                          : null;
                      return (
                        <Box
                          key={m.ticker}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            py: 0.4,
                            borderTop: i > 0 ? `1px solid ${C.border}` : "none",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: C.navy,
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              mr: 1,
                            }}
                          >
                            {m.title || m.ticker}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 800,
                              color: C.blue,
                              minWidth: 36,
                              textAlign: "right",
                            }}
                          >
                            {prob != null ? `${prob}%` : "—"}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Typography
                      sx={{
                        fontSize: 8,
                        color: C.muted,
                        mt: 0.75,
                        fontStyle: "italic",
                      }}
                    >
                      {raceSeriesD} · Kalshi · real money at risk
                    </Typography>
                  </Box>
                );
              })()}
            {retireProb != null && (
              <MktCard
                label="Candidate Exits Early"
                value={retireProb}
                sub="before midterms · Kalshi"
                accent={retireProb >= 20 ? C.red : C.green}
                info={{
                  eyebrow: `PREDICTION MARKET · ${retireSeries}`,
                  title: `${retireProb}% — Candidate Exits Current Seat`,
                  subtitle: "Kalshi · retirement or departure before midterms",
                  sections: [
                    {
                      heading: "WHAT IT MEASURES",
                      body: "Market-implied probability that the candidate vacates their current seat before the 2026 midterms — which would occur upon winning the target race and being inaugurated.",
                    },
                    {
                      heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                      body: `At ${retireProb}%, the market prices this as a low-probability event — consistent with a candidate actively pursuing the target race. This market moves on major negative news. A sustained rise above 25% is a credibility warning sign requiring immediate earned media response.`,
                    },
                  ],
                }}
              />
            )}
            {scSenateRProb != null && (
              <MktCard
                label={`R Wins ${cfg.state || ""} Senate '26`}
                value={scSenateRProb}
                sub="coattails signal"
                accent={scSenateRProb >= 55 ? C.green : C.orange}
                info={{
                  eyebrow: `PREDICTION MARKET · ${senateSeries}`,
                  title: `Republicans ${scSenateRProb}% to Win Senate Seat`,
                  subtitle: `Kalshi · ${cfg.state || "State"} U.S. Senate 2026 · coattails signal`,
                  sections: [
                    {
                      heading: "WHAT IT MEASURES",
                      body: `Probability that the Republican candidate wins the ${cfg.state || "state"} U.S. Senate race in 2026. Tracks R-brand strength at the top of the ticket.`,
                    },
                    {
                      heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                      body: "A strong Senate R probability signals a favorable top-of-ticket environment. When the Senate and Governor races both lean R, base turnout models favor primary candidates who energize the GOP base. This is a confidence signal for base mobilization messaging.",
                    },
                  ],
                }}
              />
            )}
          </Box>
        </>
      )}

      {/* ── Issue Signals ── */}
      <GroupLabel>
        Issue Environment · {cfg.state || "National"} Voter Salience
      </GroupLabel>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {tariffChina != null && (
          <MktCard
            label="China Tariff ≥30% Jul 2026"
            value={tariffChina}
            sub="trade signal"
            accent={C.orange}
            info={{
              eyebrow: "PREDICTION MARKET · KXTARIFFRATEPRC",
              title: `${tariffChina}% Chance China Tariffs ≥30% by Jul 2026`,
              subtitle: "Kalshi · U.S.–China tariff policy signal",
              sections: [
                {
                  heading: "WHAT IT MEASURES",
                  body: "Probability that U.S. tariffs on Chinese goods remain at or above 30% through July 2026. Reflects market expectations for trade-war escalation or de-escalation.",
                },
                {
                  heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                  body: "SC has significant manufacturing exposure to trade disruption (BMW, Volvo, Boeing supply chain). Elevated tariff probability activates economic anxiety among SC business owners and workers. A candidate with a clear trade-impact narrative — protecting SC jobs while projecting strength — captures both MAGA trade hawks and concerned moderate R business voters.",
                },
              ],
            }}
          />
        )}
        {deportations != null && (
          <MktCard
            label="ICE Removals FY2026"
            value={deportations}
            sub="immigration signal"
            accent={C.navy}
            info={{
              eyebrow: "PREDICTION MARKET · KXDEPORTATIONS",
              title: `ICE Deportations FY2026 — ${deportations}%`,
              subtitle: "Kalshi · enforcement intensity signal",
              sections: [
                {
                  heading: "WHAT IT MEASURES",
                  body: "Kalshi market tracking the probability of a specific ICE removal threshold being met in fiscal year 2026 — a proxy for federal immigration enforcement intensity.",
                },
                {
                  heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                  body: "Immigration enforcement salience among SC primary voters is high. A strong market signal on deportation activity reinforces the case for border-aligned messaging. Position the candidate as the Governor who will use state law enforcement resources to support federal immigration priorities — a high-resonance framing in the SC GOP primary electorate.",
                },
              ],
            }}
          />
        )}
        {tariffChina == null && deportations == null && (
          <Typography
            sx={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}
          >
            {kalshiLoading
              ? "Loading issue markets…"
              : "No active issue markets — connect Kalshi proxy to load live data."}
          </Typography>
        )}
      </Box>

      {/* ── Historical Trends ── */}
      {(() => {
        const maxPoints = Math.max(
          0,
          ...Object.values(kalshiHistory?.history || {}).map(
            (s) => s.candlesticks?.length ?? 0,
          ),
        );
        const isBuilding = maxPoints < 14;
        return (
          <Box sx={{ mb: isBuilding ? 1 : 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <GroupLabel>Market-Implied Trends · Price History</GroupLabel>
              <BackfillButton
                onDone={onRefresh}
                historyEmpty={maxPoints <= 1}
              />
            </Box>
            {isBuilding && maxPoints > 0 && (
              <Typography sx={{ fontSize: 10, color: C.muted, mb: 1.5 }}>
                {maxPoints}-day dataset · accumulates daily · trend lines extend
                as data grows
              </Typography>
            )}
          </Box>
        );
      })()}
      <KalshiTrendCharts
        history={kalshiHistory}
        loading={historyLoading}
        onChartClick={setMktModal}
      />

      {/* ── Narrative Summary ── */}
      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${C.border}` }}>
        <KalshiSignalCard kalshi={kalshi} kalshiLoading={kalshiLoading} />
      </Box>

      {/* ── Disclaimer ── */}
      <Typography
        sx={{
          fontSize: 9,
          color: C.muted,
          fontStyle: "italic",
          mt: 2,
          pt: 1.5,
          borderTop: `1px solid ${C.border}`,
        }}
      >
        Market probabilities derived from Kalshi binary contract mid-prices
        (threshold where yes probability ≈ 50%). Not investment advice. Source:
        Kalshi.com
      </Typography>

      <InfoModal info={mktModal} onClose={() => setMktModal(null)} />
    </Box>
  );
};

// ─── Messaging Tab ─────────────────────────────────────────────────────────
const INTELLIGENCE_URL = process.env.REACT_APP_INTELLIGENCE_API_URL || "";
const IMAGINE_URL = process.env.REACT_APP_IMAGINE_API_URL || "";
const POLLEN_URL =
  process.env.REACT_APP_POLLEN_URL ||
  (INTELLIGENCE_URL
    ? INTELLIGENCE_URL.replace(/\/intelligence$/, "/pollen")
    : "");

const SEGMENTS = [
  { value: "small_dollar", label: "Small-Dollar (WinRed)" },
  { value: "major_donor", label: "Major Donor ($1,000+)" },
  { value: "lapsed", label: "Lapsed (90d+)" },
];
const TONES = [
  { value: "urgent", label: "Urgent / High-Stakes" },
  { value: "optimistic", label: "Momentum / Optimistic" },
  { value: "contrast", label: "Contrast / Opponent" },
];

const LS_INTEL_DATA = "pp_intel_data";
const LS_IMG_DATA = "pp_img_data";
const LS_HASHTAGS = "pp_campaign_hashtags";
const LS_LINK = "pp_campaign_link";

// Campaign image library — upload/manage headshots, logos, templates, aesthetic references in the
// campaign's private S3 bucket (via the PHP /assets route). The 'candidate' headshot is used as an
// OpenAI image-edit reference so generated campaign graphics can feature the candidate's likeness.
function CampaignImageLibrary({ campaignId, C = {} }) {
  const { authFetch } = useAuth();
  const [assets, setAssets] = useState([]);
  const [kind, setKind] = useState("candidate");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const muted = C.muted || "#64748b";
  const navy = C.navy || "#0f2540";
  const border = C.border || "#e2e8f0";

  const load = useCallback(async () => {
    if (!PHP_API_URL || !campaignId) return;
    try {
      const res = await authFetch(
        `${PHP_API_URL}/assets?campaign_id=${campaignId}`,
      );
      const data = await res.json();
      if (res.ok) setAssets(data.assets || []);
    } catch {
      /* ignore */
    }
  }, [campaignId, authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (file) => {
    if (!file || !campaignId) return;
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("campaign_id", String(campaignId));
      fd.append("kind", kind);
      const res = await authFetch(`${PHP_API_URL}/assets`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    setAssets((a) => a.filter((x) => x.id !== id));
    try {
      await authFetch(`${PHP_API_URL}/assets?id=${id}`, { method: "DELETE" });
    } catch {
      /* ignore */
    }
  };

  if (!campaignId) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            color: navy,
            textTransform: "uppercase",
            letterSpacing: "0.09em",
          }}
        >
          Asset Library
        </Typography>
        <Typography sx={{ fontSize: 10, color: muted, mt: 0.25, mb: 1 }}>
          Headshots, logos, templates & aesthetic references · the candidate
          headshot is used when generating graphics that feature them
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
        <Select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          size="small"
          sx={{
            fontSize: 11,
            minWidth: 150,
            "& .MuiSelect-select": { py: 0.6 },
          }}
        >
          <MenuItem value="candidate" sx={{ fontSize: 11 }}>
            Candidate headshot
          </MenuItem>
          <MenuItem value="logo" sx={{ fontSize: 11 }}>
            Logo
          </MenuItem>
          <MenuItem value="template" sx={{ fontSize: 11 }}>
            Template
          </MenuItem>
          <MenuItem value="reference" sx={{ fontSize: 11 }}>
            Reference / aesthetic
          </MenuItem>
        </Select>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          hidden
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          size="small"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "none",
            bgcolor: navy,
            color: "#fff",
            px: 1.6,
            py: 0.6,
            "&:hover": { bgcolor: "#1e3a5f" },
          }}
        >
          {busy ? "Uploading…" : "+ Upload"}
        </Button>
      </Box>

      {err && (
        <Typography sx={{ fontSize: 10, color: C.red || "#dc2626", mb: 1 }}>
          {err}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {assets.length === 0 && (
          <Typography sx={{ fontSize: 10, color: muted, fontStyle: "italic" }}>
            No images yet — upload the candidate's headshot to feature them in
            generated graphics.
          </Typography>
        )}
        {assets.map((a) => (
          <Box
            key={a.id}
            sx={{
              position: "relative",
              width: "20%",
              // height: 64,
              borderRadius: 1,
              overflow: "hidden",
              border: `2px solid ${a.kind === "candidate" ? C.orange || "#c89b3c" : border}`,
            }}
          >
            <Box
              component="img"
              src={a.url}
              alt={a.label || a.kind}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: "rgba(0,0,0,0.6)",
                color: "#fff",
                fontSize: 10,
                textAlign: "center",
                py: "1px",
                textTransform: "capitalize",
              }}
            >
              {a.kind}
            </Box>
            <Box
              onClick={() => remove(a.id)}
              sx={{
                position: "absolute",
                top: 3,
                right: 3,
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.65)",
                color: "#333",
                fontSize: 11,
                lineHeight: "15px",
                textAlign: "center",
                fontWeight: "bold",
                cursor: "pointer",
                "&:hover": { bgcolor: "#dc2626" },
              }}
            >
              ×
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const MessagingTab = ({
  cfg = {},
  kalshi,
  winredData,
  liveTotalRaised,
  liveLastMonth,
  liveVelocityPct,
  liveTotalDonors,
  cd = {},
  liveFEC,
  section = "content",
  activeCampaignId,
  messagingIntelCbRef,
  messagingImageCbRef,
}) => {
  const { authFetch } = useAuth();
  const [segment, setSegment] = useState("small_dollar");
  const [tone, setTone] = useState("urgent");
  const [campaignHashtags, setCampaignHashtags] = useState(() => {
    try {
      return localStorage.getItem(LS_HASHTAGS) || "";
    } catch {
      return "";
    }
  });
  const [campaignLink, setCampaignLink] = useState(() => {
    try {
      return localStorage.getItem(LS_LINK) || "";
    } catch {
      return "";
    }
  });
  const [campaignLinkDraft, setCampaignLinkDraft] = useState(campaignLink);
  useEffect(() => {
    const t = setTimeout(() => {
      setCampaignLink(campaignLinkDraft);
      try {
        localStorage.setItem(LS_LINK, campaignLinkDraft);
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [campaignLinkDraft]); // eslint-disable-line react-hooks/exhaustive-deps
  const [intel, setIntel] = useState(() => {
    try {
      const d = localStorage.getItem(LS_INTEL_DATA);
      return d ? JSON.parse(d) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [imgJob, setImgJob] = useState(() => {
    try {
      const d = localStorage.getItem(LS_IMG_DATA);
      return d ? JSON.parse(d) : null;
    } catch {
      return null;
    }
  });
  const [imgSelected, setImgSelected] = useState(() => {
    try {
      const d = localStorage.getItem(LS_IMG_DATA);
      return d ? JSON.parse(d)?.selected || null : null;
    } catch {
      return null;
    }
  });
  const [imgLoading, setImgLoading] = useState(false);
  const [imgPickerOpen, setImgPickerOpen] = useState(false);
  const [contentTab, setContentTab] = useState("Email");
  const [pollenStatus, setPollenStatus] = useState(null);
  const [pollenPostId, setPollenPostId] = useState(null);
  const [pollenError, setPollenError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState("preview");
  const [viewingPost, setViewingPost] = useState(null);
  const [previewChannel, setPreviewChannel] = useState("facebook");
  const [expandedChannels, setExpandedChannels] = useState({
    facebook: true,
    instagram: false,
    x: false,
    linkedin: false,
    tiktok: false,
    email: false,
    sms: false,
  });
  const [editingChannel, setEditingChannel] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [postingChannel, setPostingChannel] = useState(null);
  const [channelPostStatus, setChannelPostStatus] = useState({});
  const [activeChannels, setActiveChannels] = useState({
    facebook: true,
    instagram: true,
    x: false,
    linkedin: false,
    tiktok: false,
    email: false,
    sms: false,
  });
  const [channelContent, setChannelContent] = useState({
    facebook: { body: "" },
    instagram: { body: "", hashtags: "" },
    x: { body: "" },
    linkedin: { body: "" },
    tiktok: { caption: "", script: "" },
    email: { subject: "", body: "" },
    sms: { body: "" },
  });
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [syncAllStatus, setSyncAllStatus] = useState(null); // null | "loading" | { synced } | "error"

  const fetchPosts = () => {
    if (!POLLEN_URL || !activeCampaignId) return Promise.resolve([]);
    setPostsLoading(true);
    return fetch(`${POLLEN_URL}?campaign_id=${activeCampaignId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.posts) {
          setPosts(d.posts);
          return d.posts;
        }
        return [];
      })
      .catch(() => [])
      .finally(() => setPostsLoading(false));
  };

  useEffect(() => {
    fetchPosts();
  }, [activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncAllStats = () => {
    if (!POLLEN_URL || syncAllStatus === "loading") return;
    setSyncAllStatus("loading");
    fetch(`${POLLEN_URL}/sync-stats`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ campaign_id: activeCampaignId }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) {
          setSyncAllStatus("error");
          setTimeout(() => setSyncAllStatus(null), 4000);
          return;
        }
        setSyncAllStatus(d);
        fetchPosts();
        setTimeout(() => setSyncAllStatus(null), 5000);
      })
      .catch(() => {
        setSyncAllStatus("error");
        setTimeout(() => setSyncAllStatus(null), 4000);
      });
  };

  // ── Build context payload ──────────────────────────────────────────────────
  const buildContext = () => {
    const hMkts = kalshi?.series?.CONTROLH?.markets || [];
    const dWins = hMkts.find(
      (m) =>
        m.ticker?.endsWith("-D") || m.title?.toLowerCase().includes("democrat"),
    );
    const recMkt = (kalshi?.series?.KXRECSSNBER?.markets || [])[0];
    const cdPollAverages = cd.pollAverages || [];
    const latestPoll = cdPollAverages[cdPollAverages.length - 1] || {};
    const msgOppKeys = Object.keys(cd.opponentFR || {});
    const msgOpp1 = msgOppKeys[0];

    return {
      campaign_id: activeCampaignId,
      campaign: {
        candidate: cfg.candidateName,
        office: cfg.office,
        state: cfg.state,
        party: cfg.party,
        primaryDate: cfg.primaryDate,
        electionDate: cfg.electionDate,
        partyBaseline: cfg.partyBaseline,
        daysToPrimary: daysUntil(cfg.primaryDate),
        stage: detectStage(cfg.primaryDate, cfg.electionDate).label,
        opponents: msgOppKeys.length
          ? msgOppKeys
          : cfg.opponentsLabel
            ? cfg.opponentsLabel.split(", ")
            : [],
      },
      kalshi: {
        dWinsHouseProb:
          dWins?.last_price != null ? Math.round(dWins.last_price * 100) : 86,
        recessionProb:
          recMkt?.last_price != null ? Math.round(recMkt.last_price * 100) : 23,
        approvalMarkets: (kalshi?.series?.KXAPRPOTUS?.markets || []).length,
        genericBallotMarkets: (
          kalshi?.series?.KXGENERICBALLOTVOTEHUB?.markets || []
        ).length,
      },
      polling: {
        candidate: latestPoll.candidate,
        ...(msgOpp1 ? { [msgOpp1]: latestPoll[msgOpp1] } : {}),
        undecided: latestPoll.Undecided,
        candidateLead: msgOpp1
          ? +(latestPoll.candidate - (latestPoll[msgOpp1] ?? 0)).toFixed(1)
          : null,
      },
      winred: (() => {
        const monthly = winredData?.summary?.monthly_totals || [];
        const mn = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const now = new Date();
        const curLabel = `${mn[now.getMonth()]} '${String(now.getFullYear()).slice(2)}`;
        const completed = monthly.filter((m) => m.month !== curLabel);
        const lastM = completed[completed.length - 1];
        return {
          totalRaised: liveTotalRaised,
          totalDonors: liveTotalDonors,
          lastMonth: lastM
            ? { raised: lastM.raised, month: lastM.month }
            : undefined,
          allMonths: completed.slice(-12),
        };
      })(),
      fec: (() => {
        const lastQ = Math.max(0, (cd.fecQuarters || []).length - 1);
        const candName = cfg.candidateName || "";
        const candCoh =
          liveFEC?.totals?.[candName]?.cash_on_hand ??
          cd.candidateFR?.cashOnHand?.[lastQ] ??
          null;
        const totals = {};
        if (candName && candCoh != null)
          totals[candName] = { cash_on_hand: candCoh };
        const opp1Key = Object.keys(cd.opponentFR || {})[0];
        if (opp1Key) {
          const oppCohArr = cd.opponentFR[opp1Key]?.cashOnHand || [];
          const oppCoh = oppCohArr[oppCohArr.length - 1] ?? null;
          if (oppCoh != null) totals[opp1Key] = { cash_on_hand: oppCoh };
        }
        const history = (cd.fecQuarters || [])
          .map((q, i) => ({
            quarter: q,
            candidateCoh: cd.candidateFR?.cashOnHand?.[i] ?? null,
            candidateReceipts: cd.candidateFR?.receipts?.[i] ?? null,
          }))
          .filter((h) => h.candidateCoh != null);
        return { totals, history };
      })(),
    };
  };

  // ── Generate all content — POST then receive via campaign PubNub channel ──
  const generate = async () => {
    if (!PHP_API_URL || !activeCampaignId) {
      setError("PHP API not configured");
      return;
    }
    setLoading(true);
    setError(null);
    setIntel(null);
    setImgJob(null);
    setImgSelected(null);
    try {
      localStorage.removeItem(LS_INTEL_DATA);
    } catch {}
    try {
      localStorage.removeItem(LS_IMG_DATA);
    } catch {}

    try {
      // 1. Register callback on the shared campaign PubNub channel before posting
      const resultPromise = messagingIntelCbRef
        ? new Promise((resolve, reject) => {
            let settled = false;
            const timeoutId = setTimeout(() => {
              if (!settled) {
                settled = true;
                messagingIntelCbRef.current = null;
                reject(
                  new Error("Timed out waiting for intelligence — try again"),
                );
              }
            }, 240000);
            messagingIntelCbRef.current = ({ action, data }) => {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              messagingIntelCbRef.current = null;
              if (action === "messaging-intel-done") resolve(data);
              else
                reject(
                  new Error(data?.error || "Intelligence generation failed"),
                );
            };
          })
        : null;

      // 2. POST to the PHP intelligence API (api.pollis.us) — kicks off async generation (202).
      //    JWT-authenticated. The {segment}-{tone} slug caches each variant to its own S3 object;
      //    context is built server-side, so no need to ship buildContext().
      const slug = `${segment}-${tone}`;
      const startTs = Date.now();
      const res = await authFetch(
        `${PHP_API_URL}/intelligence/${activeCampaignId}/messaging_intel/${slug}`,
        { method: "POST", body: JSON.stringify({ segment, tone }) },
      );
      if (!res.ok && res.status !== 202)
        throw new Error(`Intelligence request failed (${res.status})`);

      // 3. Result arrives via PubNub `messaging-intel-done` (fast path) OR by polling the cached
      //    object (robust fallback — survives a missed/late/oversized PubNub message). Accept the
      //    cached result only if generated AFTER this request started, so a stale prior run for the
      //    same segment/tone is never mistaken for this one. ~4-minute budget.
      let data = null;
      let pubnubData = null;
      if (resultPromise)
        resultPromise
          .then((d) => {
            pubnubData = d;
          })
          .catch(() => {});
      for (let i = 0; i < 80; i++) {
        if (pubnubData) {
          data = pubnubData;
          break;
        }
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const poll = await authFetch(
            `${PHP_API_URL}/intelligence/${activeCampaignId}/messaging_intel/${slug}`,
          );
          if (poll.ok) {
            const cached = await poll.json();
            const r = cached?.result;
            if (
              r?.status === "completed" &&
              r?.data &&
              new Date(r.generated_at || 0).getTime() >= startTs - 5000
            ) {
              data = {
                ...r.data,
                generated_at: r.generated_at,
                status: "completed",
              };
              break;
            }
          }
        } catch {
          /* transient fetch error — keep polling */
        }
      }

      if (!data || data.status !== "completed")
        throw new Error("Timed out waiting for intelligence — try again");

      setIntel(data);
      setChannelContent({
        facebook: { body: data.social?.facebook || "" },
        instagram: {
          body: data.social?.facebook || "",
          hashtags: data.social?.hashtags || campaignHashtags || "",
        },
        x: { body: data.social?.twitter || "" },
        linkedin: { body: data.social?.facebook || "" },
        tiktok: { caption: "", script: "" },
        email: {
          subject: data.email?.subject || "",
          body: data.email?.body || "",
        },
        sms: { body: data.sms?.urgency || "" },
      });
      try {
        localStorage.setItem(LS_INTEL_DATA, JSON.stringify(data));
      } catch {}
      if (data.social?.image_prompt) generateImage(data.social.image_prompt);
    } catch (e) {
      setError(e.message);
      if (messagingIntelCbRef) messagingIntelCbRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  // ── Copy to clipboard ──────────────────────────────────────────────────────
  const copy = (key, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // ── Generate social image — result arrives via PubNub `image-done` (not interval polling) ──
  const generateImage = async (imagePrompt) => {
    if (!IMAGINE_URL || !imagePrompt) return;
    setImgLoading(true);
    setImgJob(null);
    setImgSelected(null);
    try {
      localStorage.removeItem(LS_IMG_DATA);
    } catch {}

    let settled = false;
    let timeoutId = null;
    const finish = (job) => {
      if (settled) return;
      settled = true;
      if (messagingImageCbRef) messagingImageCbRef.current = null;
      if (timeoutId) clearTimeout(timeoutId);
      const sel = job.choices?.[0] || null;
      setImgJob(job);
      setImgSelected(sel);
      setImgLoading(false);
      try {
        localStorage.setItem(
          LS_IMG_DATA,
          JSON.stringify({ ...job, selected: sel }),
        );
      } catch {}
    };
    const fail = (msg) => {
      if (settled) return;
      settled = true;
      if (messagingImageCbRef) messagingImageCbRef.current = null;
      if (timeoutId) clearTimeout(timeoutId);
      setImgLoading(false);
      if (msg) setError(msg);
    };

    try {
      const res = await fetch(IMAGINE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // brief → art-director agent expands it into a detailed, fact-correct prompt
          brief: imagePrompt,
          art_direct: true,
          campaign_id: activeCampaignId,
          include_candidate: true, // headshot + aesthetic refs used as image-edit references
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Imagine API error");
      if (!data.jobId)
        throw new Error("No jobId returned from imagine endpoint");
      const jobId = data.jobId;
      setImgJob({ jobId, status: "pending", composite: null, choices: [] });

      // Primary path: the campaign PubNub channel pushes image-done / image-error (jobId-scoped).
      if (messagingImageCbRef) {
        messagingImageCbRef.current = ({ action, data: d }) => {
          if (d?.jobId && d.jobId !== jobId) return; // ignore other jobs on this channel
          if (action === "image-done" && d?.choices?.length) {
            finish({
              jobId,
              status: "completed",
              composite: d.composite,
              choices: d.choices,
            });
          } else if (action === "image-error") {
            fail(d?.error || "Image generation failed");
          }
        };
      }

      // Backstop: a single job-status check at the timeout in case the PubNub push was missed
      // (no tight interval — the result is pushed, not polled).
      timeoutId = setTimeout(async () => {
        try {
          const poll = await fetch(`${IMAGINE_URL}?jobId=${jobId}`);
          const pd = await poll.json();
          if (pd.status === "completed" && pd.choices?.length) {
            finish({
              jobId,
              status: "completed",
              composite: pd.composite,
              choices: pd.choices,
            });
          } else if (pd.status === "error") {
            fail(pd.error || "Image generation failed");
          } else {
            fail("Image generation timed out — try again");
          }
        } catch {
          fail("Image generation timed out — try again");
        }
      }, 240000);
    } catch (e) {
      fail(e.message);
    }
  };

  // ── Publish to Pollen Social ──────────────────────────────────────────────
  const publishToPollen = async () => {
    const tags = campaignHashtags.trim();
    const withTags = (text) =>
      text ? (tags ? `${text}\n\n${tags}` : text) : text;
    const withLink = (text) => applyLink(text);
    const fbBody = withTags(withLink(channelContent.facebook.body));
    const igHashtags = [channelContent.instagram.hashtags, tags]
      .filter(Boolean)
      .join(" ");
    const igBody =
      withLink(channelContent.instagram.body) +
      (igHashtags ? `\n\n${igHashtags}` : "");
    const content = activeChannels.facebook ? fbBody : igBody;
    if (!POLLEN_URL || !content) return;
    setPollenStatus("submitting");
    setPollenError(null);
    // Build channelContent with hashtags appended and link substituted for all channels
    const taggedChannelContent = {
      facebook: { body: withTags(withLink(channelContent.facebook.body)) },
      instagram: {
        body: withLink(channelContent.instagram.body),
        hashtags: igHashtags,
      },
      x: { body: withTags(withLink(channelContent.x.body)) },
      linkedin: { body: withTags(withLink(channelContent.linkedin.body)) },
      tiktok: {
        caption: withTags(withLink(channelContent.tiktok.caption)),
        script: channelContent.tiktok.script,
      },
      email: {
        subject: withLink(channelContent.email.subject),
        body: withTags(withLink(channelContent.email.body)),
      },
      sms: { body: withTags(withLink(channelContent.sms.body)) },
    };
    try {
      const body = {
        campaign_id: activeCampaignId,
        content,
        isFacebook: activeChannels.facebook,
        isInstagram: activeChannels.instagram,
        isX: activeChannels.x,
        isLinkedin: activeChannels.linkedin,
        isTiktok: activeChannels.tiktok,
        isEmail: activeChannels.email,
        isSms: activeChannels.sms,
        channelContent: taggedChannelContent,
        campaignName: `${cfg.candidateName || "Campaign"} - ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
      };
      if (imgSelected) {
        try {
          // Fetch image in-browser (has CDN auth cookies) and send as base64 so Lambda can upload to S3
          const imgRes = await fetch(imgSelected);
          const blob = await imgRes.blob();
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          body.imageBase64 = base64;
          body.imageType = blob.type || "image/jpeg";
        } catch {
          body.mediaUrls = [imgSelected];
        }
      }
      const res = await fetch(POLLEN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || `Pollen returned ${res.status}`);
      setPollenPostId(data?.data?.id || null);
      setPollenStatus("submitted");
      fetchPosts();
    } catch (e) {
      setPollenError(e.message);
      setPollenStatus("error");
    }
  };

  // ── Post a single channel to Pollen ──────────────────────────────────────
  const postSingleChannel = async (ch) => {
    if (!POLLEN_URL) return;
    setPostingChannel(ch);
    setChannelPostStatus((prev) => ({ ...prev, [ch]: null }));
    try {
      const tags = campaignHashtags.trim();
      const withTags = (text) =>
        text && tags ? `${text}\n\n${tags}` : text || "";
      const withLink = (text) => applyLink(text);
      const igHashtags = [channelContent.instagram.hashtags, tags]
        .filter(Boolean)
        .join(" ");
      let content = "";
      if (ch === "facebook")
        content = withTags(withLink(channelContent.facebook.body));
      else if (ch === "instagram")
        content =
          withLink(channelContent.instagram.body) +
          (igHashtags ? `\n\n${igHashtags}` : "");
      else if (ch === "x") content = withTags(withLink(channelContent.x.body));
      else if (ch === "linkedin")
        content = withTags(withLink(channelContent.linkedin.body));
      else if (ch === "tiktok")
        content = withTags(withLink(channelContent.tiktok.caption));
      else if (ch === "email")
        content = withTags(withLink(channelContent.email.body));
      else if (ch === "sms")
        content = withTags(withLink(channelContent.sms.body));
      if (!content.trim()) {
        setPostingChannel(null);
        return;
      }
      const taggedCC = {
        facebook: { body: withTags(withLink(channelContent.facebook.body)) },
        instagram: {
          body: withLink(channelContent.instagram.body),
          hashtags: igHashtags,
        },
        x: { body: withTags(withLink(channelContent.x.body)) },
        linkedin: { body: withTags(withLink(channelContent.linkedin.body)) },
        tiktok: {
          caption: withTags(withLink(channelContent.tiktok.caption)),
          script: channelContent.tiktok.script,
        },
        email: {
          subject: withLink(channelContent.email.subject),
          body: withTags(withLink(channelContent.email.body)),
        },
        sms: { body: withTags(withLink(channelContent.sms.body)) },
      };
      const body = {
        campaign_id: activeCampaignId,
        content,
        isFacebook: ch === "facebook",
        isInstagram: ch === "instagram",
        isX: ch === "x",
        isLinkedin: ch === "linkedin",
        isTiktok: ch === "tiktok",
        isEmail: ch === "email",
        isSms: ch === "sms",
        channelContent: taggedCC,
        campaignName: `${cfg.candidateName || "Campaign"} - ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
      };
      if (imgSelected) {
        try {
          const imgRes = await fetch(imgSelected);
          const blob = await imgRes.blob();
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          body.imageBase64 = base64;
          body.imageType = blob.type || "image/jpeg";
        } catch {
          body.mediaUrls = [imgSelected];
        }
      }
      const res = await fetch(POLLEN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || `Pollen returned ${res.status}`);
      setChannelPostStatus((prev) => ({ ...prev, [ch]: "submitted" }));
      fetchPosts();
      setTimeout(
        () => setChannelPostStatus((prev) => ({ ...prev, [ch]: null })),
        6000,
      );
    } catch (e) {
      setChannelPostStatus((prev) => ({ ...prev, [ch]: "error" }));
      setTimeout(
        () => setChannelPostStatus((prev) => ({ ...prev, [ch]: null })),
        6000,
      );
    } finally {
      setPostingChannel(null);
    }
  };

  // ── Populate channelContent when new intelligence arrives ──────────────────
  // Always overwrite with latest intel so previews stay fresh after each generation
  useEffect(() => {
    if (!intel) return;
    setChannelContent({
      facebook: { body: intel.social?.facebook || "" },
      instagram: {
        body: intel.social?.facebook || "",
        hashtags: intel.social?.hashtags || campaignHashtags || "",
      },
      x: { body: intel.social?.twitter || "" },
      linkedin: { body: intel.social?.facebook || "" },
      tiktok: { caption: "", script: "" },
      email: {
        subject: intel.email?.subject || "",
        body: intel.email?.body || "",
      },
      sms: { body: intel.sms?.urgency || "" },
    });
  }, [intel?.generated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-regenerate on mount if cached intel is stale (before today's 6 AM)
  useEffect(() => {
    if (!PHP_API_URL || !activeCampaignId) return;
    if (isStale6am(intel?.generated_at)) generate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateCC = (channel, field, value) =>
    setChannelContent((prev) => ({
      ...prev,
      [channel]: { ...prev[channel], [field]: value },
    }));

  const applyLink = (text) =>
    campaignLink
      ? (text || "").replace(/\[link\]/gi, campaignLink)
      : text || "";

  const renderEmailHtml = (text) => {
    if (!text) return "";
    const html = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/\n/g, "<br/>");
    return html;
  };

  const openCompose = () => {
    if (intel) {
      setChannelContent({
        facebook: { body: intel.social?.facebook || "" },
        instagram: {
          body: intel.social?.facebook || "",
          hashtags: intel.social?.hashtags || campaignHashtags || "",
        },
        x: { body: intel.social?.twitter || "" },
        linkedin: { body: intel.social?.facebook || "" },
        tiktok: { caption: "", script: "" },
        email: {
          subject: intel.email?.subject || "",
          body: intel.email?.body || "",
        },
        sms: { body: intel.sms?.urgency || "" },
      });
    }
    setActiveChannels({
      facebook: true,
      instagram: true,
      x: false,
      linkedin: false,
      tiktok: false,
      email: false,
      sms: false,
    });
    const saved = (() => {
      try {
        const d = localStorage.getItem(LS_IMG_DATA);
        return d ? JSON.parse(d)?.selected || null : null;
      } catch {
        return null;
      }
    })();
    setImgSelected(saved);
    setViewingPost(null);
    setDrawerOpen(true);
  };

  const loadPost = (p) => {
    const cc = p.channel_content || {};
    setChannelContent({
      facebook: { body: cc.facebook?.body || "" },
      instagram: {
        body: cc.instagram?.body || "",
        hashtags: cc.instagram?.hashtags || "",
      },
      x: { body: cc.x?.body || "" },
      linkedin: { body: cc.linkedin?.body || "" },
      tiktok: {
        caption: cc.tiktok?.caption || "",
        script: cc.tiktok?.script || "",
      },
      email: { subject: cc.email?.subject || "", body: cc.email?.body || "" },
      sms: { body: cc.sms?.body || "" },
    });
    setActiveChannels({
      facebook: !!p.is_facebook,
      instagram: !!p.is_instagram,
      x: !!p.is_x,
      linkedin: !!p.is_linkedin,
      tiktok: !!p.is_tiktok,
      email: !!p.is_email,
      sms: !!p.is_sms,
    });
    if (p.image_url) setImgSelected(p.image_url);
    setViewingPost(p);
    setDrawerOpen(true);
    // Silently sync stats from Pollen in the background; refresh table + viewingPost with fresh data
    if (p.pollen_post_id && POLLEN_URL) {
      fetch(`${POLLEN_URL}/sync-stats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_id: activeCampaignId,
          postId: p.pollen_post_id,
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.synced >= 0) {
            fetchPosts().then((freshPosts) => {
              const updated = freshPosts?.find((fp) => fp.id === p.id);
              if (updated) setViewingPost(updated);
            });
          }
        })
        .catch(() => {});
    }
  };

  // ── UI helpers ─────────────────────────────────────────────────────────────
  const CopyBtn = ({ id, text, label = "Copy" }) => (
    <Box
      onClick={() => copy(id, text)}
      sx={{
        fontSize: 10,
        fontWeight: 700,
        px: 1.5,
        py: 0.4,
        border: `1px solid ${C.border}`,
        borderRadius: 1,
        color: copied === id ? C.green : C.muted,
        cursor: "pointer",
        transition: "all 0.15s",
        flexShrink: 0,
        "&:hover": { borderColor: C.navy, color: C.navy },
      }}
    >
      {copied === id ? "Copied ✓" : label}
    </Box>
  );

  const ContentCard = ({ title, accent, children, actions }) => (
    <Box
      sx={{
        border: `1px solid ${C.border}`,
        borderTop: `3px solid ${accent || C.navy}`,
        borderRadius: 1,
        p: 2,
        bgcolor: C.white,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 800,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: "0.09em",
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>{actions}</Box>
      </Box>
      {children}
    </Box>
  );

  const Skeleton = ({ lines = 3 }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      {Array(lines)
        .fill(0)
        .map((_, i) => (
          <Box
            key={i}
            sx={{
              height: 12,
              bgcolor: "#e2e8f0",
              borderRadius: 1,
              width: i === lines - 1 ? "60%" : "100%",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
        ))}
    </Box>
  );

  if (section === "controls")
    return (
      <Box>
        {/* ── Controls ── */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2.5,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                mb: 0.5,
              }}
            >
              Donor Segment
            </Typography>
            <Box
              sx={{
                display: "flex",
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {SEGMENTS.map((s, i) => (
                <Box
                  key={s.value}
                  onClick={() => setSegment(s.value)}
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    bgcolor: segment === s.value ? C.navy : C.white,
                    color: segment === s.value ? C.white : C.muted,
                    borderLeft: i > 0 ? `1px solid ${C.border}` : "none",
                    transition: "all 0.15s",
                  }}
                >
                  {s.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                mb: 0.5,
              }}
            >
              Tone
            </Typography>
            <Box
              sx={{
                display: "flex",
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {TONES.map((t, i) => (
                <Box
                  key={t.value}
                  onClick={() => setTone(t.value)}
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    bgcolor: tone === t.value ? C.red : C.white,
                    color: tone === t.value ? C.white : C.muted,
                    borderLeft: i > 0 ? `1px solid ${C.border}` : "none",
                    transition: "all 0.15s",
                  }}
                >
                  {t.label}
                </Box>
              ))}
            </Box>
          </Box>
          <SpinnerBtn
            loading={loading}
            onClick={generate}
            sx={{
              px: 2.5,
              py: 0.85,
              bgcolor: C.navy,
              color: C.white,
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 1,
              transition: "all 0.15s",
              "&:hover": { bgcolor: "#1e3a5f" },
              "&.Mui-disabled": {
                bgcolor: C.muted,
                color: C.white,
                opacity: 0.7,
              },
            }}
          >
            {intel ? "↻ Regenerate All" : "⚡ Generate Intelligence"}
          </SpinnerBtn>
        </Box>

        {error && (
          <Box
            sx={{
              bgcolor: "#fef2f2",
              border: `1px solid #fca5a5`,
              borderRadius: 1,
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 11, color: C.red }}>{error}</Typography>
          </Box>
        )}

        {/* ── Signal Brief ── */}
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: C.navy,
                  textTransform: "uppercase",
                  letterSpacing: "0.09em",
                }}
              >
                Signal Brief
              </Typography>
              <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
                Campaign messaging intelligence ·{" "}
                {loading ? (
                  <Box component="span" sx={{ color: C.blue, fontWeight: 600 }}>
                    Analyzing…
                  </Box>
                ) : intel?.generated_at ? (
                  <Box
                    component="span"
                    sx={{ color: "#22c55e", fontWeight: 700 }}
                  >
                    Updated {fmtUpdatedAt(intel.generated_at)}
                  </Box>
                ) : !intel ? (
                  <Box component="span">Not yet generated</Box>
                ) : null}
              </Typography>
            </Box>
            <Box
              component="button"
              onClick={loading ? undefined : generate}
              disabled={loading}
              title="Regenerate intelligence"
              sx={{
                background: "none",
                border: "none",
                p: 0,
                lineHeight: 1,
                cursor: loading ? "not-allowed" : "pointer",
                color: C.muted,
                fontSize: 15,
                display: "flex",
                alignItems: "center",
                opacity: loading ? 0.4 : 0.7,
                "&:hover": { opacity: 1, color: C.navy },
                animation: loading ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            >
              ↻
            </Box>
          </Box>
          {loading ? (
            <IntelLoader bars={[100, 80, 90, 60, 95]} />
          ) : intel?.narrative ? (
            <>
              {intel.headline && (
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.navy,
                    mb: 0.75,
                  }}
                >
                  {intel.headline}
                </Typography>
              )}
              <Box
                sx={{
                  fontSize: 12,
                  color: C.charcoal,
                  lineHeight: 1.75,
                  "& p": { m: 0, mb: 1 },
                  "& strong": { fontWeight: 700 },
                }}
              >
                <ReactMarkdown>{intel.narrative}</ReactMarkdown>
              </Box>
              {intel.key_insight && (
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1,
                    bgcolor: "#f0f4ff",
                    borderRadius: 1,
                    borderLeft: `3px solid ${C.navy}`,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 10, fontWeight: 700, color: C.navy }}
                  >
                    {intel.key_insight}
                  </Typography>
                </Box>
              )}
              {intel.news_context?.length > 0 && (
                <Box
                  sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${C.border}` }}
                >
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.09em",
                      mb: 0.75,
                    }}
                  >
                    News Context
                  </Typography>
                  {intel.news_context.map((h, i) => (
                    <Typography
                      key={i}
                      sx={{ fontSize: 10, color: C.muted, lineHeight: 1.6 }}
                    >
                      {h
                        .replace(/\s*[—–-]\s*&lt;.*$/i, "")
                        .replace(/\s*[—–-]\s*<a\s.*$/i, "")
                        .trim()}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Typography
              sx={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}
            >
              Select your segment and tone above, then click Generate
              Intelligence to produce this week's campaign content.
            </Typography>
          )}
        </Box>

        {/* ── Press / Earned Media ── */}
        <Box sx={{ mt: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: C.navy,
                  textTransform: "uppercase",
                  letterSpacing: "0.09em",
                }}
              >
                Press / Earned Media
              </Typography>
              <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
                Earned media strategy ·{" "}
                {loading ? (
                  <Box component="span" sx={{ color: C.blue, fontWeight: 600 }}>
                    Analyzing…
                  </Box>
                ) : intel?.generated_at ? (
                  <Box
                    component="span"
                    sx={{ color: "#22c55e", fontWeight: 700 }}
                  >
                    Updated {fmtUpdatedAt(intel.generated_at)}
                  </Box>
                ) : !intel ? (
                  <Box component="span">Not yet generated</Box>
                ) : null}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {intel?.press?.statement && (
                <Box
                  onClick={() =>
                    copy(
                      "press",
                      `${intel.press.statement}\n\n${(intel.press.bullets || []).join("\n")}`,
                    )
                  }
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: C.muted,
                    cursor: "pointer",
                    px: 1,
                    py: 0.3,
                    border: `1px solid ${C.border}`,
                    borderRadius: 0.75,
                    "&:hover": { bgcolor: C.bg },
                  }}
                >
                  {copied === "press" ? "Copied!" : "Copy"}
                </Box>
              )}
              <Box
                component="button"
                onClick={loading ? undefined : generate}
                disabled={loading}
                title="Regenerate intelligence"
                sx={{
                  background: "none",
                  border: "none",
                  p: 0,
                  lineHeight: 1,
                  cursor: loading ? "not-allowed" : "pointer",
                  color: C.muted,
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  opacity: loading ? 0.4 : 0.7,
                  "&:hover": { opacity: 1, color: C.navy },
                  animation: loading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              >
                ↻
              </Box>
            </Box>
          </Box>
          {loading ? (
            <IntelLoader bars={[100, 80, 90, 60, 95]} />
          ) : intel?.press ? (
            <>
              <Typography
                sx={{
                  fontSize: 11,
                  color: C.charcoal,
                  fontStyle: "italic",
                  lineHeight: 1.7,
                  mb: 1.25,
                }}
              >
                "{intel.press.statement}"
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {(intel.press.bullets || []).map((b, i) => (
                  <Box
                    component="li"
                    key={i}
                    sx={{
                      fontSize: 10,
                      color: C.muted,
                      lineHeight: 1.65,
                      mb: 0.4,
                    }}
                  >
                    {b}
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography
              sx={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}
            >
              Generate intelligence to populate press content.
            </Typography>
          )}
        </Box>
      </Box>
    );

  // section === "content" — Post History table + Compose drawer
  const CHANNELS = [
    "facebook",
    "instagram",
    "x",
    "linkedin",
    "tiktok",
    "email",
    "sms",
  ];
  const CH_LABEL = {
    facebook: "Facebook",
    instagram: "Instagram",
    x: "X (Twitter)",
    linkedin: "LinkedIn",
    tiktok: "TikTok",
    email: "Email",
    sms: "SMS",
  };
  const CH_COLOR = {
    facebook: "#1877f2",
    instagram: "#e1306c",
    x: "#000000",
    linkedin: "#0a66c2",
    tiktok: "#fe2c55",
    email: "#1e3a5f",
    sms: "#16a34a",
  };
  const CH_ICON = {
    facebook: faFacebook,
    instagram: faInstagram,
    x: faXTwitter,
    linkedin: faLinkedin,
    tiktok: faTiktok,
    email: faEnvelope,
    sms: faMessage,
  };
  const CH_ICON_COLOR = {
    facebook: "#1877f2",
    instagram: "#e1306c",
    x: "#000",
    linkedin: "#0a66c2",
    tiktok: "#fe2c55",
    email: C.navy,
    sms: "#22c55e",
  };
  return (
    <Box>
      {/* ── Post History Header ── */}
      <Box
        sx={{
          pb: 1,
          borderBottom: `2px solid ${C.navy}`,
          mb: 2,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.09em",
            }}
          >
            Post History
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
            Published posts · engagement tracking
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {syncAllStatus &&
            syncAllStatus !== "loading" &&
            syncAllStatus !== "error" && (
              <Typography sx={{ fontSize: 10, color: "#16a34a" }}>
                ✓ Synced {syncAllStatus.synced ?? 0} stat
                {syncAllStatus.synced !== 1 ? "s" : ""}
              </Typography>
            )}
          {syncAllStatus === "error" && (
            <Typography sx={{ fontSize: 10, color: "#dc2626" }}>
              Sync failed
            </Typography>
          )}
          <SpinnerBtn
            loading={syncAllStatus === "loading"}
            onClick={syncAllStats}
            variant="outlined"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              px: 1.5,
              py: 0.5,
              borderColor: C.border,
              color: C.charcoal,
              bgcolor: "#fff",
              textTransform: "none",
              borderRadius: 1,
              minWidth: 0,
              "&:hover": {
                borderColor: C.navy,
                color: C.navy,
                bgcolor: "#fff",
              },
              "&.Mui-disabled": { borderColor: C.border, color: C.muted },
            }}
          >
            ↻ Sync Stats
          </SpinnerBtn>
        </Box>
      </Box>

      {/* ── Compose CTA banner ── */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          bgcolor: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{ fontSize: 13, fontWeight: 800, color: C.navy, mb: 0.75 }}
        >
          Compose &amp; Publish Post
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: C.muted, mb: 1.75, lineHeight: 1.5 }}
        >
          Draft and publish content across Facebook, Instagram, X, LinkedIn,
          TikTok, Email &amp; SMS — edit per channel before posting.
        </Typography>
        <Box
          onClick={openCompose}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontSize: 12,
            fontWeight: 800,
            px: 2.5,
            py: 1,
            bgcolor: C.navy,
            color: C.white,
            borderRadius: 1,
            cursor: "pointer",
            "&:hover": { bgcolor: C.navyDark },
          }}
        >
          Compose New Post →
        </Box>
      </Box>

      {/* ── Post History Table ── */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
        }}
      >
        <colgroup>
          <col style={{ width: 62 }} /> {/* Created */}
          <col style={{ width: 62 }} /> {/* Posted */}
          <col style={{ width: 68 }} /> {/* Channels */}
          <col style={{ width: 120 }} /> {/* Content — fixed narrow */}
          <col style={{ width: 44 }} /> {/* Image */}
          <col style={{ width: 82 }} /> {/* Status */}
          <col style={{ width: 72 }} /> {/* Impressions */}
          <col style={{ width: 72 }} /> {/* Engagements */}
        </colgroup>
        <thead>
          <tr>
            {[
              "Created",
              "Posted",
              "Channels",
              "Content",
              "Image",
              "Status",
              "Impressions",
              "Engagements",
            ].map((h) => (
              <th
                key={h}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  padding: "6px 10px",
                  borderBottom: `1px solid ${C.border}`,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {posts.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                style={{
                  padding: "32px 10px",
                  textAlign: "center",
                  fontSize: 12,
                  color: C.muted,
                  fontStyle: "italic",
                }}
              >
                No posts yet.
              </td>
            </tr>
          ) : (
            posts.map((p, i) => (
              <tr
                key={p.id}
                onClick={() => loadPost(p)}
                style={{
                  backgroundColor: i % 2 === 1 ? C.rowAlt : "transparent",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = C.bg)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    i % 2 === 1 ? C.rowAlt : "transparent")
                }
              >
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {p.created_at
                    ? new Date(p.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    : "—"}
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {p.first_posted_at ? (
                    <span style={{ color: "#16a34a", fontWeight: 700 }}>
                      {new Date(p.first_posted_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  ) : (
                    <span style={{ color: C.muted }}>—</span>
                  )}
                </td>
                <td style={{ padding: "7px 10px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.625,
                      alignItems: "center",
                    }}
                  >
                    {CHANNELS.filter((ch) => p[`is_${ch}`]).map((ch) => (
                      <FontAwesomeIcon
                        key={ch}
                        icon={CH_ICON[ch]}
                        style={{ fontSize: 14, color: CH_ICON_COLOR[ch] }}
                        title={CH_LABEL[ch]}
                      />
                    ))}
                  </Box>
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.channel_content?.facebook?.body ||
                    p.channel_content?.instagram?.body ||
                    "—"}
                </td>
                <td style={{ padding: "7px 4px" }}>
                  {p.image_url ? (
                    <Box
                      component="img"
                      src={p.image_url}
                      sx={{
                        width: 36,
                        height: 24,
                        objectFit: "cover",
                        borderRadius: 0.5,
                        display: "block",
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: 11, color: C.muted }}>—</span>
                  )}
                </td>
                <td style={{ padding: "7px 10px" }}>
                  {(() => {
                    const s = p.pollen_status || p.status || "draft";
                    const color =
                      s === "posted" || s === "sent"
                        ? "#16a34a"
                        : s === "approved"
                          ? "#2563eb"
                          : s === "failed"
                            ? "#dc2626"
                            : s === "scheduled"
                              ? "#d97706"
                              : C.muted;
                    return (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          px: 1,
                          py: 0.3,
                          borderRadius: "3px",
                          display: "inline-block",
                          border: `1px solid ${color}44`,
                          color,
                          bgcolor: `${color}18`,
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s}
                      </Box>
                    );
                  })()}
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                  }}
                >
                  {p.impressions != null
                    ? Number(p.impressions).toLocaleString()
                    : "—"}
                </td>
                <td
                  style={{
                    padding: "7px 10px",
                    fontSize: 11,
                    color: C.charcoal,
                  }}
                >
                  {p.engagements != null
                    ? Number(p.engagements).toLocaleString()
                    : "—"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* ── Generated-at footer ── */}
      {intel?.generated_at && (
        <Typography
          sx={{
            fontSize: 9,
            color: C.muted,
            fontStyle: "italic",
            mt: 2,
            textAlign: "right",
          }}
        >
          Generated {new Date(intel.generated_at).toLocaleString()} · Claude
          Sonnet 4.6
        </Typography>
      )}

      {/* ── Compose Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        transitionDuration={300}
        PaperProps={{
          sx: {
            width: { xs: "100vw", md: "50vw" },
            minWidth: { xs: 0, md: 480 },
            display: "flex",
            flexDirection: "column",
            bgcolor: "#f9fafb",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: C.white,
            px: 3,
            py: 2.5,
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 800,
                  color: C.charcoal,
                  lineHeight: 1.25,
                }}
              >
                {viewingPost
                  ? `Post ID ${viewingPost.pollen_post_id || viewingPost.id}`
                  : "Publish Posts"}
              </Typography>
              {viewingPost &&
                (() => {
                  const s =
                    viewingPost.pollen_status || viewingPost.status || "draft";
                  const color =
                    s === "posted" || s === "sent"
                      ? "#16a34a"
                      : s === "approved"
                        ? "#2563eb"
                        : s === "failed"
                          ? "#dc2626"
                          : s === "scheduled"
                            ? "#d97706"
                            : C.muted;
                  return (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        px: 1,
                        py: 0.3,
                        borderRadius: "3px",
                        border: `1px solid ${color}44`,
                        color,
                        bgcolor: `${color}18`,
                        textTransform: "uppercase",
                      }}
                    >
                      {s}
                    </Box>
                  );
                })()}
            </Box>
            <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.3 }}>
              {viewingPost?.approval_completed_at
                ? `Approved ${new Date(viewingPost.approval_completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}`
                : Object.keys(activeChannels)
                    .filter((ch) => activeChannels[ch])
                    .map((ch) => CH_LABEL[ch])
                    .join(" · ") || "No channels selected"}
            </Typography>
          </Box>
          <Box
            onClick={() => setDrawerOpen(false)}
            sx={{
              fontSize: 20,
              color: C.muted,
              cursor: "pointer",
              lineHeight: 1,
              "&:hover": { color: C.charcoal },
            }}
          >
            ✕
          </Box>
        </Box>
        {/* Body */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          {(() => {
            const initials = (cfg.candidateName || "Candidate")
              .split(" ")
              .map((w) => w[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();
            const handle = (cfg.candidateName || "candidate")
              .toLowerCase()
              .replace(/ /g, "_");
            const today = new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            });
            const activeList = CHANNELS.filter((ch) => activeChannels[ch]);
            const toggleCh = (ch) =>
              setExpandedChannels((p) => {
                const opening = !p[ch];
                if (opening)
                  return CHANNELS.reduce(
                    (a, c) => ({ ...a, [c]: c === ch }),
                    {},
                  );
                return { ...p, [ch]: false };
              });
            const btnBase = {
              fontSize: 11,
              fontWeight: 700,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              cursor: "pointer",
              whiteSpace: "nowrap",
              userSelect: "none",
              display: "inline-flex",
              alignItems: "center",
            };
            return (
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                {/* ══ LEFT COLUMN ══ */}
                <Box
                  sx={{
                    width: "40%",
                    flexShrink: 0,
                    position: "sticky",
                    top: 0,
                    alignSelf: "flex-start",
                  }}
                >
                  {/* Image */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        mb: 0.75,
                      }}
                    >
                      Image
                    </Typography>
                    {imgLoading ? (
                      <Box
                        sx={{
                          width: "100%",
                          aspectRatio: "16/9",
                          border: `1px dashed ${C.border}`,
                          borderRadius: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#fafafa",
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 10, color: C.blue }}>
                          ⏳ Generating…
                        </Typography>
                      </Box>
                    ) : imgSelected ? (
                      <Box
                        component="img"
                        src={imgSelected}
                        onError={() => {
                          setImgSelected(null);
                          try {
                            localStorage.removeItem(LS_IMG_DATA);
                          } catch {}
                        }}
                        sx={{
                          width: "100%",
                          aspectRatio: "16/9",
                          objectFit: "cover",
                          borderRadius: 1.5,
                          display: "block",
                          border: `1px solid ${C.border}`,
                          mb: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          aspectRatio: "16/9",
                          border: `1px dashed ${C.border}`,
                          borderRadius: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#fafafa",
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 10, color: C.muted }}>
                          No image
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      {imgJob?.choices?.length > 0 && (
                        <Box
                          onClick={() => setImgPickerOpen(true)}
                          sx={{
                            ...btnBase,
                            border: `1px solid ${C.border}`,
                            color: C.charcoal,
                            bgcolor: "#fff",
                            "&:hover": { borderColor: C.navy, color: C.navy },
                          }}
                        >
                          Change
                        </Box>
                      )}
                      <Box
                        onClick={() =>
                          generateImage(
                            intel?.social?.image_prompt ||
                              `${cfg.candidateName || "Candidate"} campaign photo`,
                          )
                        }
                        sx={{
                          ...btnBase,
                          border: `1px solid ${C.navy}`,
                          color: C.navy,
                          bgcolor: "#fff",
                          "&:hover": { bgcolor: C.navy, color: "#fff" },
                        }}
                      >
                        {imgLoading ? "Generating…" : "✦ Regenerate"}
                      </Box>
                    </Box>
                  </Box>

                  {/* Channels */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        mb: 0.75,
                      }}
                    >
                      Channels
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {CHANNELS.map((ch) => (
                        <Box
                          key={ch}
                          onClick={() => {
                            setActiveChannels((prev) => {
                              const turningOn = !prev[ch];
                              if (turningOn)
                                setExpandedChannels(
                                  CHANNELS.reduce(
                                    (a, c) => ({ ...a, [c]: c === ch }),
                                    {},
                                  ),
                                );
                              else
                                setExpandedChannels((p) => ({
                                  ...p,
                                  [ch]: false,
                                }));
                              return { ...prev, [ch]: !prev[ch] };
                            });
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 10,
                            fontWeight: 700,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1,
                            cursor: "pointer",
                            border: `1px solid ${activeChannels[ch] ? CH_COLOR[ch] : C.border}`,
                            bgcolor: activeChannels[ch]
                              ? CH_COLOR[ch] + "18"
                              : "transparent",
                            color: activeChannels[ch] ? CH_COLOR[ch] : C.muted,
                            userSelect: "none",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={CH_ICON[ch]}
                            style={{ fontSize: 11 }}
                          />
                          {CH_LABEL[ch]}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Hashtags */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        mb: 0.5,
                      }}
                    >
                      Campaign Hashtags
                    </Typography>
                    <Box
                      component="input"
                      placeholder="#Campaign2026 #CandidateName"
                      value={campaignHashtags}
                      onChange={(e) => {
                        setCampaignHashtags(e.target.value);
                        try {
                          localStorage.setItem(LS_HASHTAGS, e.target.value);
                        } catch {}
                      }}
                      sx={{
                        width: "100%",
                        fontSize: 11,
                        px: 1.25,
                        py: 0.625,
                        border: `1px solid ${C.border}`,
                        borderRadius: 1,
                        outline: "none",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                        bgcolor: "#fff",
                      }}
                    />
                    <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.4 }}>
                      Appended to all channels on publish
                    </Typography>
                  </Box>

                  {/* Campaign Link */}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        mb: 0.5,
                      }}
                    >
                      Campaign Link
                    </Typography>
                    <Box
                      component="input"
                      placeholder="https://nancymace.com/donate"
                      value={campaignLinkDraft}
                      onChange={(e) => setCampaignLinkDraft(e.target.value)}
                      sx={{
                        width: "100%",
                        fontSize: 11,
                        px: 1.25,
                        py: 0.625,
                        border: `1px solid ${C.border}`,
                        borderRadius: 1,
                        outline: "none",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                        bgcolor: "#fff",
                      }}
                    />
                    <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.4 }}>
                      Replaces [link] in generated content
                    </Typography>
                  </Box>

                  {/* Publish All CTA */}
                  <Box
                    onClick={() => {
                      if (pollenStatus !== "submitting") publishToPollen();
                    }}
                    sx={{
                      width: "100%",
                      py: 1.25,
                      bgcolor:
                        pollenStatus === "submitted"
                          ? "#16a34a"
                          : pollenStatus === "submitting"
                            ? "#94a3b8"
                            : C.navy,
                      color: "#fff",
                      borderRadius: 1.5,
                      textAlign: "center",
                      fontSize: 12,
                      fontWeight: 800,
                      cursor:
                        pollenStatus === "submitting" ? "default" : "pointer",
                      "&:hover": {
                        opacity: pollenStatus === "submitting" ? 1 : 0.9,
                      },
                      transition: "background-color 0.15s",
                      userSelect: "none",
                    }}
                  >
                    {pollenStatus === "submitting"
                      ? "Publishing…"
                      : pollenStatus === "submitted"
                        ? "✓ Published"
                        : `Publish to ${activeList.length} Channel${activeList.length !== 1 ? "s" : ""} →`}
                  </Box>
                  {pollenError && (
                    <Typography
                      sx={{ fontSize: 10, color: "#dc2626", mt: 0.75 }}
                    >
                      {pollenError}
                    </Typography>
                  )}

                  {/* Post Performance — only shown when viewing a post */}
                  {viewingPost &&
                    (() => {
                      const aggMetrics = [
                        { label: "Impressions", val: viewingPost.impressions },
                        { label: "Reach", val: viewingPost.reach },
                        { label: "Engagements", val: viewingPost.engagements },
                        { label: "Likes", val: viewingPost.likes },
                        { label: "Comments", val: viewingPost.comments },
                        { label: "Shares", val: viewingPost.shares },
                        { label: "Clicks", val: viewingPost.clicks },
                        { label: "Plays", val: viewingPost.plays },
                      ].filter((m) => m.val != null);
                      const chStats = viewingPost.channel_stats || {};
                      const chEntries = Object.entries(chStats);
                      if (!aggMetrics.length && !chEntries.length) return null;
                      return (
                        <Box sx={{ mt: 2.5 }}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.muted,
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              mb: 1,
                            }}
                          >
                            Post Performance
                          </Typography>

                          {/* Aggregate totals */}
                          {aggMetrics.length > 0 && (
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 0.75,
                                mb: 1.5,
                              }}
                            >
                              {aggMetrics.map(({ label, val }) => (
                                <Box
                                  key={label}
                                  sx={{
                                    bgcolor: "#fff",
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 1,
                                    px: 1.25,
                                    py: 0.75,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 9,
                                      fontWeight: 700,
                                      color: C.muted,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 800,
                                      color: C.charcoal,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {Number(val).toLocaleString()}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Per-channel breakdown */}
                          {chEntries.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.75,
                              }}
                            >
                              {chEntries.map(([ch, cs]) => {
                                const hasStats = [
                                  cs.impressions,
                                  cs.engagements,
                                  cs.likes,
                                  cs.reach,
                                ].some((v) => v != null);
                                const postedDate = cs.posted_at
                                  ? new Date(cs.posted_at).toLocaleDateString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      },
                                    )
                                  : null;
                                return (
                                  <Box
                                    key={ch}
                                    sx={{
                                      bgcolor: "#fff",
                                      border: `1px solid ${C.border}`,
                                      borderRadius: 1,
                                      px: 1.25,
                                      py: 0.875,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: hasStats ? 0.75 : 0,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.625,
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={CH_ICON[ch]}
                                          style={{
                                            fontSize: 12,
                                            color: CH_ICON_COLOR[ch],
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: C.charcoal,
                                          }}
                                        >
                                          {CH_LABEL[ch]}
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.75,
                                        }}
                                      >
                                        {postedDate && (
                                          <Typography
                                            sx={{
                                              fontSize: 9,
                                              color: "#16a34a",
                                              fontWeight: 700,
                                            }}
                                          >
                                            Posted {postedDate}
                                          </Typography>
                                        )}
                                        {!postedDate && (
                                          <Typography
                                            sx={{
                                              fontSize: 9,
                                              color: C.muted,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            Not yet posted
                                          </Typography>
                                        )}
                                        {cs.external_id && (
                                          <Box
                                            component="a"
                                            href={`https://www.facebook.com/${cs.external_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                              fontSize: 9,
                                              fontWeight: 700,
                                              color: C.blue,
                                              textDecoration: "none",
                                              "&:hover": {
                                                textDecoration: "underline",
                                              },
                                            }}
                                          >
                                            ↗ View
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                    {hasStats && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: "4px 12px",
                                        }}
                                      >
                                        {[
                                          { k: "Impr", v: cs.impressions },
                                          { k: "Reach", v: cs.reach },
                                          { k: "Eng", v: cs.engagements },
                                          { k: "Likes", v: cs.likes },
                                          { k: "Cmnts", v: cs.comments },
                                          { k: "Shares", v: cs.shares },
                                          { k: "Clicks", v: cs.clicks },
                                          { k: "Plays", v: cs.plays },
                                          {
                                            k: "Avg Watch",
                                            v: cs.avg_watch_time
                                              ? `${cs.avg_watch_time.toFixed(1)}s`
                                              : null,
                                          },
                                        ]
                                          .filter((m) => m.v != null)
                                          .map(({ k, v }) => (
                                            <Box key={k}>
                                              <Typography
                                                sx={{
                                                  fontSize: 8,
                                                  fontWeight: 700,
                                                  color: C.muted,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.04em",
                                                }}
                                              >
                                                {k}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: 12,
                                                  fontWeight: 800,
                                                  color: C.charcoal,
                                                  lineHeight: 1.2,
                                                }}
                                              >
                                                {typeof v === "number"
                                                  ? Number(v).toLocaleString()
                                                  : v}
                                              </Typography>
                                            </Box>
                                          ))}
                                        {cs.pollen_score != null && (
                                          <Box
                                            sx={{
                                              ml: "auto",
                                              textAlign: "right",
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                fontSize: 8,
                                                fontWeight: 700,
                                                color: "#7c3aed",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.04em",
                                              }}
                                            >
                                              Pollen Score
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: 13,
                                                fontWeight: 800,
                                                color: "#7c3aed",
                                                lineHeight: 1.2,
                                              }}
                                            >
                                              {cs.pollen_score.toFixed(1)}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}

                          {/* First comment if set */}
                          {viewingPost.first_comment && (
                            <Box
                              sx={{
                                mt: 1.25,
                                p: 1.25,
                                bgcolor: "#fff",
                                border: `1px solid ${C.border}`,
                                borderRadius: 1,
                                borderLeft: `3px solid ${C.muted}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  color: C.muted,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  mb: 0.4,
                                }}
                              >
                                First Comment
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: C.charcoal,
                                  lineHeight: 1.5,
                                }}
                              >
                                {viewingPost.first_comment}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                </Box>

                {/* ══ RIGHT COLUMN ══ */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Expand / Collapse All */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      onClick={() =>
                        setExpandedChannels(
                          CHANNELS.reduce(
                            (a, ch) => ({ ...a, [ch]: true }),
                            {},
                          ),
                        )
                      }
                      sx={{
                        fontSize: 11,
                        color: C.blue,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Expand All
                    </Typography>
                    <Typography
                      onClick={() =>
                        setExpandedChannels(
                          CHANNELS.reduce(
                            (a, ch) => ({ ...a, [ch]: false }),
                            {},
                          ),
                        )
                      }
                      sx={{
                        fontSize: 11,
                        color: C.blue,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Collapse All
                    </Typography>
                  </Box>

                  {activeList.length === 0 && (
                    <Typography
                      sx={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}
                    >
                      Enable at least one channel.
                    </Typography>
                  )}

                  {/* ── Channel list (single column) ── */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {activeList.map((ch) => {
                      const isEditing = editingChannel === ch;
                      const isPosting = postingChannel === ch;
                      const postStatus = channelPostStatus[ch];
                      return (
                        <Box
                          key={ch}
                          sx={{
                            border: `1px solid ${isEditing ? C.navy : C.border}`,
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "#fff",
                          }}
                        >
                          {/* Card header */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              px: 1.5,
                              py: 1,
                              borderBottom: expandedChannels[ch]
                                ? `1px solid ${C.border}`
                                : "none",
                              bgcolor: isEditing ? "#f8faff" : "#fff",
                            }}
                          >
                            {/* Left: icon + name → click to toggle */}
                            <Box
                              onClick={() => toggleCh(ch)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                flex: 1,
                                cursor: "pointer",
                                minWidth: 0,
                              }}
                            >
                              <FontAwesomeIcon
                                icon={CH_ICON[ch]}
                                style={{
                                  fontSize: 13,
                                  color: CH_ICON_COLOR[ch],
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#111",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {CH_LABEL[ch]}
                              </Typography>
                            </Box>
                            {/* Right: action buttons */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexShrink: 0,
                              }}
                            >
                              {isEditing ? (
                                <>
                                  <Box
                                    onClick={() => {
                                      setChannelContent((prev) => ({
                                        ...prev,
                                        [ch]: { ...editDraft },
                                      }));
                                      setEditingChannel(null);
                                    }}
                                    sx={{
                                      ...btnBase,
                                      bgcolor: C.navy,
                                      color: "#fff",
                                      border: `1px solid ${C.navy}`,
                                      "&:hover": { opacity: 0.88 },
                                    }}
                                  >
                                    Save
                                  </Box>
                                  <Box
                                    onClick={() => setEditingChannel(null)}
                                    sx={{
                                      ...btnBase,
                                      border: `1px solid ${C.border}`,
                                      color: C.charcoal,
                                      bgcolor: "#fff",
                                      "&:hover": {
                                        borderColor: C.navy,
                                        color: C.navy,
                                      },
                                    }}
                                  >
                                    Cancel
                                  </Box>
                                </>
                              ) : (
                                <Box
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditDraft({ ...channelContent[ch] });
                                    setEditingChannel(ch);
                                    setExpandedChannels(
                                      CHANNELS.reduce(
                                        (a, c) => ({ ...a, [c]: c === ch }),
                                        {},
                                      ),
                                    );
                                  }}
                                  sx={{
                                    ...btnBase,
                                    border: `1px solid ${C.border}`,
                                    color: C.charcoal,
                                    bgcolor: "#fff",
                                    "&:hover": {
                                      borderColor: C.navy,
                                      color: C.navy,
                                    },
                                  }}
                                >
                                  Edit
                                </Box>
                              )}
                              <Box
                                onClick={() => toggleCh(ch)}
                                sx={{
                                  fontSize: 9,
                                  color: C.muted,
                                  cursor: "pointer",
                                  pl: 0.5,
                                  userSelect: "none",
                                }}
                              >
                                {expandedChannels[ch] ? "▲" : "▼"}
                              </Box>
                            </Box>
                          </Box>

                          {/* Edit mode */}
                          {expandedChannels[ch] && isEditing && (
                            <Box
                              sx={{
                                p: 1.5,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {ch === "email" && (
                                <Box
                                  component="input"
                                  placeholder="Subject line"
                                  value={editDraft.subject || ""}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      subject: e.target.value,
                                    }))
                                  }
                                  sx={{
                                    width: "100%",
                                    fontSize: 12,
                                    px: 1.25,
                                    py: 0.625,
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 1,
                                    outline: "none",
                                    fontFamily: "inherit",
                                    boxSizing: "border-box",
                                  }}
                                />
                              )}
                              <Box sx={{ position: "relative" }}>
                                <Box
                                  component="textarea"
                                  value={
                                    ch === "tiktok"
                                      ? editDraft.caption || ""
                                      : editDraft.body || ""
                                  }
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      [ch === "tiktok" ? "caption" : "body"]:
                                        e.target.value,
                                    }))
                                  }
                                  autoFocus
                                  rows={
                                    ch === "email"
                                      ? 10
                                      : ch === "sms"
                                        ? 4
                                        : ch === "x"
                                          ? 5
                                          : 8
                                  }
                                  sx={{
                                    width: "100%",
                                    fontSize: 12,
                                    px: 1.25,
                                    py: 1,
                                    border: `1px solid ${C.navy}`,
                                    borderRadius: 1,
                                    outline: "none",
                                    fontFamily: "inherit",
                                    resize: "vertical",
                                    boxSizing: "border-box",
                                  }}
                                />
                                {(ch === "x" || ch === "sms") && (
                                  <Typography
                                    sx={{
                                      position: "absolute",
                                      bottom: 8,
                                      right: 8,
                                      fontSize: 9,
                                      color:
                                        (editDraft.body || "").length >
                                        (ch === "x" ? 280 : 160)
                                          ? "#dc2626"
                                          : C.muted,
                                    }}
                                  >
                                    {(editDraft.body || "").length}/
                                    {ch === "x" ? 280 : 160}
                                  </Typography>
                                )}
                              </Box>
                              {ch === "instagram" && (
                                <Box
                                  component="input"
                                  placeholder="Hashtags"
                                  value={editDraft.hashtags || ""}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      hashtags: e.target.value,
                                    }))
                                  }
                                  sx={{
                                    width: "100%",
                                    fontSize: 12,
                                    px: 1.25,
                                    py: 0.625,
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 1,
                                    outline: "none",
                                    fontFamily: "inherit",
                                    boxSizing: "border-box",
                                  }}
                                />
                              )}
                              {ch === "tiktok" && (
                                <Box
                                  component="textarea"
                                  placeholder="Video script / talking points"
                                  value={editDraft.script || ""}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      script: e.target.value,
                                    }))
                                  }
                                  rows={6}
                                  sx={{
                                    width: "100%",
                                    fontSize: 12,
                                    px: 1.25,
                                    py: 1,
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 1,
                                    outline: "none",
                                    fontFamily: "inherit",
                                    resize: "vertical",
                                    boxSizing: "border-box",
                                  }}
                                />
                              )}
                            </Box>
                          )}

                          {/* ── Facebook (preview) ── */}
                          {expandedChannels[ch] &&
                            !isEditing &&
                            ch === "facebook" && (
                              <Box sx={{ p: 2 }}>
                                {/* Header */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    mb: 1.25,
                                  }}
                                >
                                  <Box sx={{ display: "flex", gap: 1.25 }}>
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "50%",
                                        bgcolor: "#6c4de6",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontWeight: 800,
                                        fontSize: 14,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {initials}
                                    </Box>
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: "#050505",
                                          lineHeight: 1.3,
                                        }}
                                      >
                                        {cfg.candidateName || "Candidate"}
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            color: "#65676b",
                                          }}
                                        >
                                          {today}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            color: "#65676b",
                                          }}
                                        >
                                          ·
                                        </Typography>
                                        <FontAwesomeIcon
                                          icon={faGlobe}
                                          style={{
                                            fontSize: 10,
                                            color: "#65676b",
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                  <FontAwesomeIcon
                                    icon={faEllipsis}
                                    style={{
                                      fontSize: 16,
                                      color: "#65676b",
                                      cursor: "pointer",
                                      marginTop: 4,
                                    }}
                                  />
                                </Box>
                                {/* Body */}
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    color: "#050505",
                                    lineHeight: 1.55,
                                    whiteSpace: "pre-wrap",
                                    mb: imgSelected ? 1.5 : 0,
                                  }}
                                >
                                  {applyLink(channelContent.facebook.body) || (
                                    <span
                                      style={{
                                        color: "#65676b",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      No content yet
                                    </span>
                                  )}
                                </Typography>
                                {/* Image */}
                                {imgSelected && (
                                  <Box
                                    component="img"
                                    src={imgSelected}
                                    sx={{
                                      width: "100%",
                                      borderRadius: 1.5,
                                      display: "block",
                                      mb: 0,
                                    }}
                                  />
                                )}
                                {/* Reaction row */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 0.75,
                                    mt: 1,
                                    borderTop: "1px solid #e4e6eb",
                                    borderBottom: "1px solid #e4e6eb",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.25,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 18,
                                          height: 18,
                                          borderRadius: "50%",
                                          bgcolor: "#1877f2",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faThumbsUpSolid}
                                          style={{
                                            fontSize: 10,
                                            color: "#fff",
                                          }}
                                        />
                                      </Box>
                                      <Box
                                        sx={{
                                          width: 18,
                                          height: 18,
                                          borderRadius: "50%",
                                          bgcolor: "#f02849",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          ml: "-4px",
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faHeartSolid}
                                          style={{
                                            fontSize: 10,
                                            color: "#fff",
                                          }}
                                        />
                                      </Box>
                                      <Box
                                        sx={{
                                          width: 18,
                                          height: 18,
                                          borderRadius: "50%",
                                          bgcolor: "#f7b928",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          ml: "-4px",
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faFireSolid}
                                          style={{
                                            fontSize: 10,
                                            color: "#fff",
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "#65676b",
                                        ml: 0.5,
                                      }}
                                    >
                                      3
                                    </Typography>
                                  </Box>
                                  <Typography
                                    sx={{ fontSize: 12, color: "#65676b" }}
                                  >
                                    2 comments · 1 share
                                  </Typography>
                                </Box>
                                {/* Action bar */}
                                <Box sx={{ display: "flex", pt: 0.25 }}>
                                  {[
                                    [faThumbsUp, "Like"],
                                    [faComment, "Comment"],
                                    [faShare, "Share"],
                                  ].map(([icon, label]) => (
                                    <Box
                                      key={label}
                                      sx={{
                                        flex: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.75,
                                        py: 0.875,
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        "&:hover": { bgcolor: "#f0f2f5" },
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={icon}
                                        style={{
                                          fontSize: 16,
                                          color: "#65676b",
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: "#65676b",
                                        }}
                                      >
                                        {label}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}

                          {/* ── Instagram ── */}
                          {expandedChannels[ch] &&
                            !isEditing &&
                            ch === "instagram" && (
                              <Box>
                                {/* Header */}
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 1.25,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.25,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        background:
                                          "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontWeight: 800,
                                        fontSize: 12,
                                      }}
                                    >
                                      {initials}
                                    </Box>
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: "#262626",
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        {handle}
                                      </Typography>
                                      <Typography
                                        sx={{ fontSize: 10, color: "#8e8e8e" }}
                                      >
                                        Sponsored
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <FontAwesomeIcon
                                    icon={faEllipsis}
                                    style={{
                                      fontSize: 16,
                                      color: "#262626",
                                      cursor: "pointer",
                                    }}
                                  />
                                </Box>
                                {/* Image */}
                                {imgSelected ? (
                                  <Box
                                    component="img"
                                    src={imgSelected}
                                    sx={{ width: "100%", display: "block" }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      height: 260,
                                      bgcolor: "#fafafa",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{ color: "#c7c7c7", fontSize: 12 }}
                                    >
                                      No image selected
                                    </Typography>
                                  </Box>
                                )}
                                {/* Actions */}
                                <Box sx={{ px: 1.5, py: 1 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      mb: 0.75,
                                    }}
                                  >
                                    <Box sx={{ display: "flex", gap: 1.5 }}>
                                      <FontAwesomeIcon
                                        icon={faHeart}
                                        style={{
                                          fontSize: 22,
                                          cursor: "pointer",
                                          color: "#262626",
                                        }}
                                      />
                                      <FontAwesomeIcon
                                        icon={faComment}
                                        style={{
                                          fontSize: 22,
                                          cursor: "pointer",
                                          color: "#262626",
                                        }}
                                      />
                                      <FontAwesomeIcon
                                        icon={faArrowUpFromBracket}
                                        style={{
                                          fontSize: 22,
                                          cursor: "pointer",
                                          color: "#262626",
                                        }}
                                      />
                                    </Box>
                                    <FontAwesomeIcon
                                      icon={faBookmark}
                                      style={{
                                        fontSize: 22,
                                        cursor: "pointer",
                                        color: "#262626",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      color: "#262626",
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    <span style={{ fontWeight: 700 }}>
                                      {handle}{" "}
                                    </span>
                                    {applyLink(
                                      channelContent.instagram.body,
                                    ) || (
                                      <span
                                        style={{
                                          color: "#a8a8a8",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        No caption
                                      </span>
                                    )}
                                  </Typography>
                                  {channelContent.instagram.hashtags && (
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "#00376b",
                                        mt: 0.5,
                                      }}
                                    >
                                      {channelContent.instagram.hashtags}
                                    </Typography>
                                  )}
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: "#8e8e8e",
                                      mt: 0.5,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.03em",
                                    }}
                                  >
                                    {today}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                          {/* ── X / Twitter ── */}
                          {expandedChannels[ch] && !isEditing && ch === "x" && (
                            <Box sx={{ p: 2, bgcolor: "#000" }}>
                              <Box sx={{ display: "flex", gap: 1.5 }}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    bgcolor: "#333",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontWeight: 800,
                                    fontSize: 14,
                                    flexShrink: 0,
                                  }}
                                >
                                  {initials}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: "#e7e9ea",
                                          lineHeight: 1.3,
                                        }}
                                      >
                                        {cfg.candidateName || "Candidate"}
                                      </Typography>
                                      <Typography
                                        sx={{ fontSize: 11, color: "#71767b" }}
                                      >
                                        @{handle} · now
                                      </Typography>
                                    </Box>
                                    <FontAwesomeIcon
                                      icon={faEllipsis}
                                      style={{
                                        fontSize: 16,
                                        color: "#71767b",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      color: "#e7e9ea",
                                      mt: 0.75,
                                      lineHeight: 1.5,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {applyLink(channelContent.x.body) || (
                                      <span
                                        style={{
                                          color: "#71767b",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        No content yet
                                      </span>
                                    )}
                                  </Typography>
                                  {imgSelected && (
                                    <Box
                                      component="img"
                                      src={imgSelected}
                                      sx={{
                                        width: "100%",
                                        mt: 1.5,
                                        borderRadius: 2,
                                      }}
                                    />
                                  )}
                                  <Box
                                    sx={{ display: "flex", gap: 4, mt: 1.5 }}
                                  >
                                    {[
                                      [faComment, "0"],
                                      [faRetweet, "0"],
                                      [faHeart, "0"],
                                      [faArrowUpFromBracket, ""],
                                    ].map(([icon, count], i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.75,
                                          cursor: "pointer",
                                          "&:hover .x-icon": {
                                            color: "#1d9bf0",
                                          },
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={icon}
                                          className="x-icon"
                                          style={{
                                            fontSize: 16,
                                            color: "#71767b",
                                          }}
                                        />
                                        {count && (
                                          <Typography
                                            sx={{
                                              fontSize: 12,
                                              color: "#71767b",
                                            }}
                                          >
                                            {count}
                                          </Typography>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}

                          {/* ── LinkedIn ── */}
                          {expandedChannels[ch] &&
                            !isEditing &&
                            ch === "linkedin" && (
                              <Box>
                                <Box sx={{ p: 2 }}>
                                  <Box
                                    sx={{ display: "flex", gap: 1.5, mb: 1.5 }}
                                  >
                                    <Box
                                      sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: "50%",
                                        bgcolor: "#0a66c2",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontWeight: 800,
                                        fontSize: 16,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {initials}
                                    </Box>
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: "rgba(0,0,0,0.9)",
                                        }}
                                      >
                                        {cfg.candidateName || "Candidate"}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: 11,
                                          color: "rgba(0,0,0,0.6)",
                                        }}
                                      >
                                        Political Candidate
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: 10,
                                            color: "rgba(0,0,0,0.4)",
                                          }}
                                        >
                                          {today}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 10,
                                            color: "rgba(0,0,0,0.4)",
                                          }}
                                        >
                                          ·
                                        </Typography>
                                        <FontAwesomeIcon
                                          icon={faGlobe}
                                          style={{
                                            fontSize: 9,
                                            color: "rgba(0,0,0,0.4)",
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      color: "rgba(0,0,0,0.9)",
                                      lineHeight: 1.5,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {applyLink(
                                      channelContent.linkedin.body,
                                    ) || (
                                      <span
                                        style={{
                                          color: "#a0a0a0",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        No content yet
                                      </span>
                                    )}
                                  </Typography>
                                  {imgSelected && (
                                    <Box
                                      component="img"
                                      src={imgSelected}
                                      sx={{
                                        width: "100%",
                                        mt: 1.5,
                                        borderRadius: 1,
                                      }}
                                    />
                                  )}
                                </Box>
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 0.75,
                                    borderTop: "1px solid #e0e0e0",
                                    display: "flex",
                                    gap: 1,
                                  }}
                                >
                                  {[
                                    [faThumbsUp, "Like"],
                                    [faComment, "Comment"],
                                    [faRetweet, "Repost"],
                                    [faArrowUpFromBracket, "Send"],
                                  ].map(([icon, label]) => (
                                    <Box
                                      key={label}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        px: 1.25,
                                        py: 0.625,
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        "&:hover": { bgcolor: "#f3f2ef" },
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={icon}
                                        style={{
                                          fontSize: 16,
                                          color: "rgba(0,0,0,0.6)",
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "rgba(0,0,0,0.6)",
                                        }}
                                      >
                                        {label}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}

                          {/* ── TikTok ── */}
                          {expandedChannels[ch] &&
                            !isEditing &&
                            ch === "tiktok" && (
                              <Box
                                sx={{
                                  bgcolor: "#010101",
                                  maxWidth: 320,
                                  mx: "auto",
                                }}
                              >
                                <Box
                                  sx={{
                                    position: "relative",
                                    height: 420,
                                    bgcolor: "#1c1c1c",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {imgSelected ? (
                                    <Box
                                      component="img"
                                      src={imgSelected}
                                      sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <Typography
                                      sx={{ color: "#555", fontSize: 12 }}
                                    >
                                      Video frame
                                    </Typography>
                                  )}
                                  {/* Right actions */}
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      bottom: 16,
                                      right: 12,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 2,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 0.25,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: "50%",
                                          bgcolor: "#ff2d55",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faHeartSolid}
                                          style={{
                                            fontSize: 18,
                                            color: "#fff",
                                          }}
                                        />
                                      </Box>
                                      <Typography
                                        sx={{ fontSize: 10, color: "#fff" }}
                                      >
                                        124
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 0.25,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: "50%",
                                          bgcolor: "rgba(255,255,255,0.15)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faComment}
                                          style={{
                                            fontSize: 18,
                                            color: "#fff",
                                          }}
                                        />
                                      </Box>
                                      <Typography
                                        sx={{ fontSize: 10, color: "#fff" }}
                                      >
                                        12
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 0.25,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: "50%",
                                          bgcolor: "rgba(255,255,255,0.15)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faArrowUpFromBracket}
                                          style={{
                                            fontSize: 18,
                                            color: "#fff",
                                          }}
                                        />
                                      </Box>
                                      <Typography
                                        sx={{ fontSize: 10, color: "#fff" }}
                                      >
                                        Share
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {/* Bottom text */}
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      bottom: 16,
                                      left: 12,
                                      right: 64,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: "#fff",
                                        mb: 0.5,
                                      }}
                                    >
                                      @{handle}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "rgba(255,255,255,0.9)",
                                        lineHeight: 1.4,
                                        whiteSpace: "pre-wrap",
                                      }}
                                    >
                                      {applyLink(
                                        channelContent.tiktok.caption,
                                      ) || (
                                        <span style={{ fontStyle: "italic" }}>
                                          No caption
                                        </span>
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}

                          {/* ── Email ── */}
                          {expandedChannels[ch] &&
                            !isEditing &&
                            ch === "email" && (
                              <Box>
                                <Box
                                  sx={{
                                    bgcolor: "#f9fafb",
                                    px: 3,
                                    py: 2,
                                    borderBottom: `1px solid ${C.border}`,
                                  }}
                                >
                                  {[
                                    ["From", cfg.candidateName || "Candidate"],
                                    ["To", "Campaign List"],
                                    [
                                      "Subject",
                                      applyLink(channelContent.email.subject),
                                    ],
                                  ].map(([label, val]) => (
                                    <Box
                                      key={label}
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        mb: 0.5,
                                        "&:last-child": { mb: 0 },
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 11,
                                          color: "#6b7280",
                                          minWidth: 52,
                                        }}
                                      >
                                        {label}:
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: 11,
                                          color: "#111827",
                                          fontWeight:
                                            label === "Subject" ? 700 : 400,
                                          fontStyle:
                                            label === "Subject" && !val
                                              ? "italic"
                                              : "normal",
                                        }}
                                      >
                                        {val || (
                                          <span style={{ color: "#9ca3af" }}>
                                            No subject
                                          </span>
                                        )}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                                <Box sx={{ p: 3 }}>
                                  {channelContent.email.body ? (
                                    <Box
                                      sx={{
                                        fontSize: 13,
                                        color: "#374151",
                                        lineHeight: 1.65,
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: renderEmailHtml(
                                          applyLink(channelContent.email.body),
                                        ),
                                      }}
                                    />
                                  ) : (
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        fontStyle: "italic",
                                        color: "#9ca3af",
                                      }}
                                    >
                                      No body yet
                                    </Typography>
                                  )}
                                  {imgSelected && (
                                    <Box
                                      component="img"
                                      src={imgSelected}
                                      sx={{
                                        width: "100%",
                                        mt: 2,
                                        borderRadius: 1,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}

                          {/* ── SMS ── */}
                          {expandedChannels[ch] &&
                            !isEditing &&
                            ch === "sms" && (
                              <Box sx={{ p: 2 }}>
                                <Box
                                  sx={{
                                    bgcolor: "#f3f4f6",
                                    borderRadius: 2,
                                    p: 2,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: "#6b7280",
                                      mb: 1.5,
                                      textAlign: "center",
                                    }}
                                  >
                                    Messages
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1,
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        bgcolor: "#e5e7eb",
                                        borderRadius: "16px 16px 16px 4px",
                                        px: 1.5,
                                        py: 1,
                                        maxWidth: "85%",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          color: "#111827",
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {applyLink(channelContent.sms.body) || (
                                          <span
                                            style={{
                                              fontStyle: "italic",
                                              color: "#9ca3af",
                                            }}
                                          >
                                            No message yet
                                          </span>
                                        )}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Typography
                                    sx={{
                                      fontSize: 9,
                                      color: "#9ca3af",
                                      textAlign: "left",
                                      mt: 0.75,
                                    }}
                                  >
                                    Delivered
                                  </Typography>
                                </Box>
                                {channelContent.sms.body &&
                                  (() => {
                                    const resolved = applyLink(
                                      channelContent.sms.body,
                                    );
                                    return (
                                      <Typography
                                        sx={{
                                          fontSize: 10,
                                          color:
                                            resolved.length > 160
                                              ? "#dc2626"
                                              : C.muted,
                                          textAlign: "right",
                                          mt: 0.75,
                                        }}
                                      >
                                        {resolved.length} chars ·{" "}
                                        {Math.ceil(resolved.length / 160)}{" "}
                                        segment
                                        {Math.ceil(resolved.length / 160) > 1
                                          ? "s"
                                          : ""}
                                      </Typography>
                                    );
                                  })()}
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Drawer>

      {/* ── Image picker modal ── */}
      {imgPickerOpen && imgJob?.choices?.length > 0 && (
        <Box
          onClick={() => setImgPickerOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.82)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: C.white,
              borderRadius: 2,
              p: 3,
              maxWidth: 600,
              width: "100%",
              boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2.5,
              }}
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 800, color: C.charcoal }}
              >
                Choose Image Variant
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Box
                  onClick={() => {
                    setImgPickerOpen(false);
                    generateImage(intel.social.image_prompt);
                  }}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    px: 1.5,
                    py: 0.5,
                    bgcolor: C.navy,
                    color: C.white,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { opacity: 0.88 },
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  ✦ Regenerate
                </Box>
                <Box
                  onClick={() => setImgPickerOpen(false)}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    px: 1.5,
                    py: 0.5,
                    border: `1px solid ${C.border}`,
                    color: C.charcoal,
                    bgcolor: "#fff",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { borderColor: C.navy, color: C.navy },
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  ✕ Close
                </Box>
              </Box>
            </Box>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              {imgJob.choices.map((url, i) => (
                <Box
                  key={i}
                  onClick={() => {
                    setImgSelected(url);
                    setImgPickerOpen(false);
                    try {
                      localStorage.setItem(
                        LS_IMG_DATA,
                        JSON.stringify({ ...imgJob, selected: url }),
                      );
                    } catch {}
                  }}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    border: `3px solid ${imgSelected === url ? C.blue : C.border}`,
                    transition: "border-color 0.12s",
                    "&:hover": { borderColor: C.blue },
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt={`Variant ${i + 1}`}
                    sx={{ width: "100%", display: "block" }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11,
                      textAlign: "center",
                      py: 0.75,
                      color: imgSelected === url ? C.blue : C.muted,
                      fontWeight: imgSelected === url ? 800 : 400,
                    }}
                  >
                    {imgSelected === url
                      ? `Variant ${i + 1} ✓`
                      : `Variant ${i + 1}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Shared simulation confirm modal ────────────────────────────────────────
const SimConfirmModal = ({ onClose, onConfirm }) => (
  <Box
    onClick={onClose}
    sx={{
      position: "fixed",
      inset: 0,
      bgcolor: "rgba(0,0,0,0.55)",
      zIndex: 1400,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: 2,
    }}
  >
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        bgcolor: "#1a2744",
        borderRadius: 3,
        maxWidth: 520,
        width: "100%",
        p: 4,
        color: "#fff",
        position: "relative",
      }}
    >
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 16,
          right: 18,
          cursor: "pointer",
          fontSize: 18,
          color: "#94a3b8",
          "&:hover": { color: "#fff" },
        }}
      >
        ✕
      </Box>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: "#94a3b8",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        Electorate Simulation
      </Typography>
      <Typography
        sx={{ fontSize: 22, fontWeight: 800, color: "#fff", mb: 0.75 }}
      >
        Run a Voter Simulation
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#94a3b8", mb: 3 }}>
        Simulate how your electorate thinks, reacts, and decides—before you
        deploy messaging.
      </Typography>
      {[
        [
          "Electorate Model",
          "A dynamic, representative electorate built from voter-file data, demographics, and behavioral patterns.",
        ],
        [
          "Ask Any Question",
          "Test issue positions, candidate preference, and message resonance using natural language.",
        ],
        [
          "Individual Responses",
          "Each simulated voter responds independently based on their modeled profile.",
        ],
        [
          "Instant Results",
          "Full response breakdown delivered immediately upon completion.",
        ],
      ].map(([title, desc]) => (
        <Box key={title} sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#22c55e",
              mt: "4px",
              flexShrink: 0,
            }}
          />
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>
              {desc}
            </Typography>
          </Box>
        </Box>
      ))}
      <Box
        sx={{
          bgcolor: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 2,
          px: 2.5,
          py: 2,
          mt: 2.5,
          mb: 1,
        }}
      >
        <Box
          sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}
        >
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
            Price per voter
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>
            $5.00
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
            Billing
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
            Charged on submission
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{ fontSize: 11, color: "#64748b", mb: 3, fontStyle: "italic" }}
      >
        Select voter count in the next step. A 100-voter simulation costs $500.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "#94a3b8",
            fontWeight: 600,
            fontSize: 13,
            textTransform: "none",
            px: 2.5,
            "&:hover": { color: "#fff" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            bgcolor: "#16a34a",
            color: "#fff",
            fontWeight: 700,
            fontSize: 13,
            px: 3,
            py: 1,
            textTransform: "none",
            borderRadius: "8px",
            "&:hover": { bgcolor: "#15803d" },
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  </Box>
);

// ─── Electorate Simulations Tab ────────────────────────────────────────────
const SimulationsTab = () => {
  const simUrl = process.env.REACT_APP_SIMULATIONS_API_URL;
  const { selectedCampaign: authCampaign } = useAuth();
  const campaignId = authCampaign?.id;

  const [simulations, setSimulations] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 15;

  const loadSims = () => {
    if (!simUrl || !campaignId) return;
    fetch(`${simUrl}?campaign_id=${campaignId}`)
      .then((r) => r.json())
      .then((d) => setSimulations(d.simulations || []))
      .catch(() => {});
  };
  useEffect(() => {
    loadSims();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => setConfirmOpen(true);
  const confirmNew = () => {
    setConfirmOpen(false);
    setActiveJobId(null);
    setDrawerOpen(true);
  };
  const openSim = (id) => {
    setActiveJobId(id);
    setDrawerOpen(true);
  };
  const closeDrawer = (didSubmit) => {
    setDrawerOpen(false);
    setActiveJobId(null);
    if (didSubmit) loadSims();
  };
  const updateSimRow = (id, patch) =>
    setSimulations((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    );

  const fmtDt = (s) => {
    if (!s) return "—";
    try {
      return new Date(s).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return s;
    }
  };

  const hasSims = simulations.length > 0;

  const thSx = {
    fontSize: 10,
    fontWeight: 700,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    padding: "6px 8px",
    borderBottom: `1px solid ${C.border}`,
    textAlign: "left",
  };
  const tdSx = {
    padding: "7px 8px",
    fontSize: 11,
    color: C.charcoal,
    borderBottom: `1px solid ${C.border}`,
  };

  const capabilities = [
    {
      icon: "◎",
      title: "Simulate Your Electorate",
      desc: "Build a representative model grounded in real demographics, behavior, and media exposure.",
    },
    {
      icon: "⟳",
      title: "Test Before Deployment",
      desc: "Run messaging across thousands of simulated voters before a single dollar is spent.",
    },
    {
      icon: "≋",
      title: "Compare Scenarios",
      desc: "Run multiple strategy variants side by side and measure predicted outcomes.",
    },
    {
      icon: "↗",
      title: "Iterate to Convergence",
      desc: "Rapidly refine messaging and strategy until you reach maximum predicted effectiveness.",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
        alignItems: { xs: "stretch", md: "stretch" },
      }}
    >
      {/* Left: image (always) */}
      <Box
        sx={{
          flex: { xs: "0 0 100%", md: "0 0 42%" },
          width: { xs: "100%", md: "auto" },
          display: "flex",
        }}
      >
        <Box
          component="img"
          src="/simulate02.png"
          alt="Electorate Simulation Preview"
          sx={{
            width: "100%",
            borderRadius: 2,
            display: "block",
            objectFit: "cover",
            height: { xs: "auto", md: "100%" },
          }}
        />
      </Box>

      {/* Right: hero always, then table or landing content */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        {/* Hero — always visible */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${C.navy} 0%, #0d2d50 60%, #112240 100%)`,
            borderRadius: 2,
            px: { xs: 3, md: 5 },
            py: { xs: 5, md: 7 },
            mb: 3,
            position: "relative",
            overflow: "hidden",
            border: `1px solid #1a3d5c`,
          }}
        >
          {/* Background grid texture */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.04,
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "40px 40px",
              pointerEvents: "none",
            }}
          />

          {/* Coming Soon chip */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              bgcolor: "#FF0A4B18",
              border: "1px solid #FF0A4B44",
              borderRadius: "20px",
              px: 1.5,
              py: 0.4,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#FF0A4B",
                animation: "pulse 1.4s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%,100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              }}
            />
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: "#FF0A4B",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              Coming Soon
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: { xs: 26, md: 36 },
              fontWeight: 900,
              color: C.white,
              lineHeight: 1.15,
              mb: 1.5,
              letterSpacing: "-0.02em",
            }}
          >
            Electorate
            <br />
            Simulations
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 13, md: 15 },
              color: "#7aaccc",
              fontWeight: 400,
              mb: 3,
              maxWidth: 480,
              lineHeight: 1.6,
            }}
          >
            Test outcomes before they happen. Pollis builds a dynamic model of
            your electorate — simulating how voters think, react, and decide.
          </Typography>

          {/* Signal chain + CTA */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.75, md: 1.5 },
                flexWrap: "wrap",
              }}
            >
              {["Signal", "Simulate", "Orchestrate", "Convert"].map(
                (step, i, arr) => (
                  <Box
                    key={step}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.75, md: 1.5 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 10, md: 11 },
                        fontWeight: 700,
                        color: i === 1 ? "#4ade80" : "#5a7898",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {step}
                    </Typography>
                    {i < arr.length - 1 && (
                      <Typography sx={{ fontSize: 10, color: "#2a4d6c" }}>
                        →
                      </Typography>
                    )}
                  </Box>
                ),
              )}
            </Box>
            <Button
              onClick={openNew}
              variant="contained"
              sx={{
                bgcolor: "#22c55e",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                py: 0.75,
                px: 2.5,
                textTransform: "none",
                borderRadius: "6px",
                flexShrink: 0,
                "&:hover": { bgcolor: "#16a34a" },
              }}
            >
              {hasSims ? "+ New Simulation" : "Run your first simulation"}
            </Button>
          </Box>

          {/* Decorative rings */}
          <Box
            sx={{
              position: "absolute",
              right: { xs: -60, md: -40 },
              top: -40,
              width: 260,
              height: 260,
              borderRadius: "50%",
              border: "1px solid #1a3d5c",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              right: { xs: -30, md: -10 },
              top: -10,
              width: 180,
              height: 180,
              borderRadius: "50%",
              border: "1px solid #1e4870",
              pointerEvents: "none",
            }}
          />
        </Box>

        {/* Simulations table — when sims exist */}
        {hasSims && (
          <Box
            sx={{
              mt: 2,
              borderRadius: 2,
              border: `1px solid ${C.border}`,
              bgcolor: C.white,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${C.border}` }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}
              >
                Simulations
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.25 }}>
                Electorate simulation history
              </Typography>
            </Box>
            <Box>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: C.bg }}>
                    <th style={thSx}>Date</th>
                    <th style={thSx}>Question</th>
                    <th style={thSx}>ZIP</th>
                    <th style={thSx}>Voters</th>
                    <th style={thSx}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {simulations
                    .slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
                    .map((sim) => (
                      <tr
                        key={sim.id}
                        onClick={() => openSim(sim.id)}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f8fafc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <td style={tdSx}>{fmtDt(sim.created_at)}</td>
                        <td style={{ ...tdSx, maxWidth: 240 }}>
                          <Box
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sim.question}
                          </Box>
                        </td>
                        <td style={tdSx}>{sim.zip_code}</td>
                        <td style={tdSx}>{sim.voter_count}</td>
                        <td style={tdSx}>
                          <SimStatusBadge status={sim.status} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Box>
            {simulations.length > PAGE_SIZE && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2.5,
                  py: 1.25,
                  borderTop: `1px solid ${C.border}`,
                }}
              >
                <Typography sx={{ fontSize: 11, color: C.muted }}>
                  {page * PAGE_SIZE + 1}–
                  {Math.min((page + 1) * PAGE_SIZE, simulations.length)} of{" "}
                  {simulations.length}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Button
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      fontSize: 11,
                      color: C.muted,
                      "&:not(:disabled)": { color: C.charcoal },
                    }}
                  >
                    ← Prev
                  </Button>
                  <Button
                    size="small"
                    disabled={(page + 1) * PAGE_SIZE >= simulations.length}
                    onClick={() => setPage((p) => p + 1)}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      fontSize: 11,
                      color: C.muted,
                      "&:not(:disabled)": { color: C.charcoal },
                    }}
                  >
                    Next →
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {!hasSims && (
          <>
            {/* Capabilities grid */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: 2,
                }}
              >
                Capabilities
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {capabilities.map((c) => (
                  <Box
                    key={c.title}
                    sx={{
                      p: 2.5,
                      borderRadius: 1.5,
                      border: `1px solid ${C.border}`,
                      bgcolor: C.white,
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 18,
                        lineHeight: 1,
                        color: "#4ade80",
                        mt: 0.1,
                      }}
                    >
                      {c.icon}
                    </Typography>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: C.charcoal,
                          mb: 0.5,
                        }}
                      >
                        {c.title}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}
                      >
                        {c.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* How it works + Why it matters */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 1.5,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 1.5,
                  border: `1px solid ${C.border}`,
                  bgcolor: C.white,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    mb: 1.5,
                  }}
                >
                  How It Works
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: C.charcoal, lineHeight: 1.7 }}
                >
                  Pollis models the electorate as a dynamic system shaped by
                  demographics, behavior, and media exposure. Simulations
                  reflect how voters process information and respond to
                  messaging, allowing campaigns to evaluate outcomes before
                  acting.
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 1.5,
                  border: `1px solid ${C.border}`,
                  bgcolor: C.white,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    mb: 1.5,
                  }}
                >
                  Why It Matters
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {[
                    "Reduce wasted spend on ineffective messaging",
                    "Increase conversion before deployment",
                    "Understand voter response without waiting for polls",
                    "Move from reactive decisions to engineered outcomes",
                  ].map((item) => (
                    <Box
                      component="li"
                      key={item}
                      sx={{
                        fontSize: 12,
                        color: C.charcoal,
                        lineHeight: 1.7,
                        mb: 0.25,
                      }}
                    >
                      {item}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* CTA */}
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                bgcolor: C.navy,
                border: `1px solid #1a3d5c`,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#4ade80",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  mb: 1,
                }}
              >
                Early Access
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 18, md: 22 },
                  fontWeight: 800,
                  color: C.white,
                  mb: 1,
                  letterSpacing: "-0.01em",
                }}
              >
                Don't guess. Simulate.
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#7aaccc", mb: 3 }}>
                Be first to access Electorate Simulation.
              </Typography>
            </Box>
          </>
        )}
      </Box>
      {/* end right column */}

      {confirmOpen && (
        <SimConfirmModal
          onClose={() => setConfirmOpen(false)}
          onConfirm={confirmNew}
        />
      )}
      <SimulationDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        jobId={activeJobId}
        onSimUpdate={updateSimRow}
      />
    </Box>
  );
};

// ─── Simulations Testing Tab ───────────────────────────────────────────────

const STATUS_COLORS_SIM = {
  queued: { bg: "#f0f9ff", border: "#bae6fd", text: "#0369a1" },
  running: { bg: "#fffbeb", border: "#fde68a", text: "#b45309" },
  completed: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
  failed: { bg: "#fef2f2", border: "#fecaca", text: "#dc2626" },
};

const SimStatusBadge = ({ status }) => {
  const s = STATUS_COLORS_SIM[status] || STATUS_COLORS_SIM.queued;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        px: 1,
        py: 0.3,
        borderRadius: "3px",
        bgcolor: s.bg,
        border: `1px solid ${s.border}`,
        color: s.text,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        display: "inline-block",
      }}
    >
      {status}
    </Box>
  );
};

const SimulationDrawer = ({
  open,
  onClose,
  jobId: initialJobId,
  onSimUpdate,
}) => {
  const simUrl = process.env.REACT_APP_SIMULATIONS_API_URL;
  const pubnubKey = process.env.REACT_APP_PUBNUB_SUB_KEY;
  const { selectedCampaign: authCampaign } = useAuth();
  const campaignId = authCampaign?.id;

  const [zipCode, setZipCode] = useState("");
  const [voterCount, setVoterCount] = useState(100);
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [statusLabel, setStatusLabel] = useState("");
  const [result, setResult] = useState(null);
  const [simError, setSimError] = useState(null);
  const [loadedSim, setLoadedSim] = useState(null);
  const [jobId, setJobId] = useState(null);
  const didSubmitRef = useRef(false);

  const pnRef = useRef(null);
  const channelRef = useRef(null);

  // Load existing simulation when jobId prop supplied (row click)
  useEffect(() => {
    if (!open) return;
    // Reset form state on every open
    setZipCode("");
    setVoterCount(100);
    setQuestion("");
    setSubmitting(false);
    setStatusLabel("");
    setResult(null);
    setSimError(null);
    setLoadedSim(null);
    setJobId(null);
    didSubmitRef.current = false;
    channelRef.current = null;

    if (initialJobId && simUrl) {
      fetch(`${simUrl}/${initialJobId}`)
        .then((r) => r.json())
        .then((d) => {
          setLoadedSim(d);
          if (d.result) setResult(d.result);
          // Push status update back to table row immediately
          if (onSimUpdate)
            onSimUpdate(d.id, {
              status: d.status,
              completed_at: d.completed_at,
            });
          // Re-attach live PubNub if sim is still in-flight
          if (
            d.pubnub_channel &&
            (d.status === "running" || d.status === "queued")
          ) {
            channelRef.current = d.pubnub_channel;
            setSubmitting(true);
            setStatusLabel("Simulation running…");
            setJobId(d.id);
          }
        })
        .catch(() => {});
    }
  }, [open, initialJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // PubNub subscription — wired to channelRef so it stays live during polling
  useEffect(() => {
    if (!channelRef.current || !pubnubKey) return;
    const channel = channelRef.current;
    const pn = new PubNub({ subscribeKey: pubnubKey, userId: "pollen-app" });
    pnRef.current = pn;
    pn.addListener({
      message: ({ message: msg }) => {
        const { type, data } = msg;
        if (type === "status") setStatusLabel(data.label || "");
        if (type === "done") {
          setResult(data);
          setSubmitting(false);
          setStatusLabel("");
        }
        if (type === "error") {
          const detail = data.detail
            ? `\n${JSON.stringify(data.detail, null, 2)}`
            : "";
          setSimError((data.message || "Simulation failed") + detail);
          setSubmitting(false);
          setStatusLabel("");
        }
      },
    });
    pn.subscribe({ channels: [channel] });
    return () => {
      pn.unsubscribeAll();
      pn.destroy();
      pnRef.current = null;
    };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (!simUrl) {
      setSimError("REACT_APP_SIMULATIONS_API_URL not set");
      return;
    }
    if (!/^\d{5}$/.test(zipCode)) {
      setSimError("ZIP code must be exactly 5 digits");
      return;
    }
    if (!question.trim()) {
      setSimError("Question is required");
      return;
    }

    const channel = `sim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    channelRef.current = channel;

    didSubmitRef.current = true;
    setSubmitting(true);
    setSimError(null);
    setResult(null);
    setStatusLabel("Connecting…");
    // Trigger PubNub sub by updating jobId (any truthy value works before we have the real id)
    setJobId(`pending-${Date.now()}`);

    fetch(simUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        zip_code: zipCode,
        voter_count: voterCount,
        question: question.trim(),
        campaign_id: campaignId,
        pubnub_channel: channel,
      }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) {
          setSimError(d.error);
          setSubmitting(false);
          setStatusLabel("");
          return;
        }
        setJobId(d.job_id);
        setStatusLabel("Queued — building electorate…");
      })
      .catch((e) => {
        setSimError(e.message);
        setSubmitting(false);
        setStatusLabel("");
      });
  };

  const isViewMode = !!initialJobId;
  const displaySim = loadedSim;

  const fieldSx = {
    width: "100%",
    fontSize: 12,
    color: C.charcoal,
    border: `1px solid ${C.border}`,
    borderRadius: "6px",
    px: 1.5,
    py: 1,
    fontFamily: "inherit",
    outline: "none",
    bgcolor: C.white,
    boxSizing: "border-box",
    "&:focus": { borderColor: C.navy },
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={() => onClose(didSubmitRef.current)}
      transitionDuration={300}
      PaperProps={{
        sx: {
          width: { xs: "100vw", md: "52vw" },
          minWidth: { xs: 0, md: 440 },
          bgcolor: "#f9fafb",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: C.white,
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.charcoal }}>
            {isViewMode ? "Simulation Results" : "New Simulation"}
          </Typography>
          {isViewMode && displaySim && (
            <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.25 }}>
              {displaySim.zip_code} · {displaySim.voter_count} voters
            </Typography>
          )}
        </Box>
        <Box
          onClick={() => onClose(didSubmitRef.current)}
          sx={{
            cursor: "pointer",
            color: C.muted,
            fontSize: 16,
            fontWeight: 700,
            "&:hover": { color: C.charcoal },
          }}
        >
          ✕
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
        {/* ── View mode: read-only summary ── */}
        {isViewMode && displaySim && (
          <Box
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 1.5,
              border: `1px solid ${C.border}`,
              bgcolor: C.white,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 1.5,
              }}
            >
              Parameters
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
            >
              {[
                ["ZIP Code", displaySim.zip_code],
                ["Voter Count", displaySim.voter_count],
                ["Status", <SimStatusBadge status={displaySim.status} />],
                ["Created", new Date(displaySim.created_at).toLocaleString()],
              ].map(([label, val]) => (
                <Box key={label}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: C.charcoal,
                      fontWeight: 600,
                      mt: 0.25,
                    }}
                  >
                    {val}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: 10,
                  color: C.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}
              >
                Question
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: C.charcoal,
                  mt: 0.25,
                  lineHeight: 1.5,
                }}
              >
                {displaySim.question}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── New simulation form ── */}
        {!isViewMode && (
          <Box
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 1.5,
              border: `1px solid ${C.border}`,
              bgcolor: C.white,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 2,
              }}
            >
              Parameters
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.charcoal,
                    mb: 0.5,
                  }}
                >
                  Target ZIP Code
                </Typography>
                <Box
                  component="input"
                  value={zipCode}
                  onChange={(e) =>
                    setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))
                  }
                  placeholder="e.g. 29464"
                  maxLength={5}
                  disabled={submitting}
                  sx={{ ...fieldSx, display: "block" }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.charcoal,
                    mb: 0.5,
                  }}
                >
                  Voter Count{" "}
                  <Box
                    component="span"
                    sx={{ color: C.muted, fontWeight: 400 }}
                  >
                    ({voterCount})
                  </Box>
                </Typography>
                <Box
                  component="input"
                  type="range"
                  min={1}
                  max={1000}
                  value={voterCount}
                  onChange={(e) => setVoterCount(Number(e.target.value))}
                  disabled={submitting}
                  sx={{ width: "100%", mt: 0.75, accentColor: C.navy }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 9, color: C.muted }}>
                    1
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: C.muted }}>
                    1,000
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.charcoal,
                  mb: 0.5,
                }}
              >
                Polling Question
              </Typography>
              <Box
                component="textarea"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="e.g. Who is your top choice for Governor in the upcoming race?"
                rows={3}
                disabled={submitting}
                sx={{
                  ...fieldSx,
                  display: "block",
                  resize: "vertical",
                  lineHeight: 1.5,
                }}
              />
            </Box>

            {simError && (
              <Box
                sx={{
                  bgcolor: "#fff1f2",
                  border: "1px solid #fecdd3",
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1.5,
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontSize: 11,
                    color: "#be123c",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                  }}
                >
                  {simError}
                </pre>
              </Box>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                bgcolor: "#16a34a",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                py: 1.25,
                textTransform: "none",
                borderRadius: "6px",
                "&:hover": { bgcolor: "#15803d" },
                "&:disabled": { bgcolor: C.border },
              }}
            >
              {submitting ? "Running…" : "Start Poll"}
            </Button>
          </Box>
        )}

        {/* ── Live status indicator ── */}
        {submitting && statusLabel && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              px: 2.5,
              py: 1.5,
              bgcolor: "#eff6ff",
              borderRadius: 1.5,
              border: "1px solid #bfdbfe",
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: C.navy,
                animation: "pulse 1.2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%,100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.navy }}>
              {statusLabel}
            </Typography>
          </Box>
        )}

        {/* ── Results ── */}
        {result && (
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 1.5,
              }}
            >
              Results
            </Typography>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                border: `1px solid ${C.border}`,
                bgcolor: C.white,
              }}
            >
              <Box
                component="pre"
                sx={{
                  fontSize: 11,
                  color: C.charcoal,
                  m: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.6,
                  fontFamily: "monospace",
                }}
              >
                {JSON.stringify(result, null, 2)}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

// ─── Audience Explorer — the CRM/persons report ────────────────────────────
// All persons (donors, leads, matched — profiled or not); donor + voter + issue facets side by side;
// configurable columns; faceted filters; row selection → actions. Backend: GET /identity/persons.
const EXPLORER_COLUMNS = [
  { key: "person", label: "Person", def: true, w: "1.7fr" },
  { key: "roles", label: "Strategic roles", def: true, w: "1.3fr" },
  { key: "fit_tier", label: "Top model", def: true, w: "1fr" },
  { key: "source", label: "Source", def: false, w: "0.9fr" },
  { key: "location", label: "Location", def: true, w: "1fr" },
  { key: "party", label: "Party", def: false, w: "0.7fr" },
  { key: "turnout", label: "Turnout", def: true, w: "0.7fr" },
  { key: "total_donated", label: "Donated", def: false, w: "0.7fr" },
  { key: "charity", label: "Charity ask", def: false, w: "0.9fr" },
  { key: "issues", label: "Issues", def: true, w: "2.2fr" },
  { key: "next_best_action", label: "Next best action", def: true, w: "1.6fr" },
  { key: "last_donation", label: "Last gift", def: false, w: "0.8fr" },
  { key: "donation_count", label: "# Gifts", def: false, w: "0.6fr" },
  { key: "issue_count", label: "# Issues", def: false, w: "0.6fr" },
];
// Parse roles_json (a JSON string from personReport) → [{role,emoji,...}]. Tolerant of null/object.
function explorerParseRoles(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try {
    const p = JSON.parse(v);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}
function explorerParseIssues(str) {
  if (!str) return [];
  return str
    .split("|")
    .map((seg) => {
      const [slug, label, stance, stanceLabel, intensity] = seg.split(":");
      return {
        slug,
        label,
        stance,
        stanceLabel,
        intensity: parseFloat(intensity) || 0,
      };
    })
    .filter((x) => x.label);
}
const explorerMoney = (v) => {
  const n = Number(v || 0);
  if (!n) return "—";
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
};

// Isolated search box: holds the draft in ITS OWN local state so keystrokes re-render only this
// tiny input — NOT the parent Explorer + its table. Commits (fires the API search) only after the
// user pauses 200ms AND has typed ≥3 chars (empty resets). onSearch must be a stable callback
// (useCallback in the parent) or memo won't hold.
const ExplorerSearch = memo(function ExplorerSearch({ onSearch }) {
  const [draft, setDraft] = useState("");
  const timer = useRef(null);
  const handle = (e) => {
    const v = e.target.value;
    setDraft(v);
    if (timer.current) clearTimeout(timer.current);
    const t = v.trim();
    timer.current = setTimeout(() => {
      if (t.length === 0 || t.length >= 3) onSearch(t);
    }, 200);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return (
    <Box
      component="input"
      value={draft}
      onChange={handle}
      placeholder="Search name / email / phone… (3+ chars)"
      sx={{
        fontSize: 12,
        fontFamily: "inherit",
        p: 0.6,
        border: `1px solid ${C.border}`,
        borderRadius: 1,
        bgcolor: C.white,
        width: 220,
      }}
    />
  );
});

// Map metric key → the People-list filter it implies, so the list under the Ground Game map shows the
// SAME people the map is colored by. Metrics with no clean people-filter (turnout/model/context/people-total)
// return {} → the list is geo-scoped only.
const TERRAIN_ROLE_SLUG_TO_NAME = {
  "high-dollar-donor": "High-Dollar Donor",
  "small-dollar-donor": "Small-Dollar Donor",
  influencer: "Influencer",
  "media-amplifier": "Media Amplifier",
  "digital-amplifier": "Digital Amplifier",
  surrogate: "Surrogate",
  "host-fundraiser": "Host Fundraiser",
  recruiter: "Recruiter",
  volunteer: "Volunteer",
  "persuadable-voter": "Persuadable Voter",
  "reliable-supporter": "Reliable Supporter",
  "policy-validator": "Policy Validator",
  "community-leader": "Community Leader",
};
function metricToListFilter(metric) {
  const f = { charity: false, tableProspect: false, donors: false, party: "", issue: "", tier: "", role: "" };
  if (!metric) return f;
  if (metric === "charity:givers") f.charity = true;
  else if (metric === "charity:tableprospects") f.tableProspect = true;
  else if (metric === "donors:count" || metric === "donors:raised") f.donors = true;
  else if (metric === "voters:rep") f.party = "R";
  else if (metric === "voters:dem") f.party = "D";
  else if (metric === "fit:priority") f.tier = "priority";
  else if (metric === "fit:target") f.tier = "target";
  else {
    const mi = /^issue:(.+):(?:intensity|volume)$/.exec(metric);
    if (mi) f.issue = mi[1];
    const mr = /^role:(.+)$/.exec(metric);
    if (mr) f.role = TERRAIN_ROLE_SLUG_TO_NAME[mr[1]] || "";
  }
  return f;
}

function AudienceExplorer({ campaignId, geoScope, pageSize, metric }) {
  const { authFetch } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ issues: [], parties: [] });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fState, setFState] = useState("");
  const [fParty, setFParty] = useState("");
  const [fIssue, setFIssue] = useState("");
  const [donorsOnly, setDonorsOnly] = useState(false);
  const [contactOnly, setContactOnly] = useState(false);
  const [sort, setSort] = useState("recent");
  const [dir, setDir] = useState("desc");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [cols, setCols] = useState(
    new Set(EXPLORER_COLUMNS.filter((c) => c.def).map((c) => c.key)),
  );
  const [colPicker, setColPicker] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);
  // Unified People table — everything is a person now (leads included). These filters map to personReport's
  // type/kind/source/voters/donors params. Default: a clean verified-human view.
  const [typeFilter, setTypeFilter] = useState("identified"); // identified | lead | all
  const [enrichMenuOpen, setEnrichMenuOpen] = useState(false); // bulk-enrich leg picker on the selection bar
  const [enrichMsg, setEnrichMsg] = useState(""); // transient "queued N…" confirmation
  const [kindFilter, setKindFilter] = useState("person"); // person | org | all
  const [sourceFilter, setSourceFilter] = useState(""); // '' | winred | social
  const [tierFilter, setTierFilter] = useState(""); // '' | priority | target | watch
  const [roleFilter, setRoleFilter] = useState(""); // '' | a Strategic Role name
  const [votersOnly, setVotersOnly] = useState(false);
  const [charityOnly, setCharityOnly] = useState(false);
  const [tableProspectOnly, setTableProspectOnly] = useState(false);
  const LIMIT = pageSize || 100;

  // Stable so the memoized ExplorerSearch never re-renders on parent updates (table loads, etc.).
  const handleSearch = useCallback((val) => {
    setQ(val);
    setOffset(0);
  }, []);

  // Sync the list to the Ground Game map's selected metric: when the map colors by "Charity givers",
  // the list under it shows charity givers too. Fires only on metric CHANGE (not manual chip toggles),
  // and resets the metric-derived filters so switching metrics doesn't stack them.
  useEffect(() => {
    if (metric === undefined) return; // not embedded under the map (People tab) → leave filters alone
    const f = metricToListFilter(metric);
    setCharityOnly(f.charity);
    setTableProspectOnly(f.tableProspect);
    setDonorsOnly(f.donors);
    setFParty(f.party);
    setFIssue(f.issue);
    setTierFilter(f.tier);
    setRoleFilter(f.role);
    setOffset(0);
  }, [metric]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bulk enrichment queue — fire a self-chaining background job over the SELECTED people. leg picks the
  // enrichment: full contact waterfall (enrich), or a per-person web/social/deep-dive fan-out.
  const runBulkEnrich = async (leg, platform = "x") => {
    setEnrichMenuOpen(false);
    // selected holds rowKey() strings: 'p{personId}' for person rows, 's{profileId}' for social-only rows.
    // Enrichment keys on person_id, so take the 'p' rows.
    const person_ids = [...selected]
      .filter((k) => typeof k === "string" && k[0] === "p")
      .map((k) => parseInt(k.slice(1), 10))
      .filter((n) => n > 0);
    if (!person_ids.length || !SOCIAL_API_URL) {
      setEnrichMsg("Select person rows (not un-promoted leads) to enrich.");
      setTimeout(() => setEnrichMsg(""), 5000);
      return;
    }
    const labels = {
      research: "web research",
      enrich: "contact enrichment",
      "find-email": "email search",
      "find-social": "social-handle search",
      "deep-dive": platform === "x" ? "X deep-dive" : `${platform === "facebook" ? "Facebook" : "Instagram"} deep-dive`,
    };
    try {
      const r = await authFetch(`${SOCIAL_API_URL}/enrich-people`, {
        method: "POST",
        body: JSON.stringify({ campaign_id: campaignId, leg, platform, person_ids }),
      });
      const d = await r.json().catch(() => ({}));
      setEnrichMsg(
        `Queued ${d.queued ?? person_ids.length} for ${labels[leg] || leg} — running in the background. Refresh in a bit to see results.`,
      );
      setSelected(new Set());
      setTimeout(() => setEnrichMsg(""), 8000);
    } catch (e) {
      setEnrichMsg("Could not start enrichment.");
      setTimeout(() => setEnrichMsg(""), 5000);
    }
  };

  const load = useCallback(async () => {
    if (!IDENTITY_API_URL) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (campaignId) p.set("campaign_id", campaignId);
      if (q) p.set("q", q);
      if (fState) p.set("state", fState);
      if (fParty) p.set("party", fParty);
      if (fIssue) p.set("issue", fIssue);
      if (donorsOnly) p.set("donors", "1");
      if (votersOnly) p.set("voters", "1");
      if (contactOnly) p.set("has_contact", "1");
      if (charityOnly) p.set("charity", "1");
      if (tableProspectOnly) p.set("table_prospect", "1");
      // Always send type — the backend defaults a MISSING type to 'identified', so omitting it on the
      // "All" tab silently collapsed All → Verified. 'all' tells the backend to skip the type filter.
      p.set("type", typeFilter || "all");
      if (kindFilter && kindFilter !== "all") p.set("kind", kindFilter);
      if (sourceFilter) p.set("source", sourceFilter);
      if (tierFilter) p.set("tier", tierFilter);
      if (roleFilter) p.set("role", roleFilter);
      // Ground Game drill — scope the table to the selected map region.
      if (geoScope?.level && geoScope?.key) {
        p.set("geo_level", geoScope.level);
        p.set("geo_key", geoScope.key);
      }
      p.set("sort", sort);
      p.set("dir", dir);
      p.set("limit", String(LIMIT));
      p.set("offset", String(offset));
      const r = await authFetch(`${IDENTITY_API_URL}/persons?${p.toString()}`);
      const d = await r.json();
      setRows(d.persons || []);
      setTotal(d.total || 0);
      if (d.facets) setFacets(d.facets);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [
    authFetch,
    campaignId,
    q,
    fState,
    fParty,
    fIssue,
    donorsOnly,
    votersOnly,
    contactOnly,
    charityOnly,
    tableProspectOnly,
    typeFilter,
    kindFilter,
    sourceFilter,
    tierFilter,
    roleFilter,
    sort,
    dir,
    offset,
    geoScope?.level,
    geoScope?.key,
  ]);
  useEffect(() => {
    load();
  }, [load]);
  // Reset to page 1 when the selected region changes.
  useEffect(() => {
    setOffset(0);
  }, [geoScope?.level, geoScope?.key]);

  const activeCols = EXPLORER_COLUMNS.filter((c) => cols.has(c.key));
  const grid = `34px ${activeCols.map((c) => c.w).join(" ")}`;
  // Persons key on `id`; leads (social-only) key on `profile_id` — one helper covers both.
  const rowKey = (r) => (r.id != null ? `p${r.id}` : `s${r.profile_id}`);
  const toggleSel = (k) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const allSel = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const toggleAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allSel) rows.forEach((r) => n.delete(rowKey(r)));
      else rows.forEach((r) => n.add(rowKey(r)));
      return n;
    });

  const chip = (active, label, onClick) => (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        fontSize: 11,
        fontWeight: 700,
        px: 1,
        py: 0.4,
        borderRadius: "6px",
        border: `1px solid ${active ? C.navy : C.border}`,
        bgcolor: active ? C.navy : C.white,
        color: active ? "#fff" : C.charcoal,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );

  const dot = (on) => (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        mr: 0.4,
        bgcolor: on ? "#16a34a" : C.border,
      }}
    />
  );

  const renderCell = (key, row) => {
    switch (key) {
      case "person":
        return (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.charcoal,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.full_name ||
                  [row.first_name, row.last_name].filter(Boolean).join(" ") ||
                  (row.username ? "@" + row.username : "—")}
              </Typography>
              {row.email ? <span title={row.email}>✉</span> : null}
              {row.phone ? <span title={row.phone}>📞</span> : null}
            </Box>
            {(() => {
              // Platform-correct identity line. X → x.com, FB → facebook.com, IG → instagram.com. A FB
              // commenter with no vanity handle (profile.php?id) still links via their id so they never
              // look identity-less next to X people.
              const plat = row.social_platform || "x";
              const host =
                plat === "facebook"
                  ? "facebook.com"
                  : plat === "instagram"
                    ? "instagram.com"
                    : "x.com";
              const u = (row.username || "").replace(/^@/, "");
              const pid = String(row.platform_user_id || "");
              if (u) {
                return (
                  <Box
                    component="a"
                    href={`https://${host}/${u}`}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ fontSize: 10, color: C.navy, textDecoration: "none" }}
                  >
                    @{u}
                  </Box>
                );
              }
              if (
                (plat === "facebook" || plat === "instagram") &&
                pid &&
                !/^(facebook|instagram):/i.test(pid)
              ) {
                return (
                  <Box
                    component="a"
                    href={`https://${host}/${pid}`}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ fontSize: 10, color: C.navy, textDecoration: "none" }}
                  >
                    {plat === "instagram" ? "Instagram" : "Facebook"} profile ↗
                  </Box>
                );
              }
              return null;
            })()}
          </Box>
        );
      case "source": {
        const src = row.origin_source || "";
        const srcLabel = /winred/i.test(src)
          ? "WinRed"
          : /social/i.test(src)
            ? "Social"
            : src || "—";
        const tag = (label, fg, bg) => (
          <Box
            component="span"
            sx={{
              fontSize: 9,
              fontWeight: 800,
              color: fg,
              bgcolor: bg,
              borderRadius: "4px",
              px: 0.5,
              py: 0.1,
            }}
          >
            {label}
          </Box>
        );
        return (
          <Box sx={{ display: "flex", gap: 0.4, alignItems: "center", flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 11, color: C.charcoal }}>{srcLabel}</Typography>
            {row.person_type === "lead" && tag("LEAD", "#b45309", "#fef3c7")}
            {row.kind === "org" && tag("ORG", C.muted, C.bg)}
          </Box>
        );
      }
      case "location":
        return (
          <Typography sx={{ fontSize: 11, color: C.charcoal }}>
            {[row.city, row.state].filter(Boolean).join(", ") || "—"}
            {row.zip_code ? ` ${row.zip_code}` : ""}
          </Typography>
        );
      case "party":
        return row.party ? (
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 800,
              px: 0.6,
              py: 0.2,
              borderRadius: "4px",
              bgcolor: row.party === "R" ? "rgba(220,38,38,0.1)" : "rgba(37,99,235,0.1)",
              color:
                row.party === "R"
                  ? "#dc2626"
                  : row.party === "D"
                    ? "#2563eb"
                    : C.muted,
            }}
          >
            {row.party}
            {row.cd ? ` · CD-${row.cd}` : ""}
          </Box>
        ) : (
          "—"
        );
      case "turnout":
        return row.general_last == null && row.general_previous == null ? (
          <Typography sx={{ fontSize: 10, color: C.muted }}>—</Typography>
        ) : (
          <Box title="Last / previous general" sx={{ whiteSpace: "nowrap" }}>
            {dot(!!row.general_last)}
            {dot(!!row.general_previous)}
          </Box>
        );
      case "total_donated":
        return (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: row.total_donated > 0 ? 700 : 400,
              color: row.total_donated > 0 ? "#16a34a" : C.muted,
            }}
          >
            {explorerMoney(row.total_donated)}
          </Typography>
        );
      case "last_donation":
        return (
          <Typography sx={{ fontSize: 10, color: C.muted }}>
            {row.last_donation ? String(row.last_donation).slice(0, 10) : "—"}
          </Typography>
        );
      case "donation_count":
        return (
          <Typography sx={{ fontSize: 11, color: C.charcoal }}>
            {row.donation_count || "—"}
          </Typography>
        );
      case "issue_count":
        return (
          <Typography sx={{ fontSize: 11, color: C.charcoal }}>
            {row.issue_count || "—"}
          </Typography>
        );
      case "issues": {
        const allIss = explorerParseIssues(row.issues);
        let iss = allIss.slice(0, 4);
        // When an issue filter is active, always surface THAT issue — even if it ranks below the
        // top-4 by intensity (a weak/single signal, common for voter-file-matched people). Otherwise
        // a row that clearly matched the filter shows unrelated issues (or "—") and reads like a bug.
        if (fIssue && !iss.some((x) => x.slug === fIssue)) {
          const hit = allIss.find((x) => x.slug === fIssue);
          if (hit) iss = [hit, ...iss.slice(0, 3)];
        }
        if (!iss.length)
          return <Typography sx={{ fontSize: 10, color: C.muted }}>—</Typography>;
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            {iss.map((it, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 5,
                    borderRadius: 3,
                    bgcolor: C.navy + "18",
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.max(it.intensity * 100, 4)}%`,
                      height: "100%",
                      bgcolor: C.navy,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 9.5,
                    color: C.charcoal,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {it.label}
                  {it.stanceLabel ? (
                    <Box component="span" sx={{ color: C.muted }}>
                      {" · "}
                      {it.stanceLabel}
                    </Box>
                  ) : null}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      }
      case "roles": {
        const rls = explorerParseRoles(row.roles_json).slice(0, 4);
        if (!rls.length)
          return <Typography sx={{ fontSize: 10, color: C.muted }}>—</Typography>;
        return (
          <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
            {rls.map((r, i) => (
              <Box
                key={i}
                component="span"
                title={r.role}
                sx={{
                  fontSize: 10,
                  fontWeight: 800,
                  bgcolor: `${C.navy}14`,
                  color: C.navy,
                  borderRadius: "4px",
                  px: 0.6,
                  py: 0.2,
                  whiteSpace: "nowrap",
                }}
              >
                {r.role}
              </Box>
            ))}
          </Box>
        );
      }
      case "fit_tier": {
        if (row.top_score == null || !row.top_model)
          return <Typography sx={{ fontSize: 10, color: C.muted }}>—</Typography>;
        const m = SCORE_MODELS.find((x) => x.key === row.top_model);
        const score = Number(row.top_score) || 0;
        const tierKey =
          score >= 90 ? "priority" : score >= 75 ? "target" : score >= 50 ? "watch" : "hold";
        const tier = TIER_META[tierKey];
        const mc = m?.color || C.navy;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexWrap: "wrap" }}>
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 800,
                bgcolor: `${mc}18`,
                color: mc,
                borderRadius: "4px",
                px: 0.6,
                py: 0.2,
                whiteSpace: "nowrap",
              }}
            >
              {m?.label || row.top_model} {score}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 9,
                fontWeight: 800,
                textTransform: "uppercase",
                bgcolor: `${tier.color}18`,
                color: tier.color,
                borderRadius: "4px",
                px: 0.5,
                py: 0.2,
                letterSpacing: "0.03em",
                whiteSpace: "nowrap",
              }}
            >
              {tier.label}
            </Box>
          </Box>
        );
      }
      case "next_best_action":
        return row.next_best_action ? (
          <Typography sx={{ fontSize: 10.5, color: C.charcoal, lineHeight: 1.35 }}>
            {row.next_best_action}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 10, color: C.muted }}>—</Typography>
        );
      case "charity": {
        const anchor = Number(row.charity_ask_anchor) || 0;
        if (anchor <= 0)
          return <Typography sx={{ fontSize: 10, color: C.muted }}>—</Typography>;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexWrap: "wrap" }}>
            <Box
              component="span"
              sx={{ fontSize: 10.5, fontWeight: 700, color: C.charcoal, whiteSpace: "nowrap" }}
            >
              ${Math.round(anchor).toLocaleString("en-US")}
            </Box>
            {!!Number(row.is_event_table_prospect) && (
              <Box component="span" title="Buys tables/seats at fundraiser dinners" sx={{ fontSize: 11 }}>
                🍽️
              </Box>
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  const SORTS = [
    ["recent", "Most recent"],
    ["donated", "Top donors"],
    ["charity", "Top charity ask"],
    ["issues", "Most issues"],
    ["activity", "Recent activity"],
    ["name", "Name"],
    ["state", "State"],
  ];
  const inSx = {
    fontSize: 12,
    fontFamily: "inherit",
    p: 0.6,
    border: `1px solid ${C.border}`,
    borderRadius: 1,
    bgcolor: C.white,
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>
            People{total ? ` · ${total.toLocaleString()}` : ""}
          </Typography>
          {geoScope?.label && (
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: C.navy,
                bgcolor: "#eaf3ff",
                px: 1,
                py: 0.4,
                borderRadius: 1,
                whiteSpace: "nowrap",
              }}
            >
              in {geoScope.label}
            </Box>
          )}
          {[
            ["identified", "Verified"],
            ["lead", "Leads"],
            ["all", "All"],
          ].map(([val, lbl]) =>
            chip(typeFilter === val, lbl, () => {
              setTypeFilter(val);
              setOffset(0);
              setSelected(new Set());
            }),
          )}
        </Box>
        <Box sx={{ position: "relative" }}>
          <Box
            component="button"
            onClick={() => setColPicker((o) => !o)}
            sx={{ ...inSx, fontWeight: 700, cursor: "pointer" }}
          >
            ⚙ Columns
          </Box>
          {colPicker && (
            <Box
              sx={{
                position: "absolute",
                right: 0,
                top: "110%",
                zIndex: 20,
                bgcolor: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                p: 1,
                width: 180,
              }}
            >
              {EXPLORER_COLUMNS.map((c) => (
                <Box
                  key={c.key}
                  component="label"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    fontSize: 11,
                    py: 0.3,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={cols.has(c.key)}
                    onChange={() =>
                      setCols((s) => {
                        const n = new Set(s);
                        n.has(c.key) ? n.delete(c.key) : n.add(c.key);
                        return n;
                      })
                    }
                  />
                  {c.label}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Filter rail */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          alignItems: "center",
          mb: 1,
        }}
      >
        <ExplorerSearch onSearch={handleSearch} />
        <Box
          component="select"
          value={fState}
          onChange={(e) => {
            setFState(e.target.value);
            setOffset(0);
          }}
          sx={inSx}
        >
          <option value="">All states</option>
          {US_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Box>
        <Box
          component="select"
          value={fIssue}
          onChange={(e) => {
            setFIssue(e.target.value);
            setOffset(0);
          }}
          sx={inSx}
        >
          <option value="">All issues</option>
          {(facets.issues || []).map((it) => (
            <option key={it.slug} value={it.slug}>
              {it.label} ({it.n})
            </option>
          ))}
        </Box>
        {(facets.parties || []).map((p) =>
          chip(fParty === p.party, `${p.party} (${p.n})`, () => {
            setFParty(fParty === p.party ? "" : p.party);
            setOffset(0);
          }),
        )}
        {chip(donorsOnly, "Donors", () => {
          setDonorsOnly((v) => !v);
          setOffset(0);
        })}
        {chip(votersOnly, "Voters", () => {
          setVotersOnly((v) => !v);
          setOffset(0);
        })}
        {chip(contactOnly, "Has contact", () => {
          setContactOnly((v) => !v);
          setOffset(0);
        })}
        {chip(charityOnly, "Charity giver", () => {
          setCharityOnly((v) => !v);
          setOffset(0);
        })}
        {chip(tableProspectOnly, "🍽️ Table prospect", () => {
          setTableProspectOnly((v) => !v);
          setOffset(0);
        })}
        {chip(sourceFilter === "winred", "WinRed", () => {
          setSourceFilter((v) => (v === "winred" ? "" : "winred"));
          setOffset(0);
        })}
        {chip(sourceFilter === "social", "Social", () => {
          setSourceFilter((v) => (v === "social" ? "" : "social"));
          setOffset(0);
        })}
        {chip(kindFilter === "org", "Orgs", () => {
          setKindFilter((v) => (v === "org" ? "person" : "org"));
          setOffset(0);
        })}
        {/* Campaign-Fit filters — only meaningful when campaign-scoped */}
        {campaignId && (
          <>
            {["priority", "target", "watch"].map((tk) =>
              chip(tierFilter === tk, TIER_META[tk].label, () => {
                setTierFilter((v) => (v === tk ? "" : tk));
                setOffset(0);
              }),
            )}
            <Box
              component="select"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setOffset(0);
              }}
              sx={inSx}
            >
              <option value="">All roles</option>
              {STRATEGIC_ROLE_NAMES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Box>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Box
          component="select"
          value={sort}
          onChange={(e) => {
            setSort(e.target.value);
            setOffset(0);
          }}
          sx={inSx}
        >
          {SORTS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </Box>
      </Box>

      {/* Selection action bar */}
      {selected.size > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            p: 1,
            bgcolor: C.navy + "0a",
            border: `1px solid ${C.navy}33`,
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy }}>
            {selected.size} selected
          </Typography>
          <Box sx={{ position: "relative" }}>
            <Box
              component="button"
              onClick={() => setEnrichMenuOpen((o) => !o)}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                px: 1.25,
                py: 0.5,
                borderRadius: "6px",
                bgcolor: C.navy,
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              ✨ Enrich selected ▾
            </Box>
            {enrichMenuOpen && (
              <Box
                sx={{
                  position: "absolute",
                  top: "110%",
                  left: 0,
                  zIndex: 30,
                  minWidth: 230,
                  bgcolor: C.white,
                  border: `1px solid ${C.border}`,
                  borderRadius: "8px",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.14)",
                  overflow: "hidden",
                }}
              >
                {[
                  { leg: "research", label: "🔎 Research the web (role · career · address · emails)", hint: "live web + LinkedIn — the primary resolver" },
                  { leg: "enrich", label: "📇 Find contact (email · phone · name)", hint: "PDL · Datagma · Apollo waterfall + DISC" },
                  { leg: "find-email", label: "✉️ Find email (web search)", hint: "high-confidence single email" },
                  { leg: "find-social", label: "🔗 Find social handles", hint: "needs an email on the person" },
                  { leg: "deep-dive", platform: "x", label: "📊 X deep-dive (issue signals)", hint: "reads their posts → topics" },
                  { leg: "deep-dive", platform: "facebook", label: "📘 Facebook deep-dive (issue signals)", hint: "mines FB posts (needs a linked FB handle)" },
                  { leg: "deep-dive", platform: "instagram", label: "📸 Instagram deep-dive (issue signals)", hint: "mines IG posts (needs a linked IG handle)" },
                ].map((o) => (
                  <Box
                    key={o.leg + (o.platform || "")}
                    component="button"
                    onClick={() => runBulkEnrich(o.leg, o.platform || "x")}
                    sx={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      px: 1.25,
                      py: 0.75,
                      bgcolor: C.white,
                      border: "none",
                      borderBottom: `1px solid ${C.border}`,
                      cursor: "pointer",
                      "&:hover": { bgcolor: C.navy + "08" },
                    }}
                  >
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.navy }}>
                      {o.label}
                    </Typography>
                    <Typography sx={{ fontSize: 9.5, color: C.muted }}>{o.hint}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {enrichMsg && (
            <Typography sx={{ fontSize: 10.5, color: C.navy, fontWeight: 600 }}>
              {enrichMsg}
            </Typography>
          )}
          <Box
            component="button"
            onClick={() => setSelected(new Set())}
            sx={{
              fontSize: 11,
              px: 1,
              py: 0.5,
              borderRadius: "6px",
              bgcolor: C.white,
              border: `1px solid ${C.border}`,
              cursor: "pointer",
            }}
          >
            Clear
          </Box>
        </Box>
      )}

      {/* Table */}
      <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 1, overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: grid,
            gap: 1,
            px: 1,
            py: 0.75,
            bgcolor: C.navy + "06",
            fontSize: 9,
            fontWeight: 800,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          <Box>
            <input type="checkbox" checked={allSel} onChange={toggleAll} />
          </Box>
          {activeCols.map((c) => (
            <Box key={c.key}>{c.label}</Box>
          ))}
        </Box>
        {loading && (
          <Box sx={{ p: 2 }}>
            <IntelLoader bars={[80, 100, 60, 90]} />
          </Box>
        )}
        {!loading && rows.length === 0 && (
          <Typography sx={{ fontSize: 12, color: C.muted, p: 2 }}>
            No people match these filters.
          </Typography>
        )}
        {!loading &&
          rows.map((row) => (
            <Box
              key={rowKey(row)}
              sx={{
                display: "grid",
                gridTemplateColumns: grid,
                gap: 1,
                px: 1,
                py: 0.75,
                borderTop: `1px solid ${C.border}`,
                alignItems: "center",
                cursor: "pointer",
                "&:hover": { bgcolor: C.navy + "05" },
              }}
              onClick={() => setDrawerRow(row)}
            >
              <Box onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(rowKey(row))}
                  onChange={() => toggleSel(rowKey(row))}
                />
              </Box>
              {activeCols.map((c) => (
                <Box key={c.key} sx={{ minWidth: 0 }}>
                  {renderCell(c.key, row)}
                </Box>
              ))}
            </Box>
          ))}
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          mt: 1,
        }}
      >
        <Typography sx={{ fontSize: 11, color: C.muted }}>
          {total
            ? `${offset + 1}–${Math.min(offset + rows.length, total)} of ${total.toLocaleString()}`
            : ""}
        </Typography>
        <Box
          component="button"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          sx={{ ...inSx, cursor: offset === 0 ? "default" : "pointer", opacity: offset === 0 ? 0.5 : 1 }}
        >
          ‹ Prev
        </Box>
        <Box
          component="button"
          disabled={offset + rows.length >= total}
          onClick={() => setOffset(offset + LIMIT)}
          sx={{
            ...inSx,
            cursor: offset + rows.length >= total ? "default" : "pointer",
            opacity: offset + rows.length >= total ? 0.5 : 1,
          }}
        >
          Next ›
        </Box>
      </Box>

      {/* Person profile — reuses the shared donor ProfileDrawer. Kept ALWAYS MOUNTED (open toggles off the row)
          so MUI can play the slide-out transition; mount-gating it would yank it from the DOM with no exit anim. */}
      <ProfileDrawer
        personId={drawerRow?.id ?? null}
        email={drawerRow?.email || ""}
        donorRow={{
          "First Name": drawerRow?.first_name || "",
          "Last Name": drawerRow?.last_name || "",
          Email: drawerRow?.email || "",
          Phone: drawerRow?.phone || "",
          Zip: drawerRow?.zip_code || "",
          zip_code: drawerRow?.zip_code || "",
          State: drawerRow?.state || "",
          state: drawerRow?.state || "",
          City: drawerRow?.city || "",
          Cohort: drawerRow?.donor_cohort || "",
        }}
        onClose={() => setDrawerRow(null)}
        campaignId={campaignId}
        onDeepDive={(p) =>
          alert(
            `Queued ${p.name || "@" + p.username} for X deep-dive — reads their recent replies & comments to mine new issue signals. (Backend batch job pending.)`,
          )
        }
      />
    </Box>
  );
}

// ─── Deep-link helpers ─────────────────────────────────────────────────────
const TAB_SLUGS = {
  "Daily Brief": "brief",
  "Prediction Markets": "prediction-markets",
  Polling: "polling",
  Finance: "finance",
  Campaign: "campaign",
  Policy: "policy",
  News: "news",
  Social: "social",
  "Message Orchestration": "messaging",
  "Electorate Simulation": "simulations",
  Voters: "donors", // tab key kept as "Voters" internally; surfaced as "Donors"
  People: "people",
  "Ground Game": "ground-game",
  "Scoring Model": "scoring-model",
};
const SLUG_TO_TAB = Object.fromEntries(
  Object.entries(TAB_SLUGS).map(([k, v]) => [v, k]),
);

const BRIEF_SUB_SLUGS = {
  Overview: "",
  "Prediction Markets": "prediction-markets",
  Polling: "polling",
  Finance: "finance",
  Campaign: "campaign",
  Policy: "policy",
  News: "news",
};
const SLUG_TO_BRIEF_SUB = Object.fromEntries(
  Object.entries(BRIEF_SUB_SLUGS)
    .filter(([, v]) => v)
    .map(([k, v]) => [v, k]),
);

// base64url encode/decode — keeps email out of the URL
function donorToId(email) {
  return btoa(email).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
function idToDonor(id) {
  const rem = id.length % 4;
  const padded =
    id.replace(/-/g, "+").replace(/_/g, "/") + (rem ? "=".repeat(4 - rem) : "");
  try {
    return atob(padded);
  } catch {
    return null;
  }
}

function readPath() {
  const parts = window.location.pathname.replace(/^\/|\/$/g, "").split("/");
  const [seg0, seg1, seg2, seg3] = parts;
  const base = {
    briefSubTab: "Overview",
    subView: "donors",
    email: null,
    socialMode: "monitor",
    socialCollectionId: null,
  };
  if (!seg0) return { ...base, tab: "Daily Brief" };
  if (seg0 === "l" && seg1) {
    const deepPath = parts.slice(2).filter(Boolean).join("/");
    if (deepPath) sessionStorage.setItem("pollis_deep_link", `/${deepPath}`);
    const linksBase = process.env.REACT_APP_LINKS_URL || "";
    window.location.replace(`${linksBase}/l/${seg1}`);
    return { ...base, tab: "Daily Brief" };
  }
  if (seg0 === "explorer" || seg0 === "people") return { ...base, tab: "People" };
  // Voters + Leads folded into People — old links redirect there.
  if ((seg0 === "voters" && !seg1) || seg0 === "leads")
    return { ...base, tab: "People" };
  if (seg0 === "donations")
    return { ...base, tab: "Voters", subView: "donations" };
  if (seg0 === "donors" && seg1)
    return {
      ...base,
      tab: "Voters",
      subView: "donors",
      email: idToDonor(seg1),
    };
  if (seg0 === "donors") return { ...base, tab: "Voters", subView: "donors" };
  if (seg0 === "brief" && seg1) {
    const briefSubTab = SLUG_TO_BRIEF_SUB[seg1] || "Overview";
    return { ...base, tab: "Daily Brief", briefSubTab };
  }
  if (seg0 === "social") {
    if (seg1 === "discover")
      return { ...base, tab: "Social", socialMode: "discovery" };
    if (seg1 === "topic-research")
      return { ...base, tab: "Social", socialMode: "topics" };
    if (seg1 === "collect")
      return { ...base, tab: "Social", socialMode: "collect" };
    if (seg1 === "audiences") {
      if (seg2 === "poll" && seg3)
        return {
          ...base,
          tab: "Social",
          socialMode: "audiences",
          socialCollectionId: parseInt(seg3),
        };
      return { ...base, tab: "Social", socialMode: "audiences" };
    }
    return { ...base, tab: "Social", socialMode: "monitor" };
  }
  const tab = SLUG_TO_TAB[seg0];
  return { ...base, tab: tab || "Daily Brief" };
}

function writePath(
  tab,
  { subView, email, briefSubTab, socialMode, socialCollectionId } = {},
) {
  let path;
  if (tab === "Daily Brief" && briefSubTab && briefSubTab !== "Overview") {
    path = `/brief/${BRIEF_SUB_SLUGS[briefSubTab] || ""}`;
  } else if (tab === "Social") {
    if (socialMode === "audiences" && socialCollectionId)
      path = `/social/audiences/poll/${socialCollectionId}`;
    else if (socialMode === "discovery") path = "/social/discover";
    else if (socialMode === "topics") path = "/social/topic-research";
    else if (socialMode === "collect") path = "/social/collect";
    else if (socialMode === "audiences") path = "/social/audiences";
    else path = "/social";
  } else if (tab === "Voters" && subView === "leads") {
    path = "/leads";
  } else if (tab === "Voters" && subView === "donations") {
    path = "/donations";
  } else if (tab === "Voters" && subView === "donors") {
    path = "/donors";
  } else if (tab === "Voters" && email) {
    path = `/donors/${donorToId(email)}`;
  } else {
    path = `/${TAB_SLUGS[tab] || ""}`;
  }
  if (window.location.pathname !== path)
    window.history.pushState(null, "", path);
}

// ─── CampaignSetupDrawer ────────────────────────────────────────────────────
const CAMPAIGNS_URL_BASE = PHP_API_URL ? `${PHP_API_URL}/campaigns` : "";

const CampaignSetupDrawer = ({
  open,
  onClose,
  cfg,
  selectedCampaign,
  isSuperuser,
  onNewCampaign,
  onCfgUpdated,
}) => {
  const { authFetch } = useAuth();
  const [copied, setCopied] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { ok, text }
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  // Edit draft state — local only (Architecture §13: form inputs never at App level)
  const [draftPrimaryDate, setDraftPrimaryDate] = useState("");
  const [draftElectionDate, setDraftElectionDate] = useState("");
  const [draftPartyBaseline, setDraftPartyBaseline] = useState("");
  const [draftFundraisingGoal, setDraftFundraisingGoal] = useState("");
  const [draftOpponents, setDraftOpponents] = useState([]); // [{name, fec_id}]
  const [draftWinredKey, setDraftWinredKey] = useState("");
  const [fecSearch, setFecSearch] = useState({}); // { [idx]: { loading, results, open } }
  const fecDebounce = useRef({});

  const lookupFec = useCallback((name, idx) => {
    if (fecDebounce.current[idx]) clearTimeout(fecDebounce.current[idx]);
    if (!name || name.length < 3) {
      setFecSearch((prev) => ({
        ...prev,
        [idx]: { loading: false, results: [], open: false },
      }));
      return;
    }
    setFecSearch((prev) => ({
      ...prev,
      [idx]: { loading: true, results: [], open: true },
    }));
    fecDebounce.current[idx] = setTimeout(async () => {
      try {
        const key = process.env.REACT_APP_FEC_API_KEY || "DEMO_KEY";
        const res = await fetch(
          `https://api.open.fec.gov/v1/names/candidates/?q=${encodeURIComponent(name)}&api_key=${key}`,
        );
        const data = await res.json();
        setFecSearch((prev) => ({
          ...prev,
          [idx]: {
            loading: false,
            results: (data.results || []).slice(0, 6),
            open: true,
          },
        }));
      } catch {
        setFecSearch((prev) => ({
          ...prev,
          [idx]: { loading: false, results: [], open: false },
        }));
      }
    }, 450);
  }, []);

  const startEdit = () => {
    setDraftPrimaryDate(cfg.primaryDate ? cfg.primaryDate.slice(0, 10) : "");
    setDraftElectionDate(cfg.electionDate ? cfg.electionDate.slice(0, 10) : "");
    setDraftPartyBaseline(cfg.partyBaseline || "");
    setDraftFundraisingGoal(
      cfg.fundraisingGoal != null ? String(cfg.fundraisingGoal) : "",
    );
    setDraftOpponents(
      Array.isArray(cfg.opponents) && cfg.opponents.length
        ? cfg.opponents.map((o) => ({
            name: o.name || "",
            fec_id: o.fec_id || "",
            role: o.role || "",
          }))
        : [],
    );
    setDraftWinredKey(cfg.winredKey || "");
    setSaveMsg(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveMsg(null);
  };

  const saveEdit = async () => {
    if (!selectedCampaign?.id || !CAMPAIGNS_URL_BASE) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await authFetch(`${CAMPAIGNS_URL_BASE}/update`, {
        method: "POST",
        body: JSON.stringify({
          id: selectedCampaign.id,
          primary_date: draftPrimaryDate || null,
          election_date: draftElectionDate || null,
          party_baseline: draftPartyBaseline || null,
          fundraising_goal: draftFundraisingGoal
            ? parseInt(draftFundraisingGoal.replace(/,/g, ""))
            : null,
          opponents: draftOpponents.filter((o) => o.name.trim()),
          winred_key: draftWinredKey || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      setSaveMsg({ ok: true, text: "Saved" });
      setEditing(false);
      onCfgUpdated?.();
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const copy = (val, key) => {
    navigator.clipboard?.writeText(val).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const CopyField = ({ label, value, copyKey, hint }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: C.muted,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "#f0f2f5",
          border: "1px solid #dde1e8",
          borderRadius: 1,
          px: 1.5,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: "monospace",
            color: C.charcoal,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || "—"}
        </Typography>
        {value && (
          <Box
            component="button"
            onClick={() => copy(value, copyKey)}
            sx={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: copied === copyKey ? "#16a34a" : C.muted,
              fontSize: 11,
              fontWeight: 700,
              px: 0.5,
              flexShrink: 0,
              "&:hover": { color: C.navy },
            }}
          >
            {copied === copyKey ? "✓ Copied" : "Copy"}
          </Box>
        )}
      </Box>
      {hint && (
        <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );

  const InfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        py: 0.75,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <Typography sx={{ fontSize: 12, color: C.muted, flexShrink: 0, mr: 2 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: C.charcoal,
          textAlign: "right",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={300}
      PaperProps={{ sx: { width: { xs: "100%", sm: "50vw" }, bgcolor: C.bg } }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: C.navy,
          px: 3,
          py: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ color: C.white, fontWeight: 700, fontSize: 16 }}>
            Campaign Setup
          </Typography>
          <Typography sx={{ color: "#5a7898", fontSize: 11, mt: 0.25 }}>
            {cfg.displayName || cfg.candidateName || "—"}
          </Typography>
        </Box>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.5)",
            fontSize: 20,
            cursor: "pointer",
            lineHeight: 1,
            "&:hover": { color: C.white },
          }}
        >
          ×
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 2.5, overflowY: "auto", flex: 1 }}>
        {/* Campaign details */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
            pb: 0.5,
            borderBottom: `2px solid ${C.navy}`,
            width: "fit-content",
            minWidth: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: C.navy,
              textTransform: "uppercase",
              letterSpacing: "0.09em",
            }}
          >
            Campaign Details
          </Typography>
          {!editing ? (
            <Box
              component="button"
              onClick={startEdit}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                px: 1.25,
                py: 0.35,
                bgcolor: C.white,
                color: C.navy,
                border: `1px solid ${C.navy + "44"}`,
                borderRadius: "5px",
                cursor: "pointer",
                "&:hover": { bgcolor: C.navy + "08" },
              }}
            >
              Edit
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 0.75 }}>
              <Box
                component="button"
                onClick={cancelEdit}
                disabled={saving}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  px: 1.25,
                  py: 0.35,
                  bgcolor: C.white,
                  color: C.muted,
                  border: `1px solid ${C.border}`,
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                onClick={saveEdit}
                disabled={saving}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.35,
                  bgcolor: saving ? C.border : C.navy,
                  color: saving ? C.muted : "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Saving…" : "Save"}
              </Box>
            </Box>
          )}
        </Box>
        {saveMsg && (
          <Box
            sx={{
              mb: 1,
              px: 1.5,
              py: 0.75,
              bgcolor: saveMsg.ok ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${saveMsg.ok ? "#86efac" : "#fca5a5"}`,
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{ fontSize: 11, color: saveMsg.ok ? "#16a34a" : "#dc2626" }}
            >
              {saveMsg.text}
            </Typography>
          </Box>
        )}
        {!editing ? (
          <Box
            sx={{
              bgcolor: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: 1.5,
              px: 2,
              py: 0.5,
              mb: 3,
            }}
          >
            <InfoRow label="Candidate" value={cfg.candidateName} />
            <InfoRow label="Display name" value={cfg.displayName} />
            <InfoRow label="Office" value={cfg.office} />
            <InfoRow label="Party" value={cfg.party} />
            <InfoRow label="State" value={cfg.state} />
            <InfoRow label="Stage" value={cfg.stage} />
            <InfoRow
              label="Election"
              value={cfg.electionDate ? fmtDate(cfg.electionDate) : cfg.stage}
            />
            {cfg.primaryDate && (
              <InfoRow label="Primary" value={fmtDate(cfg.primaryDate)} />
            )}
            {cfg.opponentsLabel && (
              <InfoRow label="Opponents" value={cfg.opponentsLabel} />
            )}
            {cfg.partyBaseline && (
              <InfoRow label="Baseline" value={cfg.partyBaseline} />
            )}
            {cfg.fundraisingGoal && (
              <InfoRow
                label="Goal"
                value={`$${Number(cfg.fundraisingGoal).toLocaleString()}`}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: 1.5,
              px: 2,
              py: 2,
              mb: 3,
            }}
          >
            {/* Read-only fields */}
            <InfoRow label="Candidate" value={cfg.candidateName} />
            <InfoRow label="Display name" value={cfg.displayName} />
            <InfoRow label="Office" value={cfg.office} />
            <InfoRow label="Party" value={cfg.party} />
            <InfoRow label="State" value={cfg.state} />
            <Box sx={{ height: 12 }} />
            {/* Editable fields */}
            {[
              {
                label: "Primary Date",
                val: draftPrimaryDate,
                set: setDraftPrimaryDate,
                type: "date",
              },
              {
                label: "Election Date",
                val: draftElectionDate,
                set: setDraftElectionDate,
                type: "date",
              },
              {
                label: "Party Baseline",
                val: draftPartyBaseline,
                set: setDraftPartyBaseline,
                type: "text",
                placeholder: "e.g. R+12",
              },
              {
                label: "Fundraising Goal",
                val: draftFundraisingGoal,
                set: setDraftFundraisingGoal,
                type: "text",
                placeholder: "e.g. 1500000",
              },
              {
                label: "WinRed Key",
                val: draftWinredKey,
                set: setDraftWinredKey,
                type: "text",
                placeholder: "e.g. mace-winred-data",
              },
            ].map(({ label, val, set, type, placeholder }) => (
              <Box key={label} sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    mb: 0.5,
                  }}
                >
                  {label}
                </Typography>
                <input
                  type={type}
                  value={val}
                  onChange={(e) => set(e.target.value)}
                  placeholder={placeholder || ""}
                  style={{
                    width: "100%",
                    fontSize: 12,
                    padding: "8px 10px",
                    border: `1px solid ${C.border}`,
                    borderRadius: "6px",
                    color: C.charcoal,
                    outline: "none",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    background: "#fff",
                  }}
                />
              </Box>
            ))}
            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: 0.5,
                }}
              >
                Opponents
              </Typography>
              <Typography sx={{ fontSize: 10, color: C.muted, mb: 1 }}>
                Add FEC candidate ID to pull opponent fundraising data
                automatically.
              </Typography>
              {draftOpponents.map((opp, i) => {
                const fs = fecSearch[i] || {};
                const officeLabel = { H: "House", S: "Senate", P: "President" };
                return (
                  <Box key={i} sx={{ mb: 0.75 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <input
                        value={opp.name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftOpponents((prev) =>
                            prev.map((o, j) =>
                              j === i ? { ...o, name: v } : o,
                            ),
                          );
                          lookupFec(v, i);
                        }}
                        onBlur={() =>
                          setTimeout(
                            () =>
                              setFecSearch((prev) => ({
                                ...prev,
                                [i]: { ...prev[i], open: false },
                              })),
                            200,
                          )
                        }
                        placeholder="Full name"
                        style={{
                          flex: 2,
                          fontSize: 12,
                          padding: "7px 10px",
                          border: `1px solid ${C.border}`,
                          borderRadius: "6px",
                          color: C.charcoal,
                          outline: "none",
                          fontFamily: "inherit",
                          background: "#fff",
                        }}
                      />
                      <input
                        value={opp.fec_id}
                        onChange={(e) =>
                          setDraftOpponents((prev) =>
                            prev.map((o, j) =>
                              j === i
                                ? { ...o, fec_id: e.target.value.trim() }
                                : o,
                            ),
                          )
                        }
                        placeholder={
                          fs.loading ? "Searching…" : "FEC ID (e.g. S6IA00150)"
                        }
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "7px 10px",
                          border: `1px solid ${opp.fec_id ? C.navy : C.border}`,
                          borderRadius: "6px",
                          color: C.charcoal,
                          outline: "none",
                          background: "#fff",
                          fontFamily: "monospace",
                        }}
                      />
                      <Box
                        component="button"
                        onClick={() =>
                          setDraftOpponents((prev) =>
                            prev.filter((_, j) => j !== i),
                          )
                        }
                        sx={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: C.muted,
                          fontSize: 16,
                          px: 0.5,
                          "&:hover": { color: C.red },
                        }}
                      >
                        ×
                      </Box>
                    </Box>
                    {fs.open && fs.results && fs.results.length > 0 && (
                      <Box
                        sx={{
                          mt: 0.25,
                          border: `1px solid ${C.border}`,
                          borderRadius: "6px",
                          background: "#fff",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          overflow: "hidden",
                          zIndex: 10,
                          position: "relative",
                        }}
                      >
                        {fs.results.map((r) => {
                          const tc = (s) =>
                            s
                              .toLowerCase()
                              .replace(/\b\w/g, (c) => c.toUpperCase());
                          const parts = r.name.split(",");
                          const displayName =
                            parts.length > 1
                              ? tc(
                                  parts.slice(1).join(" ").trim() +
                                    " " +
                                    parts[0].trim(),
                                )
                              : tc(r.name.trim());
                          return (
                            <Box
                              key={r.id}
                              onMouseDown={() => {
                                setDraftOpponents((prev) =>
                                  prev.map((o, j) =>
                                    j === i
                                      ? {
                                          ...o,
                                          name: displayName,
                                          fec_id: r.id,
                                        }
                                      : o,
                                  ),
                                );
                                setFecSearch((prev) => ({
                                  ...prev,
                                  [i]: {
                                    open: false,
                                    results: [],
                                    loading: false,
                                  },
                                }));
                              }}
                              sx={{
                                px: 1.5,
                                py: 0.75,
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                "&:hover": { background: C.navyLight },
                                borderBottom: `1px solid ${C.border}`,
                                "&:last-child": { borderBottom: "none" },
                              }}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: C.charcoal,
                                  }}
                                >
                                  {displayName}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 10, color: C.muted }}
                                >
                                  {officeLabel[r.office_sought] ||
                                    r.office_sought}
                                </Typography>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  fontFamily: "monospace",
                                  color: C.navy,
                                  fontWeight: 700,
                                }}
                              >
                                {r.id}
                              </Typography>
                            </Box>
                          );
                        })}
                        {!fs.loading && fs.results.length === 0 && (
                          <Typography
                            sx={{
                              px: 1.5,
                              py: 1,
                              fontSize: 10,
                              color: C.muted,
                            }}
                          >
                            No federal candidates found · FEC covers House,
                            Senate &amp; President only
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
              <Box
                component="button"
                onClick={() =>
                  setDraftOpponents((prev) => [
                    ...prev,
                    { name: "", fec_id: "" },
                  ])
                }
                sx={{
                  mt: 0.5,
                  fontSize: 11,
                  color: C.navy,
                  background: "none",
                  border: `1px dashed ${C.border}`,
                  borderRadius: "6px",
                  cursor: "pointer",
                  px: 2,
                  py: 0.75,
                  width: "100%",
                  "&:hover": { borderColor: C.navy },
                }}
              >
                + Add opponent
              </Box>
            </Box>
          </Box>
        )}

        {/* Infrastructure */}
        <SectionHeader title="Infrastructure" />
        <Box sx={{ mb: 3 }}>
          <CopyField
            label="Campaign ID"
            value={String(selectedCampaign?.id || "")}
            copyKey="campaign_id"
            hint="Reference ID used across all Pollis services"
          />
          <CopyField
            label="S3 Data Bucket"
            value={
              cfg.s3Bucket || `pollis-campaign-${selectedCampaign?.id || ""}`
            }
            copyKey="s3_bucket"
            hint="Campaign intelligence and data files are stored here"
          />
          {cfg.winredKey && (
            <CopyField
              label="WinRed Integration Key"
              value={cfg.winredKey}
              copyKey="winred_key"
              hint="Paste this into your WinRed account under Integrations → Data Export"
            />
          )}
          {selectedCampaign?.slug && (
            <CopyField
              label="Campaign Slug"
              value={selectedCampaign.slug}
              copyKey="slug"
            />
          )}
          {/* Force data sync */}
          {selectedCampaign?.id &&
            (() => {
              const dataSyncUrl = PHP_API_URL ? `${PHP_API_URL}/data-sync` : "";
              if (!dataSyncUrl) return null;
              const triggerSync = async () => {
                setSyncing(true);
                setSyncMsg(null);
                try {
                  const res = await authFetch(
                    `${dataSyncUrl}/trigger?campaign_id=${selectedCampaign.id}`,
                    {
                      method: "POST",
                    },
                  );
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.error || "Sync failed");
                  setSyncMsg({
                    ok: true,
                    text: "Sync started — FEC data will update in ~2 minutes",
                  });
                  onCfgUpdated?.();
                } catch (e) {
                  setSyncMsg({ ok: false, text: e.message });
                }
                setSyncing(false);
              };
              return (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      mb: 0.75,
                    }}
                  >
                    Data Sync
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      component="button"
                      onClick={triggerSync}
                      disabled={syncing}
                      sx={{
                        px: 2,
                        py: 0.75,
                        fontSize: 11,
                        fontWeight: 700,
                        border: `1px solid ${C.navy}`,
                        borderRadius: 1,
                        bgcolor: syncing ? C.border : C.navy,
                        color: syncing ? C.muted : "#fff",
                        cursor: syncing ? "not-allowed" : "pointer",
                      }}
                    >
                      {syncing ? "Syncing…" : "Force Sync"}
                    </Box>
                    {syncMsg && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: syncMsg.ok ? "#16a34a" : "#c0392b",
                        }}
                      >
                        {syncMsg.text}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.5 }}>
                    Rebuilds FEC, polling, and field data from source APIs. Run
                    after adding opponent FEC IDs.
                  </Typography>
                </Box>
              );
            })()}
        </Box>

        {/* WinRed setup guide */}
        <SectionHeader title="WinRed Setup" />
        <Box
          sx={{
            bgcolor: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 1.5,
            px: 2,
            py: 1.5,
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: 12, color: C.charcoal, lineHeight: 1.7 }}>
            To connect WinRed donation data to this campaign:
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, mt: 1, mb: 0 }}>
            {[
              "Log into your WinRed account",
              "Go to Settings → Integrations → Data Export",
              `Set the S3 bucket name to the value above`,
              "Export frequency: Daily (recommended)",
              "Contact your Pollis rep to complete IAM access setup",
            ].map((step, i) => (
              <Box
                component="li"
                key={i}
                sx={{
                  fontSize: 12,
                  color: "#4a5568",
                  mb: 0.75,
                  lineHeight: 1.5,
                }}
              >
                {step}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Connected Social Accounts */}
        <SectionHeader title="Connected Accounts" />
        <Box sx={{ mb: 3 }}>
          <ConnectedAccountsPanel activeCampaignId={selectedCampaign?.id} />
        </Box>
      </Box>

      {/* Footer */}
      {isSuperuser && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${C.border}`,
            bgcolor: C.white,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              onClose();
              onNewCampaign?.();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              borderRadius: 1.5,
              color: C.navy,
              borderColor: C.navy,
              py: 1,
              "&:hover": { bgcolor: C.navy, color: C.white },
            }}
          >
            + Provision new campaign
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

// ─── UserDrawer ────────────────────────────────────────────────────────────
const PHP_AUTH_URL = process.env.REACT_APP_PHP_API_URL || "";
const ALERTS_URL_BASE = process.env.REACT_APP_ALERTS_URL || "";

const UserDrawer = ({ open, user, onClose, onLogout, onUserUpdate }) => {
  const { authFetch } = useAuth();
  const [localUser, setLocalUser] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [smsAlerts, setSmsAlerts] = useState(false);
  const [alertRunning, setAlertRunning] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null); // { ok, text } | null
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { ok, text }
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  // Fetch fresh user data (incl. sms_alerts) on open
  useEffect(() => {
    if (!open || !PHP_AUTH_URL) return;
    authFetch(`${PHP_AUTH_URL}/auth/me`)
      .then((r) => r.json())
      .then((d) => {
        const u = d; // PHP /auth/me returns flat user object
        setLocalUser(u);
        setFirstName(u.first_name || "");
        setLastName(u.last_name || "");
        setPhone(u.phone || "");
        setSmsAlerts(!!u.sms_alerts);
        setDirty(false);
        setSaveMsg(null);
        setPwOpen(false);
        setCurPw("");
        setNewPw("");
        setPwMsg(null);
      })
      .catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (body) =>
    authFetch(`${PHP_AUTH_URL}/auth/me`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await patch({
        first_name: firstName,
        last_name: lastName,
        phone,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      setLocalUser(data);
      setDirty(false);
      setSaveMsg({ ok: true, text: "Saved" });
      onUserUpdate(data);
      setTimeout(() => setSaveMsg(null), 2500);
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSmsToggle = async (checked) => {
    setSmsAlerts(checked);
    try {
      const res = await patch({ sms_alerts: checked });
      if (!res.ok) setSmsAlerts(!checked);
    } catch {
      setSmsAlerts(!checked);
    }
  };

  const handlePasswordSave = async () => {
    if (!newPw || newPw.length < 8) {
      setPwMsg({ ok: false, text: "Minimum 8 characters" });
      return;
    }
    setPwSaving(true);
    setPwMsg(null);
    try {
      const res = await authFetch(`${PHP_AUTH_URL}/auth/password`, {
        method: "POST",
        body: JSON.stringify({ current_password: curPw, new_password: newPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setPwMsg({ ok: true, text: "Password updated" });
      setCurPw("");
      setNewPw("");
      setTimeout(() => {
        setPwMsg(null);
        setPwOpen(false);
      }, 2000);
    } catch (e) {
      setPwMsg({ ok: false, text: e.message });
    } finally {
      setPwSaving(false);
    }
  };

  const display = localUser || user || {};
  const initials =
    (
      (display.first_name?.[0] || "") + (display.last_name?.[0] || "")
    ).toUpperCase() || "?";
  const fieldSx = {
    "& .MuiInputBase-input": { fontSize: 13 },
    "& .MuiInputLabel-root": { fontSize: 13 },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={300}
      PaperProps={{
        sx: {
          width: { xs: "100vw", md: 420 },
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f9fafb",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: C.white,
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              width: 54,
              height: 54,
              bgcolor: "#1e3a5f",
              fontSize: 18,
              fontWeight: 800,
              color: C.white,
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 800,
                color: C.charcoal,
                lineHeight: 1.3,
              }}
            >
              {display.first_name} {display.last_name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.25 }}>
              {display.email}
            </Typography>
          </Box>
        </Box>
        <Box
          onClick={onClose}
          sx={{
            fontSize: 20,
            color: C.muted,
            cursor: "pointer",
            lineHeight: 1,
            "&:hover": { color: C.charcoal },
          }}
        >
          ✕
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
        {/* Profile */}
        <SectionHeader title="Profile" />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <TextField
            label="First name"
            size="small"
            value={firstName}
            sx={fieldSx}
            onChange={(e) => {
              setFirstName(e.target.value);
              setDirty(true);
              setSaveMsg(null);
            }}
          />
          <TextField
            label="Last name"
            size="small"
            value={lastName}
            sx={fieldSx}
            onChange={(e) => {
              setLastName(e.target.value);
              setDirty(true);
              setSaveMsg(null);
            }}
          />
          <TextField
            label="Email"
            size="small"
            value={display.email || ""}
            disabled
            sx={{ ...fieldSx, gridColumn: "1 / -1" }}
          />
          <TextField
            label="Phone"
            size="small"
            value={phone}
            sx={{ ...fieldSx, gridColumn: "1 / -1" }}
            onChange={(e) => {
              setPhone(e.target.value);
              setDirty(true);
              setSaveMsg(null);
            }}
          />
        </Box>
        {(dirty || saveMsg) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            {dirty && (
              <Button
                size="small"
                variant="contained"
                disabled={saving}
                onClick={handleSave}
                sx={{
                  bgcolor: C.navy,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#163260" },
                }}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            )}
            {saveMsg && (
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: saveMsg.ok ? "#16a34a" : C.red,
                }}
              >
                {saveMsg.text}
              </Typography>
            )}
          </Box>
        )}
        {!dirty && !saveMsg && <Box sx={{ mb: 3 }} />}

        {/* Notifications */}
        <SectionHeader title="Notifications" />
        <Box
          sx={{
            bgcolor: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 1.5,
            px: 2,
            py: 1.75,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ pr: 2 }}>
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}
              >
                Daily SMS briefing
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.25 }}>
                Fundraising stat or poll standing at 7am — requires a verified
                phone number
              </Typography>
            </Box>
            <Switch
              checked={smsAlerts}
              onChange={(e) => handleSmsToggle(e.target.checked)}
              size="small"
              sx={{
                "& .MuiSwitch-thumb": { bgcolor: smsAlerts ? C.navy : "#ccc" },
                "& .Mui-checked + .MuiSwitch-track": { bgcolor: C.navy },
              }}
            />
          </Box>
          {ALERTS_URL_BASE && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: `1px solid ${C.border}`,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                disabled={alertRunning}
                onClick={async () => {
                  setAlertRunning(true);
                  setAlertMsg(null);
                  try {
                    const res = await authFetch(`${ALERTS_URL_BASE}/run`, {
                      method: "POST",
                    });
                    const data = await res.json();
                    if (res.ok) {
                      setAlertMsg({
                        ok: true,
                        text: `Checked ${data.checked} campaign${data.checked !== 1 ? "s" : ""} · ${data.alertsSent} alert${data.alertsSent !== 1 ? "s" : ""} sent`,
                      });
                    } else {
                      setAlertMsg({
                        ok: false,
                        text: data.error || "Check failed",
                      });
                    }
                  } catch (e) {
                    setAlertMsg({ ok: false, text: e.message });
                  } finally {
                    setAlertRunning(false);
                  }
                }}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "none",
                  borderColor: C.border,
                  color: C.muted,
                  "&:hover": { borderColor: C.navy, color: C.navy },
                }}
              >
                {alertRunning ? "Checking…" : "Check alerts now"}
              </Button>
              {alertMsg && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: alertMsg.ok ? "#16a34a" : C.red,
                  }}
                >
                  {alertMsg.text}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Security */}
        <SectionHeader title="Security" />
        <Box
          sx={{
            bgcolor: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 1.5,
            overflow: "hidden",
            mb: 3,
          }}
        >
          <Box
            onClick={() => {
              setPwOpen((v) => !v);
              setPwMsg(null);
            }}
            sx={{
              px: 2,
              py: 1.75,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": { bgcolor: "#f8f9fb" },
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}
            >
              Change password
            </Typography>
            <Typography sx={{ fontSize: 16, color: C.muted, lineHeight: 1 }}>
              {pwOpen ? "−" : "+"}
            </Typography>
          </Box>
          {pwOpen && (
            <Box
              sx={{
                px: 2,
                pb: 2,
                pt: 0.5,
                borderTop: `1px solid ${C.border}`,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
              }}
            >
              <TextField
                label="Current password"
                type="password"
                size="small"
                value={curPw}
                sx={fieldSx}
                onChange={(e) => {
                  setCurPw(e.target.value);
                  setPwMsg(null);
                }}
              />
              <TextField
                label="New password"
                type="password"
                size="small"
                value={newPw}
                sx={fieldSx}
                onChange={(e) => {
                  setNewPw(e.target.value);
                  setPwMsg(null);
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={pwSaving}
                  onClick={handlePasswordSave}
                  sx={{
                    bgcolor: C.navy,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#163260" },
                  }}
                >
                  {pwSaving ? "Updating…" : "Update password"}
                </Button>
                {pwMsg && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: pwMsg.ok ? "#16a34a" : C.red,
                    }}
                  >
                    {pwMsg.text}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer — sign out */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${C.border}`,
          bgcolor: C.white,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={onLogout}
          sx={{
            color: C.red,
            borderColor: "#fca5a5",
            fontWeight: 700,
            fontSize: 13,
            textTransform: "none",
            borderRadius: 1.5,
            py: 1,
            "&:hover": { bgcolor: "#fff5f5", borderColor: C.red },
          }}
        >
          Sign out
        </Button>
      </Box>
    </Drawer>
  );
};

// ─── Scoring shared meta (used by the drawer Fit box, the People table, and the model page) ──
const SCORE_MODELS = [
  { key: "fundraising", label: "Fundraising", emoji: "💰", color: "#1a7a4a" },
  { key: "persuasion", label: "Persuasion", emoji: "☑️", color: "#1f6da8" },
  { key: "mobilization", label: "Mobilization", emoji: "✊", color: "#FF0A4B" },
  { key: "amplification", label: "Amplification", emoji: "📣", color: "#6c3483" },
  { key: "coalition", label: "Coalition", emoji: "🤝", color: "#d35400" },
];
const TIER_META = {
  priority: { label: "Priority", color: "#dc2626" },
  target: { label: "Target", color: "#ea580c" },
  watch: { label: "Watch", color: "#d97706" },
  hold: { label: "Hold", color: "#9ca3af" },
};
// Strategic Role names for the People-table role filter (mirrors identity.php scoringModelConfig roles).
const STRATEGIC_ROLE_NAMES = [
  "High-Dollar Donor",
  "Small-Dollar Donor",
  "Influencer",
  "Media Amplifier",
  "Digital Amplifier",
  "Surrogate",
  "Host Fundraiser",
  "Recruiter",
  "Volunteer",
  "Persuadable Voter",
  "Reliable Supporter",
  "Policy Validator",
  "Community Leader",
];

// ─── Campaign Fit box — the person-drawer scorecard (renders personDetail.fit) ───────────────
// Roles line · 5 model percentile bars + tier · strategic-importance stars · confidence · Next Best Action.
function CampaignFitBox({ fit }) {
  const { authFetch } = useAuth();
  const scores = fit.scores_json || {};
  const roles = fit.roles_json || [];
  const importance = fit.strategic_importance || 0;
  const confidence = fit.confidence != null ? Math.round(fit.confidence * 100) : null;
  const [narrative, setNarrative] = useState(fit.narrative || "");
  const [narrating, setNarrating] = useState(false);

  const explain = () => {
    if (!PHP_API_URL || narrating || !fit.person_id || !fit.campaign_id) return;
    setNarrating(true);
    authFetch(
      `${PHP_API_URL}/identity/fit-narrative?person_id=${fit.person_id}&campaign_id=${fit.campaign_id}`,
    )
      .then((r) => r.json())
      .then((d) => setNarrative(d?.narrative || "No explanation available."))
      .catch(() => setNarrative("Couldn’t generate an explanation."))
      .finally(() => setNarrating(false));
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 0.5,
        borderRadius: "8px",
        border: `1px solid ${C.border}`,
        overflow: "hidden",
      }}
    >
      {/* Header: label · importance stars · confidence */}
      <Box
        sx={{
          px: 1.75,
          py: 1.1,
          bgcolor: C.rowAlt,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: C.muted,
            }}
          >
            Campaign Fit
          </Typography>
          {importance > 0 && (
            <Typography sx={{ fontSize: 12, color: "#f59e0b", letterSpacing: "1px" }}>
              {"★".repeat(importance)}
              <span style={{ color: C.border }}>{"★".repeat(5 - importance)}</span>
            </Typography>
          )}
        </Box>
        {confidence != null && (
          <Typography sx={{ fontSize: 10.5, color: C.muted, fontWeight: 600 }}>
            {confidence}% confidence
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.75, py: 1.5 }}>
        {/* Roles */}
        {roles.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.5 }}>
            {roles.map((r, i) => (
              <Box
                key={i}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: `${C.navy}0d`,
                  border: `1px solid ${C.navy}22`,
                  color: C.navy,
                  borderRadius: "999px",
                  px: 1,
                  py: 0.3,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.4,
                }}
              >
                <span>{r.emoji}</span>
                {r.role}
              </Box>
            ))}
          </Box>
        )}

        {/* Model percentile bars */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
          {SCORE_MODELS.map((m) => {
            const s = scores[m.key];
            if (!s) return null;
            const val = s.adj != null ? s.adj : s.score || 0;
            const tier = TIER_META[s.tier] || TIER_META.hold;
            return (
              <Box key={m.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{ fontSize: 11.5, fontWeight: 700, color: C.charcoal, width: 96, flexShrink: 0 }}
                >
                  <span style={{ marginRight: 4 }}>{m.emoji}</span>
                  {m.label}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: 7,
                    bgcolor: C.rowAlt,
                    borderRadius: 4,
                    overflow: "hidden",
                    minWidth: 60,
                  }}
                >
                  <Box
                    sx={{ width: `${val}%`, height: "100%", bgcolor: m.color, opacity: 0.85 }}
                  />
                </Box>
                <Typography
                  sx={{ fontSize: 12, fontWeight: 800, color: m.color, width: 26, textAlign: "right" }}
                >
                  {val}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    color: tier.color,
                    width: 46,
                    textAlign: "right",
                    letterSpacing: "0.03em",
                  }}
                >
                  {tier.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Next Best Action */}
        {fit.next_best_action && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.1,
              borderRadius: "6px",
              bgcolor: `${C.green}10`,
              border: `1px solid ${C.green}33`,
              display: "flex",
              gap: 0.75,
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{ fontSize: 9.5, fontWeight: 800, color: C.green, textTransform: "uppercase", flexShrink: 0, mt: 0.2 }}
            >
              Next →
            </Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.charcoal }}>
              {fit.next_best_action}
            </Typography>
          </Box>
        )}

        {/* AI narration — explains the scores in prose, on demand (opt-in to avoid cost per open) */}
        <Box sx={{ mt: 1.25 }}>
          {narrative ? (
            <Box
              sx={{
                fontSize: 12.5,
                color: C.charcoal,
                lineHeight: 1.55,
                "& p": { m: 0, mb: 0.5 },
              }}
            >
              <ReactMarkdown>{narrative}</ReactMarkdown>
            </Box>
          ) : (
            <Box
              component="button"
              onClick={explain}
              disabled={narrating}
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                color: C.navy,
                bgcolor: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: "6px",
                px: 1,
                py: 0.5,
                cursor: narrating ? "default" : "pointer",
                "&:hover": { bgcolor: `${C.navy}08` },
              }}
            >
              {narrating ? "Explaining…" : "✨ Explain this fit"}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Scoring Model — methodology page ───────────────────────────────────────
// Renders the LIVE scoring config served by GET /identity/scoring-model (the same config
// the engine scores from), so this documentation can never drift from the real model.
// MarketPilot-style: models → dimensions → weights → metric index → directions → tiers → roles.
function ScoringModelView({ campaignId }) {
  const { authFetch } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rescoring, setRescoring] = useState(false);
  const [rescoreMsg, setRescoreMsg] = useState("");
  const [status, setStatus] = useState(null); // { run, coverage }
  const pnRef = useRef(null);
  const guardRef = useRef(null);
  const pubnubSubKey = process.env.REACT_APP_PUBNUB_SUB_KEY;

  const relTime = (ts) => {
    if (!ts) return "";
    const then = new Date(String(ts).replace(" ", "T") + "Z").getTime();
    if (isNaN(then)) return "";
    const s = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  };

  const fetchStatus = useCallback(() => {
    if (!PHP_API_URL) return Promise.resolve(null);
    return authFetch(
      `${PHP_API_URL}/identity/scoring-status${campaignId ? `?campaign_id=${campaignId}` : ""}`,
    )
      .then((r) => r.json())
      .then((d) => {
        setStatus(d);
        return d;
      })
      .catch(() => null);
  }, [authFetch, campaignId]);

  const runRescore = () => {
    if (!PHP_API_URL || rescoring) return;
    setRescoring(true);
    setRescoreMsg("");
    authFetch(`${PHP_API_URL}/identity/score-all`, { method: "POST" })
      .then((r) => r.json().catch(() => ({})))
      .then(() => {
        setRescoreMsg("");
        // No polling — the persistent `scoring-all` PubNub subscription (below) flips the banner to
        // completed when the run publishes `done`. Refresh once so it shows "in progress" immediately.
        fetchStatus();
        // Safety net: if PubNub never delivers (worker died), refresh once after 12 min so the banner
        // reflects the stalled state instead of hanging on "in progress".
        if (guardRef.current) clearTimeout(guardRef.current);
        guardRef.current = setTimeout(() => fetchStatus(), 12 * 60 * 1000);
      })
      .catch((e) => setRescoreMsg(`Couldn’t start re-scoring: ${e?.message || e}`))
      .finally(() => setRescoring(false));
  };

  // One status fetch on mount + a persistent PubNub subscription on `scoring-all` — the batch pushes
  // `done`/`geo_done`/`error` (202 + PubNub, our standard pattern), so we NEVER poll. Any run finishing
  // (this tab's Re-score OR the 6am cron) refreshes the banner if the tab is open.
  useEffect(() => {
    fetchStatus();
    if (!pubnubSubKey) return;
    const pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
    pnRef.current = pn;
    pn.addListener({
      message: ({ channel: ch, message: m }) => {
        if (ch !== "scoring-all") return;
        if (m.type === "done" || m.type === "geo_done" || m.type === "error") fetchStatus();
      },
    });
    pn.subscribe({ channels: ["scoring-all"] });
    return () => {
      try {
        pn.removeAllListeners();
        pn.unsubscribeAll();
      } catch (e) {
        /* ignore */
      }
      pnRef.current = null;
      if (guardRef.current) clearTimeout(guardRef.current);
    };
  }, [fetchStatus, pubnubSubKey]);

  useEffect(() => {
    if (!PHP_API_URL) {
      setError("REACT_APP_PHP_API_URL not configured");
      setLoading(false);
      return;
    }
    let alive = true;
    authFetch(`${PHP_API_URL}/identity/scoring-model`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setCfg(d);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(String(e?.message || e));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [authFetch]);

  const dirLabel = (dir) =>
    dir === "down"
      ? "↓ inverted (sooner / fewer is better)"
      : dir === "mid"
        ? "◆ peaks mid-range (persuadable)"
        : "↑ higher is better";
  const dirColor = (dir) =>
    dir === "down" ? C.orange : dir === "mid" ? C.purple : C.green;

  const modelColor = (key) =>
    ({
      fundraising: C.green,
      persuasion: C.blue,
      mobilization: C.red,
      amplification: C.purple,
      coalition: C.orange,
    })[key] || C.navy;

  if (loading)
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton height={40} width="40%" />
        <Skeleton height={20} width="80%" sx={{ mt: 2 }} />
        <Skeleton height={220} sx={{ mt: 2 }} />
      </Box>
    );
  if (error || !cfg)
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ color: C.red, fontWeight: 700 }}>
          Couldn’t load the scoring model
        </Typography>
        <Typography sx={{ color: C.muted, fontSize: 13, mt: 0.5 }}>
          {error || "No config returned."}
        </Typography>
      </Box>
    );

  const models = cfg.models || {};
  const tiers = cfg.tiers || [];
  const metricIndex = cfg.metric_index || [];
  const roles = cfg.roles || [];

  const SectionTitle = ({ children, sub }) => (
    <Box sx={{ mb: 2, mt: 4 }}>
      <Typography
        sx={{ fontSize: 18, fontWeight: 800, color: C.navy, letterSpacing: "-0.01em" }}
      >
        {children}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.5, maxWidth: 820 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{ fontSize: 26, fontWeight: 900, color: C.navy, letterSpacing: "-0.02em" }}
        >
          Scoring Model
        </Typography>
        <Button
          onClick={runRescore}
          disabled={rescoring}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: 13,
            borderColor: C.navy,
            color: C.navy,
            "&:hover": { borderColor: C.navy, bgcolor: `${C.navy}0a` },
          }}
        >
          {rescoring ? "Starting…" : "↻ Re-score now"}
        </Button>
      </Box>
      <Typography sx={{ fontSize: 14, color: C.charcoal, mt: 1, maxWidth: 860 }}>
        Every person is scored against <b>this campaign’s universe</b> on five models.
        Each score is a <b>universe-relative percentile</b> — a “Giving 81” means the 81st
        percentile among this campaign’s people, not a made-up probability. Nothing here is
        a black box: every model below lists its dimensions, weights, inputs, and direction.
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 1 }}>
        Scoring re-runs automatically every morning after the daily data sync. Use{" "}
        <b>Re-score now</b> to force a refresh in between.
      </Typography>
      {rescoreMsg && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.25,
            borderRadius: 2,
            bgcolor: `${C.red}10`,
            border: `1px solid ${C.red}33`,
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: C.red, fontWeight: 600 }}>
            {rescoreMsg}
          </Typography>
        </Box>
      )}

      {/* Scoring status — in progress vs. last completed + coverage for this campaign */}
      {(() => {
        const run = status?.run;
        const cov = status?.coverage;
        const inProgress = run?.status === "started";
        const stalled = run?.status === "stalled";
        const failed = run?.status === "error";
        const tone = inProgress
          ? C.blue
          : failed || stalled
            ? C.red
            : C.green;
        return (
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 2,
              bgcolor: `${tone}10`,
              border: `1px solid ${tone}33`,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: tone,
                flexShrink: 0,
                ...(inProgress && {
                  animation: "pulse 1.2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%,100%": { opacity: 1 },
                    "50%": { opacity: 0.3 },
                  },
                }),
              }}
            />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tone }}>
              {inProgress
                ? `Scoring in progress — started ${relTime(run.started_at)}`
                : stalled
                  ? "Scoring stalled — the last run timed out before finishing. Click Re-score now to retry."
                  : failed
                    ? `Last scoring run failed ${relTime(run.finished_at)}`
                    : run && run.status === "done"
                      ? `Last full scoring completed ${relTime(run.finished_at)} · ${run.scored?.toLocaleString?.() || run.scored} people scored across ${run.campaigns} campaign${run.campaigns === 1 ? "" : "s"}`
                      : "Not scored yet — click Re-score now to run the first pass."}
            </Typography>
            {cov && cov.scored > 0 && (
              <Typography sx={{ fontSize: 12, color: C.muted }}>
                · {cov.scored.toLocaleString()} scored in this campaign
                {cov.last_computed ? ` (updated ${relTime(cov.last_computed)})` : ""}
              </Typography>
            )}
          </Box>
        );
      })()}

      {/* Methodology cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: 2,
          mt: 3,
        }}
      >
        {[
          ["Universe", cfg.universe],
          ["Normalization", cfg.normalization],
          ["Confidence multiplier", cfg.confidence_formula],
        ].map(([label, body]) => (
          <Card key={label} elevation={0} sx={{ border: `1px solid ${C.border}` }}>
            <CardContent>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: C.muted,
                }}
              >
                {label}
              </Typography>
              <Typography sx={{ fontSize: 13, color: C.charcoal, mt: 1, lineHeight: 1.5 }}>
                {body}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Tiers legend */}
      <SectionTitle sub="A person's tier is the percentile band of their confidence-adjusted model score.">
        Tiers
      </SectionTitle>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {tiers.map((t) => (
          <Box
            key={t.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              px: 1.5,
              py: 1,
              bgcolor: C.white,
            }}
          >
            <span style={{ fontSize: 16 }}>{t.emoji}</span>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.navy }}>
                {t.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.muted }}>{t.desc}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Models */}
      <SectionTitle sub="Five theses, one universe. Each model is a weighted composite of its dimensions; each dimension is the average of its present inputs (missing inputs never score zero — the weights renormalize).">
        The five models
      </SectionTitle>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {Object.entries(models).map(([key, m]) => {
          const mc = modelColor(key);
          return (
            <Card
              key={key}
              elevation={0}
              sx={{ border: `1px solid ${C.border}`, borderLeft: `4px solid ${mc}` }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <span style={{ fontSize: 22 }}>{m.emoji}</span>
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: C.navy }}>
                      {m.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: C.muted }}>
                      {m.thesis}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {(m.dimensions || []).map((d) => (
                    <Box key={d.key}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 13.5, fontWeight: 700, color: C.charcoal, minWidth: 120 }}
                        >
                          {d.label}
                        </Typography>
                        {/* weight bar */}
                        <Box
                          sx={{
                            flex: 1,
                            height: 8,
                            bgcolor: C.rowAlt,
                            borderRadius: 4,
                            overflow: "hidden",
                            maxWidth: 320,
                          }}
                        >
                          <Box
                            sx={{
                              width: `${d.weight}%`,
                              height: "100%",
                              bgcolor: mc,
                              opacity: 0.85,
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 800, color: mc, width: 42, textAlign: "right" }}
                        >
                          {d.weight}%
                        </Typography>
                      </Box>
                      {/* metrics + direction */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5, ml: 0 }}>
                        {(d.metrics || []).map((mm) => (
                          <Box
                            key={mm.k}
                            sx={{
                              fontSize: 11,
                              px: 0.9,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: `${dirColor(mm.dir)}14`,
                              color: dirColor(mm.dir),
                              border: `1px solid ${dirColor(mm.dir)}33`,
                              fontWeight: 600,
                            }}
                          >
                            {mm.k}{" "}
                            {mm.dir === "down" ? "↓" : mm.dir === "mid" ? "◆" : "↑"}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
      <Typography sx={{ fontSize: 12, color: C.muted, mt: 1.5 }}>
        <b>Strategic Importance ★</b> blends the top model scores plus a rarity bonus (few
        people clear multiple models). ↑ higher-is-better · ↓ inverted · ◆ peaks mid-range
        (persuadability).
      </Typography>

      {/* Metric index */}
      <SectionTitle sub="Every input the engine can use, where it comes from, and which model dimension it feeds.">
        Metric index
      </SectionTitle>
      <Card elevation={0} sx={{ border: `1px solid ${C.border}` }}>
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <Box component="thead">
              <Box component="tr" sx={{ bgcolor: C.rowAlt }}>
                {["Metric", "Source", "Unit", "Direction", "Feeds"].map((h) => (
                  <Box
                    key={h}
                    component="th"
                    sx={{
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: C.muted,
                      p: 1.25,
                      borderBottom: `1px solid ${C.border}`,
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {metricIndex.map((row, i) => (
                <Box
                  component="tr"
                  key={i}
                  sx={{ "&:nth-of-type(even)": { bgcolor: C.rowAlt } }}
                >
                  <Box component="td" sx={{ p: 1.25, fontSize: 12.5, fontWeight: 700, color: C.navy, fontFamily: "monospace" }}>
                    {row.metric}
                  </Box>
                  <Box component="td" sx={{ p: 1.25, fontSize: 12.5, color: C.charcoal }}>
                    {row.source}
                  </Box>
                  <Box component="td" sx={{ p: 1.25, fontSize: 12.5, color: C.muted }}>
                    {row.unit}
                  </Box>
                  <Box
                    component="td"
                    sx={{ p: 1.25, fontSize: 12, color: dirColor(row.dir.startsWith("↓") || row.dir.includes("down") ? "down" : row.dir.includes("mid") ? "mid" : "up"), fontWeight: 700 }}
                  >
                    {row.dir}
                  </Box>
                  <Box component="td" sx={{ p: 1.25, fontSize: 12.5, color: C.charcoal }}>
                    {row.feeds}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Strategic Roles */}
      <SectionTitle sub="Roles are threshold rules over the model/dimension percentiles — and each role is a workflow. A person can hold several. The highest-value role becomes their Next Best Action.">
        Strategic Roles
      </SectionTitle>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        {roles.map((r, i) => (
          <Card key={i} elevation={0} sx={{ border: `1px solid ${C.border}` }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                <span style={{ fontSize: 20, lineHeight: 1 }}>{r.emoji}</span>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: C.navy }}>
                    {r.role}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.25 }}>
                    <b>Rule:</b> {r.rule}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: C.green, mt: 0.25, fontWeight: 600 }}>
                    → {r.action}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 4, fontStyle: "italic" }}>
        Config version {cfg.version}. Weights, tier cutoffs, and role thresholds are
        configuration — they can be tuned without a code change. The AI narrates these
        scores in the person drawer; it never computes them.
      </Typography>
    </Box>
  );
}

// ─── Terrain Map — geographic projection of the person scores/issues/donors/roles ───────────────
// Choropleth (filled counties) + heatmap (centroid bubbles), colored by a selected metric. Data from
// GET /identity/geo (the precomputed geo_rollup); geometry from us-atlas; joined on county FIPS.
// Spec: TERRAIN_MAP_SPEC.md.
const COUNTIES_TOPO_URL =
  "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";
const STATES_TOPO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
// TIGERweb (Census ArcGIS) — sub-county geometry fetched on drill-in, per county, as GeoJSON polygons.
const TIGERWEB_TRACTS =
  "https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/Tracts_Blocks/MapServer/0/query";
const TIGERWEB_ZCTA =
  "https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/PUMA_TAD_TAZ_UGA_ZCTA/MapServer/1/query";
// The neighborhood layer = Census Block Groups (complete tiling), TIGERweb layer 1; names come from the API's
// neighborhood_names overlay (in the geo payload), so the frontend just needs the polygons keyed by GEOID.
const TIGERWEB_BG =
  "https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/Tracts_Blocks/MapServer/1/query";
// Esri/ArcGIS (TIGERweb) emits polygon rings CLOCKWISE; d3-geo's spherical math expects RFC-7946 winding
// (exterior CCW, holes CW), so a raw Esri ring reads as a polygon covering the ENTIRE globe with a tiny hole —
// the map fills solid with one broken shape. Reversing every ring flips exteriors CW→CCW and holes CCW→CW.
const rewindEsriGeo = (gj) => {
  if (!gj || !Array.isArray(gj.features)) return gj || { type: "FeatureCollection", features: [] };
  const rev = (rings) => rings.map((r) => r.slice().reverse());
  return {
    type: "FeatureCollection",
    features: gj.features.map((f) => {
      const g = f.geometry;
      if (!g) return f;
      let coordinates = g.coordinates;
      if (g.type === "Polygon") coordinates = rev(g.coordinates);
      else if (g.type === "MultiPolygon") coordinates = g.coordinates.map(rev);
      return { ...f, geometry: { ...g, coordinates } };
    }),
  };
};

// Is this ZIP rollup row inside the focused county? Prefer the exact county FIPS the backend stamps on each
// ZIP row (metrics._county); fall back to county name+state so the drill still works before that field ships.
const zipRowInCounty = (g, fc) => {
  if (!fc) return true;
  const c = g.metrics && g.metrics._county;
  if (c != null && c !== "") return String(c) === fc.fips;
  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\s+county$/, "")
      .trim();
  return norm(g.name) === norm(fc.name) && (g.state || "") === (fc.state || "");
};

// Fetch the polygons for the sub-county view of one county:
//   tract — all tracts by STATE+COUNTY (join GEOID)
//   zip   — the ZCTAs for the county's ZIP list (join ZCTA5)
//   place — the named Places we have people in, by GEOID list, across Incorporated (4) + CDP (5) layers.
// `keys` = ZIP list for zip, or Place-GEOID list for place (ignored for tract).
const fetchSubCountyGeo = async (subLevel, countyFips, keys) => {
  try {
    // tract + place both fetch a whole county of Census polygons by STATE+COUNTY (tracts = layer 0, block
    // groups = layer 1); join by GEOID. place = the complete neighborhood geography.
    if (subLevel === "tract" || subLevel === "place") {
      const st = countyFips.slice(0, 2),
        co = countyFips.slice(2, 5);
      const base = subLevel === "place" ? TIGERWEB_BG : TIGERWEB_TRACTS;
      const url = `${base}?where=${encodeURIComponent(`STATE='${st}' AND COUNTY='${co}'`)}&outFields=GEOID&returnGeometry=true&f=geojson&outSR=4326`;
      const r = await fetch(url);
      return rewindEsriGeo(await r.json());
    }
    if (!keys || !keys.length) return { type: "FeatureCollection", features: [] };
    const inList = keys.map((z) => `'${z}'`).join(",");
    const url = `${TIGERWEB_ZCTA}?where=${encodeURIComponent(`ZCTA5 IN (${inList})`)}&outFields=ZCTA5&returnGeometry=true&f=geojson&outSR=4326`;
    const r = await fetch(url);
    return rewindEsriGeo(await r.json());
  } catch (e) {
    return { type: "FeatureCollection", features: [] };
  }
};

// geoAlbersUsa projects the 50 states + DC + AK + HI, but returns NULL for the territories (PR/VI/GU/AS/MP)
// and any bad coordinate — and react-simple-maps' <Marker> then crashes destructuring that null. Only place
// a centroid bubble if the point is actually projectable.
const TERRAIN_TERRITORIES = new Set(["PR", "VI", "GU", "AS", "MP"]);
// ll = [lat, lon]. geoAlbersUsa only projects the 50 states + DC + AK + HI; ANYTHING else (territories,
// [0,0], foreign or out-of-range coords) returns null → <Marker> crashes destructuring it. So bound-check
// hard to US-projectable coordinates.
const terrainProjectable = (state, ll) =>
  Array.isArray(ll) &&
  ll.length === 2 &&
  Number.isFinite(ll[0]) &&
  Number.isFinite(ll[1]) &&
  ll[0] >= 15 &&
  ll[0] <= 72 && // lat: HI ~19 → AK ~71
  ll[1] >= -180 &&
  ll[1] <= -64 && // lon: AK/HI west → ME east
  !(ll[0] === 0 && ll[1] === 0) &&
  !TERRAIN_TERRITORIES.has(state);

// Derive state 2-digit FIPS + centroid (avg of the state's county centroids) from the county gazetteer —
// no extra data needed. Used for the State-level choropleth (us-atlas states-10m geo.id = 2-digit FIPS).
const { STATE_FIPS_BY_ABBR, STATE_CENTROIDS } = (() => {
  const fipsBy = {};
  const acc = {};
  for (const fips in US_COUNTIES) {
    const st = US_COUNTIES[fips][1];
    const lat = US_COUNTIES[fips][3];
    const lon = US_COUNTIES[fips][4];
    if (!fipsBy[st]) fipsBy[st] = fips.slice(0, 2);
    if (!acc[st]) acc[st] = { lat: 0, lon: 0, n: 0 };
    acc[st].lat += lat;
    acc[st].lon += lon;
    acc[st].n += 1;
  }
  const cent = {};
  for (const st in acc) cent[st] = [acc[st].lat / acc[st].n, acc[st].lon / acc[st].n];
  return { STATE_FIPS_BY_ABBR: fipsBy, STATE_CENTROIDS: cent };
})();

// county name normalizer → build a (state|normalizedName) → FIPS index from the bundled gazetteer.
const terrainNormCounty = (s) =>
  (s || "")
    .toUpperCase()
    .replace(/\b(COUNTY|PARISH|BOROUGH|CENSUS AREA|MUNICIPIO)\b/g, "")
    .replace(/[^A-Z]/g, "");
const TERRAIN_FIPS_BY_KEY = (() => {
  const m = {};
  for (const fips in US_COUNTIES) {
    const [name, state] = US_COUNTIES[fips];
    m[`${state}|${terrainNormCounty(name)}`] = fips;
  }
  return m;
})();

// Some state voter files store `county` as a numeric state code (1–N alphabetical), not a name. SC does
// (1=Abbeville … 46=York). Those map to FIPS by the standard alphabetical-odd convention: code N → state
// FIPS + zero-padded (2N−1). Extend TERRAIN_NUMERIC_STATE_FIPS as more states load.
const TERRAIN_NUMERIC_STATE_FIPS = { SC: "45" };
const terrainFipsForGeo = (state, rawName) => {
  const raw = String(rawName ?? "").trim();
  const stFips = TERRAIN_NUMERIC_STATE_FIPS[state];
  if (stFips && /^\d+$/.test(raw)) {
    const n = parseInt(raw, 10);
    if (n >= 1 && n <= 100) return stFips + String(2 * n - 1).padStart(3, "0");
  }
  return TERRAIN_FIPS_BY_KEY[`${state}|${terrainNormCounty(raw)}`];
};
// Real county name for display (from the gazetteer once we have the FIPS), falling back to the raw value.
const terrainDisplayName = (fips, rawName) =>
  (fips && US_COUNTIES[fips] && US_COUNTIES[fips][0]) || rawName;

// Friendly labels for metric keys.
const terrainMetricLabel = (key) => {
  const fixed = {
    "people:total": "People (total)",
    "voters:rep": "Republican voters",
    "voters:dem": "Democrat voters",
    "voters:oth": "Independent / other",
    "turnout:high": "Reliable voters",
    "turnout:mid": "Intermittent voters",
    "turnout:low": "Low-propensity voters",
    "donors:count": "Donors",
    "donors:raised": "Dollars raised",
    "fit:priority": "Priority-tier people",
    "fit:target": "Target-tier people",
    "importance:5star": "★★★★★ importance",
    "importance:4star": "★★★★+ importance",
    "model:fundraising": "Fundraising people",
    "model:persuasion": "Persuasion targets",
    "model:mobilization": "Mobilization people",
    "model:amplification": "Amplification people",
    "model:coalition": "Coalition people",
    "charity:givers": "Charity givers",
    "charity:capacity": "Charity capacity (avg $)",
    "charity:tableprospects": "Table prospects 🍽️",
    "context:population": "Population",
    "context:median-income": "Median income",
    "context:poverty-rate": "Poverty rate",
    "context:unemployment-rate": "Unemployment rate",
    "context:pop-growth": "Population growth",
    "context:home-value": "Home value",
    "context:broadband": "Broadband access",
  };
  if (fixed[key]) return fixed[key];
  const titleCase = (s) =>
    s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  let m = key.match(/^issue:(.+):intensity$/);
  if (m) return `${titleCase(m[1])} — intensity`;
  m = key.match(/^issue:(.+):volume$/);
  if (m) return `${titleCase(m[1])} — volume`;
  m = key.match(/^role:(.+)$/);
  if (m) return `${titleCase(m[1])}s`;
  return key;
};

// What each Strategic Role actually IS and why the campaign should care — not "we flagged them."
const TERRAIN_ROLE_DESC = {
  "high-dollar-donor":
    "High-capacity givers who can write a near-max check. Worth candidate time and a personal call — one ask here can equal hundreds of small-dollar ones.",
  "small-dollar-donor":
    "Repeat small-dollar givers — already bought in and cheap to re-solicit. The backbone of online/recurring money; move them onto a monthly program.",
  influencer:
    "People with real reach AND engagement on social — when they post, a lot of the right people actually see it. Your best organic megaphones; brief them and feed them content.",
  "media-amplifier":
    "Press, podcasters, radio hosts, and creators — they have a platform and an audience by profession. Pitch them interviews, bookings, and surrogate hits.",
  "digital-amplifier":
    "Their posts punch above their follower count — high engagement per post, so what they share actually travels. Hand them shareable graphics/video and they'll spread your message organically.",
  surrogate:
    "High-reach people who also line up with you on the issues — safe to put on stage or on camera speaking for the campaign. Recruit them to represent you publicly.",
  "host-fundraiser":
    "Well-networked, higher-capacity people who can open their home and their rolodex. A host brings their whole circle — ask them to hold an event, not just to give.",
  recruiter:
    "Big networks and a habit of bringing others in — one recruiter can produce dozens of volunteers or donors. Ask them for introductions and to build a team.",
  volunteer:
    "Highly mobilizable — likely to show up, knock doors, make calls, and turn out. Your field army; get them onto shifts and events.",
  "persuadable-voter":
    "Genuinely movable — not locked into either side and reachable on the issues. The people actually worth spending persuasion budget and messaging on.",
  "reliable-supporter":
    "Consistent voters who already agree with you — no persuasion needed. Confirm they turn out and ask each to bring a few others (GOTV multipliers).",
  "policy-validator":
    "Credible voices on a specific issue by profession or experience — a nurse on healthcare, a realtor on housing. Their word carries weight; ask for a testimonial on their issue.",
  "community-leader":
    "Locally rooted, respected organizers who speak for a whole constituency. An endorsement or co-host here unlocks a community — bring them into the coalition.",
};

// Plain-language explanation of what each metric counts (shown under the selector when chosen).
const terrainMetricDesc = (key) => {
  const fixed = {
    "people:total": "Everyone in this campaign's universe who is located here — donors, voters, and social-identified people combined.",
    "voters:rep": "Registered Republicans (from the voter file).",
    "voters:dem": "Registered Democrats (from the voter file).",
    "voters:oth": "Registered independents / unaffiliated / other (from the voter file).",
    "turnout:high": "Reliable voters — turned out in both of the last two general elections.",
    "turnout:mid": "Intermittent voters — voted in one of the last two generals, not both.",
    "turnout:low": "Low-propensity voters — sat out both of the last two generals.",
    "donors:count": "People here who have given to this campaign at least once.",
    "donors:raised": "Total dollars raised from people located here.",
    "fit:priority": "Priority-tier people — those scoring in the top ~10% (90th percentile or higher) on their strongest model. Act on these now.",
    "fit:target": "Target-tier people — those scoring 75th–89th percentile on their strongest model. High value, worth prioritizing.",
    "importance:5star": "5★ strategic-importance people — the rare few who clear multiple models at once.",
    "importance:4star": "4★ and up strategic-importance people — high all-around value across models.",
    "model:fundraising": "People whose strongest model is Fundraising — most likely, and able, to give.",
    "model:persuasion": "People whose strongest model is Persuasion — the most movable, worth spending persuasion on.",
    "model:mobilization": "People whose strongest model is Mobilization — most likely to turn out, volunteer, or bring others.",
    "model:amplification": "People whose strongest model is Amplification — the biggest reach; your best surrogates.",
    "model:coalition": "People whose strongest model is Coalition — they represent or organize a constituency.",
    "charity:givers": "People here with charitable-giving history (Event.Gives) — a capacity/ask-level proxy, NOT political money.",
    "charity:capacity": "Average charitable ask anchor (giving-weighted ceiling) of givers here — a rough read on how large an ask this area can bear.",
    "charity:tableprospects": "People here who buy tables/seats at charity fundraiser dinners ($250+ ticket) — prime targets for a campaign dinner.",
    "context:population": "Total population of the county (Census ACS) — the size of the place, not your people.",
    "context:median-income": "Median household income (Census ACS) — read alongside cost-of-living and property-tax messaging.",
    "context:poverty-rate": "Share of residents below the poverty line (Census ACS) — economic-anxiety terrain.",
    "context:unemployment-rate": "Local unemployment rate (Census ACS) — jobs/economy messaging fit.",
    "context:pop-growth": "Recent population growth (Census ACS) — booming vs. hollowing-out counties.",
    "context:home-value": "Typical home value (Zillow) — housing-cost and property-tax terrain.",
    "context:broadband": "Share of the county with broadband access (FCC) — reachability by digital vs. mail/door.",
  };
  if (fixed[key]) return fixed[key];
  const tc = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  let m = key.match(/^issue:(.+):intensity$/);
  if (m) return `How intensely people here are engaged on ${tc(m[1])} (average signal strength, 0–100).`;
  m = key.match(/^issue:(.+):volume$/);
  if (m) return `How many people here have shown a position on ${tc(m[1])}.`;
  m = key.match(/^role:(.+)$/);
  if (m)
    return (
      TERRAIN_ROLE_DESC[m[1]] ||
      `${tc(m[1])}s — a Strategic Role from the Scoring Model.`
    );
  return "";
};

// Preferred ordering of metric groups for the dropdown.
const TERRAIN_METRIC_ORDER = [
  "fit:priority",
  "fit:target",
  "importance:5star",
  "donors:raised",
  "donors:count",
  "charity:givers",
  "charity:capacity",
  "charity:tableprospects",
  "people:total",
  "voters:rep",
  "voters:dem",
  "voters:oth",
  "turnout:high",
  "turnout:mid",
  "turnout:low",
];

// 2-stop color ramp light → Pollis red (#FF0A4B) for a metric value's position in [min,max]. Count data is
// heavily skewed (one big county, many small), so a LINEAR scale washes the map out — everyone but the top
// maps near-white. A sqrt (perceptual) scale spreads the low/mid range so the whole map reads.
const terrainColor = (v, min, max) => {
  if (v == null || isNaN(v)) return "#eef1f5";
  let t = max > min ? (v - min) / (max - min) : v > 0 ? 1 : 0;
  t = Math.sqrt(Math.max(0, Math.min(1, t)));
  const a = [255, 228, 234],
    b = [255, 10, 75]; // #ffe4ea → #FF0A4B
  const c = a.map((x, i) => Math.round(x + (b[i] - x) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
};

function TerrainMap({ campaignId }) {
  const { authFetch } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("people:total");
  const [mode, setMode] = useState("choropleth"); // choropleth | heatmap
  // Drill hierarchy: US (states) → click a state → its counties → click a county → its ZIPs/tracts.
  // `level` derives from drill depth; ZIPs/tracts only ever render inside one county.
  const [focusState, setFocusState] = useState(null); // {abbr} drilled-into state
  const [focusCounty, setFocusCounty] = useState(null); // {fips,name,state} drilled-into county
  const [subLevel, setSubLevel] = useState("tract"); // zip | tract — granularity inside a county
  const [subGeo, setSubGeo] = useState(null); // TIGERweb GeoJSON polygons for the sub-county view
  const level = focusCounty ? subLevel : focusState ? "county" : "state";
  const [sel, setSel] = useState(null);
  const [hover, setHover] = useState(null);
  const pubnubSubKey = process.env.REACT_APP_PUBNUB_SUB_KEY;

  const load = useCallback(() => {
    if (!PHP_API_URL || !campaignId) {
      setLoading(false);
      return;
    }
    const lvl = focusCounty ? subLevel : focusState ? "county" : "state";
    setLoading(true);
    setSel(null);
    authFetch(`${PHP_API_URL}/identity/geo?campaign_id=${campaignId}&level=${lvl}`)
      .then((r) => r.json())
      .then((d) => {
        setPayload(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [authFetch, campaignId, focusState, focusCounty, subLevel]);

  useEffect(() => {
    load();
    // Refresh the map when the geo roll-up finishes (published on `scoring-all` after a scoring run).
    if (!pubnubSubKey) return;
    const pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
    pn.addListener({
      message: ({ channel: ch, message: m }) => {
        if (ch === "scoring-all" && m.type === "geo_done") load();
      },
    });
    pn.subscribe({ channels: ["scoring-all"] });
    return () => {
      try {
        pn.removeAllListeners();
        pn.unsubscribeAll();
      } catch (e) {
        /* ignore */
      }
    };
  }, [load, pubnubSubKey]);

  // Fetch the sub-county polygon geometry from TIGERweb when drilled into a county (tracts by county, or
  // ZCTAs for the ZIPs we have data in that county).
  useEffect(() => {
    if (!focusCounty) {
      setSubGeo(null);
      return;
    }
    let alive = true;
    setSubGeo(null);
    // zip fetches geometry by the ZIPs we actually have people in (scoped to the county); tract + place fetch a
    // whole county of Census polygons and need no key list.
    const keys =
      subLevel === "zip"
        ? (payload?.geos || [])
            .filter((g) => zipRowInCounty(g, focusCounty))
            .map((g) => g.geo_key)
        : [];
    fetchSubCountyGeo(subLevel, focusCounty.fips, keys).then((gj) => {
      if (alive) setSubGeo(gj);
    });
    return () => {
      alive = false;
    };
  }, [focusCounty, subLevel, payload]);

  // Join our geos to a per-level key; index it for the choropleth.
  const { byFips, geoRows, availableMetrics } = useMemo(() => {
    const geos = payload?.geos || [];
    const byFips = {};
    const rows = [];
    const metricSet = new Set();
    for (const g of geos) {
      // `_`-prefixed keys (e.g. _lat/_lng/_county) are payload, not selectable metrics.
      Object.keys(g.metrics || {}).forEach((k) => {
        if (!k.startsWith("_")) metricSet.add(k);
      });
      // Sub-county views only show the drilled-into county's geos.
      if ((level === "zip" || level === "place") && focusCounty && !zipRowInCounty(g, focusCounty)) continue;
      if (level === "tract" && focusCounty && String(g.geo_key).slice(0, 5) !== focusCounty.fips) continue;
      // joinKey = how this row joins to its polygon (state/county FIPS · ZIP=ZCTA5 · tract=GEOID · place=GEOID).
      let joinKey, label, ll;
      if (level === "tract") {
        joinKey = g.geo_key; // 11-digit tract GEOID
        label = g.name; // "Tract 123.45"
        ll = g.metrics && g.metrics._lat != null ? [g.metrics._lat, g.metrics._lng] : null;
      } else if (level === "place") {
        joinKey = g.geo_key; // 7-digit place GEOID
        label = g.name; // "Mount Pleasant town"
        ll = null;
      } else if (level === "zip") {
        joinKey = g.geo_key; // 5-digit ZIP (= ZCTA5)
        label = g.geo_key;
        ll = US_ZIPS[g.geo_key] || null;
      } else if (level === "state") {
        joinKey = STATE_FIPS_BY_ABBR[g.state]; // 2-digit state FIPS
        label = g.state;
        ll = STATE_CENTROIDS[g.state] || null;
      } else {
        joinKey = /^\d{5}$/.test(g.geo_key) ? g.geo_key : terrainFipsForGeo(g.state, g.name);
        label = terrainDisplayName(joinKey, g.name);
        ll = joinKey && US_COUNTIES[joinKey] ? [US_COUNTIES[joinKey][3], US_COUNTIES[joinKey][4]] : null;
      }
      const row = { ...g, fips: joinKey, label, ll };
      rows.push(row);
      if (joinKey) byFips[joinKey] = row;
    }
    const present = [...metricSet];
    const isOrdered = (k) => TERRAIN_METRIC_ORDER.includes(k);
    const ordered = [
      ...TERRAIN_METRIC_ORDER.filter((k) => metricSet.has(k)),
      ...present.filter((k) => k.startsWith("model:")).sort(),
      ...present.filter((k) => k.startsWith("role:")).sort(),
      ...present
        .filter((k) => k.startsWith("issue:") && k.endsWith(":intensity"))
        .sort(),
      ...present.filter((k) => k.startsWith("context:")).sort(),
      ...present.filter(
        (k) =>
          !isOrdered(k) &&
          !k.startsWith("model:") &&
          !k.startsWith("role:") &&
          !k.startsWith("issue:") &&
          !k.startsWith("context:"),
      ),
    ];
    return { byFips, geoRows: rows, availableMetrics: ordered };
  }, [payload, level]);

  const bounds = payload?.metric_bounds?.[metric] || { min: 0, max: 1 };
  const valOf = (g) => (g?.metrics ? g.metrics[metric] : undefined);
  // State is redundant inside a drilled-in view (the breadcrumb already says US › SC › …), so labels drop it.
  const geoTitle = (g) =>
    !g
      ? ""
      : level === "zip"
        ? `${g.label} · ${US_ZIPS[g.geo_key]?.[2] || g.name}` // ZIP · City
        : g.label; // state abbr · county · Tract N · neighborhood name

  // Drill: US(states) → click a state → its counties → click a county → its ZIPs/tracts.
  const drillToState = (abbr) => {
    setFocusState(abbr);
    setFocusCounty(null);
    setSel(null);
  };
  const drillToCounty = (g) => {
    if (!g || !g.fips) return;
    setFocusCounty({ fips: g.fips, name: g.label, state: g.state });
    setSel(null);
  };
  const backToStates = () => {
    setFocusState(null);
    setFocusCounty(null);
    setSel(null);
  };
  const backToCounties = () => {
    setFocusCounty(null);
    setSel(null);
  };
  const onGeoClick = (g) => {
    if (!g) return;
    if (level === "state") drillToState(g.state);
    else if (level === "county") drillToCounty(g);
    else setSel(g);
  };
  // Only the county base filters by focused state; the useMemo already scopes sub-county rows to the county.
  const inFocus = (g) => level !== "county" || !focusState || g.state === focusState;
  // Sub-county view fits a plain geoMercator to the county's own geometry (geoAlbersUsa is a fixed-scale
  // composite that can't zoom to a single county). react-simple-maps uses a projection FUNCTION as-is.
  const subProjection = useMemo(() => {
    if (!focusCounty || !subGeo || !(subGeo.features || []).length) return null;
    try {
      return geoMercator().fitExtent(
        [
          [10, 10],
          [870, 510],
        ],
        subGeo,
      );
    } catch (e) {
      return null;
    }
  }, [focusCounty, subGeo]);
  const subCenter = useMemo(() => {
    if (!subProjection || !subGeo) return null;
    try {
      const b = geoBounds(subGeo); // [[minLon,minLat],[maxLon,maxLat]]
      return [(b[0][0] + b[1][0]) / 2, (b[0][1] + b[1][1]) / 2];
    } catch (e) {
      return null;
    }
  }, [subProjection, subGeo]);

  // Zoom: focused county (fitted projection, neutral group) → its state → national. Memoized so re-renders
  // don't reset pan/zoom.
  const zg = useMemo(() => {
    if (focusCounty) {
      // The fitted sub-county projection already frames the county; keep the group neutral at its center.
      if (subCenter) return { center: subCenter, zoom: 1 };
      if (US_COUNTIES[focusCounty.fips]) {
        // Geometry still loading (geoAlbersUsa fallback) — frame the county area, not the whole US.
        const c = US_COUNTIES[focusCounty.fips]; // [name,state,pop,lat,lon]
        return { center: [c[4], c[3]], zoom: 8 };
      }
    }
    if (focusState && STATE_CENTROIDS[focusState]) {
      return { center: [STATE_CENTROIDS[focusState][1], STATE_CENTROIDS[focusState][0]], zoom: 5 };
    }
    return { center: [-97, 38], zoom: 1 };
  }, [focusState, focusCounty, subCenter]);

  const rankedAll = useMemo(
    () =>
      [...geoRows]
        .filter((g) => valOf(g) != null && inFocus(g))
        .sort((a, b) => (valOf(b) || 0) - (valOf(a) || 0)),
    [geoRows, metric, focusState, focusCounty, level], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const RANK_PAGE = 10;
  const [rankPage, setRankPage] = useState(0);
  const rankPages = Math.max(1, Math.ceil(rankedAll.length / RANK_PAGE));
  const rankStart = Math.min(rankPage, rankPages - 1) * RANK_PAGE;
  const ranked = rankedAll.slice(rankStart, rankStart + RANK_PAGE);
  // Reset to the first page whenever the ranked set changes (metric/level/region).
  useEffect(() => {
    setRankPage(0);
  }, [metric, level, focusState, focusCounty]);
  const maxPeople = useMemo(
    () => Math.max(1, ...geoRows.map((g) => g.n_people || 0)),
    [geoRows],
  );
  // The detail box defaults to the top-ranked geo (overall, not the current page) until one is clicked.
  const detailGeo = sel || rankedAll[0] || null;

  // The People table below the map is scoped to the selected region. Key per level: state=abbr, county=5-digit
  // FIPS, zip=ZIP, tract=11-digit GEOID, place=12-digit Block Group GEOID (matches personReport's geo filter).
  const geoScope = useMemo(() => {
    if (!detailGeo) return null;
    const key =
      level === "state" ? detailGeo.state : level === "county" ? detailGeo.fips : detailGeo.geo_key;
    if (!key) return null;
    return { level, key, label: geoTitle(detailGeo) };
  }, [detailGeo, level]); // eslint-disable-line react-hooks/exhaustive-deps

  // If a level switch drops the current metric (e.g. a county "context:" metric → ZIP), fall back.
  useEffect(() => {
    if (availableMetrics.length && !availableMetrics.includes(metric)) {
      setMetric(availableMetrics.includes("people:total") ? "people:total" : availableMetrics[0]);
    }
  }, [availableMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!campaignId)
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ color: C.muted }}>
          Select a campaign to see its terrain map.
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header + controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 900, color: C.navy }}>
            Ground Game
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: C.muted }}>
            {loading ? (
              "Loading…"
            ) : (
              <>
                {/* Drill breadcrumb — click a crumb to zoom back out. */}
                <Box
                  component="span"
                  onClick={backToStates}
                  sx={{
                    color: focusState ? C.red : C.muted,
                    fontWeight: focusState ? 700 : 400,
                    cursor: focusState ? "pointer" : "default",
                    mr: 0.5,
                  }}
                >
                  US
                </Box>
                {focusState && (
                  <>
                    <Box component="span" sx={{ color: C.muted, mr: 0.5 }}>›</Box>
                    <Box
                      component="span"
                      onClick={backToCounties}
                      sx={{
                        color: focusCounty ? C.red : C.muted,
                        fontWeight: focusCounty ? 700 : 400,
                        cursor: focusCounty ? "pointer" : "default",
                        mr: 0.5,
                      }}
                    >
                      {focusState}
                    </Box>
                  </>
                )}
                {focusCounty && (
                  <>
                    <Box component="span" sx={{ color: C.muted, mr: 0.5 }}>›</Box>
                    <Box component="span" sx={{ color: C.muted, mr: 0.5 }}>{focusCounty.name}</Box>
                  </>
                )}
                <Box component="span" sx={{ color: C.muted }}>
                  ·{" "}
                  {focusCounty
                    ? `${geoRows.length} ${level === "zip" ? "ZIPs" : level === "place" ? "neighborhoods" : "tracts"} · ${terrainMetricLabel(metric)}`
                    : focusState
                      ? `${rankedAll.length} counties · ${terrainMetricLabel(metric)}`
                      : `${geoRows.length} states · ${terrainMetricLabel(metric)}`}
                </Box>
              </>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {/* Granularity toggle only applies inside a county (ZIP vs census tract). Above that, the drill
              level (US states → counties) is driven by clicking the map. */}
          {focusCounty && (
            <Box sx={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 1, overflow: "hidden" }}>
              {[
                ["tract", "Tracts"],
                ["zip", "ZIPs"],
                ["place", "Hoods"],
              ].map(([lv, lbl]) => (
                <Box
                  key={lv}
                  component="button"
                  onClick={() => setSubLevel(lv)}
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    px: 1.25,
                    py: 0.75,
                    border: "none",
                    cursor: "pointer",
                    bgcolor: subLevel === lv ? C.navy : C.white,
                    color: subLevel === lv ? "#fff" : C.muted,
                  }}
                >
                  {lbl}
                </Box>
              ))}
            </Box>
          )}
          <Box
            component="select"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            sx={{
              fontSize: 13,
              px: 1,
              py: 0.75,
              borderRadius: 1,
              border: `1px solid ${C.border}`,
              bgcolor: C.white,
              color: C.charcoal,
              minWidth: 200,
            }}
          >
            {availableMetrics.map((k) => (
              <option key={k} value={k}>
                {terrainMetricLabel(k)}
              </option>
            ))}
          </Box>
          <Box sx={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 1, overflow: "hidden" }}>
            {["choropleth", "heatmap"].map((m) => (
              <Box
                key={m}
                component="button"
                onClick={() => setMode(m)}
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  px: 1.25,
                  py: 0.75,
                  border: "none",
                  cursor: "pointer",
                  bgcolor: mode === m ? C.navy : C.white,
                  color: mode === m ? "#fff" : C.muted,
                  textTransform: "capitalize",
                }}
              >
                {m}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", lg: "row" } }}>
        {/* Map */}
        <Box
          sx={{
            flex: "1 1 42%",
            position: "relative",
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#f7f9fc",
            minHeight: 420,
          }}
        >
          <ComposableMap
            projection={subProjection || "geoAlbersUsa"}
            width={880}
            height={520}
            style={{ width: "100%", height: "auto" }}
          >
            <ZoomableGroup center={zg.center} zoom={zg.zoom}>
              {/* When drilled into a county, still draw the county outlines underneath the sub-county fill
                  so the county borders stay visible while ZIP/tract polygons fill in. */}
              {focusCounty && (
                <Geographies geography={COUNTIES_TOPO_URL}>
                  {({ geographies }) =>
                    geographies
                      .filter((geo) => geo.id === focusCounty.fips)
                      .map((geo) => (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill="#eef2f7"
                          stroke="#c9d3e0"
                          strokeWidth={0.5}
                          style={{ default: { outline: "none" }, hover: { outline: "none" }, pressed: { outline: "none" } }}
                        />
                      ))
                  }
                </Geographies>
              )}
              <Geographies
                geography={
                  level === "state"
                    ? STATES_TOPO_URL
                    : level === "county"
                      ? COUNTIES_TOPO_URL
                      : subGeo || { type: "FeatureCollection", features: [] }
                }
              >
                {({ geographies }) =>
                  geographies.map((geo) => {
                    // Join key: state/county use the topojson FIPS id; sub-county polygons carry their key
                    // in properties (tract GEOID · ZCTA5). byFips is already scoped to the focused county.
                    const joinKey =
                      level === "tract" || level === "place"
                        ? geo.properties?.GEOID
                        : level === "zip"
                          ? geo.properties?.ZCTA5
                          : geo.id;
                    const raw = byFips[joinKey];
                    const g = raw && inFocus(raw) ? raw : undefined;
                    const v = g ? valOf(g) : undefined;
                    const fill =
                      mode === "heatmap"
                        ? g
                          ? "#fdeef2"
                          : "#f2f5f9"
                        : terrainColor(v, bounds.min, bounds.max);
                    const selected = g && detailGeo && g.geo_key === detailGeo.geo_key;
                    const hovered = g && hover && g.geo_key === hover.geo_key;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={selected ? "#2c3344" : hovered ? "#03a9f4" : fill}
                        stroke="#ffffff"
                        strokeWidth={level === "state" || level === "county" ? 0.25 : 0.4}
                        onMouseEnter={() => g && setHover({ ...g, v })}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => onGeoClick(g)}
                        style={{
                          default: { outline: "none" },
                          hover: { outline: "none", cursor: g ? "pointer" : "default" },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {/* Heatmap mode (state/county only) overlays people-weighted bubbles on the choropleth. */}
              {mode === "heatmap" &&
                (level === "state" || level === "county") &&
                geoRows.map((g) => {
                  const v = valOf(g);
                  if (!terrainProjectable(g.state, g.ll) || v == null || !inFocus(g))
                    return null;
                  const [lat, lon] = g.ll;
                  const r = 3 + 16 * Math.sqrt((g.n_people || 0) / maxPeople);
                  return (
                    <Marker key={g.geo_key} coordinates={[lon, lat]}>
                      <circle
                        r={r}
                        fill={terrainColor(v, bounds.min, bounds.max)}
                        fillOpacity={0.72}
                        stroke="#0a2240"
                        strokeWidth={0.4}
                        style={{ cursor: "pointer" }}
                        onClick={() => onGeoClick(g)}
                      />
                    </Marker>
                  );
                })}
            </ZoomableGroup>
          </ComposableMap>

          {/* Sub-county geometry loads on demand from TIGERweb — show status while it fetches. */}
          {focusCounty && subGeo === null && (
            <Box
              sx={{
                position: "absolute",
                bottom: 10,
                left: 10,
                bgcolor: "rgba(10,34,64,0.9)",
                color: "#fff",
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                fontSize: 11.5,
                fontWeight: 700,
              }}
            >
              Loading {subLevel === "zip" ? "ZIP" : subLevel === "place" ? "neighborhood" : "tract"} boundaries…
            </Box>
          )}
          {focusCounty && subGeo && (subGeo.features || []).length === 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 10,
                left: 10,
                bgcolor: "rgba(10,34,64,0.9)",
                color: "#fff",
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                fontSize: 11.5,
                fontWeight: 700,
              }}
            >
              No {subLevel === "zip" ? "ZIP" : subLevel === "place" ? "neighborhood" : "tract"} boundaries for this county
            </Box>
          )}

          {/* Hover tooltip */}
          {hover && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                bgcolor: C.navy,
                color: "#fff",
                px: 1.25,
                py: 0.75,
                borderRadius: 1,
                pointerEvents: "none",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                {geoTitle(hover)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#93c5fd" }}>
                {terrainMetricLabel(metric)}:{" "}
                {hover.v != null ? Number(hover.v).toLocaleString() : "—"}
              </Typography>
            </Box>
          )}

          {/* Legend */}
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              bgcolor: "rgba(255,255,255,0.92)",
              border: `1px solid ${C.border}`,
              borderRadius: 1,
              px: 1,
              py: 0.75,
            }}
          >
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: C.muted, mb: 0.4 }}>
              {terrainMetricLabel(metric).toUpperCase()}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ fontSize: 9, color: C.muted }}>
                {Math.round(bounds.min).toLocaleString()}
              </Typography>
              <Box
                sx={{
                  width: 90,
                  height: 8,
                  borderRadius: 4,
                  background: "linear-gradient(90deg, #ffe4ea, rgb(255,74,122) 50%, #FF0A4B)",
                }}
              />
              <Typography sx={{ fontSize: 9, color: C.muted }}>
                {Math.round(bounds.max).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Rankings + selection */}
        <Box sx={{ flex: "1 1 38%", minWidth: 0 }}>
          {/* What the chosen metric means */}
          {terrainMetricDesc(metric) && (
            <Box
              sx={{
                mb: 1.5,
                px: 1.25,
                py: 0.9,
                bgcolor: "#eaf3ff",
                border: `1px solid ${C.border}`,
                borderRadius: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: C.charcoal, lineHeight: 1.5 }}>
                <b style={{ color: C.navy }}>{terrainMetricLabel(metric)}</b> —{" "}
                {terrainMetricDesc(metric)}
              </Typography>
            </Box>
          )}
          {detailGeo && (
            <Card elevation={0} sx={{ border: `1px solid ${C.border}`, mb: 1.5 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.navy }}>
                    {geoTitle(detailGeo)}
                  </Typography>
                  {!sel && (
                    <Typography sx={{ fontSize: 10, color: C.muted, fontStyle: "italic" }}>
                      {level === "state"
                        ? "top state · click a state to drill in"
                        : level === "county"
                          ? "top county · click a county to drill in"
                          : `top ${level === "zip" ? "ZIP" : level === "place" ? "neighborhood" : "tract"} · click to change`}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, color: C.muted, mb: 1 }}>
                  {detailGeo.n_people?.toLocaleString()} people
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {Object.entries(detailGeo.metrics || {})
                    .filter(([, v]) => v > 0)
                    .slice(0, 12)
                    .map(([k, v]) => (
                      <Box
                        key={k}
                        sx={{
                          fontSize: 10.5,
                          bgcolor: `${C.navy}0d`,
                          borderRadius: "4px",
                          px: 0.75,
                          py: 0.35,
                        }}
                      >
                        <b>{terrainMetricLabel(k)}:</b>{" "}
                        {k === "donors:raised" || k === "charity:capacity" ? "$" : ""}
                        {Number(v).toLocaleString()}
                      </Box>
                    ))}
                </Box>
              </CardContent>
            </Card>
          )}
          <Card elevation={0} sx={{ border: `1px solid ${C.border}` }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", mb: 1 }}>
                Top {level === "state" ? "states" : level === "zip" ? "ZIPs" : level === "tract" ? "tracts" : level === "place" ? "neighborhoods" : "counties"} · {terrainMetricLabel(metric)}
              </Typography>
              {loading ? (
                <Skeleton height={200} />
              ) : rankedAll.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: C.muted }}>
                  No data yet — run scoring (Scoring Model → Re-score now) to populate the map.
                </Typography>
              ) : (
                ranked.map((g, i) => {
                  const v = valOf(g);
                  const w = bounds.max > 0 ? (v / bounds.max) * 100 : 0;
                  const rowSelected = detailGeo && g.geo_key === detailGeo.geo_key;
                  const rowHover = !rowSelected && hover && g.geo_key === hover.geo_key;
                  return (
                    <Box
                      key={g.geo_key}
                      onClick={() => (level === "state" || level === "county" ? onGeoClick(g) : setSel(g))}
                      onMouseEnter={() => setHover({ ...g, v })}
                      onMouseLeave={() => setHover(null)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 0.5,
                        px: 0.75,
                        borderRadius: 1,
                        cursor: "pointer",
                        bgcolor: rowSelected ? "#2c3344" : rowHover ? "#eaf3ff" : "transparent",
                        "&:hover": { bgcolor: rowSelected ? "#2c3344" : "#eaf3ff" },
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: rowSelected ? "rgba(255,255,255,0.55)" : C.muted, width: 22 }}>
                        {rankStart + i + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: rowSelected ? "#fff" : C.charcoal,
                          width: 156,
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={geoTitle(g)}
                      >
                        {geoTitle(g)}
                      </Typography>
                      <Box sx={{ flex: 1, height: 6, bgcolor: rowSelected ? "rgba(255,255,255,0.18)" : C.rowAlt, borderRadius: 3, overflow: "hidden" }}>
                        <Box sx={{ width: `${w}%`, height: "100%", bgcolor: C.red }} />
                      </Box>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: rowSelected ? "#fff" : C.navy, width: 54, textAlign: "right" }}>
                        {k_isMoney(metric) ? "$" : ""}
                        {Number(v).toLocaleString()}
                      </Typography>
                    </Box>
                  );
                })
              )}
              {rankedAll.length > RANK_PAGE && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 1,
                    pt: 1,
                    borderTop: `1px solid ${C.border}`,
                  }}
                >
                  <Typography sx={{ fontSize: 11, color: C.muted }}>
                    {rankStart + 1}–{Math.min(rankStart + RANK_PAGE, rankedAll.length)} of{" "}
                    {rankedAll.length}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {[
                      ["‹", () => setRankPage((p) => Math.max(0, p - 1)), rankPage <= 0],
                      ["›", () => setRankPage((p) => Math.min(rankPages - 1, p + 1)), rankPage >= rankPages - 1],
                    ].map(([lbl, fn, disabled], idx) => (
                      <Box
                        key={idx}
                        component="button"
                        onClick={fn}
                        disabled={disabled}
                        sx={{
                          fontSize: 13,
                          fontWeight: 800,
                          lineHeight: 1,
                          px: 1,
                          py: 0.5,
                          border: `1px solid ${C.border}`,
                          borderRadius: 1,
                          bgcolor: C.white,
                          color: disabled ? C.border : C.navy,
                          cursor: disabled ? "default" : "pointer",
                        }}
                      >
                        {lbl}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* People in the selected region — mirrors the People tab, scoped to the map selection above. */}
      {geoScope && (
        <Box
          sx={{
            mt: 2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            bgcolor: C.white,
            p: { xs: 1.5, md: 2 },
          }}
        >
          <AudienceExplorer
            campaignId={campaignId}
            geoScope={geoScope}
            metric={metric}
            pageSize={20}
          />
        </Box>
      )}
    </Box>
  );
}
const k_isMoney = (metric) =>
  metric === "donors:raised" || metric === "charity:capacity";

// ─── Main App ──────────────────────────────────────────────────────────────
export default function App() {
  const {
    isLoggedIn,
    loading: authLoading,
    user,
    campaigns,
    selectedCampaign,
    setSelectedCampaign,
    logout,
    refreshUser,
    refreshCampaigns,
    authFetch,
  } = useAuth();
  const activeCampaignId = selectedCampaign?.id || null;

  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [campaignSetupOpen, setCampaignSetupOpen] = useState(false);
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [tab, _setTab] = useState(() => readPath().tab);
  const [briefSubTab, setBriefSubTab] = useState(
    () => readPath().briefSubTab || "Overview",
  );
  const [donorsSubView, setDonorsSubView] = useState(() => readPath().subView);
  const [kalshi, setKalshi] = useState(null);
  const [kalshiLoading, setKalshiLoading] = useState(false);
  const [kalshiFetchedAt, setKalshiFetchedAt] = useState(null);
  const [kalshiRefresh, setKalshiRefresh] = useState(0);
  const [kalshiHistory, setKalshiHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [campaignBrief, setCampaignBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefBuildDone, setBriefBuildDone] = useState({}); // slug→true: which dep tabs the brief cascade has finished
  const campaignPnRef = useRef(null);
  const refreshFecRef = useRef(null);
  const newsCampaignRef = useRef(null); // tracks which campaign the current newsData was fetched for
  const messagingIntelCbRef = useRef(null);
  const messagingImageCbRef = useRef(null);
  const skipClearCollectionRef = useRef(false); // set true during URL-restore to prevent selectedAudienceId effect from clearing selectedCollectionId
  const [winredData, setWinredData] = useState(null);
  const [winredDaily, setWinredDaily] = useState(null);
  const [winredLoading, setWinredLoading] = useState(false);
  const [winredDailyLoading, setWinredDailyLoading] = useState(false);
  const [donorCohorts, setDonorCohorts] = useState(null);
  const [livePolls, setLivePolls] = useState(null);
  const [liveFEC, setLiveFEC] = useState(null);
  const [pollsRefreshing, setPollsRefreshing] = useState(false);
  const [newsData, setNewsData] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [pollingSummary, setPollingSummary] = useState(null);
  const [pollingSummaryLoading, setPollingSummaryLoading] = useState(false);
  const [tabSummaries, setTabSummaries] = useState({});
  const [tabSummaryLoading, setTabSummaryLoading] = useState({});
  const [opponentIntel, setOpponentIntel] = useState(null);
  const [opponentIntelLoading, setOpponentIntelLoading] = useState(false);
  const [policyPositions, setPolicyPositions] = useState(null);
  const [policyPositionsLoading, setPolicyPositionsLoading] = useState(false);
  const [campaignData, setCampaignData] = useState({});
  const [campaignDataLoading, setCampaignDataLoading] = useState(false);
  const intelUrl = PHP_API_URL ? `${PHP_API_URL}/intelligence` : "";

  // ── Social media state (shared across left/right columns) ──────────────────
  const [socialMentions, setSocialMentions] = useState([]);
  const [socialTermMentions, setSocialTermMentions] = useState(null); // null = not fetched; [] or [...] = fetched
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialSyncing, setSocialSyncing] = useState(false);
  const [socialSyncResult, setSocialSyncResult] = useState(null);
  const [socialLastSynced, setSocialLastSynced] = useState(null);
  const [socialError, setSocialError] = useState(null);
  const [socialSearchQuery, setSocialSearchQuery] = useState("");
  const [socialFilter, setSocialFilter] = useState("all");
  const [socialAuthorFilter, setSocialAuthorFilter] = useState(null);
  const [socialPage, setSocialPage] = useState(1);
  const [socialMode, setSocialMode] = useState(
    () => readPath().socialMode || "monitor",
  );
  const [socialLeads, setSocialLeads] = useState([]);
  const [socialCollecting, setSocialCollecting] = useState(false);
  const [socialCollectResult, setSocialCollectResult] = useState(null);
  const [socialCollectProgress, setSocialCollectProgress] = useState(null); // { tweetId, collected, done } — live background-crawl count
  const [socialCollectTweetUrl, setSocialCollectTweetUrl] = useState("");
  const [audiencePreFill, setAudiencePreFill] = useState(null);
  const [socialTerms, setSocialTerms] = useState([]);
  const [socialTermsLoading, setSocialTermsLoading] = useState(false);
  const [socialSelectedTerm, setSocialSelectedTerm] = useState(null);
  const [socialSyncTick, setSocialSyncTick] = useState(0); // bump on sync done → refresh selected-term feed
  const socialTermPrevRef = useRef(null);
  const [socialAudiences, setSocialAudiences] = useState([]);
  const [socialAudiencesLoading, setSocialAudiencesLoading] = useState(false);
  const [selectedAudienceId, setSelectedAudienceId] = useState(null);
  const [socialCollections, setSocialCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(
    () => readPath().socialCollectionId || null,
  );
  const [socialCollectionAnswers, setSocialCollectionAnswers] = useState([]);
  const [socialCollectionInsights, setSocialCollectionInsights] =
    useState(null);
  const [socialPolling, setSocialPolling] = useState(false);
  const [socialPollProgress, setSocialPollProgress] = useState(null); // { processed, total } | null
  const [bulkReplyJob, setBulkReplyJob] = useState(null); // { jobId, total, sent, failed, status, answerFilter }
  const [socialDonorLinks, setSocialDonorLinks] = useState([]);
  const [dataSyncing, setDataSyncing] = useState(false);
  const [socialEnriching, setSocialEnriching] = useState(false);
  const [socialEnrichProgress, setSocialEnrichProgress] = useState(null); // { processed, total, enriched, matched }
  const [socialLocating, setSocialLocating] = useState(false);
  const [leadsMsg, setLeadsMsg] = useState(null);
  const [leadsRefreshTick, setLeadsRefreshTick] = useState(0);
  const [collectingAllTermId, setCollectingAllTermId] = useState(null);
  const [collectAllProgress, setCollectAllProgress] = useState(null); // { processed, total } | null
  const [profileEmail, setProfileEmail] = useState(null);
  const [profileRow, setProfileRow] = useState(null);
  const [campaignConfig, setCampaignConfig] = useState(null);

  // Derived config — only uses DB values, never falls back to .env campaign-specific constants
  const cfg = {
    candidateName: campaignConfig?.candidate?.full_name || "",
    party: campaignConfig?.candidate?.party || "Republican",
    office: campaignConfig?.campaign?.office || "",
    state: campaignConfig?.candidate?.state || "",
    primaryDate: campaignConfig?.campaign?.primary_date || null,
    electionDate: campaignConfig?.campaign?.election_date || null,
    opponents: (campaignConfig?.campaign?.opponents || [])
      .map((o) =>
        typeof o === "object" ? o : { name: String(o), fec_id: "", role: "" },
      )
      .filter((o) => o.name),
    opponentsLabel: (campaignConfig?.campaign?.opponents || [])
      .map((o) => (typeof o === "object" ? o.name || "" : String(o)))
      .filter(Boolean)
      .join(", "),
    fundraisingGoal: campaignConfig?.campaign?.fundraising_goal ?? null,
    partyBaseline: campaignConfig?.campaign?.party_baseline || "",
    declarationDate: campaignConfig?.campaign?.declaration_date || "",
    stage: campaignConfig?.campaign?.stage || "",
    displayName:
      campaignConfig?.campaign?.display_name ||
      campaignConfig?.candidate?.full_name ||
      "",
    s3Bucket: campaignConfig?.campaign?.s3_bucket || null,
    district: campaignConfig?.campaign?.office_district || null,
    kalshiSeries: (() => {
      const db = campaignConfig?.campaign?.kalshi_series || [];
      const derived = deriveRaceSeries(
        campaignConfig?.candidate?.state,
        campaignConfig?.campaign?.office,
        campaignConfig?.campaign?.office_district,
      );
      return [...new Set([...derived, ...db])].filter(Boolean);
    })(),
    winredKey: campaignConfig?.campaign?.winred_key || null,
    pollenKey: campaignConfig?.campaign?.pollen_key || null,
    pollenId: campaignConfig?.campaign?.pollen_id || null,
    financeSource: campaignConfig?.campaign?.finance_source || "fec",
  };

  // Resolved campaign data — uses S3-fetched data when available, empty fallback otherwise.
  // Never falls back to data.js constants so wrong-campaign data is never shown.
  const EMPTY_ARR = [];
  const EMPTY_OBJ = {};
  const cd = {
    pollDates: campaignData.polling?.poll_dates || EMPTY_ARR,
    pollAverages: (campaignData.polling?.poll_averages || EMPTY_ARR).map((r) =>
      typeof r === "number" ? { candidate: r } : r || {},
    ),
    favDates: campaignData.polling?.fav_dates || EMPTY_ARR,
    favNet: (campaignData.polling?.fav_net || EMPTY_ARR).map((r) =>
      typeof r === "number" ? { candidate: r } : r || {},
    ),
    pollingData: campaignData.polling?.polling_data || {
      labels: [],
      candidate: [],
      opponent: [],
    },
    fecQuarters: campaignData.fec?.fec_quarters || EMPTY_ARR,
    candidateFR: campaignData.fec?.candidate_fundraising || {
      receipts: [],
      disbursements: [],
      cashOnHand: [],
    },
    opponentFR: campaignData.fec?.opponent_fundraising || EMPTY_OBJ,
    outsideSpend: campaignData.fec?.outside_spending || {
      forCandidate: [],
      againstCandidate: [],
    },
    smallDonors: campaignData.fec?.small_donors || EMPTY_ARR,
    winredMonthly: campaignData.fec?.winred_monthly || {
      labels: [],
      raised: [],
      donorCount: [],
    },
    rawPolls: campaignData.polling?.raw_polls || EMPTY_ARR,
    candidatesGop: (() => {
      const lastNameKey = (n) =>
        (n || "").toLowerCase().trim().split(/\s+/).slice(-1)[0];
      // race_context.candidates: { "Full Name": { role, party, bio_summary, ... } }
      const rcByKey = {};
      Object.entries(campaignData.race_context?.candidates || {}).forEach(
        ([name, v]) => {
          rcByKey[lastNameKey(name)] = v || {};
        },
      );
      // race_status.candidates: [ { name, party, status, notes } ]
      const rsByKey = {};
      (campaignData.race_status?.candidates || []).forEach((c) => {
        rsByKey[lastNameKey(c.name)] = c;
      });
      const STATUS_LABEL = {
        running: "Running",
        lost_primary: "Lost primary",
        lost_runoff: "Lost runoff",
        withdrew: "Withdrew",
        disqualified: "Disqualified",
        endorsed_out: "Endorsed out",
        won: "Won",
      };

      const base = campaignData.field?.candidates_gop?.length
        ? campaignData.field.candidates_gop
        : campaignData.race_finance?.candidates?.length
          ? campaignData.race_finance.candidates
              .filter(
                (c) => !/withdrew|withdraw|disqualified/i.test(c.status || ""),
              )
              .map((c) => ({ name: c.name, role: "", note: "" }))
          : [];
      if (!base.length) return EMPTY_ARR;

      return base.map((c) => {
        const k = lastNameKey(c.name);
        const rc = rcByKey[k] || {};
        const rs = rsByKey[k] || {};
        const role = c.role || rc.role || "";
        const noteParts = [STATUS_LABEL[rs.status] || "", rs.notes].filter(
          Boolean,
        );
        const note = c.note || noteParts.join(" · ") || rc.bio_summary || "";
        return { name: c.name, role, note };
      });
    })(),
    // Full cross-party field for the segmented Campaign field table: every candidate with
    // structured party + race-state status (Ballotpedia source of truth) + note. Distinct from
    // candidatesGop (which drives polling lines and stays party/active-filtered).
    fieldCandidates: (() => {
      const lnk = (n) =>
        (n || "").toLowerCase().trim().split(/\s+/).slice(-1)[0];
      const normParty = (p) => {
        const s = (p || "").toLowerCase();
        if (s.includes("republican") || s === "rep" || s === "r") return "REP";
        if (s.includes("democrat") || s === "dem" || s === "d") return "DEM";
        if (s.includes("libertarian")) return "LIB";
        if (s.includes("green")) return "GRE";
        if (s.includes("independ")) return "IND";
        return (p || "").toUpperCase().slice(0, 3);
      };
      const STATUS_LABEL = {
        running: "Running",
        lost_primary: "Lost primary",
        lost_runoff: "Lost runoff",
        withdrew: "Withdrew",
        disqualified: "Disqualified",
        endorsed_out: "Endorsed out",
        won: "Won",
      };
      const rc = {};
      Object.entries(campaignData.race_context?.candidates || {}).forEach(
        ([n, v]) => {
          rc[lnk(n)] = v || {};
        },
      );
      const rs = {};
      (campaignData.race_status?.candidates || []).forEach((c) => {
        rs[lnk(c.name)] = c;
      });
      // Union everyone across race_finance + race_status + field.json (dedupe by last name)
      const seen = {};
      const base = [];
      const add = (name, party, status) => {
        const k = lnk(name);
        if (name && !seen[k]) {
          seen[k] = 1;
          base.push({ name, party, status });
        }
      };
      (campaignData.race_finance?.candidates || []).forEach((c) =>
        add(c.name, c.party, c.status),
      );
      (campaignData.race_status?.candidates || []).forEach((c) =>
        add(c.name, c.party, c.status),
      );
      (campaignData.field?.candidates_gop || []).forEach((c) =>
        add(c.name, c.party, null),
      );
      if (!base.length) return EMPTY_ARR;
      return base.map((c) => {
        const k = lnk(c.name);
        const status = (rs[k]?.status || c.status || "").toLowerCase();
        return {
          name: c.name,
          party: normParty(rs[k]?.party || c.party || rc[k]?.party || ""),
          status,
          statusLabel: STATUS_LABEL[status] || "",
          note: rs[k]?.notes || rc[k]?.bio_summary || "",
        };
      });
    })(),
    candidatesDem: campaignData.field?.candidates_dem || EMPTY_ARR,
    issueSalience: campaignData.strategy?.issue_salience || EMPTY_ARR,
    fundraisingDrivers: campaignData.strategy?.fundraising_drivers || EMPTY_ARR,
    donorCohorts: campaignData.strategy?.donor_cohorts || EMPTY_ARR,
    voterRegDates: campaignData.voter_reg?.voter_reg_dates || EMPTY_ARR,
    voterRegNet: campaignData.voter_reg?.voter_reg_net || EMPTY_ARR,
    pastResults: campaignData.election_history?.past_results || EMPTY_ARR,
    electionHistory:
      campaignData.election_history?.election_history || EMPTY_ARR,
    race_context: campaignData.race_context ?? null,
    raceStatus: campaignData.race_status ?? null,
    race_finance: campaignData.race_finance ?? null,
    policyPositions:
      policyPositions?.positions || campaignData.policy?.positions || EMPTY_ARR,
    policyOpponent: policyPositions?.opponent || null,
    policyOpponentParty: policyPositions?.opponentParty || null,
    freshness: {
      polling:
        campaignData.polling?.uploaded_at ||
        campaignData.polling?.generated_at ||
        null,
      fec: campaignData.fec?.fetched_at || null,
      field:
        campaignData.field?.generated_at ||
        campaignData.field?.uploaded_at ||
        null,
      strategy:
        campaignData.strategy?.generated_at ||
        campaignData.strategy?.uploaded_at ||
        null,
      voter_reg:
        campaignData.voter_reg?.uploaded_at ||
        campaignData.voter_reg?.generated_at ||
        null,
      election_history: campaignData.election_history?.generated_at || null,
      race_context: campaignData.race_context?.generated_at || null,
      race_finance: campaignData.race_finance?.generated_at || null,
    },
    loaded: !campaignDataLoading && Object.keys(campaignData).length > 0,
  };

  const setPrimaryTab = (newTab) => {
    _setTab(newTab);
    if (newTab !== "Daily Brief") setBriefSubTab("Overview");
    writePath(newTab, newTab === "Voters" ? { subView: donorsSubView } : {});
  };
  const setSubTab = (sub) => {
    setBriefSubTab(sub);
    writePath("Daily Brief", { briefSubTab: sub });
  };
  const BRIEF_SUB_TABS = [
    "Overview",
    "Prediction Markets",
    "Polling",
    "Finance",
    "Campaign",
    "Policy",
    "News",
  ];
  const effectiveTab =
    tab === "Daily Brief" && briefSubTab !== "Overview" ? briefSubTab : tab;

  const openProfile = (email, row) => {
    setProfileEmail(email);
    setProfileRow(row || null);
    writePath("Voters", { email });
  };
  const closeProfile = () => {
    setProfileEmail(null);
    setProfileRow(null);
    writePath("Voters", { subView: donorsSubView });
  };

  const handleSubViewChange = (sv) => {
    setDonorsSubView(sv);
    writePath("Voters", { subView: sv });
  };

  // Sync state on browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const {
        tab: t,
        briefSubTab: bst,
        subView: sv,
        email,
        socialMode: sm,
        socialCollectionId: scid,
      } = readPath();
      _setTab(t);
      setBriefSubTab(bst || "Overview");
      setDonorsSubView(sv);
      setSocialMode(sm || "monitor");
      setSelectedCollectionId(scid || null);
      if (email) {
        setProfileEmail(email);
      } else {
        setProfileEmail(null);
        setProfileRow(null);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Fetch campaign + candidate config from DB whenever active campaign changes
  const fetchCampaignConfig = useCallback(() => {
    if (!isLoggedIn || !activeCampaignId || !PHP_API_URL) return;
    setCampaignConfig(null);
    authFetch(`${PHP_API_URL}/campaigns/${activeCampaignId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.campaign) setCampaignConfig(d);
      })
      .catch(() => {});
  }, [activeCampaignId, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCampaignConfig();
  }, [fetchCampaignConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open donor profile from URL on first load
  useEffect(() => {
    const { email } = readPath();
    if (email) {
      setProfileEmail(email);
      setProfileRow(null);
    }
    // Normalise path if landing on root
    if (window.location.pathname === "/") writePath(tab);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefer the date-derived stage; if dates aren't configured, fall back to the Ballotpedia
  // race_status stage (ARCHITECTURE: race_status is the source of truth for race state) so the
  // card is never blank when we actually know the stage.
  const _stageByDate = detectStage(cfg.primaryDate, cfg.electionDate);
  const _rsStage = cd.raceStatus?.race_stage;
  const stage =
    _stageByDate.label && _stageByDate.label !== "—"
      ? _stageByDate
      : _rsStage
        ? {
            label: String(_rsStage).replace(/^\w/, (ch) => ch.toUpperCase()),
            color: C.orange,
          }
        : _stageByDate;
  const daysToElection = daysUntil(cfg.electionDate);
  const daysToPrimary = daysUntil(cfg.primaryDate);

  useEffect(() => {
    if (!isLoggedIn || !KALSHI_URL) return;
    if (!campaignConfig) {
      // Campaign config cleared (initial load or campaign switch) — reset and wait for real config
      setKalshi(null);
      setKalshiHistory(null);
      setKalshiLoading(true);
      setHistoryLoading(true);
      return;
    }

    setKalshiLoading(true);
    setHistoryLoading(true);

    (async () => {
      let campaignSeries = cfg.kalshiSeries || [];

      // Auto-discover: if no campaign series configured, derive from state/office first,
      // then ask the PHP API (which uses same derive logic + Kalshi validation).
      if (!campaignSeries.length && cfg.state && cfg.office) {
        campaignSeries =
          deriveRaceSeries(cfg.state, cfg.office, cfg.district) || [];
        if (!campaignSeries.length) {
          // PHP derive as fallback (handles edge-case offices the JS logic may miss)
          try {
            const params = new URLSearchParams({
              discover: cfg.candidateName || "",
              state: cfg.state,
              office: cfg.office,
              ...(cfg.district ? { district: cfg.district } : {}),
            });
            const dr = await fetch(`${KALSHI_URL}?${params}`).then((r) =>
              r.json(),
            );
            campaignSeries = dr.series || [];
          } catch {}
        }
      }

      const allSeries = [
        ...new Set([...KALSHI_MACRO_SERIES, ...campaignSeries]),
      ].join(",");
      const histSeries = [
        ...new Set([...KALSHI_HISTORY_SERIES, ...campaignSeries]),
      ].join(",");

      const [seriesRes, histRes] = await Promise.allSettled([
        fetch(`${KALSHI_URL}?series=${allSeries}`).then((r) => r.json()),
        fetch(`${KALSHI_URL}?history=${histSeries}&debug=1`).then((r) =>
          r.json(),
        ),
      ]);

      if (seriesRes.status === "fulfilled") {
        // Re-discover if the primary R series (candidate win probability) has no markets.
        // Check only the R series — don't block on DEM/Senate series having data.
        const rSeriesTicker = campaignSeries.find((s) => /R$/.test(s));
        const noRaceMarkets =
          !!rSeriesTicker &&
          !seriesRes.value?.series?.[rSeriesTicker]?.markets?.length;
        if (noRaceMarkets && cfg.state && cfg.office) {
          try {
            // Pass structured state+office — PHP derives the correct ticker, no text search
            const params = new URLSearchParams({
              discover: cfg.candidateName || "",
              state: cfg.state,
              office: cfg.office,
              ...(cfg.district ? { district: cfg.district } : {}),
            });
            const dr = await fetch(`${KALSHI_URL}?${params}`).then((r) =>
              r.json(),
            );
            if (dr.series?.length) {
              const retryAllSeries = [
                ...new Set([...KALSHI_MACRO_SERIES, ...dr.series]),
              ].join(",");
              const retry = await fetch(
                `${KALSHI_URL}?series=${retryAllSeries}`,
              ).then((r) => r.json());
              setKalshi(retry);
              setKalshiFetchedAt(new Date());
              setKalshiLoading(false);
            } else {
              setKalshi(seriesRes.value);
              setKalshiFetchedAt(new Date());
              setKalshiLoading(false);
            }
          } catch {
            setKalshi(seriesRes.value);
            setKalshiFetchedAt(new Date());
            setKalshiLoading(false);
          }
        } else {
          setKalshi(seriesRes.value);
          setKalshiFetchedAt(new Date());
          setKalshiLoading(false);
        }
      } else {
        setKalshiLoading(false);
      }

      if (histRes.status === "fulfilled") {
        setKalshiHistory(histRes.value);
        if (histRes.value.diagnostics)
          console.warn(
            "[Kalshi history diagnostics]",
            histRes.value.diagnostics,
          );
      }
      setHistoryLoading(false);
    })();
  }, [kalshiRefresh, isLoggedIn, activeCampaignId, campaignConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-campaign structured data from S3 (polling, fec, field, strategy, voter_reg, election_history)
  useEffect(() => {
    if (!isLoggedIn || !activeCampaignId) return;
    if (!PHP_API_URL) return;
    setCampaignDataLoading(true);
    setCampaignData({});
    // `policy` omitted — data/policy.json is never generated (policy comes from the policy_positions
    // intelligence call instead); requesting it only produced a noisy 404.
    const types = [
      "polling",
      "fec",
      "field",
      "strategy",
      "voter_reg",
      "election_history",
      "race_context",
      "race_status",
      "race_finance",
    ];
    Promise.allSettled(
      types.map((type) =>
        authFetch(`${PHP_API_URL}/campaigns/data/${activeCampaignId}/${type}`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
          .then((d) => ({ type, data: d?.data ?? d })),
      ),
    ).then((results) => {
      const merged = {};
      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value.data)
          merged[r.value.type] = r.value.data;
      });
      setCampaignData(merged);
      setCampaignDataLoading(false);
    });
  }, [isLoggedIn, activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshFecData = useCallback(() => {
    if (!PHP_API_URL || !activeCampaignId) return;
    // Small delay to ensure S3 has propagated the new file before reading
    setTimeout(() => {
      authFetch(`${PHP_API_URL}/campaigns/data/${activeCampaignId}/fec`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.data) setCampaignData((prev) => ({ ...prev, fec: d.data }));
        })
        .catch(() => {});
    }, 2000);
  }, [activeCampaignId]);
  refreshFecRef.current = refreshFecData;

  const reloadPolling = useCallback(() => {
    if (!PHP_API_URL || !activeCampaignId) return;
    setTimeout(() => {
      authFetch(`${PHP_API_URL}/campaigns/data/${activeCampaignId}/polling`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.data)
            setCampaignData((prev) => ({ ...prev, polling: d.data }));
        })
        .catch(() => {});
    }, 500);
  }, [activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveManualPolls = useCallback(
    (polls) => {
      const dataSyncUrl = PHP_API_URL ? `${PHP_API_URL}/data-sync` : "";
      if (!dataSyncUrl || !activeCampaignId) return Promise.reject("No URL");
      return authFetch(`${dataSyncUrl}/polls?campaign_id=${activeCampaignId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ polls }),
      })
        .then((r) =>
          r.ok
            ? r.json()
            : r.json().then((e) => Promise.reject(e?.error || "Save failed")),
        )
        .then(() => {
          reloadPolling();
        });
    },
    [activeCampaignId, reloadPolling],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Build the COMPLETE intelligence context — every data source, every tab
  const buildIntelContext = ({
    cfg: c,
    cd: d,
    kalshi: k,
    winred: w,
    liveFEC: fec,
  }) => {
    // ── FEC / CoH resolution ──────────────────────────────────────────────────
    const lastQ = Math.max(0, (d.fecQuarters?.length || 1) - 1);
    const opp1Key = Object.keys(d.opponentFR || {})[0];
    const candCoh =
      fec?.totals?.[c.candidateName]?.cash_on_hand ??
      (d.candidateFR?.cashOnHand || [])[lastQ] ??
      0;
    const oppCoh = opp1Key
      ? ((d.opponentFR[opp1Key]?.cashOnHand || [])[lastQ] ?? 0)
      : 0;
    const fecTotals = { [c.candidateName]: { cash_on_hand: candCoh } };
    if (opp1Key && oppCoh > 0) fecTotals[opp1Key] = { cash_on_hand: oppCoh };

    // ── WinRed — exclude current (partial) month ──────────────────────────────
    const cdMonthly = d.winredMonthly;
    const _mn = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const _now = new Date();
    const _curLabel = `${_mn[_now.getMonth()]} '${String(_now.getFullYear()).slice(2)}`;
    const winredResolved = (() => {
      if (!w) {
        const labels = cdMonthly.labels || [];
        const raised = cdMonthly.raised || [];
        return {
          totalRaised: raised.reduce((s, v) => s + v, 0),
          totalDonors: (cdMonthly.donorCount || []).reduce((s, v) => s + v, 0),
          allMonths: labels.map((m, i) => ({
            month: m,
            raised: raised[i] || 0,
          })),
          lastMonth: { month: labels.at(-1), raised: raised.at(-1) },
          source: "static",
        };
      }
      const _completed = (w.monthly || []).filter((m) => m.month !== _curLabel);
      return {
        totalRaised: w.summary?.total_raised,
        totalDonors: w.summary?.unique_donors,
        allMonths: _completed,
        lastMonth: _completed[_completed.length - 1],
        recentMonths: _completed.slice(-3),
      };
    })();

    // ── FEC quarterly history ─────────────────────────────────────────────────
    const fecHistory = (d.fecQuarters || []).map((q, i) => ({
      quarter: q,
      candidateCoh: (d.candidateFR?.cashOnHand || [])[i] ?? null,
      candidateReceipts: (d.candidateFR?.receipts || [])[i] ?? null,
      ...(opp1Key
        ? {
            [opp1Key + "_coh"]:
              (d.opponentFR[opp1Key]?.cashOnHand || [])[i] ?? null,
          }
        : {}),
    }));

    // ── Polling — full history + favorability ─────────────────────────────────
    const pollHistory = (d.pollDates || []).map((dt, i) => ({
      date: dt,
      averages: d.pollAverages[i] || {},
    }));
    const favHistory = (d.favDates || []).map((dt, i) => {
      const fv = d.favNet[i];
      return {
        date: dt,
        net:
          fv != null && typeof fv === "object"
            ? (fv.candidate ?? null)
            : (fv ?? null),
      };
    });

    return {
      campaign: {
        name: c.candidateName,
        party: c.party,
        office: c.office,
        state: c.state,
        daysToPrimary: daysUntil(c.primaryDate),
        daysToElection: daysUntil(c.electionDate),
        primaryDate: c.primaryDate,
        electionDate: c.electionDate,
        opponents: c.opponents || "",
        fundraisingGoal: c.fundraisingGoal,
      },
      kalshi: k,
      winred: winredResolved,
      fec: { totals: fecTotals, history: fecHistory },
      polling: {
        history: pollHistory,
        latest: d.pollAverages[d.pollAverages.length - 1] || {},
        favorability: favHistory,
        latestFav: (() => {
          const lf = d.favNet[d.favNet.length - 1];
          return lf != null && typeof lf === "object"
            ? (lf.candidate ?? null)
            : (lf ?? null);
        })(),
      },
      field: {
        gop: d.candidatesGop,
        dem: d.candidatesDem,
      },
      strategy: {
        issueSalience: d.issueSalience,
        fundraisingDrivers: d.fundraisingDrivers,
        policyPositions: d.policyPositions,
      },
      voterReg: (d.voterRegDates || []).map((dt, i) => {
        const vr = d.voterRegNet[i];
        const net = Array.isArray(vr)
          ? (vr[0] ?? 0) - (vr[1] ?? 0)
          : (vr ?? null);
        return { date: dt, net };
      }),
      electionHistory: d.electionHistory,
      raceContext: d.race_context ?? null,
    };
  };

  // Campaign brief — POST to trigger generation; response arrives on campaign-{id} PubNub channel
  // Dep tabs the brief synthesizes from — must match the backend cascade in intelligence.php.
  const BRIEF_DEP_TABS = [
    ["prediction-markets", "Prediction Markets"],
    ["finance", "Finance"],
    ["campaign", "Campaign"],
    ["policy", "Policy"],
    ["news", "News"],
  ];

  const triggerBriefGeneration = () => {
    // Pre-mark tabs that are already fresh as done — the backend reuses those (no tab-done
    // fires for them), so the cascade progress should show them already complete.
    const done = {};
    BRIEF_DEP_TABS.forEach(([slug, label]) => {
      const s = tabSummaries[label];
      if (s?.generated_at && !isStale6am(s.generated_at)) done[slug] = true;
    });
    setBriefBuildDone(done);
    setBriefLoading(true);
    authFetch(`${intelUrl}/${activeCampaignId}/campaign_brief`, {
      method: "POST",
      body: JSON.stringify({}),
    }).catch(() => setBriefLoading(false));
  };

  const triggerPollingSummaryGeneration = () => {
    if (!intelUrl) return;
    setPollingSummary(null);
    setPollingSummaryLoading(true);
    authFetch(`${intelUrl}/${activeCampaignId}/polling_summary`, {
      method: "POST",
      body: JSON.stringify({}),
    }).catch(() => setPollingSummaryLoading(false));
  };

  useEffect(() => {
    if (!isLoggedIn || !campaignConfig || !intelUrl) return;
    setPollingSummary(null);
    authFetch(`${intelUrl}/${activeCampaignId}/polling_summary`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const r = d?.result;
        if (r?.status === "completed") {
          setPollingSummary({
            ...r.data,
            status: r.status,
            generated_at: r.generated_at,
          });
          if (isStale6am(r.generated_at)) triggerPollingSummaryGeneration();
        } else {
          triggerPollingSummaryGeneration();
        }
      })
      .catch(() => triggerPollingSummaryGeneration());
  }, [isLoggedIn, campaignConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const TAB_SLUG_MAP = {
    "Prediction Markets": "prediction-markets",
    Finance: "finance",
    Campaign: "campaign",
    Policy: "policy",
    News: "news",
  };
  const TAB_S3_KEY = {
    "Prediction Markets": "tab-prediction-markets",
    Finance: "tab-finance",
    Campaign: "tab-campaign",
    Policy: "tab-policy",
  };
  const TAB_NAME_MAP = {
    "prediction-markets": "Prediction Markets",
    finance: "Finance",
    campaign: "Campaign",
    policy: "Policy",
    news: "News",
  };

  // Single persistent PubNub subscription per campaign — all intelligence actions route through here
  useEffect(() => {
    const pubnubSubKey = process.env.REACT_APP_PUBNUB_SUB_KEY;
    if (!pubnubSubKey || !activeCampaignId || !isLoggedIn) return;
    const pn = new PubNub({ subscribeKey: pubnubSubKey, userId: "pollen-app" });
    campaignPnRef.current = pn;
    pn.addListener({
      message: ({ message: msg }) => {
        const { action, data } = msg;
        if (action === "brief-done") {
          setCampaignBrief({ ...data, status: "completed" });
          setBriefLoading(false);
          setBriefBuildDone({});
        }
        if (action === "brief-error") {
          setBriefLoading(false);
          setBriefBuildDone({});
        }
        if (action === "polling-summary-done") {
          setPollingSummary({ ...data, status: "completed" });
          setPollingSummaryLoading(false);
        }
        if (action === "polling-summary-error") setPollingSummaryLoading(false);
        if (action === "tab-done") {
          const tabName = TAB_NAME_MAP[data._tab];
          if (tabName) {
            setTabSummaries((p) => ({
              ...p,
              [tabName]: { ...data, status: "completed" },
            }));
            setTabSummaryLoading((p) => ({ ...p, [tabName]: false }));
          }
          if (data._tab)
            setBriefBuildDone((p) => ({ ...p, [data._tab]: true }));
        }
        if (action === "tab-error") {
          const tabName = TAB_NAME_MAP[data._tab];
          if (tabName)
            setTabSummaryLoading((p) => ({ ...p, [tabName]: false }));
        }
        if (action === "fec-sync-done") {
          refreshFecRef.current?.();
          fetchCampaignConfig();
        }
        if (action === "sync_complete") {
          setSocialSyncing(false);
          setSocialSyncResult({
            inserted: data.new_mentions,
            terms_synced: data.terms_synced,
          });
          fetchSocial();
          fetchTerms();
          setSocialSyncTick((t) => t + 1); // refresh the selected-term feed (count + list) post-sync
        }
        if (action === "opponent-done") {
          setOpponentIntel(data);
          setOpponentIntelLoading(false);
        }
        if (action === "opponent-error") setOpponentIntelLoading(false);
        if (action === "policy-done") {
          setPolicyPositions(data);
          setPolicyPositionsLoading(false);
        }
        if (action === "policy-error") setPolicyPositionsLoading(false);
        if (
          action === "messaging-intel-done" ||
          action === "messaging-intel-error"
        ) {
          messagingIntelCbRef.current?.({ action, data });
        }
        if (action === "image-done" || action === "image-error") {
          messagingImageCbRef.current?.({ action, data });
        }
        // In-flight events also flip socialPolling back on so the indicator survives a page
        // reload / tab navigation — the background poll keeps publishing, and the next event
        // restores the UI even though the click-time state was lost.
        if (action === "poll-preparing") {
          setSocialPolling(true);
          setSocialPollProgress({ phase: "building", processed: 0, total: 0 });
        }
        if (action === "poll-ready") {
          setSocialPolling(true);
          setSocialPollProgress({
            phase: "polling",
            processed: 0,
            total: data.total,
          });
        }
        if (action === "poll-progress") {
          setSocialPolling(true);
          setSocialPollProgress({
            phase: "polling",
            processed: data.processed,
            total: data.total,
          });
        }
        if (action === "poll-done") {
          setSelectedCollectionId(data.collection_id);
          fetchCollections(data.audience_id);
          fetchCollectionAnswers(data.collection_id);
          setSocialPolling(false);
          setSocialPollProgress(null);
        }
        if (action === "insights-done") {
          fetchCollectionAnswers(data.collection_id);
        }
        if (action === "poll-error") {
          setSocialError(data.error || "Poll failed");
          setSocialPolling(false);
          setSocialPollProgress(null);
        }
        if (action === "bulk-reply-progress") {
          setBulkReplyJob((prev) =>
            prev
              ? {
                  ...prev,
                  sent: data.sent,
                  failed: data.failed,
                  status: "running",
                }
              : null,
          );
        }
        if (action === "bulk-reply-done") {
          setBulkReplyJob((prev) =>
            prev
              ? {
                  ...prev,
                  sent: data.sent || prev.sent,
                  failed: data.failed || prev.failed,
                  status: data.error ? "failed" : "complete",
                }
              : null,
          );
        }
        if (action === "collect-all-start") {
          setCollectAllProgress({ processed: 0, total: data.total });
        }
        if (action === "collect-all-progress") {
          setCollectAllProgress({
            processed: data.processed,
            total: data.total,
          });
        }
        if (action === "collect-all-done") {
          setCollectingAllTermId(null);
          setCollectAllProgress(null);
          fetchAudiences();
        }
        if (action === "enrich-identity-start") {
          setSocialEnrichProgress({
            processed: 0,
            total: data.total,
            enriched: 0,
            matched: 0,
          });
        }
        if (action === "enrich-identity-progress") {
          setSocialEnrichProgress({
            processed: data.processed,
            total: data.total,
            enriched: data.enriched,
            matched: data.matched,
          });
        }
        if (action === "enrich-identity-done") {
          setSocialEnriching(false);
          setSocialEnrichProgress(null);
          fetchDonorLinks();
          setLeadsRefreshTick((t) => t + 1);
        }
        if (action === "enrich-identity-error") {
          setSocialEnriching(false);
          setSocialEnrichProgress(null);
          setSocialError(data.error || "Identity matching failed");
        }
        if (action === "enrich-locations-start") {
          setSocialLocating(true);
          setSocialEnrichProgress({
            processed: 0,
            enriched: 0,
            total: data.total,
            matched: 0,
          });
        }
        if (action === "enrich-locations-progress") {
          setSocialEnrichProgress({
            processed: data.processed,
            enriched: data.enriched,
            total: data.total,
            matched: 0,
          });
        }
        if (action === "enrich-locations-done") {
          setSocialLocating(false);
          setSocialEnrichProgress(null);
          setLeadsRefreshTick((t) => t + 1);
        }
        if (action === "enrich-locations-error") {
          setSocialLocating(false);
          setSocialEnrichProgress(null);
          setSocialError(data.error || "Location enrichment failed");
        }
        if (action === "backfill-term-done") {
          setLeadsRefreshTick((t) => t + 1);
        }
        // Background reply crawl (collectReplies) — live count for the Collect view.
        if (action === "collect_progress") {
          setSocialCollectProgress({
            tweetId: data.source_tweet_id,
            collected: data.collected || 0,
            done: false,
          });
        }
        if (action === "collect_complete") {
          setSocialCollectProgress({
            tweetId: data.source_tweet_id,
            collected: data.total_replies || 0,
            done: true,
          });
          // The foreground preview only loaded the first batch; pull the FULL stored reply set
          // so the Browse-Replies list + count reflect everything collected (e.g. 2,391).
          if (data.source_tweet_id && SOCIAL_API_URL) {
            authFetch(
              `${SOCIAL_API_URL}/leads?campaign_id=${activeCampaignId}&source_tweet_id=${data.source_tweet_id}`,
            )
              .then((r) => r.json())
              .then((d) => {
                if (Array.isArray(d.leads)) setSocialLeads(d.leads);
              })
              .catch(() => {});
          }
        }
      },
    });
    pn.subscribe({ channels: [`campaign-${activeCampaignId}`] });
    return () => {
      pn.unsubscribeAll();
      pn.destroy();
      campaignPnRef.current = null;
    };
  }, [activeCampaignId, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerTabSummary = (tabName, keepExisting = false) => {
    if (!intelUrl) return;
    if (!keepExisting) setTabSummaries((p) => ({ ...p, [tabName]: null }));
    setTabSummaryLoading((p) => ({ ...p, [tabName]: true }));
    authFetch(
      `${intelUrl}/${activeCampaignId}/tab_summary/${TAB_SLUG_MAP[tabName]}`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    ).catch(() => setTabSummaryLoading((p) => ({ ...p, [tabName]: false })));
  };

  const generateOpponentIntel = () => {
    if (!intelUrl) return;
    setOpponentIntelLoading(true);
    authFetch(`${intelUrl}/${activeCampaignId}/opponent_analysis`, {
      method: "POST",
      body: JSON.stringify({}),
    }).catch(() => setOpponentIntelLoading(false));
  };

  useEffect(() => {
    if (!isLoggedIn || !campaignConfig || !intelUrl) return;
    authFetch(`${intelUrl}/${activeCampaignId}/opponent_analysis`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const r = d?.result;
        if (r?.status === "completed")
          setOpponentIntel({
            ...r.data,
            status: r.status,
            generated_at: r.generated_at,
          });
      })
      .catch(() => {});
  }, [isLoggedIn, campaignConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const generatePolicyPositions = () => {
    if (!intelUrl) return;
    setPolicyPositionsLoading(true);
    authFetch(`${intelUrl}/${activeCampaignId}/policy_positions`, {
      method: "POST",
      body: JSON.stringify({}),
    }).catch(() => setPolicyPositionsLoading(false));
  };

  useEffect(() => {
    if (!isLoggedIn || !campaignConfig || !intelUrl) return;
    setPolicyPositions(null);
    authFetch(`${intelUrl}/${activeCampaignId}/policy_positions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const r = d?.result;
        if (r?.status === "completed") {
          setPolicyPositions({
            ...r.data,
            status: r.status,
            generated_at: r.generated_at,
          });
          if (isStale6am(r.generated_at)) generatePolicyPositions();
        } else {
          generatePolicyPositions();
        }
      })
      .catch(() => generatePolicyPositions());
  }, [isLoggedIn, campaignConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoggedIn || !campaignConfig || !intelUrl) return;
    if (!cd.loaded) return; // wait for campaign data before generating tab summaries
    setTabSummaries({});
    Object.keys(TAB_SLUG_MAP).forEach((tabName) => {
      authFetch(
        `${intelUrl}/${activeCampaignId}/tab_summary/${TAB_SLUG_MAP[tabName]}`,
      )
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const r = d?.result;
          if (r?.status === "completed" && r?.data?.headline) {
            setTabSummaries((p) => ({
              ...p,
              [tabName]: {
                ...r.data,
                status: r.status,
                generated_at: r.generated_at,
              },
            }));
            if (isStale6am(r.generated_at)) triggerTabSummary(tabName, true);
          } else {
            triggerTabSummary(tabName);
          }
        })
        .catch(() => triggerTabSummary(tabName));
    });
  }, [isLoggedIn, activeCampaignId, !!campaignConfig, cd.loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoggedIn || !campaignConfig || !kalshi) return;
    if (!intelUrl) return;
    setCampaignBrief(null);
    setBriefLoading(true);
    authFetch(`${intelUrl}/${activeCampaignId}/campaign_brief`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const r = d?.result;
        if (r?.status === "completed") {
          setCampaignBrief({
            ...r.data,
            status: r.status,
            generated_at: r.generated_at,
          });
          if (isStale6am(r.generated_at)) {
            triggerBriefGeneration();
          } else {
            setBriefLoading(false);
          }
        } else {
          triggerBriefGeneration();
        }
      })
      .catch(() => triggerBriefGeneration());
  }, [kalshi, campaignConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoggedIn) return;
    const winredUrl = PHP_API_URL ? `${PHP_API_URL}/winred` : null;
    if (!winredUrl || !activeCampaignId || !cfg.winredKey) {
      setWinredData(null);
      setWinredDaily(null);
      setDonorCohorts(null);
      setWinredLoading(false);
      return;
    }
    setWinredLoading(true);
    authFetch(`${winredUrl}?campaign_id=${activeCampaignId}`)
      .then((r) => r.json())
      .then((d) => {
        setWinredData(d);
        setWinredLoading(false);
      })
      .catch(() => setWinredLoading(false));
    setWinredDailyLoading(true);
    authFetch(`${winredUrl}?campaign_id=${activeCampaignId}&type=daily`)
      .then((r) => r.json())
      .then((d) => {
        setWinredDaily(d);
        setWinredDailyLoading(false);
      })
      .catch(() => setWinredDailyLoading(false));
    authFetch(`${winredUrl}?campaign_id=${activeCampaignId}&type=donors&page=1`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.cohort_counts) setDonorCohorts(d.cohort_counts);
      })
      .catch(() => {});
  }, [isLoggedIn, activeCampaignId, cfg.winredKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoggedIn || !activeCampaignId) return;
    setLiveFEC(null); // clear stale data from previous campaign
    const dataSyncUrl = PHP_API_URL ? `${PHP_API_URL}/data-sync` : "";
    if (!dataSyncUrl) return;
    authFetch(`${dataSyncUrl}/polls?campaign_id=${activeCampaignId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.averages?.length) setLivePolls(d);
      })
      .catch(() => {});
  }, [activeCampaignId, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshPolls = () => {
    const dataSyncUrl = PHP_API_URL ? `${PHP_API_URL}/data-sync` : "";
    if (!dataSyncUrl || !activeCampaignId || pollsRefreshing) return;
    setPollsRefreshing(true);
    authFetch(
      `${dataSyncUrl}/polls?campaign_id=${activeCampaignId}&refresh=1&bust=${Date.now()}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.averages?.length) setLivePolls(d);
      })
      .catch(() => {})
      .finally(() => {
        setPollsRefreshing(false);
        triggerPollingSummaryGeneration();
      });
  };

  const fetchNews = (refresh = false) => {
    const base = PHP_API_URL ? `${PHP_API_URL}/data-sync` : "";
    const candidateName =
      selectedCampaign?.candidate_name || cfg.candidateName || "";
    if (!base || !activeCampaignId || !candidateName) return;
    setNewsLoading(true);
    const cfgOpps = (Array.isArray(cfg.opponents) ? cfg.opponents : [])
      .map((o) =>
        typeof o === "object"
          ? o.name || ""
          : typeof o === "number" || /^\d+$/.test(String(o))
            ? ""
            : String(o),
      )
      .filter(Boolean);
    // Use coh_ranking to find top opponent by financial significance (avoids fringe candidates with common names)
    const rfCohRanking = cd.race_finance?.field_analytics?.coh_ranking || [];
    const rfCands = cd.race_finance?.candidates || [];
    const rfTopOpp = rfCohRanking.find(
      (c) =>
        !rfCands.find((x) => x.name === c.name && x.role === "our_candidate"),
    );
    const rfOpps = rfTopOpp?.name ? [rfTopOpp.name] : [];
    const frOpps = Object.keys(cd.opponentFR || {});
    const opponentNames = (
      cfgOpps.length ? cfgOpps : rfOpps.length ? rfOpps : frOpps
    )
      .slice(0, 1)
      .join(",");
    const params = new URLSearchParams({
      campaign_id: activeCampaignId,
      candidate: candidateName,
      ...(opponentNames ? { opponents: opponentNames } : {}),
      ...(refresh ? { refresh: "1" } : {}),
    });
    authFetch(`${base}/news?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.articles) setNewsData(d);
      })
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  };

  // ── Social fetch / sync ────────────────────────────────────────────────────
  const fetchSocial = useCallback(async () => {
    if (!SOCIAL_API_URL || !activeCampaignId) return;
    setSocialLoading(true);
    setSocialError(null);
    try {
      const params = new URLSearchParams({
        campaign_id: activeCampaignId,
        limit: 500,
      });
      const res = await authFetch(`${SOCIAL_API_URL}?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load mentions");
      setSocialMentions(data.mentions || []);
      setSocialLastSynced(data.last_synced_at || null);
    } catch (e) {
      setSocialError(e.message);
    } finally {
      setSocialLoading(false);
    }
  }, [activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTermMentions = useCallback(
    async (termId) => {
      if (!SOCIAL_API_URL || !activeCampaignId || !termId) return;
      try {
        const params = new URLSearchParams({
          campaign_id: activeCampaignId,
          term_id: termId,
          limit: 2000,
        });
        const res = await authFetch(`${SOCIAL_API_URL}?${params}`);
        const data = await res.json();
        if (res.ok) setSocialTermMentions(data.mentions || []);
      } catch {
        /* silent */
      }
    },
    [activeCampaignId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTerms = useCallback(async () => {
    if (!SOCIAL_API_URL || !activeCampaignId) return;
    setSocialTermsLoading(true);
    try {
      const res = await authFetch(
        `${SOCIAL_API_URL}/terms?campaign_id=${activeCampaignId}`,
      );
      const data = await res.json();
      setSocialTerms(data.terms || []);
    } catch {
      /* silent */
    } finally {
      setSocialTermsLoading(false);
    }
  }, [activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTerm = useCallback(
    async (query, monitorType = "mention") => {
      if (!SOCIAL_API_URL || !activeCampaignId || !query.trim()) return;
      const res = await authFetch(`${SOCIAL_API_URL}/terms`, {
        method: "POST",
        body: JSON.stringify({
          campaign_id: activeCampaignId,
          query: query.trim(),
          monitor_type: monitorType,
        }),
      });
      const data = await res.json();
      await fetchTerms();
      await fetchSocial();
      return data;
    },
    [activeCampaignId, fetchTerms, fetchSocial],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteTerm = useCallback(
    async (termId) => {
      if (!SOCIAL_API_URL) return;
      await authFetch(`${SOCIAL_API_URL}/terms`, {
        method: "DELETE",
        body: JSON.stringify({ id: termId }),
      });
      await fetchTerms();
      setSocialSelectedTerm(null);
    },
    [fetchTerms],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTerm = useCallback(
    async (termId, query) => {
      if (!SOCIAL_API_URL || !query.trim()) return;
      const res = await authFetch(`${SOCIAL_API_URL}/terms`, {
        method: "PUT",
        body: JSON.stringify({ id: termId, query: query.trim() }),
      });
      const data = await res.json();
      if (data.term)
        setSocialTerms((prev) =>
          prev.map((t) => (t.id === termId ? data.term : t)),
        );
      await fetchSocial();
    },
    [setSocialTerms, fetchSocial],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshTerm = useCallback(
    async (termId) => {
      if (!SOCIAL_API_URL || !activeCampaignId) return;
      const syncRes = await authFetch(`${SOCIAL_API_URL}/sync`, {
        method: "POST",
        body: JSON.stringify({
          campaign_id: activeCampaignId,
          term_id: termId,
          backfill: true, // ↻ = deep history crawl (ignore watermark); plain Sync stays incremental
        }),
      });
      // 202 = async; sync-done PubNub message will trigger fetchSocial/fetchTerms
      // Non-202 = sync completed synchronously; re-fetch now
      if (syncRes.status !== 202) {
        fetchSocial();
        fetchTermMentions(termId);
        try {
          const res = await authFetch(
            `${SOCIAL_API_URL}/terms?campaign_id=${activeCampaignId}`,
          );
          const data = await res.json();
          if (data.terms) {
            const updated = data.terms.find((t) => t.id === termId);
            if (updated)
              setSocialTerms((prev) =>
                prev.map((t) => (t.id === termId ? updated : t)),
              );
          }
        } catch {
          /* silent */
        }
      }
    },
    [activeCampaignId, fetchSocial, fetchTermMentions, setSocialTerms],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const syncSocial = useCallback(
    async (platform = "x") => {
      if (!SOCIAL_API_URL || !activeCampaignId || socialSyncing) return;
      setSocialSyncing(true);
      setSocialSyncResult(null);
      setSocialError(null);
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/sync`, {
          method: "POST",
          body: JSON.stringify({ campaign_id: activeCampaignId, platform }),
        });
        const data = await res.json();
        if (!res.ok && res.status !== 202)
          throw new Error(data.error || "Sync failed");
        if (res.status === 202) {
          setSocialSyncResult({
            message:
              data.message || "Sync started — posts will appear shortly.",
          });
        } else {
          setSocialSyncResult({
            inserted: data.inserted,
            total_fetched: data.total_fetched,
            terms: data.terms,
          });
        }
        await fetchSocial();
        await fetchTerms();
      } catch (e) {
        setSocialError(e.message);
      } finally {
        setSocialSyncing(false);
      }
    },
    [activeCampaignId, socialSyncing, fetchSocial, fetchTerms],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSourceTweet = useCallback(async (tweetUrl) => {
    if (!SOCIAL_API_URL || !tweetUrl) return;
    const m = (tweetUrl || "").match(/\/status\/(\d+)/);
    if (!m) return;
    const tweetId = m[1];
    try {
      const res = await authFetch(
        `${SOCIAL_API_URL}/tweet?tweet_id=${tweetId}`,
      );
      const data = await res.json();
      if (!res.ok) return;
      setSocialCollectResult((prev) => ({
        ...(prev || {}),
        source_tweet_id: tweetId,
        source_tweet: data,
      }));
    } catch {
      /* silent */
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const collectReplies = useCallback(
    async (tweetUrl) => {
      if (!SOCIAL_API_URL || !activeCampaignId || socialCollecting) return;
      const url = (tweetUrl || "").trim();
      if (!url) {
        setSocialError("Enter a tweet URL first");
        return;
      }
      setSocialCollecting(true);
      setSocialError(null);
      setSocialCollectResult(null);
      setSocialCollectProgress(null);
      setSocialLeads([]);
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/collect`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            tweet_url: url,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Collect failed");
        setSocialCollectResult({
          count: data.leads?.length || 0,
          source_tweet_id: data.source_tweet_id || data.tweet_id || null,
          platform: data.platform || "x",
          tweet_text: data.tweet_text || null,
          source_tweet: data.source_tweet || null,
          too_old: data.too_old || false,
          tweet_age_days: data.tweet_age_days,
          is_nested_reply: data.is_nested_reply || false,
        });
        setSocialLeads(data.leads || []);
      } catch (e) {
        setSocialError(e.message);
      } finally {
        setSocialCollecting(false);
      }
    },
    [activeCampaignId, socialCollecting],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const collectAll = useCallback(
    async (termId, platform = "x") => {
      if (!SOCIAL_API_URL || !activeCampaignId || collectingAllTermId) return;
      setCollectingAllTermId(termId);
      setCollectAllProgress(null);
      setSocialError(null);
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/collect-all`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            term_id: termId,
            platform,
            min_replies: 1,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Collect All failed");
      } catch (e) {
        setSocialError(e.message);
        setCollectingAllTermId(null);
      }
    },
    [activeCampaignId, collectingAllTermId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAudiences = useCallback(async () => {
    if (!SOCIAL_API_URL || !activeCampaignId) return;
    setSocialAudiencesLoading(true);
    try {
      const res = await authFetch(
        `${SOCIAL_API_URL}/audiences?campaign_id=${activeCampaignId}`,
      );
      const data = await res.json();
      setSocialAudiences(data.audiences || []);
    } catch {
      /* silently fail */
    } finally {
      setSocialAudiencesLoading(false);
    }
  }, [activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const createAudience = useCallback(
    async (name, sourceQuery) => {
      if (!SOCIAL_API_URL || !activeCampaignId || !name.trim()) return null;
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/audiences`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            name: name.trim(),
            source_query: sourceQuery.trim(),
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to create audience");
        await fetchAudiences();
        setSelectedAudienceId(data.id);
        setSocialCollections([]);
        setSelectedCollectionId(null);
        setSocialCollectionAnswers([]);
        setSocialCollectionInsights(null);
        return data;
      } catch (e) {
        setSocialError(e.message);
        return null;
      }
    },
    [activeCampaignId, fetchAudiences],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCollections = useCallback(async (audienceId) => {
    if (!SOCIAL_API_URL || !audienceId) return;
    try {
      const res = await authFetch(
        `${SOCIAL_API_URL}/collections?audience_id=${audienceId}`,
      );
      const data = await res.json();
      const cols = data.collections || [];
      setSocialCollections(cols);
    } catch {
      /* silently fail */
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCollectionAnswers = useCallback(
    async (collectionId) => {
      if (!SOCIAL_API_URL || !collectionId) return;
      try {
        const res = await authFetch(
          `${SOCIAL_API_URL}/collection-answers?collection_id=${collectionId}`,
        );
        const data = await res.json();
        setSocialCollectionAnswers(data.answers || []);
        setSocialCollectionInsights(data.insights || null);
        if (data.collection?.audience_id) {
          skipClearCollectionRef.current = true;
          setSelectedAudienceId((prev) => prev ?? data.collection.audience_id);
          fetchAudiences();
          fetchCollections(data.collection.audience_id);
        }
      } catch {
        /* silently fail */
      }
    },
    [fetchAudiences, fetchCollections],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizeCollection = useCallback(async (collectionId) => {
    if (!SOCIAL_API_URL || !collectionId) return null;
    const res = await authFetch(`${SOCIAL_API_URL}/normalize-collection`, {
      method: "POST",
      body: JSON.stringify({ collection_id: collectionId }),
    });
    const data = await res.json();
    if (data.error) {
      alert(`Normalization failed: ${data.error}`);
      return null;
    }
    const map = data.normalization_map || {};
    setSocialCollections((cols) =>
      cols.map((c) =>
        c.id === collectionId
          ? { ...c, normalization_map: JSON.stringify(map) }
          : c,
      ),
    );
    return map;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteAudience = useCallback(
    async (audienceId) => {
      if (!SOCIAL_API_URL || !audienceId) return;
      await authFetch(`${SOCIAL_API_URL}/audiences?audience_id=${audienceId}`, {
        method: "DELETE",
      });
      setSocialAudiences((prev) => prev.filter((a) => a.id !== audienceId));
      if (selectedAudienceId === audienceId) {
        setSelectedAudienceId(null);
        setSocialCollections([]);
        setSocialCollectionAnswers([]);
        setSocialCollectionInsights(null);
        setSelectedCollectionId(null);
      }
    },
    [selectedAudienceId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteCollection = useCallback(
    async (collectionId, audienceId) => {
      if (!SOCIAL_API_URL || !collectionId) return;
      await authFetch(
        `${SOCIAL_API_URL}/collections?collection_id=${collectionId}`,
        { method: "DELETE" },
      );
      setSocialCollections((cols) => cols.filter((c) => c.id !== collectionId));
      if (selectedCollectionId === collectionId) {
        setSelectedCollectionId(null);
        setSocialCollectionAnswers([]);
        setSocialCollectionInsights(null);
      }
      if (audienceId) fetchCollections(audienceId);
    },
    [selectedCollectionId, fetchCollections],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAudience = useCallback(
    async (audienceId) => {
      if (!SOCIAL_API_URL || !activeCampaignId || !audienceId) return;
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/audiences/refresh`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            audience_id: audienceId,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Refresh failed");
        await fetchAudiences();
      } catch (e) {
        setSocialError(e.message);
      }
    },
    [activeCampaignId, fetchAudiences],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const enrichLocations = useCallback(
    async (audienceId) => {
      if (!SOCIAL_API_URL || !activeCampaignId || socialLocating) return;
      setSocialLocating(true);
      setSocialError(null);
      setLeadsMsg(null);
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/enrich-locations`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            audience_id: audienceId,
          }),
        });
        const data = await res.json();
        if (!res.ok)
          throw new Error(data.error || "Location enrichment failed");
        if (data.message) {
          setSocialLocating(false);
          const s = data.stats;
          setLeadsMsg(
            s
              ? `${s.with_zip} of ${s.total} leads have zip codes · ${s.state_only} state-only (location too broad to geocode) · ${s.geocode_failed} unresolvable`
              : data.message,
          );
        }
      } catch (e) {
        setSocialError(e.message);
        setSocialLocating(false);
      }
    },
    [activeCampaignId, socialLocating],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const enrichIdentity = useCallback(
    async (audienceId) => {
      if (!SOCIAL_API_URL || !activeCampaignId || socialEnriching) return;
      setSocialEnriching(true);
      setSocialEnrichProgress(null);
      setSocialError(null);
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/enrich-identity`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            audience_id: audienceId,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Identity matching failed");
        if (data.message) {
          setSocialEnriching(false);
        }
      } catch (e) {
        setSocialError(e.message);
        setSocialEnriching(false);
      }
    },
    [activeCampaignId, socialEnriching],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const importHandles = useCallback(
    async (handles) => {
      if (!SOCIAL_API_URL || !activeCampaignId)
        return { error: "Not configured" };
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/import-handles`, {
          method: "POST",
          body: JSON.stringify({ campaign_id: activeCampaignId, handles }),
        });
        const data = await res.json();
        if (!res.ok) return { error: data.error || "Import failed" };
        setLeadsRefreshTick((t) => t + 1);
        return data;
      } catch (e) {
        return { error: e.message };
      }
    },
    [activeCampaignId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const pollAudience = useCallback(
    async (audienceId, question, answerOptions) => {
      if (!SOCIAL_API_URL || !activeCampaignId || !audienceId || socialPolling)
        return;
      setSocialPolling(true);
      setSocialPollProgress(null);
      setSocialError(null);
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/poll`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            audience_id: audienceId,
            question,
            answer_options: answerOptions,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Poll failed");
        if (data.status === "processing" && data.collection_id) {
          setSelectedCollectionId(data.collection_id);
        } else {
          if (data.collection_id) {
            setSelectedCollectionId(data.collection_id);
            setSocialCollectionAnswers(data.answers || []);
          }
          await fetchCollections(audienceId);
          setSocialPolling(false);
        }
      } catch (e) {
        setSocialError(e.message);
        setSocialPolling(false);
      }
    },
    [activeCampaignId, socialPolling, fetchCollections],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const pollFromPost = useCallback(
    async (tweetUrl, question, answerOptions) => {
      if (!SOCIAL_API_URL || !activeCampaignId || socialPolling) return;
      setSocialPolling(true);
      setSocialPollProgress(null);
      setSocialError(null);
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/polls/from-post`, {
          method: "POST",
          // Poll the FULL collected universe stored in social_replies (server resolves all reply
          // authors for this tweet) — NOT just the loaded preview — so N is maximal and MOE minimal.
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            tweet_url: tweetUrl,
            question,
            answer_options: answerOptions,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Poll failed");
        if (data.status === "processing" && data.collection_id) {
          setSelectedCollectionId(data.collection_id);
          if (data.audience_id) setSelectedAudienceId(data.audience_id);
          setSocialMode("audiences");
          fetchAudiences();
        } else {
          setSocialPolling(false);
        }
      } catch (e) {
        setSocialError(e.message);
        setSocialPolling(false);
      }
    },
    [activeCampaignId, socialPolling, fetchAudiences],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkReply = useCallback(
    async (collectionId, answerFilter, message, opts = {}) => {
      if (!SOCIAL_API_URL || !activeCampaignId) return null;
      const { sourceTweetId, variations } = opts;
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/bulk-reply`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            collection_id: collectionId,
            answer_filter: answerFilter,
            message,
            source_tweet_id: sourceTweetId || undefined,
            variations: variations || undefined,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Bulk reply failed");
        if (data.job_id) {
          setBulkReplyJob({
            jobId: data.job_id,
            total: data.total,
            sent: 0,
            failed: 0,
            status: "queued",
            answerFilter: answerFilter || "__collect_all__",
          });
        }
        return data;
      } catch (e) {
        setSocialError(e.message);
        return null;
      }
    },
    [activeCampaignId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const generateReplyVariations = useCallback(
    async (template, count = 10) => {
      if (!SOCIAL_API_URL) return [template];
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/reply-variations`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            template,
            count,
          }),
        });
        const data = await res.json();
        return data.variations || [template];
      } catch {
        return [template];
      }
    },
    [activeCampaignId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const tagCohort = useCallback(
    async (collectionId, answer, audienceName, rawAnswers) => {
      if (!SOCIAL_API_URL || !activeCampaignId) return null;
      try {
        const res = await authFetch(`${SOCIAL_API_URL}/tag-cohort`, {
          method: "POST",
          body: JSON.stringify({
            campaign_id: activeCampaignId,
            collection_id: collectionId,
            answer,
            audience_name: audienceName,
            raw_answers: rawAnswers,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Tag failed");
        await fetchAudiences();
        return data.audience;
      } catch (e) {
        setSocialError(e.message);
        return null;
      }
    },
    [activeCampaignId, fetchAudiences],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const postReply = useCallback(
    async (tweetId, content, authorId, authorUsername, platform = "x") => {
      if (!SOCIAL_API_URL || !activeCampaignId) return null;
      const res = await authFetch(`${SOCIAL_API_URL}/reply`, {
        method: "POST",
        body: JSON.stringify({
          campaign_id: activeCampaignId,
          tweet_id: tweetId,
          content,
          author_id: authorId,
          author_username: authorUsername,
          platform,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Reply failed");
      return data;
    },
    [activeCampaignId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDonorLinks = useCallback(async () => {
    if (!SOCIAL_API_URL || !activeCampaignId) return;
    try {
      const res = await authFetch(
        `${SOCIAL_API_URL}/donor-links?campaign_id=${activeCampaignId}`,
      );
      const data = await res.json();
      setSocialDonorLinks(data.links || []);
    } catch {
      /* silently fail */
    }
  }, [activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDonorLink = useCallback(
    async (
      authorId,
      authorUsername,
      authorName,
      donorEmail,
      donorName,
      collectionId,
    ) => {
      if (!SOCIAL_API_URL || !activeCampaignId || !authorId || !donorEmail)
        return null;
      const res = await authFetch(`${SOCIAL_API_URL}/donor-links`, {
        method: "POST",
        body: JSON.stringify({
          campaign_id: activeCampaignId,
          author_id: authorId,
          author_username: authorUsername,
          author_name: authorName,
          donor_email: donorEmail,
          donor_name: donorName,
          collection_id: collectionId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Link failed");
      setSocialDonorLinks((prev) => {
        const filtered = prev.filter((l) => l.author_id !== authorId);
        return [...filtered, data.link];
      });
      return data.link;
    },
    [activeCampaignId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      tab === "Social" &&
      socialMode === "audiences" &&
      SOCIAL_API_URL &&
      activeCampaignId
    )
      fetchDonorLinks();
  }, [tab, socialMode, activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch on tab activation or campaign change
  useEffect(() => {
    if (tab === "Social" && SOCIAL_API_URL && activeCampaignId) {
      fetchSocial();
      fetchTerms();
    }
  }, [tab, activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL when social navigation state changes
  useEffect(() => {
    if (tab !== "Social") return;
    writePath("Social", {
      socialMode,
      socialCollectionId: selectedCollectionId,
    });
  }, [tab, socialMode, selectedCollectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a term is selected, fetch its full mention set (may exceed the 500-row general limit)
  useEffect(() => {
    if (socialSelectedTerm) {
      // Blank → loading only when the term actually changed; a post-sync refresh (same term) refetches silently.
      if (socialTermPrevRef.current !== socialSelectedTerm)
        setSocialTermMentions(null);
      fetchTermMentions(socialSelectedTerm);
    } else {
      setSocialTermMentions(null);
    }
    socialTermPrevRef.current = socialSelectedTerm;
  }, [socialSelectedTerm, fetchTermMentions, socialSyncTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect mode: no auto-fetch of all leads — leads load only after "Collect Replies" click

  // Auto-fetch source tweet when collect mode is active and URL changes
  useEffect(() => {
    if (tab === "Social" && socialMode === "collect" && socialCollectTweetUrl) {
      fetchSourceTweet(socialCollectTweetUrl);
    }
  }, [tab, socialMode, socialCollectTweetUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      tab === "Social" &&
      socialMode === "audiences" &&
      SOCIAL_API_URL &&
      activeCampaignId
    )
      fetchAudiences();
  }, [tab, socialMode, activeCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedAudienceId) {
      if (skipClearCollectionRef.current) {
        skipClearCollectionRef.current = false;
        fetchCollections(selectedAudienceId);
        return;
      }
      setSocialCollections([]);
      setSelectedCollectionId(null);
      setSocialCollectionAnswers([]);
      setSocialCollectionInsights(null);
      fetchCollections(selectedAudienceId);
    }
  }, [selectedAudienceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedCollectionId) fetchCollectionAnswers(selectedCollectionId);
  }, [selectedCollectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live-or-static WinRed stats ─────────────────────────────────────────
  const liveMonthly =
    winredData?.monthly?.length > 0 ? winredData.monthly : null;
  const staticMonthly = cd.winredMonthly;
  const staticTotalRaised = (staticMonthly.raised || []).reduce(
    (s, v) => s + v,
    0,
  );
  const staticLastMonth = (staticMonthly.raised || []).at(-1) ?? 0;
  const staticPrevMonth = (staticMonthly.raised || []).at(-2) ?? 0;
  const staticTotalDonors = (staticMonthly.donorCount || []).reduce(
    (s, v) => s + v,
    0,
  );
  const liveTotalRaised = liveMonthly
    ? liveMonthly.reduce((s, m) => s + m.raised, 0)
    : staticTotalRaised;
  // Filter out the current (partial) month so MoM always compares two completed months
  const _mn2 = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const _now2 = new Date();
  const _curLabel2 = `${_mn2[_now2.getMonth()]} '${String(_now2.getFullYear()).slice(2)}`;
  const completedMonthly = liveMonthly
    ? liveMonthly.filter((m) => m.month !== _curLabel2)
    : null;
  const liveLastMonth = completedMonthly
    ? (completedMonthly[completedMonthly.length - 1]?.raised ?? 0)
    : staticLastMonth;
  const livePrevMonth = completedMonthly
    ? (completedMonthly[completedMonthly.length - 2]?.raised ?? liveLastMonth)
    : staticPrevMonth;
  const liveLastMonthLabel =
    completedMonthly?.[completedMonthly.length - 1]?.month ?? "";
  const livePrevMonthLabel =
    completedMonthly?.[completedMonthly.length - 2]?.month ?? "";
  // No pro-rating — both months are complete
  const liveVelocityPct = livePrevMonth
    ? (((liveLastMonth - livePrevMonth) / livePrevMonth) * 100).toFixed(1)
    : "0.0";
  const liveTotalDonors =
    winredData?.summary?.unique_donors ?? staticTotalDonors;

  // ── Correlation chart: candidate + opponent fundraising vs polling ──
  // X-axis = FEC quarters from campaign data; polling null-padded to match quarter count
  const cdFecQ = cd.fecQuarters;
  const cdCandFR = cd.candidateFR;
  const cdOppFR = cd.opponentFR;
  // Map each FEC quarter to nearest poll index (spread polls across available quarters)
  const pollCount = cd.pollAverages.length;
  const fecCount = cdFecQ.length;
  const mkPollLine = (key) =>
    cdFecQ.map((_, qi) => {
      if (!pollCount) return null;
      // Map last N quarters to poll entries; earlier quarters are null
      const offset = fecCount - pollCount;
      const pi = qi - offset;
      return pi >= 0 && pi < pollCount
        ? (cd.pollAverages[pi]?.[key] ?? null)
        : null;
    });

  // Opponent keys for the second bar dataset
  const oppKeys = Object.keys(cdOppFR);
  const opp1Key = oppKeys[0];
  const opp1FR = opp1Key ? cdOppFR[opp1Key] || {} : {};

  const correlationChartData = {
    labels: cdFecQ,
    datasets: [
      // ── Fundraising bars (right axis) ──
      {
        type: "bar",
        label: `${cfg.candidateName || "Candidate"} Raised`,
        data: cdCandFR.receipts || [],
        backgroundColor: PARTY_COLORS.Republican + "99",
        borderColor: PARTY_COLORS.Republican,
        borderWidth: 1,
        borderRadius: 2,
        yAxisID: "yRaised",
        order: 4,
      },
      ...(opp1Key
        ? [
            {
              type: "bar",
              label: `${opp1Key} Raised`,
              data: opp1FR.receipts || [],
              backgroundColor: C.navyMid + "99",
              borderColor: C.navyMid,
              borderWidth: 1,
              borderRadius: 2,
              yAxisID: "yRaised",
              order: 5,
            },
          ]
        : []),
      // ── Polling lines (left axis) ──
      {
        type: "line",
        label: `${cfg.candidateName || "Candidate"} %`,
        data: mkPollLine("candidate"),
        borderColor: PARTY_COLORS.Republican,
        backgroundColor: "transparent",
        borderWidth: 2.5,
        pointRadius: 4,
        tension: 0.3,
        spanGaps: false,
        yAxisID: "yPoll",
        order: 1,
      },
      ...(opp1Key
        ? [
            {
              type: "line",
              label: `${opp1Key} %`,
              data: mkPollLine(opp1Key),
              borderColor: C.navyMid,
              backgroundColor: "transparent",
              borderWidth: 2,
              borderDash: [4, 3],
              pointRadius: 3,
              tension: 0.3,
              spanGaps: false,
              yAxisID: "yPoll",
              order: 2,
            },
          ]
        : []),
    ],
  };

  const correlationOpts = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      tooltip: {
        ...baseTooltip,
        callbacks: {
          label: (ctx) => {
            if (ctx.dataset.yAxisID === "yRaised")
              return ` ${ctx.dataset.label}: ${fmtMoney(ctx.parsed.y)}`;
            return ctx.parsed.y != null
              ? ` ${ctx.dataset.label}: ${ctx.parsed.y}%`
              : null;
          },
        },
      },
      legend: baseLegend,
    },
    scales: {
      x: {
        ...baseGridScale,
        grid: { display: false },
        ticks: { ...baseGridScale.ticks, maxRotation: 30 },
      },
      yPoll: {
        ...baseGridScale,
        position: "left",
        min: 0,
        max: 35,
        title: {
          display: true,
          text: "Poll %",
          font: { size: 9 },
          color: C.muted,
        },
        ticks: { ...baseGridScale.ticks, callback: (v) => v + "%" },
      },
      yRaised: {
        ...baseGridScale,
        position: "right",
        grid: { display: false },
        title: {
          display: true,
          text: "Qtr Raised",
          font: { size: 9 },
          color: C.muted,
        },
        ticks: { ...baseGridScale.ticks, callback: (v) => fmtMoney(v) },
      },
    },
  };

  // Fetch news when the News tab is active and campaign or candidate name is ready.
  // Uses selectedCampaign?.candidate_name (always current from AuthContext) first
  // so we never use a stale Pollen-config name from a previous campaign.
  // Forces refresh=true on campaign switch so the server ignores its S3 cache and
  // regenerates news for the correct candidate (avoids stale cached articles from
  // a previous campaign persisting in the new campaign's bucket).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const candidateName = selectedCampaign?.candidate_name || cfg.candidateName;
    if (!isLoggedIn || !candidateName || effectiveTab !== "News") return;
    const forceRefresh = newsCampaignRef.current !== activeCampaignId;
    newsCampaignRef.current = activeCampaignId;
    if (forceRefresh) setNewsData(null);
    if (!forceRefresh && newsData) return;
    fetchNews(forceRefresh);
  }, [
    effectiveTab,
    activeCampaignId,
    isLoggedIn,
    selectedCampaign?.candidate_name,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const TABS = [
    "Daily Brief",
    "Social",
    "Voters",
    "People",
    "Ground Game",
    "Scoring Model",
    "Message Orchestration",
    "Electorate Simulation",
  ];

  // Timeline progress bar
  const timelineStart = new Date("2025-01-01");
  const timelineEnd = new Date(cfg.electionDate || "2099-01-01");
  const timelineNow = new Date();
  const timelinePct = Math.round(
    Math.min(1, (timelineNow - timelineStart) / (timelineEnd - timelineStart)) *
      100,
  );
  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        })
      : "—";

  // Show login modal until auth resolves
  if (authLoading) return null;
  if (!isLoggedIn) return <LoginModal onSuccess={() => {}} />;

  return (
    <Box
      sx={{
        fontFamily: "'Inter','Helvetica Neue',sans-serif",
        bgcolor: C.bg,
        minHeight: "100vh",
      }}
    >
      {/* ── Sticky shell: header + tab bar ────────────────────────────────── */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 1100 }}>
        {/* ── Header ────────────────────────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: C.navy,
            px: { xs: 2, md: 4 },
            py: { xs: 1, md: 1.25 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `3px solid ${C.red}`,
          }}
        >
          {/* Left: logo + title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              component="img"
              src={pollenLogo}
              alt="Pollis"
              sx={{ height: 40 }}
            />
            <Box
              sx={{
                width: 2,
                height: 20,
                bgcolor: "#FF0A4B",
                display: { xs: "none", md: "block" },
              }}
            />
            <Typography
              sx={{
                color: C.white,
                fontWeight: 400,
                fontSize: 13,
                display: { xs: "none", md: "block" },
              }}
            >
              Campaign Intelligence & Orchestration
            </Typography>
          </Box>

          {/* Right: user avatar */}
          {isLoggedIn && (
            <Box
              onClick={() => setUserDrawerOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                borderRadius: 2,
                px: 1,
                py: 0.5,
                "&:hover": { bgcolor: "rgba(255,255,255,0.07)" },
                transition: "background 0.15s",
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "#1e3a5f",
                  border: "2px solid rgba(255,255,255,0.2)",
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.white,
                }}
              >
                {(user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")}
              </Avatar>
              <Typography
                sx={{
                  color: C.white,
                  fontSize: 13,
                  fontWeight: 600,
                  userSelect: "none",
                }}
              >
                {user?.first_name} {user?.last_name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Campaign Bar ──────────────────────────────────────────────────── */}
        {isLoggedIn && (
          <Box
            sx={{
              bgcolor: "#f0f2f5",
              borderBottom: "1px solid #dde1e8",
              px: { xs: 2, md: 4 },
              py: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: 44,
              gap: 2,
            }}
          >
            {/* Left: campaign selector + meta pills */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0,
                flexWrap: "nowrap",
                overflow: "hidden",
              }}
            >
              {campaigns.length > 0 ? (
                <Select
                  value={selectedCampaign?.id || ""}
                  onChange={(e) =>
                    setSelectedCampaign(
                      campaigns.find((c) => c.id === e.target.value) || null,
                    )
                  }
                  size="small"
                  displayEmpty
                  variant="standard"
                  disableUnderline
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.navy,
                    "& .MuiSelect-select": {
                      py: "10px",
                      pr: "24px !important",
                      pl: 0,
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiSelect-icon": { color: C.navy, right: 0 },
                    minWidth: 140,
                    flexShrink: 0,
                  }}
                >
                  {campaigns.map((c) => (
                    <MenuItem
                      key={c.id}
                      value={c.id}
                      sx={{ fontSize: 13, fontWeight: 600 }}
                    >
                      {c.candidate_name || c.display_name}
                      {c.election_cycle
                        ? ` · ${c.office || ""} ${c.election_cycle}`
                        : ""}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: C.navy }}
                >
                  {cfg.candidateName || "No campaign"}
                </Typography>
              )}
              {[cfg.office, cfg.party, cfg.state]
                .filter(Boolean)
                .map((pill, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 11, color: "#8a96a3", mx: 0.75 }}
                    >
                      ·
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: "#4a5568", fontWeight: 400 }}
                    >
                      {pill}
                    </Typography>
                  </Box>
                ))}
            </Box>

            {/* Right: Campaign Setup + New Campaign */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              {user?.superuser && (
                <Button
                  onClick={() => setShowProvisionModal(true)}
                  size="small"
                  sx={{
                    display: { xs: "none", md: "inline-flex" },
                    color: "#4a5568",
                    border: "1px solid #c8cfd8",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(10,34,64,0.05)",
                      borderColor: C.navy,
                    },
                  }}
                >
                  + New campaign
                </Button>
              )}
              <Button
                onClick={() => setCampaignSetupOpen(true)}
                size="small"
                sx={{
                  color: C.navy,
                  border: `1px solid ${C.navy}`,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.5,
                  "&:hover": { bgcolor: C.navy, color: C.white },
                  transition: "all 0.15s",
                }}
              >
                Campaign Setup
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Tab Bar ───────────────────────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: C.white,
            borderBottom: `1px solid ${C.border}`,
            px: { xs: 0, md: 4 },
            display: "flex",
            gap: 0,
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {TABS.map((t) => {
            const isSim = t === "Electorate Simulation";
            const isMsg = t === "Message Orchestration";
            return (
              <Box
                key={t}
                onClick={() => setPrimaryTab(t)}
                sx={{
                  px: { xs: 1.75, md: 2.5 },
                  py: { xs: 1.25, md: 1.5 },
                  fontSize: { xs: 12, md: 13 },
                  fontWeight: 700,
                  cursor: "pointer",
                  ...(isSim
                    ? {
                        color: tab === t ? "#fff" : "#FF0A4B",
                        bgcolor: tab === t ? "#FF0A4B" : "transparent",
                        borderBottom: `3px solid #FF0A4B`,
                        "&:hover": {
                          bgcolor: tab === t ? "#e0093f" : "#FF0A4B12",
                        },
                      }
                    : isMsg
                      ? {
                          color: tab === t ? C.navy : C.muted,
                          borderBottom:
                            tab === t
                              ? `3px solid ${C.navy}`
                              : "3px solid transparent",
                          borderLeft: `1px solid ${C.border}`,
                          ml: "auto",
                          "&:hover": { color: C.navy },
                        }
                      : {
                          color: tab === t ? C.navy : C.muted,
                          borderBottom:
                            tab === t
                              ? `3px solid ${C.navy}`
                              : "3px solid transparent",
                          "&:hover": { color: C.navy },
                        }),
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {t === "Voters" ? "Donors" : t}
              </Box>
            );
          })}
        </Box>

        {/* ── Daily Brief Sub-Tab Bar ──────────────────────────────────────── */}
        {tab === "Daily Brief" && (
          <Box
            sx={{
              bgcolor: C.white,
              borderBottom: `1px solid ${C.border}`,
              px: { xs: 0, md: 4 },
              display: "flex",
              gap: 0,
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {BRIEF_SUB_TABS.map((t) => (
              <Box
                key={t}
                onClick={() => setSubTab(t)}
                sx={{
                  px: { xs: 1.75, md: 2.5 },
                  py: { xs: 1.25, md: 1.5 },
                  fontSize: { xs: 12, md: 13 },
                  fontWeight: 700,
                  cursor: "pointer",
                  color: briefSubTab === t ? C.navy : C.muted,
                  borderBottom:
                    briefSubTab === t
                      ? `3px solid ${C.navy}`
                      : "3px solid transparent",
                  "&:hover": { color: C.navy },
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {t}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {/* end sticky shell */}

      {/* ── Main Content ──────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 } }}>
        {/* No campaign selected — prompt user to pick one */}
        {isLoggedIn && !activeCampaignId && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.navy }}>
              Select a campaign to get started
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: C.muted,
                textAlign: "center",
                maxWidth: 400,
              }}
            >
              Use the campaign dropdown in the header to choose a campaign. Your
              data will load automatically.
            </Typography>
          </Box>
        )}
        {activeCampaignId && (
          <>
            {/* ── Stat row — Overview sub-tab only (hidden on mobile so the Daily Brief leads) ── */}
            {tab === "Daily Brief" && briefSubTab === "Overview" && (
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 3,
                }}
              >
                <StatCard
                  label="Total Raised"
                  value={fmtMoney(liveTotalRaised)}
                  sub={winredData ? "WinRed live" : "WinRed static"}
                  accent={C.green}
                  setup={
                    winredData
                      ? undefined
                      : {
                          label: "Connect WinRed",
                          sub: "in Campaign Setup",
                          onClick: () => setCampaignSetupOpen(true),
                        }
                  }
                  info={{
                    eyebrow: "WINRED · CYCLE FUNDRAISING",
                    title: "Total Raised — Cycle to Date",
                    subtitle: "WinRed online fundraising · all transactions",
                    sections: [
                      {
                        heading: "WHAT IT MEASURES",
                        body: "Cumulative gross receipts from WinRed since the start of the election cycle. Includes all online contributions processed through the WinRed platform.",
                      },
                      {
                        heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                        body: "Cycle-to-date total sets the baseline for pace against goal. Track this against your fundraising goal and against the same period in prior comparable cycles to gauge whether momentum is ahead or behind historical norms.",
                      },
                    ],
                  }}
                />
                <StatCard
                  label="Last Month"
                  value={fmtMoney(liveLastMonth)}
                  sub={liveLastMonthLabel || "latest completed month"}
                  accent={C.blue}
                  info={{
                    eyebrow: "WINRED · MONTHLY FUNDRAISING",
                    title: "Last Completed Month",
                    subtitle: `${liveLastMonthLabel || "Latest month"} · WinRed`,
                    sections: [
                      {
                        heading: "WHAT IT MEASURES",
                        body: "Total gross receipts from WinRed for the most recently completed calendar month. Partial current-month data is excluded to prevent misleading comparisons.",
                      },
                      {
                        heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                        body: "Monthly totals are the most comparable fundraising metric across campaigns and cycles. This figure is what gets cited in press releases, FEC filings, and competitive comparisons. Velocity month-over-month tells you whether the program is accelerating or decelerating.",
                      },
                    ],
                  }}
                />
                <StatCard
                  label="Velocity MoM"
                  value={
                    (Number(liveVelocityPct) > 0 ? "+" : "") +
                    liveVelocityPct +
                    "%"
                  }
                  sub={
                    liveLastMonthLabel && livePrevMonthLabel
                      ? `${liveLastMonthLabel} vs ${livePrevMonthLabel}`
                      : "last vs prior month"
                  }
                  accent={Number(liveVelocityPct) > 0 ? C.green : C.red}
                  info={{
                    eyebrow: "WINRED · FUNDRAISING VELOCITY",
                    title: "Month-over-Month Velocity",
                    subtitle: `${liveLastMonthLabel || "Last month"} vs. ${livePrevMonthLabel || "prior month"} · completed months only`,
                    sections: [
                      {
                        heading: "WHAT IT MEASURES",
                        body: "Percentage change in WinRed fundraising between the two most recently completed calendar months. Only completed months are compared — the current partial month is never included, which would produce a misleading decline.",
                      },
                      {
                        heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                        body: "Velocity is the most important leading indicator in fundraising. A positive MoM trend means the program is accelerating — donors are more engaged and the pipeline is healthy. A sustained negative trend is an early warning: investigate which cohorts are lapsing and whether email/SMS volume or deliverability has changed.",
                      },
                    ],
                  }}
                />
                <StatCard
                  label="Total Donors"
                  value={liveTotalDonors.toLocaleString()}
                  sub={winredData?.summary ? "WinRed live" : "WinRed static"}
                  accent={C.navy}
                  setup={
                    winredData?.summary
                      ? undefined
                      : {
                          label: "Connect WinRed",
                          sub: "in Campaign Setup",
                          onClick: () => setCampaignSetupOpen(true),
                        }
                  }
                  info={{
                    eyebrow: "WINRED · DONOR BASE",
                    title: "Unique Donor Count",
                    subtitle: "WinRed · unique email addresses",
                    sections: [
                      {
                        heading: "WHAT IT MEASURES",
                        body: "Total count of unique donors who have contributed through WinRed in this election cycle, measured by unique email address.",
                      },
                      {
                        heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                        body: "Donor count is the breadth metric — how many people have voted with their wallet. A large donor base at low average gift is more renewable than a small base at high average gift. Watch for the ratio of new donors to returning donors — a high new-donor rate signals successful acquisition; a high returning-donor rate signals strong retention but may indicate acquisition is stalling.",
                      },
                    ],
                  }}
                />
                <StatCard
                  label="Campaign Stage"
                  value={stage.label}
                  sub={cfg.primaryDate ? `${daysToPrimary}d to primary` : ""}
                  accent={stage.color}
                  info={{
                    eyebrow: "CAMPAIGN · STAGE",
                    title: "Current Campaign Stage",
                    subtitle: "Primary / General / Runoff",
                    sections: [
                      {
                        heading: "WHAT IT MEASURES",
                        body: "The current competitive stage of the campaign — Primary, General Election, or Runoff — based on the election calendar configured for this campaign.",
                      },
                      {
                        heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                        body: "Stage determines the appropriate messaging strategy, target voter universe, and spending cadence. Primary strategy prioritizes base activation and party primary voters; general strategy expands to persuasion targets and independents; runoff strategy maximizes turnout of committed supporters in a compressed timeline.",
                      },
                    ],
                  }}
                />
                <StatCard
                  label="Days to Primary"
                  value={cfg.primaryDate ? `${daysToPrimary}d` : ""}
                  sub={
                    cfg.primaryDate
                      ? new Date(cfg.primaryDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "not set"
                  }
                  accent={daysToPrimary < 60 ? C.orange : C.green}
                  setup={
                    cfg.primaryDate
                      ? undefined
                      : {
                          label: "Set primary date",
                          sub: "in Campaign Setup",
                          onClick: () => setCampaignSetupOpen(true),
                        }
                  }
                  info={{
                    eyebrow: "CAMPAIGN · TIMELINE",
                    title: "Days to Primary Election",
                    subtitle: cfg.primaryDate || "Date not configured",
                    sections: [
                      {
                        heading: "WHAT IT MEASURES",
                        body: "Calendar days remaining until the primary election date configured for this campaign.",
                      },
                      {
                        heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                        body: "Under 90 days: paid media windows are filling; ad rates will rise. Under 60 days: the fundraising close is now urgent — dollars raised in the final 45 days before the primary buy the most impactful media and mail. Under 30 days: GOTV is the primary activity; fundraising appeals should lean on urgency and deadline framing.",
                      },
                    ],
                  }}
                />
                <StatCard
                  label="Days to Election"
                  value={cfg.electionDate ? `${daysToElection}d` : ""}
                  sub={
                    cfg.electionDate
                      ? new Date(cfg.electionDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "not set"
                  }
                  accent={daysToElection < 90 ? C.red : C.navy}
                  setup={
                    cfg.electionDate
                      ? undefined
                      : {
                          label: "Set election date",
                          sub: "in Campaign Setup",
                          onClick: () => setCampaignSetupOpen(true),
                        }
                  }
                  info={{
                    eyebrow: "CAMPAIGN · TIMELINE",
                    title: "Days to General Election",
                    subtitle: cfg.electionDate || "Date not configured",
                    sections: [
                      {
                        heading: "WHAT IT MEASURES",
                        body: "Calendar days remaining until the general election date configured for this campaign.",
                      },
                      {
                        heading: "WHAT IT MEANS FOR THE CAMPAIGN",
                        body: "General election timing determines the paid media and GOTV schedule for the full race. In competitive districts, campaigns that are outspent in the final 30 days rarely close the gap — budget allocation against this countdown is the most consequential financial decision of the cycle.",
                      },
                    ],
                  }}
                />
              </Box>
            )}

            {tab === "Voters" && (
              <Card elevation={0} sx={{ border: `1px solid ${C.border}` }}>
                <CardContent>
                  <DonorsTab
                    onOpenProfile={openProfile}
                    subView={donorsSubView}
                    onSubViewChange={handleSubViewChange}
                    donorCohorts={donorCohorts}
                    cd={cd}
                    campaignId={activeCampaignId}
                    onEnrichLocations={() => enrichLocations(null)}
                    onEnrichIdentity={() => enrichIdentity(null)}
                    onImportHandles={importHandles}
                    locating={socialLocating}
                    enriching={socialEnriching}
                    leadsEnrichProgress={socialEnrichProgress}
                    leadsRefreshTick={leadsRefreshTick}
                    leadsMsg={leadsMsg}
                  />
                </CardContent>
              </Card>
            )}

            {tab === "People" && (
              <Card elevation={0} sx={{ border: `1px solid ${C.border}` }}>
                <CardContent>
                  <AudienceExplorer campaignId={activeCampaignId} />
                </CardContent>
              </Card>
            )}

            {tab === "Ground Game" && (
              <Card elevation={0} sx={{ border: `1px solid ${C.border}` }}>
                <CardContent>
                  <TerrainMap campaignId={activeCampaignId} />
                </CardContent>
              </Card>
            )}

            {tab === "Scoring Model" && (
              <Card elevation={0} sx={{ border: `1px solid ${C.border}` }}>
                <CardContent>
                  <ScoringModelView campaignId={activeCampaignId} />
                </CardContent>
              </Card>
            )}

            {tab === "Electorate Simulation" && (
              <Card elevation={0} sx={{ border: `1px solid ${C.border}` }}>
                <CardContent>
                  <SimulationsTab />
                </CardContent>
              </Card>
            )}

            <Box
              sx={{
                display:
                  tab === "Voters" ||
                  tab === "People" ||
                  tab === "Ground Game" ||
                  tab === "Scoring Model" ||
                  tab === "Electorate Simulation"
                    ? "none"
                    : "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2.5,
                mb: 3,
                alignItems: "flex-start",
              }}
            >
              {/* Left column (full-width for Social → Discover, which has no right feed) */}
              <Box
                sx={{
                  flex: {
                    xs: "0 0 100%",
                    md:
                      effectiveTab === "Social" &&
                      (socialMode === "discovery" || socialMode === "topics")
                        ? "0 0 100%"
                        : "0 0 46%",
                  },
                  minWidth: 0,
                  width: { xs: "100%", md: "auto" },
                }}
              >
                {/* Polling tab — intelligence summary in left column */}
                {effectiveTab === "Polling" && (
                  <Card
                    elevation={0}
                    sx={{ border: `1px solid ${C.border}`, mb: 2 }}
                  >
                    <CardContent>
                      <PollingTab
                        kalshi={kalshi}
                        livePolls={livePolls}
                        liveFEC={liveFEC}
                        onRefreshPolls={refreshPolls}
                        pollsRefreshing={pollsRefreshing}
                        pollingSummary={pollingSummary}
                        pollingSummaryLoading={pollingSummaryLoading}
                        cfg={cfg}
                        section="intelligence"
                        cd={cd}
                        opponentIntel={opponentIntel}
                        onGenerateOpponentIntel={generateOpponentIntel}
                        opponentIntelLoading={opponentIntelLoading}
                        onSaveManualPolls={saveManualPolls}
                      />
                    </CardContent>
                  </Card>
                )}
                {/* PM / Finance / Campaign — tab-specific intelligence summary */}
                {[
                  "Prediction Markets",
                  "Finance",
                  "Campaign",
                  "Policy",
                ].includes(effectiveTab) && (
                  <Card
                    elevation={0}
                    sx={{ border: `1px solid ${C.border}`, mb: 2 }}
                  >
                    <CardContent>
                      <TabSummaryCard
                        tabName={effectiveTab}
                        summary={tabSummaries[effectiveTab]}
                        loading={!!tabSummaryLoading[effectiveTab]}
                        onRefresh={() => triggerTabSummary(effectiveTab)}
                      />
                    </CardContent>
                  </Card>
                )}
                {/* Message Orchestration — controls + signal brief in left column */}
                {tab === "Message Orchestration" && (
                  <Card
                    elevation={0}
                    sx={{ border: `1px solid ${C.border}`, mb: 2 }}
                  >
                    <CardContent>
                      <MessagingTab
                        cfg={cfg}
                        kalshi={kalshi}
                        winredData={winredData}
                        liveTotalRaised={liveTotalRaised}
                        liveLastMonth={liveLastMonth}
                        liveVelocityPct={liveVelocityPct}
                        liveTotalDonors={liveTotalDonors}
                        cd={cd}
                        liveFEC={liveFEC}
                        section="controls"
                        activeCampaignId={activeCampaignId}
                        messagingIntelCbRef={messagingIntelCbRef}
                        messagingImageCbRef={messagingImageCbRef}
                      />
                    </CardContent>
                  </Card>
                )}
                {/* Asset Library — its own card below the Message Orchestration box */}
                {tab === "Message Orchestration" && (
                  <Card
                    elevation={0}
                    sx={{ border: `1px solid ${C.border}`, mb: 2 }}
                  >
                    <CardContent>
                      <CampaignImageLibrary
                        campaignId={activeCampaignId}
                        C={C}
                      />
                    </CardContent>
                  </Card>
                )}
                {/* News — insight panel in left column */}
                {effectiveTab === "News" && (
                  <Card
                    elevation={0}
                    sx={{ border: `1px solid ${C.border}`, mb: 2 }}
                  >
                    <CardContent>
                      <TabSummaryCard
                        tabName="News"
                        summary={tabSummaries["News"]}
                        loading={!!tabSummaryLoading["News"]}
                        onRefresh={() => triggerTabSummary("News")}
                      />
                      <NewsTab
                        section="insight"
                        newsData={newsData}
                        newsLoading={newsLoading}
                        onRefresh={() => fetchNews(true)}
                        cfg={cfg}
                        cd={cd}
                        opponentIntel={opponentIntel}
                        onGenerateOpponentIntel={generateOpponentIntel}
                        opponentIntelLoading={opponentIntelLoading}
                      />
                    </CardContent>
                  </Card>
                )}
                {/* Social — intelligence panel in left column */}
                {effectiveTab === "Social" && (
                  <Card
                    elevation={0}
                    sx={{ border: `1px solid ${C.border}`, mb: 2 }}
                  >
                    <CardContent>
                      <SocialTab
                        section="intelligence"
                        cfg={cfg}
                        allMentions={
                          socialSelectedTerm && socialTermMentions !== null
                            ? socialTermMentions
                            : socialMentions
                        }
                        loading={socialLoading}
                        syncing={socialSyncing}
                        syncResult={socialSyncResult}
                        lastSynced={socialLastSynced}
                        error={socialError}
                        searchQuery={socialSearchQuery}
                        filter={socialFilter}
                        authorFilter={socialAuthorFilter}
                        page={socialPage}
                        onFilterChange={(f) => {
                          setSocialFilter(f);
                          setSocialPage(1);
                        }}
                        onAuthorChange={(id) => {
                          setSocialAuthorFilter(id);
                          setSocialPage(1);
                        }}
                        onPageChange={setSocialPage}
                        onSync={syncSocial}
                        terms={socialTerms}
                        termsLoading={socialTermsLoading}
                        selectedTerm={socialSelectedTerm}
                        onSelectTerm={setSocialSelectedTerm}
                        onAddTerm={addTerm}
                        onDeleteTerm={deleteTerm}
                        onRefreshTerm={refreshTerm}
                        onUpdateTerm={updateTerm}
                        mode={socialMode}
                        onModeChange={(m) => {
                          setSocialMode(m);
                          if (m === "collect") {
                            setSocialCollectResult(null);
                            setSocialLeads([]);
                            setSocialCollectTweetUrl("");
                          }
                          if (m === "audiences") setSelectedAudienceId(null);
                        }}
                        leads={socialLeads}
                        leadsLoading={socialCollecting}
                        collecting={socialCollecting}
                        collectResult={socialCollectResult}
                        collectProgress={socialCollectProgress}
                        collectTweetUrl={socialCollectTweetUrl}
                        onCollectTweetUrlChange={setSocialCollectTweetUrl}
                        onCollect={collectReplies}
                        audiencePreFill={audiencePreFill}
                        onCreateAudienceFromTweet={(url, srcAuthor) => {
                          setAudiencePreFill({
                            name: srcAuthor
                              ? `Replies to @${srcAuthor}`
                              : "Tweet Audience",
                            query: url,
                          });
                          setSocialMode("audiences");
                          setSocialLeads([]);
                          setSocialCollectResult(null);
                          setSocialCollectTweetUrl("");
                        }}
                        onClearAudiencePreFill={() => setAudiencePreFill(null)}
                        audiences={socialAudiences}
                        audiencesLoading={socialAudiencesLoading}
                        selectedAudienceId={selectedAudienceId}
                        onSelectAudience={setSelectedAudienceId}
                        collections={socialCollections}
                        selectedCollectionId={selectedCollectionId}
                        onSelectCollection={setSelectedCollectionId}
                        collectionAnswers={socialCollectionAnswers}
                        collectionInsights={socialCollectionInsights}
                        donorLinks={socialDonorLinks}
                        polling={socialPolling}
                        pollProgress={socialPollProgress}
                        onCreateAudience={createAudience}
                        onRefreshAudience={refreshAudience}
                        onPollAudience={pollAudience}
                        onPollFromPost={pollFromPost}
                        onBulkReply={bulkReply}
                        bulkReplyJob={bulkReplyJob}
                        onTagCohort={tagCohort}
                        onPostReply={postReply}
                        onGenerateVariations={generateReplyVariations}
                        onSaveDonorLink={saveDonorLink}
                        activeCampaignId={activeCampaignId}
                        onCollectAll={collectAll}
                        collectingAllTermId={collectingAllTermId}
                        collectAllProgress={collectAllProgress}
                        onDeleteCollection={deleteCollection}
                        onNormalizeCollection={normalizeCollection}
                        onDeleteAudience={deleteAudience}
                        enriching={socialEnriching}
                        enrichProgress={socialEnrichProgress}
                        locating={socialLocating}
                        onEnrichLocations={enrichLocations}
                        onEnrichIdentity={enrichIdentity}
                      />
                    </CardContent>
                  </Card>
                )}
                {/* Daily Brief + signals — Daily Brief tab + other tabs not handled above */}
                {![
                  "Polling",
                  "Prediction Markets",
                  "Finance",
                  "Campaign",
                  "Policy",
                  "Message Orchestration",
                  "News",
                  "Social",
                ].includes(effectiveTab) && (
                  <>
                    <Card
                      elevation={0}
                      sx={{ border: `1px solid ${C.border}`, mb: 2 }}
                    >
                      <CardContent>
                        {(() => {
                          const latestPoll =
                            cd.pollAverages[cd.pollAverages.length - 1] || {};
                          const oppKeys = Object.keys(cd.opponentFR);
                          const primaryOppKey = oppKeys[0];
                          const lead = primaryOppKey
                            ? (
                                latestPoll.candidate -
                                (latestPoll[primaryOppKey] ?? 0)
                              ).toFixed(1)
                            : null;
                          const rfCohRanking =
                            cd.race_finance?.field_analytics?.coh_ranking || [];
                          const rfCands = cd.race_finance?.candidates || [];
                          const rfOurCoh = rfCohRanking.find((c) =>
                            rfCands.find(
                              (x) =>
                                x.name === c.name && x.role === "our_candidate",
                            ),
                          )?.coh;
                          const candidateCoh =
                            (cd.candidateFR.cashOnHand || []).at(-1) ??
                            rfOurCoh ??
                            0;
                          const rfTopOpp = rfCohRanking.find(
                            (c) =>
                              !rfCands.find(
                                (x) =>
                                  x.name === c.name &&
                                  x.role === "our_candidate",
                              ),
                          );
                          const opponentCoh = primaryOppKey
                            ? ((
                                cd.opponentFR[primaryOppKey]?.cashOnHand || []
                              ).at(-1) ??
                              rfTopOpp?.coh ??
                              0)
                            : (rfTopOpp?.coh ?? 0);
                          const briefOpp1 =
                            primaryOppKey || rfTopOpp?.name || null;
                          const hMkts = kalshi?.series?.CONTROLH?.markets || [];
                          const dProb = Math.round(
                            (hMkts.find(
                              (m) =>
                                m.ticker?.endsWith("-D") ||
                                m.title?.toLowerCase().includes("democrat"),
                            )?.last_price ?? 0.86) * 100,
                          );
                          return (
                            <>
                              {/* Brief header */}
                              <Box sx={{ mb: 1.5 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: C.navy,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.09em",
                                      }}
                                    >
                                      Daily Brief
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 10,
                                        color: C.muted,
                                        mt: 0.25,
                                      }}
                                    >
                                      {cfg.office || "Campaign"}{" "}
                                      {cfg.state ? `· ${cfg.state}` : ""}
                                      {cfg.primaryDate
                                        ? ` · ${daysToPrimary}d to primary`
                                        : ""}{" "}
                                      ·{" "}
                                      {briefLoading ? (
                                        <Box
                                          component="span"
                                          sx={{
                                            color: C.blue,
                                            fontWeight: 600,
                                          }}
                                        >
                                          Analyzing…
                                        </Box>
                                      ) : campaignBrief?.generated_at ? (
                                        <Box
                                          component="span"
                                          sx={{
                                            color: "#22c55e",
                                            fontWeight: 700,
                                          }}
                                        >
                                          Updated{" "}
                                          {fmtUpdatedAt(
                                            campaignBrief.generated_at,
                                          )}
                                        </Box>
                                      ) : (
                                        <Box
                                          component="span"
                                          sx={{
                                            color: C.blue,
                                            fontWeight: 600,
                                          }}
                                        >
                                          Analyzing…
                                        </Box>
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box
                                    component="button"
                                    title="Regenerate brief"
                                    onClick={() => {
                                      if (briefLoading || !intelUrl) return;
                                      setCampaignBrief(null);
                                      triggerBriefGeneration();
                                    }}
                                    sx={{
                                      background: "none",
                                      border: "none",
                                      p: 0,
                                      lineHeight: 1,
                                      cursor: briefLoading
                                        ? "not-allowed"
                                        : "pointer",
                                      color: C.muted,
                                      fontSize: 15,
                                      display: "flex",
                                      alignItems: "center",
                                      opacity: briefLoading ? 0.4 : 0.7,
                                      "&:hover": { opacity: 1, color: C.navy },
                                      animation: briefLoading
                                        ? "spin 1s linear infinite"
                                        : "none",
                                      "@keyframes spin": {
                                        from: { transform: "rotate(0deg)" },
                                        to: { transform: "rotate(360deg)" },
                                      },
                                    }}
                                  >
                                    ↻
                                  </Box>
                                </Box>
                                {(campaignBrief?.headline ||
                                  campaignBrief?.lead_signal) && (
                                  <Box
                                    sx={{
                                      mt: 2,
                                      mb: 0.5,
                                      bgcolor: "#f0f4f8",
                                      border: `1px solid ${C.border}`,
                                      borderLeft: `4px solid ${C.navy}`,
                                      borderRadius: "0 4px 4px 0",
                                      px: 2,
                                      py: 1.25,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        color: C.charcoal,
                                        fontStyle: "italic",
                                        lineHeight: 1.25,
                                      }}
                                    >
                                      {campaignBrief.headline ||
                                        campaignBrief.lead_signal}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {/* Signals cards */}
                              {Array.isArray(campaignBrief?.signals) &&
                                campaignBrief.signals.length > 0 && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.75,
                                      mb: 2,
                                    }}
                                  >
                                    {campaignBrief.signals.map((sig, i) => {
                                      const sigColors = {
                                        existential: C.red,
                                        momentum: C.green,
                                        opportunity: C.blue,
                                        context: C.muted,
                                      };
                                      const col =
                                        sigColors[sig.type] || C.muted;
                                      return (
                                        <Box
                                          key={i}
                                          sx={{
                                            bgcolor: col + "14",
                                            border: `1px solid ${col}44`,
                                            borderRadius: 1,
                                            px: 1.25,
                                            py: 0.5,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 0.75,
                                            maxWidth: "100%",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: 8,
                                              fontWeight: 800,
                                              color: col,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.08em",
                                              pt: 0.2,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {sig.type}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: 10,
                                              color: C.charcoal,
                                              lineHeight: 1.5,
                                            }}
                                          >
                                            {sig.text}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}

                              {/* Main narrative */}
                              <Box sx={{ mb: 2 }}>
                                {campaignBrief?.summary ? (
                                  <ReactMarkdown
                                    components={{
                                      p: ({ children }) => (
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            color: C.charcoal,
                                            lineHeight: 1.85,
                                            mb: 1.25,
                                            "&:last-child": { mb: 0 },
                                          }}
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      strong: ({ children }) => (
                                        <strong>{children}</strong>
                                      ),
                                    }}
                                  >
                                    {campaignBrief.summary}
                                  </ReactMarkdown>
                                ) : (
                                  // Single loader until the brief is ready — accompanied by the per-tab
                                  // cascade progress so the user sees the Overview building from each tab.
                                  <IntelLoader
                                    bars={[90, 100, 85, 100, 70, 100, 60]}
                                    barHeight={14}
                                    steps={
                                      briefLoading
                                        ? BRIEF_DEP_TABS.map(
                                            ([slug, label]) => ({
                                              label,
                                              done: !!briefBuildDone[slug],
                                            }),
                                          )
                                        : null
                                    }
                                  />
                                )}

                                {/* Key findings */}
                                {Array.isArray(campaignBrief?.key_findings) &&
                                  campaignBrief.key_findings.length > 0 && (
                                    <Box sx={{ mt: 2, mb: 1.5 }}>
                                      <Typography
                                        sx={{
                                          fontSize: 9,
                                          fontWeight: 800,
                                          color: C.navy,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.08em",
                                          mb: 0.75,
                                        }}
                                      >
                                        Key Findings
                                      </Typography>
                                      {campaignBrief.key_findings.map(
                                        (f, i) => (
                                          <Box
                                            key={i}
                                            sx={{
                                              display: "flex",
                                              alignItems: "flex-start",
                                              gap: 1,
                                              mb: 0.75,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: 5,
                                                height: 5,
                                                borderRadius: "50%",
                                                bgcolor: C.navy,
                                                mt: 0.6,
                                                flexShrink: 0,
                                              }}
                                            />
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: C.charcoal,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {f}
                                            </Typography>
                                          </Box>
                                        ),
                                      )}
                                    </Box>
                                  )}

                                {/* Opponent lens */}
                                {campaignBrief?.opponent_lens && (
                                  <Box
                                    sx={{
                                      bgcolor: "#f8f9fa",
                                      border: `1px solid ${C.border}`,
                                      borderRadius: 1,
                                      px: 2,
                                      py: 1.5,
                                      mb: 2,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 9,
                                        fontWeight: 800,
                                        color: C.red,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.08em",
                                        mb: 0.5,
                                      }}
                                    >
                                      Opponent Lens
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 11,
                                        color: C.charcoal,
                                        lineHeight: 1.7,
                                      }}
                                    >
                                      {campaignBrief.opponent_lens}
                                    </Typography>
                                  </Box>
                                )}

                                {/* Today's actions — prioritized list synthesized across all tabs + social */}
                                {Array.isArray(campaignBrief?.action_list) &&
                                campaignBrief.action_list.length > 0 ? (
                                  <Box
                                    sx={{
                                      bgcolor: C.navy,
                                      borderRadius: 1,
                                      px: 2,
                                      py: 1.5,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: "rgba(255,255,255,0.5)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.1em",
                                        mb: 0.75,
                                      }}
                                    >
                                      Today's Actions
                                    </Typography>
                                    {campaignBrief.action_list.map((a, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          gap: 1,
                                          mb:
                                            i <
                                            campaignBrief.action_list.length - 1
                                              ? 0.85
                                              : 0,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: 12,
                                            fontWeight: 800,
                                            color: C.orange,
                                            lineHeight: 1.55,
                                            flexShrink: 0,
                                            minWidth: 14,
                                          }}
                                        >
                                          {i + 1}.
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: "#fff",
                                            lineHeight: 1.55,
                                          }}
                                        >
                                          {a}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                ) : campaignBrief?.recommended_action ||
                                  campaignBrief?.todays_action ? (
                                  <Box
                                    sx={{
                                      bgcolor: C.navy,
                                      borderRadius: 1,
                                      px: 2,
                                      py: 1.5,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: "rgba(255,255,255,0.5)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.1em",
                                        mb: 0.5,
                                      }}
                                    >
                                      Recommended Action
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: "#fff",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {campaignBrief.recommended_action ||
                                        campaignBrief.todays_action}
                                    </Typography>
                                  </Box>
                                ) : null}
                              </Box>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>

                    {/* Across the Tabs + Voter Sentiment moved into the right rail (see Daily Brief right column) */}

                    {effectiveTab !== "Daily Brief" && (
                      <Card
                        elevation={0}
                        sx={{ border: `1px solid ${C.border}` }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 2,
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color: C.navy,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.09em",
                                }}
                              >
                                Polling &amp; Fundraising Correlation
                              </Typography>
                              <Typography
                                sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}
                              >
                                WinRed bars (right axis) · Poll % lines (left
                                axis) · primary race
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ height: 300 }}>
                            <Bar
                              data={correlationChartData}
                              options={correlationOpts}
                            />
                          </Box>

                          {/* Campaign timeline */}
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: `1px solid ${C.border}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.75,
                              }}
                            >
                              <Typography sx={{ fontSize: 10, color: C.muted }}>
                                Declaration
                                {cfg.declarationDate
                                  ? ` · ${new Date(cfg.declarationDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`
                                  : ""}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: stage.color,
                                  fontWeight: 700,
                                }}
                              >
                                Primary · {fmtDate(cfg.primaryDate)}
                              </Typography>
                              <Typography sx={{ fontSize: 10, color: C.muted }}>
                                Election · {fmtDate(cfg.electionDate)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                position: "relative",
                                height: 6,
                                bgcolor: "#e2e8f0",
                                borderRadius: 3,
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  height: "100%",
                                  width: `${timelinePct}%`,
                                  bgcolor: C.navy,
                                  borderRadius: 3,
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 10,
                                color: C.muted,
                                mt: 0.5,
                                textAlign: "center",
                              }}
                            >
                              {timelinePct}% of campaign elapsed
                            </Typography>
                          </Box>

                          {/* Supplemental signals */}
                          {(() => {
                            const latestPoll =
                              cd.pollAverages[cd.pollAverages.length - 1] || {};
                            const suppOppKeys = Object.keys(cd.opponentFR);
                            const suppOpp1 = suppOppKeys[0];
                            const candLead = suppOpp1
                              ? (
                                  latestPoll.candidate -
                                  (latestPoll[suppOpp1] ?? 0)
                                ).toFixed(1)
                              : null;
                            const candidateCoh =
                              (cd.candidateFR.cashOnHand || []).at(-1) ?? 0;
                            const oppCoh = suppOpp1
                              ? ((cd.opponentFR[suppOpp1]?.cashOnHand || []).at(
                                  -1,
                                ) ?? 0)
                              : 0;
                            const hMkts =
                              kalshi?.series?.CONTROLH?.markets || [];
                            const dProb =
                              (hMkts.find(
                                (m) =>
                                  m.ticker?.endsWith("-D") ||
                                  m.title?.toLowerCase().includes("democrat"),
                              )?.last_price ?? 0.86) * 100;
                            const recProb =
                              (kalshi?.series?.KXRECSSNBER?.markets?.[0]
                                ?.last_price ?? 0.23) * 100;
                            const signals = [
                              ...(candLead != null
                                ? [
                                    {
                                      label: `${cfg.candidateName || "Candidate"} vs ${suppOpp1} (poll)`,
                                      value: `${parseFloat(candLead) >= 0 ? "+" : ""}${candLead}`,
                                      sig:
                                        parseFloat(candLead) >= 0
                                          ? "Tailwind"
                                          : "Headwind",
                                      interp:
                                        "Polling lead entering final sprint",
                                    },
                                  ]
                                : []),
                              {
                                label: "D wins House (Kalshi)",
                                value: `${Math.round(dProb)}%`,
                                sig: "Strong Headwind",
                                interp:
                                  "Anti-R wave creates outsider/challenger narrative asset for primary",
                              },
                              ...(suppOpp1
                                ? [
                                    {
                                      label: `CoH vs ${suppOpp1}`,
                                      value:
                                        candidateCoh - oppCoh >= 0
                                          ? `+${fmtMoney(candidateCoh - oppCoh)}`
                                          : `-${fmtMoney(oppCoh - candidateCoh)}`,
                                      sig:
                                        candidateCoh >= oppCoh
                                          ? "Tailwind"
                                          : "Headwind",
                                      interp:
                                        "Cash-on-hand position heading into primary",
                                    },
                                  ]
                                : []),
                              {
                                label: "Recession risk (Kalshi)",
                                value: `${Math.round(recProb)}%`,
                                sig: "Headwind",
                                interp:
                                  "Economic anxiety suppresses giving; fiscal responsibility frame required",
                              },
                            ];
                            return (
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 2,
                                  borderTop: `1px solid ${C.border}`,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: C.navy,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.09em",
                                    mb: 1,
                                  }}
                                >
                                  Supplemental Signals
                                </Typography>
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                  }}
                                >
                                  <thead>
                                    <tr>
                                      {[
                                        "Signal",
                                        "Value",
                                        "Interpretation",
                                      ].map((h) => (
                                        <th
                                          key={h}
                                          style={{
                                            fontSize: 9,
                                            fontWeight: 700,
                                            color: C.muted,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.07em",
                                            padding: "4px 6px",
                                            borderBottom: `1px solid ${C.border}`,
                                            textAlign: "left",
                                          }}
                                        >
                                          {h}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {signals.map((s, i) => (
                                      <tr
                                        key={i}
                                        style={{
                                          backgroundColor:
                                            i % 2 === 1
                                              ? C.rowAlt
                                              : "transparent",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "5px 6px",
                                            fontSize: 10,
                                            fontWeight: 600,
                                            color: C.charcoal,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {s.label}
                                        </td>
                                        <td style={{ padding: "5px 6px" }}>
                                          <Box
                                            component="span"
                                            sx={{
                                              fontSize: 10,
                                              fontWeight: 700,
                                              px: 0.75,
                                              py: 0.2,
                                              borderRadius: "3px",
                                              bgcolor:
                                                (SIGNAL_COLORS[s.sig] ||
                                                  C.muted) + "18",
                                              border: `1px solid ${SIGNAL_COLORS[s.sig] || C.muted}44`,
                                              color:
                                                SIGNAL_COLORS[s.sig] || C.muted,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {s.value}
                                          </Box>
                                        </td>
                                        <td
                                          style={{
                                            padding: "5px 6px",
                                            fontSize: 10,
                                            color: C.muted,
                                            lineHeight: 1.4,
                                          }}
                                        >
                                          {s.interp}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </Box>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </Box>

              {/* Right: Tab content */}
              {tab !== "Voters" && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  <Card elevation={0} sx={{ border: `1px solid ${C.border}` }}>
                    <CardContent>
                      {effectiveTab === "Daily Brief" &&
                        (() => {
                          const lnk = (s) =>
                            (s || "").split(" ").pop().toLowerCase();
                          const latestPoll =
                            cd.pollAverages[cd.pollAverages.length - 1] || {};
                          const hasPolling =
                            (cd.pollAverages?.length || 0) > 0 &&
                            typeof latestPoll.candidate === "number";
                          const candidateLastName = lnk(cfg.candidateName);
                          const candShort = (cfg.candidateName || "Candidate")
                            .split(" ")
                            .pop();

                          // ACTIVE opponent — ARCHITECTURE tenet: race_status (Ballotpedia) is the SOLE source of
                          // truth for who is still running. NEVER pick by cash-on-hand (a dropped or other-party
                          // candidate can hold the most cash — that was the "Billy Webster" bug).
                          const activeOpp =
                            (cd.candidatesGop || []).find((c) => {
                              const st = (c.status || "").toLowerCase();
                              return (
                                lnk(c.name) !== candidateLastName &&
                                (st === "running" || st === "")
                              );
                            }) || null;
                          const briefOpp1 = activeOpp?.name || null;
                          const oppShort = briefOpp1
                            ? briefOpp1.split(" ").pop()
                            : null;

                          // Finance — match by last name; CoH from candidateFR / race_finance.
                          const rfCands2 = cd.race_finance?.candidates || [];
                          // CoH lives in field_analytics.coh_ranking (SC-Ethics-sourced for state races),
                          // NOT candidates[].coh — restoring that lookup (the rewrite had dropped it → blank CoH).
                          const rfCohRanking =
                            cd.race_finance?.field_analytics?.coh_ranking || [];
                          const cohOf = (nm) =>
                            rfCohRanking.find((c) => lnk(c.name) === lnk(nm))
                              ?.coh ??
                            rfCands2.find((x) => lnk(x.name) === lnk(nm))?.coh;
                          const candidateCoh =
                            (cd.candidateFR.cashOnHand || []).at(-1) ??
                            cohOf(cfg.candidateName) ??
                            rfCands2.find((x) => x.role === "our_candidate")
                              ?.coh ??
                            0;
                          const opponentCoh = briefOpp1
                            ? ((cd.opponentFR[briefOpp1]?.cashOnHand || []).at(
                                -1,
                              ) ??
                              cohOf(briefOpp1) ??
                              0)
                            : 0;
                          const cohGap = candidateCoh - opponentCoh;

                          // Poll lead only when we actually have polling for both candidate and the active opponent.
                          const candLead =
                            hasPolling &&
                            briefOpp1 &&
                            typeof latestPoll[briefOpp1] === "number"
                              ? (
                                  latestPoll.candidate - latestPoll[briefOpp1]
                                ).toFixed(1)
                              : null;

                          // Kalshi race markets = primary-nominee win probabilities — the resilient stand-in for
                          // polling when no public polls exist. cfg.kalshiSeries[0] is the primary race series.
                          const raceSeries = cfg.kalshiSeries?.[0];
                          const raceMarkets =
                            (raceSeries &&
                              kalshi?.series?.[raceSeries]?.markets) ||
                            [];
                          const candidateRaceMarket =
                            raceMarkets.find(
                              (m) =>
                                candidateLastName &&
                                m.title
                                  ?.toLowerCase()
                                  .includes(candidateLastName),
                            ) || null;
                          const candidateRaceProb =
                            candidateRaceMarket?.last_price ?? null;
                          const oppRaceProb = briefOpp1
                            ? (raceMarkets.find((m) =>
                                m.title?.toLowerCase().includes(lnk(briefOpp1)),
                              )?.last_price ?? null)
                            : null;
                          const marketOdds = raceMarkets
                            .map((m) => ({
                              name:
                                (m.title || "").match(
                                  /Will\s+(.+?)\s+be\s+the/i,
                                )?.[1] ||
                                m.title ||
                                "",
                              prob: m.last_price,
                            }))
                            .filter((x) => x.prob != null)
                            .sort((a, b) => b.prob - a.prob)
                            .slice(0, 4);

                          const hMkts = kalshi?.series?.CONTROLH?.markets || [];
                          const dProb =
                            (hMkts.find(
                              (m) =>
                                m.ticker?.endsWith("-D") ||
                                m.title?.toLowerCase().includes("democrat"),
                            )?.last_price ?? 0.86) * 100;
                          const recProb =
                            (kalshi?.series?.KXRECSSNBER?.markets?.[0]
                              ?.last_price ?? 0.23) * 100;

                          const signals = [
                            ...(candLead != null
                              ? [
                                  {
                                    label: `${candShort} vs ${oppShort} (poll)`,
                                    value: `${parseFloat(candLead) >= 0 ? "+" : ""}${candLead}pts`,
                                    sig:
                                      parseFloat(candLead) >= 0
                                        ? "Tailwind"
                                        : "Headwind",
                                    interp:
                                      "Polling lead over the active opponent",
                                  },
                                ]
                              : []),
                            {
                              label: "D wins House (Kalshi)",
                              value: `${Math.round(dProb)}%`,
                              sig: "Strong Headwind",
                              interp:
                                "Anti-R national wave — outsider/challenger narrative asset",
                            },
                            {
                              label: "Recession risk (Kalshi)",
                              value: `${Math.round(recProb)}%`,
                              sig: "Headwind",
                              interp:
                                "Economic anxiety suppresses giving; fiscal-responsibility frame",
                            },
                          ];
                          const labelStyle = {
                            fontSize: 9,
                            fontWeight: 800,
                            color: C.muted,
                            textTransform: "uppercase",
                            letterSpacing: "0.09em",
                            mb: 1.5,
                            mt: 2.5,
                          };
                          return (
                            <Box>
                              {/* ── Win-probability hero (market-implied — resilient when there's no polling) ── */}
                              {candidateRaceProb != null && (
                                <Box
                                  sx={{
                                    bgcolor: "#f0f4f8",
                                    border: `1px solid ${C.border}`,
                                    borderLeft: `4px solid ${candidateRaceProb >= 0.5 ? "#16a34a" : C.red}`,
                                    borderRadius: "0 6px 6px 0",
                                    px: 2,
                                    py: 1.5,
                                    mb: 2,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 9,
                                      fontWeight: 800,
                                      color: C.muted,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.08em",
                                      mb: 0.25,
                                    }}
                                  >
                                    {candidateRaceMarket?.title ||
                                      `${candShort} win probability`}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "baseline",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 38,
                                        fontWeight: 800,
                                        color:
                                          candidateRaceProb >= 0.5
                                            ? "#16a34a"
                                            : C.red,
                                        lineHeight: 1,
                                      }}
                                    >
                                      {Math.round(candidateRaceProb * 100)}%
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: 10, color: C.muted }}
                                    >
                                      market-implied · Kalshi
                                      {oppRaceProb != null && oppShort
                                        ? ` · ${oppShort} ${Math.round(oppRaceProb * 100)}%`
                                        : ""}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              {/* ── At a Glance ── */}
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color: C.navy,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.09em",
                                  mb: 0.25,
                                }}
                              >
                                At a Glance
                              </Typography>
                              <Typography
                                sx={{ fontSize: 10, color: C.muted, mb: 1.5 }}
                              >
                                Key signals across all tabs
                              </Typography>

                              {/* Race-state chips — always populated (no poll dependency) */}
                              {(() => {
                                const chips = [
                                  {
                                    l: "Stage",
                                    v: stage.label || "—",
                                    c: stage.color,
                                  },
                                  {
                                    l: `${candShort} CoH`,
                                    v: candidateCoh
                                      ? fmtMoney(candidateCoh)
                                      : "—",
                                    c: C.navy,
                                  },
                                  briefOpp1 && opponentCoh
                                    ? {
                                        l: `CoH vs ${oppShort}`,
                                        v: `${cohGap >= 0 ? "+" : "-"}${fmtMoney(Math.abs(cohGap))}`,
                                        c: cohGap >= 0 ? "#16a34a" : C.red,
                                      }
                                    : {
                                        l: "D Wins House",
                                        v: `${Math.round(dProb)}%`,
                                        c: C.red,
                                      },
                                ];
                                return (
                                  <Box
                                    sx={{ display: "flex", gap: 1.5, mb: 2 }}
                                  >
                                    {chips.map((s) => (
                                      <Box
                                        key={s.l}
                                        sx={{
                                          flex: 1,
                                          textAlign: "center",
                                          px: 1.5,
                                          pt: 0.75,
                                          pb: 1,
                                          bgcolor: C.white,
                                          border: `1px solid ${C.border}`,
                                          borderTop: `3px solid ${s.c}`,
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: 9,
                                            color: C.muted,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.06em",
                                            whiteSpace: "nowrap",
                                            mb: 0.5,
                                          }}
                                        >
                                          {s.l}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 20,
                                            fontWeight: 800,
                                            color: s.c,
                                            lineHeight: 1,
                                          }}
                                        >
                                          {s.v}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                );
                              })()}

                              {/* Primary field — polling if we have it, else live Kalshi nominee odds */}
                              {hasPolling ? (
                                <>
                                  <Typography sx={labelStyle}>
                                    Current Polling · GOP Primary
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1.5,
                                      mb: 0.5,
                                    }}
                                  >
                                    {[
                                      {
                                        name: cfg.candidateName || "Candidate",
                                        key: "candidate",
                                        color: C.red,
                                      },
                                      ...Object.keys(latestPoll)
                                        .filter(
                                          (k) =>
                                            k !== "candidate" &&
                                            k !== "Undecided" &&
                                            typeof latestPoll[k] === "number",
                                        )
                                        .slice(0, 3)
                                        .map((k, i) => ({
                                          name: k,
                                          key: k,
                                          color: [C.navyMid, C.blue, C.muted][
                                            i
                                          ],
                                        })),
                                    ].map((c) => (
                                      <Box
                                        key={c.key}
                                        sx={{
                                          bgcolor: C.white,
                                          border: `1px solid ${C.border}`,
                                          borderTop: `3px solid ${c.color}`,
                                          p: 1.25,
                                          borderRadius: 1,
                                          flex: "1 1 100px",
                                          minWidth: 95,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: 8.5,
                                            fontWeight: 700,
                                            color: C.muted,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.05em",
                                            mb: 0.25,
                                          }}
                                        >
                                          {c.name.split(" ").pop()}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 22,
                                            fontWeight: 800,
                                            color: c.color,
                                            lineHeight: 1,
                                          }}
                                        >
                                          {latestPoll[c.key] != null
                                            ? `${latestPoll[c.key]}%`
                                            : "—"}
                                        </Typography>
                                      </Box>
                                    ))}
                                    <Box
                                      sx={{
                                        bgcolor: C.white,
                                        border: `1px solid ${C.border}`,
                                        borderTop: `3px solid ${C.muted}`,
                                        p: 1.25,
                                        borderRadius: 1,
                                        flex: "1 1 100px",
                                        minWidth: 95,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 8.5,
                                          fontWeight: 700,
                                          color: C.muted,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.05em",
                                          mb: 0.25,
                                        }}
                                      >
                                        Undecided
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: 22,
                                          fontWeight: 800,
                                          color: C.muted,
                                          lineHeight: 1,
                                        }}
                                      >
                                        {latestPoll.Undecided != null
                                          ? `${latestPoll.Undecided}%`
                                          : "—"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </>
                              ) : marketOdds.length > 0 ? (
                                <>
                                  <Typography sx={labelStyle}>
                                    Market Odds ·{" "}
                                    {cfg.party === "Democrat" ? "Dem" : "GOP"}{" "}
                                    Primary{" "}
                                    <Box
                                      component="span"
                                      sx={{
                                        color: C.muted,
                                        fontWeight: 600,
                                        textTransform: "none",
                                        letterSpacing: 0,
                                      }}
                                    >
                                      · no public polling — Kalshi nominee
                                      market
                                    </Box>
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1.5,
                                    }}
                                  >
                                    {marketOdds.map((m, i) => {
                                      const isSelf =
                                        lnk(m.name) === candidateLastName;
                                      return (
                                        <Box
                                          key={i}
                                          sx={{
                                            bgcolor: C.white,
                                            border: `1px solid ${C.border}`,
                                            borderTop: `3px solid ${isSelf ? "#16a34a" : C.muted}`,
                                            p: 1.25,
                                            borderRadius: 1,
                                            flex: "1 1 100px",
                                            minWidth: 95,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: 8.5,
                                              fontWeight: 700,
                                              color: C.muted,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.05em",
                                              mb: 0.25,
                                            }}
                                          >
                                            {m.name.split(" ").pop()}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: 22,
                                              fontWeight: 800,
                                              color: isSelf
                                                ? "#16a34a"
                                                : C.charcoal,
                                              lineHeight: 1,
                                            }}
                                          >
                                            {Math.round(m.prob * 100)}%
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </>
                              ) : null}

                              {/* ── Polling & Fundraising Correlation (only when polling exists — no empty axis) ── */}
                              <Box
                                sx={{
                                  mt: hasPolling ? 3 : 0,
                                  pt: hasPolling ? 2.5 : 0,
                                  borderTop: hasPolling
                                    ? `1px solid ${C.border}`
                                    : "none",
                                }}
                              >
                                {hasPolling && (
                                  <>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        mb: 2,
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            fontWeight: 800,
                                            color: C.navy,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.09em",
                                          }}
                                        >
                                          Polling &amp; Fundraising Correlation
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 10,
                                            color: C.muted,
                                            mt: 0.25,
                                          }}
                                        >
                                          WinRed bars (right axis) · Poll %
                                          lines (left axis) · primary race
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box sx={{ height: 300 }}>
                                      <Bar
                                        data={correlationChartData}
                                        options={correlationOpts}
                                      />
                                    </Box>
                                  </>
                                )}

                                {/* Campaign timeline */}
                                <Box
                                  sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${C.border}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      mb: 0.75,
                                    }}
                                  >
                                    <Typography
                                      sx={{ fontSize: 10, color: C.muted }}
                                    >
                                      Declaration
                                      {cfg.declarationDate
                                        ? ` · ${new Date(cfg.declarationDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`
                                        : ""}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 10,
                                        color: C.red,
                                        fontWeight: 700,
                                      }}
                                    >
                                      Primary · {fmtDate(cfg.primaryDate)}
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: 10, color: C.muted }}
                                    >
                                      Election · {fmtDate(cfg.electionDate)}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      position: "relative",
                                      height: 6,
                                      bgcolor: "#e2e8f0",
                                      borderRadius: 3,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${timelinePct}%`,
                                        bgcolor: C.navy,
                                        borderRadius: 3,
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      color: C.muted,
                                      mt: 0.5,
                                      textAlign: "center",
                                    }}
                                  >
                                    {timelinePct}% of campaign elapsed
                                  </Typography>
                                </Box>

                                {/* Supplemental Signals */}
                                <Box
                                  sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${C.border}`,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 800,
                                      color: C.navy,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.09em",
                                      mb: 1,
                                    }}
                                  >
                                    Supplemental Signals
                                  </Typography>
                                  <table
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                    }}
                                  >
                                    <thead>
                                      <tr>
                                        {[
                                          "Signal",
                                          "Value",
                                          "Interpretation",
                                        ].map((h) => (
                                          <th
                                            key={h}
                                            style={{
                                              fontSize: 9,
                                              fontWeight: 700,
                                              color: C.muted,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.07em",
                                              padding: "4px 6px",
                                              borderBottom: `1px solid ${C.border}`,
                                              textAlign: "left",
                                            }}
                                          >
                                            {h}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {signals.map((s, i) => (
                                        <tr
                                          key={i}
                                          style={{
                                            backgroundColor:
                                              i % 2 === 1
                                                ? C.rowAlt
                                                : "transparent",
                                          }}
                                        >
                                          <td
                                            style={{
                                              padding: "5px 6px",
                                              fontSize: 10,
                                              fontWeight: 600,
                                              color: C.charcoal,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {s.label}
                                          </td>
                                          <td style={{ padding: "5px 6px" }}>
                                            <Box
                                              component="span"
                                              sx={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                px: 0.75,
                                                py: 0.2,
                                                borderRadius: "3px",
                                                bgcolor:
                                                  (SIGNAL_COLORS[s.sig] ||
                                                    C.muted) + "18",
                                                border: `1px solid ${SIGNAL_COLORS[s.sig] || C.muted}44`,
                                                color:
                                                  SIGNAL_COLORS[s.sig] ||
                                                  C.muted,
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {s.value}
                                            </Box>
                                          </td>
                                          <td
                                            style={{
                                              padding: "5px 6px",
                                              fontSize: 10,
                                              color: C.muted,
                                              lineHeight: 1.4,
                                            }}
                                          >
                                            {s.interp}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </Box>
                              </Box>
                              {/* end correlation wrapper */}

                              {/* ── Across the Tabs (in the right rail to balance the layout) ── */}
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 2,
                                  borderTop: `1px solid ${C.border}`,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: C.navy,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.09em",
                                    mb: 0.25,
                                  }}
                                >
                                  Across the Tabs
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 10, color: C.muted, mb: 1.5 }}
                                >
                                  The signal driving each tab · click to open
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1.25,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {BRIEF_DEP_TABS.map(([slug, label]) => {
                                    const s = tabSummaries[label];
                                    const ready =
                                      s?.status === "completed" && s?.headline;
                                    const sig =
                                      Array.isArray(s?.signals) &&
                                      s.signals[0]?.type;
                                    const sigColors = {
                                      existential: C.red,
                                      momentum: C.green,
                                      opportunity: C.blue,
                                      context: C.muted,
                                    };
                                    const dot = sigColors[sig] || C.border;
                                    return (
                                      <Box
                                        key={slug}
                                        onClick={() => setBriefSubTab(label)}
                                        sx={{
                                          flex: "1 1 100%",
                                          border: `1px solid ${C.border}`,
                                          borderLeft: `3px solid ${ready ? dot : C.border}`,
                                          borderRadius: 1,
                                          p: 1.25,
                                          cursor: "pointer",
                                          transition: "box-shadow 0.15s",
                                          "&:hover": {
                                            boxShadow:
                                              "0 2px 10px rgba(0,0,0,0.10)",
                                            borderColor: C.navy,
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 0.25,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: 9,
                                              fontWeight: 800,
                                              color: C.navy,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.07em",
                                            }}
                                          >
                                            {label}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: 12,
                                              color: C.muted,
                                            }}
                                          >
                                            →
                                          </Typography>
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontSize: 10.5,
                                            color: ready ? C.charcoal : C.muted,
                                            lineHeight: 1.5,
                                            fontStyle: ready
                                              ? "normal"
                                              : "italic",
                                          }}
                                        >
                                          {ready
                                            ? s.headline
                                            : "Not yet generated"}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>

                              {/* ── Voter Sentiment ── */}
                              {Array.isArray(campaignBrief?.voter_sentiment) &&
                                campaignBrief.voter_sentiment.length > 0 && (
                                  <Box
                                    sx={{
                                      mt: 2,
                                      pt: 2,
                                      borderTop: `1px solid ${C.border}`,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        color: C.navy,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.09em",
                                        mb: 0.25,
                                      }}
                                    >
                                      Voter Sentiment
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 10,
                                        color: C.muted,
                                        mb: 1.5,
                                      }}
                                    >
                                      Latest social polling · click to open
                                      Social
                                    </Typography>
                                    {campaignBrief.voter_sentiment.map(
                                      (p, pi) => (
                                        <Box
                                          key={pi}
                                          onClick={() =>
                                            setPrimaryTab("Social")
                                          }
                                          sx={{
                                            mb:
                                              pi <
                                              campaignBrief.voter_sentiment
                                                .length -
                                                1
                                                ? 2
                                                : 0,
                                            pb:
                                              pi <
                                              campaignBrief.voter_sentiment
                                                .length -
                                                1
                                                ? 2
                                                : 0,
                                            borderBottom:
                                              pi <
                                              campaignBrief.voter_sentiment
                                                .length -
                                                1
                                                ? `1px solid ${C.border}`
                                                : "none",
                                            cursor: "pointer",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: 11.5,
                                              fontWeight: 700,
                                              color: C.charcoal,
                                              lineHeight: 1.5,
                                              mb: 0.75,
                                            }}
                                          >
                                            {p.question}
                                          </Typography>
                                          {(p.top || [])
                                            .slice(0, 4)
                                            .map((t, ti) => (
                                              <Box key={ti} sx={{ mb: 0.6 }}>
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                      "space-between",
                                                    mb: 0.2,
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: 10.5,
                                                      color: C.charcoal,
                                                    }}
                                                  >
                                                    {t.answer}
                                                  </Typography>
                                                  <Typography
                                                    sx={{
                                                      fontSize: 10.5,
                                                      fontWeight: 700,
                                                      color: C.navy,
                                                    }}
                                                  >
                                                    {t.pct}%
                                                  </Typography>
                                                </Box>
                                                <Box
                                                  sx={{
                                                    height: 5,
                                                    bgcolor: C.bg,
                                                    borderRadius: 3,
                                                    overflow: "hidden",
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      width: `${t.pct}%`,
                                                      height: "100%",
                                                      bgcolor: C.navy,
                                                    }}
                                                  />
                                                </Box>
                                              </Box>
                                            ))}
                                          <Typography
                                            sx={{
                                              fontSize: 9.5,
                                              color: C.muted,
                                              mt: 0.5,
                                            }}
                                          >
                                            {(
                                              p.responded || 0
                                            ).toLocaleString()}{" "}
                                            responded
                                          </Typography>
                                          {p.key_finding && (
                                            <Typography
                                              sx={{
                                                fontSize: 10.5,
                                                color: C.charcoal,
                                                lineHeight: 1.6,
                                                mt: 0.75,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              {p.key_finding}
                                            </Typography>
                                          )}
                                        </Box>
                                      ),
                                    )}
                                  </Box>
                                )}
                            </Box>
                          );
                        })()}
                      {effectiveTab === "Finance" && (
                        <FinanceTab
                          winredData={winredData}
                          winredDaily={winredDaily}
                          winredLoading={winredLoading}
                          winredDailyLoading={winredDailyLoading}
                          liveFEC={liveFEC}
                          kalshi={kalshi}
                          donorCohorts={donorCohorts}
                          cd={cd}
                          cfg={cfg}
                          raceFinance={cd.race_finance}
                          opponentIntel={opponentIntel}
                          onGenerateOpponentIntel={generateOpponentIntel}
                          opponentIntelLoading={opponentIntelLoading}
                          activeCampaignId={activeCampaignId}
                        />
                      )}
                      {effectiveTab === "Campaign" && (
                        <CampaignTab
                          kalshi={kalshi}
                          kalshiLoading={kalshiLoading}
                          cd={cd}
                          cfg={cfg}
                          liveFEC={liveFEC}
                          opponentIntel={opponentIntel}
                          onGenerateOpponentIntel={generateOpponentIntel}
                          opponentIntelLoading={opponentIntelLoading}
                        />
                      )}
                      {effectiveTab === "Polling" && (
                        <PollingTab
                          kalshi={kalshi}
                          livePolls={livePolls}
                          liveFEC={liveFEC}
                          onRefreshPolls={refreshPolls}
                          pollsRefreshing={pollsRefreshing}
                          pollingSummary={pollingSummary}
                          pollingSummaryLoading={pollingSummaryLoading}
                          cfg={cfg}
                          section="charts"
                          cd={cd}
                          opponentIntel={opponentIntel}
                          onGenerateOpponentIntel={generateOpponentIntel}
                          opponentIntelLoading={opponentIntelLoading}
                          onSaveManualPolls={saveManualPolls}
                        />
                      )}
                      {effectiveTab === "Prediction Markets" && (
                        <MacroTab
                          kalshi={kalshi}
                          kalshiLoading={kalshiLoading}
                          fetchedAt={kalshiFetchedAt}
                          onRefresh={() => {
                            setKalshiRefresh((k) => k + 1);
                            const _dsUrl = PHP_API_URL
                              ? `${PHP_API_URL}/data-sync`
                              : "";
                            if (_dsUrl && activeCampaignId && authFetch) {
                              authFetch(
                                `${_dsUrl}/trigger?campaign_id=${activeCampaignId}&regenerate=true`,
                                { method: "POST" },
                              ).catch(() => {});
                            }
                          }}
                          kalshiHistory={kalshiHistory}
                          historyLoading={historyLoading}
                          cfg={cfg}
                        />
                      )}
                      {effectiveTab === "Policy" && (
                        <PolicyTab
                          cd={cd}
                          cfg={cfg}
                          opponentIntel={opponentIntel}
                          onGenerateOpponentIntel={generateOpponentIntel}
                          opponentIntelLoading={opponentIntelLoading}
                          policyPositionsLoading={policyPositionsLoading}
                          onRegeneratePositions={generatePolicyPositions}
                        />
                      )}
                      {tab === "Message Orchestration" && (
                        <MessagingTab
                          cfg={cfg}
                          kalshi={kalshi}
                          winredData={winredData}
                          liveTotalRaised={liveTotalRaised}
                          liveLastMonth={liveLastMonth}
                          liveVelocityPct={liveVelocityPct}
                          liveTotalDonors={liveTotalDonors}
                          cd={cd}
                          liveFEC={liveFEC}
                          section="content"
                          activeCampaignId={activeCampaignId}
                          messagingIntelCbRef={messagingIntelCbRef}
                          messagingImageCbRef={messagingImageCbRef}
                        />
                      )}
                      {effectiveTab === "News" && (
                        <NewsTab
                          newsData={newsData}
                          newsLoading={newsLoading}
                          onRefresh={() => fetchNews(true)}
                          cfg={cfg}
                          cd={cd}
                          opponentIntel={opponentIntel}
                          onGenerateOpponentIntel={generateOpponentIntel}
                          opponentIntelLoading={opponentIntelLoading}
                        />
                      )}
                      {effectiveTab === "Social" &&
                        socialMode !== "discovery" &&
                        socialMode !== "topics" && (
                          <SocialTab
                            section="feed"
                            cfg={cfg}
                            allMentions={
                              socialSelectedTerm && socialTermMentions !== null
                                ? socialTermMentions
                                : socialMentions
                            }
                            loading={socialLoading}
                            syncing={socialSyncing}
                            syncResult={socialSyncResult}
                            lastSynced={socialLastSynced}
                            error={socialError}
                            searchQuery={socialSearchQuery}
                            filter={socialFilter}
                            authorFilter={socialAuthorFilter}
                            page={socialPage}
                            onFilterChange={(f) => {
                              setSocialFilter(f);
                              setSocialPage(1);
                            }}
                            onAuthorChange={(id) => {
                              setSocialAuthorFilter(id);
                              setSocialPage(1);
                            }}
                            onPageChange={setSocialPage}
                            onSync={syncSocial}
                            terms={socialTerms}
                            termsLoading={socialTermsLoading}
                            selectedTerm={socialSelectedTerm}
                            onSelectTerm={setSocialSelectedTerm}
                            onAddTerm={addTerm}
                            onDeleteTerm={deleteTerm}
                            onRefreshTerm={refreshTerm}
                            onUpdateTerm={updateTerm}
                            mode={socialMode}
                            onModeChange={(m) => {
                              setSocialMode(m);
                              if (m === "collect") {
                                setSocialCollectResult(null);
                                setSocialLeads([]);
                                setSocialCollectTweetUrl("");
                              }
                              if (m === "audiences")
                                setSelectedAudienceId(null);
                            }}
                            leads={socialLeads}
                            leadsLoading={socialCollecting}
                            collecting={socialCollecting}
                            collectResult={socialCollectResult}
                            collectProgress={socialCollectProgress}
                            collectTweetUrl={socialCollectTweetUrl}
                            onCollectTweetUrlChange={setSocialCollectTweetUrl}
                            onCollect={collectReplies}
                            onCollectCard={(url) => {
                              setSocialMode("collect");
                              setSocialCollectTweetUrl(url);
                              collectReplies(url);
                            }}
                            audiencePreFill={audiencePreFill}
                            onCreateAudienceFromTweet={(url, srcAuthor) => {
                              setAudiencePreFill({
                                name: srcAuthor
                                  ? `Replies to @${srcAuthor}`
                                  : "Tweet Audience",
                                query: url,
                              });
                              setSocialMode("audiences");
                              setSocialLeads([]);
                              setSocialCollectResult(null);
                              setSocialCollectTweetUrl("");
                            }}
                            onClearAudiencePreFill={() =>
                              setAudiencePreFill(null)
                            }
                            audiences={socialAudiences}
                            audiencesLoading={socialAudiencesLoading}
                            selectedAudienceId={selectedAudienceId}
                            onSelectAudience={setSelectedAudienceId}
                            collections={socialCollections}
                            selectedCollectionId={selectedCollectionId}
                            onSelectCollection={setSelectedCollectionId}
                            collectionAnswers={socialCollectionAnswers}
                            collectionInsights={socialCollectionInsights}
                            donorLinks={socialDonorLinks}
                            polling={socialPolling}
                            pollProgress={socialPollProgress}
                            onCreateAudience={createAudience}
                            onRefreshAudience={refreshAudience}
                            onPollAudience={pollAudience}
                            onPollFromPost={pollFromPost}
                            onBulkReply={bulkReply}
                            bulkReplyJob={bulkReplyJob}
                            onTagCohort={tagCohort}
                            onPostReply={postReply}
                            onGenerateVariations={generateReplyVariations}
                            onSaveDonorLink={saveDonorLink}
                            activeCampaignId={activeCampaignId}
                            onCollectAll={collectAll}
                            collectingAllTermId={collectingAllTermId}
                            collectAllProgress={collectAllProgress}
                            onDeleteCollection={deleteCollection}
                            onNormalizeCollection={normalizeCollection}
                            onDeleteAudience={deleteAudience}
                            enriching={socialEnriching}
                            enrichProgress={socialEnrichProgress}
                            locating={socialLocating}
                            onEnrichLocations={enrichLocations}
                            onEnrichIdentity={enrichIdentity}
                            onOpenProfile={openProfile}
                          />
                        )}
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <Box sx={{ px: 3, pb: 2, mt: 1 }} />

      <ProfileDrawer
        email={profileEmail}
        donorRow={profileRow}
        onClose={closeProfile}
        campaignId={activeCampaignId}
      />

      <UserDrawer
        open={userDrawerOpen}
        user={user}
        onClose={() => setUserDrawerOpen(false)}
        onLogout={() => {
          setUserDrawerOpen(false);
          logout();
        }}
        onUserUpdate={refreshUser}
      />

      <CampaignSetupDrawer
        open={campaignSetupOpen}
        onClose={() => setCampaignSetupOpen(false)}
        cfg={cfg}
        selectedCampaign={selectedCampaign}
        isSuperuser={!!user?.superuser}
        onNewCampaign={() => setShowProvisionModal(true)}
        onCfgUpdated={fetchCampaignConfig}
      />

      {showProvisionModal && (
        <ProvisionCampaignModal
          onClose={() => setShowProvisionModal(false)}
          onProvisioned={(camp) => {
            setShowProvisionModal(false);
            refreshCampaigns(camp?.id ?? camp?.campaign_id ?? null);
          }}
        />
      )}
    </Box>
  );
}
